import React, { useState, useRef, useEffect } from "react";
import Popup from "../../../common/Popup/Popup";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";
import { formatName, getCurrentStatus } from "../../../util/AppUtils.jsx";
import { BASE_URL } from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { getAllAppSetups } from "../../../redux/appSetupSlice";
import {
  getProcedureRevisionsForPart,
  getRevAllNodes,
} from "../../../redux/procedureRevisionSlice";
import { getBom } from "../../../redux/partSlice";
import { getTrainingLogsForProcedureRevision } from "../../../redux/trainingLogSlice";
import RevisionTable from "../../Documents/components/RevisionTable";
import RenderRevisionNodes from "../../Documents/components/RenderRevisionNodes";
import { computeTrainingLogExportRowsForPdf } from "../../Documents/components/TrainingLogTable";
import { getTrainingLogSignatureCell } from "../../../util/trainingLogSignatureDisplay";
import { resolveTrainingStatusLabelFromLog } from "../../../util/trainingLogStatus";
import {
  registerDocumentPdfExporter,
} from "../documentPdfExportApi";

/**
 * Prefer audit_trail.action when status_changed_to does not describe the event
 * (e.g. approval rows still carrying "Submitted For Approval").
 */
function deriveDisplayStatusFromAuditAction(action) {
  const s = String(action || "").trim().toLowerCase();
  if (!s) return null;
  if (/^approved\b/.test(s)) return "Approved";
  if (/^released\b/.test(s) || /^release\b/.test(s)) return "Released";
  if (/^submitted\b/.test(s)) {
    if (s.includes("for approval")) return "Submitted For Approval";
    return "Submitted";
  }
  return null;
}

const isCaptionExportDebugEnabled = () => {
  try {
    return (
      window?.__MHTML_CAPTION_DEBUG__ === true ||
      window?.localStorage?.getItem("mhtmlCaptionDebug") === "1"
    );
  } catch (_) {
    return false;
  }
};

/** Whitespace-only text nodes between image row and caption show as a gap in inline-block layout. */
function removeWhitespaceOnlyTextNodesFromElement(el) {
  if (!el) {
    return;
  }
  Array.from(el.childNodes).forEach((node) => {
    if (
      node.nodeType === Node.TEXT_NODE &&
      !/[^\s\uFEFF]/.test(node.textContent || "")
    ) {
      node.parentNode?.removeChild(node);
    }
  });
}

/**
 * Lexical inline captions use <span class="image-caption-container">. Many offline viewers
 * (Word, some MHTML clients) ignore background-color on inline spans; use a block div for export.
 */
function replaceCaptionSpanWithDivForExport(captionEl) {
  if (!captionEl || captionEl.tagName !== "SPAN") {
    return captionEl;
  }
  const parent = captionEl.parentElement;
  if (!parent) {
    return captionEl;
  }
  const div = document.createElement("div");
  div.className = captionEl.className;
  const styleAttr = captionEl.getAttribute("style");
  if (styleAttr) {
    div.setAttribute("style", styleAttr);
  }
  Array.from(captionEl.attributes).forEach((attr) => {
    if (attr.name === "class" || attr.name === "style") {
      return;
    }
    div.setAttribute(attr.name, attr.value);
  });
  while (captionEl.firstChild) {
    div.appendChild(captionEl.firstChild);
  }
  parent.replaceChild(div, captionEl);
  return div;
}

/** Match PDF export: captions below images (overrides editor absolute/side-by-side layout). */
function applyImageCaptionLayoutForExport(captionContainer) {
  if (!captionContainer) {
    return;
  }

  try {
    captionContainer.style.setProperty("display", "block", "important");
    captionContainer.style.setProperty("order", "2", "important");
    captionContainer.style.setProperty("margin-top", "0", "important");
    captionContainer.style.setProperty("visibility", "visible", "important");
    captionContainer.style.setProperty("opacity", "1", "important");
    captionContainer.style.setProperty("position", "static", "important");
    captionContainer.style.setProperty("overflow", "visible", "important");
    captionContainer.style.setProperty("width", "100%", "important");
    captionContainer.style.setProperty("padding", "0", "important");
    captionContainer.style.setProperty("box-sizing", "border-box", "important");

    const imageWrapper = captionContainer.parentElement;
    const isLexicalImageWrapper =
      imageWrapper &&
      (imageWrapper.classList?.contains("inline-editor-image") ||
        imageWrapper.classList?.contains("editor-image"));

    if (imageWrapper?.classList?.contains("inline-editor-image")) {
      // Match editor (inline-block). Flex/inline-flex can leave vertical gaps in exported HTML;
      // block-level children stack flush under the image row.
      imageWrapper.style.setProperty("display", "inline-block", "important");
      imageWrapper.style.setProperty("vertical-align", "top", "important");
      imageWrapper.style.setProperty("float", "none", "important");
      imageWrapper.style.setProperty("clear", "both", "important");
      imageWrapper.style.setProperty("max-width", "100%", "important");
      imageWrapper.style.setProperty("width", "max-content", "important");
      if (
        imageWrapper.classList.contains("position-left") ||
        imageWrapper.classList.contains("position-right")
      ) {
        imageWrapper.style.setProperty("max-width", "50%", "important");
      }
      imageWrapper.style.setProperty("position", "relative", "important");
    } else if (imageWrapper?.classList?.contains("editor-image")) {
      imageWrapper.style.setProperty("display", "flex", "important");
      imageWrapper.style.setProperty("flex-direction", "column", "important");
      imageWrapper.style.setProperty("align-items", "stretch", "important");
      imageWrapper.style.setProperty("gap", "0", "important");
      imageWrapper.style.setProperty("row-gap", "0", "important");
      imageWrapper.style.setProperty("column-gap", "0", "important");
      imageWrapper.style.setProperty("float", "none", "important");
      imageWrapper.style.setProperty("clear", "both", "important");
      imageWrapper.style.setProperty("max-width", "100%", "important");
      imageWrapper.style.setProperty("position", "relative", "important");
    }

    const isInlineCaption = !!captionContainer.closest(".inline-editor-image");
    if (isInlineCaption || captionContainer.closest(".editor-image")) {
      captionContainer.style.setProperty("background", "#f4f4f4", "important");
      captionContainer.style.setProperty(
        "background-color",
        "#f4f4f4",
        "important",
      );
      if (isInlineCaption) {
        captionContainer.style.setProperty("min-width", "0", "important");
        captionContainer.style.setProperty("width", "100%", "important");
        captionContainer.style.setProperty("max-width", "100%", "important");
      } else {
        captionContainer.style.setProperty("min-width", "100%", "important");
      }
      captionContainer.style.setProperty("color", "#000", "important");
      captionContainer.style.setProperty(
        "-webkit-print-color-adjust",
        "exact",
        "important",
      );
      captionContainer.style.setProperty("print-color-adjust", "exact", "important");
    }

    const parent = captionContainer.parentElement;
    if (parent) {
      if (!isLexicalImageWrapper) {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.display === "flex" ||
          computedStyle.display === "grid"
        ) {
          parent.style.setProperty("flex-direction", "column", "important");
          parent.style.setProperty("display", "flex", "important");
        }
      }

      const images = parent.querySelectorAll("img, .LazyImage, [draggable]");
      images.forEach((img) => {
        if (
          img &&
          img !== captionContainer &&
          !captionContainer.contains(img)
        ) {
          img.style.setProperty("order", "1", "important");
          const imgWrapper = img.parentElement;
          if (
            imgWrapper &&
            imgWrapper !== parent &&
            imgWrapper !== captionContainer
          ) {
            imgWrapper.style.setProperty("order", "1", "important");
          }
        }
      });
    }

    const placeholders = captionContainer.querySelectorAll(
      ".ImageNode__placeholder, .InlineImageNode__placeholder",
    );
    placeholders.forEach((el) => {
      el.style.setProperty("display", "none", "important");
      el.style.setProperty("visibility", "hidden", "important");
      el.style.setProperty("opacity", "0", "important");
    });

    const captionEditors = captionContainer.querySelectorAll(
      ".ImageNode__contentEditable, .InlineImageNode__contentEditable",
    );
    captionEditors.forEach((editorRoot) => {
      editorRoot.style.setProperty("display", "block", "important");
      editorRoot.style.setProperty("visibility", "visible", "important");
      editorRoot.style.setProperty("opacity", "1", "important");
      editorRoot.style.setProperty("color", "#000", "important");
      editorRoot.style.setProperty("white-space", "pre-wrap", "important");
      editorRoot.style.setProperty("word-break", "break-word", "important");
    });

    const extractCaptionText = () => {
      for (const editorRoot of Array.from(captionEditors)) {
        const nestedClone = editorRoot.cloneNode(true);
        const clonePlaceholders = nestedClone.querySelectorAll(
          ".ImageNode__placeholder, .InlineImageNode__placeholder",
        );
        clonePlaceholders.forEach((el) => el.remove());
        const text = (nestedClone.textContent || "")
          .replace(/\s+/g, " ")
          .trim();
        if (text) {
          return text;
        }
      }

      const treeView = captionContainer.querySelector(".tree-view-output");
      if (treeView) {
        const text = (treeView.textContent || "").replace(/\s+/g, " ").trim();
        if (text) {
          return text;
        }
      }

      const tempContainer = captionContainer.cloneNode(true);
      const tempPlaceholders = tempContainer.querySelectorAll(
        ".ImageNode__placeholder, .InlineImageNode__placeholder",
      );
      tempPlaceholders.forEach((el) => el.remove());
      const rawText = (tempContainer.textContent || "")
        .replace(/\s+/g, " ")
        .trim();
      return rawText;
    };

    const captionText = extractCaptionText();
    if (captionText) {
      // Drop Lexical nested editors entirely: their min-height/padding and wrapper divs
      // still consume vertical space when hidden, so captions look far below the image and
      // the grey bar can collapse oddly in saved HTML/MHTML.
      const staticCaption = document.createElement("div");
      staticCaption.setAttribute("data-pdf-caption", "true");
      staticCaption.textContent = captionText;
      staticCaption.style.setProperty("display", "block", "important");
      staticCaption.style.setProperty("visibility", "visible", "important");
      staticCaption.style.setProperty("opacity", "1", "important");
      staticCaption.style.setProperty("margin", "0", "important");
      staticCaption.style.setProperty("padding", "10px", "important");
      staticCaption.style.setProperty("font-size", "14px", "important");
      staticCaption.style.setProperty("line-height", "1.4em", "important");
      staticCaption.style.setProperty("color", "#000", "important");
      staticCaption.style.setProperty("white-space", "pre-wrap", "important");
      staticCaption.style.setProperty("word-break", "break-word", "important");
      staticCaption.style.setProperty("background", "transparent", "important");
      staticCaption.style.setProperty("box-sizing", "border-box", "important");
      staticCaption.style.setProperty("min-height", "0", "important");

      captionContainer.innerHTML = "";
      captionContainer.appendChild(staticCaption);
      const captionExportEl = replaceCaptionSpanWithDivForExport(captionContainer);

      if (isCaptionExportDebugEnabled()) {
        const el = captionExportEl || captionContainer;
        const wrapper = el.closest(".inline-editor-image, .editor-image");
        const wrapperStyle = wrapper ? window.getComputedStyle(wrapper) : null;
        const captionStyle = window.getComputedStyle(el);
        // eslint-disable-next-line no-console
      }
    }
  } catch (e) {
    // best-effort
  }
}

/** Relative url(...) in bundled CSS breaks in downloaded HTML; resolve against stylesheet URL or page. */
function absolutizeCssUrlsForMhtml(cssText, cssSourceHref) {
  if (!cssText) return "";
  const baseHref =
    cssSourceHref && cssSourceHref !== "about:blank"
      ? cssSourceHref
      : window.location.href;
  try {
    return cssText.replace(
      /url\s*\(\s*(['"]?)([^'"()]+)(['"]?)\s*\)/gi,
      (full, _q1, url) => {
        const trimmed = String(url).trim();
        if (
          trimmed.startsWith("data:") ||
          trimmed.startsWith("blob:") ||
          /^https?:\/\//i.test(trimmed)
        ) {
          return full;
        }
        try {
          const abs = trimmed.startsWith("/")
            ? new URL(trimmed, window.location.origin).href
            : new URL(trimmed, baseHref).href;
          return `url("${abs}")`;
        } catch {
          return full;
        }
      },
    );
  } catch {
    return cssText;
  }
}

const ExportFormatPopup = ({
  isOpen,
  onClose,
  bomTable = [],
  revisionData = [],
  trainingLogs = [],
  type = "",
  partNumber = "",
  myRevision = "",
  revisionId = "",
  description = "",
  contentRef,
  appSetupItems = [],
  overrideRevisionDetails = null,
  overrideReference = null,
  filteredRevisionData = [],
  status = "",
  isModulePreview = false,
  moduleProcedures = [],
  moduleData = null,
  /** When true, render only the hidden ZIP-style DOM mount + register runDocumentPdfExport (PreviewNodes). */
  renderPdfEngineOnly = false,
  /** Drives zipExportView for API-based PDF (same payload as module ZIP). */
  pdfEngineZipPayload = null,
}) => {
  const [exportFormat, setExportFormat] = useState(
    isModulePreview ? "mhtml" : "pdf",
  );
  const [isExporting, setIsExporting] = useState(false);
  const zipExportRootRef = useRef(null);
  const [zipExportView, setZipExportView] = useState(null);
  const dispatch = useDispatch();
  const persistedAppSetupItems = useSelector(
    (state) => state?.appSetup?.items || [],
  );
  const appSetupFetchPromiseRef = useRef(null);

  useEffect(() => {
    if (
      exportFormat === "signatures_workflow" &&
      status?.toLowerCase() === "not submitted"
    ) {
      setExportFormat("pdf");
    }
  }, [status, exportFormat]);

  useEffect(() => {
    if (isModulePreview) {
      if (
        !["mhtml", "pdf", "csv_module_procedures", "zip"].includes(exportFormat)
      ) {
        setExportFormat("mhtml");
      }
    }
  }, [isModulePreview, exportFormat]);

  const stripHtmlPreserveFormatting = (html) => {
    if (!html) return "";
    if (typeof html === "string" && !html.includes("<")) {
      return html.trim();
    }
    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      // If HTML includes embedded CSS/JS (common with rich-text exports),
      // ensure we don't render those as plain text in PDFs/tables.
      tempDiv
        .querySelectorAll("style,script,noscript")
        .forEach((el) => el.remove());
      const text = tempDiv.textContent || tempDiv.innerText || "";
      if (!text.trim() && html.trim()) {
        return html.trim();
      }
      return text.trim();
    } catch (e) {
      return String(html).trim();
    }
  };

  const normalizeSignatureDataUrl = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    let value = raw;
    if (value.startsWith("\\x")) {
      const hex = value.slice(2);
      let out = "";
      for (let i = 0; i < hex.length; i += 2) {
        const byte = parseInt(hex.slice(i, i + 2), 16);
        if (!Number.isNaN(byte)) {
          out += String.fromCharCode(byte);
        }
      }
      value = out;
    }
    if (value.startsWith("data:image")) return value;
    if (/^[A-Za-z0-9+/=]+$/.test(value)) {
      return `data:image/png;base64,${value}`;
    }
    return value;
  };

  const getPhoneIconSvgDataUrl = () => {
    const svgContent = `<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.5 4V2.5H5V1.5H6.5V0H7.5V1.5H9V2.5H7.5V4H6.5ZM8.475 9C7.4 9 6.35217 8.76033 5.3315 8.281C4.3105 7.802 3.40633 7.16867 2.619 6.381C1.83133 5.59367 1.198 4.6895 0.719 3.6685C0.239667 2.64783 0 1.6 0 0.525C0 0.375 0.05 0.25 0.15 0.15C0.25 0.05 0.375 0 0.525 0H2.55C2.66667 0 2.77083 0.0374999 2.8625 0.1125C2.95417 0.1875 3.00833 0.283333 3.025 0.4L3.35 2.15C3.36667 2.26667 3.36467 2.37283 3.344 2.4685C3.323 2.5645 3.275 2.65 3.2 2.725L2 3.95C2.35 4.55 2.7875 5.1125 3.3125 5.6375C3.8375 6.1625 4.41667 6.61667 5.05 7L6.225 5.825C6.3 5.75 6.398 5.69367 6.519 5.656C6.63967 5.61867 6.75833 5.60833 6.875 5.625L8.6 5.975C8.71667 6 8.8125 6.05617 8.8875 6.1435C8.9625 6.23117 9 6.33333 9 6.45V8.475C9 8.625 8.95 8.75 8.85 8.85C8.75 8.95 8.625 9 8.475 9Z" fill="#AAAAAA"/>
</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
  };

  const getPhoneIconPngDataUrl = async () => {
    return new Promise((resolve) => {
      const svgDataUrl = getPhoneIconSvgDataUrl();
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = 4;
          canvas.width = (img.naturalWidth || img.width || 9) * scale;
          canvas.height = (img.naturalHeight || img.height || 9) * scale;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          resolve("");
        }
      };
      img.onerror = (err) => {
        resolve("");
      };
      img.crossOrigin = "anonymous";
      img.src = svgDataUrl;
    });
  };

  const getDistanceIconSvgDataUrl = () => {
    const svgContent = `<svg width="10" height="10" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.29268 10.8C2.32317 10.8 1.53201 10.6493 0.919207 10.348C0.306403 10.0463 0 9.657 0 9.18C0 8.865 0.132622 8.5905 0.397866 8.3565C0.66311 8.1225 1.02896 7.938 1.49543 7.803L1.81098 8.829C1.65549 8.874 1.51372 8.93034 1.38567 8.99802C1.25762 9.06534 1.17073 9.126 1.125 9.18C1.2439 9.324 1.51829 9.45 1.94817 9.558C2.37805 9.666 2.82622 9.72 3.29268 9.72C3.75915 9.72 4.2097 9.666 4.64433 9.558C5.0786 9.45 5.35518 9.324 5.47409 9.18C5.42835 9.126 5.34146 9.06534 5.21341 8.99802C5.08537 8.93034 4.9436 8.874 4.78811 8.829L5.10366 7.803C5.57012 7.938 5.93378 8.1225 6.19463 8.3565C6.45512 8.5905 6.58537 8.865 6.58537 9.18C6.58537 9.657 6.27896 10.0463 5.66616 10.348C5.05335 10.6493 4.2622 10.8 3.29268 10.8ZM3.29268 9.18C3.19207 9.18 3.10061 9.15066 3.01829 9.09198C2.93598 9.03366 2.87652 8.955 2.83994 8.856C2.62957 8.217 2.36433 7.6815 2.04421 7.2495C1.72409 6.8175 1.41311 6.4035 1.11128 6.0075C0.818597 5.6115 0.564878 5.202 0.350122 4.779C0.135 4.356 0.0274391 3.834 0.0274391 3.213C0.0274391 2.313 0.342988 1.5525 0.974085 0.9315C1.60518 0.3105 2.37805 0 3.29268 0C4.20732 0 4.98018 0.3105 5.61128 0.9315C6.24238 1.5525 6.55793 2.313 6.55793 3.213C6.55793 3.834 6.45274 4.356 6.24238 4.779C6.03201 5.202 5.77592 5.6115 5.47409 6.0075C5.1814 6.4035 4.87281 6.8175 4.54829 7.2495C4.22341 7.6815 3.95579 8.217 3.74543 8.856C3.70884 8.955 3.64939 9.03366 3.56707 9.09198C3.48476 9.15066 3.39329 9.18 3.29268 9.18ZM3.29268 4.3605C3.6128 4.3605 3.8872 4.248 4.11585 4.023C4.34451 3.798 4.45884 3.528 4.45884 3.213C4.45884 2.898 4.34451 2.628 4.11585 2.403C3.8872 2.178 3.6128 2.0655 3.29268 2.0655C2.97256 2.0655 2.69817 2.178 2.46951 2.403C2.24085 2.628 2.12652 2.898 2.12652 3.213C2.12652 3.528 2.24085 3.798 2.46951 4.023C2.69817 4.248 2.97256 4.3605 3.29268 4.3605Z" fill="#AAAAAA"/>
</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
  };

  const getDistanceIconPngDataUrl = async () => {
    return new Promise((resolve) => {
      const svgDataUrl = getDistanceIconSvgDataUrl();
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = 4;
          canvas.width = (img.naturalWidth || img.width || 7) * scale;
          canvas.height = (img.naturalHeight || img.height || 11) * scale;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          resolve("");
        }
      };
      img.onerror = (err) => {
        resolve("");
      };
      img.crossOrigin = "anonymous";
      img.src = svgDataUrl;
    });
  };

  const convertSvgToPng = async (svgDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const scale = 4;
          canvas.width = (img.naturalWidth || img.width || 200) * scale;
          canvas.height = (img.naturalHeight || img.height || 100) * scale;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const pngDataUrl = canvas.toDataURL("image/png");
          resolve(pngDataUrl);
        } catch (err) {
          resolve("");
        }
      };
      img.onerror = (error) => {
        resolve("");
      };
      img.crossOrigin = "anonymous";
      img.src = svgDataUrl;
    });
  };

  const hideRichTextUndoToolbarsForExport = (rootElement) => {
    if (!rootElement || !rootElement.querySelectorAll) {
      return;
    }

    // Module Preview PDF should include the short-description toolbar.
    if (isModulePreview) {
      return;
    }

    try {
      const richTextBlocks = rootElement.querySelectorAll(
        "[data-export-block='rich-text']",
      );

      // Hide any toolbars that live inside rich text export blocks
      richTextBlocks.forEach((block) => {
        try {
          const blockToolbars = block.querySelectorAll(".toolbars, .toolbar");
          blockToolbars.forEach((toolbar) => {
            // Prefer removing the toolbar entirely so it cannot be captured in the PDF
            if (toolbar.parentNode) {
              toolbar.parentNode.removeChild(toolbar);
            } else {
              toolbar.style.setProperty("display", "none", "important");
              toolbar.style.setProperty("visibility", "hidden", "important");
              toolbar.style.setProperty("height", "0", "important");
              toolbar.style.setProperty("margin", "0", "important");
              toolbar.style.setProperty("padding", "0", "important");
              toolbar.style.setProperty("overflow", "hidden", "important");
            }
          });

          const undoButtons = block.querySelectorAll(
            "button[aria-label='Undo'], button[title*='Undo'], button[title*='undo']",
          );
          undoButtons.forEach((btn) => {
            btn.style.setProperty("display", "none", "important");
            btn.style.setProperty("visibility", "hidden", "important");
          });

          const undoIcons = block.querySelectorAll(
            ".toolbars .format.undo, .toolbar .format.undo, i.format.undo",
          );
          undoIcons.forEach((icon) => {
            const buttonParent = icon.closest("button");
            if (buttonParent) {
              buttonParent.style.setProperty("display", "none", "important");
              buttonParent.style.setProperty(
                "visibility",
                "hidden",
                "important",
              );
            } else {
              icon.style.setProperty("display", "none", "important");
              icon.style.setProperty("visibility", "hidden", "important");
            }
          });
        } catch (e) {}
      });

      // As an extra safety, remove any remaining editor toolbars in the cloned tree
      const allToolbars = rootElement.querySelectorAll(".toolbars, .toolbar");
      const linkEditors = rootElement.querySelectorAll(
        ".link-editor, .floating-link-editor",
      );
      if (linkEditors.length > 0) {
        
      }
      allToolbars.forEach((toolbar) => {
        if (toolbar.parentNode) {
          const hadLinks = toolbar.querySelectorAll("a[href]").length;
          if (hadLinks > 0) {
            console.warn(
              "[PDF Export - Links] Removing toolbar that contains",
              hadLinks,
              "link(s) - check if this removes content",
            );
          }
          toolbar.parentNode.removeChild(toolbar);
        } else {
          toolbar.style.setProperty("display", "none", "important");
          toolbar.style.setProperty("visibility", "hidden", "important");
          toolbar.style.setProperty("height", "0", "important");
          toolbar.style.setProperty("margin", "0", "important");
          toolbar.style.setProperty("padding", "0", "important");
          toolbar.style.setProperty("overflow", "hidden", "important");
        }
      });
    } catch (e) {}
  };

  const enforceSingleLineModulePreviewToolbarForExport = (rootElement) => {
    if (!isModulePreview || !rootElement || !rootElement.querySelectorAll) {
      return;
    }

    try {
      const toolbars = rootElement.querySelectorAll(
        ".short-description-editor-wrapper .toolbars",
      );

      toolbars.forEach((toolbar) => {
        toolbar.style.setProperty("display", "flex", "important");
        toolbar.style.setProperty("flex-wrap", "nowrap", "important");
        toolbar.style.setProperty("overflow-x", "hidden", "important");
        toolbar.style.setProperty("overflow-y", "hidden", "important");
        toolbar.style.setProperty("min-height", "32px", "important");
        toolbar.style.setProperty("height", "32px", "important");
        toolbar.style.setProperty("padding", "1px 4px", "important");
        toolbar.style.setProperty("gap", "0", "important");
        toolbar.style.setProperty("white-space", "nowrap", "important");
      });

      const toolbarButtons = rootElement.querySelectorAll(
        ".short-description-editor-wrapper .toolbars .toolbar-item, .short-description-editor-wrapper .toolbars button",
      );
      toolbarButtons.forEach((btn) => {
        btn.style.setProperty("padding", "2px 4px", "important");
        btn.style.setProperty("margin", "0", "important");
        btn.style.setProperty("flex-shrink", "1", "important");
      });

      const toolbarTexts = rootElement.querySelectorAll(
        ".short-description-editor-wrapper .toolbars .toolbar-item .text",
      );
      toolbarTexts.forEach((textEl) => {
        textEl.style.setProperty("font-size", "10px", "important");
        textEl.style.setProperty("line-height", "12px", "important");
        textEl.style.setProperty("height", "12px", "important");
        textEl.style.setProperty("padding-right", "2px", "important");
      });

      const toolbarIcons = rootElement.querySelectorAll(
        ".short-description-editor-wrapper .toolbars .toolbar-item .icon, .short-description-editor-wrapper .toolbars .toolbar-item i.format, .short-description-editor-wrapper .toolbars i.chevron-down",
      );
      toolbarIcons.forEach((iconEl) => {
        iconEl.style.setProperty("width", "11px", "important");
        iconEl.style.setProperty("height", "11px", "important");
      });

      const dividers = rootElement.querySelectorAll(
        ".short-description-editor-wrapper .toolbars .divider",
      );
      dividers.forEach((dividerEl) => {
        dividerEl.style.setProperty("min-height", "16px", "important");
        dividerEl.style.setProperty("margin", "0 2px", "important");
      });
    } catch (e) {}
  };

  const normalizeModulePreviewInputAlignmentForExport = (rootElement) => {
    // Only apply this fix for the normal modulepreview "pdf" export.
    // ZIP export and other formats reuse handleExportPDF to render blobs, so we
    // must not change their DOM behavior.
    if (
      !isModulePreview ||
      exportFormat !== "pdf" ||
      !rootElement ||
      !rootElement.querySelectorAll
    ) {
      return;
    }

    try {
      // Further narrow the condition to the AddModule/ModulePreview PDF layout,
      // so we don't disturb other module previews (e.g. audit trail previews).
      if (!rootElement.querySelector?.(".module-preview-docs-table")) {
        return;
      }

      const moduleInputs = rootElement.querySelectorAll(
        ".module-form input[type='text'][disabled]",
      );

      moduleInputs.forEach((inputEl) => {
        const wrapper = inputEl.parentElement;
        if (!wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const wrapperHeight = Math.round(wrapperRect.height);
        const effectiveHeight =
          Number.isFinite(wrapperHeight) && wrapperHeight > 0
            ? wrapperHeight
            : 34;

        // html2canvas often renders disabled input text with inconsistent
        // vertical metrics across environments. For export, replace the input
        // with a plain centered text div using the input's computed font styles.
        const computed = window.getComputedStyle(inputEl);
        const displayText =
          inputEl.value ||
          inputEl.placeholder ||
          inputEl.getAttribute("value") ||
          "";

        wrapper.style.setProperty("display", "flex", "important");
        wrapper.style.setProperty("align-items", "center", "important");
        wrapper.style.setProperty(
          "height",
          `${effectiveHeight}px`,
          "important",
        );
        wrapper.style.setProperty("box-sizing", "border-box", "important");

        const textEl = document.createElement("div");
        textEl.textContent = String(displayText ?? "");
        textEl.style.flex = "1";
        textEl.style.height = "100%";
        textEl.style.display = "flex";
        textEl.style.alignItems = "center";
        textEl.style.padding = "0 8px";
        textEl.style.boxSizing = "border-box";
        textEl.style.background = "transparent";
        textEl.style.overflow = "hidden";
        textEl.style.whiteSpace = "nowrap";
        textEl.style.textOverflow = "ellipsis";
        textEl.style.fontSize = computed.fontSize || "14px";
        textEl.style.fontFamily = computed.fontFamily || "inherit";
        textEl.style.color = computed.color || "inherit";

        wrapper.replaceChild(textEl, inputEl);
      });
    } catch (e) {}
  };

  const getCompanyLogoDataUrl = async (logoPath) => {
    if (!logoPath) {
      return "";
    }

    if (logoPath.startsWith("data:image")) {
      if (logoPath.includes("svg") || logoPath.includes("svg+xml")) {
        const pngDataUrl = await convertSvgToPng(logoPath);
        if (pngDataUrl) {
          return pngDataUrl;
        }
        return "";
      }
      return logoPath;
    }

    const resolveAbsoluteUrl = () => {
      const base = BASE_URL.replace("/api/", "");
      if (logoPath.startsWith("http")) return logoPath;
      if (logoPath.startsWith("/")) {
        return `${base.replace(/\/$/, "")}${logoPath}`;
      }
      return `${base.replace(/\/$/, "")}/${logoPath}`;
    };

    const absoluteUrl = resolveAbsoluteUrl();

    const blobToDataUrl = async (blob) =>
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(typeof reader.result === "string" ? reader.result : "");
        };
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });

    try {
      const parsedUrl = new URL(absoluteUrl, window.location.origin);
      const isSameOrigin = parsedUrl.origin === window.location.origin;

      const response = await fetch(absoluteUrl, {
        mode: "cors",
        // If the asset is same-origin it may require cookies/session to download.
        // Use include to match how the browser can display it in-app.
        credentials: isSameOrigin ? "include" : "omit",
        cache: "no-cache",
      });

      if (response.ok) {
        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl) {
          return dataUrl;
        } else {
        }
      } else {
      }
    } catch (error) {}

    return await new Promise((resolve) => {
      const img = new window.Image();

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          resolve(dataUrl);
        } catch (err) {
          resolve("");
        }
      };

      img.onerror = (error) => {
        resolve("");
      };

      img.crossOrigin = "anonymous";

      const urlWithCacheBust = absoluteUrl.includes("?")
        ? `${absoluteUrl}&_cb=${Date.now()}`
        : `${absoluteUrl}?_cb=${Date.now()}`;

      img.src = urlWithCacheBust;
    });
  };

  const normalizeLogoCandidate = (value) => {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^data:image\//i.test(trimmed)) return trimmed;
    const looksBase64 =
      /^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 200;
    if (looksBase64) {
      return `data:image/png;base64,${trimmed}`;
    }
    return trimmed;
  };

  const getAppSetupLogoPath = (appSetup) => {
    if (!appSetup) {
      return "";
    }
    const candidates = [
      { name: "company_image", value: appSetup.company_image },
      { name: "company_logo", value: appSetup.company_logo },
      { name: "company_image_url", value: appSetup.company_image_url },
      { name: "company_logo_url", value: appSetup.company_logo_url },
      { name: "company_image_base64", value: appSetup.company_image_base64 },
    ];
    for (const candidate of candidates) {
      if (candidate.value) {
      }
      const normalized = normalizeLogoCandidate(candidate.value);
      if (normalized) {
        return normalized;
      }
    }
    return "";
  };

  const formatPhoneNumber = (raw) => {
    if (!raw) return "";
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) {
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    }
    return raw;
  };

  const resolveAppSetupItems = async (forceFetch = false) => {
    const sortByDate = (items) => {
      if (!Array.isArray(items) || items.length === 0) return items;
      return [...items].sort((a, b) => {
        const dateA = new Date(a?.updated_at || a?.created_at || 0).getTime();
        const dateB = new Date(b?.updated_at || b?.created_at || 0).getTime();
        return dateB - dateA;
      });
    };

    if (forceFetch) {
      appSetupFetchPromiseRef.current = null;
    } else {
      if (appSetupItems?.length) return sortByDate(appSetupItems);
      if (persistedAppSetupItems?.length)
        return sortByDate(persistedAppSetupItems);
    }

    if (!appSetupFetchPromiseRef.current) {
      appSetupFetchPromiseRef.current = dispatch(getAllAppSetups())
        .unwrap()
        .then((payload) => {
          const list =
            payload?.data ||
            payload?.results ||
            payload?.items ||
            payload ||
            [];
          return Array.isArray(list) ? list : [];
        })
        .catch((error) => {
          return [];
        })
        .finally(() => {
          appSetupFetchPromiseRef.current = null;
        });
    }

    const fetchedItems = await appSetupFetchPromiseRef.current;
    if (fetchedItems?.length) return sortByDate(fetchedItems);
    if (appSetupItems?.length) return sortByDate(appSetupItems);
    if (persistedAppSetupItems?.length)
      return sortByDate(persistedAppSetupItems);
    return [];
  };

  const waitForContentToRender = async () => {
    if (!contentRef?.current) return;

    const isExportDomReady = () => {
      const el = contentRef.current;
      if (!el) return false;
      if (el.children.length === 0) return false;

      // CanvasNode shows this while canvas JSON is still fetching; meaningful text exists
      // immediately (revision table, headings), so we must not snapshot PDFs too early.
      const rawText = el.textContent || "";
      if (/Loading\s+Canvas/i.test(rawText)) {
        return false;
      }

      const canvasBlocks = el.querySelectorAll("[data-export-block='canvas']");
      for (let i = 0; i < canvasBlocks.length; i += 1) {
        const block = canvasBlocks[i];
        if (block.querySelector(".loader-overlay")) {
          return false;
        }
      }

      const textContent = rawText.replace(/\s+/g, "");
      if (textContent && textContent.length > 0) return true;
      if (el.querySelector("img,canvas,video,table")) return true;
      return false;
    };

    // ZIP export switches revisions quickly; canvas JSON + Konva need more time than 20×150ms.
    const maxAttempts = 80;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (isExportDomReady()) {
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve)),
        );
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  };

  const getReleaseInfoText = ({
    revisionId: revisionIdOverride,
    filteredRevisionData: filteredRevisionDataOverride,
    revisionData: revisionDataOverride,
  } = {}) => {
    const effectiveRevisionId = revisionIdOverride ?? revisionId;
    if (!effectiveRevisionId) return "";

    const dataSource =
      Array.isArray(filteredRevisionDataOverride) &&
      filteredRevisionDataOverride.length > 0
        ? filteredRevisionDataOverride
        : Array.isArray(revisionDataOverride)
          ? revisionDataOverride
          : revisionData;
    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      return "";
    }

    const matchedRevision = dataSource.find(
      (rev) =>
        String(rev?.procedure_revision_id) ===
          String(effectiveRevisionId ?? "") ||
        String(rev?.procedure_revision_id_ref) ===
          String(effectiveRevisionId ?? ""),
    );

    if (!matchedRevision) return "";

    const statusChanges = Array.isArray(matchedRevision.status_changes)
      ? matchedRevision.status_changes.slice()
      : [];

    const releaseChange = statusChanges
      .filter((statusChange) =>
        (statusChange?.status_changed_to || "")
          .toLowerCase()
          .includes("release"),
      )
      .sort((a, b) => {
        const dateA = new Date(
          a?.audit_trail?.date || a?.created_at || 0,
        ).getTime();
        const dateB = new Date(
          b?.audit_trail?.date || b?.created_at || 0,
        ).getTime();
        return dateB - dateA;
      })[0];

    const dateSource =
      releaseChange?.audit_trail?.date ||
      releaseChange?.created_at ||
      matchedRevision?.date_released;

    if (!dateSource) return "";

    let dateObj = null;
    if (dateSource) {
      const dateStr = String(dateSource).trim();
      const hasTimezone =
        dateStr.endsWith("Z") ||
        /[+-]\d{2}:?\d{2}$/.test(dateStr) ||
        /[+-]\d{4}$/.test(dateStr);

      const normalizedDateStr = hasTimezone ? dateStr : dateStr + "Z";
      dateObj = new Date(normalizedDateStr);

      if (isNaN(dateObj.getTime())) {
        dateObj = new Date(dateStr);
      }
    }

    if (!dateObj || isNaN(dateObj.getTime())) {
      return "";
    }

    const estFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const parts = estFormatter.formatToParts(dateObj);
    const year = parts.find((p) => p.type === "year")?.value || "";
    const month = parts.find((p) => p.type === "month")?.value || "";
    const day = parts.find((p) => p.type === "day")?.value || "";
    const hour = parts.find((p) => p.type === "hour")?.value || "";
    const minute = parts.find((p) => p.type === "minute")?.value || "";
    const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

    const releaseDateFormatted = `${year}-${month.padStart(
      2,
      "0",
    )}-${day.padStart(2, "0")}`;
    const releaseTimeFormatted = `${hour.padStart(2, "0")}:${minute.padStart(
      2,
      "0",
    )} ${dayPeriod.toUpperCase()}`;

    const timezoneParts = dateObj
      .toLocaleDateString("en-US", {
        timeZoneName: "short",
        timeZone: "America/New_York",
      })
      .split(", ");
    const timezoneLabel = timezoneParts[1] || "EST";

    const normalizedFallbackReleaseUser =
      matchedRevision?.released_by_user &&
      typeof matchedRevision.released_by_user === "object"
        ? `${matchedRevision.released_by_user.first_name || ""} ${
            matchedRevision.released_by_user.last_name || ""
          }`.trim()
        : matchedRevision?.released_by_user ||
          matchedRevision?.released_by ||
          "";

    const releaseUser =
      releaseChange?.audit_trail?.user ||
      releaseChange?.user ||
      releaseChange?.performed_by;
    const releaseUserName =
      (releaseUser
        ? `${releaseUser.first_name || ""} ${
            releaseUser.last_name || ""
          }`.trim()
        : releaseChange?.audit_trail?.user_name ||
          releaseChange?.user_name ||
          releaseChange?.user_display ||
          normalizedFallbackReleaseUser ||
          "") || "Unknown User";

    return `Released ${releaseDateFormatted} at ${releaseTimeFormatted} ${
      timezoneLabel || ""
    } by ${releaseUserName}`;
  };

  const splitReleaseInfoLines = (text) => {
    if (!text) return [];
    const lower = text.toLowerCase();
    const atIndex = lower.indexOf(" at ");
    const byIndex = lower.lastIndexOf(" by ");
    if (atIndex === -1 || byIndex === -1 || atIndex >= byIndex) {
      return splitAtByFallback(text);
    }
    const datePart = text.slice(0, atIndex + 4).trim();
    const timePart = text.slice(atIndex + 4, byIndex).trim();
    const byPart = text.slice(byIndex).trim();
    return [datePart, `${timePart} ${byPart}`.trim()].filter(Boolean);
  };

  const splitAtByFallback = (text) => {
    const lower = text.toLowerCase();
    const byIndex = lower.lastIndexOf(" by ");
    if (byIndex === -1) return [text];
    const firstLine = text.slice(0, byIndex).trim();
    const secondLine = text.slice(byIndex).trim();
    return [firstLine, secondLine].filter(Boolean);
  };

  const splitExportInfoLines = (text) => {
    if (!text) return [];
    const lower = text.toLowerCase();
    const atIndex = lower.indexOf(" at ");
    if (atIndex === -1) return [text];
    const splitPos = atIndex + 4;
    const firstLine = text.slice(0, splitPos).trim();
    const secondLine = text.slice(splitPos).trim();
    return [firstLine, secondLine].filter(Boolean);
  };

  const parseRevision = (rev) => {
    if (!rev) return null;

    const hasSpecialChars = /[@#$%!^&*]/.test(rev);
    if (hasSpecialChars) {
      return {
        base: "",
        type: "special_chars",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (rev === "-") {
      return {
        base: "",
        type: "special",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    if (/^[A-Z]{1,3}-$/.test(rev)) {
      return {
        base: rev.slice(0, -1),
        type: "major",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    const amendMatchWithHyphen = rev.match(/^([A-Z]{1,3})(\d+)-([a-z]+)$/);
    if (amendMatchWithHyphen) {
      const [, base, num, amendChar] = amendMatchWithHyphen;
      return {
        base,
        type: "amend",
        minorNum: parseInt(num),
        amendChar,
        original: rev,
      };
    }

    const amendMinorlessMatch = rev.match(/^([A-Z]{1,3})-([a-z]+)$/);
    if (amendMinorlessMatch) {
      const [, base, amendChar] = amendMinorlessMatch;
      return {
        base,
        type: "amend_minorless",
        minorNum: 0,
        amendChar,
        original: rev,
      };
    }

    const minorMatch = rev.match(/^([A-Z]{1,3})(\d+)([a-z]*)$/);
    if (minorMatch) {
      const [, base, num, amendChar] = minorMatch;
      return {
        base,
        type: amendChar ? "amend" : "minor",
        minorNum: parseInt(num),
        amendChar: amendChar || "",
        original: rev,
      };
    }

    return null;
  };

  const handleExportBOMCSV = () => {
    try {
      const header = ["Type", "Item Code", "Description", "Qty Per", "Unit"];

      const sortedBom = bomTable.slice().sort((a, b) => {
        const orderA = parseInt(a.orders || 0, 10);
        const orderB = parseInt(b.orders || 0, 10);
        if (orderA !== orderB) return orderA - orderB;

        const partNumberA = a.part_number || "";
        const partNumberB = b.part_number || "";

        const splitPart = (str) => {
          const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
          if (!match) return { num: NaN, rest: str };
          return {
            num: match[1] ? parseInt(match[1], 10) : NaN,
            rest: match[2] || str,
          };
        };

        const aSplit = splitPart(partNumberA);
        const bSplit = splitPart(partNumberB);

        if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
        if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
        if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
          if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num;
          return aSplit.rest.localeCompare(bSplit.rest);
        }
        return partNumberA.localeCompare(partNumberB);
      });

      const rows = sortedBom.map((row) => {
        const descriptionText = stripHtmlPreserveFormatting(
          row.part_description || "",
        )
          .replace(/\n+/g, " ")
          .trim();
        return [
          row.type_name || "",
          row.part_number || "",
          descriptionText,
          row.qty === 0 ? "" : row.qty || "",
          row.unit_name || "",
        ];
      });

      const csv = [header, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type || ""}_BOM.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const handleExportRevisionsCSV = () => {
    try {
      const header = [
        "Rev",
        "Date Released",
        "Revision Details",
        "Submitter / Requestor",
        "Approver",
        "References",
        "Status",
      ];

      const sortedRevisions = [...revisionData].sort((a, b) => {
        const revA = parseRevision(a.rev);
        const revB = parseRevision(b.rev);
        if (!revA && !revB) return 0;
        if (!revA) return 1;
        if (!revB) return -1;
        if (revA.type === "special") return -1;
        if (revB.type === "special") return 1;
        if (revA.base !== revB.base) return revA.base.localeCompare(revB.base);
        if (revA.type === "major" && revB.type !== "major") return -1;
        if (revB.type === "major" && revA.type !== "major") return 1;
        if (revA.minorNum !== revB.minorNum)
          return revA.minorNum - revB.minorNum;
        return revA.amendChar.localeCompare(revB.amendChar);
      });

      const rows = sortedRevisions.map((rev) => {
        const requiredBy = "required_by" in rev ? rev.required_by : [];

        const submitterTexts = rev.is_maintained_outside_system
          ? ["MAINTAINED OUTSIDE OF SYSTEM"]
          : requiredBy
              .filter((rb) => rb.purpose?.toLowerCase() === "submit")
              .map((rb) => {
                const name = formatName(rev, rb.user);
                return !rb.is_open
                  ? `Submitted by ${name}`
                  : `Pending Submission from ${name}`;
              });

        const approverTexts = rev.is_maintained_outside_system
          ? ["MAINTAINED OUTSIDE OF SYSTEM"]
          : requiredBy
              .filter((rb) => rb.purpose?.toLowerCase() === "approve")
              .map((rb) => {
                const name = formatName(rev, rb.user);
                return !rb.is_open
                  ? `Approved by ${name}`
                  : `Pending Approval from ${name}`;
              });

        const isCurrent = rev.procedure_revision_id === revisionId;
        const detailsValue =
          isCurrent && overrideRevisionDetails != null
            ? overrideRevisionDetails
            : rev.revision_details;
        const referenceValue =
          isCurrent && overrideReference != null
            ? overrideReference
            : rev.reference;

        const dateReleased = rev.date_released
          ? new Date(rev.date_released).toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })
          : "-";

        const status = getCurrentStatus(rev) || "-";

        return [
          rev.rev || "-",
          dateReleased,
          detailsValue || "-",
          submitterTexts.join("; ") || "-",
          approverTexts.join("; ") || "-",
          referenceValue || "-",
          status,
        ];
      });

      const csv = [header, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type || ""}_Revisions.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const handleExportTrainingCSV = () => {
    try {
      const header = [
        "Date of Training",
        "Trainer",
        "Trainer Signature",
        "Trainee",
        "Trainee Signature",
        "Revision Trained",
        "Training Status of Trainee",
      ];

      const rows = trainingLogs.map((row) => {
        const date = row.date_of_training
          ? new Date(row.date_of_training).toLocaleDateString()
          : "";

        const trainerName = row.trainer
          ? `${row.trainer.first_name || ""} ${
              row.trainer.last_name || ""
            }`.trim()
          : "N/A";

        const traineeName = row.trainee
          ? `${row.trainee.first_name || ""} ${
              row.trainee.last_name || ""
            }`.trim()
          : "N/A";

        const trainerSig = row.trainer_signature?.data
          ? "Signed"
          : "No Signature";
        const traineeSig = row.trainee_signature?.data
          ? "Signed"
          : "No Signature";

        const revisionTrained = row.version_id_ref || "N/A";
        const status = row.is_maintained_outside_system
          ? "Maintained Outside System"
          : "In System";

        return [
          date,
          trainerName,
          trainerSig,
          traineeName,
          traineeSig,
          revisionTrained,
          status,
        ];
      });

      const csv = [header, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type || ""}_Training.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const handleExportModuleProceduresCSV = () => {
    try {
      if (!moduleProcedures || moduleProcedures.length === 0) {
        alert("No procedures available to export");
        return;
      }

      const header = [
        "Item Code",
        "Type",
        "Description",
        "Training Status",
        "Rev",
      ];

      const rows = moduleProcedures.map((procedure) => {
        const partNumber = procedure.partNumber || procedure.part_number || "";
        const type = procedure.type || procedure.type_name || "";
        const description = stripHtmlPreserveFormatting(
          procedure.description || procedure.part_description || "",
        )
          .replace(/\n+/g, " ")
          .trim();
        const trainingStatus =
          procedure.trainingStatus || procedure.training_status || "";
        const rev =
          procedure.rev ||
          procedure.revision ||
          procedure.revisionNumber ||
          procedure.revision_number ||
          "";

        return [partNumber, type, description, trainingStatus, rev];
      });

      const csv = [header, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const modulePartNumber = moduleData?.itemCode || partNumber || "Module";
      a.download = `${modulePartNumber}_Table_of_Documents.csv`
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9._-]/g, "_");
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Error exporting CSV: ${e.message}`);
    }
  };

  const handleExportPDF = async (options = {}) => {
    const rootElement = options.rootElement || contentRef?.current;
    if (!rootElement) return;

    const effectiveType = options?.meta?.type ?? type;
    const effectivePartNumber = options?.meta?.partNumber ?? partNumber;
    const effectiveDescription = options?.meta?.description ?? description;
    const effectiveMyRevision = options?.meta?.myRevision ?? myRevision;
    const effectiveRevisionId = options?.meta?.revisionId ?? revisionId;

    const effectiveRevisionData = options?.revisionDataOverride ?? revisionData;
    const effectiveFilteredRevisionData =
      options?.filteredRevisionDataOverride ?? filteredRevisionData;

    const bomTableData = options?.bomTableOverride ?? bomTable;
    const trainingLogsData = options?.trainingLogsOverride ?? trainingLogs;
    /** Same tree as Document Training Log table (step labels, inherited rows, etc.). */
    const allNodesDataForTrainingLog =
      options?.allNodesDataForTrainingLog ?? null;
    // Non–module preview: always use the same layout rules as module ZIP PDF.
    // Module canvas preview keeps legacy control via explicit options?.isZipExport for ZIP blobs only.
    const isZipExport =
      !isModulePreview || options?.isZipExport === true;

    const container = rootElement;
    const pdfExportInitialTransform = container.style.transform;

    try {
      await waitForContentToRender();
      let revisionTableElement = null;
      let revisionHeadingElement = null;
      let revisionHeadingRect = null;
      let revisionHeadingPositionPx = null;

      container.style.transform = "scale(1)";

      const resolvedAppSetupItems = await resolveAppSetupItems(true);
      const appSetup =
        resolvedAppSetupItems.length > 0 ? resolvedAppSetupItems[0] : null;

      const companyName = appSetup?.company_name || "";
      const companyLogo = getAppSetupLogoPath(appSetup);

      const companyAddress = (appSetup?.company_address || "").trim();
      const rawPhone = (appSetup?.company_phone || "").trim();
      const companyPhone = formatPhoneNumber(rawPhone);

      const companyLogoUrl = await getCompanyLogoDataUrl(companyLogo);

      const currentDate = new Date();
      const exportDateFormatted = currentDate.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "America/New_York",
      });
      const exportTimeFormatted = currentDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/New_York",
      });
      const exportTimezone = currentDate
        .toLocaleDateString("en-US", {
          timeZoneName: "short",
          timeZone: "America/New_York",
        })
        .split(", ")[1];
      const exportInfo = `Exported on ${exportDateFormatted} at ${exportTimeFormatted} ${exportTimezone} by ${localStorage.getItem(
        "user_name",
      )}`;
      const releaseInfo = getReleaseInfoText({
        revisionId: effectiveRevisionId,
        filteredRevisionData: effectiveFilteredRevisionData,
        revisionData: effectiveRevisionData,
      });

      const wrapperDiv = document.createElement("div");
      wrapperDiv.style.position = "fixed";
      wrapperDiv.style.left = "-10000px";
      wrapperDiv.style.top = "0";
      wrapperDiv.style.width = "1000px";
      wrapperDiv.style.background = "#ffffff";
      wrapperDiv.style.fontFamily = "Arial, Helvetica, sans-serif";
      wrapperDiv.style.color = "#111";

      const buildSignatureHistorySection = () => {
        const targetStatuses = [
          "submitted",
          "submitted for approval",
          "approved",
          "approve",
          "released",
          "release",
        ];

        const dataSource =
          Array.isArray(effectiveFilteredRevisionData) &&
          effectiveFilteredRevisionData.length > 0
            ? effectiveFilteredRevisionData
            : effectiveRevisionData;

        if (
          !Array.isArray(dataSource) ||
          !dataSource.length ||
          !effectiveRevisionId
        ) {
          return null;
        }

        const matchedRevision = dataSource.find(
          (rev) =>
            String(rev?.procedure_revision_id) ===
            String(effectiveRevisionId ?? ""),
        );

        if (
          !matchedRevision ||
          !Array.isArray(matchedRevision.status_changes) ||
          matchedRevision.status_changes.length === 0
        ) {
          return null;
        }

        const rows = matchedRevision.status_changes
          .filter((statusChange) => {
            const status = (
              statusChange?.status_changed_to || ""
            ).toLowerCase();
            if (status === "not submitted") {
              return false;
            }
            return targetStatuses.some((target) => status.includes(target));
          })
          .map((statusChange) => {
            const auditTrail = statusChange?.audit_trail || {};
            const user = auditTrail.user || {};
            const statusRaw = statusChange?.status_changed_to || "";
            const statusLower = statusRaw.toLowerCase();
            const statusFromAction = deriveDisplayStatusFromAuditAction(
              auditTrail.action,
            );

            const isSubmittedForApproval =
              statusLower.includes("submitted for approval") ||
              (statusLower.includes("submitted") &&
                statusLower.includes("approval"));

            let normalizedStatus = (() => {
              if (statusFromAction) {
                return statusFromAction;
              }
              if (isSubmittedForApproval) {
                return "Submitted";
              }
              if (statusLower.includes("submitted")) return "Submitted";
              if (
                statusLower.includes("approved") ||
                statusLower.includes("approve")
              )
                return "Approved";
              if (
                statusLower.includes("released") ||
                statusLower.includes("release")
              )
                return "Released";
              return statusRaw;
            })();

            if (
              ![
                "Submitted",
                "Approved",
                "Released",
                "Submitted For Approval",
                "Submitted for Approval",
              ].includes(normalizedStatus)
            ) {
              return null;
            }

            if (normalizedStatus === "Submitted" && !isSubmittedForApproval) {
              if (
                matchedRevision &&
                Array.isArray(matchedRevision.required_by)
              ) {
                const submitRequiredBy = matchedRevision.required_by.find(
                  (rb) => rb?.purpose?.toLowerCase() === "submit",
                );

                if (submitRequiredBy && submitRequiredBy.is_open === true) {
                  return null;
                }
              }
            }

            if (
              normalizedStatus === "Approved" &&
              statusFromAction !== "Approved"
            ) {
              if (
                matchedRevision &&
                Array.isArray(matchedRevision.required_by)
              ) {
                const approveRequiredBy = matchedRevision.required_by.filter(
                  (rb) => {
                    const purpose = (rb?.purpose || "").toLowerCase().trim();
                    return purpose === "approve";
                  },
                );

                if (approveRequiredBy.length > 0) {
                  const hasOpenApprove = approveRequiredBy.some((rb) => {
                    const isOpen =
                      rb.is_open === true ||
                      rb.is_open === "true" ||
                      rb.is_open === 1;
                    return isOpen;
                  });

                  if (hasOpenApprove) {
                    normalizedStatus = "Submitted";
                  }
                } else {
                  normalizedStatus = "Submitted";
                }
              } else {
                normalizedStatus = "Submitted";
              }
            }

            const dateSource =
              auditTrail.date || statusChange?.created_at || "";
            let dateObj = null;
            if (dateSource) {
              const dateStr = dateSource.trim();
              const hasTimezone =
                dateStr.endsWith("Z") ||
                /[+-]\d{2}:?\d{2}$/.test(dateStr) ||
                /[+-]\d{4}$/.test(dateStr);

              const normalizedDateStr = hasTimezone ? dateStr : dateStr + "Z";
              dateObj = new Date(normalizedDateStr);

              if (isNaN(dateObj.getTime())) {
                dateObj = new Date(dateStr);
              }
            }

            const formattedDate =
              dateObj && !isNaN(dateObj.getTime())
                ? dateObj.toLocaleDateString("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    timeZone: "America/New_York",
                  })
                : "-";

            const formattedTime =
              dateObj && !isNaN(dateObj.getTime())
                ? dateObj.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "America/New_York",
                  })
                : "-";

            return {
              fullName:
                `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                "-",
              date: formattedDate,
              time: formattedTime,
              sigUrl: normalizeSignatureDataUrl(auditTrail.signature || ""),
              status: normalizedStatus,
              sortDate: dateObj ? dateObj.getTime() : 0,
              sortStatus: normalizedStatus,
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const statusOrder = {
              Submitted: 0,
              "Submitted for Approval": 0.5,
              Approved: 1,
              Released: 2,
            };
            const statusDiff =
              (statusOrder[a.sortStatus] ?? 99) -
              (statusOrder[b.sortStatus] ?? 99);
            if (statusDiff !== 0) return statusDiff;
            return a.sortDate - b.sortDate;
          });

        if (!rows.length) return null;

        const section = document.createElement("div");
        section.setAttribute("data-export-block", "signature-history");
        section.style.margin = "12px 0 24px 0";
        section.style.padding = "0";
        section.style.background = "#ffffff";

        const heading = document.createElement("div");
        const revisionLabel = matchedRevision?.rev
          ? `Revision ${matchedRevision.rev}`
          : "Revision-";
        heading.textContent = `${revisionLabel} Submission, Approval, and Release History`;
        heading.style.margin = "0 0 8px 0";
        heading.style.marginTop = "12px";
        heading.style.fontSize = "16px";
        heading.style.fontWeight = "700";
        heading.style.color = "#1F2937";
        heading.style.textAlign = "left";
        heading.style.display = "block";
        heading.style.width = "100%";
        section.appendChild(heading);

        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.fontSize = "13px";
        table.style.border = "none";

        const thead = document.createElement("thead");
        thead.innerHTML = `
          <tr>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 2, 7);font-weight:600;text-align:left;">User</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Date</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Time</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Signature</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Status</th>
          </tr>
        `;
        table.appendChild(thead);

        const tbody = document.createElement("tbody");
        rows.forEach((row) => {
          const tr = document.createElement("tr");
          tr.innerHTML = `
            <td style="padding:10px;border:1px solid #d1d5db;text-align:left;vertical-align:middle;">${row.fullName}</td>
            <td style="padding:10px;border:1px solid #d1d5db;text-align:left;vertical-align:middle;">${row.date}</td>
            <td style="padding:10px;border:1px solid #d1d5db;text-align:left;vertical-align:middle;">${row.time}</td>
            <td style="padding:6px;border:1px solid #d1d5db;text-align:center;vertical-align:middle;height:36px;min-height:36px;"></td>
            <td style="padding:10px;border:1px solid #d1d5db;text-align:left;vertical-align:middle;">${row.status}</td>
          `;
          const sigCell = tr.children[3];
          if (row.sigUrl) {
            const img = document.createElement("img");
            img.src = row.sigUrl;
            img.style.height = "28px";
            img.style.maxWidth = "80px";
            img.style.objectFit = "contain";
            img.style.display = "block";
            img.style.margin = "0 auto";
            sigCell.appendChild(img);
          } else {
            sigCell.textContent = "-";
          }
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        section.appendChild(table);
        return section;
      };

      // Diagnostic: check if original container has links BEFORE clone
      const origAnchors = container.querySelectorAll("a[href]");
      
      // Also check for Lexical link structure (span with link styling)
      const lexicalLinks = container.querySelectorAll(
        '[class*="link"], [data-lexical-decorator]',
      );
      if (lexicalLinks.length > 0) {
      }

      const clone = container.cloneNode(true);
      const cloneAnchorsRightAfter = clone.querySelectorAll("a[href]");
      clone.style.position = "fixed";
      clone.style.left = "-10000px";
      clone.style.top = "0";
      clone.style.background = "#ffffff";
      clone.style.transform = "scale(1)";
      clone.style.transformOrigin = "top left";
      clone.style.width = "1000px";
      clone.style.padding = "0";
      clone.style.margin = "0";

      hideRichTextUndoToolbarsForExport(clone);
      if (isModulePreview) {
        enforceSingleLineModulePreviewToolbarForExport(clone);
        normalizeModulePreviewInputAlignmentForExport(clone);
      }

      const hideSpecificHeaderElements = (element) => {
        const typeStr = String(effectiveType || "").trim();
        const partNumberStr = String(effectivePartNumber || "").trim();
        const allDivs = Array.from(element.querySelectorAll("div"));

        allDivs.forEach((div) => {
          const text = (div.textContent || "").trim();
          const lowerText = text.toLowerCase();

          const hasBasedOn = lowerText.includes("based on");
          const hasWhenReleased = lowerText.includes("when released");
          const hasCreatedOn =
            lowerText.includes("created on") ||
            lowerText.includes("last created on");
          const hasSubmittedForApproval =
            lowerText.includes("submitted for approval") ||
            lowerText.includes("last submitted for approval");

          const hasVersionInfo =
            hasBasedOn ||
            hasWhenReleased ||
            hasCreatedOn ||
            hasSubmittedForApproval;

          if (
            hasVersionInfo &&
            !div.closest("[data-export-block]") &&
            !div.closest("table") &&
            window.getComputedStyle(div).display !== "none"
          ) {
            const hasTitle =
              typeStr &&
              partNumberStr &&
              text.includes(typeStr) &&
              text.includes(partNumberStr);

            const hasDescription = lowerText.includes("description:");

            if (!hasTitle && !hasDescription) {
              div.style.display = "none";
            }
          }
        });

        allDivs.forEach((div) => {
          const text = (div.textContent || "").trim();
          const lowerText = text.toLowerCase();

          if (
            lowerText.includes("status:") &&
            !div.closest("[data-export-block]") &&
            !div.closest("table") &&
            window.getComputedStyle(div).display !== "none"
          ) {
            const hasTitle =
              typeStr &&
              partNumberStr &&
              text.includes(typeStr) &&
              text.includes(partNumberStr);

            const hasDescription = lowerText.includes("description:");

            if (!hasTitle && !hasDescription) {
              div.style.display = "none";
            }
          }
        });

        allDivs.forEach((div) => {
          const text = (div.textContent || "").trim();
          const lowerText = text.toLowerCase();

          if (
            lowerText.includes("revision:") &&
            lowerText.length < 100 &&
            !div.closest("[data-export-block]") &&
            !div.closest("table") &&
            window.getComputedStyle(div).display !== "none"
          ) {
            const hasTitle =
              typeStr &&
              partNumberStr &&
              text.includes(typeStr) &&
              text.includes(partNumberStr);

            const hasDescription = lowerText.includes("description:");

            if (!hasTitle && !hasDescription) {
              div.style.display = "none";
            }
          }
        });
      };
      hideSpecificHeaderElements(clone);

      const allHeadings = clone.querySelectorAll("h1, h2, h3, h4, h5, h6");
      const itemRequiredHeadingsToRemove = [];

      allHeadings.forEach((heading) => {
        const text = (heading.textContent || "").trim().toLowerCase();
        if (text === "revisions table" || text === "revision table") {
          heading.style.visibility = "hidden";
        }
        // Remove Item Required headings completely - they're now rendered by autotable
        // Check multiple variations to ensure we catch all cases
        const normalizedText = text.replace(/\s+/g, " ").trim();
        if (
          normalizedText.includes("item required") ||
          normalizedText.includes("items required") ||
          normalizedText.includes("parts required") ||
          normalizedText === "item required" ||
          normalizedText === "items required" ||
          normalizedText === "parts required"
        ) {
          // Store for removal after iteration to avoid modifying collection during iteration
          itemRequiredHeadingsToRemove.push(heading);
        }
      });

      // Remove Item Required headings from DOM completely
      // First hide them with display:none and visibility:hidden to ensure they don't appear in canvas
      itemRequiredHeadingsToRemove.forEach((heading) => {
        // Hide Item Required heading elements with display:none before removing to ensure they don't appear in canvas
        heading.style.setProperty("display", "none", "important");
        heading.style.setProperty("visibility", "hidden", "important");
        heading.remove();
      });

      // Item Required tables are now included in PDF export and rendered with autotable
      // Hide the original headings in DOM since autotable will render them

      const bomHeadings = Array.from(
        clone.querySelectorAll("h1, h2, h3, h4, h5, h6, div, span, p"),
      );
      const bomElementsToRemove = new Set();

      bomHeadings.forEach((element) => {
        const text = (element.textContent || "").trim().toLowerCase();
        if (text.endsWith(" bom")) {
          bomElementsToRemove.add(element);
        }
      });

      const bomTables = Array.from(clone.querySelectorAll("table"));
      bomTables.forEach((table) => {
        let prevSibling = table.previousElementSibling;
        let isBomTable = false;

        while (prevSibling) {
          const text = (prevSibling.textContent || "").trim().toLowerCase();
          if (text.endsWith(" bom")) {
            isBomTable = true;
            bomElementsToRemove.add(prevSibling);
            break;
          }
          if (prevSibling.querySelector) {
            const heading = prevSibling.querySelector("h1, h2, h3, h4, h5, h6");
            if (heading) {
              const headingText = (heading.textContent || "")
                .trim()
                .toLowerCase();
              if (headingText.endsWith(" bom")) {
                isBomTable = true;
                bomElementsToRemove.add(heading);
                const parent = prevSibling;
                if (
                  parent.contains(heading) &&
                  parent.nextElementSibling === table
                ) {
                  bomElementsToRemove.add(parent);
                }
                break;
              }
            }
          }
          prevSibling = prevSibling.previousElementSibling;
        }

        if (!isBomTable) {
          const parent = table.parentElement;
          if (parent) {
            const headingInParent = parent.querySelector(
              "h1, h2, h3, h4, h5, h6",
            );
            if (headingInParent) {
              const headingText = (headingInParent.textContent || "")
                .trim()
                .toLowerCase();
              if (headingText.endsWith(" bom")) {
                isBomTable = true;
                bomElementsToRemove.add(headingInParent);
                const children = Array.from(parent.children);
                if (
                  children.length === 2 &&
                  children.includes(headingInParent) &&
                  children.includes(table)
                ) {
                  bomElementsToRemove.add(parent);
                }
              }
            }
          }
        }

        if (isBomTable) {
          bomElementsToRemove.add(table);
        }
      });

      const sortedBomElements = Array.from(bomElementsToRemove).sort((a, b) => {
        if (a.contains(b)) return 1;
        if (b.contains(a)) return -1;
        return 0;
      });

      sortedBomElements.forEach((element) => {
        if (element && element.parentNode) {
          // Hide BOM heading elements with display:none before removing to ensure they don't appear in canvas
          if (element.tagName && /^H[1-6]$/i.test(element.tagName)) {
            const text = (element.textContent || "").trim().toLowerCase();
            if (text.endsWith(" bom")) {
              element.style.setProperty("display", "none", "important");
              element.style.setProperty("visibility", "hidden", "important");
            }
          }
          element.remove();
        }
      });

      const signatureSection = buildSignatureHistorySection();
      if (signatureSection) {
        const targetTable = clone.querySelector("table");
        if (targetTable?.parentNode) {
          const parentNode = targetTable.parentNode;
          parentNode.insertBefore(signatureSection, targetTable);

          revisionTableElement = revisionTableElement || targetTable;

          const revisionHeading = Array.from(
            parentNode.querySelectorAll("h1, h2, h3, h4, h5, h6"),
          ).find((headingEl) =>
            (headingEl.textContent || "")
              .toLowerCase()
              .includes("revisions table"),
          );

          const headingContainer = revisionHeading
            ? revisionHeading.parentElement
            : null;

          if (
            headingContainer &&
            headingContainer.parentNode === parentNode &&
            headingContainer !== signatureSection.nextSibling
          ) {
            parentNode.insertBefore(
              headingContainer,
              signatureSection.nextSibling,
            );
          }

          if (revisionHeading) {
            revisionHeadingElement = revisionHeading;
          }
        } else {
          clone.insertBefore(signatureSection, clone.firstChild);
        }
      }

      const trainingLogHeadings = Array.from(
        clone.querySelectorAll("h1, h2, h3, h4, h5, h6, div, span, p"),
      );
      const trainingLogElementsToRemove = new Set();

      trainingLogHeadings.forEach((element) => {
        const text = (element.textContent || "").trim().toLowerCase();
        if (text === "training log" || text === "traininglog") {
          trainingLogElementsToRemove.add(element);
        }
      });

      const trainingLogTables = Array.from(clone.querySelectorAll("table"));
      trainingLogTables.forEach((table) => {
        let prevSibling = table.previousElementSibling;
        let isTrainingLogTable = false;

        while (prevSibling) {
          const text = (prevSibling.textContent || "").trim().toLowerCase();
          if (text === "training log" || text === "traininglog") {
            isTrainingLogTable = true;
            trainingLogElementsToRemove.add(prevSibling);
            break;
          }
          if (prevSibling.querySelector) {
            const heading = prevSibling.querySelector("h1, h2, h3, h4, h5, h6");
            if (heading) {
              const headingText = (heading.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText === "training log" ||
                headingText === "traininglog"
              ) {
                isTrainingLogTable = true;
                trainingLogElementsToRemove.add(heading);
                const parent = prevSibling;
                if (
                  parent.contains(heading) &&
                  parent.nextElementSibling === table
                ) {
                  trainingLogElementsToRemove.add(parent);
                }
                break;
              }
            }
          }
          prevSibling = prevSibling.previousElementSibling;
        }

        if (!isTrainingLogTable) {
          const parent = table.parentElement;
          if (parent) {
            const headingInParent = parent.querySelector(
              "h1, h2, h3, h4, h5, h6",
            );
            if (headingInParent) {
              const headingText = (headingInParent.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText === "training log" ||
                headingText === "traininglog"
              ) {
                isTrainingLogTable = true;
                trainingLogElementsToRemove.add(headingInParent);
                const children = Array.from(parent.children);
                if (
                  children.length === 2 &&
                  children.includes(headingInParent) &&
                  children.includes(table)
                ) {
                  trainingLogElementsToRemove.add(parent);
                }
              }
            }
          }
        }

        if (isTrainingLogTable) {
          trainingLogElementsToRemove.add(table);
        }
      });

      const sortedElements = Array.from(trainingLogElementsToRemove).sort(
        (a, b) => {
          if (a.contains(b)) return 1;
          if (b.contains(a)) return -1;
          return 0;
        },
      );

      sortedElements.forEach((element) => {
        if (element && element.parentNode) {
          element.remove();
        }
      });

      const releaseHistoryPattern =
        /Revision\s+\w+\s+(Release|Archive)\s+History/i;
      const allElements = Array.from(clone.querySelectorAll("*"));
      const toRemove = new Set();

      allElements.forEach((element) => {
        const text = (element.textContent || "").replace(/\s+/g, " ").trim();

        if (releaseHistoryPattern.test(text)) {
          let current = element;
          while (current && current !== clone) {
            const currentText = (current.textContent || "")
              .replace(/\s+/g, " ")
              .trim();

            const hasTable = current.querySelector("table") !== null;
            if (
              releaseHistoryPattern.test(currentText) &&
              (currentText.includes("Created on") ||
                currentText.includes("Submitted") ||
                currentText.includes("Approved") ||
                currentText.includes("Released")) &&
              !hasTable
            ) {
              const parent = current.parentElement;
              if (!parent || parent === clone) {
                if (!hasTable) {
                  toRemove.add(current);
                }
                break;
              }

              const parentText = (parent.textContent || "")
                .replace(/\s+/g, " ")
                .trim();
              if (!releaseHistoryPattern.test(parentText)) {
                if (!hasTable) {
                  toRemove.add(current);
                }
                break;
              }
            }

            current = current.parentElement;
          }
        }
      });

      const allDivs = Array.from(clone.querySelectorAll("div"));
      allDivs.forEach((div) => {
        const text = (div.textContent || "").replace(/\s+/g, " ").trim();
        const hasTable = div.querySelector("table") !== null;

        if (
          (text.includes("Release History") ||
            text.includes("Archive History")) &&
          (text.includes("Created on") ||
            text.includes("Submitted") ||
            text.includes("Approved") ||
            text.includes("Released")) &&
          !hasTable
        ) {
          let container = div;
          let bestContainer = div;
          while (container && container !== clone) {
            const containerText = (container.textContent || "")
              .replace(/\s+/g, " ")
              .trim();
            const containerHasTable = container.querySelector("table") !== null;

            if (
              (containerText.includes("Release History") ||
                containerText.includes("Archive History")) &&
              (containerText.includes("Created on") ||
                containerText.includes("Submitted") ||
                containerText.includes("Approved") ||
                containerText.includes("Released")) &&
              !containerHasTable
            ) {
              bestContainer = container;
            }
            const parent = container.parentElement;
            if (!parent || parent === clone) {
              break;
            }
            const parentText = (parent.textContent || "")
              .replace(/\s+/g, " ")
              .trim();
            const parentHasTable = parent.querySelector("table") !== null;

            if (
              !(
                parentText.includes("Release History") ||
                parentText.includes("Archive History")
              ) ||
              parentHasTable
            ) {
              bestContainer = container;
              break;
            }
            container = parent;
          }
          if (!bestContainer.querySelector("table")) {
            toRemove.add(bestContainer);
          }
        }
      });

      toRemove.forEach((container) => {
        if (container && container.parentNode) {
          if (!container.querySelector("table")) {
            container.remove();
          }
        }
      });

      document.body.appendChild(clone);

      const remainingElements = Array.from(clone.querySelectorAll("*"));
      remainingElements.forEach((element) => {
        const text = (element.textContent || "").trim().toLowerCase();

        const isInBOMSection =
          element.closest("[data-export-block='bom']") ||
          (element.parentElement &&
            element.parentElement.querySelector("h3") &&
            element.parentElement
              .querySelector("h3")
              .textContent.trim()
              .toLowerCase()
              .endsWith(" bom"));
        if (
          isInBOMSection &&
          (text === "training log" || text === "traininglog")
        ) {
          element.remove();
          return;
        }

        if (text === "training log" || text === "traininglog") {
          const isNewTitle =
            element.closest("[data-export-block='training-log']") ||
            (element.tagName === "H3" &&
              element.textContent.trim() === "Training log" &&
              element.nextElementSibling &&
              element.nextElementSibling.tagName === "TABLE");
          if (!isNewTitle) {
            element.remove();
          }
        }
        if (text.endsWith(" bom") && text !== "training log") {
          const isNewBomTitle =
            element.tagName === "H3" &&
            element.textContent.trim().endsWith(" BOM") &&
            element.nextElementSibling &&
            element.nextElementSibling.tagName === "TABLE";
          if (!isNewBomTitle) {
            element.remove();
          }
        }
      });

      let targetRevisionTable = revisionTableElement;
      if (!targetRevisionTable) {
        const allTables = Array.from(clone.querySelectorAll("table"));
        for (const table of allTables) {
          const isSignatureTable = table.closest(
            "[data-export-block='signature-history']",
          );
          if (!isSignatureTable) {
            targetRevisionTable = table;
            break;
          }
        }
      }

      // Module preview has no Document "Revisions Table"; fallback otherwise picks the first
      // <table> in the clone — often a Lexical table inside Short Description — and inserts
      // this synthetic Training Log *inside* that subtree (wrong). Skip for module PDF export.
      if (
        (!isModulePreview || isZipExport) &&
        targetRevisionTable &&
        targetRevisionTable.parentNode
      ) {
        const parentNode = targetRevisionTable.parentNode;

        const trainingLogSection = document.createElement("div");
        trainingLogSection.setAttribute("data-export-block", "training-log");
        trainingLogSection.style.margin = "12px 0 16px 0";
        trainingLogSection.style.padding = "0";
        trainingLogSection.style.display = "block";
        trainingLogSection.style.visibility = "visible";
        trainingLogSection.style.width = "100%";

        const title = document.createElement("h3");
        title.textContent = "Training log";
        // title.style.margin = "0 0 12px 0";
        title.style.marginTop = "0";
        title.style.fontSize = "16px";
        title.style.fontWeight = "700";
        title.style.color = "#1F2937";
        title.style.display = "block";
        title.style.visibility = "visible";
        trainingLogSection.appendChild(title);

        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.fontSize = "13px";
        table.style.border = "none";
        table.style.display = "table";
        table.style.visibility = "visible";
        table.style.marginTop = "8px";
        table.style.marginBottom = "0";

        const thead = document.createElement("thead");
        thead.innerHTML = `
          <tr>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Date of Training</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Trainer</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:center;">Trainer Signature</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Trainee</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:center;">Trainee Signature</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Revision Trained</th>
            <th style="background:#000000;color:#ffffff;padding:10px;border:1px solid rgb(0, 0, 0);font-weight:600;text-align:left;">Training Status of Trainee</th>
          </tr>
        `;
        table.appendChild(thead);

        const tbody = document.createElement("tbody");
        const trainingLogTdBase =
          "padding:10px;border:1px solid #d1d5db;vertical-align:middle;";
        const { rows: tlPdfRows, emptyMessage: tlEmptyMessage } =
          computeTrainingLogExportRowsForPdf({
            trainingLogs: Array.isArray(trainingLogsData) ? trainingLogsData : [],
            revisionData: effectiveRevisionData,
            procedureRevisionId: effectiveRevisionId,
            currentRev: effectiveMyRevision,
            allNodesData: allNodesDataForTrainingLog,
          });
        if (tlPdfRows.length === 0) {
          const tr = document.createElement("tr");
          tr.innerHTML = `
            <td colspan="7" style="${trainingLogTdBase}text-align:left;color:#6c757d;">${tlEmptyMessage || "No training records for this procedure."}</td>
          `;
          tbody.appendChild(tr);
        } else {
          tlPdfRows.forEach((r) => {
            const tr = document.createElement("tr");
            tr.innerHTML = `
              <td style="${trainingLogTdBase}text-align:left;">${r.date}</td>
              <td style="${trainingLogTdBase}text-align:left;">${r.trainer}</td>
              <td style="${trainingLogTdBase}text-align:center;">${r.trainerSigHtml}</td>
              <td style="${trainingLogTdBase}text-align:left;">${r.trainee}</td>
              <td style="${trainingLogTdBase}text-align:center;">${r.traineeSigHtml}</td>
              <td style="${trainingLogTdBase}text-align:left;">${r.revisionTrained}</td>
              <td style="${trainingLogTdBase}text-align:left;">${r.status}</td>
            `;
            tbody.appendChild(tr);
          });
        }
        table.appendChild(tbody);

        trainingLogSection.appendChild(table);

        if (targetRevisionTable.nextSibling) {
          parentNode.insertBefore(
            trainingLogSection,
            targetRevisionTable.nextSibling,
          );
        } else {
          parentNode.appendChild(trainingLogSection);
        }
      }

      if (!isModulePreview || isZipExport) {
        let trainingLogTableElement = null;
        const allH3Headings = Array.from(clone.querySelectorAll("h3"));
        for (const heading of allH3Headings) {
          const text = (heading.textContent || "").trim().toLowerCase();
          if (text === "training log") {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              if (nextSibling.tagName === "TABLE") {
                trainingLogTableElement = nextSibling;
                break;
              }
              nextSibling = nextSibling.nextElementSibling;
            }
            if (!trainingLogTableElement) {
              const parent = heading.parentElement;
              if (parent) {
                const tableInParent = parent.querySelector("table");
                if (tableInParent && parent.contains(heading)) {
                  trainingLogTableElement = tableInParent;
                }
              }
            }
            if (trainingLogTableElement) break;
          }
        }

        if (trainingLogTableElement && trainingLogTableElement.parentNode) {
          const parentNode = trainingLogTableElement.parentNode;

        const bomSection = document.createElement("div");
        bomSection.setAttribute("data-export-block", "bom");
        // No bottom margin — section spacing is controlled in applySectionSpacing (avoids gap before Section 1).
        bomSection.style.margin = "16px 0 0 0";
        bomSection.style.padding = "0";
        bomSection.style.display = "block";
        bomSection.style.visibility = "visible";
        bomSection.style.width = "100%";
        bomSection.style.setProperty("overflow", "visible", "important");
        bomSection.style.setProperty("page-break-inside", "auto", "important");

        const bomTitle = document.createElement("h3");
        const trimmedType = String(effectiveType || "").trim();
        bomTitle.textContent = trimmedType ? `${trimmedType} BOM` : "BOM";
        bomTitle.style.margin = "0 0 12px 0";
        bomTitle.style.marginTop = "0";
        bomTitle.style.fontSize = "16px";
        bomTitle.style.fontWeight = "700";
        bomTitle.style.color = "#1F2937";
        bomTitle.style.display = "block";
        bomTitle.style.visibility = "visible";
        bomSection.appendChild(bomTitle);

        const bomTableElement = document.createElement("table");
        bomTableElement.className = "table";
        bomTableElement.style.width = "100%";
        bomTableElement.style.borderCollapse = "collapse";
        bomTableElement.style.fontSize = "13px";
        bomTableElement.style.border = "none";
        bomTableElement.style.display = "table";
        bomTableElement.style.visibility = "visible";

        const bomThead = document.createElement("thead");
        const bomThBase =
          "background:#000000;color:#ffffff;padding:6px 8px;border:2px solid #000;font-weight:600;text-align:left;font-size:13px;line-height:1.2;";
        bomThead.innerHTML = `
          <tr>
            <th style="${bomThBase}">Type</th>
            <th style="${bomThBase}">Item Code</th>
            <th style="${bomThBase}">Description</th>
            <th style="${bomThBase}">Qty Per</th>
            <th style="${bomThBase}">Unit</th>
          </tr>
        `;
        bomTableElement.appendChild(bomThead);

        const bomTbody = document.createElement("tbody");
        if (Array.isArray(bomTableData) && bomTableData.length > 0) {
          const sortedBom = bomTableData.slice().sort((a, b) => {
            const orderA = parseInt(a.orders || 0, 10);
            const orderB = parseInt(b.orders || 0, 10);
            if (orderA !== orderB) return orderA - orderB;

            const partNumberA = a.part_number || "";
            const partNumberB = b.part_number || "";

            const splitPart = (str) => {
              const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
              if (!match) return { num: NaN, rest: str };
              return {
                num: match[1] ? parseInt(match[1], 10) : NaN,
                rest: match[2] || str,
              };
            };

            const aSplit = splitPart(partNumberA);
            const bSplit = splitPart(partNumberB);

            if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
            if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
            if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
              if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num;
              return aSplit.rest.localeCompare(bSplit.rest);
            }
            return partNumberA.localeCompare(partNumberB);
          });

          sortedBom.forEach((row) => {
            const tr = document.createElement("tr");
            const descriptionText = stripHtmlPreserveFormatting(
              row.part_description || "",
            )
              .replace(/\n+/g, " ")
              .trim();

            const bomTdBase =
              "padding:8px;border:2px solid #000;text-align:left;vertical-align:middle;font-size:13px;";
            tr.innerHTML = `
              <td style="${bomTdBase}">${row.type_name || ""}</td>
              <td style="${bomTdBase}">${row.part_number || ""}</td>
              <td style="${bomTdBase}">${descriptionText || ""}</td>
              <td style="${bomTdBase}">${row.qty === 0 ? "" : row.qty || ""}</td>
              <td style="${bomTdBase}">${row.unit_name || ""}</td>
            `;
            bomTbody.appendChild(tr);
          });
        }
        bomTableElement.appendChild(bomTbody);

        bomSection.appendChild(bomTableElement);

        if (trainingLogTableElement.nextSibling) {
          parentNode.insertBefore(
            bomSection,
            trainingLogTableElement.nextSibling,
          );
        } else {
          parentNode.appendChild(bomSection);
        }
        }
        // Fallback: if Training Log table anchor is missing, still inject BOM so PDF vector
        // extraction can find/render headers for empty BOM exports.
        else {
        const bomTitleText = (() => {
          const trimmedType = String(effectiveType || "").trim();
          return trimmedType ? `${trimmedType} BOM` : "BOM";
        })();

        let insertionParent = null;
        let insertBeforeNode = null;
        const trainingLogHeading = Array.from(clone.querySelectorAll("h3")).find(
          (h) => (h.textContent || "").trim().toLowerCase() === "training log",
        );

        if (trainingLogHeading?.parentNode) {
          insertionParent = trainingLogHeading.parentNode;
          insertBeforeNode = trainingLogHeading.nextSibling;
        } else {
          insertionParent = clone;
        }

        if (insertionParent) {
          const bomSection = document.createElement("div");
          bomSection.setAttribute("data-export-block", "bom");
          bomSection.style.margin = "16px 0 0 0";
          bomSection.style.padding = "0";
          bomSection.style.display = "block";
          bomSection.style.visibility = "visible";
          bomSection.style.width = "100%";
          bomSection.style.setProperty("overflow", "visible", "important");
          bomSection.style.setProperty(
            "page-break-inside",
            "auto",
            "important",
          );

          const bomTitle = document.createElement("h3");
          bomTitle.textContent = bomTitleText;
          bomTitle.style.margin = "0 0 12px 0";
          bomTitle.style.marginTop = "0";
          bomTitle.style.fontSize = "16px";
          bomTitle.style.fontWeight = "700";
          bomTitle.style.color = "#1F2937";
          bomTitle.style.display = "block";
          bomTitle.style.visibility = "visible";
          bomSection.appendChild(bomTitle);

          const bomTableElement = document.createElement("table");
          bomTableElement.className = "table";
          bomTableElement.style.width = "100%";
          bomTableElement.style.borderCollapse = "collapse";
          bomTableElement.style.fontSize = "13px";
          bomTableElement.style.border = "none";
          bomTableElement.style.display = "table";
          bomTableElement.style.visibility = "visible";

          const bomThead = document.createElement("thead");
          const bomThBase =
            "background:#000000;color:#ffffff;padding:6px 8px;border:2px solid #000;font-weight:600;text-align:left;font-size:13px;line-height:1.2;";
          bomThead.innerHTML = `
            <tr>
              <th style="${bomThBase}">Type</th>
              <th style="${bomThBase}">Item Code</th>
              <th style="${bomThBase}">Description</th>
              <th style="${bomThBase}">Qty Per</th>
              <th style="${bomThBase}">Unit</th>
            </tr>
          `;
          bomTableElement.appendChild(bomThead);

          const bomTbody = document.createElement("tbody");
          if (Array.isArray(bomTableData) && bomTableData.length > 0) {
            const sortedBom = bomTableData.slice().sort((a, b) => {
              const orderA = parseInt(a.orders || 0, 10);
              const orderB = parseInt(b.orders || 0, 10);
              if (orderA !== orderB) return orderA - orderB;

              const partNumberA = a.part_number || "";
              const partNumberB = b.part_number || "";

              const splitPart = (str) => {
                const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
                if (!match) return { num: NaN, rest: str };
                return {
                  num: match[1] ? parseInt(match[1], 10) : NaN,
                  rest: match[2] || str,
                };
              };

              const aSplit = splitPart(partNumberA);
              const bSplit = splitPart(partNumberB);

              if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
              if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
              if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                if (aSplit.num !== bSplit.num) return aSplit.num - bSplit.num;
                return aSplit.rest.localeCompare(bSplit.rest);
              }
              return partNumberA.localeCompare(partNumberB);
            });

            sortedBom.forEach((row) => {
              const tr = document.createElement("tr");
              const descriptionText = stripHtmlPreserveFormatting(
                row.part_description || "",
              )
                .replace(/\n+/g, " ")
                .trim();

              const bomTdBase =
                "padding:8px;border:2px solid #000;text-align:left;vertical-align:middle;font-size:13px;";
              tr.innerHTML = `
                <td style="${bomTdBase}">${row.type_name || ""}</td>
                <td style="${bomTdBase}">${row.part_number || ""}</td>
                <td style="${bomTdBase}">${descriptionText || ""}</td>
                <td style="${bomTdBase}">${row.qty === 0 ? "" : row.qty || ""}</td>
                <td style="${bomTdBase}">${row.unit_name || ""}</td>
              `;
              bomTbody.appendChild(tr);
            });
          }
          bomTableElement.appendChild(bomTbody);
          bomSection.appendChild(bomTableElement);

          if (insertBeforeNode) {
            insertionParent.insertBefore(bomSection, insertBeforeNode);
          } else {
            insertionParent.appendChild(bomSection);
          }
        }
        }
      }

      const allTablesAfterCleanup = Array.from(clone.querySelectorAll("table"));

      const tablePositions = [];
      allTablesAfterCleanup.forEach((table, index) => {
        const thead = table.querySelector("thead");
        if (thead) {
          const theadText = (thead.textContent || "").toLowerCase();

          if (
            theadText.includes("type") &&
            (theadText.includes("item code") ||
              theadText.includes("description")) &&
            theadText.includes("qty per")
          ) {
            tablePositions.push({ table, type: "BOM", index });
          } else if (
            theadText.includes("date of training") &&
            (theadText.includes("trainer") || theadText.includes("trainee"))
          ) {
            tablePositions.push({ table, type: "TRAINING_LOG", index });
          }
        }
      });

      tablePositions.forEach(({ table, type, index }) => {
        if (type === "TRAINING_LOG") {
          if (table.closest("[data-export-block='training-log']")) {
            return;
          }
          const bomBeforeThis = tablePositions.some(
            ({ type: otherType, index: otherIndex }) =>
              otherType === "BOM" && otherIndex < index,
          );

          let prevElement = table.previousElementSibling;
          let foundBOMBefore = bomBeforeThis;

          while (!foundBOMBefore && prevElement) {
            if (prevElement.tagName === "TABLE") {
              const prevThead = prevElement.querySelector("thead");
              if (prevThead) {
                const prevTheadText = (
                  prevThead.textContent || ""
                ).toLowerCase();
                if (
                  prevTheadText.includes("type") &&
                  (prevTheadText.includes("item code") ||
                    prevTheadText.includes("description")) &&
                  prevTheadText.includes("qty per")
                ) {
                  foundBOMBefore = true;
                  break;
                }
              }
            } else if (prevElement.querySelector) {
              const bomTableInPrev = prevElement.querySelector("table");
              if (bomTableInPrev) {
                const bomThead = bomTableInPrev.querySelector("thead");
                if (bomThead) {
                  const bomTheadText = (
                    bomThead.textContent || ""
                  ).toLowerCase();
                  if (
                    bomTheadText.includes("type") &&
                    (bomTheadText.includes("item code") ||
                      bomTheadText.includes("description")) &&
                    bomTheadText.includes("qty per")
                  ) {
                    foundBOMBefore = true;
                    break;
                  }
                }
              }
            }

            const bomHeading =
              prevElement.querySelector && prevElement.querySelector("h3");
            if (bomHeading) {
              const headingText = (bomHeading.textContent || "")
                .trim()
                .toLowerCase();
              if (headingText.endsWith(" bom")) {
                foundBOMBefore = true;
                break;
              }
            }

            if (prevElement.tagName === "H3") {
              const headingText = (prevElement.textContent || "")
                .trim()
                .toLowerCase();
              if (headingText.endsWith(" bom")) {
                foundBOMBefore = true;
                break;
              }
            }

            prevElement = prevElement.previousElementSibling;
          }

          if (foundBOMBefore) {
            let prevSibling = table.previousElementSibling;
            if (prevSibling && prevSibling.tagName === "H3") {
              const headingText = (prevSibling.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText === "training log" ||
                headingText === "traininglog"
              ) {
                prevSibling.remove();
              }
            }
            const parent = table.parentElement;
            if (parent) {
              const headingInParent = parent.querySelector("h3");
              if (
                headingInParent &&
                table.compareDocumentPosition(headingInParent) &
                  Node.DOCUMENT_POSITION_PRECEDING
              ) {
                const headingText = (headingInParent.textContent || "")
                  .trim()
                  .toLowerCase();
                if (
                  headingText === "training log" ||
                  headingText === "traininglog"
                ) {
                  const nextSibling = headingInParent.nextElementSibling;
                  if (
                    nextSibling === table ||
                    (nextSibling &&
                      nextSibling.contains &&
                      nextSibling.contains(table))
                  ) {
                    headingInParent.remove();
                  }
                }
              }
            }
            table.remove();
          }
        }
      });

      const allTheadElements = Array.from(clone.querySelectorAll("thead"));
      allTheadElements.forEach((thead) => {
        const theadText = (thead.textContent || "").toLowerCase();
        if (
          theadText.includes("date of training") &&
          (theadText.includes("trainer") || theadText.includes("trainee"))
        ) {
          const parentTable = thead.closest("table");
          if (parentTable) {
            return;
          }

          let prevElement = thead.previousElementSibling;
          let foundBOMBefore = false;

          while (prevElement) {
            if (prevElement.tagName === "TABLE") {
              const prevThead = prevElement.querySelector("thead");
              if (prevThead) {
                const prevTheadText = (
                  prevThead.textContent || ""
                ).toLowerCase();
                if (
                  prevTheadText.includes("type") &&
                  (prevTheadText.includes("item code") ||
                    prevTheadText.includes("description"))
                ) {
                  foundBOMBefore = true;
                  break;
                }
              }
            }
            prevElement = prevElement.previousElementSibling;
          }

          if (foundBOMBefore) {
            thead.remove();
          }
        }
      });

      const applyTableStyles = (element) => {
        const findTableByHeading = (headingText, headingLevel = "h3") => {
          const headings = Array.from(element.querySelectorAll(headingLevel));
          for (const heading of headings) {
            const text = (heading.textContent || "").trim().toLowerCase();
            if (headingText === "Training Log" && text === "training log") {
              let nextSibling = heading.nextElementSibling;
              while (nextSibling) {
                if (
                  nextSibling.tagName === "TABLE" ||
                  nextSibling.querySelector("table")
                ) {
                  const table =
                    nextSibling.tagName === "TABLE"
                      ? nextSibling
                      : nextSibling.querySelector("table");
                  return table;
                }
                nextSibling = nextSibling.nextElementSibling;
              }
            } else if (
              headingText === "BOM" &&
              (text === "bom" || text.endsWith(" bom"))
            ) {
              let nextSibling = heading.nextElementSibling;
              while (nextSibling) {
                const table =
                  nextSibling.tagName === "TABLE"
                    ? nextSibling
                    : nextSibling.querySelector("table");
                if (table) return table;
                nextSibling = nextSibling.nextElementSibling;
              }
            }
          }
          return null;
        };

        const trainingLogTable = findTableByHeading("Training Log", "h3");
        const bomTable = findTableByHeading("BOM", "h3");

        const allTables = element.querySelectorAll("table");
        allTables.forEach((table) => {
          const isSignatureTable = table.closest(
            "[data-export-block='signature-history']",
          );
          if (isSignatureTable) {
            return;
          }

          if (table === trainingLogTable || table === bomTable) {
            return;
          }

          table.style.width = "100%";
          table.style.borderCollapse = "collapse";
          table.style.fontSize = "13px";
          table.style.border = "none";

          // If the table is inside rich text (Lexical editor), force it to clear any
          // preceding floated images so it never wraps beside them in the PDF.
          // const richTextAncestor = table.closest(
          //   ".rich-text-content-display, .editor-shell"
          // );
          // if (richTextAncestor) {
          //   table.style.setProperty("clear", "both", "important");
          //   table.style.setProperty("display", "block", "important");
          //   // Also clear the immediate div wrapper (e.g. PlaygroundEditorTheme__tableScrollableWrapper)
          //   const scrollWrapper = table.parentElement;
          //   if (
          //     scrollWrapper &&
          //     scrollWrapper.tagName === "DIV" &&
          //     scrollWrapper !== richTextAncestor
          //   ) {
          //     scrollWrapper.style.setProperty("clear", "both", "important");
          //     scrollWrapper.style.setProperty("display", "block", "important");
          //     scrollWrapper.style.setProperty("width", "100%", "important");
          //   }
          // }

          const allThs = table.querySelectorAll("thead tr th");
          allThs.forEach((th) => {
            th.style.background = "#000000";
            th.style.color = "#ffffff";
            th.style.padding = "10px";
            th.style.border = "1px solid rgb(0, 0, 0)";
            th.style.fontWeight = "600";
            const thText = (th.textContent || "").toLowerCase();
            if (thText.includes("signature")) {
              th.style.textAlign = "center";
            } else {
              th.style.textAlign = "left";
            }
          });

          const allTds = table.querySelectorAll("tbody tr td");
          allTds.forEach((td) => {
            td.style.padding = "10px";
            td.style.border = "1px solid #d1d5db";
            td.style.verticalAlign = "middle";
            td.style.backgroundColor = "#ffffff";

            const row = td.closest("tr");
            const parentTable = td.closest("table");
            if (row && parentTable) {
              const headerRow = parentTable.querySelector("thead tr");
              if (headerRow) {
                const headerCells = Array.from(
                  headerRow.querySelectorAll("th"),
                );
                const columnIndex = Array.from(
                  row.querySelectorAll("td"),
                ).indexOf(td);
                const headerCell = headerCells[columnIndex];
                if (headerCell) {
                  const headerText = (
                    headerCell.textContent || ""
                  ).toLowerCase();
                  if (headerText.includes("signature")) {
                    td.style.textAlign = "center";
                    td.style.height = "48px";
                    td.style.minHeight = "48px";
                  } else {
                    td.style.textAlign = "left";
                  }
                } else {
                  td.style.textAlign = "left";
                }
              } else {
                td.style.textAlign = "left";
              }
            } else {
              td.style.textAlign = "left";
            }
          });

          const allTrs = table.querySelectorAll("tbody tr");
          allTrs.forEach((tr) => {
            tr.style.backgroundColor = "#ffffff";
          });
        });
      };
      applyTableStyles(clone);

      const applySectionSpacing = (element) => {
        const isExportBomBlockElement = (el) => {
          if (!el || typeof el.querySelector !== "function") return false;
          const table = el.querySelector("table");
          if (!table) return false;
          const thead = table.querySelector("thead");
          if (!thead) return false;
          const t = (thead.textContent || "").toLowerCase();
          return (
            t.includes("type") &&
            t.includes("qty per") &&
            (t.includes("item code") || t.includes("description"))
          );
        };
        // BOM and section are not always adjacent (wrappers, empty nodes, etc.) — walk back siblings.
        const prevSiblingChainIncludesBom = (el) => {
          if (!el) return false;
          let p = el.previousElementSibling;
          let hops = 0;
          while (p && hops < 28) {
            if (p.getAttribute("data-export-block") === "bom") return true;
            if (isExportBomBlockElement(p)) return true;
            p = p.previousElementSibling;
            hops++;
          }
          return false;
        };
        const sectionBlocks = element.querySelectorAll(
          "[data-export-block='section']",
        );
        sectionBlocks.forEach((sectionBlock) => {
          const prevSibling = sectionBlock.previousElementSibling;

          const prevBranchHasCanvas = (startEl) => {
            if (!startEl) return false;
            let currentSibling = startEl;
            let depth = 0;
            while (currentSibling && depth < 24) {
              if (
                currentSibling.getAttribute("data-export-block") === "canvas" ||
                (typeof currentSibling.querySelector === "function" &&
                  currentSibling.querySelector("[data-export-block='canvas']"))
              ) {
                return true;
              }
              if (
                !currentSibling ||
                currentSibling.tagName === "BODY" ||
                currentSibling.getAttribute("data-export-block") === "section"
              ) {
                break;
              }
              currentSibling = currentSibling.previousElementSibling;
              depth++;
            }
            return false;
          };

          const bomPrev = prevSiblingChainIncludesBom(sectionBlock);
          const canvasPrev =
            prevSibling &&
            (prevSibling.getAttribute("data-export-block") === "canvas" ||
              (typeof prevSibling.querySelector === "function" &&
                prevSibling.querySelector("[data-export-block='canvas']")) ||
              prevBranchHasCanvas(prevSibling));

          // One vertical gap on the section wrapper only — avoid stacking large block + h2 margins
          // (was 48px + 60px after BOM → inconsistent huge gaps in PDF snapshots).
          let marginTop = "22px";
          if (bomPrev) {
            marginTop = "0px";
          } else if (canvasPrev) {
            marginTop = "28px";
          }

          // After BOM, keep a visible gap so "Section N" / Step headings are not tucked under the table
          // (margin-top 0 + tight BOM made the section title overlap the last BOM row in PDF/html2canvas).
          if (bomPrev) {
            sectionBlock.style.setProperty(
              "margin",
              "20px 0 20px 0",
              "important",
            );
          } else {
            sectionBlock.style.setProperty("margin-top", marginTop, "important");
            sectionBlock.style.setProperty(
              "margin-bottom",
              "20px",
              "important",
            );
          }
          sectionBlock.style.setProperty("padding", "0", "important");
          sectionBlock.style.setProperty("display", "block", "important");

          const sectionHeading = sectionBlock.querySelector("h2");
          if (sectionHeading) {
            sectionHeading.style.setProperty("margin-top", "0", "important");
            sectionHeading.style.setProperty(
              "margin-bottom",
              "12px",
              "important",
            );
            sectionHeading.style.setProperty("padding", "0", "important");
            sectionHeading.style.setProperty("display", "block", "important");
            sectionHeading.style.setProperty("font-size", "20px", "important");
            sectionHeading.style.setProperty(
              "font-weight",
              "bold",
              "important",
            );
            sectionHeading.style.setProperty(
              "position",
              "relative",
              "important",
            );
            sectionHeading.style.setProperty("z-index", "1000", "important");
            sectionHeading.style.setProperty(
              "break-inside",
              "avoid",
              "important",
            );
            sectionHeading.style.setProperty(
              "page-break-inside",
              "avoid",
              "important",
            );
          }

          const hrElements = sectionBlock.querySelectorAll("hr");
          hrElements.forEach((hr) => {
            hr.style.setProperty("margin-top", "5px", "important");
            hr.style.setProperty("margin-bottom", "10px", "important");
            hr.style.setProperty("display", "block", "important");
          });

          const directDivs = Array.from(sectionBlock.children).filter(
            (child) =>
              child.tagName === "DIV" &&
              !child.hasAttribute("data-export-block"),
          );
          directDivs.forEach((div, index) => {
            if (div.querySelector("h2")) {
              div.style.setProperty("margin-bottom", "10px", "important");
              return;
            }

            const text = (div.textContent || "").trim();
            if (text && text.length > 0) {
              if (
                index === 0 ||
                (index > 0 && directDivs[index - 1]?.querySelector("h2"))
              ) {
                div.style.setProperty("margin-top", "5px", "important");
                div.style.setProperty("margin-bottom", "10px", "important");
              } else {
                div.style.setProperty("margin-top", "8px", "important");
                div.style.setProperty("margin-bottom", "12px", "important");
              }
              div.style.setProperty("padding", "0", "important");
              div.style.setProperty("display", "block", "important");
            }
          });
        });

        const sectionIntroBlocks = element.querySelectorAll(
          "[data-export-block='section-intro']",
        );
        sectionIntroBlocks.forEach((introBlock) => {
          const hostSection = introBlock.closest(
            "[data-export-block='section']",
          );
          const afterBom =
            hostSection && prevSiblingChainIncludesBom(hostSection);
          if (afterBom) {
            introBlock.style.setProperty("margin-top", "4px", "important");
            introBlock.style.setProperty("margin-bottom", "10px", "important");
          } else {
            introBlock.style.setProperty("margin-top", "15px", "important");
            introBlock.style.setProperty("margin-bottom", "20px", "important");
          }
          introBlock.style.setProperty("padding", "0", "important");
          introBlock.style.setProperty("display", "block", "important");

          const children = Array.from(introBlock.children);
          children.forEach((child, index) => {
            if (index > 0) {
              child.style.setProperty("margin-top", "10px", "important");
            }
            child.style.setProperty("margin-bottom", "8px", "important");
            child.style.setProperty("display", "block", "important");
          });
        });
      };

      const applyAttachmentRichTextSpacing = (element) => {
        const blockTypesAfterAttachment = [
          "rich-text",
          "video",
          "canvas",
          "attachment",
        ];
        const selector =
          "[data-export-block='attachment'], [data-export-block='rich-text'], [data-export-block='video'], [data-export-block='canvas']";
        const allExportBlocks = Array.from(element.querySelectorAll(selector));
        allExportBlocks.sort((a, b) => {
          const pos = a.compareDocumentPosition(b);
          return pos & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : pos & Node.DOCUMENT_POSITION_PRECEDING
              ? 1
              : 0;
        });
        const isBlockAfterAttachment = (attr) =>
          attr && blockTypesAfterAttachment.includes(attr);
        allExportBlocks.forEach((block, idx) => {
          const blockType = block.getAttribute("data-export-block");
          if (isBlockAfterAttachment(blockType)) {
            const prev = allExportBlocks[idx - 1];
            if (
              prev &&
              prev.getAttribute("data-export-block") === "attachment"
            ) {
              block.style.setProperty("margin-top", "60px", "important");
              block.style.setProperty("padding-top", "8px", "important");
            }
          }
          if (blockType === "attachment") {
            const next = allExportBlocks[idx + 1];
            if (
              next &&
              isBlockAfterAttachment(next.getAttribute("data-export-block"))
            ) {
              block.style.setProperty("margin-bottom", "50px", "important");
              block.style.setProperty("padding-bottom", "10px", "important");
            }
          }
        });
      };

      applySectionSpacing(clone);

      // Pull Section 1 visually under the BOM: strip stacked bottom spacing on the BOM block and
      // any spacer wrappers so html2canvas + pagination do not reserve a huge blank band.
      const applyTightBomBlockForPdfExport = (root) => {
        if (!root?.querySelectorAll) return;
        root.querySelectorAll("[data-export-block='bom']").forEach((bom) => {
          const tbl = bom.querySelector("table");
          const tbody = tbl?.querySelector("tbody");
          const bodyRowCount = tbody
            ? tbody.querySelectorAll("tr").length
            : 0;
          const isHeaderOnlyBom = bodyRowCount === 0;

          // Small bottom cushion so the next block (Section 1) is not overlapped by table paint;
          // section margin also adds gap — avoid going back to huge blank bands before Section 1.
          bom.style.setProperty(
            "margin-bottom",
            isHeaderOnlyBom ? "24px" : "4px",
            "important",
          );
          bom.style.setProperty("padding-bottom", "0", "important");
          bom.style.setProperty("min-height", "0", "important");
          const title = bom.querySelector("h3");
          if (title) {
            title.style.setProperty("margin-bottom", "8px", "important");
          }
          if (tbl) {
            tbl.style.setProperty(
              "margin-bottom",
              isHeaderOnlyBom ? "16px" : "2px",
              "important",
            );
            tbl.style.setProperty("padding-bottom", "0", "important");
          }
        });
      };
      applyTightBomBlockForPdfExport(clone);

      const applySubstepSpacing = (element) => {
        const substepBlocks = element.querySelectorAll(
          "[data-export-block='substep']",
        );
        substepBlocks.forEach((substepBlock) => {
          // Check if previous sibling is a table (especially Item Required table)
          let prevElement = substepBlock.previousElementSibling;
          let hasTableAbove = false;

          // Check direct previous sibling or within a wrapper
          if (prevElement) {
            if (
              prevElement.tagName === "TABLE" ||
              prevElement.querySelector("table")
            ) {
              hasTableAbove = true;
            }
          }

          // If table is above, use larger margin to prevent overlap with last row
          const topMargin = hasTableAbove ? "40px" : "20px";

          substepBlock.style.setProperty("margin-top", topMargin, "important");
          substepBlock.style.setProperty("margin-bottom", "20px", "important");
          substepBlock.style.setProperty("padding", "0", "important");
          substepBlock.style.setProperty("padding-top", "10px", "important");
          substepBlock.style.setProperty("display", "block", "important");

          const substepHeader = substepBlock.querySelector("h2");
          if (!substepHeader) return;
          substepHeader.style.setProperty("break-inside", "avoid", "important");
          substepHeader.style.setProperty(
            "page-break-inside",
            "avoid",
            "important",
          );
          const substepHeaderRow =
            substepHeader.closest('div[style*="space-between"]') ||
            substepHeader.closest('div[style*="justify-content"]');
          if (substepHeaderRow) {
            substepHeaderRow.style.setProperty(
              "break-inside",
              "avoid",
              "important",
            );
            substepHeaderRow.style.setProperty(
              "page-break-inside",
              "avoid",
              "important",
            );
          }

          const treePathElement =
            substepBlock.querySelector("div[style*='color'][style*='#888']") ||
            Array.from(substepBlock.querySelectorAll("div")).find((div) => {
              const text = (div.textContent || "").trim();
              return text.includes("Section") && text.includes("Substep");
            });

          let headerContainer = null;
          let currentParent = substepHeader.parentNode;
          while (currentParent && currentParent !== substepBlock) {
            if (currentParent.parentNode === substepBlock) {
              headerContainer = currentParent;
              break;
            }
            currentParent = currentParent.parentNode;
          }

          let descriptionElement = null;
          if (headerContainer) {
            const allDivs = Array.from(headerContainer.querySelectorAll("div"));
            for (const div of allDivs) {
              if (div === treePathElement || div.contains(substepHeader)) {
                continue;
              }
              const style = window.getComputedStyle(div);
              const hasPreWrap =
                style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre";
              const inlineStyle = div.getAttribute("style") || "";
              const hasPreWrapInline =
                inlineStyle.includes("pre-wrap") ||
                inlineStyle.includes("white-space");
              const text = (div.textContent || "").trim();
              const isTreePath =
                text.includes("Section") && text.includes("Substep");

              if (
                (hasPreWrap || hasPreWrapInline) &&
                !isTreePath &&
                text.length > 0
              ) {
                if (div.parentNode === headerContainer) {
                  descriptionElement = div;
                  break;
                }
              }
            }

            if (!descriptionElement) {
              const flexContainer =
                substepHeader.closest('div[style*="display"]') ||
                Array.from(headerContainer.children).find(
                  (child) =>
                    child.contains(substepHeader) &&
                    child.style.display === "flex",
                );
              if (flexContainer && flexContainer.nextSibling) {
                let sibling = flexContainer.nextSibling;
                while (sibling) {
                  if (
                    sibling.tagName === "DIV" &&
                    sibling.textContent &&
                    sibling.textContent.trim().length > 0
                  ) {
                    const text = sibling.textContent.trim();
                    if (
                      !(text.includes("Section") && text.includes("Substep"))
                    ) {
                      descriptionElement = sibling;
                      break;
                    }
                  }
                  sibling = sibling.nextSibling;
                }
              }
            }
          }

          const allHrElements = substepBlock.querySelectorAll("hr");
          allHrElements.forEach((hr) => {
            // Never remove or move hr inside Lexical/RenderHtml content (Short description, Rich text)
            if (
              hr.closest(".rich-text-content-display") ||
              hr.closest(".editor-shell")
            ) {
              return;
            }
            if (descriptionElement) {
              const hrParent = hr.parentNode;
              const descParent = descriptionElement.parentNode;

              if (hrParent === descParent) {
                const hrIndex = Array.from(hrParent.children).indexOf(hr);
                const descIndex = Array.from(descParent.children).indexOf(
                  descriptionElement,
                );
                if (hrIndex > descIndex) {
                  hrParent.insertBefore(hr, descriptionElement);
                }
              } else {
                const hrRect = hr.getBoundingClientRect();
                const descRect = descriptionElement.getBoundingClientRect();
                if (hrRect.top > descRect.bottom + 10) {
                  if (descParent) {
                    descParent.insertBefore(hr, descriptionElement);
                  }
                }
              }
            } else {
              const hrRect = hr.getBoundingClientRect();
              const headerRect = substepHeader.getBoundingClientRect();
              if (hrRect.top > headerRect.bottom + 50) {
                hr.remove();
              }
            }
          });

          let hrElement = null;
          if (descriptionElement) {
            let checkElement = descriptionElement.previousSibling;
            while (checkElement) {
              if (checkElement.tagName === "HR") {
                hrElement = checkElement;
                break;
              }
              if (
                checkElement.contains &&
                checkElement.contains(substepHeader)
              ) {
                break;
              }
              checkElement = checkElement.previousSibling;
            }
          } else {
            if (headerContainer) {
              hrElement = headerContainer.querySelector("hr");
            }
          }

          if (!hrElement) {
            hrElement = document.createElement("hr");
            if (descriptionElement) {
              headerContainer.insertBefore(hrElement, descriptionElement);
            } else if (headerContainer) {
              const flexContainer =
                substepHeader.closest('div[style*="display"]') ||
                Array.from(headerContainer.children).find(
                  (child) =>
                    child.contains(substepHeader) &&
                    child.style.display === "flex",
                );
              if (flexContainer) {
                const isFlexContainerChild =
                  flexContainer.parentNode === headerContainer;
                if (isFlexContainerChild && flexContainer.nextSibling) {
                  headerContainer.insertBefore(
                    hrElement,
                    flexContainer.nextSibling,
                  );
                } else if (isFlexContainerChild) {
                  headerContainer.appendChild(hrElement);
                } else {
                  const flexContainerInHeader =
                    headerContainer.contains(flexContainer);
                  if (flexContainerInHeader) {
                    let nextChildSibling = null;
                    let current = flexContainer;
                    while (current && current !== headerContainer) {
                      if (current.parentNode === headerContainer) {
                        nextChildSibling = current.nextSibling;
                        break;
                      }
                      current = current.parentNode;
                    }
                    if (nextChildSibling) {
                      headerContainer.insertBefore(hrElement, nextChildSibling);
                    } else {
                      headerContainer.appendChild(hrElement);
                    }
                  } else {
                    headerContainer.appendChild(hrElement);
                  }
                }
              } else {
                headerContainer.appendChild(hrElement);
              }
            } else {
              const headerParent = substepHeader.parentNode;
              if (headerParent) {
                headerParent.insertBefore(hrElement, substepHeader.nextSibling);
              } else {
                substepBlock.insertBefore(hrElement, substepHeader.nextSibling);
              }
            }
          }

          hrElement.style.setProperty("margin-top", "5px", "important");
          hrElement.style.setProperty("margin-bottom", "10px", "important");
          hrElement.style.setProperty("display", "block", "important");
          hrElement.style.setProperty("border", "none", "important");
          hrElement.style.setProperty(
            "border-top",
            "1px solid #cccccc",
            "important",
          );
          hrElement.style.setProperty("width", "100%", "important");
        });
      };
      const applyStepSpacing = (element) => {
        const stepBlocks = element.querySelectorAll(
          "[data-export-block='step']",
        );
        stepBlocks.forEach((stepBlock) => {
          const stepHeader = stepBlock.querySelector("h2");
          if (stepHeader) {
            stepHeader.style.setProperty("break-inside", "avoid", "important");
            stepHeader.style.setProperty(
              "page-break-inside",
              "avoid",
              "important",
            );
          }
          const richTextWrapper = stepBlock.querySelector(
            ".rich-text-content-display",
          );
          if (!richTextWrapper) return;
          const shortDescWrapper = richTextWrapper.closest("div");
          if (!shortDescWrapper || shortDescWrapper === stepBlock) return;
          const stepDetailsP = Array.from(stepBlock.querySelectorAll("p")).find(
            (p) =>
              p.textContent && p.textContent.includes("Step Details Table"),
          );
          if (!stepDetailsP) return;
          shortDescWrapper.style.setProperty(
            "margin-bottom",
            "2px",
            "important",
          );
          stepDetailsP.style.setProperty("margin-top", "2px", "important");
          stepDetailsP.style.setProperty("margin-bottom", "50px", "important");
        });
      };
      const applyItemRequiredEmptySpacing = (element) => {
        const stepBlocks = element.querySelectorAll(
          "[data-export-block='step']",
        );
        stepBlocks.forEach((stepBlock) => {
          const tables = stepBlock.querySelectorAll("table");
          tables.forEach((table) => {
            const thead = table.querySelector("thead");
            const tbody = table.querySelector("tbody");
            if (!thead || !tbody) return;
            const headerText = (thead.textContent || "").toLowerCase();
            const isItemRequired =
              headerText.includes("type") &&
              headerText.includes("item code") &&
              headerText.includes("description") &&
              headerText.includes("qty per") &&
              headerText.includes("unit");
            if (!isItemRequired) return;
            const firstRow = tbody.querySelector("tr");
            if (!firstRow) return;
            const firstCellText = (
              firstRow.querySelector("td")?.textContent || ""
            ).trim();
            const isEmptyState = firstCellText === "No parts to display.";
            if (!isEmptyState) return;
            let prevElement = table.previousElementSibling;
            while (
              prevElement &&
              prevElement.getAttribute("data-export-block") === "canvas"
            ) {
              prevElement = prevElement.previousElementSibling;
            }
            if (prevElement) {
              prevElement.style.setProperty(
                "margin-bottom",
                "50px",
                "important",
              );
            }
          });
        });
      };
      const applyItemRequiredDynamicSpacing = (element) => {
        const stepBlocks = element.querySelectorAll(
          "[data-export-block='step']",
        );
        stepBlocks.forEach((stepBlock) => {
          const tables = stepBlock.querySelectorAll("table");
          tables.forEach((table) => {
            const thead = table.querySelector("thead");
            const tbody = table.querySelector("tbody");
            if (!thead || !tbody) return;
            const headerText = (thead.textContent || "").toLowerCase();
            const isItemRequired =
              headerText.includes("type") &&
              headerText.includes("item code") &&
              headerText.includes("description") &&
              headerText.includes("qty per") &&
              headerText.includes("unit");
            if (!isItemRequired) return;
            let prevElement = table.previousElementSibling;
            while (
              prevElement &&
              prevElement.getAttribute("data-export-block") === "canvas"
            ) {
              prevElement = prevElement.previousElementSibling;
            }
            if (!prevElement) return;
            const contentHeight =
              prevElement.offsetHeight ||
              prevElement.getBoundingClientRect().height ||
              0;
            const marginBottom =
              contentHeight > 80
                ? Math.min(50 + Math.round(contentHeight * 0.15), 120)
                : 50;
            prevElement.style.setProperty(
              "margin-bottom",
              marginBottom + "px",
              "important",
            );
          });
        });
      };
      const applyPostItemRequiredBlockSpacing = (element) => {
        const stepBlocks = element.querySelectorAll("[data-export-block='step']");
        const nextBlockTypes = new Set([
          "canvas",
          "rich-text",
          "video",
          "attachment",
          "form",
          "substep",
        ]);

        const isItemRequiredTable = (table) => {
          const thead = table?.querySelector("thead");
          if (!thead) return false;
          const headerText = (thead.textContent || "").toLowerCase();
          return (
            headerText.includes("type") &&
            headerText.includes("item code") &&
            headerText.includes("description") &&
            headerText.includes("qty per") &&
            headerText.includes("unit")
          );
        };

        stepBlocks.forEach((stepBlock) => {
          const tables = Array.from(stepBlock.querySelectorAll("table")).filter(
            isItemRequiredTable,
          );
          tables.forEach((table) => {
            let next = table.nextElementSibling;
            let hops = 0;
            while (next && hops < 12) {
              const t = next.getAttribute("data-export-block");
              if (t && nextBlockTypes.has(t)) {
                next.style.setProperty("margin-top", "40px", "important");
                next.style.setProperty("padding-top", "8px", "important");
                break;
              }
              // Handle plain text wrappers (e.g. "new canvas" short description) that
              // do not carry data-export-block attributes.
              if (!t) {
                const hasVisibleText =
                  (next.textContent || "").replace(/\s+/g, "").length > 0;
                const hasMedia =
                  typeof next.querySelector === "function" &&
                  !!next.querySelector("img, canvas, video, iframe");
                if (hasVisibleText || hasMedia) {
                  next.style.setProperty("margin-top", "40px", "important");
                  next.style.setProperty("padding-top", "8px", "important");
                  break;
                }
              }
              if (t === "table" || next.tagName === "TABLE") {
                // Don't walk into the next table block.
                break;
              }
              if (t === "step" || t === "section") break;
              next = next.nextElementSibling;
              hops += 1;
            }
          });
        });
      };
      applySubstepSpacing(clone);

      const inlineCanvasSnapshots = async (element) => {
        const canvasImageNodes = element.querySelectorAll(
          "[data-export-block='canvas'] img, img[src*='canvas-snapshot-']",
        );
        if (!canvasImageNodes.length) {
          return;
        }

        const canvasBlocks = element.querySelectorAll(
          "[data-export-block='canvas']",
        );
        canvasBlocks.forEach((block) => {
          block.style.display = "block";
          block.style.visibility = "visible";
          block.style.opacity = "1";
        });

        await Promise.all(
          Array.from(canvasImageNodes).map(async (img) => {
            if (!img) return;
            const currentSrc = img.currentSrc || img.src || "";
            if (!currentSrc) {
              return;
            }

            if (currentSrc.startsWith("data:image")) {
              return new Promise((resolve) => {
                const testImg = new Image();
                testImg.crossOrigin = "anonymous";
                testImg.onload = () => resolve();
                testImg.onerror = () => {
                  resolve();
                };
                testImg.src = currentSrc;
              });
            }

            try {
              img.style.display = "block";
              img.style.visibility = "visible";
              img.style.opacity = "1";

              const dataUrl = await getCompanyLogoDataUrl(currentSrc);

              if (dataUrl && dataUrl.startsWith("data:image")) {
                const originalSrc = img.src;
                img.src = dataUrl;
                img.removeAttribute("srcset");

                await new Promise((resolve) => {
                  if (
                    img.complete &&
                    img.naturalWidth > 0 &&
                    img.src === dataUrl
                  ) {
                    resolve();
                    return;
                  }

                  const timeout = setTimeout(() => {
                    resolve();
                  }, 5000);

                  img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                  };
                  img.onerror = () => {
                    clearTimeout(timeout);
                    if (originalSrc) {
                      img.src = originalSrc;
                    }
                    resolve();
                  };

                  if (img.complete) {
                    clearTimeout(timeout);
                    resolve();
                  }
                });
              } else {
              }
            } catch (error) {}
          }),
        );

        await new Promise((resolve) => setTimeout(resolve, 100));
      };

      const inlineAttachmentImages = async (element) => {
        const attachmentImages = element.querySelectorAll(
          "[data-export-block='attachment'] img, img[src*='s3.'], img[src*='amazonaws.com']",
        );

        if (!attachmentImages.length) {
          return;
        }

        const attachmentBlocks = element.querySelectorAll(
          "[data-export-block='attachment']",
        );
        attachmentBlocks.forEach((block) => {
          block.style.display = "block";
          block.style.visibility = "visible";
          block.style.opacity = "1";
          // Ensure attachment blocks allow content to expand
          block.style.setProperty("overflow", "visible", "important");
          block.style.setProperty("max-height", "none", "important");
          block.style.setProperty("height", "auto", "important");
        });

        await Promise.all(
          Array.from(attachmentImages).map(async (img) => {
            if (!img) return;
            const currentSrc = img.currentSrc || img.src || "";
            if (!currentSrc) {
              return;
            }

            if (currentSrc.startsWith("data:image")) {
              return;
            }

            try {
              img.style.display = "block";
              img.style.visibility = "visible";
              img.style.opacity = "1";

              const dataUrl = await getCompanyLogoDataUrl(currentSrc);

              if (dataUrl && dataUrl.startsWith("data:image")) {
                const originalSrc = img.src;
                img.src = dataUrl;
                img.removeAttribute("srcset");

                await new Promise((resolve) => {
                  if (
                    img.complete &&
                    img.naturalWidth > 0 &&
                    img.src === dataUrl
                  ) {
                    resolve();
                    return;
                  }

                  const timeout = setTimeout(() => {
                    resolve();
                  }, 5000);

                  img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                  };
                  img.onerror = () => {
                    clearTimeout(timeout);
                    if (originalSrc) {
                      img.src = originalSrc;
                    }
                    resolve();
                  };

                  if (img.complete) {
                    clearTimeout(timeout);
                    resolve();
                  }
                });
              } else {
              }
            } catch (error) {}
          }),
        );

        await new Promise((resolve) => setTimeout(resolve, 100));
      };

      const hideChildNodeTitles = (element) => {
        const childNodeLabels = [
          "canvas",
          "rich-text",
          "attachment",
          "video",
          "form",
          "item-required",
          "items-required",
          "parts-table",
        ];

        childNodeLabels.forEach((label) => {
          const nodes = element.querySelectorAll(
            `[data-export-block='${label}']`,
          );
          nodes.forEach((nodeEl) => {
            const allHeadings = nodeEl.querySelectorAll(
              "h1, h2, h3, h4, h5, h6",
            );
            const labelPatterns = {
              "rich-text": /^rich text\s*\d*$/i,
              canvas: /^canvas\s*\d*$/i,
              attachment: /^attachment\s*\d*$/i,
              video: /^video\s*\d*$/i,
              form: /^form\s*\d*$/i,
              "item-required": /^item required$/i,
              "items-required": /^items required$/i,
              "parts-table": /^parts table$/i,
            };
            const pattern = labelPatterns[label];
            const titleHeadings = Array.from(allHeadings).filter((heading) => {
              const isInContentArea =
                heading.closest(".rich-text-content-display") ||
                heading.closest(".editor-shell");
              if (isInContentArea) return false;
              const text = (heading.textContent || "").trim();
              return pattern && pattern.test(text);
            });
            titleHeadings.forEach((heading) => {
              heading.style.setProperty("display", "none", "important");
              heading.style.setProperty("visibility", "hidden", "important");
              heading.style.setProperty("opacity", "0", "important");
              heading.style.setProperty("height", "0", "important");
              heading.style.setProperty("margin", "0", "important");
              heading.style.setProperty("padding", "0", "important");
              heading.textContent = "";
              heading.innerHTML = "";
            });

            const allDivs = nodeEl.querySelectorAll("div");
            allDivs.forEach((div) => {
              const isContentContainer =
                div.classList.contains("rich-text-content-display") ||
                div.classList.contains("editor-shell") ||
                div.closest(".rich-text-content-display") ||
                div.closest(".editor-shell");

              if (isContentContainer) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                return;
              }

              const text = (div.textContent || "").trim().toLowerCase();
              const isTitleContainer =
                (label === "rich-text" &&
                  (text.includes("rich text") ||
                    text.match(/^rich text\s+\d+/i))) ||
                (label === "attachment" &&
                  (text.includes("attachment") ||
                    text.match(/^attachment\s+\d+/i))) ||
                (label === "video" &&
                  (text.includes("video") || text.match(/^video\s+\d+/i))) ||
                (label === "canvas" &&
                  (text.includes("canvas") || text.match(/^canvas\s+\d+/i))) ||
                (label === "form" &&
                  (text.includes("form") || text.match(/^form\s+\d+/i)));

              if (isTitleContainer) {
                const hasHtmlContent = div.querySelector(
                  "p, h1, h2, h3, ul, ol, table, blockquote, a, img, strong, em",
                );
                if (!hasHtmlContent) {
                  div.style.setProperty("display", "none", "important");
                  div.style.setProperty("visibility", "hidden", "important");
                  div.style.setProperty("opacity", "0", "important");
                  div.style.setProperty("height", "0", "important");
                  div.style.setProperty("margin", "0", "important");
                  div.style.setProperty("padding", "0", "important");
                }
              }
            });

            const allSpans = nodeEl.querySelectorAll("span");
            allSpans.forEach((span) => {
              const text = (span.textContent || "").trim().toLowerCase();
              const isLabelSpan =
                (label === "rich-text" &&
                  (text.includes("rich text") ||
                    text.match(/^rich text\s+\d+/i))) ||
                (label === "attachment" &&
                  (text.includes("attachment") ||
                    text.match(/^attachment\s+\d+/i))) ||
                (label === "video" &&
                  (text.includes("video") || text.match(/^video\s+\d+/i))) ||
                (label === "canvas" &&
                  (text.includes("canvas") || text.match(/^canvas\s+\d+/i))) ||
                (label === "form" &&
                  (text.includes("form") || text.match(/^form\s+\d+/i)));

              if (isLabelSpan) {
                span.style.setProperty("display", "none", "important");
                span.style.setProperty("visibility", "hidden", "important");
                span.style.setProperty("opacity", "0", "important");
              }
            });

            if (titleHeadings.length > 0) {
              let container = titleHeadings[0].parentElement;
              while (
                container &&
                container.parentElement &&
                container.parentElement !== nodeEl
              ) {
                container = container.parentElement;
              }

              if (
                container &&
                container.parentElement &&
                container.parentElement === nodeEl
              ) {
                container.style.setProperty("display", "none", "important");
                container.style.setProperty(
                  "visibility",
                  "hidden",
                  "important",
                );
                container.style.setProperty("opacity", "0", "important");
                container.style.setProperty("height", "0", "important");
                container.style.setProperty("margin", "0", "important");
                container.style.setProperty("padding", "0", "important");
              }
            }

            if (label === "rich-text") {
              const treePathElements = nodeEl.querySelectorAll(
                "div[style*='color'][style*='#888'], div[style*='font-size'][style*='14px']",
              );
              treePathElements.forEach((el) => {
                const text = (el.textContent || "").trim().toLowerCase();
                const isContentContainer =
                  el.classList.contains("rich-text-content-display") ||
                  el.classList.contains("editor-shell") ||
                  el.querySelector(".rich-text-content-display, .editor-shell");
                if (
                  text.includes("section") &&
                  (text.includes("rich text") || text.includes("step")) &&
                  !isContentContainer
                ) {
                  el.style.setProperty("display", "none", "important");
                  el.style.setProperty("visibility", "hidden", "important");
                  el.style.setProperty("opacity", "0", "important");
                  el.style.setProperty("height", "0", "important");
                  el.style.setProperty("margin", "0", "important");
                  el.style.setProperty("padding", "0", "important");
                }
              });

              const contentContainers = nodeEl.querySelectorAll(
                ".rich-text-content-display, .editor-shell",
              );
              contentContainers.forEach((container) => {
                container.style.setProperty("display", "block", "important");
                container.style.setProperty(
                  "visibility",
                  "visible",
                  "important",
                );
                container.style.setProperty("opacity", "1", "important");
              });
            }
          });
        });
      };

      const modifyDocumentHeaderForPDF = (element) => {
        let descriptionText = "";
        if (description) {
          const stripped = stripHtmlPreserveFormatting(description).trim();
          descriptionText = stripped || String(description).trim();
        }

        if (!descriptionText) return;
        const allSpans = element.querySelectorAll("span");
        let titleSpan = null;

        const typeStr = String(type || "").trim();
        const partNumberStr = String(partNumber || "").trim();

        for (const span of allSpans) {
          const text = (span.textContent || "").trim();
          if (
            typeStr &&
            partNumberStr &&
            text.includes(typeStr) &&
            text.includes(partNumberStr) &&
            !text.includes("Description:") &&
            text.length < 200
          ) {
            titleSpan = span;
            break;
          }
        }

        if (titleSpan) {
          const currentText = (titleSpan.textContent || "").trim();
          if (!currentText.includes(descriptionText)) {
            titleSpan.textContent = `${currentText}, ${descriptionText}`;
          }
        }

        allSpans.forEach((span) => {
          const text = (span.textContent || "").trim();
          if (text.startsWith("Description:")) {
            span.style.display = "none";
            const parent = span.parentElement;
            if (parent && parent.tagName !== "BODY") {
              const parentText = (parent.textContent || "").trim();
              if (parentText.startsWith("Description:")) {
                parent.style.display = "none";
              }
            }
          }
        });
      };
      modifyDocumentHeaderForPDF(clone);

      hideChildNodeTitles(clone);
      await inlineCanvasSnapshots(clone);
      await inlineAttachmentImages(clone);

      const ensureNestedElementsVisible = (element) => {
        const allExportBlocks = element.querySelectorAll("[data-export-block]");
        allExportBlocks.forEach((block) => {
          const computedStyle = window.getComputedStyle(block);
          if (computedStyle.display === "none") {
            block.style.display = "block";
          }
          if (computedStyle.visibility === "hidden") {
            block.style.visibility = "visible";
          }
          if (computedStyle.opacity === "0") {
            block.style.opacity = "1";
          }

          if (block.getAttribute("data-export-block") === "video") {
            const videos = block.querySelectorAll("video");
            videos.forEach((video) => {
              video.style.setProperty("display", "block", "important");
              video.style.setProperty("visibility", "visible", "important");
              video.style.setProperty("opacity", "1", "important");
              video.style.setProperty("width", "100%", "important");
              video.style.setProperty("max-width", "100%", "important");
              video.style.setProperty("height", "auto", "important");
            });

            const allDivs = block.querySelectorAll("div");
            allDivs.forEach((div) => {
              const text = div.textContent?.trim() || "";
              const style = window.getComputedStyle(div);

              if (
                text &&
                (text.startsWith("http://") || text.startsWith("https://"))
              ) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                div.style.setProperty("margin-top", "10px", "important");
                div.style.setProperty("color", "#000", "important");
                div.style.setProperty("font-size", "14px", "important");
              }

              if (
                style.whiteSpace === "pre-wrap" ||
                style.whiteSpace === "pre"
              ) {
                if (
                  text &&
                  !text.match(/^(Video|video)\s*\d*$/i) &&
                  !text.match(/^rich text\s*\d*$/i) &&
                  !text.match(/^attachment\s*\d*$/i) &&
                  !text.startsWith("http://") &&
                  !text.startsWith("https://")
                ) {
                  div.style.setProperty("display", "block", "important");
                  div.style.setProperty("visibility", "visible", "important");
                  div.style.setProperty("opacity", "1", "important");
                  div.style.setProperty("white-space", "pre-wrap", "important");
                  div.style.setProperty("color", "#000", "important");
                  div.style.setProperty("font-size", "14px", "important");
                  div.style.setProperty("margin-top", "4px", "important");
                }
              }
            });
          }

          if (block.getAttribute("data-export-block") === "rich-text") {
            const allDivs = Array.from(block.querySelectorAll("div"));
            allDivs.forEach((div) => {
              const style = window.getComputedStyle(div);
              const text = div.textContent?.trim() || "";

              const isDescriptionDiv =
                style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre";
              const isNotContentContainer =
                !div.classList.contains("rich-text-content-display") &&
                !div.classList.contains("editor-shell") &&
                !div.closest(".rich-text-content-display") &&
                !div.closest(".editor-shell");
              const hasMarginLeft =
                style.marginLeft &&
                (style.marginLeft.includes("5px") ||
                  style.marginLeft.includes("4px"));

              // Additional detection: check if div has text and is positioned before rich text content
              const richTextContent = block.querySelector(
                ".rich-text-content-display, .editor-shell",
              );
              const isBeforeRichTextContent =
                richTextContent &&
                (div.compareDocumentPosition(richTextContent) &
                  Node.DOCUMENT_POSITION_FOLLOWING) !==
                  0;
              const hasTextContent = text.length > 0;
              const isLikelyShortDesc =
                isBeforeRichTextContent &&
                hasTextContent &&
                isNotContentContainer &&
                !div.querySelector("h2") &&
                !div.querySelector('input[type="checkbox"]');

              const isCategoryDiv =
                style.marginLeft &&
                style.marginLeft.includes("4px") &&
                style.marginTop &&
                style.marginTop.includes("5px") &&
                !div.classList.contains("rich-text-content-display");

              if (
                (isDescriptionDiv && isNotContentContainer && hasMarginLeft) ||
                isCategoryDiv ||
                isLikelyShortDesc
              ) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                div.style.setProperty("color", "#000", "important");
                div.style.setProperty("white-space", "pre-wrap", "important");
                div.style.setProperty("position", "relative", "important");
                div.style.setProperty("z-index", "10", "important");
                div.style.setProperty(
                  "background-color",
                  "transparent",
                  "important",
                );
                div.style.setProperty("width", "auto", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("min-height", "auto", "important");
                div.style.setProperty("max-height", "none", "important");
                div.style.setProperty("max-width", "100%", "important");
                div.style.setProperty("word-break", "break-word", "important");
                div.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                div.style.setProperty("overflow", "visible", "important");
                div.style.setProperty("line-height", "1.5", "important");
                div.style.setProperty("padding", "2px", "important");
                div.style.setProperty("margin-bottom", "10px", "important");
                // Force layout recalculation for large text
                void div.offsetHeight;

                // Ensure parent elements don't hide it and allow expansion
                let parent = div.parentElement;
                let depth = 0;
                while (parent && parent !== block && depth < 10) {
                  const parentStyle = window.getComputedStyle(parent);
                  if (parentStyle.display === "none") {
                    parent.style.setProperty("display", "block", "important");
                  }
                  if (parentStyle.visibility === "hidden") {
                    parent.style.setProperty(
                      "visibility",
                      "visible",
                      "important",
                    );
                  }
                  if (parentStyle.opacity === "0") {
                    parent.style.setProperty("opacity", "1", "important");
                  }
                  if (parentStyle.overflow === "hidden") {
                    parent.style.setProperty(
                      "overflow",
                      "visible",
                      "important",
                    );
                  }
                  if (
                    parentStyle.maxHeight &&
                    parentStyle.maxHeight !== "none"
                  ) {
                    parent.style.setProperty("max-height", "none", "important");
                  }
                  if (
                    parentStyle.height &&
                    parentStyle.height !== "auto" &&
                    !parentStyle.height.includes("%")
                  ) {
                    parent.style.setProperty("height", "auto", "important");
                  }
                  parent = parent.parentElement;
                  depth++;
                }
              }
            });

            const richTextContents = block.querySelectorAll(
              ".rich-text-content-display, .editor-shell",
            );
            richTextContents.forEach((richTextContent) => {
              richTextContent.style.setProperty(
                "display",
                "block",
                "important",
              );
              richTextContent.style.setProperty(
                "visibility",
                "visible",
                "important",
              );
              richTextContent.style.setProperty("opacity", "1", "important");
              richTextContent.style.setProperty(
                "position",
                "relative",
                "important",
              );
              richTextContent.style.setProperty("z-index", "2000", "important");
              richTextContent.style.setProperty(
                "overflow",
                "visible",
                "important",
              );
              richTextContent.style.setProperty(
                "max-height",
                "none",
                "important",
              );

              const allChildren = richTextContent.querySelectorAll("*");
              allChildren.forEach((child) => {
                const isLayoutContainer =
                  child.classList.contains(
                    "PlaygroundEditorTheme__layoutContainer",
                  ) ||
                  child.getAttribute("data-lexical-layout-container") ===
                    "true";
                const isLayoutItem = child.classList.contains(
                  "PlaygroundEditorTheme__layoutItem",
                );
                const isLayoutElement = isLayoutContainer || isLayoutItem;

                child.style.setProperty("visibility", "visible", "important");
                child.style.setProperty("opacity", "1", "important");

                if (!isLayoutElement) {
                  if (
                    child.tagName === "P" ||
                    child.tagName === "DIV" ||
                    child.tagName === "HR" ||
                    ["H1", "H2", "H3", "H4", "H5", "H6"].includes(child.tagName)
                  ) {
                    child.style.setProperty("display", "block", "important");
                  } else if (
                    child.tagName === "SPAN" ||
                    child.tagName === "A" ||
                    child.tagName === "STRONG" ||
                    child.tagName === "EM"
                  ) {
                    child.style.setProperty("display", "inline", "important");
                  }
                }

                const computedColor = window.getComputedStyle(child).color;
                const isInvisibleColor =
                  computedColor === "rgba(0, 0, 0, 0)" ||
                  computedColor === "transparent" ||
                  !computedColor;
                if (isInvisibleColor) {
                  child.style.setProperty("color", "#000", "important");
                }
              });
            });
          }

          if (block.getAttribute("data-export-block") === "attachment") {
            // Ensure attachment block container allows expansion and proper spacing from tables above
            block.style.setProperty("overflow", "visible", "important");
            block.style.setProperty("max-height", "none", "important");
            block.style.setProperty("height", "auto", "important");
            block.style.setProperty("position", "relative", "important");
            block.style.setProperty("z-index", "100", "important");
            block.style.setProperty("margin-top", "30px", "important");
            let attachmentMarginBottom = "30px";
            let next = block.nextElementSibling;
            while (next) {
              const nextBlock = next.getAttribute("data-export-block");
              const hasChildNodeBelow =
                nextBlock === "rich-text" ||
                nextBlock === "video" ||
                nextBlock === "canvas" ||
                nextBlock === "attachment" ||
                next.querySelector("[data-export-block='rich-text']") ||
                next.querySelector("[data-export-block='video']") ||
                next.querySelector("[data-export-block='canvas']");
              if (hasChildNodeBelow) {
                attachmentMarginBottom = "50px";
                break;
              }
              if (nextBlock) break;
              next = next.nextElementSibling;
            }
            block.style.setProperty(
              "margin-bottom",
              attachmentMarginBottom,
              "important",
            );
            block.style.setProperty("padding-top", "20px", "important");

            const allDivs = Array.from(block.querySelectorAll("div"));
            allDivs.forEach((div) => {
              const style = window.getComputedStyle(div);
              const text = div.textContent?.trim() || "";

              const isNotContentContainer =
                !div.classList.contains("rich-text-content-display") &&
                !div.classList.contains("editor-shell") &&
                !div.closest(".rich-text-content-display") &&
                !div.closest(".editor-shell");

              // Check if this div is the heading container (has h2 or checkbox)
              const isHeadingContainer =
                div.querySelector("h2") ||
                div.querySelector('input[type="checkbox"]');

              // Short description detection - more flexible:
              // 1. Has whiteSpace: pre-wrap or pre
              // 2. Is not the heading container
              // 3. Is not a content container
              // 4. Has text content
              // 5. Either has marginLeft: 5px OR is positioned before images
              const hasPreWrap =
                style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre";
              const hasMarginLeft5px =
                style.marginLeft && style.marginLeft.includes("5px");

              // Check if this div comes before any img in the block
              const blockImages = block.querySelectorAll("img");
              const isBeforeImages =
                blockImages.length > 0 &&
                Array.from(blockImages).some(
                  (img) =>
                    (div.compareDocumentPosition(img) &
                      Node.DOCUMENT_POSITION_FOLLOWING) !==
                    0,
                );

              const hasTextContent = text.length > 0;

              // Primary detection: has pre-wrap and marginLeft 5px
              const isDescriptionDiv =
                hasPreWrap && hasMarginLeft5px && isNotContentContainer;

              // Fallback detection: has pre-wrap, is before images, has text, not heading container
              const isLikelyShortDesc =
                hasPreWrap &&
                isBeforeImages &&
                hasTextContent &&
                isNotContentContainer &&
                !isHeadingContainer;

              // Even more flexible: if it has pre-wrap and text and is not heading/content container
              const isVeryLikelyShortDesc =
                hasPreWrap &&
                hasTextContent &&
                isNotContentContainer &&
                !isHeadingContainer &&
                !div.querySelector("img"); // Not an image container

              if (
                isDescriptionDiv ||
                isLikelyShortDesc ||
                isVeryLikelyShortDesc
              ) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                div.style.setProperty("color", "#000", "important");
                div.style.setProperty("white-space", "pre-wrap", "important");
                // Set margin-left if not already set or doesn't have 5px
                if (!hasMarginLeft5px) {
                  div.style.setProperty("margin-left", "5px", "important");
                }
                div.style.setProperty("position", "relative", "important");
                div.style.setProperty("z-index", "10", "important");
                div.style.setProperty(
                  "background-color",
                  "#ffffff",
                  "important",
                );
                div.style.setProperty("width", "auto", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("min-height", "auto", "important");
                div.style.setProperty("max-height", "none", "important");
                div.style.setProperty("max-width", "100%", "important");
                div.style.setProperty("word-break", "break-word", "important");
                div.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                div.style.setProperty("overflow", "visible", "important");
                div.style.setProperty("line-height", "1.5", "important");
                div.style.setProperty("padding", "10px 5px", "important");
                div.style.setProperty("margin-top", "5px", "important");
                div.style.setProperty("margin-bottom", "20px", "important");
                // Force layout recalculation for large text
                void div.offsetHeight;

                // Ensure parent elements don't hide it and allow expansion
                let parent = div.parentElement;
                let depth = 0;
                while (parent && parent !== block && depth < 10) {
                  const parentStyle = window.getComputedStyle(parent);
                  if (parentStyle.display === "none") {
                    parent.style.setProperty("display", "block", "important");
                  }
                  if (parentStyle.visibility === "hidden") {
                    parent.style.setProperty(
                      "visibility",
                      "visible",
                      "important",
                    );
                  }
                  if (parentStyle.opacity === "0") {
                    parent.style.setProperty("opacity", "1", "important");
                  }
                  if (parentStyle.overflow === "hidden") {
                    parent.style.setProperty(
                      "overflow",
                      "visible",
                      "important",
                    );
                  }
                  if (
                    parentStyle.maxHeight &&
                    parentStyle.maxHeight !== "none"
                  ) {
                    parent.style.setProperty("max-height", "none", "important");
                  }
                  if (
                    parentStyle.height &&
                    parentStyle.height !== "auto" &&
                    !parentStyle.height.includes("%")
                  ) {
                    parent.style.setProperty("height", "auto", "important");
                  }
                  parent = parent.parentElement;
                  depth++;
                }
              }
            });
          }

          if (block.getAttribute("data-export-block") === "canvas") {
            // Add bottom margin to canvas block to prevent merging with content below
            block.style.setProperty("margin-bottom", "30px", "important");
            block.style.setProperty("padding-bottom", "0", "important");

            // First, ensure canvas images have lower z-index so they don't cover text
            // Also set canvas images to 400px width for PDF export
            const canvasImages = block.querySelectorAll("img");
            canvasImages.forEach((img) => {
              img.style.setProperty("position", "relative", "important");
              img.style.setProperty("z-index", "1", "important");
              img.style.setProperty("max-width", "400px", "important");
              img.style.setProperty("max-height", "none", "important");
              img.style.setProperty("width", "auto", "important");
              img.style.setProperty("height", "auto", "important");
              img.style.setProperty("object-fit", "contain", "important");
              img.style.setProperty("overflow", "visible", "important");
              img.style.setProperty("display", "block", "important");

              // Canvas snapshots can sit inside wrappers with constrained height/overflow.
              // Relax parent layout during export so html2canvas captures the full image
              // and pagination can create a new page instead of clipping at the current page end.
              let parent = img.parentElement;
              let parentDepth = 0;
              while (parent && parent !== block && parentDepth < 10) {
                const parentStyle = window.getComputedStyle(parent);
                if (parentStyle.overflow === "hidden") {
                  parent.style.setProperty("overflow", "visible", "important");
                }
                if (
                  parentStyle.maxHeight &&
                  parentStyle.maxHeight !== "none"
                ) {
                  parent.style.setProperty("max-height", "none", "important");
                }
                if (
                  parentStyle.height &&
                  parentStyle.height !== "auto" &&
                  !parentStyle.height.includes("%")
                ) {
                  parent.style.setProperty("height", "auto", "important");
                }
                parent = parent.parentElement;
                parentDepth += 1;
              }
            });

            const allDivs = Array.from(block.querySelectorAll("div"));
            allDivs.forEach((div) => {
              const style = window.getComputedStyle(div);
              const text = div.textContent?.trim() || "";

              // Check if this div is the heading container (has h2 or checkbox)
              const isHeadingContainer =
                div.querySelector("h2") ||
                div.querySelector('input[type="checkbox"]');

              // Short description div detection - more flexible:
              // 1. Has text content
              // 2. Is not the heading container
              // 3. Either has the expected styles OR is a direct child div that comes before images
              const hasExpectedStyles =
                (style.whiteSpace === "pre-wrap" ||
                  style.whiteSpace === "pre") &&
                style.marginLeft &&
                style.marginLeft.includes("5px");

              // Check if this div comes before any img in the block
              const isBeforeImages = (() => {
                let current = div;
                while (current && current !== block) {
                  const nextSibling = current.nextElementSibling;
                  if (nextSibling) {
                    const hasImg =
                      nextSibling.tagName === "IMG" ||
                      nextSibling.querySelector("img");
                    if (hasImg) return true;
                  }
                  current = current.parentElement;
                }
                // Also check if it's a direct child and there are images after it
                if (div.parentElement === block) {
                  let sibling = div.nextElementSibling;
                  while (sibling) {
                    if (
                      sibling.tagName === "IMG" ||
                      sibling.querySelector("img")
                    ) {
                      return true;
                    }
                    sibling = sibling.nextElementSibling;
                  }
                }
                return false;
              })();

              const isDescriptionDiv =
                text &&
                text.length > 0 &&
                !isHeadingContainer &&
                !div.classList.contains("rich-text-content-display") &&
                !div.classList.contains("editor-shell") &&
                !div.closest(".rich-text-content-display") &&
                !div.closest(".editor-shell") &&
                (hasExpectedStyles ||
                  (isBeforeImages && div.parentElement === block));

              if (isDescriptionDiv) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                div.style.setProperty("color", "#000", "important");
                div.style.setProperty("white-space", "pre-wrap", "important");
                div.style.setProperty("position", "relative", "important");
                div.style.setProperty("z-index", "10", "important");
                div.style.setProperty(
                  "background-color",
                  "transparent",
                  "important",
                );
                div.style.setProperty("width", "auto", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("min-height", "auto", "important");
                div.style.setProperty("max-width", "100%", "important");
                div.style.setProperty("word-break", "break-word", "important");
                div.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                div.style.setProperty("margin-bottom", "10px", "important");

                // Ensure parent elements don't hide it
                let parent = div.parentElement;
                let depth = 0;
                while (parent && parent !== block && depth < 10) {
                  const parentStyle = window.getComputedStyle(parent);
                  if (parentStyle.display === "none") {
                    parent.style.setProperty("display", "block", "important");
                  }
                  if (parentStyle.visibility === "hidden") {
                    parent.style.setProperty(
                      "visibility",
                      "visible",
                      "important",
                    );
                  }
                  if (parentStyle.opacity === "0") {
                    parent.style.setProperty("opacity", "1", "important");
                  }
                  parent = parent.parentElement;
                  depth++;
                }
              }
            });

            // Process all text elements in canvas blocks to ensure visibility
            const allTextElements = block.querySelectorAll(
              "p, h1, h2, h3, h4, h5, h6, li, td, th, span, div, a, strong, em",
            );
            allTextElements.forEach((el) => {
              const isPhrasing = [
                "SPAN",
                "A",
                "STRONG",
                "EM",
                "CODE",
                "B",
                "I",
                "U",
                "S",
              ].includes(el.tagName);
              const computedStyle = window.getComputedStyle(el);
              if (
                computedStyle.display === "none" ||
                computedStyle.visibility === "hidden"
              ) {
                const text = el.textContent?.trim() || "";
                if (text && text.length > 0) {
                  el.style.setProperty(
                    "display",
                    isPhrasing ? "inline" : "block",
                    "important",
                  );
                  el.style.setProperty("visibility", "visible", "important");
                }
              }

              const computedColor = window.getComputedStyle(el).color;
              if (
                computedColor === "rgba(0, 0, 0, 0)" ||
                computedColor === "transparent" ||
                !computedColor
              ) {
                const text = el.textContent?.trim() || "";
                if (text && text.length > 0) {
                  el.style.setProperty("color", "#000", "important");
                }
              }

              // word-break on inline phrasing (e.g. Lexical bg-color spans) forces a line break before the span in PDF
              if (!isPhrasing) {
                el.style.setProperty("word-break", "break-word", "important");
                el.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                el.style.setProperty("max-width", "100%", "important");
              }
              el.style.setProperty("box-sizing", "border-box", "important");
              el.style.setProperty("visibility", "visible", "important");
              el.style.setProperty("opacity", "1", "important");
            });
          }
        });
      };
      ensureNestedElementsVisible(clone);
      applyStepSpacing(clone);
      applyAttachmentRichTextSpacing(clone);
      applyItemRequiredEmptySpacing(clone);
      applyItemRequiredDynamicSpacing(clone);
      applyPostItemRequiredBlockSpacing(clone);

      const normalizeLayoutContainersForPDF = (element) => {
        if (!element) return;

        const layoutContainers = element.querySelectorAll(
          '.PlaygroundEditorTheme__layoutContainer, [data-lexical-layout-container="true"]',
        );

        layoutContainers.forEach((container) => {
          if (!container) return;

          const computedStyle = window.getComputedStyle(container);
          const inlineTemplate = container.style.gridTemplateColumns || "";
          const computedTemplate =
            computedStyle.gridTemplateColumns &&
            computedStyle.gridTemplateColumns !== "none"
              ? computedStyle.gridTemplateColumns
              : "";

          let template = inlineTemplate || computedTemplate || "";
          let weights = [];

          if (template) {
            const repeatMatch = template.match(
              /repeat\(\s*(\d+)\s*,\s*([0-9.]+)fr\s*\)/i,
            );
            if (repeatMatch) {
              const count = parseInt(repeatMatch[1], 10) || 0;
              const value = parseFloat(repeatMatch[2]) || 1;
              if (count > 0) {
                weights = Array(count).fill(value);
              }
            } else {
              const parts = template
                .split(/\s+/)
                .map((p) => p.trim())
                .filter(Boolean);
              if (parts.length) {
                weights = parts.map((part) => {
                  const m = part.match(/([0-9.]+)fr/i);
                  if (m && !Number.isNaN(parseFloat(m[1]))) {
                    return parseFloat(m[1]);
                  }
                  return 1;
                });
              }
            }
          }

          const items = Array.from(
            container.querySelectorAll(
              '.PlaygroundEditorTheme__layoutItem, [data-lexical-layout-item="true"]',
            ),
          );

          if (!items.length) {
            return;
          }

          if (!weights.length) {
            weights = Array(items.length).fill(1);
          } else if (weights.length < items.length) {
            const last = weights[weights.length - 1] || 1;
            while (weights.length < items.length) {
              weights.push(last);
            }
          } else if (weights.length > items.length) {
            weights = weights.slice(0, items.length);
          }

          const totalWeight = weights.reduce(
            (sum, w) => sum + (Number.isFinite(w) && w > 0 ? w : 1),
            0,
          );
          const effectiveTotal = totalWeight || items.length;

          container.style.setProperty("display", "flex", "important");
          container.style.setProperty("flex-direction", "row", "important");
          container.style.setProperty("align-items", "stretch", "important");
          container.style.setProperty(
            "justify-content",
            "space-between",
            "important",
          );

          const gap = computedStyle.gap || computedStyle.columnGap || "";
          if (gap && gap !== "0px") {
            container.style.setProperty("gap", gap, "important");
          }

          items.forEach((item, index) => {
            const weight =
              Number.isFinite(weights[index]) && weights[index] > 0
                ? weights[index]
                : 1;
            const percent = (weight / effectiveTotal) * 100;

            item.style.setProperty("display", "block", "important");
            item.style.setProperty(
              "flex",
              `0 0 ${percent.toFixed(4)}%`,
              "important",
            );
            item.style.setProperty(
              "max-width",
              `${percent.toFixed(4)}%`,
              "important",
            );
            item.style.setProperty("box-sizing", "border-box", "important");
          });
        });
      };

      let cloneRect = clone.getBoundingClientRect();
      const blockLabelsForPagination = new Set([
        "signature-history",
        "section",
        "section-intro",
        "step",
        "substep",
        "attachment",
        "rich-text",
        "video",
        "form",
        "canvas",
      ]);

      const collectBlockMetas = (baseRect) => {
        const metas = [];
        Array.from(clone.querySelectorAll("[data-export-block]")).forEach(
          (element) => {
            const label = element.getAttribute("data-export-block") || "";
            if (!blockLabelsForPagination.has(label)) return;

            const computedStyle = window.getComputedStyle(element);
            if (
              computedStyle.display === "none" ||
              computedStyle.visibility === "hidden"
            ) {
              element.style.setProperty("display", "block", "important");
              element.style.setProperty("visibility", "visible", "important");
            }

            if (label === "rich-text") {
              const richTextContent = element.querySelector(
                ".rich-text-content-display, .editor-shell",
              );
              if (richTextContent) {
                richTextContent.style.setProperty(
                  "display",
                  "block",
                  "important",
                );
                richTextContent.style.setProperty(
                  "visibility",
                  "visible",
                  "important",
                );
                richTextContent.style.setProperty("opacity", "1", "important");
                // CRITICAL: Add z-index and position to ensure rich text content appears above white backgrounds
                richTextContent.style.setProperty(
                  "position",
                  "relative",
                  "important",
                );
                richTextContent.style.setProperty(
                  "z-index",
                  "2000",
                  "important",
                );
                void richTextContent.offsetHeight;
              }
            }

            if (label === "canvas") {
              const images = element.querySelectorAll("img");
              images.forEach((img) => {
                img.style.setProperty("display", "block", "important");
                img.style.setProperty("visibility", "visible", "important");
                img.style.setProperty("opacity", "1", "important");
              });
              void element.offsetHeight;
            }

            if (label === "attachment") {
              const images = element.querySelectorAll("img");
              images.forEach((img) => {
                img.style.setProperty("display", "block", "important");
                img.style.setProperty("visibility", "visible", "important");
                img.style.setProperty("opacity", "1", "important");
              });
              void element.offsetHeight;
            }

            if (label === "video") {
              const images = element.querySelectorAll("img, canvas");
              images.forEach((img) => {
                img.style.setProperty("display", "block", "important");
                img.style.setProperty("visibility", "visible", "important");
                img.style.setProperty("opacity", "1", "important");
              });
              void element.offsetHeight;
            }

            void element.offsetHeight;

            // IMPORTANT: We use DOM bounding boxes to decide pagination.
            // For Canvas blocks, `getBoundingClientRect()` can sometimes include a large trailing blank
            // area (e.g., due to layout wrappers / spacing). That can make the paginator think the
            // canvas block is taller than it actually is, and wrongly push it to the next page.
            // To mitigate, we compute an "effective bottom" based on the last *meaningful* visible
            // descendant and trim large trailing blank space.
            let rect = element.getBoundingClientRect();

            // For rich text blocks we also want to know where the "short description" ends
            // and where the actual rich text content begins, so pagination can split them.
            // This is used later to keep the short description on the current page (if it fits)
            // while moving only the content to the next page.
            let richTextShortDescTopDom = null;
            let richTextShortDescBottomDom = null;
            let richTextContentTopDom = null;
            if (label === "rich-text") {
              try {
                const richTextContent = element.querySelector(
                  ".rich-text-content-display, .editor-shell",
                );
                if (richTextContent) {
                  const contentRect = richTextContent.getBoundingClientRect();
                  richTextContentTopDom = contentRect.top - baseRect.top;

                  const shortDescCandidates = Array.from(
                    element.querySelectorAll("div"),
                  ).filter((div) => {
                    const text = (div.textContent || "").trim();
                    if (!text) return false;
                    if (
                      div.querySelector("h2") ||
                      div.querySelector('input[type="checkbox"]')
                    )
                      return false;
                    if (
                      div.classList.contains("rich-text-content-display") ||
                      div.classList.contains("editor-shell") ||
                      div.closest(".rich-text-content-display") ||
                      div.closest(".editor-shell")
                    )
                      return false;
                    // Only consider candidates that appear before the rich text content container.
                    const isBefore =
                      (div.compareDocumentPosition(richTextContent) &
                        Node.DOCUMENT_POSITION_FOLLOWING) !==
                      0;
                    return isBefore;
                  });

                  let top = Number.POSITIVE_INFINITY;
                  let bottom = Number.NEGATIVE_INFINITY;
                  shortDescCandidates.forEach((div) => {
                    const r = div.getBoundingClientRect();
                    if (!r || r.height <= 0) return;
                    top = Math.min(top, r.top);
                    bottom = Math.max(bottom, r.bottom);
                  });

                  if (
                    Number.isFinite(top) &&
                    Number.isFinite(bottom) &&
                    bottom > top
                  ) {
                    richTextShortDescTopDom = top - baseRect.top;
                    richTextShortDescBottomDom = bottom - baseRect.top;
                  }
                }
              } catch (e) {
                // best-effort
              }
            }

            // For canvas blocks: similarly track "short description" vs the actual canvas content
            // (usually an <img> produced from the canvas composition during export).
            // This enables splitting between them: keep short description on current page if it fits,
            // and start the canvas content on the next page.
            let canvasShortDescTopDom = null;
            let canvasShortDescBottomDom = null;
            let canvasContentTopDom = null;
            if (label === "canvas") {
              try {
                // Prefer composited image; otherwise fall back to first <img> or <canvas>.
                const contentEl =
                  element.querySelector("img[data-canvas-composite]") ||
                  element.querySelector("img") ||
                  element.querySelector("canvas");
                if (contentEl) {
                  const contentRect = contentEl.getBoundingClientRect();
                  canvasContentTopDom = contentRect.top - baseRect.top;

                  // First, prefer explicit rich-text description blocks that sit above the
                  // rendered canvas content. Previous logic excluded rich-text containers,
                  // which can make canvasShortDescTop/Bottom null and hide descriptions on
                  // continuation pages.
                  const richTextDescCandidates = Array.from(
                    element.querySelectorAll(".rich-text-content-display"),
                  ).filter((div) => {
                    const r = div.getBoundingClientRect();
                    if (!r || r.height <= 0) return false;
                    const text = (div.textContent || "").trim();
                    if (!text) return false;
                    // Keep only description blocks that are visually before content image/canvas.
                    // Small gap between description and snapshot is common; +2px was too strict
                    // and dropped bounds so PDF pagination lost the short-description band.
                    return r.bottom <= contentRect.top + 12;
                  });
                  if (richTextDescCandidates.length > 0) {
                    let top = Number.POSITIVE_INFINITY;
                    let bottom = Number.NEGATIVE_INFINITY;
                    richTextDescCandidates.forEach((div) => {
                      const r = div.getBoundingClientRect();
                      if (!r || r.height <= 0) return;
                      top = Math.min(top, r.top);
                      bottom = Math.max(bottom, r.bottom);
                    });
                    if (
                      Number.isFinite(top) &&
                      Number.isFinite(bottom) &&
                      bottom > top
                    ) {
                      canvasShortDescTopDom = top - baseRect.top;
                      canvasShortDescBottomDom = bottom - baseRect.top;
                    }
                  }

                  // Candidate short description divs: text-only divs before the first image.
                  if (
                    canvasShortDescTopDom === null ||
                    canvasShortDescBottomDom === null
                  ) {
                    const shortDescCandidates = Array.from(
                      element.querySelectorAll("div"),
                    ).filter((div) => {
                      const text = (div.textContent || "").trim();
                      if (!text) return false;
                      if (
                        div.querySelector("h2") ||
                        div.querySelector('input[type="checkbox"]')
                      )
                        return false;
                      // Exclude editor-shell wrappers, but allow rich-text display divs above content.
                      if (
                        div.classList.contains("editor-shell") ||
                        div.closest(".editor-shell")
                      )
                        return false;
                      const divRect = div.getBoundingClientRect();
                      return divRect.bottom <= contentRect.top + 12;
                    });

                    let top = Number.POSITIVE_INFINITY;
                    let bottom = Number.NEGATIVE_INFINITY;
                    shortDescCandidates.forEach((div) => {
                      const r = div.getBoundingClientRect();
                      if (!r || r.height <= 0) return;
                      top = Math.min(top, r.top);
                      bottom = Math.max(bottom, r.bottom);
                    });

                    if (
                      Number.isFinite(top) &&
                      Number.isFinite(bottom) &&
                      bottom > top
                    ) {
                      canvasShortDescTopDom = top - baseRect.top;
                      canvasShortDescBottomDom = bottom - baseRect.top;
                    }
                  }
                }
              } catch (e) {
                // best-effort
              }
            }

            // For video blocks: track "short description" vs the actual video placeholder content
            // (typically an <img> or <canvas> placeholder created during export).
            // This enables splitting between them: keep short description on current page if it fits,
            // and start the video placeholder on the next page.
            let videoShortDescTopDom = null;
            let videoShortDescBottomDom = null;
            let videoContentTopDom = null;
            if (label === "video") {
              try {
                const contentEl =
                  element.querySelector("img") ||
                  element.querySelector("canvas") ||
                  null;
                if (contentEl) {
                  const contentRect = contentEl.getBoundingClientRect();
                  videoContentTopDom = contentRect.top - baseRect.top;

                  const shortDescCandidates = Array.from(
                    element.querySelectorAll("div"),
                  ).filter((div) => {
                    const text = (div.textContent || "").trim();
                    if (!text) return false;
                    if (
                      div.querySelector("h2") ||
                      div.querySelector('input[type="checkbox"]')
                    )
                      return false;
                    if (
                      div.classList.contains("rich-text-content-display") ||
                      div.classList.contains("editor-shell") ||
                      div.closest(".rich-text-content-display") ||
                      div.closest(".editor-shell")
                    )
                      return false;
                    const isBefore =
                      (div.compareDocumentPosition(contentEl) &
                        Node.DOCUMENT_POSITION_FOLLOWING) !==
                      0;
                    return isBefore;
                  });

                  let top = Number.POSITIVE_INFINITY;
                  let bottom = Number.NEGATIVE_INFINITY;
                  shortDescCandidates.forEach((div) => {
                    const r = div.getBoundingClientRect();
                    if (!r || r.height <= 0) return;
                    top = Math.min(top, r.top);
                    bottom = Math.max(bottom, r.bottom);
                  });

                  if (
                    Number.isFinite(top) &&
                    Number.isFinite(bottom) &&
                    bottom > top
                  ) {
                    videoShortDescTopDom = top - baseRect.top;
                    videoShortDescBottomDom = bottom - baseRect.top;
                  }
                }
              } catch (e) {
                // best-effort
              }
            }

            // For attachment blocks: track "short description" vs the actual attachment content
            // (usually one or more <img> elements).
            let attachmentShortDescTopDom = null;
            let attachmentShortDescBottomDom = null;
            let attachmentContentTopDom = null;
            if (label === "attachment") {
              try {
                const contentEl =
                  element.querySelector("img") ||
                  element.querySelector("canvas") ||
                  null;
                if (contentEl) {
                  const contentRect = contentEl.getBoundingClientRect();
                  attachmentContentTopDom = contentRect.top - baseRect.top;

                  const shortDescCandidates = Array.from(
                    element.querySelectorAll("div"),
                  ).filter((div) => {
                    const text = (div.textContent || "").trim();
                    if (!text) return false;
                    if (
                      div.querySelector("h2") ||
                      div.querySelector('input[type="checkbox"]')
                    )
                      return false;
                    if (
                      div.classList.contains("rich-text-content-display") ||
                      div.classList.contains("editor-shell") ||
                      div.closest(".rich-text-content-display") ||
                      div.closest(".editor-shell")
                    )
                      return false;
                    const isBefore =
                      (div.compareDocumentPosition(contentEl) &
                        Node.DOCUMENT_POSITION_FOLLOWING) !==
                      0;
                    return isBefore;
                  });

                  let top = Number.POSITIVE_INFINITY;
                  let bottom = Number.NEGATIVE_INFINITY;
                  shortDescCandidates.forEach((div) => {
                    const r = div.getBoundingClientRect();
                    if (!r || r.height <= 0) return;
                    top = Math.min(top, r.top);
                    bottom = Math.max(bottom, r.bottom);
                  });

                  if (
                    Number.isFinite(top) &&
                    Number.isFinite(bottom) &&
                    bottom > top
                  ) {
                    attachmentShortDescTopDom = top - baseRect.top;
                    attachmentShortDescBottomDom = bottom - baseRect.top;
                  }
                }
              } catch (e) {
                // best-effort
              }
            }
            if (label === "canvas" && rect && rect.height > 0) {
              try {
                // For canvas blocks, calculate actual content height from images
                const images = element.querySelectorAll("img");
                if (images.length > 0) {
                  let maxContentBottom = rect.top;
                  let hasValidImage = false;

                  images.forEach((img) => {
                    const imgRect = img.getBoundingClientRect();
                    // Only count images that are actually visible and have real height
                    if (imgRect && imgRect.height > 10) {
                      maxContentBottom = Math.max(
                        maxContentBottom,
                        imgRect.bottom,
                      );
                      hasValidImage = true;
                    }
                  });

                  // Also check for heading and description elements
                  const heading = element.querySelector("h2");
                  if (heading) {
                    const headingRect = heading.getBoundingClientRect();
                    if (headingRect && headingRect.height > 0) {
                      maxContentBottom = Math.max(
                        maxContentBottom,
                        headingRect.bottom,
                      );
                    }
                  }

                  // Add a small buffer (20px) for margins/padding after last image
                  const buffer = 20;
                  const trailingBlankPx = rect.bottom - maxContentBottom;
                  const blankTrimThresholdPx = 50; // Only trim if there's more than 50px of blank space

                  if (
                    hasValidImage &&
                    Number.isFinite(trailingBlankPx) &&
                    trailingBlankPx > blankTrimThresholdPx &&
                    Number.isFinite(maxContentBottom) &&
                    maxContentBottom > rect.top + 10
                  ) {
                    rect = {
                      top: rect.top,
                      bottom: maxContentBottom + buffer,
                      height: Math.max(0, maxContentBottom + buffer - rect.top),
                      left: rect.left,
                      width: rect.width,
                    };
                  }
                }
              } catch (e) {
                // Best-effort only. Fall back to the raw rect.
              }
            }
            const includeInPagination = true;

            let hasImages = false;
            let imageCount = 0;
            if (label === "canvas") {
              const images = element.querySelectorAll("img");
              imageCount = images.length;
              hasImages = imageCount > 0;

              if (rect.height <= 0 && imageCount > 0) {
                let totalHeight = 0;
                let spacing = 0;
                images.forEach((img, index) => {
                  const imgRect = img.getBoundingClientRect();
                  if (imgRect.height > 0) {
                    totalHeight += imgRect.height;
                    if (index > 0) {
                      spacing += 10;
                    }
                  }
                });
                if (totalHeight > 0) {
                  const adjustedRect = {
                    top: rect.top,
                    bottom: rect.top + totalHeight + spacing,
                    height: totalHeight + spacing,
                    left: rect.left,
                    width: rect.width,
                  };
                  metas.push({
                    label,
                    includeInPagination,
                    topDom: adjustedRect.top - baseRect.top,
                    bottomDom: adjustedRect.bottom - baseRect.top,
                    heightDom: adjustedRect.height,
                    element,
                    hasImages: true,
                    imageCount,
                  });
                  return;
                }
              }
            }

            if (label === "attachment") {
              const images = element.querySelectorAll("img");
              imageCount = images.length;
              hasImages = imageCount > 0;

              if (rect.height <= 0 && imageCount > 0) {
                let totalHeight = 0;
                let spacing = 0;
                images.forEach((img, index) => {
                  const imgRect = img.getBoundingClientRect();
                  if (imgRect.height > 0) {
                    totalHeight += imgRect.height;
                    if (index > 0) {
                      spacing += 10;
                    }
                  }
                });
                if (totalHeight > 0) {
                  const adjustedRect = {
                    top: rect.top,
                    bottom: rect.top + totalHeight + spacing,
                    height: totalHeight + spacing,
                    left: rect.left,
                    width: rect.width,
                  };
                  metas.push({
                    label,
                    includeInPagination,
                    topDom: adjustedRect.top - baseRect.top,
                    bottomDom: adjustedRect.bottom - baseRect.top,
                    heightDom: adjustedRect.height,
                    element,
                    hasImages: true,
                    imageCount,
                  });
                  return;
                }
              }
            }

            if (label === "video") {
              const images = element.querySelectorAll("img, canvas");
              imageCount = images.length;
              hasImages = imageCount > 0;

              if (rect.height <= 0 && imageCount > 0) {
                let totalHeight = 0;
                let spacing = 0;
                images.forEach((img, index) => {
                  const imgRect = img.getBoundingClientRect();
                  if (imgRect.height > 0) {
                    totalHeight += imgRect.height;
                    if (index > 0) {
                      spacing += 10;
                    }
                  }
                });
                if (totalHeight > 0) {
                  const adjustedRect = {
                    top: rect.top,
                    bottom: rect.top + totalHeight + spacing,
                    height: totalHeight + spacing,
                    left: rect.left,
                    width: rect.width,
                  };
                  metas.push({
                    label,
                    includeInPagination,
                    topDom: adjustedRect.top - baseRect.top,
                    bottomDom: adjustedRect.bottom - baseRect.top,
                    heightDom: adjustedRect.height,
                    element,
                    hasImages: true,
                    imageCount,
                  });
                  return;
                }
              }
            }

            if (rect.height <= 0 && label === "rich-text") {
              const richTextContent = element.querySelector(
                ".rich-text-content-display, .editor-shell",
              );
              if (richTextContent) {
                const contentRect = richTextContent.getBoundingClientRect();
                if (contentRect.height > 0) {
                  const adjustedRect = {
                    top: rect.top,
                    bottom: rect.top + contentRect.height,
                    height: contentRect.height,
                    left: rect.left,
                    width: rect.width,
                  };
                  metas.push({
                    label,
                    includeInPagination,
                    topDom: adjustedRect.top - baseRect.top,
                    bottomDom: adjustedRect.bottom - baseRect.top,
                    heightDom: adjustedRect.height,
                    element,
                    hasImages,
                    imageCount,
                    richTextShortDescTopDom,
                    richTextShortDescBottomDom,
                    richTextContentTopDom,
                  });
                  return;
                }
              }
            }

            metas.push({
              label,
              includeInPagination,
              topDom: rect.top - baseRect.top,
              bottomDom: rect.bottom - baseRect.top,
              heightDom: rect.height,
              element,
              hasImages,
              imageCount,
              richTextShortDescTopDom,
              richTextShortDescBottomDom,
              richTextContentTopDom,
              canvasShortDescTopDom,
              canvasShortDescBottomDom,
              canvasContentTopDom,
              videoShortDescTopDom,
              videoShortDescBottomDom,
              videoContentTopDom,
              attachmentShortDescTopDom,
              attachmentShortDescBottomDom,
              attachmentContentTopDom,
            });
          },
        );
        return metas;
      };

      const collectTableMetas = (baseRect) => {
        const metas = [];
        // Identify Training Log headings
        const allHeadings = Array.from(
          clone.querySelectorAll("h1, h2, h3, h4, h5, h6"),
        );
        const trainingLogHeadings = new Set();
        allHeadings.forEach((heading) => {
          const headingText = (heading.textContent || "").trim().toLowerCase();
          if (headingText === "training log" || headingText === "traininglog") {
            trainingLogHeadings.add(heading);
          }
        });

        Array.from(clone.querySelectorAll("table")).forEach((table) => {
          const thead = table.querySelector("thead");
          if (!thead) return;

          let isItemRequiredTable = false;
          let isTrainingLogTable = false;

          // Check if table has "Where Mentioned" in ANY th
          const allThs = thead.querySelectorAll("th");
          for (const th of allThs) {
            const thText = (th.textContent || "").trim();
            if (thText.includes("Where Mentioned")) {
              isItemRequiredTable = true;
              break;
            }
          }

          // Also check tbody for "Where Mentioned" in case it's in the first row
          if (!isItemRequiredTable) {
            const tbody = table.querySelector("tbody");
            if (tbody) {
              const firstRow = tbody.querySelector("tr");
              if (firstRow) {
                const firstCell = firstRow.querySelector("td, th");
                if (
                  firstCell &&
                  (firstCell.textContent || "").includes("Where Mentioned")
                ) {
                  isItemRequiredTable = true;
                }
              }
            }
          }

          // Also check for Items Required table structure (Type, Item Code, Description, Qty Per)
          // but only if it's NOT a BOM table
          if (!isItemRequiredTable && thead) {
            const theadText = (thead.textContent || "").toLowerCase();
            if (
              theadText.includes("type") &&
              theadText.includes("item code") &&
              theadText.includes("description") &&
              (theadText.includes("qty per") || theadText.includes("qty"))
            ) {
              // Check if this is NOT a BOM table
              const isBomTable =
                table.closest("[data-export-block='bom']") !== null;
              if (!isBomTable) {
                // Check if there's a BOM heading nearby
                let hasBomHeading = false;
                let checkSibling = table.previousElementSibling;
                let checkDepth = 0;
                while (checkSibling && checkDepth < 5) {
                  if (
                    checkSibling.tagName &&
                    /^H[1-6]$/i.test(checkSibling.tagName)
                  ) {
                    const headingText = (checkSibling.textContent || "")
                      .trim()
                      .toLowerCase();
                    if (headingText.endsWith(" bom")) {
                      hasBomHeading = true;
                      break;
                    }
                  }
                  if (checkSibling.querySelector) {
                    const headingInParent = checkSibling.querySelector(
                      "h1, h2, h3, h4, h5, h6",
                    );
                    if (headingInParent) {
                      const headingText = (headingInParent.textContent || "")
                        .trim()
                        .toLowerCase();
                      if (headingText.endsWith(" bom")) {
                        hasBomHeading = true;
                        break;
                      }
                    }
                  }
                  checkSibling = checkSibling.previousElementSibling;
                  checkDepth++;
                }
                // If no BOM heading found, it's likely an Items Required table
                if (!hasBomHeading) {
                  isItemRequiredTable = true;
                }
              }
            }
          }

          // Final check: table text content
          if (!isItemRequiredTable) {
            const tableText = (table.textContent || "").toLowerCase();
            if (
              tableText.includes("where mentioned") &&
              !table.closest("[data-export-block='revision']") &&
              !table.closest("[data-export-block='signature-history']")
            ) {
              isItemRequiredTable = true;
            }
          }

          // Check for Training Log tables
          if (!isItemRequiredTable && !isTrainingLogTable) {
            // Check if table follows a Training Log heading
            let prevSibling = table.previousElementSibling;
            let checkDepth = 0;
            while (prevSibling && checkDepth < 10) {
              if (trainingLogHeadings.has(prevSibling)) {
                isTrainingLogTable = true;
                break;
              }
              if (prevSibling.querySelector) {
                const headingInParent = prevSibling.querySelector(
                  "h1, h2, h3, h4, h5, h6",
                );
                if (
                  headingInParent &&
                  trainingLogHeadings.has(headingInParent)
                ) {
                  isTrainingLogTable = true;
                  break;
                }
              }
              prevSibling = prevSibling.previousElementSibling;
              checkDepth++;
            }
          }

          // Check Training Log table by thead structure (Date of Training, Trainer, Trainee, etc.)
          if (!isItemRequiredTable && !isTrainingLogTable && thead) {
            const theadText = (thead.textContent || "").toLowerCase();
            if (
              theadText.includes("date of training") &&
              theadText.includes("trainer") &&
              theadText.includes("trainee") &&
              theadText.includes("revision trained")
            ) {
              isTrainingLogTable = true;
            }
          }

          let isBomTable = false;
          if (!isItemRequiredTable && !isTrainingLogTable && thead) {
            const theadText = (thead.textContent || "").toLowerCase();
            if (
              theadText.includes("type") &&
              theadText.includes("item code") &&
              theadText.includes("description") &&
              (theadText.includes("qty per") || theadText.includes("qty"))
            ) {
              if (table.closest("[data-export-block='bom']") !== null) {
                isBomTable = true;
              } else {
                let prevSibling = table.previousElementSibling;
                let checkDepth = 0;
                while (prevSibling && checkDepth < 10) {
                  if (prevSibling.tagName === "H3") {
                    const ht = (prevSibling.textContent || "")
                      .trim()
                      .toLowerCase();
                    if (ht.endsWith(" bom")) {
                      isBomTable = true;
                      break;
                    }
                  }
                  if (prevSibling.querySelector) {
                    const h3InParent = prevSibling.querySelector("h3");
                    if (h3InParent) {
                      const ht = (h3InParent.textContent || "")
                        .trim()
                        .toLowerCase();
                      if (ht.endsWith(" bom")) {
                        isBomTable = true;
                        break;
                      }
                    }
                  }
                  prevSibling = prevSibling.previousElementSibling;
                  checkDepth++;
                }
              }
            }
          }

          // Item Required tables are now included - find parent step for tracking
          let stepIdForTable = null;
          if (isItemRequiredTable) {
            // Find the parent step container
            const stepContainer = table.closest("[data-export-block='step']");
            if (stepContainer) {
              // Find the step header to get step number
              const stepHeader = stepContainer.querySelector("h2");
              if (stepHeader) {
                const headerText = (stepHeader.textContent || "").trim();
                const stepMatch = headerText.match(/Step\s+(\d+)/i);
                if (stepMatch) {
                  // We'll match this to stepMetas later by step number
                  // For now, store a reference to find the stepId
                  stepIdForTable = stepContainer;
                } else {
                }
              } else {
              }
            } else {
            }
          }

          const tableRect = table.getBoundingClientRect();
          const theadRect = thead.getBoundingClientRect();

          let headingElement = null;
          let headingRect = null;
          let headingTopDom = null;
          let headingHeightDom = null;

          // For Training Log tables, look for H3 Training Log heading
          if (isTrainingLogTable && !headingElement) {
            let prevSibling = table.previousElementSibling;
            while (prevSibling) {
              if (prevSibling.tagName === "H3") {
                const headingText = (prevSibling.textContent || "")
                  .trim()
                  .toLowerCase();
                if (headingText === "training log") {
                  headingElement = prevSibling;
                  headingRect = prevSibling.getBoundingClientRect();
                  headingTopDom = headingRect.top - baseRect.top;
                  headingHeightDom = headingRect.height;
                  break;
                }
              }
              if (prevSibling.querySelector) {
                const h3InParent = prevSibling.querySelector("h3");
                if (h3InParent) {
                  const headingText = (h3InParent.textContent || "")
                    .trim()
                    .toLowerCase();
                  if (headingText === "training log") {
                    headingElement = h3InParent;
                    headingRect = h3InParent.getBoundingClientRect();
                    headingTopDom = headingRect.top - baseRect.top;
                    headingHeightDom = headingRect.height;
                    break;
                  }
                }
              }
              prevSibling = prevSibling.previousElementSibling;
            }

            if (!headingElement) {
              const allH3s = Array.from(clone.querySelectorAll("h3"));
              for (const h3 of allH3s) {
                const headingText = (h3.textContent || "").trim().toLowerCase();
                if (headingText === "training log") {
                  const h3Rect = h3.getBoundingClientRect();
                  if (h3Rect.bottom <= tableRect.top + 50) {
                    headingElement = h3;
                    headingRect = h3Rect;
                    headingTopDom = headingRect.top - baseRect.top;
                    headingHeightDom = headingRect.height;
                    break;
                  }
                }
              }
            }
          }

          if (isBomTable && !headingElement) {
            let prevSibling = table.previousElementSibling;
            while (prevSibling) {
              if (prevSibling.tagName === "H3") {
                const headingText = (prevSibling.textContent || "")
                  .trim()
                  .toLowerCase();
                if (headingText.endsWith(" bom")) {
                  headingElement = prevSibling;
                  headingRect = prevSibling.getBoundingClientRect();
                  headingTopDom = headingRect.top - baseRect.top;
                  headingHeightDom = headingRect.height;
                  break;
                }
              }
              if (prevSibling.querySelector) {
                const h3InParent = prevSibling.querySelector("h3");
                if (h3InParent) {
                  const headingText = (h3InParent.textContent || "")
                    .trim()
                    .toLowerCase();
                  if (headingText.endsWith(" bom")) {
                    headingElement = h3InParent;
                    headingRect = h3InParent.getBoundingClientRect();
                    headingTopDom = headingRect.top - baseRect.top;
                    headingHeightDom = headingRect.height;
                    break;
                  }
                }
              }
              prevSibling = prevSibling.previousElementSibling;
            }
            if (!headingElement) {
              const allH3s = Array.from(clone.querySelectorAll("h3"));
              for (const h3 of allH3s) {
                const headingText = (h3.textContent || "").trim().toLowerCase();
                if (headingText.endsWith(" bom")) {
                  const h3Rect = h3.getBoundingClientRect();
                  if (h3Rect.bottom <= tableRect.top + 80) {
                    headingElement = h3;
                    headingRect = h3Rect;
                    headingTopDom = headingRect.top - baseRect.top;
                    headingHeightDom = headingRect.height;
                    break;
                  }
                }
              }
            }
          }

          const tableId = metas.length;
          metas.push({
            tableId,
            element: table,
            headingElement,
            isItemRequiredTable: isItemRequiredTable || false,
            isTrainingLogTable: isTrainingLogTable || false,
            isBomTable: isBomTable || false,
            stepContainer: stepIdForTable, // Store step container reference for later matching
            topDom: tableRect.top - baseRect.top,
            bottomDom: tableRect.bottom - baseRect.top,
            headerTopDom: headingElement
              ? headingTopDom
              : theadRect.top - baseRect.top,
            headerHeightDom: headingElement
              ? headingHeightDom +
                theadRect.height +
                (isTrainingLogTable ? 2 : 10)
              : theadRect.height,
            leftDom: theadRect.left - baseRect.left,
            widthDom: theadRect.width,
            headingTopDom,
            headingHeightDom,
            theadTopDom: theadRect.top - baseRect.top,
            theadHeightDom: theadRect.height,
            headerCanvas: null,
            topPx: 0,
            bottomPx: 0,
            headerTopPx: 0,
            headerHeightPx: 0,
            leftPx: 0,
            widthPx: 0,
          });
        });
        return metas;
      };

      const collectStepMetas = (baseRect) => {
        const metas = [];
        Array.from(
          clone.querySelectorAll("[data-export-block='step']"),
        ).forEach((stepElement) => {
          const stepHeader = stepElement.querySelector("h2");
          if (!stepHeader) return;

          const headerText = (stepHeader.textContent || "").trim();
          const stepMatch = headerText.match(/Step\s+(\d+)/i);
          if (!stepMatch) return;

          const stepNumber = parseInt(stepMatch[1], 10);
          const stepRect = stepElement.getBoundingClientRect();
          const headerRect = stepHeader.getBoundingClientRect();

          // Prefer the structural rule under the title row (direct child of step), not an <hr> inside rich text.
          let hrElement = Array.from(stepElement.children).find(
            (el) => el.tagName === "HR",
          );
          if (!hrElement) {
            const headerRow =
              stepHeader.closest('[style*="space-between"]') ||
              stepHeader.closest('[style*="justify-content"]');
            if (headerRow?.nextElementSibling?.tagName === "HR") {
              hrElement = headerRow.nextElementSibling;
            }
          }
          if (!hrElement) {
            hrElement = stepElement.querySelector("hr");
          }
          let headerHeightDom = headerRect.height;
          let headerBottomDom = headerRect.bottom - baseRect.top;

          let hrTopDom = null;
          let hrHeightDom = null;
          if (hrElement) {
            const hrRect = hrElement.getBoundingClientRect();
            headerBottomDom = hrRect.bottom - baseRect.top;
            headerHeightDom = headerBottomDom - (headerRect.top - baseRect.top);
            hrTopDom =
              hrRect.top - baseRect.top - (headerRect.top - baseRect.top);
            hrHeightDom = hrRect.height;
          }

          const headerContainer =
            stepHeader.closest("div[style*='display']") ||
            stepHeader.parentElement;

          const treePathElement =
            stepElement.querySelector("div[style*='color'][style*='#888']") ||
            Array.from(stepElement.querySelectorAll("div")).find((div) => {
              const text = (div.textContent || "").trim();
              return text.includes("Section") && text.includes("Step");
            });

          let sectionIndex = null;
          let globalStepNumber = stepNumber;
          if (treePathElement) {
            const treePathText = (treePathElement.textContent || "").trim();
            const sectionMatch = treePathText.match(/Section\s+(\d+)/i);
            if (sectionMatch) {
              sectionIndex = parseInt(sectionMatch[1], 10) - 1;
            }
          }

          // Step short description: rich-text directly inside the step block.
          let stepShortDescTopDom = null;
          let stepShortDescBottomDom = null;
          const stepShortDescDiv = Array.from(
            stepElement.querySelectorAll(".rich-text-content-display"),
          ).find((div) => div.closest("[data-export-block]") === stepElement);
          if (stepShortDescDiv) {
            try {
              const shortDescRect = stepShortDescDiv.getBoundingClientRect();
              if (shortDescRect && shortDescRect.height > 0) {
                stepShortDescTopDom = shortDescRect.top - baseRect.top;
                stepShortDescBottomDom = shortDescRect.bottom - baseRect.top;
              }
            } catch (e) {
              // best-effort
            }
          }
          // Fallback: capture text rows directly under the step header/HR until child blocks begin.
          // Some templates render step descriptions outside `.rich-text-content-display`.
          if (stepShortDescTopDom == null || stepShortDescBottomDom == null) {
            let cursor = stepHeader.nextElementSibling;
            while (cursor) {
              if (cursor.hasAttribute && cursor.hasAttribute("data-export-block")) {
                break;
              }
              if (cursor.tagName === "HR") {
                cursor = cursor.nextElementSibling;
                continue;
              }
              const rawText = (cursor.textContent || "").replace(/\s+/g, " ").trim();
              const looksBreadcrumb =
                rawText.includes("Section") &&
                rawText.includes("Step") &&
                rawText.length <= 120;
              const isTableHeading =
                /^H[1-6]$/i.test(cursor.tagName || "") &&
                /(item\s*required|items\s*required|training\s*log|bom)$/i.test(
                  rawText,
                );
              if (isTableHeading) {
                break;
              }
              if (!looksBreadcrumb && rawText.length > 0) {
                try {
                  const r = cursor.getBoundingClientRect();
                  if (r && r.height > 0) {
                    const topDom = r.top - baseRect.top;
                    const bottomDom = r.bottom - baseRect.top;
                    if (
                      stepShortDescTopDom == null ||
                      topDom < stepShortDescTopDom
                    ) {
                      stepShortDescTopDom = topDom;
                    }
                    if (
                      stepShortDescBottomDom == null ||
                      bottomDom > stepShortDescBottomDom
                    ) {
                      stepShortDescBottomDom = bottomDom;
                    }
                  }
                } catch (e) {
                  // best-effort
                }
              }
              cursor = cursor.nextElementSibling;
            }
          }

          const stepId = metas.length;
          metas.push({
            stepId,
            element: stepElement,
            headerElement: stepHeader,
            hrElement: hrElement || null,
            stepNumber,
            sectionIndex: sectionIndex !== null ? sectionIndex : 0,
            globalStepNumber,
            treePathElement: treePathElement || null,
            hrElement: hrElement || null,
            hrTopDom: hrTopDom,
            hrHeightDom: hrHeightDom,
            topDom: stepRect.top - baseRect.top,
            bottomDom: stepRect.bottom - baseRect.top,
            headerTopDom: headerRect.top - baseRect.top,
            headerHeightDom: headerHeightDom,
            headerBottomDom: headerBottomDom,
            headerLeftDom: headerRect.left - baseRect.left,
            headerWidthDom: headerRect.width,
            stepShortDescTopDom: stepShortDescTopDom,
            stepShortDescBottomDom: stepShortDescBottomDom,
            headerCanvas: null,
            topPx: 0,
            bottomPx: 0,
            headerTopPx: 0,
            headerHeightPx: 0,
            headerLeftPx: 0,
            headerWidthPx: 0,
            stepShortDescTopPx: null,
            stepShortDescBottomPx: null,
          });
        });
        return metas;
      };

      const collectCanvasImageMetas = (baseRect) => {
        const metas = [];
        Array.from(
          clone.querySelectorAll(
            "[data-export-block='canvas'] img[data-canvas-composite]",
          ),
        ).forEach((img) => {
          const rect = img.getBoundingClientRect();
          if (rect.height < 10) return;

          metas.push({
            label: "canvas-image",
            includeInPagination: true,
            topDom: rect.top - baseRect.top,
            bottomDom: rect.bottom - baseRect.top,
            heightDom: rect.height,
            widthDom: rect.width,
            src: img.src ? img.src.substring(0, 100) : "",
          });
        });
        return metas;
      };

      const collectAttachmentImageMetas = (baseRect) => {
        const metas = [];
        Array.from(
          clone.querySelectorAll(
            "[data-export-block='attachment'] img, img[src*='s3.'], img[src*='amazonaws.com']",
          ),
        ).forEach((img) => {
          const rect = img.getBoundingClientRect();
          if (rect.height < 10) return;

          metas.push({
            topDom: rect.top - baseRect.top,
            bottomDom: rect.bottom - baseRect.top,
            heightDom: rect.height,
          });
        });
        return metas;
      };

      const collectSubstepMetas = (baseRect) => {
        const metas = [];
        Array.from(
          clone.querySelectorAll("[data-export-block='substep']"),
        ).forEach((substepElement) => {
          const substepHeader = substepElement.querySelector("h2");
          if (!substepHeader) return;

          const headerText = (substepHeader.textContent || "").trim();
          const substepMatch = headerText.match(/Substep\s+(\d+[A-Z])/i);
          if (!substepMatch) return;

          const substepLabel = substepMatch[1];
          const substepRect = substepElement.getBoundingClientRect();
          const headerRect = substepHeader.getBoundingClientRect();

          const treePathElement =
            substepElement.querySelector(
              "div[style*='color'][style*='#888']",
            ) ||
            Array.from(substepElement.querySelectorAll("div")).find((div) => {
              const text = (div.textContent || "").trim();
              return text.includes("Section") && text.includes("Substep");
            });

          // Title + breadcrumb sit in a flex row; h2 alone can be shorter vertically than the row.
          // PDF header strip must use the row top/height or the raster is clipped when a page break crosses the row.
          const headerRowEl =
            substepHeader.closest('div[style*="space-between"]') ||
            substepHeader.closest('div[style*="justify-content"]');
          const rowRect = headerRowEl
            ? headerRowEl.getBoundingClientRect()
            : null;
          const measureTop = rowRect ? rowRect.top : headerRect.top;

          let hrElement = null;
          let nextSibling = substepHeader.nextElementSibling;

          while (nextSibling && !hrElement) {
            if (nextSibling.tagName === "HR") {
              hrElement = nextSibling;
              break;
            }
            if (nextSibling.hasAttribute("data-export-block")) {
              break;
            }
            nextSibling = nextSibling.nextElementSibling;
          }

          if (!hrElement) {
            hrElement = substepElement.querySelector("hr");
          }
          let headerHeightDom = headerRect.height;
          let headerBottomDom = headerRect.bottom - baseRect.top;

          let hrTopDom = null;
          let hrHeightDom = null;
          if (hrElement) {
            const hrRect = hrElement.getBoundingClientRect();
            headerBottomDom = hrRect.bottom - baseRect.top;
            headerHeightDom = headerBottomDom - (measureTop - baseRect.top);
            hrTopDom = hrRect.top - baseRect.top - (measureTop - baseRect.top);
            hrHeightDom = hrRect.height;
          } else {
            const bottomRef = rowRect ? rowRect.bottom : headerRect.bottom;
            headerBottomDom = bottomRef - baseRect.top;
            headerHeightDom = headerBottomDom - (measureTop - baseRect.top);
          }

          // Substep short description: the .rich-text-content-display inside the substep block
          // that is NOT inside a child node (canvas, rich-text, etc.). Keep header + short desc together on page breaks.
          let substepShortDescTopDom = null;
          let substepShortDescBottomDom = null;
          const shortDescDiv = Array.from(
            substepElement.querySelectorAll(".rich-text-content-display"),
          ).find(
            (div) => div.closest("[data-export-block]") === substepElement,
          );
          if (shortDescDiv) {
            try {
              const shortDescRect = shortDescDiv.getBoundingClientRect();
              if (shortDescRect && shortDescRect.height > 0) {
                substepShortDescTopDom = shortDescRect.top - baseRect.top;
                substepShortDescBottomDom = shortDescRect.bottom - baseRect.top;
              }
            } catch (e) {
              // best-effort
            }
          }

          let sectionIndex = null;
          let stepNumberFromPath = null;
          if (treePathElement) {
            const treePathText = (treePathElement.textContent || "").trim();
            const sectionMatch = treePathText.match(/Section\s+(\d+)/i);
            if (sectionMatch) {
              sectionIndex = parseInt(sectionMatch[1], 10) - 1;
            }
            const stepMatch = treePathText.match(/Step\s+(\d+)/i);
            if (stepMatch) {
              stepNumberFromPath = parseInt(stepMatch[1], 10);
            }
          }

          const substepId = metas.length;
          metas.push({
            substepId,
            element: substepElement,
            headerElement: substepHeader,
            hrElement: hrElement || null,
            substepLabel,
            sectionIndex: sectionIndex !== null ? sectionIndex : 0,
            stepNumber:
              stepNumberFromPath ||
              parseInt(substepLabel.match(/^\d+/)?.[0] || "0", 10),
            treePathElement: treePathElement || null,
            hrElement: hrElement || null,
            hrTopDom: hrTopDom,
            hrHeightDom: hrHeightDom,
            topDom: substepRect.top - baseRect.top,
            bottomDom: substepRect.bottom - baseRect.top,
            headerTopDom: measureTop - baseRect.top,
            headerHeightDom: headerHeightDom,
            headerBottomDom: headerBottomDom,
            headerLeftDom: headerRect.left - baseRect.left,
            headerWidthDom: headerRect.width,
            substepShortDescTopDom: substepShortDescTopDom,
            substepShortDescBottomDom: substepShortDescBottomDom,
            headerCanvas: null,
            topPx: 0,
            bottomPx: 0,
            headerTopPx: 0,
            headerHeightPx: 0,
            headerLeftPx: 0,
            headerWidthPx: 0,
          });
        });
        return metas;
      };

      let blockMetas = collectBlockMetas(cloneRect);
      let tableMetas = collectTableMetas(cloneRect);
      let stepMetas = collectStepMetas(cloneRect);
      let substepMetas = collectSubstepMetas(cloneRect);
      let canvasImageMetas = collectCanvasImageMetas(cloneRect);
      let attachmentImageMetas = collectAttachmentImageMetas(cloneRect);

      // Match Item Required tables to their parent steps
      let itemRequiredTablesCount = 0;
      let linkedCount = 0;

      tableMetas.forEach((tableMeta, index) => {
        if (tableMeta.isItemRequiredTable) {
          itemRequiredTablesCount++;
          if (tableMeta.stepContainer) {
            // Find the matching step meta by comparing the step container element
            const matchingStep = stepMetas.find((stepMeta) => {
              return stepMeta.element === tableMeta.stepContainer;
            });

            if (matchingStep) {
              tableMeta.stepId = matchingStep.stepId;
              linkedCount++;
            } else {
            }
          } else {
          }
        }
      });

      substepMetas.forEach((meta) => {
        if (meta.headerElement) {
          const headerText = (meta.headerElement.textContent || "").trim();
          const baseHeaderText = headerText
            .replace(/\s*\(\d+\/\d+\)\s*$/, "")
            .trim();
          if (baseHeaderText !== headerText) {
            meta.headerElement.textContent = baseHeaderText;
          }
        }
      });

      const createVideoPlaceholder = (width, height) => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#f5f5f5";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.strokeStyle = "#d0d0d0";
        ctx.lineWidth = 1;
        ctx.strokeRect(0.5, 0.5, canvas.width - 1, canvas.height - 1);

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const iconSize = Math.min(canvas.width, canvas.height) * 0.3;

        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#999999";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, iconSize * 0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#666666";
        ctx.beginPath();
        const triSize = iconSize * 0.25;
        ctx.moveTo(centerX - triSize * 0.3, centerY - triSize * 0.6);
        ctx.lineTo(centerX - triSize * 0.3, centerY + triSize * 0.6);
        ctx.lineTo(centerX + triSize * 0.7, centerY);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = "#888888";
        ctx.font = `bold ${Math.max(12, canvas.height * 0.08)}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(
          "Video",
          centerX,
          canvas.height - Math.max(8, canvas.height * 0.05),
        );

        return canvas.toDataURL("image/png");
      };

      const processRichTextMedia = async (element) => {
        const richTextBlocks = element.querySelectorAll(
          "[data-export-block='rich-text']",
        );

        const blobToDataUrl = async (blob) =>
          await new Promise((resolve) => {
            try {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(typeof reader.result === "string" ? reader.result : "");
              };
              reader.onerror = () => resolve("");
              reader.readAsDataURL(blob);
            } catch (e) {
              resolve("");
            }
          });

        const urlToDataUrlBestEffort = async (rawUrl) => {
          if (!rawUrl || typeof rawUrl !== "string") return "";
          const url = rawUrl.trim();
          if (!url) return "";
          if (url.startsWith("data:image")) {
            if (url.includes("svg") || url.includes("svg+xml")) {
              const png = await convertSvgToPng(url);
              return png || "";
            }
            return url;
          }
          // blob: URLs are common for editor-inserted images; fetch + convert.
          if (url.startsWith("blob:")) {
            try {
              const resp = await fetch(url);
              if (!resp.ok) return "";
              const blob = await resp.blob();
              return await blobToDataUrl(blob);
            } catch (e) {
              return "";
            }
          }
          // For http(s) / relative URLs, reuse the existing helper which already resolves absolute URL + fetches.
          try {
            const dataUrl = await getCompanyLogoDataUrl(url);
            return dataUrl || "";
          } catch (e) {
            return "";
          }
        };

        for (const richTextBlock of richTextBlocks) {
          const richTextContent = richTextBlock.querySelector(
            ".rich-text-content-display, .editor-shell",
          );
          if (!richTextContent) continue;

          const richTextVideos = Array.from(
            richTextContent.querySelectorAll("video"),
          );
          for (const video of richTextVideos) {
            try {
              const videoSrc = video.src || video.getAttribute("src") || "";
              if (!videoSrc) continue;

              const liveVideo = Array.from(
                container.querySelectorAll("video"),
              ).find(
                (v) => (v.src || v.getAttribute("src") || "") === videoSrc,
              );

              let dataUrl = "";

              if (liveVideo) {
                try {
                  const waitForVideoReady = () => {
                    return new Promise((resolve) => {
                      if (liveVideo.readyState >= 2) {
                        resolve();
                        return;
                      }

                      const checkReady = () => {
                        if (liveVideo.readyState >= 2) {
                          resolve();
                        } else if (liveVideo.readyState >= 1) {
                          resolve();
                        } else {
                          setTimeout(checkReady, 100);
                        }
                      };

                      const timeout = setTimeout(() => {
                        resolve();
                      }, 3000);

                      liveVideo.addEventListener(
                        "loadedmetadata",
                        () => {
                          clearTimeout(timeout);
                          resolve();
                        },
                        { once: true },
                      );

                      liveVideo.addEventListener(
                        "loadeddata",
                        () => {
                          clearTimeout(timeout);
                          resolve();
                        },
                        { once: true },
                      );

                      checkReady();
                    });
                  };

                  await waitForVideoReady();

                  const can = document.createElement("canvas");
                  const rect = liveVideo.getBoundingClientRect();
                  const vw = Math.max(
                    liveVideo.videoWidth || 0,
                    liveVideo.clientWidth || rect.width || 0,
                    1,
                  );
                  const vh = Math.max(
                    liveVideo.videoHeight || 0,
                    liveVideo.clientHeight || rect.height || 0,
                    1,
                  );
                  can.width = vw;
                  can.height = vh;
                  const ctx = can.getContext("2d");

                  if (
                    liveVideo.readyState >= 2 &&
                    liveVideo.videoWidth > 0 &&
                    liveVideo.videoHeight > 0
                  ) {
                    try {
                      ctx.drawImage(liveVideo, 0, 0, vw, vh);
                      dataUrl = can.toDataURL("image/png");
                    } catch (drawErr) {}
                  }

                  if (!dataUrl) {
                    if (liveVideo.poster) {
                      dataUrl = liveVideo.poster;
                    } else if (liveVideo.getAttribute("data-video-poster")) {
                      dataUrl =
                        liveVideo.getAttribute("data-video-poster") || "";
                    }
                  }
                } catch (err) {
                  dataUrl =
                    liveVideo?.poster ||
                    liveVideo?.getAttribute("data-video-poster") ||
                    "";
                }
              } else {
                dataUrl =
                  video.poster || video.getAttribute("data-video-poster") || "";
              }

              if (!dataUrl) {
                const rect = video.getBoundingClientRect();
                const pw = Math.max(rect.width || 320, 160);
                const ph = Math.max(rect.height || 180, 90);
                dataUrl = createVideoPlaceholder(pw, ph);
              }

              const img = document.createElement("img");
              img.style.maxWidth = "100%";
              img.style.height = "auto";
              img.style.objectFit = "contain";
              img.style.display = "block";
              img.style.visibility = "visible";
              img.style.opacity = "1";

              const rect = video.getBoundingClientRect();
              if (rect.width) img.style.width = rect.width + "px";
              if (rect.height) img.style.height = rect.height + "px";

              if (dataUrl) {
                await new Promise((resolve) => {
                  const timeout = setTimeout(() => {
                    resolve();
                  }, 5000);

                  img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                  };

                  img.onerror = () => {
                    clearTimeout(timeout);
                    if (!dataUrl.startsWith("data:image")) {
                      img.src = createVideoPlaceholder(
                        rect.width || 320,
                        rect.height || 180,
                      );
                      img.onload = () => resolve();
                      img.onerror = () => resolve();
                    } else {
                      resolve();
                    }
                  };

                  img.src = dataUrl;

                  if (
                    img.complete &&
                    img.naturalWidth > 0 &&
                    img.naturalHeight > 0
                  ) {
                    clearTimeout(timeout);
                    resolve();
                  }
                });
              } else {
                img.src = createVideoPlaceholder(
                  rect.width || 320,
                  rect.height || 180,
                );
                await new Promise((resolve) => {
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                });
              }

              video.parentNode?.replaceChild(img, video);
            } catch (err) {}
          }

          const richTextIframes = Array.from(
            richTextContent.querySelectorAll("iframe"),
          );
          for (const iframe of richTextIframes) {
            try {
              const rect = iframe.getBoundingClientRect();
              const pw = Math.max(rect.width || 320, 160);
              const ph = Math.max(rect.height || 180, 90);
              const pc = document.createElement("canvas");
              pc.width = Math.round(pw);
              pc.height = Math.round(ph);
              const pctx = pc.getContext("2d");
              pctx.fillStyle = "#f7f7f7";
              pctx.fillRect(0, 0, pc.width, pc.height);
              pctx.strokeStyle = "#dddddd";
              pctx.strokeRect(0.5, 0.5, pc.width - 1, pc.height - 1);
              pctx.fillStyle = "#666";
              pctx.font = "13px Arial";
              pctx.textAlign = "center";
              pctx.fillText("Embedded Content", pc.width / 2, pc.height / 2);
              const dataUrl = pc.toDataURL("image/png");

              const img = document.createElement("img");
              img.src = dataUrl;
              if (rect.width) img.style.width = rect.width + "px";
              if (rect.height) img.style.height = rect.height + "px";
              img.style.maxWidth = "100%";
              img.style.height = "auto";
              img.style.objectFit = "contain";
              img.style.display = "block";
              img.style.visibility = "visible";
              img.style.opacity = "1";

              iframe.parentNode?.replaceChild(img, iframe);
            } catch (err) {}
          }

          const richTextImages = Array.from(
            richTextContent.querySelectorAll("img"),
          );
          for (const img of richTextImages) {
            const computedStyle = window.getComputedStyle(img);
            const originalFloat = computedStyle.float;
            const originalDisplay = computedStyle.display;
            const originalWidth = computedStyle.width;
            const originalMaxWidth = computedStyle.maxWidth;
            const originalMargin = computedStyle.margin;
            const originalMarginLeft = computedStyle.marginLeft;
            const originalMarginRight = computedStyle.marginRight;
            const originalVerticalAlign = computedStyle.verticalAlign;

            const hasPositionLeft =
              img.classList.contains("position-left") ||
              img.parentElement?.classList.contains("position-left") ||
              img.closest(".inline-editor-image.position-left");
            const hasPositionRight =
              img.classList.contains("position-right") ||
              img.parentElement?.classList.contains("position-right") ||
              img.closest(".inline-editor-image.position-right");

            img.style.setProperty("visibility", "visible", "important");
            img.style.setProperty("opacity", "1", "important");
            img.style.setProperty("object-fit", "contain", "important");

            // Help html2canvas load cross-origin images (when CORS-enabled) and avoid tainting.
            try {
              img.crossOrigin = "anonymous";
              img.referrerPolicy = "no-referrer";
              img.setAttribute("crossorigin", "anonymous");
              img.setAttribute("referrerpolicy", "no-referrer");
              img.loading = "eager";
              img.decoding = "sync";
            } catch (e) {
              // best-effort
            }

            if (originalFloat && originalFloat !== "none") {
              img.style.setProperty("float", originalFloat, "important");
            } else if (hasPositionLeft) {
              img.style.setProperty("float", "left", "important");
            } else if (hasPositionRight) {
              img.style.setProperty("float", "right", "important");
            } else {
              img.style.setProperty("display", "block", "important");
            }

            if (
              originalWidth &&
              originalWidth !== "auto" &&
              originalWidth !== "0px"
            ) {
              img.style.setProperty("width", originalWidth, "important");
              // Safety: even with an explicit pixel width, cap to container to prevent overflow
              img.style.setProperty("max-width", "100%", "important");
            } else if (
              originalMaxWidth &&
              originalMaxWidth !== "none" &&
              originalMaxWidth !== "0px"
            ) {
              img.style.setProperty("max-width", originalMaxWidth, "important");
            } else {
              // Fallback for ALL images (floated or not) – prevents intrinsic-size overflow
              img.style.setProperty("max-width", "100%", "important");
            }

            img.style.setProperty("height", "auto", "important");

            if (originalFloat && originalFloat !== "none") {
              if (originalMargin && originalMargin !== "0px") {
                img.style.setProperty("margin", originalMargin, "important");
              } else {
                if (
                  originalFloat === "left" &&
                  (!originalMarginRight || originalMarginRight === "0px")
                ) {
                  img.style.setProperty("margin-right", "10px", "important");
                  img.style.setProperty("margin-bottom", "10px", "important");
                } else if (
                  originalFloat === "right" &&
                  (!originalMarginLeft || originalMarginLeft === "0px")
                ) {
                  img.style.setProperty("margin-left", "10px", "important");
                  img.style.setProperty("margin-bottom", "10px", "important");
                }
              }
            }

            if (originalVerticalAlign && originalVerticalAlign !== "baseline") {
              img.style.setProperty(
                "vertical-align",
                originalVerticalAlign,
                "important",
              );
            }

            const effectiveSrc =
              (img.currentSrc && String(img.currentSrc)) ||
              (img.getAttribute && img.getAttribute("src")) ||
              img.src ||
              "";

            if (effectiveSrc && !String(effectiveSrc).startsWith("data:")) {
              try {
                // Best-effort: if the image is already loaded in the DOM and is drawable,
                // convert it directly to a data URL (avoids auth/CORS fetch issues for same-origin).
                if (
                  img.complete &&
                  img.naturalWidth > 0 &&
                  img.naturalHeight > 0
                ) {
                  try {
                    const c = document.createElement("canvas");
                    c.width = img.naturalWidth || 1;
                    c.height = img.naturalHeight || 1;
                    const ctx = c.getContext("2d");
                    ctx.drawImage(img, 0, 0);
                    const drawn = c.toDataURL("image/png");
                    if (drawn && drawn.startsWith("data:image")) {
                      img.src = drawn;
                      continue;
                    }
                  } catch (e) {
                    // If tainted, fall back to fetch-based conversion.
                  }
                }
                const dataUrl = await urlToDataUrlBestEffort(
                  String(effectiveSrc),
                );
                if (dataUrl && dataUrl.startsWith("data:image")) {
                  await new Promise((resolve) => {
                    const t = setTimeout(resolve, 5000);
                    img.onload = () => {
                      clearTimeout(t);
                      resolve();
                    };
                    img.onerror = () => {
                      clearTimeout(t);
                      resolve();
                    };
                    img.src = dataUrl;
                    if (
                      img.complete &&
                      img.naturalWidth > 0 &&
                      img.naturalHeight > 0
                    ) {
                      clearTimeout(t);
                      resolve();
                    }
                  });
                }
              } catch (err) {}
            }
          }
        }
      };

      await processRichTextMedia(clone);

      const waitForRichTextImages = async (element) => {
        const richTextBlocks = element.querySelectorAll(
          "[data-export-block='rich-text']",
        );
        if (richTextBlocks.length === 0) return;

        const allRichTextImages = Array.from(
          element.querySelectorAll("[data-export-block='rich-text'] img"),
        );

        if (allRichTextImages.length === 0) {
          return;
        }

        await Promise.all(
          allRichTextImages.map((img, index) => {
            return new Promise((resolve) => {
              if (
                img.complete &&
                img.naturalWidth > 0 &&
                img.naturalHeight > 0
              ) {
                resolve();
                return;
              }

              const timeout = setTimeout(() => {
                resolve();
              }, 10000);
              img.onload = () => {
                clearTimeout(timeout);
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                }
                resolve();
              };

              img.onerror = () => {
                clearTimeout(timeout);
                resolve();
              };

              if (!img.complete && img.src) {
                const currentSrc = img.src;
                img.src = "";
                img.src = currentSrc;
              }
            });
          }),
        );

        await new Promise((resolve) => setTimeout(resolve, 200));
      };

      await waitForRichTextImages(clone);

      const ensureRichTextContentVisible = (element) => {
        const richTextBlocks = element.querySelectorAll(
          "[data-export-block='rich-text']",
        );

        if (richTextBlocks.length === 0) {
          return;
        }

        richTextBlocks.forEach((block, index) => {
          block.style.setProperty("display", "block", "important");
          block.style.setProperty("visibility", "visible", "important");
          block.style.setProperty("opacity", "1", "important");
          block.style.setProperty("width", "100%", "important");
          block.style.setProperty("max-width", "100%", "important");
          block.style.setProperty("box-sizing", "border-box", "important");
          block.style.setProperty("min-height", "20px", "important");
          const allDivs = Array.from(block.querySelectorAll("div"));
          allDivs.forEach((div) => {
            const style = window.getComputedStyle(div);
            const text = div.textContent?.trim() || "";

            const isDescriptionDiv =
              (style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre") &&
              style.marginLeft &&
              style.marginLeft.includes("5px") &&
              !div.classList.contains("rich-text-content-display") &&
              !div.classList.contains("editor-shell") &&
              !div.closest(".rich-text-content-display") &&
              !div.closest(".editor-shell");

            const isCategoryDiv =
              style.marginLeft &&
              style.marginLeft.includes("4px") &&
              style.marginTop &&
              style.marginTop.includes("5px") &&
              !div.classList.contains("rich-text-content-display") &&
              !div.closest(".rich-text-content-display") &&
              !div.closest(".editor-shell") &&
              !div.querySelector(".rich-text-content-display");

            if (isDescriptionDiv || isCategoryDiv) {
              div.style.setProperty("display", "block", "important");
              div.style.setProperty("visibility", "visible", "important");
              div.style.setProperty("opacity", "1", "important");
              div.style.setProperty("color", "#000", "important");
              if (isDescriptionDiv) {
                div.style.setProperty("white-space", "pre-wrap", "important");
                div.style.setProperty("position", "relative", "important");
                div.style.setProperty("z-index", "10", "important");
                div.style.setProperty(
                  "background-color",
                  "transparent",
                  "important",
                );
                div.style.setProperty("width", "auto", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("min-height", "auto", "important");
                div.style.setProperty("max-height", "none", "important");
                div.style.setProperty("max-width", "100%", "important");
                div.style.setProperty("word-break", "break-word", "important");
                div.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                div.style.setProperty("overflow", "visible", "important");
                div.style.setProperty("line-height", "1.5", "important");
                div.style.setProperty("padding", "2px", "important");
                div.style.setProperty("margin-bottom", "10px", "important");
                // Force layout recalculation for large text
                void div.offsetHeight;

                // Ensure parent elements don't hide it and allow expansion
                let parent = div.parentElement;
                let depth = 0;
                while (parent && parent !== block && depth < 10) {
                  const parentStyle = window.getComputedStyle(parent);
                  if (parentStyle.display === "none") {
                    parent.style.setProperty("display", "block", "important");
                  }
                  if (parentStyle.visibility === "hidden") {
                    parent.style.setProperty(
                      "visibility",
                      "visible",
                      "important",
                    );
                  }
                  if (parentStyle.opacity === "0") {
                    parent.style.setProperty("opacity", "1", "important");
                  }
                  if (parentStyle.overflow === "hidden") {
                    parent.style.setProperty(
                      "overflow",
                      "visible",
                      "important",
                    );
                  }
                  if (
                    parentStyle.maxHeight &&
                    parentStyle.maxHeight !== "none"
                  ) {
                    parent.style.setProperty("max-height", "none", "important");
                  }
                  if (
                    parentStyle.height &&
                    parentStyle.height !== "auto" &&
                    !parentStyle.height.includes("%")
                  ) {
                    parent.style.setProperty("height", "auto", "important");
                  }
                  parent = parent.parentElement;
                  depth++;
                }
              }
            }
          });

          const richTextContentDisplays = block.querySelectorAll(
            ".rich-text-content-display",
          );
          const editorShells = block.querySelectorAll(".editor-shell");

          richTextContentDisplays.forEach((richTextContentDisplay) => {
            richTextContentDisplay.style.setProperty(
              "display",
              "block",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "visibility",
              "visible",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "opacity",
              "1",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "width",
              "100%",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "min-height",
              "10px",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "position",
              "relative",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "z-index",
              "2000",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "overflow",
              "visible",
              "important",
            );
            richTextContentDisplay.style.setProperty(
              "max-height",
              "none",
              "important",
            );
          });

          editorShells.forEach((editorShell) => {
            editorShell.style.setProperty("display", "block", "important");
            editorShell.style.setProperty("visibility", "visible", "important");
            editorShell.style.setProperty("opacity", "1", "important");
            editorShell.style.setProperty("width", "100%", "important");
            editorShell.style.setProperty("min-height", "10px", "important");
            editorShell.style.setProperty("position", "relative", "important");
            editorShell.style.setProperty("z-index", "2000", "important");
            editorShell.style.setProperty("overflow", "visible", "important");
            editorShell.style.setProperty("max-height", "none", "important");

            const innerContentDiv = editorShell.querySelector(
              "div:not(.editor-shell)",
            );
            if (innerContentDiv) {
              const isLayoutContainer =
                innerContentDiv.classList.contains(
                  "PlaygroundEditorTheme__layoutContainer",
                ) ||
                innerContentDiv.getAttribute(
                  "data-lexical-layout-container",
                ) === "true";

              if (!isLayoutContainer) {
                innerContentDiv.style.setProperty(
                  "display",
                  "block",
                  "important",
                );
                innerContentDiv.style.setProperty("width", "100%", "important");
              }
              innerContentDiv.style.setProperty(
                "visibility",
                "visible",
                "important",
              );
              innerContentDiv.style.setProperty("opacity", "1", "important");
              innerContentDiv.style.removeProperty("color");
              innerContentDiv.style.setProperty(
                "font-family",
                "Arial, sans-serif",
                "important",
              );
              innerContentDiv.style.setProperty(
                "line-height",
                "1.4",
                "important",
              );
              innerContentDiv.style.setProperty(
                "overflow",
                "visible",
                "important",
              );
              innerContentDiv.style.setProperty(
                "max-height",
                "none",
                "important",
              );
            }
          });

          // Force common Lexical/editor root containers to be visible. Do NOT set color here -
          // it would override user's font colors (e.g. purple) in rich text content.
          const lexicalRoots = block.querySelectorAll(
            ".PlaygroundEditorTheme__root, .editor-content, [data-lexical-editor], [contenteditable]",
          );
          lexicalRoots.forEach((el) => {
            el.style.setProperty("display", "block", "important");
            el.style.setProperty("visibility", "visible", "important");
            el.style.setProperty("opacity", "1", "important");
            el.style.removeProperty("color");
            el.style.setProperty("background", "transparent", "important");
            el.style.setProperty("max-width", "100%", "important");
            el.style.setProperty("overflow", "visible", "important");
            // Ensure cloned content doesn't rely on editing state.
            if (
              el.getAttribute &&
              el.getAttribute("contenteditable") === "true"
            ) {
              el.setAttribute("contenteditable", "false");
            }
          });

          if (
            richTextContentDisplays.length === 0 &&
            editorShells.length === 0
          ) {
            const allDivs = Array.from(block.querySelectorAll("div"));
            for (const div of allDivs) {
              const hasHtmlContent = div.querySelector(
                "p, h1, h2, h3, strong, em, code, pre, ul, ol, table, blockquote, a, img",
              );
              if (hasHtmlContent && div.innerHTML.trim().length > 0) {
                div.style.setProperty("display", "block", "important");
                div.style.setProperty("visibility", "visible", "important");
                div.style.setProperty("opacity", "1", "important");
                div.style.setProperty("width", "100%", "important");
                div.style.setProperty("color", "#000", "important");
                break;
              }
            }
          }

          const allChildren = block.querySelectorAll("*");
          allChildren.forEach((child) => {
            const isHeading = ["H1", "H2", "H3", "H4", "H5", "H6"].includes(
              child.tagName,
            );
            const isTreePath =
              child.textContent?.trim().toLowerCase().includes("section") &&
              (child.textContent?.trim().toLowerCase().includes("step") ||
                child.textContent?.trim().toLowerCase().includes("rich text"));
            const isCheckboxContainer =
              child.tagName === "LABEL" ||
              child.querySelector("input[type='checkbox']");
            const isLayoutContainer =
              child.classList.contains(
                "PlaygroundEditorTheme__layoutContainer",
              ) ||
              child.getAttribute("data-lexical-layout-container") === "true";
            const isLayoutItem = child.classList.contains(
              "PlaygroundEditorTheme__layoutItem",
            );
            const isLayoutElement = isLayoutContainer || isLayoutItem;

            const isContentHeading =
              isHeading &&
              (child.closest(".rich-text-content-display") ||
                child.closest(".editor-shell"));
            const isStructuralHeading = isHeading && !isContentHeading;
            if (
              isStructuralHeading ||
              (isTreePath && !child.closest(".rich-text-content-display")) ||
              isCheckboxContainer
            ) {
              return;
            }

            const isHidden =
              child.style.display === "none" ||
              child.style.visibility === "hidden" ||
              child.classList.contains("hidden");

            if (!isHidden) {
              child.style.setProperty("visibility", "visible", "important");
              child.style.setProperty("opacity", "1", "important");

              if (child.tagName === "IMG") {
                const computedStyle = window.getComputedStyle(child);
                const float = computedStyle.float;
                return;
              }

              if (!isLayoutElement) {
                if (
                  child.tagName === "SPAN" ||
                  child.tagName === "A" ||
                  child.tagName === "STRONG" ||
                  child.tagName === "EM" ||
                  child.tagName === "CODE"
                ) {
                  child.style.setProperty("display", "inline", "important");
                } else if (
                  [
                    "P",
                    "H1",
                    "H2",
                    "H3",
                    "H4",
                    "H5",
                    "H6",
                    "DIV",
                    "UL",
                    "OL",
                    "LI",
                    "TABLE",
                    "TR",
                    "TD",
                    "TH",
                    "BLOCKQUOTE",
                    "PRE",
                    "HR",
                  ].includes(child.tagName)
                ) {
                  const isLexicalChecklistLi =
                    child.tagName === "LI" &&
                    (child.classList.contains(
                      "PlaygroundEditorTheme__listItemChecked",
                    ) ||
                      child.classList.contains(
                        "PlaygroundEditorTheme__listItemUnchecked",
                      ));
                  child.style.setProperty(
                    "display",
                    child.tagName === "TABLE"
                      ? "table"
                      : child.tagName === "TR"
                        ? "table-row"
                        : child.tagName === "TD" || child.tagName === "TH"
                          ? "table-cell"
                          : child.tagName === "LI"
                            ? isLexicalChecklistLi
                              ? "block"
                              : "list-item"
                            : "block",
                    "important",
                  );
                }
              }

              if (
                [
                  "P",
                  "H1",
                  "H2",
                  "H3",
                  "H4",
                  "H5",
                  "H6",
                  "LI",
                  "TD",
                  "TH",
                  "SPAN",
                  "DIV",
                  "A",
                  "BLOCKQUOTE",
                  "PRE",
                  "CODE",
                ].includes(child.tagName)
              ) {
                const computedColor = window.getComputedStyle(child).color;
                if (
                  computedColor === "rgba(0, 0, 0, 0)" ||
                  computedColor === "transparent" ||
                  !computedColor
                ) {
                  child.style.setProperty("color", "#000", "important");
                }
              }

              const computedBg = window.getComputedStyle(child).backgroundColor;
              if (
                computedBg === "rgba(0, 0, 0, 0)" ||
                computedBg === "transparent"
              ) {
                if (["BLOCKQUOTE", "PRE", "CODE"].includes(child.tagName)) {
                  child.style.setProperty(
                    "background-color",
                    "#f5f5f5",
                    "important",
                  );
                }
              }
            }
          });

          void block.offsetHeight;
          richTextContentDisplays.forEach((el) => void el.offsetHeight);
          editorShells.forEach((el) => void el.offsetHeight);

          const hasContent =
            richTextContentDisplays.length > 0 ||
            editorShells.length > 0 ||
            block.querySelector("p, h1, h2, h3, ul, ol, table");
          if (hasContent) {
          } else {
          }
        });
      };

      const ensureEquationsClearAfterFloatedImagesForPDF = (rootElement) => {
        if (!rootElement || !rootElement.querySelectorAll) {
          return;
        }

        const editorShells = rootElement.querySelectorAll(".editor-shell");
        editorShells.forEach((container) => {
          const allElements = Array.from(container.children || []);
          if (!allElements.length) {
            return;
          }

          allElements.forEach((el, index) => {
            if (!el || !el.querySelector) {
              return;
            }

            // Check if this element contains a floated inline image
            const floatedImage = el.querySelector(
              ".inline-editor-image.position-left, .inline-editor-image.position-right",
            );

            if (!floatedImage) {
              return;
            }

            // Ensure there's a clearfix after the floated image element
            const nextSibling = el.nextElementSibling;
            const hasClearfix =
              nextSibling &&
              nextSibling.classList &&
              nextSibling.classList.contains("clearfix");

            if (!hasClearfix) {
              const clearfixDiv = (
                rootElement.ownerDocument || document
              ).createElement("div");
              clearfixDiv.className = "clearfix";
              clearfixDiv.style.setProperty("clear", "both", "important");
              clearfixDiv.style.setProperty("display", "block", "important");
              clearfixDiv.style.setProperty("width", "100%", "important");
              clearfixDiv.style.setProperty("height", "0", "important");
              clearfixDiv.style.setProperty("margin", "0", "important");
              clearfixDiv.style.setProperty("padding", "0", "important");
              clearfixDiv.style.setProperty("overflow", "hidden", "important");
              clearfixDiv.style.setProperty("float", "none", "important");

              if (el.nextSibling) {
                el.parentNode &&
                  el.parentNode.insertBefore(clearfixDiv, el.nextSibling);
              } else {
                el.parentNode && el.parentNode.appendChild(clearfixDiv);
              }
            }

            // Find the next non-empty block element that may contain an equation
            for (let i = index + 1; i < allElements.length; i++) {
              const nextEl = allElements[i];
              if (!nextEl || !nextEl.querySelector) {
                continue;
              }

              const textContent = (nextEl.textContent || "").trim();
              const equationSpan = nextEl.querySelector(
                "[data-lexical-equation]",
              );
              const hasContent =
                textContent.length > 0 ||
                nextEl.querySelector(
                  "img, [data-lexical-equation], table, hr, details",
                ) !== null;

              const containsTable = !!nextEl.querySelector("table");

              // Handle P tags, equation DIVs, and DIV/TABLE elements wrapping Lexical tables
              if (
                hasContent &&
                (nextEl.tagName === "P" ||
                  nextEl.tagName === "TABLE" ||
                  (nextEl.tagName === "DIV" &&
                    (!!equationSpan || containsTable)))
              ) {
                if (
                  nextEl.tagName === "DIV" &&
                  nextEl.classList.contains("equation-clearfix-wrapper")
                ) {
                  // Already wrapped, just ensure it has the right styles
                  nextEl.style.setProperty("clear", "both", "important");
                  nextEl.style.setProperty("display", "block", "important");
                  nextEl.style.setProperty("width", "100%", "important");
                  nextEl.style.setProperty("float", "none", "important");
                  nextEl.style.setProperty("overflow", "hidden", "important");

                  const paraInside = nextEl.querySelector("p");
                  if (paraInside) {
                    paraInside.style.setProperty("clear", "both", "important");
                    paraInside.style.setProperty(
                      "display",
                      "block",
                      "important",
                    );
                    paraInside.style.setProperty("width", "100%", "important");
                    paraInside.style.setProperty("float", "none", "important");
                  }
                } else if (nextEl.tagName === "P") {
                  // Ensure the paragraph after the floated image fully clears it
                  nextEl.style.setProperty("clear", "both", "important");
                  nextEl.style.setProperty("display", "block", "important");
                  nextEl.style.setProperty("width", "100%", "important");
                  nextEl.style.setProperty("float", "none", "important");
                  nextEl.style.setProperty("overflow", "hidden", "important");
                  nextEl.classList.add("equation-after-float");
                } else if (nextEl.tagName === "TABLE" || containsTable) {
                  // Ensure tables following a floated image are rendered below it
                  nextEl.style.setProperty("clear", "both", "important");
                  nextEl.style.setProperty("display", "block", "important");
                  nextEl.style.setProperty("width", "100%", "important");
                  nextEl.style.setProperty("float", "none", "important");
                  if (containsTable && nextEl.tagName === "DIV") {
                    const innerTable = nextEl.querySelector("table");
                    if (innerTable) {
                      innerTable.style.setProperty(
                        "clear",
                        "both",
                        "important",
                      );
                      innerTable.style.setProperty(
                        "display",
                        "block",
                        "important",
                      );
                      innerTable.style.setProperty(
                        "width",
                        "100%",
                        "important",
                      );
                    }
                  }
                }

                // Ensure the actual equation span is rendered as a block below floats
                if (equationSpan) {
                  equationSpan.style.setProperty("clear", "both", "important");
                  equationSpan.style.setProperty(
                    "display",
                    "block",
                    "important",
                  );
                  equationSpan.style.setProperty("float", "none", "important");
                  equationSpan.style.setProperty("width", "100%", "important");
                }

                // Remove Lexical paragraph class to avoid conflicting styles
                if (nextEl.tagName === "P") {
                  nextEl.classList.remove("PlaygroundEditorTheme__paragraph");
                }

                break;
              }
            }
          });

          // Remove Lexical paragraph class from all paragraphs in this editor shell
          const allParagraphs = container.querySelectorAll(
            "p.PlaygroundEditorTheme__paragraph",
          );
          allParagraphs.forEach((p) => {
            p.classList.remove("PlaygroundEditorTheme__paragraph");
          });
        });
      };

      ensureRichTextContentVisible(clone);

      // Lexical checklist items use CSS line-through on .PlaygroundEditorTheme__listItemChecked; html2canvas
      // often misses decoration on <li>, and forcing display:list-item on all <li> breaks the theme's block layout.
      const applyLexicalCheckboxListStrikethroughForPdf = (root) => {
        if (!root || !root.querySelectorAll) return;
        try {
          root
            .querySelectorAll("li.PlaygroundEditorTheme__listItemChecked")
            .forEach((li) => {
              li.style.setProperty("display", "block", "important");
              li.style.setProperty(
                "text-decoration",
                "line-through",
                "important",
              );
              li.style.setProperty(
                "text-decoration-line",
                "line-through",
                "important",
              );
              li.style.setProperty(
                "text-decoration-style",
                "solid",
                "important",
              );
              li.style.setProperty(
                "text-decoration-color",
                "currentColor",
                "important",
              );
              li.style.setProperty(
                "text-decoration-skip-ink",
                "none",
                "important",
              );
              li.querySelectorAll(
                "p, span, strong, em, u, i, h1, h2, h3, h4, h5, h6",
              ).forEach((el) => {
                el.style.setProperty(
                  "text-decoration",
                  "line-through",
                  "important",
                );
                el.style.setProperty(
                  "text-decoration-line",
                  "line-through",
                  "important",
                );
              });
            });
          root.querySelectorAll(".editor-text-strikethrough").forEach((el) => {
            el.style.setProperty(
              "text-decoration",
              "line-through",
              "important",
            );
            el.style.setProperty(
              "text-decoration-line",
              "line-through",
              "important",
            );
          });
        } catch (e) {
          /* best-effort for PDF capture */
        }
      };
      applyLexicalCheckboxListStrikethroughForPdf(clone);

      // html2canvas often omits list markers (::marker / CSS variables). Force visible bullets/numbers
      // for Lexical + Simple Editor lists in short description and rich text (same rules as onclone).
      const injectPdfListBulletStylesForPdf = (root) => {
        if (!root) return;
        try {
          const doc = root.nodeType === 9 ? root : root.ownerDocument;
          if (!doc) return;
          doc
            .querySelectorAll("style[data-pdf-list-bullets]")
            .forEach((s) => s.remove());
          const styleEl = doc.createElement("style");
          styleEl.setAttribute("data-pdf-list-bullets", "true");
          styleEl.textContent = `
            .rich-text-content-display ul.PlaygroundEditorTheme__ul,
            .editor-shell ul.PlaygroundEditorTheme__ul,
            [data-export-block] .rich-text-content-display ul.PlaygroundEditorTheme__ul,
            [data-export-block] .editor-shell ul.PlaygroundEditorTheme__ul {
              list-style-type: disc !important;
              list-style-position: outside !important;
              padding-left: 1.5em !important;
              margin: 0.25em 0 !important;
            }
            .rich-text-content-display li.PlaygroundEditorTheme__listItem:not(.PlaygroundEditorTheme__listItemChecked):not(.PlaygroundEditorTheme__listItemUnchecked),
            .editor-shell li.PlaygroundEditorTheme__listItem:not(.PlaygroundEditorTheme__listItemChecked):not(.PlaygroundEditorTheme__listItemUnchecked) {
              display: list-item !important;
              list-style-type: disc !important;
              list-style-position: outside !important;
              color: #000000 !important;
            }
            .rich-text-content-display li.PlaygroundEditorTheme__nestedListItem,
            .editor-shell li.PlaygroundEditorTheme__nestedListItem {
              display: list-item !important;
              list-style-type: disc !important;
              list-style-position: outside !important;
              color: #000000 !important;
            }
            .rich-text-content-display ol.PlaygroundEditorTheme__ol1, .editor-shell ol.PlaygroundEditorTheme__ol1 { list-style-type: decimal !important; }
            .rich-text-content-display ol.PlaygroundEditorTheme__ol2, .editor-shell ol.PlaygroundEditorTheme__ol2 { list-style-type: upper-alpha !important; }
            .rich-text-content-display ol.PlaygroundEditorTheme__ol3, .editor-shell ol.PlaygroundEditorTheme__ol3 { list-style-type: lower-alpha !important; }
            .rich-text-content-display ol.PlaygroundEditorTheme__ol4, .editor-shell ol.PlaygroundEditorTheme__ol4 { list-style-type: upper-roman !important; }
            .rich-text-content-display ol.PlaygroundEditorTheme__ol5, .editor-shell ol.PlaygroundEditorTheme__ol5 { list-style-type: lower-roman !important; }
            .rich-text-content-display ol[class*="PlaygroundEditorTheme__ol"],
            .editor-shell ol[class*="PlaygroundEditorTheme__ol"] {
              list-style-position: outside !important;
              padding-left: 1.5em !important;
              margin: 0.25em 0 !important;
            }
            .rich-text-content-display ol[class*="PlaygroundEditorTheme__ol"] > li,
            .editor-shell ol[class*="PlaygroundEditorTheme__ol"] > li {
              display: list-item !important;
              color: #000000 !important;
            }
            .rich-text-content-display ul.editor-list-ul,
            .editor-shell ul.editor-list-ul {
              list-style-type: disc !important;
              list-style-position: outside !important;
              padding-left: 1.2em !important;
            }
            .rich-text-content-display li.editor-listitem:not(.editor-nested-listitem),
            .editor-shell li.editor-listitem:not(.editor-nested-listitem),
            .rich-text-content-display li.editor-nested-listitem,
            .editor-shell li.editor-nested-listitem {
              display: list-item !important;
              list-style-type: disc !important;
              list-style-position: outside !important;
              color: #000000 !important;
            }
            .rich-text-content-display ul:not(.PlaygroundEditorTheme__checklist):not([class*="checklist"]),
            .editor-shell ul:not(.PlaygroundEditorTheme__checklist):not([class*="checklist"]) {
              list-style-type: disc !important;
              list-style-position: outside !important;
              padding-left: 1.5em !important;
            }
          `;
          const head = doc.head;
          if (head) {
            head.appendChild(styleEl);
          } else if (root.nodeType === 1 && root.insertBefore) {
            root.insertBefore(styleEl, root.firstChild);
          }
        } catch (e) {
          /* best-effort */
        }
      };
      injectPdfListBulletStylesForPdf(clone);

      // html2canvas frequently paints zero-height list markers. Insert real bullet/number text so PDF matches UI.
      const applyPdfListBulletDomFallback = (root) => {
        if (!root || !root.querySelectorAll) return;
        const doc = root.ownerDocument || document;
        const inExportRichText = (li) =>
          !!(
            li.closest(".rich-text-content-display") ||
            li.closest(".editor-shell") ||
            li.closest("[data-export-block='rich-text']") ||
            li.closest("[data-export-block='section']") ||
            li.closest("[data-export-block='step']") ||
            li.closest("[data-export-block='substep']") ||
            li.closest("[data-export-block='attachment']") ||
            li.closest("[data-export-block='canvas']") ||
            li.closest("[data-export-block='video']")
          );
        try {
          root.querySelectorAll("li").forEach((li) => {
            if (!inExportRichText(li)) return;
            if (li.querySelector("[data-pdf-list-glyph]")) return;
            if (
              li.classList.contains("PlaygroundEditorTheme__listItemChecked") ||
              li.classList.contains("PlaygroundEditorTheme__listItemUnchecked")
            ) {
              return;
            }
            const parentList = li.parentElement;
            if (
              !parentList ||
              (parentList.tagName !== "UL" && parentList.tagName !== "OL")
            )
              return;
            if (parentList.tagName === "UL") {
              if (
                parentList.classList.contains(
                  "PlaygroundEditorTheme__checklist",
                ) ||
                (typeof parentList.className === "string" &&
                  parentList.className.includes("checklist"))
              ) {
                return;
              }
            }
            const glyph = doc.createElement("span");
            glyph.setAttribute("data-pdf-list-glyph", "true");
            glyph.style.setProperty("display", "inline-block", "important");
            glyph.style.setProperty("margin-right", "0.35em", "important");
            glyph.style.setProperty("color", "#000000", "important");
            glyph.style.setProperty("font-weight", "bold", "important");
            glyph.style.setProperty("vertical-align", "baseline", "important");
            glyph.style.setProperty("white-space", "nowrap", "important");
            if (parentList.tagName === "OL") {
              const lis = Array.from(parentList.children).filter(
                (c) => c.tagName === "LI",
              );
              const n = lis.indexOf(li) + 1;
              const start = parseInt(
                parentList.getAttribute("start") || "1",
                10,
              );
              const num = Number.isFinite(start) ? start + n - 1 : n;
              glyph.textContent = `${num}. `;
            } else {
              const isNested =
                li.classList.contains(
                  "PlaygroundEditorTheme__nestedListItem",
                ) || li.classList.contains("editor-nested-listitem");
              glyph.textContent = isNested ? "\u25E6 " : "\u2022 ";
            }
            parentList.style.setProperty("list-style", "none", "important");
            li.style.setProperty("list-style", "none", "important");
            li.insertBefore(glyph, li.firstChild);
          });
        } catch (e) {
          /* best-effort */
        }
      };
      applyPdfListBulletDomFallback(clone);

      ensureEquationsClearAfterFloatedImagesForPDF(clone);

      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const liveVideos = Array.from(container.querySelectorAll("video"));
        const cloneVideos = Array.from(clone.querySelectorAll("video"));

        const getVideoId = (video) => {
          return (
            video.src || video.getAttribute("src") || video.currentSrc || ""
          );
        };

        for (const cloneVideo of cloneVideos) {
          const cloneVideoId = getVideoId(cloneVideo);
          if (!cloneVideoId) continue;

          const liveVideo = liveVideos.find((v) => {
            const liveId = getVideoId(v);
            return liveId && liveId === cloneVideoId;
          });

          if (!liveVideo) {
            continue;
          }

          let liveVideoBlock = liveVideo.closest('[data-export-block="video"]');
          let cloneVideoBlock = cloneVideo.closest(
            '[data-export-block="video"]',
          );

          if (cloneVideoBlock) {
            const buttons = Array.from(
              cloneVideoBlock.querySelectorAll("button"),
            );
            buttons.forEach((btn) => {
              const title = (btn.getAttribute("title") || "").toLowerCase();
              const className = (btn.className || "").toLowerCase();
              const hasSvg = btn.querySelector("svg");

              if (
                title.includes("fullscreen") ||
                title.includes("play") ||
                className.includes("icon-button") ||
                hasSvg
              ) {
                btn.remove();
              }
            });

            const svgs = Array.from(cloneVideoBlock.querySelectorAll("svg"));
            svgs.forEach((svg) => {
              const parent = svg.parentElement;
              if (parent && parent.tagName === "BUTTON") {
                return;
              }
              svg.remove();
            });

            const overlayDivs = Array.from(
              cloneVideoBlock.querySelectorAll("div"),
            );
            overlayDivs.forEach((div) => {
              const style = window.getComputedStyle(div);
              if (
                style.position === "absolute" &&
                (style.top === "10px" || style.right === "10px") &&
                div.querySelector("svg")
              ) {
                div.remove();
              }
            });
          }

          cloneVideo.removeAttribute("controls");
          cloneVideo.removeAttribute("controlsList");
          cloneVideo.style.setProperty("pointer-events", "none", "important");
          cloneVideo.style.setProperty(
            "background",
            "transparent",
            "important",
          );

          liveVideo.removeAttribute("controls");
          liveVideo.removeAttribute("controlsList");

          let videoUrl = "";
          let videoDescription = "";
          let liveUrlDiv = null;
          let liveDescDiv = null;

          if (liveVideoBlock) {
            const allElements = Array.from(
              liveVideoBlock.querySelectorAll("*"),
            );
            const allChildren = Array.from(liveVideoBlock.children);

            const videoIndex = Array.from(liveVideoBlock.children).indexOf(
              liveVideo,
            );

            for (let i = videoIndex + 1; i < allChildren.length; i++) {
              const child = allChildren[i];
              if (child === liveVideo || child.tagName === "VIDEO") continue;

              const text = child.textContent?.trim() || "";
              if (
                text &&
                (text.startsWith("http://") || text.startsWith("https://"))
              ) {
                videoUrl = text;
                liveUrlDiv = child;
                break;
              }

              const nestedUrl = child.querySelector("*");
              if (nestedUrl) {
                const nestedText = nestedUrl.textContent?.trim() || "";
                if (
                  nestedText &&
                  (nestedText.startsWith("http://") ||
                    nestedText.startsWith("https://"))
                ) {
                  videoUrl = nestedText;
                  liveUrlDiv = nestedUrl;
                  break;
                }
              }
            }

            if (!videoUrl) {
              for (const elem of allElements) {
                if (elem === liveVideo || elem.tagName === "VIDEO") continue;
                const text = elem.textContent?.trim() || "";
                if (
                  text &&
                  (text.startsWith("http://") || text.startsWith("https://"))
                ) {
                  videoUrl = text;
                  liveUrlDiv = elem;
                  break;
                }
              }
            }

            for (let i = videoIndex + 1; i < allChildren.length; i++) {
              const child = allChildren[i];
              if (child === liveVideo || child.tagName === "VIDEO") continue;

              const text = child.textContent?.trim() || "";
              if (!text) continue;

              if (text === videoUrl || (videoUrl && text.includes(videoUrl)))
                continue;

              if (text.startsWith("http://") || text.startsWith("https://"))
                continue;

              if (
                child.tagName === "H1" ||
                child.tagName === "H2" ||
                child.tagName === "H3" ||
                child.tagName === "H4" ||
                child.tagName === "H5" ||
                child.tagName === "H6" ||
                child.tagName === "LABEL" ||
                child.querySelector("input[type='checkbox']")
              ) {
                continue;
              }

              const style = window.getComputedStyle(child);
              if (
                style.whiteSpace === "pre-wrap" ||
                style.whiteSpace === "pre" ||
                (text.length > 0 &&
                  !text.match(/^(Video|video)\s*\d*$/i) &&
                  !text.match(/^rich text\s*\d*$/i) &&
                  !text.match(/^attachment\s*\d*$/i))
              ) {
                videoDescription = text;
                liveDescDiv = child;
                break;
              }
            }

            if (!videoDescription) {
              for (const elem of allElements) {
                if (elem === liveVideo || elem.tagName === "VIDEO") continue;
                if (elem === liveUrlDiv) continue;

                const text = elem.textContent?.trim() || "";
                if (!text) continue;

                if (text === videoUrl || (videoUrl && text.includes(videoUrl)))
                  continue;
                if (text.startsWith("http://") || text.startsWith("https://"))
                  continue;

                if (
                  elem.tagName === "H1" ||
                  elem.tagName === "H2" ||
                  elem.tagName === "H3" ||
                  elem.tagName === "H4" ||
                  elem.tagName === "H5" ||
                  elem.tagName === "H6" ||
                  elem.tagName === "LABEL" ||
                  elem.querySelector("input[type='checkbox']")
                ) {
                  continue;
                }

                const style = window.getComputedStyle(elem);
                if (
                  style.whiteSpace === "pre-wrap" ||
                  style.whiteSpace === "pre" ||
                  (text.length > 0 &&
                    !text.match(/^(Video|video)\s*\d*$/i) &&
                    !text.match(/^rich text\s*\d*$/i) &&
                    !text.match(/^attachment\s*\d*$/i))
                ) {
                  videoDescription = text;
                  liveDescDiv = elem;
                  break;
                }
              }
            }
          }

          let dataUrl = "";
          try {
            const waitForVideoReady = () => {
              return new Promise((resolve) => {
                if (
                  liveVideo.readyState >= 2 &&
                  liveVideo.videoWidth > 0 &&
                  liveVideo.videoHeight > 0
                ) {
                  resolve();
                  return;
                }

                if (liveVideo.readyState === 0) {
                  liveVideo.load();
                }

                let checkCount = 0;
                const maxChecks = 30;
                const checkReady = () => {
                  checkCount++;
                  if (
                    liveVideo.readyState >= 2 &&
                    liveVideo.videoWidth > 0 &&
                    liveVideo.videoHeight > 0
                  ) {
                    resolve();
                  } else if (liveVideo.readyState >= 1) {
                    resolve();
                  } else if (checkCount >= maxChecks) {
                    resolve();
                  } else {
                    setTimeout(checkReady, 100);
                  }
                };

                const timeout = setTimeout(() => {
                  resolve();
                }, 5000);

                const onLoadedMetadata = () => {
                  clearTimeout(timeout);
                  setTimeout(() => resolve(), 200);
                };

                const onLoadedData = () => {
                  clearTimeout(timeout);
                  resolve();
                };

                const onCanPlay = () => {
                  clearTimeout(timeout);
                  resolve();
                };

                liveVideo.addEventListener("loadedmetadata", onLoadedMetadata, {
                  once: true,
                });
                liveVideo.addEventListener("loadeddata", onLoadedData, {
                  once: true,
                });
                liveVideo.addEventListener("canplay", onCanPlay, {
                  once: true,
                });

                checkReady();
              });
            };

            await waitForVideoReady();

            const can = document.createElement("canvas");
            const rect = liveVideo.getBoundingClientRect();
            const vw = Math.max(
              liveVideo.videoWidth || 0,
              liveVideo.clientWidth || rect.width || 0,
              1,
            );
            const vh = Math.max(
              liveVideo.videoHeight || 0,
              liveVideo.clientHeight || rect.height || 0,
              1,
            );

            can.width = vw;
            can.height = vh;
            const ctx = can.getContext("2d");

            if (
              liveVideo.readyState >= 2 &&
              liveVideo.videoWidth > 0 &&
              liveVideo.videoHeight > 0
            ) {
              try {
                ctx.drawImage(liveVideo, 0, 0, vw, vh);

                const imageData = ctx.getImageData(
                  0,
                  0,
                  Math.min(vw, 100),
                  Math.min(vh, 100),
                );
                const pixels = imageData.data;
                let nonBlackPixels = 0;
                const sampleSize = Math.min(pixels.length, 40000);
                for (let i = 0; i < sampleSize; i += 4) {
                  const r = pixels[i];
                  const g = pixels[i + 1];
                  const b = pixels[i + 2];
                  if (r > 10 || g > 10 || b > 10) {
                    nonBlackPixels++;
                  }
                }

                const nonBlackRatio = nonBlackPixels / (sampleSize / 4);
                if (nonBlackRatio < 0.01) {
                  dataUrl = "";
                } else {
                  dataUrl = can.toDataURL("image/png");
                  if (dataUrl && dataUrl.length > 100) {
                  } else {
                    dataUrl = "";
                  }
                }
              } catch (drawErr) {
                dataUrl = "";
              }
            } else {
            }

            if (!dataUrl) {
              if (liveVideo.poster) {
                dataUrl = liveVideo.poster;
              } else if (liveVideo.getAttribute("data-video-poster")) {
                dataUrl = liveVideo.getAttribute("data-video-poster") || "";
              } else {
              }
            }
          } catch (err) {
            dataUrl =
              liveVideo?.poster ||
              liveVideo?.getAttribute("data-video-poster") ||
              "";
          }

          if (!dataUrl) {
            const rect = liveVideo.getBoundingClientRect();
            const pw = Math.max(
              liveVideo.clientWidth ||
                rect.width ||
                liveVideo.videoWidth ||
                320,
              160,
            );
            const ph = Math.max(
              liveVideo.clientHeight ||
                rect.height ||
                liveVideo.videoHeight ||
                180,
              90,
            );
            dataUrl = createVideoPlaceholder(pw, ph);
          }

          const rect = liveVideo.getBoundingClientRect();
          const w = Math.max(
            liveVideo.clientWidth || rect.width || liveVideo.videoWidth || 320,
            160,
          );
          const h = Math.max(
            liveVideo.clientHeight ||
              rect.height ||
              liveVideo.videoHeight ||
              180,
            90,
          );

          const img = document.createElement("img");

          img.style.setProperty("display", "block", "important");
          img.style.setProperty("visibility", "visible", "important");
          img.style.setProperty("opacity", "1", "important");
          img.style.setProperty("max-width", "100%", "important");
          img.style.setProperty("width", w + "px", "important");
          img.style.setProperty("height", "auto", "important");
          img.style.setProperty("object-fit", "contain", "important");
          img.style.setProperty("background", "transparent", "important");

          if (dataUrl) {
            await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                resolve();
              }, 10000);
              img.onload = () => {
                clearTimeout(timeout);
                resolve();
              };

              img.onerror = (err) => {
                clearTimeout(timeout);
                if (!dataUrl.startsWith("data:image")) {
                  img.src = createVideoPlaceholder(w, h);
                  img.onload = () => {
                    resolve();
                  };
                  img.onerror = () => resolve();
                } else {
                  resolve();
                }
              };

              img.src = dataUrl;

              if (
                img.complete &&
                img.naturalWidth > 0 &&
                img.naturalHeight > 0
              ) {
                clearTimeout(timeout);
                resolve();
              }
            });
          } else {
            img.src = createVideoPlaceholder(w, h);
            await new Promise((resolve) => {
              img.onload = () => {
                resolve();
              };
              img.onerror = () => {
                resolve();
              };
            });
          }

          if (!img.src || (!img.complete && img.naturalWidth === 0)) {
          }

          if (cloneVideo.parentNode) {
            const oldParent = cloneVideo.parentNode;

            oldParent.replaceChild(img, cloneVideo);

            void img.offsetHeight;

            if (cloneVideoBlock) {
              const remainingVideos = cloneVideoBlock.querySelectorAll("video");
              remainingVideos.forEach((v) => {
                v.remove();
              });

              const remainingButtons =
                cloneVideoBlock.querySelectorAll("button");
              remainingButtons.forEach((btn) => {
                const title = (btn.getAttribute("title") || "").toLowerCase();
                if (
                  title.includes("fullscreen") ||
                  title.includes("play") ||
                  btn.querySelector("svg")
                ) {
                  btn.remove();
                }
              });

              img.style.setProperty("display", "block", "important");
              img.style.setProperty("visibility", "visible", "important");
              img.style.setProperty("opacity", "1", "important");
              img.style.setProperty("max-width", "100%", "important");
              img.style.setProperty("height", "auto", "important");
              img.style.setProperty("object-fit", "contain", "important");

              const imgRect = img.getBoundingClientRect();

              if (imgRect.width === 0 || imgRect.height === 0) {
              }
            }
          } else {
          }

          if (cloneVideoBlock) {
            const allCloneChildren = Array.from(cloneVideoBlock.children);
            const allCloneElements = Array.from(
              cloneVideoBlock.querySelectorAll("*"),
            );

            if (videoUrl) {
              let urlDiv = null;

              urlDiv = allCloneChildren.find((child) => {
                if (child === img || child.tagName === "IMG") return false;
                const text = child.textContent?.trim() || "";
                return (
                  text &&
                  (text.startsWith("http://") ||
                    text.startsWith("https://") ||
                    text === videoUrl)
                );
              });

              if (!urlDiv) {
                urlDiv = allCloneElements.find((elem) => {
                  if (elem === img || elem.tagName === "IMG") return false;
                  const text = elem.textContent?.trim() || "";
                  return (
                    text &&
                    (text.startsWith("http://") ||
                      text.startsWith("https://") ||
                      text === videoUrl)
                  );
                });
              }

              if (!urlDiv) {
                urlDiv = document.createElement("div");
                urlDiv.style.marginTop = "10px";
                urlDiv.style.display = "block";
                urlDiv.style.visibility = "visible";
                urlDiv.style.opacity = "1";
                urlDiv.style.color = "#000";
                urlDiv.style.fontSize = "14px";
                const urlAnchor = document.createElement("a");
                urlAnchor.href = videoUrl;
                urlAnchor.textContent = videoUrl;
                urlAnchor.target = "_blank";
                urlAnchor.rel = "noopener noreferrer";
                urlAnchor.style.color = "#0066cc";
                urlAnchor.style.textDecoration = "underline";
                urlDiv.appendChild(urlAnchor);
                if (img.nextSibling) {
                  cloneVideoBlock.insertBefore(urlDiv, img.nextSibling);
                } else {
                  cloneVideoBlock.appendChild(urlDiv);
                }
              } else {
                const urlAnchor = document.createElement("a");
                urlAnchor.href = videoUrl;
                urlAnchor.textContent = videoUrl;
                urlAnchor.target = "_blank";
                urlAnchor.rel = "noopener noreferrer";
                urlAnchor.style.color = "#0066cc";
                urlAnchor.style.textDecoration = "underline";
                urlDiv.innerHTML = "";
                urlDiv.appendChild(urlAnchor);
                urlDiv.style.setProperty("display", "block", "important");
                urlDiv.style.setProperty("visibility", "visible", "important");
                urlDiv.style.setProperty("opacity", "1", "important");
                urlDiv.style.setProperty("margin-top", "10px", "important");
                urlDiv.style.setProperty("color", "#000", "important");
                urlDiv.style.setProperty("font-size", "14px", "important");
                let parent = urlDiv.parentElement;
                while (parent && parent !== cloneVideoBlock) {
                  parent.style.setProperty("display", "block", "important");
                  parent.style.setProperty(
                    "visibility",
                    "visible",
                    "important",
                  );
                  parent.style.setProperty("opacity", "1", "important");
                  parent = parent.parentElement;
                }
              }
            }

            if (videoDescription) {
              let descDiv = null;

              for (const child of allCloneChildren) {
                if (child === img || child.tagName === "IMG") continue;
                const text = child.textContent?.trim() || "";
                if (!text) continue;

                if (
                  text === videoUrl ||
                  (videoUrl && text.includes(videoUrl)) ||
                  text.startsWith("http://") ||
                  text.startsWith("https://")
                )
                  continue;

                if (
                  child.tagName === "H1" ||
                  child.tagName === "H2" ||
                  child.tagName === "H3" ||
                  child.tagName === "H4" ||
                  child.tagName === "H5" ||
                  child.tagName === "H6" ||
                  child.tagName === "LABEL" ||
                  child.querySelector("input[type='checkbox']")
                ) {
                  continue;
                }

                const style = window.getComputedStyle(child);
                if (
                  style.whiteSpace === "pre-wrap" ||
                  style.whiteSpace === "pre" ||
                  (text.length > 0 &&
                    !text.match(/^(Video|video)\s*\d*$/i) &&
                    !text.match(/^rich text\s*\d*$/i) &&
                    !text.match(/^attachment\s*\d*$/i))
                ) {
                  descDiv = child;
                  break;
                }
              }

              if (!descDiv) {
                for (const elem of allCloneElements) {
                  if (elem === img || elem.tagName === "IMG") continue;
                  const text = elem.textContent?.trim() || "";
                  if (!text) continue;

                  if (
                    text === videoUrl ||
                    (videoUrl && text.includes(videoUrl)) ||
                    text.startsWith("http://") ||
                    text.startsWith("https://")
                  )
                    continue;

                  if (
                    elem.tagName === "H1" ||
                    elem.tagName === "H2" ||
                    elem.tagName === "H3" ||
                    elem.tagName === "H4" ||
                    elem.tagName === "H5" ||
                    elem.tagName === "H6" ||
                    elem.tagName === "LABEL" ||
                    elem.querySelector("input[type='checkbox']")
                  ) {
                    continue;
                  }

                  const style = window.getComputedStyle(elem);
                  if (
                    style.whiteSpace === "pre-wrap" ||
                    style.whiteSpace === "pre" ||
                    (text.length > 0 &&
                      !text.match(/^(Video|video)\s*\d*$/i) &&
                      !text.match(/^rich text\s*\d*$/i) &&
                      !text.match(/^attachment\s*\d*$/i))
                  ) {
                    descDiv = elem;
                    break;
                  }
                }
              }

              if (!descDiv) {
                descDiv = document.createElement("div");
                descDiv.style.whiteSpace = "pre-wrap";
                descDiv.style.display = "block";
                descDiv.style.visibility = "visible";
                descDiv.style.opacity = "1";
                descDiv.style.color = "#000";
                descDiv.style.fontSize = "14px";
                descDiv.style.marginTop = "4px";
                descDiv.textContent = videoDescription;
                const urlDiv =
                  allCloneChildren.find((child) => {
                    if (child === img || child.tagName === "IMG") return false;
                    const text = child.textContent?.trim() || "";
                    return (
                      text &&
                      (text.startsWith("http://") ||
                        text.startsWith("https://") ||
                        text === videoUrl)
                    );
                  }) ||
                  allCloneElements.find((elem) => {
                    if (elem === img || elem.tagName === "IMG") return false;
                    const text = elem.textContent?.trim() || "";
                    return (
                      text &&
                      (text.startsWith("http://") ||
                        text.startsWith("https://") ||
                        text === videoUrl)
                    );
                  }) ||
                  img;
                if (urlDiv && urlDiv.nextSibling) {
                  cloneVideoBlock.insertBefore(descDiv, urlDiv.nextSibling);
                } else if (urlDiv && urlDiv.parentElement) {
                  urlDiv.parentElement.insertBefore(
                    descDiv,
                    urlDiv.nextSibling,
                  );
                } else if (img.nextSibling) {
                  cloneVideoBlock.insertBefore(descDiv, img.nextSibling);
                } else {
                  cloneVideoBlock.appendChild(descDiv);
                }
              } else {
                // Do NOT overwrite descDiv.textContent - it contains rich HTML (links, images) from Lexical.
                // Only ensure visibility; preserve existing content.
                descDiv.style.setProperty("display", "block", "important");
                descDiv.style.setProperty("visibility", "visible", "important");
                descDiv.style.setProperty("opacity", "1", "important");
                descDiv.style.setProperty(
                  "white-space",
                  "pre-wrap",
                  "important",
                );
                descDiv.style.setProperty("color", "#000", "important");
                descDiv.style.setProperty("font-size", "14px", "important");
                descDiv.style.setProperty("margin-top", "4px", "important");
                descDiv.style.setProperty("overflow", "visible", "important");
                descDiv.style.setProperty("max-height", "none", "important");
                let parent = descDiv.parentElement;
                while (parent && parent !== cloneVideoBlock) {
                  parent.style.setProperty("display", "block", "important");
                  parent.style.setProperty(
                    "visibility",
                    "visible",
                    "important",
                  );
                  parent.style.setProperty("opacity", "1", "important");
                  parent.style.setProperty("overflow", "visible", "important");
                  parent.style.setProperty("max-height", "none", "important");
                  parent = parent.parentElement;
                }
              }
            }
          }
        }

        const remainingVideos = Array.from(clone.querySelectorAll("video"));
        if (remainingVideos.length > 0) {
          remainingVideos.forEach((v) => {
            const rect = v.getBoundingClientRect();
            const pw = Math.max(rect.width || 320, 160);
            const ph = Math.max(rect.height || 180, 90);

            const placeholderImg = document.createElement("img");
            placeholderImg.src = createVideoPlaceholder(pw, ph);
            placeholderImg.style.width = rect.width + "px";
            placeholderImg.style.height = "auto";
            placeholderImg.style.display = "block";
            placeholderImg.style.visibility = "visible";
            placeholderImg.style.opacity = "1";
            placeholderImg.style.setProperty(
              "object-fit",
              "contain",
              "important",
            );

            if (v.parentNode) {
              v.parentNode.replaceChild(placeholderImg, v);
            } else {
              v.remove();
            }
          });
        }

        const allButtons = Array.from(clone.querySelectorAll("button"));
        allButtons.forEach((btn) => {
          const title = (btn.getAttribute("title") || "").toLowerCase();
          const className = (btn.className || "").toLowerCase();
          if (
            title.includes("fullscreen") ||
            title.includes("play") ||
            className.includes("icon-button") ||
            btn.querySelector("svg")
          ) {
            const videoBlock = btn.closest('[data-export-block="video"]');
            if (videoBlock) {
              btn.remove();
            }
          }
        });

        let styleElement = clone.querySelector("style[data-pdf-video-hide]");
        if (!styleElement) {
          styleElement = document.createElement("style");
          styleElement.setAttribute("data-pdf-video-hide", "true");
          styleElement.textContent = `
            video::-webkit-media-controls { display: none !important; }
            video::-webkit-media-controls-enclosure { display: none !important; }
            video::-webkit-media-controls-panel { display: none !important; }
            video::-webkit-media-controls-play-button { display: none !important; }
            video::-webkit-media-controls-start-playback-button { display: none !important; }
            video::before { display: none !important; content: none !important; }
            video::after { display: none !important; content: none !important; }
            [data-export-block="video"] button { display: none !important; visibility: hidden !important; }
            [data-export-block="video"] svg { display: none !important; visibility: hidden !important; }
          `;
          clone.appendChild(styleElement);
        }
      } catch (err) {}

      try {
        const liveCanvases = Array.from(
          container.querySelectorAll("canvas"),
        ).filter((c) => !c.closest("[data-export-block='canvas']"));
        const cloneCanvases = Array.from(
          clone.querySelectorAll("canvas"),
        ).filter((c) => !c.closest("[data-export-block='canvas']"));
        for (let i = 0; i < cloneCanvases.length; i++) {
          const live = liveCanvases[i];
          const c = cloneCanvases[i];
          if (!live || !c) continue;
          let dataURL = "";
          try {
            dataURL = live.toDataURL("image/png");
          } catch (_) {
            const snap = await html2canvas(live, {
              useCORS: true,
              crossOrigin: "anonymous",
              allowTaint: false,
              imageTimeout: 0,
              backgroundColor: "#fff",
              scale: 2,
              logging: false,
            });
            dataURL = snap.toDataURL("image/png");
          }
          const img = document.createElement("img");
          img.src = dataURL;
          const rect = live.getBoundingClientRect();
          if (rect.width) img.style.width = rect.width + "px";
          if (rect.height) img.style.height = rect.height + "px";
          img.style.objectFit = "contain";
          c.replaceWith(img);
        }
      } catch (_) {}

      const cloneImages = Array.from(clone.querySelectorAll("img"));
      if (cloneImages.length) {
        await Promise.all(
          cloneImages.map((img, index) => {
            return new Promise((resolve) => {
              img.style.setProperty("display", "block", "important");
              img.style.setProperty("visibility", "visible", "important");
              img.style.setProperty("opacity", "1", "important");

              if (
                img.complete &&
                img.naturalWidth > 0 &&
                img.naturalHeight > 0
              ) {
                resolve();
                return;
              }

              if (img.decode) {
                img
                  .decode()
                  .then(() => {
                    resolve();
                  })
                  .catch(() => {
                    const timeout = setTimeout(() => {
                      resolve();
                    }, 10000);

                    img.onload = () => {
                      clearTimeout(timeout);
                      resolve();
                    };
                    img.onerror = () => {
                      clearTimeout(timeout);
                      resolve();
                    };
                  });
              } else {
                const timeout = setTimeout(() => {
                  resolve();
                }, 10000);

                img.onload = () => {
                  clearTimeout(timeout);
                  resolve();
                };
                img.onerror = () => {
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          }),
        );
      }

      ensureNestedElementsVisible(clone);
      applyStepSpacing(clone);
      applyAttachmentRichTextSpacing(clone);
      applyItemRequiredEmptySpacing(clone);
      applyItemRequiredDynamicSpacing(clone);

      cloneRect = clone.getBoundingClientRect();
      revisionHeadingRect =
        revisionHeadingElement?.getBoundingClientRect() || null;

      ensureNestedElementsVisible(clone);
      applyStepSpacing(clone);
      applyAttachmentRichTextSpacing(clone);
      applyItemRequiredEmptySpacing(clone);
      applyItemRequiredDynamicSpacing(clone);

      cloneRect = clone.getBoundingClientRect();

      void clone.offsetHeight;
      await new Promise((resolve) => setTimeout(resolve, 100));

      blockMetas = collectBlockMetas(cloneRect);

      blockMetas.forEach((meta) => {
        if (meta.label === "rich-text" && meta.element) {
          void meta.element.offsetHeight;

          const updatedRect = meta.element.getBoundingClientRect();
          if (
            updatedRect.height > 0 &&
            Math.abs(updatedRect.height - meta.heightDom) > 5
          ) {
            meta.heightDom = updatedRect.height;
            meta.topDom = updatedRect.top - cloneRect.top;
            meta.bottomDom = updatedRect.bottom - cloneRect.top;
          }
        }
      });
      tableMetas = collectTableMetas(cloneRect);
      stepMetas = collectStepMetas(cloneRect);
      substepMetas = collectSubstepMetas(cloneRect);
      canvasImageMetas = collectCanvasImageMetas(cloneRect);

      // CRITICAL: Re-link Item Required tables to their parent steps after recollection
      let relinkedCount = 0;
      tableMetas.forEach((tableMeta, index) => {
        if (tableMeta.isItemRequiredTable && tableMeta.stepContainer) {
          const matchingStep = stepMetas.find((stepMeta) => {
            return stepMeta.element === tableMeta.stepContainer;
          });
          if (matchingStep) {
            tableMeta.stepId = matchingStep.stepId;
            relinkedCount++;
          }
        }
      });
      const childBlockLabels = new Set([
        "attachment",
        "rich-text",
        "video",
        "form",
        "canvas",
      ]);

      blockMetas.forEach((meta) => {
        if (!childBlockLabels.has(meta.label) || !meta.element) return;

        let parent = meta.element.parentElement;
        while (parent && parent !== clone) {
          if (parent.hasAttribute("data-export-block")) {
            const parentLabel = parent.getAttribute("data-export-block") || "";

            if (parentLabel === "substep") {
              const substepMeta = substepMetas.find(
                (s) => s.element === parent,
              );
              if (substepMeta) {
                meta.substepId = substepMeta.substepId;
              }
            }

            if (parentLabel === "step") {
              const stepMeta = stepMetas.find((s) => s.element === parent);
              if (stepMeta) {
                meta.stepId = stepMeta.stepId;
                break;
              }
            }
          }
          parent = parent.parentElement;
        }
      });

      const blockTypesFound = new Set(blockMetas.map((m) => m.label));
      const substepChildren = blockMetas.filter(
        (m) =>
          m.label === "attachment" ||
          m.label === "rich-text" ||
          m.label === "video" ||
          m.label === "canvas",
      );

      const childElementsWithSteps = blockMetas.filter(
        (m) =>
          childBlockLabels.has(m.label) &&
          m.stepId !== null &&
          m.stepId !== undefined,
      );

      const applyRevisionTableStylesToTables = (element) => {
        const findTableByHeading = (headingText, headingLevel = "h3") => {
          const headings = Array.from(element.querySelectorAll(headingLevel));
          for (const heading of headings) {
            const text = (heading.textContent || "").trim().toLowerCase();
            if (headingText === "Training Log" && text === "training log") {
              let nextSibling = heading.nextElementSibling;
              while (nextSibling) {
                if (
                  nextSibling.tagName === "TABLE" ||
                  nextSibling.querySelector("table")
                ) {
                  const table =
                    nextSibling.tagName === "TABLE"
                      ? nextSibling
                      : nextSibling.querySelector("table");
                  return { table, heading };
                }
                nextSibling = nextSibling.nextElementSibling;
              }
            } else if (
              headingText === "BOM" &&
              (text === "bom" || text.endsWith(" bom"))
            ) {
              let nextSibling = heading.nextElementSibling;
              while (nextSibling) {
                const table =
                  nextSibling.tagName === "TABLE"
                    ? nextSibling
                    : nextSibling.querySelector("table");
                if (table) return { table, heading };
                nextSibling = nextSibling.nextElementSibling;
              }
            }
          }
          return null;
        };

        const trainingLogHeadings = Array.from(
          element.querySelectorAll("h3"),
        ).filter(
          (h) => (h.textContent || "").trim().toLowerCase() === "training log",
        );
        if (trainingLogHeadings.length > 1) {
          let validHeading = null;
          for (const heading of trainingLogHeadings) {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              if (
                nextSibling.tagName === "TABLE" ||
                nextSibling.querySelector("table")
              ) {
                validHeading = heading;
                break;
              }
              nextSibling = nextSibling.nextElementSibling;
            }
            if (validHeading) break;
          }
          trainingLogHeadings.forEach((heading) => {
            if (heading !== validHeading) {
              heading.style.setProperty("display", "none", "important");
              heading.style.setProperty("visibility", "hidden", "important");
            }
          });
        }

        const bomHeadings = Array.from(element.querySelectorAll("h3")).filter(
          (h) => {
            const t = (h.textContent || "").trim().toLowerCase();
            return t === "bom" || t.endsWith(" bom");
          },
        );
        if (bomHeadings.length > 1) {
          let validHeading = null;
          for (const heading of bomHeadings) {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              const table =
                nextSibling.tagName === "TABLE"
                  ? nextSibling
                  : nextSibling.querySelector("table");
              if (table) {
                validHeading = heading;
                break;
              }
              nextSibling = nextSibling.nextElementSibling;
            }
            if (validHeading) break;
          }
          bomHeadings.forEach((heading) => {
            if (heading !== validHeading) {
              heading.style.setProperty("display", "none", "important");
              heading.style.setProperty("visibility", "hidden", "important");
            }
          });
        }

        const trainingLogResult = findTableByHeading("Training Log", "h3");
        const bomResult = findTableByHeading("BOM", "h3");
        const trainingLogTable = trainingLogResult
          ? trainingLogResult.table
          : null;
        const bomTable = bomResult ? bomResult.table : null;

        if (trainingLogResult && trainingLogResult.heading) {
          trainingLogResult.heading.style.setProperty(
            "margin-top",
            "20px",
            "important",
          );
          trainingLogResult.heading.style.setProperty(
            "margin-bottom",
            "6px",
            "important",
          );
          trainingLogResult.heading.style.setProperty(
            "font-size",
            "16px",
            "important",
          );
        }

        if (bomResult && bomResult.heading) {
          // Hide BOM heading since it's being rendered by autotable to prevent duplicate
          bomResult.heading.style.setProperty(
            "visibility",
            "hidden",
            "important",
          );
          bomResult.heading.style.setProperty(
            "margin-top",
            "0px",
            "important",
          );
          bomResult.heading.style.setProperty(
            "margin-bottom",
            "10px",
            "important",
          );
          bomResult.heading.style.setProperty("font-size", "16px", "important");
        }

        const applyRevisionStyles = (table) => {
          if (!table) return;

          const thead = table.querySelector("thead");
          if (thead) {
            thead.style.setProperty(
              "display",
              "table-header-group",
              "important",
            );
            thead.style.setProperty("visibility", "visible", "important");
            thead.style.setProperty("opacity", "1", "important");
            let parent = thead.parentElement;
            while (parent && parent !== table) {
              const computedStyle = window.getComputedStyle(parent);
              if (
                computedStyle.display === "none" ||
                computedStyle.visibility === "hidden"
              ) {
                parent.style.setProperty("display", "block", "important");
                parent.style.setProperty("visibility", "visible", "important");
              }
              parent = parent.parentElement;
            }
          }

          // Check if next sibling is a substep header
          let nextElement = table.nextElementSibling;
          let hasSubstepBelow = false;
          if (
            nextElement &&
            nextElement.getAttribute("data-export-block") === "substep"
          ) {
            hasSubstepBelow = true;
          }

          // Increase bottom margin if substep follows to prevent last row from being hidden
          const bottomMargin = hasSubstepBelow ? "30px" : "8px";

          table.style.setProperty("width", "100%", "important");
          table.style.setProperty("border-collapse", "collapse", "important");
          table.style.setProperty("background", "#ffffff", "important");
          table.style.setProperty(
            "border-radius",
            "5px 5px 0px 0px",
            "important",
          );
          table.style.setProperty("margin-top", "8px", "important");
          table.style.setProperty("margin-bottom", bottomMargin, "important");
          table.style.setProperty("position", "relative", "important");
          table.style.setProperty("z-index", "1", "important");

          const allThs = table.querySelectorAll("thead tr th");
          allThs.forEach((th) => {
            th.style.setProperty("background", "#000000", "important");
            th.style.setProperty("color", "#ffffff", "important");
            th.style.setProperty("padding", "10px", "important");
            th.style.setProperty("border", "2px solid black", "important");
            th.style.setProperty("text-align", "left", "important");
            th.style.setProperty("font-weight", "600", "important");
            th.style.setProperty("display", "table-cell", "important");
            th.style.setProperty("visibility", "visible", "important");

            const thText = (th.textContent || "").toLowerCase();
            if (thText.includes("signature")) {
              th.style.setProperty("text-align", "center", "important");
            }
          });

          const headerRow = table.querySelector("thead tr");
          if (headerRow) {
            headerRow.style.setProperty("display", "table-row", "important");
            headerRow.style.setProperty("visibility", "visible", "important");
          }

          if (thead) {
            thead.style.setProperty(
              "display",
              "table-header-group",
              "important",
            );
            thead.style.setProperty("visibility", "visible", "important");
          }

          const allTds = table.querySelectorAll("tbody tr td");
          allTds.forEach((td) => {
            td.style.setProperty("padding", "10px", "important");
            td.style.setProperty("border", "2px solid black", "important");
            td.style.setProperty("text-align", "left", "important");
            td.style.setProperty("background-color", "#ffffff", "important");

            const row = td.closest("tr");
            const parentTable = td.closest("table");
            if (row && parentTable) {
              const headerRow = parentTable.querySelector("thead tr");
              if (headerRow) {
                const headerCells = Array.from(
                  headerRow.querySelectorAll("th"),
                );
                const columnIndex = Array.from(
                  row.querySelectorAll("td"),
                ).indexOf(td);
                const headerCell = headerCells[columnIndex];
                if (headerCell) {
                  const headerText = (
                    headerCell.textContent || ""
                  ).toLowerCase();
                  if (headerText.includes("signature")) {
                    td.style.setProperty("text-align", "center", "important");
                  }
                }
              }
            }
          });

          const allTrs = table.querySelectorAll("tbody tr");
          allTrs.forEach((tr) => {
            tr.style.setProperty("background-color", "#ffffff", "important");
          });
        };

        const applyLightGrayBorderStyles = (table, styleOpts = {}) => {
          if (!table) return;
          const matchRevisionTable = styleOpts.matchRevisionTable === true;
          const thPadding = matchRevisionTable ? "6px 8px" : "10px";
          const tdPadding = matchRevisionTable ? "8px" : "10px";
          const cellBorder = matchRevisionTable
            ? "2px solid #000000"
            : "1px solid #e6e6e6";
          const thBorder = matchRevisionTable
            ? "2px solid #000000"
            : "1px solid #000000";

          const thead = table.querySelector("thead");
          if (thead) {
            thead.style.setProperty(
              "display",
              "table-header-group",
              "important",
            );
            thead.style.setProperty("visibility", "visible", "important");
            thead.style.setProperty("opacity", "1", "important");
            let parent = thead.parentElement;
            while (parent && parent !== table) {
              const computedStyle = window.getComputedStyle(parent);
              if (
                computedStyle.display === "none" ||
                computedStyle.visibility === "hidden"
              ) {
                parent.style.setProperty("display", "block", "important");
                parent.style.setProperty("visibility", "visible", "important");
              }
              parent = parent.parentElement;
            }
          }

          // Check if next sibling is a substep header
          let nextElement = table.nextElementSibling;
          let hasSubstepBelow = false;
          if (
            nextElement &&
            nextElement.getAttribute("data-export-block") === "substep"
          ) {
            hasSubstepBelow = true;
          }

          // Increase bottom margin if substep follows to prevent last row from being hidden
          const bottomMargin = hasSubstepBelow ? "30px" : "8px";

          table.style.setProperty("width", "100%", "important");
          table.style.setProperty("border-collapse", "collapse", "important");
          table.style.setProperty("background", "#ffffff", "important");
          table.style.setProperty(
            "border-radius",
            "5px 5px 0px 0px",
            "important",
          );
          table.style.setProperty("margin-top", "8px", "important");
          table.style.setProperty("margin-bottom", bottomMargin, "important");
          table.style.setProperty("overflow", "visible", "important");
          table.style.setProperty("page-break-inside", "auto", "important");
          table.style.setProperty("position", "relative", "important");
          table.style.setProperty("z-index", "1", "important");

          const allThs = table.querySelectorAll("thead tr th");
          allThs.forEach((th) => {
            th.style.setProperty("background", "#000000", "important");
            th.style.setProperty("color", "#ffffff", "important");
            th.style.setProperty("padding", thPadding, "important");
            th.style.setProperty("border", thBorder, "important");
            if (matchRevisionTable) {
              th.style.setProperty("line-height", "1.2", "important");
              th.style.setProperty("font-size", "13px", "important");
            }
            th.style.setProperty("text-align", "left", "important");
            th.style.setProperty("font-weight", "600", "important");
            th.style.setProperty("display", "table-cell", "important");
            th.style.setProperty("visibility", "visible", "important");

            const thText = (th.textContent || "").toLowerCase();
            if (thText.includes("signature")) {
              th.style.setProperty("text-align", "center", "important");
            }
          });

          const headerRow = table.querySelector("thead tr");
          if (headerRow) {
            headerRow.style.setProperty("display", "table-row", "important");
            headerRow.style.setProperty("visibility", "visible", "important");
          }

          if (thead) {
            thead.style.setProperty(
              "display",
              "table-header-group",
              "important",
            );
            thead.style.setProperty("visibility", "visible", "important");
          }

          const tbody = table.querySelector("tbody");
          if (tbody) {
            tbody.style.setProperty("display", "table-row-group", "important");
            tbody.style.setProperty("visibility", "visible", "important");
            tbody.style.setProperty("overflow", "visible", "important");
            tbody.style.setProperty("page-break-inside", "auto", "important");
          }

          const allTds = table.querySelectorAll("tbody tr td");
          allTds.forEach((td) => {
            td.style.setProperty("padding", tdPadding, "important");
            td.style.setProperty("border", cellBorder, "important");
            td.style.setProperty("text-align", "left", "important");
            td.style.setProperty("background-color", "#ffffff", "important");
            td.style.setProperty("display", "table-cell", "important");
            td.style.setProperty("visibility", "visible", "important");
            td.style.setProperty("opacity", "1", "important");
            td.style.setProperty("position", "static", "important");
            td.style.setProperty("overflow", "visible", "important");

            const row = td.closest("tr");
            const parentTable = td.closest("table");
            if (row && parentTable) {
              const headerRow = parentTable.querySelector("thead tr");
              if (headerRow) {
                const headerCells = Array.from(
                  headerRow.querySelectorAll("th"),
                );
                const columnIndex = Array.from(
                  row.querySelectorAll("td"),
                ).indexOf(td);
                const headerCell = headerCells[columnIndex];
                if (headerCell) {
                  const headerText = (
                    headerCell.textContent || ""
                  ).toLowerCase();
                  if (headerText.includes("signature")) {
                    td.style.setProperty("text-align", "center", "important");
                  }
                }
              }
            }
          });

          const allTrs = table.querySelectorAll("tbody tr");
          allTrs.forEach((tr) => {
            tr.style.setProperty("background-color", "#ffffff", "important");
            tr.style.setProperty("display", "table-row", "important");
            tr.style.setProperty("visibility", "visible", "important");
            tr.style.setProperty("opacity", "1", "important");
            tr.style.setProperty("page-break-inside", "auto", "important");
            tr.style.setProperty("position", "static", "important");
            tr.style.setProperty("overflow", "visible", "important");
          });
        };

        if (trainingLogTable) {
          const isHidden =
            trainingLogTable.style.display === "none" ||
            trainingLogTable.style.visibility === "hidden" ||
            window.getComputedStyle(trainingLogTable).display === "none" ||
            window.getComputedStyle(trainingLogTable).visibility === "hidden";
          if (!isHidden) {
            applyLightGrayBorderStyles(trainingLogTable);
            trainingLogTable.style.setProperty(
              "margin-top",
              "8px",
              "important",
            );
            trainingLogTable.style.setProperty(
              "margin-bottom",
              "0",
              "important",
            );
            const trainingLogParent = trainingLogTable.parentElement;
            if (trainingLogParent) {
              // Use padding for ZIP so spacing does not collapse with previous table margins.
              trainingLogParent.style.setProperty(
                "padding-top",
                "0px",
                "important",
              );
              trainingLogParent.style.setProperty(
                "margin-top",
                "10px",
                "important",
              );
              trainingLogParent.style.setProperty(
                "margin-bottom",
                "0px",
                "important",
              );
              trainingLogParent.style.setProperty(
                "padding-bottom",
                "10px",
                "important",
              );
            }
          }
        }
        if (bomTable) {
          const isHidden =
            bomTable.style.display === "none" ||
            bomTable.style.visibility === "hidden" ||
            window.getComputedStyle(bomTable).display === "none" ||
            window.getComputedStyle(bomTable).visibility === "hidden";
          if (!isHidden) {
            applyLightGrayBorderStyles(bomTable, { matchRevisionTable: true });
            bomTable.style.setProperty(
              "margin-top",
              "0px",
              "important",
            );
            const bomParent = bomTable.parentElement;
            const bomInExportBlock =
              bomParent &&
              bomParent.getAttribute("data-export-block") === "bom";
            bomTable.style.setProperty(
              "margin-bottom",
              bomInExportBlock ? "0px" : "15px",
              "important",
            );
            if (bomParent) {
              bomParent.style.setProperty(
                "margin-top",
                "0px",
                "important",
              );
              bomParent.style.setProperty("margin-bottom", "0px", "important");
              bomParent.style.setProperty("padding-bottom", "0px", "important");
              bomParent.style.setProperty("overflow", "visible", "important");
              bomParent.style.setProperty(
                "page-break-inside",
                "auto",
                "important",
              );
              bomParent.style.setProperty("min-height", "auto", "important");
            }
          }
        }
      };

      applyRevisionTableStylesToTables(clone);

      const allTables = clone.querySelectorAll("table");
      allTables.forEach((table) => {
        const thead = table.querySelector("thead");
        if (thead) {
          thead.style.setProperty("display", "table-header-group", "important");
          thead.style.setProperty("visibility", "visible", "important");
          thead.style.setProperty("opacity", "1", "important");

          const allThs = thead.querySelectorAll("th");
          allThs.forEach((th) => {
            th.style.setProperty("display", "table-cell", "important");
            th.style.setProperty("visibility", "visible", "important");
            th.style.setProperty("opacity", "1", "important");
          });

          const headerRow = thead.querySelector("tr");
          if (headerRow) {
            headerRow.style.setProperty("display", "table-row", "important");
            headerRow.style.setProperty("visibility", "visible", "important");
          }
        }
      });

      revisionHeadingRect =
        revisionHeadingElement?.getBoundingClientRect() || revisionHeadingRect;

      const richTextBlocksFinal = clone.querySelectorAll(
        "[data-export-block='rich-text']",
      );
      // Compute once per export — previously this ran inside every rich-text block (O(n²) DOM work).
      const attachmentAndRichTextBlocksOrdered = Array.from(
        clone.querySelectorAll(
          "[data-export-block='attachment'], [data-export-block='rich-text']",
        ),
      ).sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1,
      );
      richTextBlocksFinal.forEach((block, index) => {
        // Apply exact same styles as RichTextNode.js main container
        block.style.setProperty("display", "block", "important");
        block.style.setProperty("visibility", "visible", "important");
        block.style.setProperty("opacity", "1", "important");
        block.style.setProperty("clear", "both", "important");
        // Ensure rich text blocks are left-aligned in PDF (no extra left margin)
        block.style.setProperty("margin-left", "0", "important");
        // margin-top is conditional in RichTextNode: showCheckbox ? "20px" : "30px"
        // Check if checkbox exists to determine margin
        const hasCheckbox = block.querySelector('input[type="checkbox"]');
        let marginTop = hasCheckbox ? "20px" : "30px";
        // When Rich Text follows Attachment in document order, use extra margin to prevent merging/overlap
        const richIdx = attachmentAndRichTextBlocksOrdered.indexOf(block);
        if (
          richIdx > 0 &&
          attachmentAndRichTextBlocksOrdered[richIdx - 1].getAttribute(
            "data-export-block",
          ) === "attachment"
        ) {
          marginTop = "60px";
        }
        block.style.setProperty("margin-top", marginTop, "important");
        // CRITICAL: Add z-index and position to ensure rich text appears above canvas white backgrounds
        block.style.setProperty("position", "relative", "important");
        block.style.setProperty("z-index", "1000", "important");

        // Style heading h2 to match RichTextNode.js
        const heading = block.querySelector("h2");
        if (heading) {
          heading.style.setProperty("font-size", "20px", "important");
          heading.style.setProperty("margin", "0", "important");
          heading.style.setProperty("user-select", "none", "important");
        }

        // Style heading container div (flex container)
        const headingContainer = block.querySelector("div");
        if (headingContainer && headingContainer.querySelector("h2")) {
          const computedStyle = window.getComputedStyle(headingContainer);
          if (
            computedStyle.display === "flex" ||
            headingContainer.querySelector("input[type='checkbox']")
          ) {
            headingContainer.style.setProperty("display", "flex", "important");
            headingContainer.style.setProperty(
              "justify-content",
              "space-between",
              "important",
            );
            headingContainer.style.setProperty(
              "align-items",
              "center",
              "important",
            );
          }
        }

        const allDivs = Array.from(block.querySelectorAll("div"));
        allDivs.forEach((div) => {
          const style = window.getComputedStyle(div);
          const text = div.textContent?.trim() || "";

          const isNotContentContainer =
            !div.classList.contains("rich-text-content-display") &&
            !div.classList.contains("editor-shell") &&
            !div.closest(".rich-text-content-display") &&
            !div.closest(".editor-shell");

          // Short description div: marginLeft: "5px", whiteSpace: "pre-wrap"
          const isDescriptionDiv =
            (style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre") &&
            style.marginLeft &&
            style.marginLeft.includes("5px") &&
            isNotContentContainer;

          // Additional detection: check if div has text and is positioned before rich text content
          const richTextContent = block.querySelector(
            ".rich-text-content-display, .editor-shell",
          );
          const isBeforeRichTextContent =
            richTextContent &&
            (div.compareDocumentPosition(richTextContent) &
              Node.DOCUMENT_POSITION_FOLLOWING) !==
              0;
          const hasTextContent = text.length > 0;
          const isLikelyShortDesc =
            isBeforeRichTextContent &&
            hasTextContent &&
            isNotContentContainer &&
            !div.querySelector("h2") &&
            !div.querySelector('input[type="checkbox"]') &&
            !div.querySelector(".rich-text-content-display");

          // Category div: marginLeft: "4px", marginTop: "5px"
          const isCategoryDiv =
            style.marginLeft &&
            style.marginLeft.includes("4px") &&
            style.marginTop &&
            style.marginTop.includes("5px") &&
            !div.classList.contains("rich-text-content-display") &&
            !div.closest(".rich-text-content-display") &&
            !div.closest(".editor-shell") &&
            !div.querySelector(".rich-text-content-display");

          // Rich text content wrapper div: fontSize: "14px", marginLeft: "4px", marginTop: "5px"
          const isRichTextContentWrapper =
            style.fontSize &&
            style.fontSize.includes("14px") &&
            style.marginLeft &&
            style.marginLeft.includes("4px") &&
            style.marginTop &&
            style.marginTop.includes("5px") &&
            (div.querySelector(".rich-text-content-display") ||
              div.querySelector(".editor-shell"));

          if (isDescriptionDiv || isLikelyShortDesc) {
            div.style.setProperty("display", "block", "important");
            div.style.setProperty("visibility", "visible", "important");
            div.style.setProperty("opacity", "1", "important");
            div.style.setProperty("color", "#000", "important");
            div.style.setProperty("white-space", "pre-wrap", "important");
            if (isDescriptionDiv) {
              div.style.setProperty("margin-left", "5px", "important");
            } else if (isLikelyShortDesc && !style.marginLeft.includes("5px")) {
              div.style.setProperty("margin-left", "5px", "important");
            }
            div.style.setProperty("position", "relative", "important");
            div.style.setProperty("z-index", "10", "important");
            div.style.setProperty(
              "background-color",
              "transparent",
              "important",
            );
            div.style.setProperty("width", "auto", "important");
            div.style.setProperty("height", "auto", "important");
            div.style.setProperty("min-height", "auto", "important");
            div.style.setProperty("max-height", "none", "important");
            div.style.setProperty("max-width", "100%", "important");
            div.style.setProperty("word-break", "break-word", "important");
            div.style.setProperty("overflow-wrap", "break-word", "important");
            div.style.setProperty("overflow", "visible", "important");
            div.style.setProperty("line-height", "1.5", "important");
            div.style.setProperty("padding", "2px", "important");
            div.style.setProperty("margin-bottom", "10px", "important");
            // Force layout recalculation for large text
            void div.offsetHeight;

            // Ensure parent elements don't hide it and allow expansion
            let parent = div.parentElement;
            let depth = 0;
            while (parent && parent !== block && depth < 10) {
              const parentStyle = window.getComputedStyle(parent);
              if (parentStyle.display === "none") {
                parent.style.setProperty("display", "block", "important");
              }
              if (parentStyle.visibility === "hidden") {
                parent.style.setProperty("visibility", "visible", "important");
              }
              if (parentStyle.opacity === "0") {
                parent.style.setProperty("opacity", "1", "important");
              }
              if (parentStyle.overflow === "hidden") {
                parent.style.setProperty("overflow", "visible", "important");
              }
              if (parentStyle.maxHeight && parentStyle.maxHeight !== "none") {
                parent.style.setProperty("max-height", "none", "important");
              }
              if (
                parentStyle.height &&
                parentStyle.height !== "auto" &&
                !parentStyle.height.includes("%")
              ) {
                parent.style.setProperty("height", "auto", "important");
              }
              parent = parent.parentElement;
              depth++;
            }
          } else if (isCategoryDiv) {
            div.style.setProperty("display", "block", "important");
            div.style.setProperty("visibility", "visible", "important");
            div.style.setProperty("opacity", "1", "important");
            div.style.setProperty("color", "#000", "important");
            div.style.setProperty("margin-left", "4px", "important");
            div.style.setProperty("margin-top", "5px", "important");
          } else if (isRichTextContentWrapper) {
            div.style.setProperty("display", "block", "important");
            div.style.setProperty("visibility", "visible", "important");
            div.style.setProperty("opacity", "1", "important");
            div.style.setProperty("font-size", "14px", "important");
            div.style.setProperty("margin-left", "4px", "important");
            div.style.setProperty("margin-top", "5px", "important");
          }
        });

        const richTextContentDisplaysAll = block.querySelectorAll(
          ".rich-text-content-display",
        );
        const editorShellsAll = block.querySelectorAll(".editor-shell");

        richTextContentDisplaysAll.forEach((richTextContentDisplay) => {
          richTextContentDisplay.style.setProperty(
            "display",
            "block",
            "important",
          );
          richTextContentDisplay.style.setProperty(
            "visibility",
            "visible",
            "important",
          );
          richTextContentDisplay.style.setProperty("opacity", "1", "important");
          richTextContentDisplay.style.setProperty(
            "min-height",
            "10px",
            "important",
          );
          richTextContentDisplay.style.setProperty(
            "position",
            "relative",
            "important",
          );
          richTextContentDisplay.style.setProperty(
            "z-index",
            "2000",
            "important",
          );
          richTextContentDisplay.style.setProperty(
            "overflow",
            "visible",
            "important",
          );
          richTextContentDisplay.style.setProperty(
            "max-height",
            "none",
            "important",
          );
        });

        editorShellsAll.forEach((editorShell) => {
          editorShell.style.setProperty("display", "block", "important");
          editorShell.style.setProperty("visibility", "visible", "important");
          editorShell.style.setProperty("opacity", "1", "important");
          editorShell.style.setProperty("min-height", "10px", "important");
          editorShell.style.setProperty("position", "relative", "important");
          editorShell.style.setProperty("z-index", "2000", "important");
          editorShell.style.setProperty("overflow", "visible", "important");
          editorShell.style.setProperty("max-height", "none", "important");

          const innerDivs = editorShell.querySelectorAll("div");
          innerDivs.forEach((innerDiv) => {
            const isLayoutContainer =
              innerDiv.classList.contains(
                "PlaygroundEditorTheme__layoutContainer",
              ) ||
              innerDiv.getAttribute("data-lexical-layout-container") === "true";
            const isLayoutItem = innerDiv.classList.contains(
              "PlaygroundEditorTheme__layoutItem",
            );
            const isLayoutElement = isLayoutContainer || isLayoutItem;

            const hasContent =
              innerDiv.querySelector(
                "p, h1, h2, h3, ul, ol, table, blockquote, a, img, strong, em, code, pre",
              ) || innerDiv.innerHTML.trim().length > 0;

            if (hasContent) {
              if (!isLayoutElement) {
                innerDiv.style.setProperty("display", "block", "important");
                innerDiv.style.setProperty("width", "100%", "important");
              }

              // Ensure rich text editor blocks cannot overflow horizontally
              innerDiv.style.setProperty("max-width", "100%", "important");
              innerDiv.style.setProperty(
                "box-sizing",
                "border-box",
                "important",
              );
              innerDiv.style.setProperty("overflow", "visible", "important");

              innerDiv.style.setProperty("visibility", "visible", "important");
              innerDiv.style.setProperty("opacity", "1", "important");
              innerDiv.style.removeProperty("color");

              const nestedElements = innerDiv.querySelectorAll("*");
              nestedElements.forEach((nested) => {
                const isNestedLayoutContainer =
                  nested.classList.contains(
                    "PlaygroundEditorTheme__layoutContainer",
                  ) ||
                  nested.getAttribute("data-lexical-layout-container") ===
                    "true";
                const isNestedLayoutItem = nested.classList.contains(
                  "PlaygroundEditorTheme__layoutItem",
                );
                const isNestedLayoutElement =
                  isNestedLayoutContainer || isNestedLayoutItem;

                nested.style.setProperty("visibility", "visible", "important");
                nested.style.setProperty("opacity", "1", "important");

                // Prevent nested rich text content from exceeding the page width
                nested.style.setProperty("max-width", "100%", "important");
                nested.style.setProperty(
                  "box-sizing",
                  "border-box",
                  "important",
                );
                nested.style.setProperty("overflow", "visible", "important");

                if (!isNestedLayoutElement) {
                  if (
                    nested.tagName === "SPAN" ||
                    nested.tagName === "A" ||
                    nested.tagName === "STRONG" ||
                    nested.tagName === "EM" ||
                    nested.tagName === "CODE"
                  ) {
                    nested.style.setProperty("display", "inline", "important");
                  } else if (
                    [
                      "P",
                      "DIV",
                      "H1",
                      "H2",
                      "H3",
                      "H4",
                      "H5",
                      "H6",
                      "UL",
                      "OL",
                      "LI",
                      "BLOCKQUOTE",
                      "PRE",
                    ].includes(nested.tagName)
                  ) {
                    const isLexicalChecklistLiNested =
                      nested.tagName === "LI" &&
                      (nested.classList.contains(
                        "PlaygroundEditorTheme__listItemChecked",
                      ) ||
                        nested.classList.contains(
                          "PlaygroundEditorTheme__listItemUnchecked",
                        ));
                    nested.style.setProperty(
                      "display",
                      nested.tagName === "LI"
                        ? isLexicalChecklistLiNested
                          ? "block"
                          : "list-item"
                        : "block",
                      "important",
                    );
                  }
                }

                const computedColor = window.getComputedStyle(nested).color;
                if (
                  computedColor === "rgba(0, 0, 0, 0)" ||
                  computedColor === "transparent" ||
                  !computedColor
                ) {
                  nested.style.setProperty("color", "#000", "important");
                }
              });
            }
          });
        });

        if (
          richTextContentDisplaysAll.length === 0 &&
          editorShellsAll.length === 0
        ) {
          const allDivs = Array.from(block.querySelectorAll("div"));
          for (const div of allDivs) {
            const hasHtml = div.querySelector(
              "p, h1, h2, h3, strong, em, code, pre, ul, ol, table, blockquote, a, img",
            );
            if (hasHtml && div.innerHTML.trim().length > 0) {
              div.style.setProperty("display", "block", "important");
              div.style.setProperty("visibility", "visible", "important");
              div.style.setProperty("opacity", "1", "important");
              div.style.setProperty("color", "#000", "important");
              break;
            }
          }
        }

        // Fix image caption ordering and visibility - ensure captions appear below images
        const imageCaptionContainers = block.querySelectorAll(
          ".image-caption-container",
        );
        imageCaptionContainers.forEach((captionContainer) => {
          applyImageCaptionLayoutForExport(captionContainer);
        });

        const allTextElements = block.querySelectorAll(
          "p, h1, h2, h3, h4, h5, h6, li, td, th, span, div, a, strong, em",
        );
        allTextElements.forEach((el) => {
          const isPhrasing = [
            "SPAN",
            "A",
            "STRONG",
            "EM",
            "CODE",
            "B",
            "I",
            "U",
            "S",
          ].includes(el.tagName);
          if (el.style.display === "none" || el.style.visibility === "hidden")
            return;

          const computedColor = window.getComputedStyle(el).color;
          if (
            computedColor === "rgba(0, 0, 0, 0)" ||
            computedColor === "transparent" ||
            !computedColor
          ) {
            el.style.setProperty("color", "#000", "important");
          }

          // Make sure long text and links wrap instead of overflowing off the page
          if (!isPhrasing) {
            el.style.setProperty("word-break", "break-word", "important");
            el.style.setProperty("overflow-wrap", "break-word", "important");
            el.style.setProperty("max-width", "100%", "important");
          }
          el.style.setProperty("box-sizing", "border-box", "important");

          el.style.setProperty("visibility", "visible", "important");
          el.style.setProperty("opacity", "1", "important");
        });

        void block.offsetHeight;
      });

      // Process canvas blocks to ensure short descriptions are visible in PDF
      const canvasBlocksFinal = clone.querySelectorAll(
        "[data-export-block='canvas']",
      );
      canvasBlocksFinal.forEach((block) => {
        // Ensure canvas block is visible
        block.style.setProperty("display", "block", "important");
        block.style.setProperty("visibility", "visible", "important");
        block.style.setProperty("opacity", "1", "important");
        // Add bottom margin to prevent merging with content below
        block.style.setProperty("margin-bottom", "30px", "important");
        block.style.setProperty("padding-bottom", "0", "important");

        // First, ensure canvas images have lower z-index so they don't cover text
        // Also set canvas images to 400px width for PDF export
        const canvasImages = block.querySelectorAll("img");
        canvasImages.forEach((img) => {
          img.style.setProperty("position", "relative", "important");
          img.style.setProperty("z-index", "1", "important");
          img.style.setProperty("max-width", "400px", "important");
          img.style.setProperty("width", "auto", "important");
          img.style.setProperty("height", "auto", "important");
          // Add bottom margin to each canvas image for spacing
          img.style.setProperty("margin-bottom", "10px", "important");
        });

        // Find and style short description divs within canvas blocks
        const allDivs = Array.from(block.querySelectorAll("div"));
        allDivs.forEach((div) => {
          const style = window.getComputedStyle(div);
          const text = div.textContent?.trim() || "";

          // Check if this div is the heading container (has h2 or checkbox)
          const isHeadingContainer =
            div.querySelector("h2") ||
            div.querySelector('input[type="checkbox"]');

          // Short description div detection - more flexible:
          // 1. Has text content
          // 2. Is not the heading container
          // 3. Either has the expected styles OR is a direct child div that comes before images
          const hasExpectedStyles =
            (style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre") &&
            style.marginLeft &&
            style.marginLeft.includes("5px");

          // Check if this div comes before any img in the block
          const isBeforeImages = (() => {
            let current = div;
            while (current && current !== block) {
              const nextSibling = current.nextElementSibling;
              if (nextSibling) {
                const hasImg =
                  nextSibling.tagName === "IMG" ||
                  nextSibling.querySelector("img");
                if (hasImg) return true;
              }
              current = current.parentElement;
            }
            // Also check if it's a direct child and there are images after it
            if (div.parentElement === block) {
              let sibling = div.nextElementSibling;
              while (sibling) {
                if (sibling.tagName === "IMG" || sibling.querySelector("img")) {
                  return true;
                }
                sibling = sibling.nextElementSibling;
              }
            }
            return false;
          })();

          const isDescriptionDiv =
            text &&
            text.length > 0 &&
            !isHeadingContainer &&
            !div.classList.contains("rich-text-content-display") &&
            !div.classList.contains("editor-shell") &&
            !div.closest(".rich-text-content-display") &&
            !div.closest(".editor-shell") &&
            (hasExpectedStyles ||
              (isBeforeImages && div.parentElement === block));

          if (isDescriptionDiv) {
            div.style.setProperty("display", "block", "important");
            div.style.setProperty("visibility", "visible", "important");
            div.style.setProperty("opacity", "1", "important");
            div.style.setProperty("color", "#000", "important");
            div.style.setProperty("white-space", "pre-wrap", "important");
            div.style.setProperty("position", "relative", "important");
            div.style.setProperty("z-index", "10", "important");
            div.style.setProperty(
              "background-color",
              "transparent",
              "important",
            );
            div.style.setProperty("width", "auto", "important");
            div.style.setProperty("height", "auto", "important");
            div.style.setProperty("min-height", "auto", "important");
            div.style.setProperty("max-width", "100%", "important");
            div.style.setProperty("word-break", "break-word", "important");
            div.style.setProperty("overflow-wrap", "break-word", "important");
            div.style.setProperty("margin-bottom", "10px", "important");

            // Ensure parent elements don't hide it
            let parent = div.parentElement;
            let depth = 0;
            while (parent && parent !== block && depth < 10) {
              const parentStyle = window.getComputedStyle(parent);
              if (parentStyle.display === "none") {
                parent.style.setProperty("display", "block", "important");
              }
              if (parentStyle.visibility === "hidden") {
                parent.style.setProperty("visibility", "visible", "important");
              }
              if (parentStyle.opacity === "0") {
                parent.style.setProperty("opacity", "1", "important");
              }
              parent = parent.parentElement;
              depth++;
            }
          }
        });

        // Process all text elements in canvas blocks to ensure visibility (similar to rich-text blocks)
        const allTextElements = block.querySelectorAll(
          "p, h1, h2, h3, h4, h5, h6, li, td, th, span, div, a, strong, em",
        );
        allTextElements.forEach((el) => {
          const isPhrasing = [
            "SPAN",
            "A",
            "STRONG",
            "EM",
            "CODE",
            "B",
            "I",
            "U",
            "S",
          ].includes(el.tagName);
          const computedStyle = window.getComputedStyle(el);
          if (
            computedStyle.display === "none" ||
            computedStyle.visibility === "hidden"
          ) {
            // Only make visible if it's not intentionally hidden and has text content
            const text = el.textContent?.trim() || "";
            if (text && text.length > 0) {
              el.style.setProperty(
                "display",
                isPhrasing ? "inline" : "block",
                "important",
              );
              el.style.setProperty("visibility", "visible", "important");
            }
          }

          const computedColor = window.getComputedStyle(el).color;
          if (
            computedColor === "rgba(0, 0, 0, 0)" ||
            computedColor === "transparent" ||
            !computedColor
          ) {
            const text = el.textContent?.trim() || "";
            if (text && text.length > 0) {
              el.style.setProperty("color", "#000", "important");
            }
          }

          // Make sure long text and links wrap instead of overflowing off the page
          if (!isPhrasing) {
            el.style.setProperty("word-break", "break-word", "important");
            el.style.setProperty("overflow-wrap", "break-word", "important");
            el.style.setProperty("max-width", "100%", "important");
          }
          el.style.setProperty("box-sizing", "border-box", "important");

          el.style.setProperty("visibility", "visible", "important");
          el.style.setProperty("opacity", "1", "important");
        });

        void block.offsetHeight;
      });
      // Fix image caption ordering across entire document (not just rich text blocks)
      const allImageCaptionContainers = clone.querySelectorAll(
        ".image-caption-container",
      );
      allImageCaptionContainers.forEach((captionContainer) => {
        applyImageCaptionLayoutForExport(captionContainer);
      });

      if (richTextBlocksFinal.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      normalizeLayoutContainersForPDF(clone);

      // In the live UI, node short descriptions are rendered via the Lexical viewer and are wrapped
      // in a container with a negative left margin (e.g. -42px/-45px) for alignment.
      // During PDF capture, that negative margin can push the text out of the capture area,
      // making short descriptions look clipped/missing. Normalize only for export.
      const normalizeNegativeLeftMarginsForPDF = (rootEl) => {
        try {
          const candidates = rootEl.querySelectorAll(
            "[data-export-block] [style*='margin-left']",
          );
          candidates.forEach((el) => {
            const mlRaw = el.style?.marginLeft || "";
            const ml = parseFloat(String(mlRaw).replace("px", ""));
            if (!Number.isNaN(ml) && ml < 0) {
              el.style.setProperty("margin-left", "0px", "important");
            }
          });
        } catch (e) {
          // best-effort: don't block export if DOM parsing fails
        }
      };

      // Short descriptions across nodes (Section/Step/Substep/RichText/Video/Attachment/Canvas) are
      // rendered using a Lexical read-only viewer:
      // `.rich-text-content-display > .editor-shell ...`.
      // In the export clone, these wrappers can end up collapsed/hidden due to inherited overflow
      // constraints or missing computed styles. Force-expand them for PDF only.
      const ensureLexicalViewersVisibleForPDF = (rootEl) => {
        try {
          const lexicalRoots = rootEl.querySelectorAll(
            "[data-export-block] .rich-text-content-display, [data-export-block] .editor-shell",
          );
          const withLinks = Array.from(lexicalRoots).filter(
            (el) => el.querySelectorAll("a[href]").length > 0,
          );
          if (withLinks.length > 0) {
          }
          lexicalRoots.forEach((el) => {
            el.style.setProperty("display", "block", "important");
            el.style.setProperty("visibility", "visible", "important");
            el.style.setProperty("opacity", "1", "important");
            el.style.setProperty("max-height", "none", "important");
            el.style.setProperty("height", "auto", "important");
            el.style.setProperty("min-height", "10px", "important");
            el.style.setProperty("overflow", "visible", "important");
            el.style.setProperty("max-width", "100%", "important");
            el.style.setProperty("box-sizing", "border-box", "important");
            el.style.setProperty("position", "relative", "important");
            el.style.setProperty("z-index", "2000", "important");

            // Ensure ancestor containers don’t clip the viewer.
            let p = el.parentElement;
            let depth = 0;
            while (p && depth < 10) {
              if (p.hasAttribute && p.hasAttribute("data-export-block")) break;
              p.style?.setProperty?.("overflow", "visible", "important");
              p.style?.setProperty?.("max-height", "none", "important");
              p.style?.setProperty?.("height", "auto", "important");
              p = p.parentElement;
              depth++;
            }
          });

          // Tables rendered inside Lexical viewers (Short Description + Content) should size to content.
          // In PDF capture, they can sometimes expand to full width; normalize to auto width.
          const lexicalTables = rootEl.querySelectorAll(
            "[data-export-block] .rich-text-content-display table, [data-export-block] .editor-shell table",
          );
          lexicalTables.forEach((table) => {
            table.style.setProperty("width", "auto", "important");
            table.style.setProperty("max-width", "100%", "important");
            table.style.setProperty("table-layout", "auto", "important");
            table.style.setProperty("display", "table", "important");
            table.style.setProperty("overflow", "visible", "important");
            table.style.setProperty("border-collapse", "collapse", "important");
          });

          const lexicalTableCells = rootEl.querySelectorAll(
            "[data-export-block] .rich-text-content-display th, [data-export-block] .rich-text-content-display td," +
              " [data-export-block] .editor-shell th, [data-export-block] .editor-shell td",
          );
          lexicalTableCells.forEach((cell) => {
            cell.style.setProperty("white-space", "normal", "important");
            cell.style.setProperty("word-break", "break-word", "important");
            cell.style.setProperty("overflow-wrap", "break-word", "important");
            cell.style.setProperty("max-width", "100%", "important");
          });

          // Prevent Lexical-rendered images (used in short description + rich text content)
          // from being clipped by fixed-height/overflow containers in the cloned DOM.
          // Horizontal rules: Lexical uses ::after for the line; html2canvas does not render pseudo-elements.
          // Force hr/.PlaygroundEditorTheme__hr to have visible styling (height + background).
          const lexicalHrs = rootEl.querySelectorAll(
            "[data-export-block] .rich-text-content-display hr, [data-export-block] .editor-shell hr," +
              "[data-export-block] .rich-text-content-display .PlaygroundEditorTheme__hr, [data-export-block] .editor-shell .PlaygroundEditorTheme__hr",
          );
          lexicalHrs.forEach((hrEl) => {
            hrEl.style.setProperty("display", "block", "important");
            hrEl.style.setProperty("visibility", "visible", "important");
            hrEl.style.setProperty("height", "2px", "important");
            hrEl.style.setProperty("min-height", "2px", "important");
            hrEl.style.setProperty("background-color", "#ccc", "important");
            hrEl.style.setProperty("border", "none", "important");
            hrEl.style.setProperty("border-top", "1px solid #ccc", "important");
            hrEl.style.setProperty("width", "100%", "important");
            hrEl.style.setProperty("margin", "10px 0", "important");
          });

          const lexicalImages = rootEl.querySelectorAll(
            "[data-export-block] .rich-text-content-display img, [data-export-block] .editor-shell img",
          );
          lexicalImages.forEach((img) => {
            // Keep sizing conservative to avoid "huge" images in export.
            // Main goal is to prevent clipping, not to change layout.
            img.style.setProperty(
              "display",
              img.style.display || "inline-block",
              "important",
            );
            img.style.setProperty("visibility", "visible", "important");
            img.style.setProperty("opacity", "1", "important");
            img.style.setProperty("max-width", "100%", "important");
            img.style.setProperty("height", "auto", "important");
            img.style.setProperty("max-height", "none", "important");
            img.style.setProperty("object-fit", "contain", "important");
            img.style.setProperty("overflow", "visible", "important");
            img.style.setProperty("break-inside", "avoid", "important");
            img.style.setProperty("page-break-inside", "avoid", "important");
            // Cap very large images so they don't dominate the page.
            img.style.setProperty("max-width", "600px", "important");

            // Ensure parent wrappers don't clip the image.
            let parent = img.parentElement;
            let depth = 0;
            while (parent && depth < 12) {
              if (
                parent.hasAttribute &&
                parent.hasAttribute("data-export-block")
              )
                break;
              parent.style?.setProperty?.("overflow", "visible", "important");
              parent.style?.setProperty?.("max-height", "none", "important");
              parent.style?.setProperty?.("height", "auto", "important");
              parent.style?.setProperty?.("min-height", "auto", "important");
              parent = parent.parentElement;
              depth++;
            }
          });
        } catch (e) {
          // best-effort
        }
      };

      normalizeNegativeLeftMarginsForPDF(clone);
      ensureLexicalViewersVisibleForPDF(clone);

      // Ensure containers wrapping link content are expanded (fixes "content not showing" when link in short description)
      try {
        const anchorsForExpand = clone.querySelectorAll("a[href]");
        anchorsForExpand.forEach((a) => {
          const href = (a.getAttribute("href") || a.href || "").trim();
          if (!href || href === "#" || /^javascript:/i.test(href)) return;
          let p = a.parentElement;
          let depth = 0;
          while (p && depth < 15) {
            if (p.hasAttribute?.("data-export-block")) break;
            p.style?.setProperty?.("overflow", "visible", "important");
            p.style?.setProperty?.("max-height", "none", "important");
            p.style?.setProperty?.("height", "auto", "important");
            p.style?.setProperty?.("display", "block", "important");
            p.style?.setProperty?.("visibility", "visible", "important");
            p = p.parentElement;
            depth++;
          }
        });
      } catch (e) {
        // Best-effort
      }

      // Force layout recalculation for all short description divs before capture
      const allRichTextShortDescDivs = clone.querySelectorAll(
        "[data-export-block='rich-text'] div",
      );
      allRichTextShortDescDivs.forEach((div) => {
        const hasLinks = div.querySelectorAll("a[href]").length;
        const text = div.textContent?.trim() || "";
        if (hasLinks > 0) {
          const rect = div.getBoundingClientRect();
          const style = window.getComputedStyle(div);
        }
        if (text.length > 50) {
          // Force reflow for divs with substantial text
          void div.offsetHeight;
          void div.scrollHeight;
        }
      });

      // Force layout recalculation for attachment short description divs
      const allAttachmentShortDescDivs = clone.querySelectorAll(
        "[data-export-block='attachment'] div",
      );
      allAttachmentShortDescDivs.forEach((div) => {
        const text = div.textContent?.trim() || "";
        if (text.length > 50) {
          // Force reflow for divs with substantial text
          void div.offsetHeight;
          void div.scrollHeight;
        }
      });

      // Additional delay to ensure all layout recalculations are complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Canvas/Konva: composite live canvas content into images inside the cloned DOM
      // so PDF export matches the on-screen preview (no scroll/overflow cropping).
      try {
        // Allow Konva to finish painting before we capture canvas content
        await new Promise((r) =>
          requestAnimationFrame(() => setTimeout(r, 200)),
        );

        // Canvas blocks (Konva): composite all layers into one image using intrinsic dimensions
        const canvasBlocks = container.querySelectorAll(
          "[data-export-block='canvas']",
        );
        const cloneCanvasBlocks = clone.querySelectorAll(
          "[data-export-block='canvas']",
        );

        for (let blockIdx = 0; blockIdx < canvasBlocks.length; blockIdx++) {
          const liveBlock = canvasBlocks[blockIdx];
          const cloneBlock = cloneCanvasBlocks[blockIdx];
          if (!liveBlock || !cloneBlock) continue;

          const liveCanvasesInBlock = Array.from(
            liveBlock.querySelectorAll("canvas"),
          );
          if (liveCanvasesInBlock.length === 0) continue;

          const cloneCanvasesInBlock = Array.from(
            cloneBlock.querySelectorAll("canvas"),
          );
          if (liveCanvasesInBlock.length !== cloneCanvasesInBlock.length)
            continue;

          const dpr = window.devicePixelRatio || 1;
          const pageGapPx = 20;
          const stageContainers = liveBlock.querySelectorAll(
            "[id^='main-canvas-stage-container-']",
          );
          const isMultiPage = stageContainers.length > 1;

          let compositeW, compositeH, displayW, displayH;
          if (isMultiPage) {
            let totalW = 0;
            let totalH = 0;
            for (const cv of liveCanvasesInBlock) {
              const w = cv.width || 0;
              const h = cv.height || 0;
              totalW = Math.max(totalW, w);
              totalH += h + (totalH > 0 ? pageGapPx : 0);
            }
            compositeW = totalW;
            compositeH = totalH;
            displayW = compositeW / dpr;
            displayH = compositeH / dpr;
          } else {
            const fc = liveCanvasesInBlock[0];
            compositeW = fc.width || 0;
            compositeH = fc.height || 0;
            displayW = compositeW / dpr;
            displayH = compositeH / dpr;
          }
          if (displayW < 1 || displayH < 1) continue;

          const liveCanvasContentWrapper = liveBlock.querySelector(
            "[id^='main-canvas-stage-container-']",
          )?.parentElement?.parentElement;

          const firstStageContainer = cloneBlock.querySelector(
            "[id^='main-canvas-stage-container-']",
          );
          const canvasContentWrapper =
            firstStageContainer?.parentElement?.parentElement ||
            firstStageContainer?.parentElement ||
            cloneCanvasesInBlock[0]?.parentElement;

          if (!canvasContentWrapper) {
            continue;
          }

          if (isMultiPage && stageContainers.length > 0) {
            const pageImages = [];
            for (let i = 0; i < stageContainers.length; i++) {
              const stageContainer = stageContainers[i];
              const pageWrapper = stageContainer.parentElement;
              if (!pageWrapper) continue;

              let pageDataURL = "";
              try {
                const snap = await html2canvas(pageWrapper, {
                  useCORS: true,
                  crossOrigin: "anonymous",
                  allowTaint: true,
                  imageTimeout: 5000,
                  backgroundColor: "#fff",
                  scale: 2,
                  logging: false,
                });
                pageDataURL = snap.toDataURL("image/png");
              } catch (_) {
                const cv = liveCanvasesInBlock[i];
                if (cv) {
                  try {
                    pageDataURL = cv.toDataURL("image/png");
                  } catch (__) {}
                }
              }

              if (pageDataURL) {
                const img = document.createElement("img");
                img.src = pageDataURL;
                img.setAttribute("data-canvas-composite", "true");
                img.setAttribute("data-canvas-page", String(i));
                img.style.width = pageWrapper.offsetWidth + "px";
                img.style.height = pageWrapper.offsetHeight + "px";
                img.style.display = "block";
                img.style.objectFit = "contain";
                img.style.maxWidth = "100%";
                pageImages.push({
                  img,
                  isLast: i === stageContainers.length - 1,
                });
              }
            }

            if (pageImages.length > 0) {
              while (canvasContentWrapper.firstChild) {
                canvasContentWrapper.removeChild(
                  canvasContentWrapper.firstChild,
                );
              }
              canvasContentWrapper.style.setProperty(
                "overflow",
                "visible",
                "important",
              );
              canvasContentWrapper.style.setProperty(
                "max-height",
                "none",
                "important",
              );
              pageImages.forEach(({ img, isLast }) => {
                const wrapper = document.createElement("div");
                wrapper.setAttribute("data-canvas-page-unit", "true");
                wrapper.style.marginBottom = isLast ? "0" : pageGapPx + "px";
                wrapper.appendChild(img);
                canvasContentWrapper.appendChild(wrapper);
              });
            }
          } else {
            let dataURL = "";
            let usedHtml2Canvas = false;
            if (liveCanvasContentWrapper) {
              try {
                const snap = await html2canvas(liveCanvasContentWrapper, {
                  useCORS: true,
                  crossOrigin: "anonymous",
                  allowTaint: true,
                  imageTimeout: 5000,
                  backgroundColor: "#fff",
                  scale: 2,
                  logging: false,
                });
                dataURL = snap.toDataURL("image/png");
                usedHtml2Canvas = true;
              } catch (_) {}
            }

            if (!dataURL) {
              const composite = document.createElement("canvas");
              composite.width = compositeW;
              composite.height = compositeH;
              const ctx = composite.getContext("2d");
              if (ctx) {
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, composite.width, composite.height);
                try {
                  for (const cv of liveCanvasesInBlock) {
                    try {
                      ctx.drawImage(
                        cv,
                        0,
                        0,
                        composite.width,
                        composite.height,
                      );
                    } catch (_) {}
                  }
                  dataURL = composite.toDataURL("image/png");
                } catch (_) {
                  try {
                    dataURL =
                      liveCanvasesInBlock[
                        liveCanvasesInBlock.length - 1
                      ].toDataURL("image/png");
                  } catch (__) {
                    const snap = await html2canvas(liveBlock, {
                      useCORS: true,
                      crossOrigin: "anonymous",
                      allowTaint: true,
                      imageTimeout: 5000,
                      backgroundColor: "#fff",
                      scale: 2,
                      logging: false,
                    });
                    dataURL = snap.toDataURL("image/png");
                  }
                }
              }
            }

            if (!dataURL) continue;

            const imgW =
              usedHtml2Canvas && liveCanvasContentWrapper
                ? liveCanvasContentWrapper.offsetWidth
                : displayW;
            const imgH =
              usedHtml2Canvas && liveCanvasContentWrapper
                ? liveCanvasContentWrapper.offsetHeight
                : displayH;

            const img = document.createElement("img");
            img.src = dataURL;
            img.setAttribute("data-canvas-composite", "true");
            img.style.width = (imgW || displayW) + "px";
            img.style.height = (imgH || displayH) + "px";
            img.style.display = "block";
            img.style.objectFit = "contain";
            img.style.maxWidth = "100%";

            while (canvasContentWrapper.firstChild) {
              canvasContentWrapper.removeChild(canvasContentWrapper.firstChild);
            }
            canvasContentWrapper.style.setProperty(
              "overflow",
              "visible",
              "important",
            );
            canvasContentWrapper.style.setProperty(
              "max-height",
              "none",
              "important",
            );
            canvasContentWrapper.appendChild(img);
          }
        }

        // Non-canvas-block canvases (e.g. Lexical): replace individually; use intrinsic dimensions
        const remainingLive = Array.from(
          container.querySelectorAll("canvas"),
        ).filter((c) => !c.closest("[data-export-block='canvas']"));
        const remainingClone = Array.from(
          clone.querySelectorAll("canvas"),
        ).filter((c) => !c.closest("[data-export-block='canvas']"));

        for (let i = 0; i < remainingClone.length; i++) {
          const live = remainingLive[i];
          const c = remainingClone[i];
          if (!live || !c) continue;

          let dataURL = "";
          try {
            dataURL = live.toDataURL("image/png");
          } catch (_) {
            try {
              const snap = await html2canvas(live, {
                useCORS: true,
                crossOrigin: "anonymous",
                allowTaint: false,
                imageTimeout: 0,
                backgroundColor: "#fff",
                scale: 2,
                logging: false,
              });
              dataURL = snap.toDataURL("image/png");
            } catch (__) {}
          }

          if (dataURL) {
            const img = document.createElement("img");
            img.src = dataURL;
            const dpr2 = window.devicePixelRatio || 1;
            const displayW2 =
              Math.max(1, (live.width || 0) / dpr2) ||
              (() => {
                const r = live.getBoundingClientRect();
                return r.width || 0;
              })();
            const displayH2 =
              Math.max(1, (live.height || 0) / dpr2) ||
              (() => {
                const r = live.getBoundingClientRect();
                return r.height || 0;
              })();
            if (displayW2) img.style.width = displayW2 + "px";
            if (displayH2) img.style.height = displayH2 + "px";
            img.style.objectFit = "contain";
            c.replaceWith(img);
          }
        }
      } catch (_) {
        // best-effort; do not block export
      }

      /**
       * Measure vertical gaps between major flow blocks (headings, export blocks, tables).
       * If gap exceeds maxGapPx, trim margin-bottom (upper) and margin-top (lower) so PDF/html2canvas
       * does not preserve stacked margins + padding as "tons" of white space.
       */
      const clampPdfVerticalWhitespaceGaps = (root, opts = {}) => {
        const maxGapPx = opts.maxGapPx ?? 32;
        if (!root?.querySelectorAll) return;

        const selector = [
          "[data-export-block='section']",
          "[data-export-block='bom']",
          "[data-export-block='training-log']",
          "[data-export-block='step']",
          "[data-export-block='substep']",
          "[data-export-block='canvas']",
          "[data-export-block='rich-text']",
          "[data-export-block='video']",
          "[data-export-block='attachment']",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "table",
        ].join(",");

        for (let pass = 0; pass < 4; pass++) {
          const raw = Array.from(root.querySelectorAll(selector)).filter(
            (el) => {
              const r = el.getBoundingClientRect();
              return r.width > 2 && r.height > 2;
            },
          );

          raw.sort((a, b) => {
            const p = a.compareDocumentPosition(b);
            if (p & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
            if (p & Node.DOCUMENT_POSITION_PRECEDING) return 1;
            return 0;
          });

          const flat = [];
          for (const el of raw) {
            if (flat.some((x) => x !== el && x.contains(el))) continue;
            flat.push(el);
          }

          let changed = false;
          for (let i = 0; i < flat.length - 1; i++) {
            const a = flat[i];
            const b = flat[i + 1];
            if (a.contains(b)) continue;

            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            const gap = rb.top - ra.bottom;
            if (gap <= maxGapPx) continue;

            const excess = gap - maxGapPx;
            const csA = window.getComputedStyle(a);
            const csB = window.getComputedStyle(b);
            let mbA = parseFloat(csA.marginBottom) || 0;
            let mtB = parseFloat(csB.marginTop) || 0;
            let pbA = parseFloat(csA.paddingBottom) || 0;
            let ptB = parseFloat(csB.paddingTop) || 0;

            let remaining = excess;
            const takeFrom = (avail, amount) => Math.min(avail, amount);

            const cutMarginBottom = takeFrom(mbA, remaining);
            mbA -= cutMarginBottom;
            remaining -= cutMarginBottom;
            const cutMarginTop = takeFrom(mtB, remaining);
            mtB -= cutMarginTop;
            remaining -= cutMarginTop;
            const cutPadBottom = takeFrom(pbA, remaining);
            pbA -= cutPadBottom;
            remaining -= cutPadBottom;
            const cutPadTop = takeFrom(ptB, remaining);
            ptB -= cutPadTop;
            remaining -= cutPadTop;

            if (cutMarginBottom + cutMarginTop + cutPadBottom + cutPadTop < 0.5) {
              continue;
            }

            if (cutMarginBottom > 0.25) {
              a.style.setProperty(
                "margin-bottom",
                `${Math.round(mbA)}px`,
                "important",
              );
              changed = true;
            }
            if (cutMarginTop > 0.25) {
              b.style.setProperty(
                "margin-top",
                `${Math.round(mtB)}px`,
                "important",
              );
              changed = true;
            }
            if (cutPadBottom > 0.25) {
              a.style.setProperty(
                "padding-bottom",
                `${Math.round(pbA)}px`,
                "important",
              );
              changed = true;
            }
            if (cutPadTop > 0.25) {
              b.style.setProperty(
                "padding-top",
                `${Math.round(ptB)}px`,
                "important",
              );
              changed = true;
            }
          }
          if (!changed) break;
        }
      };

      const stripEmptyBomTbodyForPdfExport = (root) => {
        if (!root || !root.querySelectorAll) return;
        root.querySelectorAll("table").forEach((table) => {
          const thead = table.querySelector("thead");
          const tbody = table.querySelector("tbody");
          if (!thead || !tbody) return;
          const theadText = (thead.textContent || "").toLowerCase();
          if (
            !theadText.includes("type") ||
            !theadText.includes("item code") ||
            !theadText.includes("description") ||
            !(theadText.includes("qty per") || theadText.includes("qty"))
          ) {
            return;
          }
          const inBomBlock =
            table.closest("[data-export-block='bom']") !== null;
          let hasBomHeading = false;
          let prev = table.previousElementSibling;
          let depth = 0;
          while (prev && depth < 10) {
            const h3 =
              prev.tagName === "H3"
                ? prev
                : prev.querySelector && prev.querySelector("h3");
            if (
              h3 &&
              (h3.textContent || "").trim().toLowerCase().endsWith(" bom")
            ) {
              hasBomHeading = true;
              break;
            }
            prev = prev.previousElementSibling;
            depth++;
          }
          if (!inBomBlock && !hasBomHeading) return;
          const trs = tbody.querySelectorAll("tr");
          if (trs.length !== 1) return;
          const rowText = (trs[0].textContent || "")
            .replace(/\s+/g, " ")
            .trim();
          if (rowText === "No parts to display.") {
            trs[0].remove();
          }
        });
      };

      stripEmptyBomTbodyForPdfExport(clone);

      // Horizontal rules: Lexical uses ::after for the visible line; html2canvas does not render pseudo-elements.
      // 1) Inject CSS so hr/.PlaygroundEditorTheme__hr get visible styling (height + background) in the clone.
      // 2) Replace elements with visible divs as fallback.
      const hrStyle = clone.ownerDocument.createElement("style");
      hrStyle.setAttribute("data-pdf-hr-fix", "true");
      hrStyle.textContent = `
        hr, .PlaygroundEditorTheme__hr {
          display: block !important;
          visibility: visible !important;
          height: 2px !important;
          min-height: 2px !important;
          background-color: #ccc !important;
          border: none !important;
          border-top: 1px solid #ccc !important;
          width: 100% !important;
          margin: 10px 0 !important;
        }
      `;
      clone.insertBefore(hrStyle, clone.firstChild);

      const hrElementsInClone = Array.from(
        clone.querySelectorAll("hr, .PlaygroundEditorTheme__hr"),
      );
      hrElementsInClone.forEach((el) => {
        const replacement = clone.ownerDocument.createElement("div");
        replacement.setAttribute("data-pdf-hr-replacement", "true");
        replacement.style.cssText =
          "display:block !important; visibility:visible !important; opacity:1 !important; margin-top:10px !important; margin-bottom:10px !important; border:none !important; border-top:1px solid #cccccc !important; width:100% !important; height:2px !important; min-height:2px !important; background-color:#cccccc !important;";
        if (el.parentNode) {
          el.parentNode.replaceChild(replacement, el);
        }
      });

      applyLexicalCheckboxListStrikethroughForPdf(clone);
      injectPdfListBulletStylesForPdf(clone);
      applyPdfListBulletDomFallback(clone);

      try {
        clampPdfVerticalWhitespaceGaps(clone, { maxGapPx: 32 });
      } catch (_) {}

      // IMPORTANT: After final layout (including whitespace clamp), re-collect metas for pagination.
      try {
        cloneRect = clone.getBoundingClientRect();
        blockMetas = collectBlockMetas(cloneRect);
        tableMetas = collectTableMetas(cloneRect);
        stepMetas = collectStepMetas(cloneRect);
        substepMetas = collectSubstepMetas(cloneRect);
        canvasImageMetas = collectCanvasImageMetas(cloneRect);
        attachmentImageMetas = collectAttachmentImageMetas(cloneRect);

        // Final recollection replaces blockMetas with new objects. Re-apply the same
        // step/substep linking as after the first collect (~8785); otherwise canvas,
        // rich-text, etc. have no stepId and multi-page "Step N (x/y)" breaks (notably
        // in ZIP export where the same path runs with returnBlob).
        tableMetas.forEach((tableMeta) => {
          if (tableMeta.isItemRequiredTable && tableMeta.stepContainer) {
            const matchingStep = stepMetas.find(
              (stepMeta) => stepMeta.element === tableMeta.stepContainer,
            );
            if (matchingStep) {
              tableMeta.stepId = matchingStep.stepId;
            }
          }
        });
        const linkChildBlockLabels = new Set([
          "attachment",
          "rich-text",
          "video",
          "form",
          "canvas",
        ]);
        blockMetas.forEach((meta) => {
          if (!linkChildBlockLabels.has(meta.label) || !meta.element) return;
          let parent = meta.element.parentElement;
          while (parent && parent !== clone) {
            if (parent.hasAttribute("data-export-block")) {
              const parentLabel = parent.getAttribute("data-export-block") || "";
              if (parentLabel === "substep") {
                const substepMeta = substepMetas.find(
                  (s) => s.element === parent,
                );
                if (substepMeta) {
                  meta.substepId = substepMeta.substepId;
                }
              }
              if (parentLabel === "step") {
                const stepMeta = stepMetas.find((s) => s.element === parent);
                if (stepMeta) {
                  meta.stepId = stepMeta.stepId;
                  break;
                }
              }
            }
            parent = parent.parentElement;
          }
        });
      } catch (e) {
        // best-effort
      }

      // If any tracked content extends below clone bounds (common with canvas wrappers),
      // expand clone/wrapper height before html2canvas so the tail is not clipped.
      try {
        const cloneHeightDom = cloneRect?.height || 0;
        const maxBlockBottomDom = blockMetas.reduce(
          (max, m) => Math.max(max, Number(m?.bottomDom) || 0),
          0,
        );
        const maxCanvasImageBottomDom = canvasImageMetas.reduce(
          (max, m) => Math.max(max, Number(m?.bottomDom) || 0),
          0,
        );
        const maxAttachmentImageBottomDom = attachmentImageMetas.reduce(
          (max, m) => Math.max(max, Number(m?.bottomDom) || 0),
          0,
        );
        const requiredHeightDom =
          Math.max(
            cloneHeightDom,
            maxBlockBottomDom,
            maxCanvasImageBottomDom,
            maxAttachmentImageBottomDom,
          ) + 24;

        if (requiredHeightDom > cloneHeightDom + 1) {
          const requiredHeightPx = `${Math.ceil(requiredHeightDom)}px`;
          clone.style.setProperty("min-height", requiredHeightPx, "important");
          clone.style.setProperty("height", requiredHeightPx, "important");
          wrapperDiv.style.setProperty("min-height", requiredHeightPx, "important");
          wrapperDiv.style.setProperty("height", requiredHeightPx, "important");
          wrapperDiv.style.setProperty("overflow", "visible", "important");
          void clone.offsetHeight;

          cloneRect = clone.getBoundingClientRect();
          blockMetas = collectBlockMetas(cloneRect);
          tableMetas = collectTableMetas(cloneRect);
          stepMetas = collectStepMetas(cloneRect);
          substepMetas = collectSubstepMetas(cloneRect);
          canvasImageMetas = collectCanvasImageMetas(cloneRect);
          attachmentImageMetas = collectAttachmentImageMetas(cloneRect);

        }
      } catch (e) {
        // best-effort
      }

      const canvas = await html2canvas(clone, {
        useCORS: true,
        crossOrigin: "anonymous",
        allowTaint: false,
        imageTimeout: 0,
        backgroundColor: "#ffffff",
        scale: 2,
        logging: false,
        onclone: (clonedDoc) => {
          // Apply the same negative-margin normalization inside html2canvas' internal cloned document
          // (the element-level styles above do not always carry over 1:1).
          normalizeNegativeLeftMarginsForPDF(clonedDoc);
          ensureLexicalViewersVisibleForPDF(clonedDoc);
          try {
            const spacingRoot =
              clonedDoc.body || clonedDoc.documentElement || clonedDoc;
            if (spacingRoot) {
              clampPdfVerticalWhitespaceGaps(spacingRoot, { maxGapPx: 32 });
              applyTightBomBlockForPdfExport(spacingRoot);
            }
          } catch (_) {}

          // Inject CSS for horizontal rules - Lexical uses ::after; html2canvas does not render pseudo-elements.
          const hrStyleEl = clonedDoc.createElement("style");
          hrStyleEl.setAttribute("data-pdf-hr-fix", "true");
          hrStyleEl.textContent = `
            hr, .PlaygroundEditorTheme__hr {
              display: block !important;
              visibility: visible !important;
              height: 2px !important;
              min-height: 2px !important;
              background-color: #ccc !important;
              border: none !important;
              border-top: 1px solid #ccc !important;
              width: 100% !important;
              margin: 10px 0 !important;
            }
          `;
          const clonedRoot =
            clonedDoc.body || clonedDoc.documentElement || clonedDoc;
          if (clonedRoot) {
            clonedRoot.insertBefore(hrStyleEl, clonedRoot.firstChild);
          }

          // Replace horizontal rules with visible divs (fallback if elements exist in clone)
          const clonedHrElements =
            clonedRoot && clonedRoot.querySelectorAll
              ? Array.from(
                  clonedRoot.querySelectorAll("hr, .PlaygroundEditorTheme__hr"),
                )
              : [];
          clonedHrElements.forEach((el) => {
            const replacement = clonedDoc.createElement("div");
            replacement.setAttribute("data-pdf-hr-replacement", "true");
            replacement.style.cssText =
              "display:block !important; visibility:visible !important; opacity:1 !important; margin-top:10px !important; margin-bottom:10px !important; border:none !important; border-top:1px solid #cccccc !important; width:100% !important; height:2px !important; min-height:2px !important; background-color:#cccccc !important;";
            if (el.parentNode) {
              el.parentNode.replaceChild(replacement, el);
            }
          });

          // Ensure all short description divs are fully expanded in the cloned document
          // Handle rich text blocks
          const clonedRichTextShortDescDivs = clonedDoc.querySelectorAll(
            "[data-export-block='rich-text'] div",
          );
          clonedRichTextShortDescDivs.forEach((div) => {
            const style = window.getComputedStyle(div);
            const text = div.textContent?.trim() || "";
            if (text.length > 0) {
              const isNotContentContainer =
                !div.classList.contains("rich-text-content-display") &&
                !div.classList.contains("editor-shell") &&
                !div.closest(".rich-text-content-display") &&
                !div.closest(".editor-shell");
              if (
                isNotContentContainer &&
                (style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre")
              ) {
                div.style.setProperty("max-height", "none", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("overflow", "visible", "important");
              }
            }
          });

          // Handle attachment blocks
          const clonedAttachmentShortDescDivs = clonedDoc.querySelectorAll(
            "[data-export-block='attachment'] div",
          );
          clonedAttachmentShortDescDivs.forEach((div) => {
            const style = window.getComputedStyle(div);
            const text = div.textContent?.trim() || "";
            if (text.length > 0) {
              const isNotContentContainer =
                !div.classList.contains("rich-text-content-display") &&
                !div.classList.contains("editor-shell") &&
                !div.closest(".rich-text-content-display") &&
                !div.closest(".editor-shell");
              const isHeadingContainer =
                div.querySelector("h2") ||
                div.querySelector('input[type="checkbox"]');
              const hasPreWrap =
                style.whiteSpace === "pre-wrap" || style.whiteSpace === "pre";

              // More flexible detection: has pre-wrap, not heading, not content container, has text
              if (
                hasPreWrap &&
                isNotContentContainer &&
                !isHeadingContainer &&
                !div.querySelector("img")
              ) {
                div.style.setProperty("max-height", "none", "important");
                div.style.setProperty("height", "auto", "important");
                div.style.setProperty("overflow", "visible", "important");
                div.style.setProperty("word-break", "break-word", "important");
                div.style.setProperty(
                  "overflow-wrap",
                  "break-word",
                  "important",
                );
                div.style.setProperty("white-space", "pre-wrap", "important");
                div.style.setProperty("line-height", "1.5", "important");
                // Ensure margin-left is set if not present
                if (!style.marginLeft || !style.marginLeft.includes("5px")) {
                  div.style.setProperty("margin-left", "5px", "important");
                }
              }
            }
          });

          injectPdfListBulletStylesForPdf(clonedDoc);
          applyPdfListBulletDomFallback(
            clonedDoc.body || clonedDoc.documentElement,
          );
          const onCloneRoot = clonedDoc.body || clonedDoc.documentElement;
          if (onCloneRoot && onCloneRoot.querySelectorAll) {
            applyLexicalCheckboxListStrikethroughForPdf(onCloneRoot);
          }
        },
      });

      // Collect link metadata for PDF link annotations (before clone is removed from DOM)
      const linkMetasDom = [];
      try {
        const anchors = clone.querySelectorAll("a[href]");
        console.log(
          "[PDF Export - Links] Found anchors:",
          anchors.length,
          Array.from(anchors).map((a) => ({
            href: (a.getAttribute("href") || a.href || "").slice(0, 50),
            text: (a.textContent || "").slice(0, 30),
            parentBlock: a
              .closest("[data-export-block]")
              ?.getAttribute("data-export-block"),
          })),
        );
        anchors.forEach((a) => {
          const href = (a.getAttribute("href") || a.href || "").trim();
          if (!href || href === "#" || /^javascript:/i.test(href)) {
            return;
          }
          const normalizedHref =
            /^https?:\/\//i.test(href) || /^mailto:/i.test(href)
              ? href
              : href.startsWith("//")
                ? "https:" + href
                : href.startsWith("/")
                  ? (window.location?.origin || "") + href
                  : href;
          const rect = a.getBoundingClientRect();
          if (!rect || rect.width <= 0 || rect.height <= 0) {
            const ancestors = [];
            let p = a.parentElement;
            for (let i = 0; i < 5 && p; i++) {
              const s = window.getComputedStyle(p);
              ancestors.push({
                tag: p.tagName,
                className: p.className?.slice(0, 50),
                display: s.display,
                visibility: s.visibility,
                overflow: s.overflow,
                maxHeight: s.maxHeight,
              });
              p = p.parentElement;
            }
            console.warn(
              "[PDF Export - Links] BLOCKING: Anchor has zero size (content may be hidden):",
              {
                href: normalizedHref?.slice(0, 40),
                rect: rect ? { w: rect.width, h: rect.height } : null,
                display: window.getComputedStyle(a).display,
                visibility: window.getComputedStyle(a).visibility,
                ancestorsBlocking: ancestors,
              },
            );
            return;
          }
          const style = window.getComputedStyle(a);
          if (style.display === "none" || style.visibility === "hidden") {
            return;
          }
          linkMetasDom.push({
            topDom: rect.top - cloneRect.top,
            leftDom: rect.left - cloneRect.left,
            widthDom: rect.width,
            heightDom: rect.height,
            href: normalizedHref,
          });
        });
      } catch (e) {
        console.error("[PDF Export - Links] Error collecting links:", e);
        // Best-effort; do not block PDF export
      }

      const domToCanvasScaleX =
        cloneRect.width > 0 ? canvas.width / cloneRect.width : 1;
      const domToCanvasScaleY =
        cloneRect.height > 0 ? canvas.height / cloneRect.height : 1;

      // Convert link metadata to canvas pixels for PDF link annotations
      const linkMetasPx = (linkMetasDom || []).map((link) => ({
        topPx: Math.round(link.topDom * domToCanvasScaleY),
        leftPx: Math.round(link.leftDom * domToCanvasScaleX),
        widthPx: Math.max(1, Math.round(link.widthDom * domToCanvasScaleX)),
        heightPx: Math.max(1, Math.round(link.heightDom * domToCanvasScaleY)),
        bottomPx: Math.round(
          (link.topDom + link.heightDom) * domToCanvasScaleY,
        ),
        href: link.href,
      }));
      // Collect "keep together" pairs for Section headers:
      // If a Section heading (h2) and its short description would be split by a page break,
      // we move BOTH to the next page.
      const sectionHeadingPairsDom = (() => {
        try {
          const pairs = [];
          const sectionBlocks = Array.from(
            clone.querySelectorAll("[data-export-block='section']"),
          );

          sectionBlocks.forEach((sectionBlock) => {
            const sectionHeading = sectionBlock.querySelector("h2");
            if (!sectionHeading) return;

            const headingText = (sectionHeading.textContent || "").trim();
            // Only apply to actual "Section X" style headings to avoid affecting unrelated h2s.
            const isSectionHeading = /^section\b/i.test(headingText);
            if (!isSectionHeading) return;

            const headingRect = sectionHeading.getBoundingClientRect();
            if (!headingRect || headingRect.height <= 0) return;

            // Robust: find the closest text element *below* the Section heading inside the Section block.
            // This covers cases where the short description lives inside nested wrappers like
            // `data-export-block='section-intro'` or other containers.
            const candidates = Array.from(
              sectionBlock.querySelectorAll("div, p, span"),
            );
            let bestCandidateRect = null;
            candidates.forEach((el) => {
              if (!el || el === sectionHeading) return;
              if (el.contains(sectionHeading)) return;
              if (el.tagName === "HR") return;
              const text = (el.textContent || "").trim();
              if (!text) return;
              // Avoid picking tree-path labels or repeated heading text
              if (/^section\b/i.test(text)) return;

              const rect = el.getBoundingClientRect();
              if (!rect || rect.height <= 0) return;
              // Only consider content that is visually below the heading
              if (rect.top < headingRect.bottom - 1) return;

              if (!bestCandidateRect || rect.top < bestCandidateRect.top) {
                bestCandidateRect = rect;
              }
            });

            if (!bestCandidateRect) return;

            pairs.push({
              headingTopDom: headingRect.top - cloneRect.top,
              headingBottomDom: headingRect.bottom - cloneRect.top,
              descBottomDom: bestCandidateRect.bottom - cloneRect.top,
            });
          });

          return pairs;
        } catch (e) {
          return [];
        }
      })();

      const sectionHeadingPairsPx = (sectionHeadingPairsDom || [])
        .map((pair) => ({
          headingTopPx: Math.round(pair.headingTopDom * domToCanvasScaleY),
          headingBottomPx: Math.round(
            pair.headingBottomDom * domToCanvasScaleY,
          ),
          descBottomPx: Math.round(pair.descBottomDom * domToCanvasScaleY),
        }))
        .filter(
          (p) =>
            Number.isFinite(p.headingTopPx) &&
            Number.isFinite(p.headingBottomPx) &&
            Number.isFinite(p.descBottomPx) &&
            p.headingTopPx >= 0 &&
            p.descBottomPx > p.headingTopPx,
        );

      // Collect ALL section headers (heading only) for "don't cut" protection - even if no short description
      // This fixes Section 2, Section 3 etc. being cut when they lack a matching description in pairs
      const sectionHeadingOnlyPx = (() => {
        try {
          const list = [];
          const seen = new Set();
          const currentCloneRect = clone.getBoundingClientRect();
          const measureSectionHeadingBandDom = (
            sectionHeading,
            sectionBlockEl,
          ) => {
            const headingRect = sectionHeading.getBoundingClientRect();
            if (!headingRect || headingRect.height <= 0) return null;
            let topDom = headingRect.top;
            let bottomDom = headingRect.bottom;
            // Title row only (checkbox + h2). Avoid closest('div[style*="display"]') — it matches
            // almost any wrapper and can inflate the band to the whole section / page.
            const rowEl =
              sectionHeading.closest('div[style*="flex"]') ||
              sectionHeading.closest('div[style*="justify-content"]');
            if (rowEl) {
              const rr = rowEl.getBoundingClientRect();
              topDom = Math.min(topDom, rr.top);
              bottomDom = Math.max(bottomDom, rr.bottom);
            }
            const intro =
              (sectionBlockEl &&
                sectionBlockEl.querySelector(
                  '[data-export-block="section-intro"]',
                )) ||
              sectionHeading.closest('[data-export-block="section-intro"]');
            if (intro) {
              let el = rowEl
                ? rowEl.nextElementSibling
                : sectionHeading.parentElement?.nextElementSibling;
              let hrCount = 0;
              while (el && el.tagName === "HR" && hrCount < 2) {
                try {
                  const hrRect = el.getBoundingClientRect();
                  if (hrRect && hrRect.height > 0) {
                    bottomDom = Math.max(bottomDom, hrRect.bottom);
                  }
                } catch (e) {
                  /* ignore */
                }
                hrCount += 1;
                el = el.nextElementSibling;
              }
            }
            return { topDom, bottomDom };
          };
          // First: from section blocks
          const sectionBlocks = Array.from(
            clone.querySelectorAll("[data-export-block='section']"),
          );
          sectionBlocks.forEach((sectionBlock) => {
            const sectionHeading = sectionBlock.querySelector("h2");
            if (!sectionHeading) return;
            const headingText = (sectionHeading.textContent || "").trim();
            if (!/^section\b/i.test(headingText)) return;
            const band = measureSectionHeadingBandDom(
              sectionHeading,
              sectionBlock,
            );
            if (!band) return;
            const bandBottomPadPx = Math.round(12 * domToCanvasScaleY);
            const topPx = Math.round(
              (band.topDom - currentCloneRect.top) * domToCanvasScaleY,
            );
            const bottomPx =
              Math.round(
                (band.bottomDom - currentCloneRect.top) * domToCanvasScaleY,
              ) + bandBottomPadPx;
            if (
              Number.isFinite(topPx) &&
              Number.isFinite(bottomPx) &&
              topPx >= 0 &&
              bottomPx > topPx
            ) {
              list.push({ headingTopPx: topPx, headingBottomPx: bottomPx });
              seen.add(sectionHeading);
            }
          });
          // Fallback: ANY h2 matching "Section X" in the document (catches different DOM structures)
          const allH2s = Array.from(clone.querySelectorAll("h2"));
          allH2s.forEach((h2) => {
            if (seen.has(h2)) return;
            const headingText = (h2.textContent || "").trim();
            if (!/^section\b/i.test(headingText)) return;
            const sectionBlock = h2.closest("[data-export-block='section']");
            const band = measureSectionHeadingBandDom(h2, sectionBlock);
            if (!band) return;
            const bandBottomPadPx = Math.round(12 * domToCanvasScaleY);
            const topPx = Math.round(
              (band.topDom - currentCloneRect.top) * domToCanvasScaleY,
            );
            const bottomPx =
              Math.round(
                (band.bottomDom - currentCloneRect.top) * domToCanvasScaleY,
              ) + bandBottomPadPx;
            if (
              Number.isFinite(topPx) &&
              Number.isFinite(bottomPx) &&
              topPx >= 0 &&
              bottomPx > topPx
            ) {
              list.push({ headingTopPx: topPx, headingBottomPx: bottomPx });
            }
          });
          // Sort by topPx for consistent ordering
          list.sort((a, b) => a.headingTopPx - b.headingTopPx);
          return list;
        } catch (e) {
          return [];
        }
      })();

      // Keep exact section heading text coordinates so we can force-draw title text
      // after all overlays/masks. This fixes cases where HR remains visible but "Section X"
      // text gets wiped by later redraw passes.
      const sectionHeadingTextMetasPx = (() => {
        try {
          const currentCloneRect = clone.getBoundingClientRect();
          return Array.from(clone.querySelectorAll("h2"))
            .map((h2) => {
              const headingText = (h2.textContent || "").trim();
              if (!/^section\s+\d+/i.test(headingText)) return null;
              const rect = h2.getBoundingClientRect();
              if (!rect || rect.height <= 0 || rect.width <= 0) return null;
              return {
                text: headingText.replace(/\s+/g, " ").trim(),
                topPx: Math.round((rect.top - currentCloneRect.top) * domToCanvasScaleY),
                leftPx: Math.round(
                  (rect.left - currentCloneRect.left) * domToCanvasScaleX,
                ),
                heightPx: Math.max(1, Math.round(rect.height * domToCanvasScaleY)),
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.topPx - b.topPx);
        } catch (e) {
          return [];
        }
      })();

      if (revisionHeadingRect) {
        revisionHeadingPositionPx = {
          topPx: Math.round(
            (revisionHeadingRect.top - cloneRect.top) * domToCanvasScaleY,
          ),
          leftPx: Math.round(
            (revisionHeadingRect.left - cloneRect.left) * domToCanvasScaleX,
          ),
        };
      }

      // Helper function to check if a table meta represents an Item Required table
      const isItemRequiredTableMeta = (meta) => {
        if (!meta || !meta.element) return false;
        const table = meta.element;

        // Check if table has "Where Mentioned" in ANY th
        const thead = table.querySelector("thead");
        if (thead) {
          const allThs = thead.querySelectorAll("th");
          for (const th of allThs) {
            const thText = (th.textContent || "").trim();
            if (thText.includes("Where Mentioned")) {
              return true;
            }
          }
        }

        // Also check tbody for "Where Mentioned"
        const tbody = table.querySelector("tbody");
        if (tbody) {
          const firstRow = tbody.querySelector("tr");
          if (firstRow) {
            const firstCell = firstRow.querySelector("td, th");
            if (
              firstCell &&
              (firstCell.textContent || "").includes("Where Mentioned")
            ) {
              return true;
            }
          }
        }

        // Check table text content
        const tableText = (table.textContent || "").toLowerCase();
        if (
          tableText.includes("where mentioned") &&
          !table.closest("[data-export-block='revision']") &&
          !table.closest("[data-export-block='signature-history']")
        ) {
          return true;
        }

        // Check if table follows a heading with "item required", "items required", or "parts required"
        let prevSibling = table.previousElementSibling;
        let checkDepth = 0;
        while (prevSibling && checkDepth < 10) {
          if (prevSibling.tagName && /^H[1-6]$/i.test(prevSibling.tagName)) {
            const headingText = (prevSibling.textContent || "")
              .trim()
              .toLowerCase();
            if (
              headingText.includes("item required") ||
              headingText.includes("items required") ||
              headingText.includes("parts required")
            ) {
              return true;
            }
          }
          if (prevSibling.querySelector) {
            const headingInParent = prevSibling.querySelector(
              "h1, h2, h3, h4, h5, h6",
            );
            if (headingInParent) {
              const headingText = (headingInParent.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText.includes("item required") ||
                headingText.includes("items required") ||
                headingText.includes("parts required")
              ) {
                return true;
              }
            }
          }
          prevSibling = prevSibling.previousElementSibling;
          checkDepth++;
        }

        // Check in parent containers
        if (table.parentElement) {
          const parent = table.parentElement;
          const headingInParent = parent.querySelector(
            "h1, h2, h3, h4, h5, h6",
          );
          if (headingInParent) {
            const headingText = (headingInParent.textContent || "")
              .trim()
              .toLowerCase();
            if (
              headingText.includes("item required") ||
              headingText.includes("items required") ||
              headingText.includes("parts required")
            ) {
              const allChildren = Array.from(parent.children);
              const headingIndex = allChildren.indexOf(headingInParent);
              const tableIndex = allChildren.indexOf(table);
              if (
                headingIndex >= 0 &&
                tableIndex >= 0 &&
                headingIndex < tableIndex
              ) {
                return true;
              }
            }
          }
        }

        // Check in step/rich-text containers
        const stepContainer = table.closest(
          "[data-export-block='step'], [data-export-block='substep'], [data-export-block='rich-text'], [data-export-block='section']",
        );
        if (stepContainer) {
          const headingInContainer = stepContainer.querySelector(
            "h1, h2, h3, h4, h5, h6",
          );
          if (headingInContainer) {
            const headingText = (headingInContainer.textContent || "")
              .trim()
              .toLowerCase();
            if (
              headingText.includes("item required") ||
              headingText.includes("items required") ||
              headingText.includes("parts required")
            ) {
              const allElements = Array.from(
                stepContainer.querySelectorAll("*"),
              );
              const headingPos = allElements.indexOf(headingInContainer);
              const tablePos = allElements.indexOf(table);
              if (headingPos >= 0 && tablePos >= 0 && headingPos < tablePos) {
                return true;
              }
            }
          }
        }

        // Also check for Items Required table structure (Type, Item Code, Description, Qty Per)
        // but only if it's NOT a BOM table
        if (thead) {
          const theadText = (thead.textContent || "").toLowerCase();
          if (
            theadText.includes("type") &&
            theadText.includes("item code") &&
            theadText.includes("description") &&
            (theadText.includes("qty per") || theadText.includes("qty"))
          ) {
            // Check if this is NOT a BOM table
            const isBomTable =
              table.closest("[data-export-block='bom']") !== null;
            if (!isBomTable) {
              // Check if there's a BOM heading nearby
              let hasBomHeading = false;
              let checkSibling = table.previousElementSibling;
              let checkDepth = 0;
              while (checkSibling && checkDepth < 5) {
                if (
                  checkSibling.tagName &&
                  /^H[1-6]$/i.test(checkSibling.tagName)
                ) {
                  const headingText = (checkSibling.textContent || "")
                    .trim()
                    .toLowerCase();
                  if (headingText.endsWith(" bom")) {
                    hasBomHeading = true;
                    break;
                  }
                }
                if (checkSibling.querySelector) {
                  const headingInParent = checkSibling.querySelector(
                    "h1, h2, h3, h4, h5, h6",
                  );
                  if (headingInParent) {
                    const headingText = (headingInParent.textContent || "")
                      .trim()
                      .toLowerCase();
                    if (headingText.endsWith(" bom")) {
                      hasBomHeading = true;
                      break;
                    }
                  }
                }
                checkSibling = checkSibling.previousElementSibling;
                checkDepth++;
              }
              // If no BOM heading found, it's likely an Items Required table
              if (!hasBomHeading) {
                return true;
              }
            }
          }
        }

        return false;
      };

      // Item Required tables are now included in PDF export
      // No longer filtering them out from tableMetas

      tableMetas.forEach((meta) => {
        meta.leftPx = Math.round(meta.leftDom * domToCanvasScaleX);
        meta.widthPx = Math.max(
          1,
          Math.round(meta.widthDom * domToCanvasScaleX),
        );
        meta.topPx = Math.round(meta.topDom * domToCanvasScaleY);
        meta.bottomPx = Math.round(meta.bottomDom * domToCanvasScaleY);
        meta.headerTopPx = Math.round(meta.headerTopDom * domToCanvasScaleY);
        meta.headerHeightPx = Math.max(
          1,
          Math.round(meta.headerHeightDom * domToCanvasScaleY),
        );

        if (meta.widthPx <= 0 || meta.headerHeightPx <= 0) {
          meta.headerCanvas = null;
          return;
        }

        const headerCanvas = document.createElement("canvas");
        headerCanvas.width = meta.widthPx;
        headerCanvas.height = meta.headerHeightPx;
        const headerCtx = headerCanvas.getContext("2d");

        headerCtx.fillStyle = "#ffffff";
        headerCtx.fillRect(0, 0, meta.widthPx, meta.headerHeightPx);

        if (
          meta.headingElement &&
          meta.headingTopDom !== null &&
          meta.headingHeightDom !== null
        ) {
          const headingTopPx = Math.round(
            meta.headingTopDom * domToCanvasScaleY,
          );
          const headingHeightPx = Math.round(
            meta.headingHeightDom * domToCanvasScaleY,
          );
          const headingLeftPx = meta.leftPx;
          headerCtx.drawImage(
            canvas,
            meta.leftPx,
            headingTopPx,
            meta.widthPx,
            headingHeightPx,
            0,
            0,
            meta.widthPx,
            headingHeightPx,
          );

          // Use 2px spacing for Training Log tables, 10px for others (Item Required)
          const spacingPx = Math.round(
            (meta.isTrainingLogTable ? 2 : 10) * domToCanvasScaleY,
          );
          const theadTopPx = Math.round(meta.theadTopDom * domToCanvasScaleY);
          const theadHeightPx = Math.round(
            meta.theadHeightDom * domToCanvasScaleY,
          );
          headerCtx.drawImage(
            canvas,
            meta.leftPx,
            theadTopPx,
            meta.widthPx,
            theadHeightPx,
            0,
            headingHeightPx + spacingPx,
            meta.widthPx,
            theadHeightPx,
          );
        } else {
          headerCtx.drawImage(
            canvas,
            meta.leftPx,
            meta.headerTopPx,
            meta.widthPx,
            meta.headerHeightPx,
            0,
            0,
            meta.widthPx,
            meta.headerHeightPx,
          );
        }

        meta.headerCanvas = headerCanvas;
      });

      stepMetas.forEach((meta) => {
        meta.topPx = Math.round(meta.topDom * domToCanvasScaleY);
        meta.bottomPx = Math.round(meta.bottomDom * domToCanvasScaleY);
        meta.headerTopPx = Math.round(meta.headerTopDom * domToCanvasScaleY);
        meta.headerHeightPx = Math.max(
          1,
          Math.round(meta.headerHeightDom * domToCanvasScaleY),
        );
        meta.headerLeftPx = Math.round(meta.headerLeftDom * domToCanvasScaleX);
        meta.headerWidthPx = Math.max(
          1,
          Math.round(meta.headerWidthDom * domToCanvasScaleX),
        );
        if (
          meta.stepShortDescTopDom != null &&
          meta.stepShortDescBottomDom != null
        ) {
          meta.stepShortDescTopPx = Math.round(
            meta.stepShortDescTopDom * domToCanvasScaleY,
          );
          meta.stepShortDescBottomPx = Math.round(
            meta.stepShortDescBottomDom * domToCanvasScaleY,
          );
        } else {
          meta.stepShortDescTopPx = null;
          meta.stepShortDescBottomPx = null;
        }

        if (meta.hrTopDom !== null && meta.hrHeightDom !== null) {
          meta.hrTopPx = Math.round(meta.hrTopDom * domToCanvasScaleY);
          meta.hrHeightPx = Math.max(
            1,
            Math.round(meta.hrHeightDom * domToCanvasScaleY),
          );
        } else {
          meta.hrTopPx = null;
          meta.hrHeightPx = null;
        }

        if (meta.headerWidthPx <= 0 || meta.headerHeightPx <= 0) {
          meta.headerCanvas = null;
          return;
        }

        const headerCanvas = document.createElement("canvas");
        headerCanvas.width = canvas.width;
        headerCanvas.height = meta.headerHeightPx;
        const headerCtx = headerCanvas.getContext("2d");

        headerCtx.fillStyle = "#ffffff";
        headerCtx.fillRect(0, 0, headerCanvas.width, headerCanvas.height);

        headerCtx.drawImage(
          canvas,
          0,
          meta.headerTopPx,
          canvas.width,
          meta.headerHeightPx,
          0,
          0,
          canvas.width,
          meta.headerHeightPx,
        );
        meta.headerCanvas = headerCanvas;
      });

      substepMetas.forEach((meta) => {
        meta.topPx = Math.round(meta.topDom * domToCanvasScaleY);
        meta.bottomPx = Math.round(meta.bottomDom * domToCanvasScaleY);
        meta.headerTopPx = Math.round(meta.headerTopDom * domToCanvasScaleY);
        meta.headerHeightPx = Math.max(
          1,
          Math.round(meta.headerHeightDom * domToCanvasScaleY),
        );
        meta.headerLeftPx = Math.round(meta.headerLeftDom * domToCanvasScaleX);
        meta.headerWidthPx = Math.max(
          1,
          Math.round(meta.headerWidthDom * domToCanvasScaleX),
        );
        if (
          meta.substepShortDescTopDom != null &&
          meta.substepShortDescBottomDom != null
        ) {
          meta.substepShortDescTopPx = Math.round(
            meta.substepShortDescTopDom * domToCanvasScaleY,
          );
          meta.substepShortDescBottomPx = Math.round(
            meta.substepShortDescBottomDom * domToCanvasScaleY,
          );
        } else {
          meta.substepShortDescTopPx = null;
          meta.substepShortDescBottomPx = null;
        }

        if (meta.hrTopDom !== null && meta.hrHeightDom !== null) {
          meta.hrTopPx = Math.round(meta.hrTopDom * domToCanvasScaleY);
          meta.hrHeightPx = Math.max(
            1,
            Math.round(meta.hrHeightDom * domToCanvasScaleY),
          );
        } else {
          meta.hrTopPx = null;
          meta.hrHeightPx = null;
        }

        if (meta.headerWidthPx <= 0 || meta.headerHeightPx <= 0) {
          meta.headerCanvas = null;
          return;
        }

        const headerCanvas = document.createElement("canvas");
        headerCanvas.width = canvas.width;
        headerCanvas.height = meta.headerHeightPx;
        const headerCtx = headerCanvas.getContext("2d");

        headerCtx.fillStyle = "#ffffff";
        headerCtx.fillRect(0, 0, headerCanvas.width, headerCanvas.height);

        headerCtx.drawImage(
          canvas,
          0,
          meta.headerTopPx,
          canvas.width,
          meta.headerHeightPx,
          0,
          0,
          canvas.width,
          meta.headerHeightPx,
        );
        meta.headerCanvas = headerCanvas;
      });

      const blockMetasPx = blockMetas
        .map((meta) => ({
          label: meta.label,
          includeInPagination: meta.includeInPagination,
          topPx: Math.round(meta.topDom * domToCanvasScaleY),
          bottomPx: Math.round(meta.bottomDom * domToCanvasScaleY),
          heightPx: Math.max(1, Math.round(meta.heightDom * domToCanvasScaleY)),
          stepId: meta.stepId ?? null,
          substepId: meta.substepId ?? null,
          hasImages: meta.hasImages ?? false,
          imageCount: meta.imageCount ?? 0,
          richTextShortDescTopPx:
            meta.richTextShortDescTopDom !== null &&
            meta.richTextShortDescTopDom !== undefined
              ? Math.round(meta.richTextShortDescTopDom * domToCanvasScaleY)
              : null,
          richTextShortDescBottomPx:
            meta.richTextShortDescBottomDom !== null &&
            meta.richTextShortDescBottomDom !== undefined
              ? Math.round(meta.richTextShortDescBottomDom * domToCanvasScaleY)
              : null,
          richTextContentTopPx:
            meta.richTextContentTopDom !== null &&
            meta.richTextContentTopDom !== undefined
              ? Math.round(meta.richTextContentTopDom * domToCanvasScaleY)
              : null,
          canvasShortDescTopPx:
            meta.canvasShortDescTopDom !== null &&
            meta.canvasShortDescTopDom !== undefined
              ? Math.round(meta.canvasShortDescTopDom * domToCanvasScaleY)
              : null,
          canvasShortDescBottomPx:
            meta.canvasShortDescBottomDom !== null &&
            meta.canvasShortDescBottomDom !== undefined
              ? Math.round(meta.canvasShortDescBottomDom * domToCanvasScaleY)
              : null,
          canvasContentTopPx:
            meta.canvasContentTopDom !== null &&
            meta.canvasContentTopDom !== undefined
              ? Math.round(meta.canvasContentTopDom * domToCanvasScaleY)
              : null,
          videoShortDescTopPx:
            meta.videoShortDescTopDom !== null &&
            meta.videoShortDescTopDom !== undefined
              ? Math.round(meta.videoShortDescTopDom * domToCanvasScaleY)
              : null,
          videoShortDescBottomPx:
            meta.videoShortDescBottomDom !== null &&
            meta.videoShortDescBottomDom !== undefined
              ? Math.round(meta.videoShortDescBottomDom * domToCanvasScaleY)
              : null,
          videoContentTopPx:
            meta.videoContentTopDom !== null &&
            meta.videoContentTopDom !== undefined
              ? Math.round(meta.videoContentTopDom * domToCanvasScaleY)
              : null,
          attachmentShortDescTopPx:
            meta.attachmentShortDescTopDom !== null &&
            meta.attachmentShortDescTopDom !== undefined
              ? Math.round(meta.attachmentShortDescTopDom * domToCanvasScaleY)
              : null,
          attachmentShortDescBottomPx:
            meta.attachmentShortDescBottomDom !== null &&
            meta.attachmentShortDescBottomDom !== undefined
              ? Math.round(
                  meta.attachmentShortDescBottomDom * domToCanvasScaleY,
                )
              : null,
          attachmentContentTopPx:
            meta.attachmentContentTopDom !== null &&
            meta.attachmentContentTopDom !== undefined
              ? Math.round(meta.attachmentContentTopDom * domToCanvasScaleY)
              : null,
        }))
        .filter((meta) => meta.heightPx > 0);

      const canvasImageMetasPx = canvasImageMetas
        .map((meta) => ({
          label: meta.label,
          includeInPagination: meta.includeInPagination,
          topPx: Math.round(meta.topDom * domToCanvasScaleY),
          bottomPx: Math.round(meta.bottomDom * domToCanvasScaleY),
          heightPx: Math.max(1, Math.round(meta.heightDom * domToCanvasScaleY)),
          widthPx: Math.max(1, Math.round(meta.widthDom * domToCanvasScaleX)),
          src: meta.src,
        }))
        .filter((meta) => meta.heightPx > 0);

      const attachmentImageMetasPx = attachmentImageMetas
        .map((meta) => ({
          topPx: Math.round(meta.topDom * domToCanvasScaleY),
          bottomPx: Math.round(meta.bottomDom * domToCanvasScaleY),
          heightPx: Math.max(1, Math.round(meta.heightDom * domToCanvasScaleY)),
        }))
        .filter((meta) => meta.heightPx > 0);

      if (canvasImageMetasPx.length > 0) {
        const canvasBlockDebug = blockMetasPx
          .filter((m) => m.label === "canvas")
          .map((m) => ({
            topPx: m.topPx,
            bottomPx: m.bottomPx,
            heightPx: m.heightPx,
            shortDescTopPx: m.canvasShortDescTopPx,
            shortDescBottomPx: m.canvasShortDescBottomPx,
            contentTopPx: m.canvasContentTopPx,
            includeInPagination: m.includeInPagination,
            stepId: m.stepId,
            substepId: m.substepId,
          }));
        const canvasImageDebug = canvasImageMetasPx.map((m) => ({
          topPx: m.topPx,
          bottomPx: m.bottomPx,
          heightPx: m.heightPx,
          widthPx: m.widthPx,
          includeInPagination: m.includeInPagination,
          srcPreview: (m.src || "").slice(0, 80),
        }));
      }

      document.body.removeChild(clone);

      container.style.transform = pdfExportInitialTransform;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4", true);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const horizontalMargin = 12;
      const headerHeight = 42;
      const footerHeight = 3;
      const contentTopPadding = 2;
      const contentBottomPadding = 6;
      const availableHeight =
        pdfHeight -
        headerHeight -
        footerHeight -
        contentTopPadding -
        contentBottomPadding;
      const imgWidth = pdfWidth - horizontalMargin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pixelToMm = imgWidth / canvas.width;
      // Vector autotable layout (mm). Tuned separately: revision vs training/BOM stacks.
      const PDF_AUTOTABLE_HEADING_TO_TABLE_MM = 4;
      const PDF_AUTOTABLE_REVISION_HEADING_TO_TABLE_MM = 2.5;
      // Keep heading->table gap consistent across ZIP tables.
      const PDF_AUTOTABLE_ZIP_HEADING_TO_TABLE_MM = 2.5;
      const effectiveHeadingToTableMm = isZipExport
        ? PDF_AUTOTABLE_ZIP_HEADING_TO_TABLE_MM
        : PDF_AUTOTABLE_HEADING_TO_TABLE_MM;
      const effectiveRevisionHeadingToTableMm = isZipExport
        ? PDF_AUTOTABLE_ZIP_HEADING_TO_TABLE_MM
        : PDF_AUTOTABLE_REVISION_HEADING_TO_TABLE_MM;
      const PDF_AUTOTABLE_REVISION_TOP_LIFT_MM = 5.5;
      const PDF_AUTOTABLE_GAP_AFTER_REVISION_MM = 10;
      const PDF_AUTOTABLE_GAP_AFTER_TRAINING_MM = 10;
      // Keep revision heading offset consistent across normal and ZIP exports.
      const PDF_AUTOTABLE_REVISION_HEADING_EXTRA_TOP_MM = 3;
      const availableHeightPx = Math.floor(availableHeight / pixelToMm);

      const identifyTableByHeading = (headingText, headingLevel = "h3") => {
        const headings = Array.from(clone.querySelectorAll(headingLevel));
        for (const heading of headings) {
          const text = (heading.textContent || "").trim().toLowerCase();
          if (headingText === "Training Log" && text === "training log") {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              if (
                nextSibling.tagName === "TABLE" ||
                nextSibling.querySelector("table")
              ) {
                const table =
                  nextSibling.tagName === "TABLE"
                    ? nextSibling
                    : nextSibling.querySelector("table");
                if (
                  table &&
                  (table.style.display === "none" ||
                    table.style.visibility === "hidden")
                ) {
                  nextSibling = nextSibling.nextElementSibling;
                  continue;
                }
                return table;
              }
              nextSibling = nextSibling.nextElementSibling;
            }
          } else if (
            headingText === "BOM" &&
            (text === "bom" || text.endsWith(" bom"))
          ) {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              const table =
                nextSibling.tagName === "TABLE"
                  ? nextSibling
                  : nextSibling.querySelector("table");
              if (
                table &&
                (table.style.display === "none" ||
                  table.style.visibility === "hidden")
              ) {
                nextSibling = nextSibling.nextElementSibling;
                continue;
              }
              if (table) return table;
              nextSibling = nextSibling.nextElementSibling;
            }
          } else if (
            headingText === "Revision Table" &&
            (text.includes("revision table") ||
              text.includes("revisions table"))
          ) {
            let nextSibling = heading.nextElementSibling;
            while (nextSibling) {
              if (
                nextSibling.tagName === "TABLE" ||
                nextSibling.querySelector("table")
              ) {
                const table =
                  nextSibling.tagName === "TABLE"
                    ? nextSibling
                    : nextSibling.querySelector("table");
                return table;
              }
              nextSibling = nextSibling.nextElementSibling;
            }
          }
        }
        return null;
      };

      const trainingLogTable = identifyTableByHeading("Training Log", "h3");
      const bomTable = identifyTableByHeading("BOM", "h3");

      if (!revisionTableElement) {
        const allHeadings = Array.from(
          clone.querySelectorAll("h1, h2, h3, h4, h5, h6"),
        );
        for (const heading of allHeadings) {
          const text = (heading.textContent || "").trim().toLowerCase();
          if (
            text.includes("revision table") ||
            text.includes("revisions table") ||
            text === "revision table" ||
            text === "revisions table"
          ) {
            if (!revisionHeadingElement) {
              revisionHeadingElement = heading;
            }
            let nextSibling = heading.nextElementSibling;
            while (nextSibling && nextSibling !== heading.parentElement) {
              if (
                nextSibling.tagName === "TABLE" ||
                nextSibling.querySelector("table")
              ) {
                const table =
                  nextSibling.tagName === "TABLE"
                    ? nextSibling
                    : nextSibling.querySelector("table");
                if (table && table.querySelector("thead")) {
                  revisionTableElement = table;
                  break;
                }
              }
              nextSibling = nextSibling.nextElementSibling;
            }
            if (!revisionTableElement && heading.parentElement) {
              const parentTables =
                heading.parentElement.querySelectorAll("table");
              for (const table of parentTables) {
                if (table.querySelector("thead")) {
                  if (
                    table.compareDocumentPosition(heading) &
                    Node.DOCUMENT_POSITION_PRECEDING
                  ) {
                    revisionTableElement = table;
                    break;
                  }
                }
              }
            }
            if (revisionTableElement) break;
          }
        }

        // Style the Revision table heading to match Training log styling
        if (revisionHeadingElement) {
          revisionHeadingElement.style.setProperty(
            "font-size",
            "16px",
            "important",
          );
          revisionHeadingElement.style.setProperty(
            "font-weight",
            "700",
            "important",
          );
        }

        if (!revisionTableElement) {
          const allTables = Array.from(clone.querySelectorAll("table"));
          for (const table of allTables) {
            if (!table.querySelector("thead")) continue;
            const prevElements = [];
            let prev = table.previousElementSibling;
            let count = 0;
            while (prev && count < 5) {
              prevElements.unshift(prev);
              prev = prev.previousElementSibling;
              count++;
            }
            const nearbyText = prevElements
              .map((el) => (el.textContent || "").trim().toLowerCase())
              .join(" ");
            if (
              nearbyText.includes("revision table") ||
              nearbyText.includes("revisions table")
            ) {
              revisionTableElement = table;
              break;
            }
          }
        }
      }

      const trainingLogTableId = trainingLogTable
        ? (tableMetas.find((meta) => meta.element === trainingLogTable)
            ?.tableId ?? null)
        : null;

      const bomTableId = bomTable
        ? (tableMetas.find((meta) => meta.element === bomTable)?.tableId ??
          null)
        : null;

      const revisionTableId = revisionTableElement
        ? (tableMetas.find((meta) => meta.element === revisionTableElement)
            ?.tableId ?? null)
        : null;
      const revisionTableMeta =
        revisionTableId != null
          ? tableMetas.find((meta) => meta.tableId === revisionTableId) || null
          : null;
      const trainingLogTableMeta =
        trainingLogTableId != null
          ? tableMetas.find((meta) => meta.tableId === trainingLogTableId) ||
            null
          : null;
      const bomTableMeta =
        bomTableId != null
          ? tableMetas.find((meta) => meta.tableId === bomTableId) || null
          : null;

      // Extract revision table data for autotable rendering
      let revisionTableData = null;
      if (revisionTableElement) {
        const thead = revisionTableElement.querySelector("thead");
        const tbody = revisionTableElement.querySelector("tbody");

        if (thead && tbody) {
          const headerRow = thead.querySelector("tr");
          if (headerRow) {
            const headers = Array.from(headerRow.querySelectorAll("th, td"))
              .map((th) => {
                // Get text content, excluding action buttons
                const text = th.textContent?.trim() || "";
                return text;
              })
              .filter((h, index, arr) => {
                // Filter out the last column if it's "Action" (typically the action button column)
                if (
                  index === arr.length - 1 &&
                  (h.toLowerCase() === "action" || h === "")
                ) {
                  return false;
                }
                return true;
              });

            const rows = Array.from(tbody.querySelectorAll("tr")).map((tr) => {
              const cells = Array.from(tr.querySelectorAll("td"));
              // Filter out action column (last column typically)
              const filteredCells = cells.filter((cell, index, arr) => {
                // Check if this is the action column (usually has buttons or is empty)
                const hasButton = cell.querySelector("button");
                const isEmpty = !cell.textContent?.trim();
                const isLast = index === arr.length - 1;
                // Exclude if it's the last column and has button or is empty
                if (isLast && (hasButton || isEmpty)) {
                  return false;
                }
                return true;
              });

              return filteredCells.map((td) => {
                // Get text content, handling nested elements
                const text = td.textContent?.trim() || "-";
                return text;
              });
            });

            if (headers.length > 0 && rows.length > 0) {
              revisionTableData = {
                headers: headers,
                rows: rows,
                element: revisionTableElement,
              };
              // Keep table visible for pagination calculations, we'll mask it later
            }
          }
        }
      }

      // Extract training log table data for autotable rendering
      let trainingLogTableData = null;
      if (trainingLogTable) {
        const thead = trainingLogTable.querySelector("thead");
        const tbody = trainingLogTable.querySelector("tbody");

        if (thead && tbody) {
          const headerRow = thead.querySelector("tr");
          if (headerRow) {
            const headers = Array.from(headerRow.querySelectorAll("th, td"))
              .map((th) => {
                // Get text content, excluding action buttons
                const text = th.textContent?.trim() || "";
                return text;
              })
              .filter((h, index, arr) => {
                // Filter out the last column if it's "Action" (typically the action button column)
                if (
                  index === arr.length - 1 &&
                  (h.toLowerCase() === "action" || h === "")
                ) {
                  return false;
                }
                return true;
              });

            const trainingLogPdfEmptyMessage =
              "No training records for this procedure.";
            const isBlankTrainingLogCellText = (s) => {
              const t = String(s ?? "")
                .replace(/\u00a0/g, " ")
                .trim();
              return !t || t === "-" || t === "—";
            };

            const rows = Array.from(tbody.querySelectorAll("tr")).map((tr) => {
              const cells = Array.from(tr.querySelectorAll("td"));
              // Filter out action column (last column typically)
              const filteredCells = cells.filter((cell, index, arr) => {
                // Check if this is the action column (usually has buttons or is empty)
                const hasButton = cell.querySelector("button");
                const isEmpty = !cell.textContent?.trim();
                const isLast = index === arr.length - 1;
                // Exclude if it's the last column and has button or is empty
                if (isLast && (hasButton || isEmpty)) {
                  return false;
                }
                return true;
              });

              const firstCell = filteredCells[0];
              const firstCellColSpan = Number(firstCell?.colSpan || 0);
              const isFullWidthMessageRow =
                filteredCells.length === 1 &&
                firstCellColSpan > 1 &&
                firstCellColSpan >= headers.length;
              if (isFullWidthMessageRow) {
                return [
                  {
                    content:
                      firstCell?.textContent?.trim() ||
                      trainingLogPdfEmptyMessage,
                    colSpan: headers.length,
                    styles: {
                      halign: "center",
                      textColor: [108, 117, 125],
                    },
                  },
                ];
              }

              const filteredCellTexts = filteredCells.map(
                (td) => (td.textContent || "").trim(),
              );
              const firstText = (filteredCellTexts[0] || "").toLowerCase();
              const restAreBlank = filteredCellTexts
                .slice(1)
                .every((txt) => isBlankTrainingLogCellText(txt));
              const isPseudoFullWidthEmptyStateRow =
                filteredCellTexts.length > 1 &&
                firstText.includes("no training records") &&
                restAreBlank;
              if (isPseudoFullWidthEmptyStateRow) {
                return [
                  {
                    content:
                      filteredCellTexts[0] || trainingLogPdfEmptyMessage,
                    colSpan: headers.length,
                    styles: {
                      halign: "center",
                      textColor: [108, 117, 125],
                    },
                  },
                ];
              }

              return filteredCells.map((td) => {
                // Get text content, handling nested elements
                const text = td.textContent?.trim() || "-";
                return text;
              });
            });

            // Empty tbody: still emit autotable (head only, no body rows) + masks so the
            // canvas thead is not the only paint (avoids page slices / BOM bleed).
            const bodyRows = rows.length > 0 ? rows : [];

            if (headers.length > 0) {
              trainingLogTableData = {
                headers: headers,
                rows: bodyRows,
                element: trainingLogTable,
              };
              // Keep table visible for pagination calculations, we'll mask it later
            }
          }
        }
      }

      // PDF vector table reads text nodes only; signature columns are <img> with no text, so DOM
      // scraping yields "-" / empty rows. Prefer API rows (same fields as TrainingLogTable) and
      // draw signature images in autoTable didDrawCell.
      const standardTrainingLogPdfHeaders = [
        "Date of Training",
        "Trainer",
        "Trainer Signature",
        "Trainee",
        "Trainee Signature",
        "Revision Trained",
        "Training Status of Trainee",
      ];
      const formatTrainingLogPdfDate = (value) => {
        if (value == null || value === "") return "—";
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
      };
      const formatTrainingLogPdfTrainer = (row) => {
        if (row.trainer_name && String(row.trainer_name).trim()) {
          return row.trainer_name.trim();
        }
        const t = row.trainer;
        if (t && typeof t === "object") {
          const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
          return name || "N/A";
        }
        return "N/A";
      };
      const formatTrainingLogPdfTrainee = (row) => {
        if (row.trainee_name && String(row.trainee_name).trim()) {
          return row.trainee_name.trim();
        }
        const t = row.trainee;
        if (t && typeof t === "object") {
          const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
          return name || "N/A";
        }
        return "N/A";
      };
      const formatTrainingLogPdfRevision = (row) => {
        if (row.rev != null && String(row.rev).trim() !== "") {
          return String(row.rev).trim();
        }
        if (row.version_id_ref != null && String(row.version_id_ref).trim() !== "") {
          return String(row.version_id_ref);
        }
        return "N/A";
      };
      // Match TrainingLogTable "Training Status of Trainee" (Trained, Partially Trained, etc.)
      const formatTrainingLogPdfStatus = (row) => {
        if (!row || typeof row !== "object") {
          return "—";
        }
        const directKeys = [
          "training_status_of_trainee",
          "training_status",
          "viewer_training_status",
          "overall_training_status",
          "status",
          "trainingStatus",
        ];
        for (const k of directKeys) {
          const v = row[k];
          if (typeof v === "string" && v.trim()) {
            return v.trim();
          }
        }
        const fromLog = resolveTrainingStatusLabelFromLog(row);
        if (typeof fromLog === "string" && fromLog.trim()) {
          return fromLog.trim();
        }
        if (row.is_maintained_outside_system) {
          return "Maintained Outside System";
        }
        return "—";
      };
      const trainingLogSigTextForPdf = (sig) => {
        const cell = getTrainingLogSignatureCell(sig);
        // Raster signatures are drawn in didDrawCell; keep cell text empty so "Signed" never shows beside the image.
        if (cell.type === "img") {
          return "";
        }
        if (cell.type === "text" && cell.text) {
          return cell.text;
        }
        return "No Signature";
      };
      const buildTrainingLogPdfRowsFromApi = (logs) =>
        logs.map((row) => [
          formatTrainingLogPdfDate(row.date_of_training),
          formatTrainingLogPdfTrainer(row),
          trainingLogSigTextForPdf(row.trainer_signature),
          formatTrainingLogPdfTrainee(row),
          trainingLogSigTextForPdf(row.trainee_signature),
          formatTrainingLogPdfRevision(row),
          formatTrainingLogPdfStatus(row),
        ]);

      if (
        Array.isArray(trainingLogsData) &&
        trainingLogsData.length > 0 &&
        trainingLogTableData
      ) {
        trainingLogTableData = {
          ...trainingLogTableData,
          headers: standardTrainingLogPdfHeaders,
          rows: buildTrainingLogPdfRowsFromApi(trainingLogsData),
        };
      }

      // Extract BOM table data for autotable rendering
      const getCellTextForPdfTable = (cell) => {
        try {
          // Some cells can contain embedded <style> (rich text exports). Avoid rendering CSS as text.
          const cloned = cell.cloneNode(true);
          if (cloned.querySelectorAll) {
            cloned
              .querySelectorAll("style,script,noscript")
              .forEach((el) => el.remove());
          }
          const text = cloned.textContent || cloned.innerText || "";
          return (text || "").trim() || "-";
        } catch (e) {
          const fallback = cell?.textContent || cell?.innerText || "";
          return (fallback || "").trim() || "-";
        }
      };

      let bomTableAutotableData = null;
      if (bomTable) {
        const thead = bomTable.querySelector("thead");
        const tbody = bomTable.querySelector("tbody");

        if (thead && tbody) {
          const headerRow = thead.querySelector("tr");
          if (headerRow) {
            const headers = Array.from(headerRow.querySelectorAll("th, td"))
              .map((th) => {
                // Get text content, excluding action buttons
                const text = th.textContent?.trim() || "";
                return text;
              })
              .filter((h, index, arr) => {
                // Filter out the last column if it's "Action" (typically the action button column)
                if (
                  index === arr.length - 1 &&
                  (h.toLowerCase() === "action" || h === "")
                ) {
                  return false;
                }
                return true;
              });

            const rows = Array.from(tbody.querySelectorAll("tr")).map((tr) => {
              const cells = Array.from(tr.querySelectorAll("td"));
              // Filter out action column (last column typically)
              const filteredCells = cells.filter((cell, index, arr) => {
                // Check if this is the action column (usually has buttons or is empty)
                const hasButton = cell.querySelector("button");
                const isEmpty = !cell.textContent?.trim();
                const isLast = index === arr.length - 1;
                // Exclude if it's the last column and has button or is empty
                if (isLast && (hasButton || isEmpty)) {
                  return false;
                }
                return true;
              });

              return filteredCells.map((td) => {
                return getCellTextForPdfTable(td);
              });
            });

            const isEmptyBom =
              rows.length === 0 ||
              (rows.length === 1 &&
                String(rows[0][0] || "").trim() === "No parts to display." &&
                rows[0].slice(1).every((c) => !String(c || "").trim()));

            if (headers.length > 0) {
              bomTableAutotableData = {
                headers: headers,
                // PDF: empty BOM shows header row only (no "No parts to display." row)
                rows: isEmptyBom ? [] : rows,
                element: bomTable,
              };
              // Keep table visible for pagination calculations, we'll mask it later
            }
          }
        }
      }

      // Extract Item Required tables data for autotable rendering
      const itemRequiredTablesData = [];
      tableMetas.forEach((meta) => {
        if (meta.isItemRequiredTable && meta.element) {
          const table = meta.element;
          const thead = table.querySelector("thead");
          const tbody = table.querySelector("tbody");

          if (thead && tbody) {
            const headerRow = thead.querySelector("tr");
            if (headerRow) {
              const headers = Array.from(headerRow.querySelectorAll("th, td"))
                .map((th) => {
                  // Get text content, excluding action buttons
                  const text = th.textContent?.trim() || "";
                  return text;
                })
                .filter((h, index, arr) => {
                  // Filter out the last column if it's "Action" (typically the action button column)
                  if (
                    index === arr.length - 1 &&
                    (h.toLowerCase() === "action" || h === "")
                  ) {
                    return false;
                  }
                  return true;
                });

              const rows = Array.from(tbody.querySelectorAll("tr")).map(
                (tr) => {
                  const cells = Array.from(tr.querySelectorAll("td"));
                  // Filter out action column (last column typically)
                  const filteredCells = cells.filter((cell, index, arr) => {
                    // Check if this is the action column (usually has buttons or is empty)
                    const hasButton = cell.querySelector("button");
                    const isEmpty = !cell.textContent?.trim();
                    const isLast = index === arr.length - 1;
                    // Exclude if it's the last column and has button or is empty
                    if (isLast && (hasButton || isEmpty)) {
                      return false;
                    }
                    return true;
                  });

                  return filteredCells.map((td) => {
                    return getCellTextForPdfTable(td);
                  });
                },
              );

              if (headers.length > 0 && rows.length > 0) {
                // Get the heading text for the table name
                let tableName = "Item Required";
                if (meta.headingElement) {
                  const headingText = (
                    meta.headingElement.textContent || ""
                  ).trim();
                  tableName =
                    headingText.replace(/\s*\(\d+\/\d+\)\s*$/, "").trim() ||
                    "Item Required";
                }

                // Detect empty state: single row with "No parts to display." (from PartsTable)
                // In PDF we render this as a single cell spanning all columns, centered, no column dividers
                const isEmptyState =
                  rows.length === 1 &&
                  String(rows[0][0] || "").trim() === "No parts to display." &&
                  rows[0].slice(1).every((c) => !String(c || "").trim());

                itemRequiredTablesData.push({
                  tableId: meta.tableId,
                  headers: headers,
                  rows: rows,
                  element: table,
                  meta: meta,
                  tableName: tableName,
                  isEmptyState: isEmptyState,
                });
              }
            }
          }
        }
      });

      const getTableName = (tableId, tableElement) => {
        // Check table types
        if (tableId === revisionTableId) return "Revision Table";
        if (tableId === trainingLogTableId) return "Training Log";
        if (tableId === bomTableId) {
          if (tableElement && bomTable) {
            let element = tableElement.previousElementSibling;
            while (element) {
              if (
                element.tagName === "H3" &&
                (() => {
                  const t = (element.textContent || "").trim();
                  return t === "BOM" || t.endsWith(" BOM");
                })()
              ) {
                return (element.textContent || "").trim();
              }
              element = element.previousElementSibling;
            }
            const bomHeading = Array.from(clone.querySelectorAll("h3")).find(
              (h) => {
                const t = (h.textContent || "").trim();
                return t === "BOM" || t.endsWith(" BOM");
              },
            );
            return bomHeading ? (bomHeading.textContent || "").trim() : "BOM";
          }
          return "BOM";
        }
        // Check if it's an Item Required table
        const itemRequiredTableData = itemRequiredTablesData.find(
          (t) => t.tableId === tableId,
        );
        if (itemRequiredTableData) {
          return itemRequiredTableData.tableName;
        }
        return null;
      };

      const tableIdToName = new Map();
      [revisionTableId, trainingLogTableId, bomTableId].forEach((tableId) => {
        if (tableId !== null) {
          const meta = tableMetas.find((m) => m.tableId === tableId);
          if (meta) {
            const name = getTableName(tableId, meta.element);
            if (name) {
              tableIdToName.set(tableId, name);
            }
          } else {
            if (tableId === revisionTableId && revisionTableElement) {
              tableIdToName.set(tableId, "Revision Table");
            } else if (tableId === trainingLogTableId) {
              tableIdToName.set(tableId, "Training Log");
            } else if (tableId === bomTableId) {
              tableIdToName.set(tableId, "BOM");
            }
          }
        }
      });

      // Add Item Required tables to tableIdToName map
      itemRequiredTablesData.forEach((tableData) => {
        tableIdToName.set(tableData.tableId, tableData.tableName);
      });

      const pageImages = [];
      let currentOffsetPx = 0;
      const totalHeightPx = canvas.height;
      // One shared margin for section title + HR band: snap, slice checks, and “move to next page” logic.
      const sectionHeadingBandGuardPx = Math.max(
        44,
        Math.round(60 * domToCanvasScaleY),
      );
      // "Training log" h3 + thead strip — avoid html2canvas slicing through the black header row.
      const trainingLogHeaderBandGuardPx = Math.max(
        24,
        Math.round(52 * domToCanvasScaleY),
      );
      // BOM h3 + thead — same slice issue as Training log when the page break lands mid-band.
      const bomHeaderBandGuardPx = trainingLogHeaderBandGuardPx;

      const multiPageTables = new Map();
      const multiPageSteps = new Map();
      const multiPageSubsteps = new Map();

      // When the first Section after the BOM starts on the *next* PDF page, the default slice
      // height would include the entire blank DOM gap below the BOM on page 1. End the slice at
      // the BOM bottom (+ small pad) so page 1 is not mostly empty white space.
      const padAfterBomTailPx = Math.max(4, Math.round(8 * domToCanvasScaleY));
      // BOM end Y in canvas px: prefer live layout from the export clone (table + wrapper). If we
      // only rely on tableMetas, meta lookup can miss or disagree with html2canvas and trimming never runs.
      const bomEndCanvasPx = (() => {
        if (bomTable) {
          try {
            const tableB = Math.round(
              (bomTable.getBoundingClientRect().bottom - cloneRect.top) *
                domToCanvasScaleY,
            );
            const wrap = bomTable.closest("[data-export-block='bom']");
            const wrapB = wrap
              ? Math.round(
                  (wrap.getBoundingClientRect().bottom - cloneRect.top) *
                    domToCanvasScaleY,
                )
              : tableB;
            const liveBottom = Math.max(tableB, wrapB);
            // html2canvas clone/layout can occasionally report 0 for BOM bounds.
            // Treat tiny/zero values as invalid and fall back to table meta below.
            if (Number.isFinite(liveBottom) && liveBottom > 8) {
              return liveBottom;
            }
          } catch (_) {
            /* fall through */
          }
        }
        if (bomTableMeta && typeof bomTableMeta.bottomPx === "number") {
          if (Number.isFinite(bomTableMeta.bottomPx) && bomTableMeta.bottomPx > 8) {
            return bomTableMeta.bottomPx;
          }
        }
        return null;
      })();
      // Earliest Section block/heading after the BOM (canvas px). Used to trim page-1 tail whitespace
      // and to avoid MIN_PAGE inflating an intentional BOM-only short slice.
      const firstSectionTopAfterBomPx =
        bomEndCanvasPx != null
          ? (() => {
              const bot = bomEndCanvasPx;
              let best = null;
              const consider = (topPx) => {
                if (typeof topPx !== "number" || !Number.isFinite(topPx)) return;
                if (topPx <= bot + 2) return;
                if (best === null || topPx < best) best = topPx;
              };
              try {
                clone
                  .querySelectorAll("[data-export-block='section']")
                  .forEach((sec) => {
                    const r = sec.getBoundingClientRect();
                    consider(
                      Math.round((r.top - cloneRect.top) * domToCanvasScaleY),
                    );
                  });
              } catch (_) {}
              try {
                clone.querySelectorAll("h2").forEach((h2) => {
                  const t = (h2.textContent || "").trim();
                  if (!/^section\b/i.test(t)) return;
                  const r = h2.getBoundingClientRect();
                  consider(
                    Math.round((r.top - cloneRect.top) * domToCanvasScaleY),
                  );
                });
              } catch (_) {}
              if (sectionHeadingOnlyPx?.length) {
                sectionHeadingOnlyPx.forEach((s) => consider(s.headingTopPx));
              }
              if (blockMetasPx?.length) {
                blockMetasPx.forEach((m) => {
                  if (m.label !== "section") return;
                  if (m.includeInPagination === false) return;
                  consider(m.topPx);
                });
              }
              return best;
            })()
          : null;
      // Only treat page-1 as "BOM-only short page" when there is a meaningful visual gap
      // between BOM end and the first section. If gap is tiny, keep Section 1 on page 1.
      const bomToFirstSectionGapPx =
        firstSectionTopAfterBomPx != null && bomEndCanvasPx != null
          ? firstSectionTopAfterBomPx - bomEndCanvasPx
          : null;
      const minBomToSectionTrimGapPx = Math.max(
        24,
        Math.round(40 * domToCanvasScaleY),
      );
      const keepFirstSectionOnPage1 =
        // Normal case: valid BOM anchor and small BOM->Section gap.
        (firstSectionTopAfterBomPx != null &&
          bomToFirstSectionGapPx != null &&
          bomToFirstSectionGapPx < minBomToSectionTrimGapPx) ||
        // Fallback: BOM anchor missing/invalid (seen as 0 in logs) — if Section 1
        // is already within page-1 usable slice, keep it on page 1.
        (firstSectionTopAfterBomPx != null &&
          (bomEndCanvasPx == null || bomEndCanvasPx <= 8) &&
          firstSectionTopAfterBomPx <= Math.floor(availableHeightPx * 0.9));
      // Section headings should flow naturally; do not push them to next page.
      const disableSectionHeadingPush = true;
      const firstStepHeaderTopPx =
        Array.isArray(stepMetas) && stepMetas.length > 0
          ? stepMetas.reduce((minTop, s) => {
              const top = Number(s?.headerTopPx);
              if (!Number.isFinite(top)) return minTop;
              return minTop == null ? top : Math.min(minTop, top);
            }, null)
          : null;
      while (currentOffsetPx < totalHeightPx - 1) {
        // Canvas pagination is a hard horizontal slice. If the slice line falls through the
        // substep title row (h2 + breadcrumb + hr), text is bisected. Snap the next page to
        // start after the full band when the previous slice ended mid-band (rounding / order).
        const substepBandGuardPx = Math.max(
          12,
          Math.round(20 * domToCanvasScaleY),
        );
        for (const sm of substepMetas) {
          if (!sm.headerCanvas || sm.headerHeightPx <= 0) continue;
          const bandTop = sm.headerTopPx - substepBandGuardPx;
          const bandBottom =
            sm.headerTopPx + sm.headerHeightPx + substepBandGuardPx;
          if (currentOffsetPx > bandTop && currentOffsetPx < bandBottom) {
            currentOffsetPx = bandBottom;
          }
        }
        if (sectionHeadingOnlyPx && sectionHeadingOnlyPx.length > 0) {
          for (const sh of sectionHeadingOnlyPx) {
            const bandTop = sh.headingTopPx - sectionHeadingBandGuardPx;
            const bandBottom = sh.headingBottomPx + sectionHeadingBandGuardPx;
            if (currentOffsetPx > bandTop && currentOffsetPx < bandBottom) {
              // Never move page start backward; backward movement causes duplicated content
              // across consecutive pages. Keep offsets monotonic.
              const safeHeadingTop = Math.max(0, Math.floor(sh.headingTopPx));
              if (safeHeadingTop > currentOffsetPx) {
                currentOffsetPx = safeHeadingTop;
              }
            }
          }
        }
        for (const tm of tableMetas) {
          if (
            !tm.isTrainingLogTable ||
            !tm.headerHeightPx ||
            tm.headerHeightPx <= 0
          )
            continue;
          const bandTop = tm.headerTopPx - trainingLogHeaderBandGuardPx;
          const bandBottom =
            tm.headerTopPx + tm.headerHeightPx + trainingLogHeaderBandGuardPx;
          if (currentOffsetPx > bandTop && currentOffsetPx < bandBottom) {
            currentOffsetPx = bandBottom;
          }
        }
        for (const tm of tableMetas) {
          if (!tm.isBomTable || !tm.headerHeightPx || tm.headerHeightPx <= 0)
            continue;
          const bandTop = tm.headerTopPx - bomHeaderBandGuardPx;
          const bandBottom =
            tm.headerTopPx + tm.headerHeightPx + bomHeaderBandGuardPx;
          if (currentOffsetPx > bandTop && currentOffsetPx < bandBottom) {
            currentOffsetPx = bandBottom;
          }
        }
        if (currentOffsetPx >= totalHeightPx - 1) break;

        const pageStartPx = currentOffsetPx;
        const continuingTables = tableMetas
          .filter((meta) => {
            // Basic check: table starts before current page and extends into it
            if (
              meta.topPx >= currentOffsetPx ||
              meta.bottomPx <= currentOffsetPx + 1
            ) {
              return false;
            }

            // CRITICAL FIX: For Item Required tables, check if table BODY actually extends into current page
            // This removes dependency on Canvas elements and prevents false positives when there's a gap
            // after the table on the previous page
            if (meta.isItemRequiredTable) {
              // Calculate table body start (after header: thead)
              const theadHeightPx = Math.round(
                meta.theadHeightDom * domToCanvasScaleY,
              );
              const tableBodyStartPx = meta.headerTopPx + theadHeightPx;

              // For Item Required tables, check if table body (tbody rows) actually extends into current page
              // The table body must start before the current page AND end after the current page start
              const tableBodyStartsBeforePage =
                tableBodyStartPx < currentOffsetPx;
              const tableBodyEndsAfterPageStart =
                meta.bottomPx > currentOffsetPx;

              // Both conditions must be true for table to be continuing
              if (!tableBodyStartsBeforePage || !tableBodyEndsAfterPageStart) {
                return false;
              }

              // Additionally, ensure meaningful overlap - at least 50px of table body content
              // This prevents false positives when there's just a tiny sliver or gap
              const bodyOverlap =
                Math.min(meta.bottomPx, currentOffsetPx + 1) -
                Math.max(tableBodyStartPx, currentOffsetPx);
              const minBodyOverlapPx = 50;

              // Only consider it continuing if there's meaningful table body content on this page
              return bodyOverlap >= minBodyOverlapPx;
            }

            const tableNameForCont = tableIdToName.get(meta.tableId);
            const isBOMContinuation =
              tableNameForCont &&
              tableNameForCont.toLowerCase().includes("bom");
            if (isBOMContinuation) {
              const theadHeightPx = Math.round(
                meta.theadHeightDom * domToCanvasScaleY,
              );
              const tableBodyStartPx = meta.headerTopPx + theadHeightPx;
              const tableBodyStartsBeforePage =
                tableBodyStartPx < currentOffsetPx;
              const tableBodyEndsAfterPageStart =
                meta.bottomPx > currentOffsetPx;
              if (!tableBodyStartsBeforePage || !tableBodyEndsAfterPageStart) {
                return false;
              }
              const bodyOverlap =
                Math.min(meta.bottomPx, currentOffsetPx + 1) -
                Math.max(tableBodyStartPx, currentOffsetPx);
              const minBodyOverlapPx = 50;
              return bodyOverlap >= minBodyOverlapPx;
            }

            // Training Log: same as BOM — header strip is h3 + thead; without tbody overlap, do not
            // reserve headerInsertPx or the page shows only the repeated header row and blank space.
            if (meta.isTrainingLogTable) {
              const tableBodyStartPx = meta.headerTopPx + meta.headerHeightPx;
              const tableBodyStartsBeforePage =
                tableBodyStartPx < currentOffsetPx;
              const tableBodyEndsAfterPageStart =
                meta.bottomPx > currentOffsetPx;
              if (!tableBodyStartsBeforePage || !tableBodyEndsAfterPageStart) {
                return false;
              }
              const bodyOverlap =
                Math.min(meta.bottomPx, currentOffsetPx + 1) -
                Math.max(tableBodyStartPx, currentOffsetPx);
              const minBodyOverlapPx = 50;
              return bodyOverlap >= minBodyOverlapPx;
            }

            // For non-Item Required tables, use original logic
            return true;
          })
          .sort((a, b) => a.topPx - b.topPx);

        let headerInsertPx = 0;
        continuingTables.forEach((meta) => {
          const headerHeight = meta.headerCanvas?.height || 0;
          if (headerHeight <= 0) return;
          const existingSlot = pageImages.length
            ? pageImages[pageImages.length - 1].headerSlots?.find(
                (slot) => slot.source === meta,
              )
            : null;
          if (!existingSlot) {
            headerInsertPx += headerHeight;
          }
        });

        if (headerInsertPx > availableHeightPx * 0.7) {
          headerInsertPx = Math.max(0, Math.floor(availableHeightPx * 0.35));
        }

        // Add minimum top margin to prevent Section/Step/Substep headers from being clipped
        // when they appear just below the page header area
        // This ensures headers have enough space and aren't hidden by the PDF page header
        const minTopMarginPx = Math.max(0, Math.floor(2 / pixelToMm)); // 8mm minimum margin for safety
        if (headerInsertPx < minTopMarginPx) {
          headerInsertPx = minTopMarginPx;
        }

        // CRITICAL: Add extra spacing for Item Required tables that start near the top of the page
        // when a step header will be drawn above them to prevent overlap
        const pageStartPxForCheck = currentOffsetPx;
        const pageEndPxForCheck = currentOffsetPx + availableHeightPx;

        // Find Item Required and Training Log tables that start near the top of this page (within first 200px)
        const itemRequiredTablesNearTop = tableMetas.filter((meta) => {
          if (!meta.isItemRequiredTable && !meta.isTrainingLogTable)
            return false;
          if (!meta.headingElement || meta.headingTopDom === null) return false;

          const headingTopPx = Math.round(
            meta.headingTopDom * domToCanvasScaleY,
          );
          const tableOnPage =
            headingTopPx >= pageStartPxForCheck &&
            headingTopPx < pageEndPxForCheck;
          const headingNearTop = headingTopPx - pageStartPxForCheck < 200;

          return tableOnPage && headingNearTop;
        });

        // Check if any of these tables have step headers that will be drawn as an overlay at the top.
        // Extra spacing is only needed when the step header is NOT on the current page (i.e. it lived
        // on a previous page and must be re-drawn as an overlay so the table heading remains readable).
        // When the step header IS on the current page it flows as normal content and no additional
        // headerInsertPx space is required — adding it would shrink pageContentPx and cause the
        // step to be incorrectly pushed to the next page even when there is enough room.
        const needsExtraSpacing = itemRequiredTablesNearTop.some((meta) => {
          if (!meta.stepId) return false;

          // Find the corresponding step
          const stepMeta = stepMetas.find((s) => s.stepId === meta.stepId);
          if (!stepMeta) return false;

          // Only trigger when the step header will be drawn as an overlay, i.e. when it is NOT
          // visible on the current page (it was on a prior page).
          const stepHeaderOnPage =
            stepMeta.headerTopPx >= pageStartPxForCheck &&
            stepMeta.headerTopPx < pageEndPxForCheck;
          return !stepHeaderOnPage;
        });

        // Add 100px extra spacing if table is near top with step header
        if (needsExtraSpacing && itemRequiredTablesNearTop.length > 0) {
          headerInsertPx += 100;
        }

        const footerHeightPx = Math.floor(footerHeight / pixelToMm);
        const tableBottomMarginPx = 0;
        let pageContentPx = Math.min(
          Math.max(0, availableHeightPx - headerInsertPx - tableBottomMarginPx),
          totalHeightPx - currentOffsetPx,
        );

        if (pageContentPx <= 0) {
          pageContentPx = Math.min(
            totalHeightPx - currentOffsetPx,
            availableHeightPx - tableBottomMarginPx,
          );
          headerInsertPx = 0;
        }

        if (
          firstSectionTopAfterBomPx != null &&
          bomEndCanvasPx != null &&
          bomToFirstSectionGapPx != null &&
          bomToFirstSectionGapPx >= minBomToSectionTrimGapPx &&
          bomEndCanvasPx > currentOffsetPx &&
          bomEndCanvasPx < currentOffsetPx + pageContentPx &&
          firstSectionTopAfterBomPx >=
            currentOffsetPx + pageContentPx - 0.5
        ) {
          const snap = Math.floor(
            bomEndCanvasPx - currentOffsetPx + padAfterBomTailPx,
          );
          if (snap > 0 && snap < pageContentPx) {
            pageContentPx = snap;
          }
        }

        // CRITICAL: Run step/substep/section header protection FIRST to prevent headers from being cut
        const runEarlyHeaderProtection = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          // Large values force page breaks too early (whitespace, substeps on new pages).
          const headerBreakSafetyPx = isZipExport
            ? Math.max(100, Math.round(120 * domToCanvasScaleY))
            : Math.max(40, Math.round(48 * domToCanvasScaleY));
          const headerExtraPx = Math.round(15 * domToCanvasScaleY); // Account for rounding/measurement
          for (const meta of [...stepMetas, ...substepMetas]) {
            let protectBottomPx =
              meta.headerTopPx + (meta.headerHeightPx || 0) + headerExtraPx;
            if (
              meta.substepId != null &&
              meta.substepShortDescBottomPx != null
            ) {
              protectBottomPx = Math.max(
                protectBottomPx,
                meta.substepShortDescBottomPx + headerExtraPx,
              );
            }
            if (meta.stepShortDescBottomPx != null) {
              protectBottomPx = Math.max(
                protectBottomPx,
                meta.stepShortDescBottomPx + headerExtraPx,
              );
            }
            const overlaps =
              meta.headerTopPx < pageEnd && protectBottomPx > pageStart;
            if (overlaps && protectBottomPx > pageEnd - headerBreakSafetyPx) {
              // Never shrink page content for step/substep bands. Wrapper rects include
              // nested content; shrinking here caused large whitespace and pushed blocks
              // forward (normal PDF previously shrank here while ZIP skipped — see isZipExport
              // history). Headers are handled in the raster overlay pass.
              continue;
            }
          }
          // Section headers: keep full band (title + HRs) off the slice line; move to next page if it won't fit.
          // Use a small tail margin only — pageContentPx already excludes the PDF footer; a 100–150px
          // extra "safe" zone was forcing Section 2 onto the next page with lots of blank space above.
          const sectionBandTailSafetyPx = Math.max(
            8,
            Math.round(14 * domToCanvasScaleY),
          );
          if (disableSectionHeadingPush) return false;
          if (sectionHeadingOnlyPx && sectionHeadingOnlyPx.length > 0) {
            for (const sh of sectionHeadingOnlyPx) {
              if (
                pageStart === 0 &&
                keepFirstSectionOnPage1 &&
                Math.abs(sh.headingTopPx - firstSectionTopAfterBomPx) <= 3
              ) {
                continue;
              }
              const bandTop = sh.headingTopPx - sectionHeadingBandGuardPx;
              const bandBottom = sh.headingBottomPx + sectionHeadingBandGuardPx;
              const contentMaxY = pageEnd - sectionBandTailSafetyPx;
              if (bandBottom <= pageStart || bandTop >= pageEnd) continue;
              const bandFitsOnPage =
                bandTop >= pageStart && bandBottom <= contentMaxY;
              if (bandFitsOnPage) continue;
              const sliceCutsBand = pageEnd > bandTop && pageEnd < bandBottom;
              const bandStartsOnPage =
                bandTop >= pageStart && bandTop < pageEnd;
              const bandDoesNotFit =
                bandStartsOnPage && bandBottom > contentMaxY;
              if (sliceCutsBand || bandDoesNotFit) {
                let proposed = Math.max(0, Math.floor(bandTop - pageStart));
                if (
                  firstSectionTopAfterBomPx != null &&
                  bomEndCanvasPx != null &&
                  Math.abs(sh.headingTopPx - firstSectionTopAfterBomPx) < 3 &&
                  bomEndCanvasPx > pageStart &&
                  bomEndCanvasPx < sh.headingTopPx
                ) {
                  const snapAfterBom = Math.floor(
                    bomEndCanvasPx - pageStart + padAfterBomTailPx,
                  );
                  if (snapAfterBom > 0) {
                    proposed = Math.min(proposed, snapAfterBom);
                  }
                }
                if (proposed > 0 && proposed < pageContentPx) {
                  pageContentPx = proposed;
                  return true;
                }
              }
            }
          }
          return false;
        };
        while (runEarlyHeaderProtection()) {}

        const adjustForBlocks = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          // Section headers: move-to-next-page disabled - sections may split across pages.

          const childNodeTypes = new Set([
            "canvas",
            "rich-text",
            "video",
            "form",
          ]);

          // Skip large structural wrappers. Step/substep `[data-export-block]` rects span
          // nested tables and media; treating them as atomic blocking blocks forces whole
          // steps onto the next page and leaves huge whitespace (substep “orphaned” on a
          // later page even when space remains). Section is skipped for the same reason.
          // Pagination uses earlyHeaderProtection + canvas/rich-text/… child blocks instead.
          const nonSplittableStructuralWrappers = new Set([
            "section",
            "step",
            "substep",
          ]);

          const blockingBlock = blockMetasPx.find((meta) => {
            if (!meta.includeInPagination) return false;

            // Skip child node types - allow them to split across pages
            if (childNodeTypes.has(meta.label)) {
              return false;
            }

            if (nonSplittableStructuralWrappers.has(meta.label)) {
              return false;
            }

            const blockStartsOnThisPage =
              meta.topPx >= pageStart && meta.topPx < pageEnd;
            const blockExtendsBeyondPage = meta.bottomPx > pageEnd;

            if (!blockStartsOnThisPage || !blockExtendsBeyondPage) {
              return false;
            }

            if (meta.heightPx >= availableHeightPx * 0.95) return false;
            return true;
          });

          if (!blockingBlock) return false;
          const proposed = blockingBlock.topPx - currentOffsetPx;
          // Only adjust when it *reduces* the current page size; otherwise this can loop forever.
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = proposed;

            const nodeTypeNames = {
              canvas: "Canvas",
              "rich-text": "Rich Text",
              attachment: "Attachment",
              video: "Video",
              form: "Form",
            };

            const nodeTypeName =
              nodeTypeNames[blockingBlock.label] || blockingBlock.label;

            return true;
          }
          return false;
        };

        if (!isZipExport) {
          while (adjustForBlocks()) {}
        }

        const adjustForRichTextBlocks = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const splitRichTextBlock = blockMetasPx.find((meta) => {
            if (meta.label !== "rich-text") return false;
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            // If the rich text block is taller than the usable page height, it
            // cannot fit on a single page anyway, so allow it to split.
            // Otherwise, if it can fit on one page, treat it as non-splittable
            // and move it wholly to the next page.
            if (meta.heightPx >= availableHeightPx) return false;
            return true;
          });

          if (!splitRichTextBlock) return false;

          // If we can identify a "short description" portion that fits on the current page,
          // prefer breaking between short description and content (instead of moving the whole block).
          // This keeps short description on the current page and starts the rich text content on the next page.
          if (
            Number.isFinite(splitRichTextBlock.richTextShortDescBottomPx) &&
            Number.isFinite(splitRichTextBlock.richTextContentTopPx)
          ) {
            const shortDescBottomPx =
              splitRichTextBlock.richTextShortDescBottomPx;
            const contentTopPx = splitRichTextBlock.richTextContentTopPx;

            const shortDescFitsOnPage = shortDescBottomPx <= pageEnd;
            const contentStartsOnThisPage =
              contentTopPx >= pageStart && contentTopPx < pageEnd;

            if (shortDescFitsOnPage && contentStartsOnThisPage) {
              const proposed = contentTopPx - currentOffsetPx;
              // Only adjust when it *reduces* the current page size; otherwise this can loop forever.
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }

          // Calculate where the next page would start after moving the rich text block
          const proposedPageEnd = splitRichTextBlock.topPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start.
          // The header position is absolute, so if it's at the same position as the rich text
          // start, they will overlap on the next page.
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            // Check if step header starts at or very close to where the rich text would start
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start.
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 20 &&
              substepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the rich text block,
          // we need to end the current page earlier so the rich text content does not get hidden
          // behind the Step/Substep title header.
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const richTextStart = splitRichTextBlock.topPx;

            // Add spacing after the header to ensure the rich text content doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // The rich text content will be hidden/overlapped if it starts at or before the
            // header bottom + spacing.
            const wouldOverlap = richTextStart <= headerBottom + headerSpacing;

            // Also treat the case where the rich text is very close to the header (increased range)
            const isVeryClose =
              richTextStart >= headerTop - 20 &&
              richTextStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts.
              // This ensures that on the next page:
              // - The header is at the top of the page.
              // - The header renders first.
              // - The rich text content appears after the header instead of behind it.
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the rich text)
              if (
                adjustedPageEnd < richTextStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          const proposed = splitRichTextBlock.topPx - currentOffsetPx;
          // Only adjust when it *reduces* the current page size; otherwise this can loop forever.
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        while (adjustForRichTextBlocks()) {}

        // Keep section-intro blocks (section short description area, including lexical images)
        // on a single page when they can fit. This specifically avoids image slicing in ZIP exports.
        const adjustForSectionIntroBlocks = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const splitSectionIntro = blockMetasPx.find((meta) => {
            if (meta.label !== "section-intro") return false;
            if (!meta.includeInPagination) return false;
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            if (meta.heightPx >= availableHeightPx * 0.95) return false;
            return true;
          });
          if (!splitSectionIntro) return false;

          const proposed = splitSectionIntro.topPx - currentOffsetPx;
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = Math.floor(proposed);
            return true;
          }
          return false;
        };
        while (adjustForSectionIntroBlocks()) {}

        // Adjust page breaks for Item Required and Training Log tables FIRST - ensure heading doesn't split
        // This must run before canvas adjustments to ensure tables are positioned independently
        // DISABLED: Step + Item Required table may split across pages (no move-to-next-page).
        const adjustForItemRequiredTables = () => {
          return false;

          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const footerHeightPx = Math.floor(footerHeight / pixelToMm);
          const footerMarginPx = Math.max(0, Math.floor(footerHeightPx * 0.25));
          const minSpaceFromFooterPx = footerMarginPx;
          const safePageEnd = pageContentPx - minSpaceFromFooterPx;
          // Small safety padding to avoid leaving a 1–3px "sliver" of the heading due to rounding
          // differences between DOM measurements and canvas rendering.
          const breakSafetyPaddingPx = Math.max(
            2,
            Math.round(8 * domToCanvasScaleY),
          );
          // Only apply the padding when the break point is not near the very top of the page,
          // to avoid creating near-empty pages.
          const applyBreakPadding = (posInPagePx) => {
            if (posInPagePx === null || posInPagePx === undefined)
              return posInPagePx;
            const minDistanceFromTopForPadding = 50;
            if (posInPagePx <= minDistanceFromTopForPadding) return posInPagePx;
            return Math.max(0, posInPagePx - breakSafetyPaddingPx);
          };

          // Find Item Required and Training Log tables that would cause heading/header splitting
          // CRITICAL: Ensure heading and table stay together as one block
          // CRITICAL: For Item Required tables inside steps, ensure entire step moves with table
          const itemRequiredTableToMove = tableMetas.find((meta) => {
            // Check if this is an Item Required or Training Log table
            if (!isItemRequiredTableMeta(meta) && !meta.isTrainingLogTable)
              return false;

            // CRITICAL: If this Item Required table is inside a step, check if moving just the table
            // would separate it from the step. If so, we need to move the entire step together.
            if (
              meta.isItemRequiredTable &&
              meta.stepId !== undefined &&
              meta.stepId !== null
            ) {
              const parentStep = stepMetas.find(
                (stepMeta) => stepMeta.stepId === meta.stepId,
              );
              if (parentStep) {
                const stepTopPx = parentStep.topPx;
                const stepHeaderTopPx = parentStep.headerTopPx;
                const stepHeaderBottomPx =
                  stepHeaderTopPx + parentStep.headerHeightPx;
                const tableTopPx = meta.topPx;
                const tableBottomPx = meta.bottomPx;
                const tableTopInPage = tableTopPx - pageStart;
                const tableBottomInPage = tableBottomPx - pageStart;

                // Check if the step header is on this page
                const stepHeaderOnThisPage =
                  stepHeaderTopPx >= pageStart && stepHeaderTopPx < pageEnd;

                // Check if the table overlaps with this page
                const tableOnThisPage =
                  tableTopPx < pageEnd && tableBottomPx > pageStart;

                // CRITICAL: If step header is on this page AND the table extends beyond the page,
                // we need to move the entire step (to avoid orphaning the step header)
                if (stepHeaderOnThisPage && tableOnThisPage) {
                  // Check if table would extend beyond this page
                  if (
                    tableBottomInPage > safePageEnd ||
                    tableBottomInPage > pageContentPx ||
                    tableBottomPx > pageEnd
                  ) {
                    return true; // Table would be split from step - move entire step to next page
                  }
                }

                // Also check: if step starts on this page and table doesn't fit, move entire step
                const stepStartsOnThisPage =
                  stepTopPx >= pageStart && stepTopPx < pageEnd;
                if (stepStartsOnThisPage && tableOnThisPage) {
                  if (
                    tableBottomInPage > safePageEnd ||
                    tableBottomInPage > pageContentPx
                  ) {
                    return true; // Step and table don't fit together - move entire step
                  }
                }

                // NEW: If the step header is on this page but the Item Required table
                // starts entirely on the NEXT page (pushed there by other adjustments),
                // move the step so step + item required table stay together on one page.
                const tableStartsOnNextPage = tableTopPx >= pageEnd;
                if (stepHeaderOnThisPage && tableStartsOnNextPage) {
                  // Only move if the combined block (step header → table bottom) can fit
                  // on a single page; if it's too tall, splitting is unavoidable.
                  const combinedHeight = tableBottomPx - stepHeaderTopPx;
                  if (combinedHeight < availableHeightPx * 0.95) {
                    return true; // Move entire step to keep it together with its Item Required table
                  }
                }
              }
            }

            // Get heading and table positions
            const headingTopPx =
              meta.headingElement && meta.headingTopDom !== null
                ? Math.round(meta.headingTopDom * domToCanvasScaleY)
                : meta.headerTopPx;
            const tableTopPx = meta.topPx;
            const tableBottomPx = meta.bottomPx;

            // Check if heading is on this page
            const headingOnPage =
              headingTopPx >= pageStart && headingTopPx < pageEnd;

            // CRITICAL: If heading is on this page, ensure table fits entirely on same page
            // If table doesn't fit, move both heading and table to next page
            if (headingOnPage) {
              const headingTopInPage = headingTopPx - pageStart;

              // Only check if heading is within page content area
              if (headingTopInPage >= 0 && headingTopInPage < pageContentPx) {
                // Calculate table bottom position relative to page start
                const tableBottomInPage = tableBottomPx - pageStart;

                // CRITICAL: Check if table fits entirely on this page
                // Use both safePageEnd (accounting for footer) and pageContentPx (actual page height)
                // If table extends beyond either, move both heading and table to next page
                const tableDoesntFit =
                  tableBottomInPage > safePageEnd ||
                  tableBottomInPage > pageContentPx;

                if (tableDoesntFit) {
                  return true; // Heading and table can't fit together - move both to next page
                }
              }
            }

            // Continue with other checks for heading/table overlap
            const headingOverlapsPage =
              headingTopPx < pageEnd && headingTopPx >= pageStart;
            const tableOverlapsPage =
              tableTopPx < pageEnd && tableBottomPx > pageStart;

            // If neither heading nor table overlaps with this page, skip
            if (!headingOverlapsPage && !tableOverlapsPage) return false;

            // CRITICAL: Check if the table heading (e.g., "Item Required" or "Training log") would be split across pages
            // This must be checked FIRST and separately from the header to prevent heading splitting
            // Check even if table doesn't "start" on this page - if heading overlaps, it could split
            if (
              meta.headingElement &&
              meta.headingTopDom !== null &&
              meta.headingHeightDom !== null
            ) {
              const headingHeightPx = Math.round(
                meta.headingHeightDom * domToCanvasScaleY,
              );
              const headingBottomPx = headingTopPx + headingHeightPx;

              // Check if heading overlaps with this page (could be split)
              const headingOverlapsPageCheck =
                headingTopPx < pageEnd && headingBottomPx > pageStart;

              if (headingOverlapsPageCheck) {
                // If heading starts before page but extends into it, it's being split
                if (headingTopPx < pageStart && headingBottomPx > pageStart) {
                  return true; // Heading is being split - move entire table to next page
                }

                // If heading starts on this page but extends beyond page end, it's being split
                if (
                  headingTopPx >= pageStart &&
                  headingTopPx < pageEnd &&
                  headingBottomPx > pageEnd
                ) {
                  return true; // Heading is being split - move entire table to next page
                }

                // Also check if heading is too close to footer (would be split near footer)
                const headingTopInPage = headingTopPx - pageStart;
                const headingBottomInPage = headingBottomPx - pageStart;

                if (headingTopInPage >= 0 && headingTopInPage < pageContentPx) {
                  if (headingBottomInPage > safePageEnd) {
                    return true; // Heading too close to footer - move entire table to next page
                  }
                }

                // Additional check: if heading is very close to page boundary, move it to prevent any chance of splitting
                const minDistanceFromBoundary = Math.round(
                  5 * domToCanvasScaleY,
                ); // 5px minimum distance
                if (
                  headingTopInPage >= 0 &&
                  headingTopInPage < minDistanceFromBoundary &&
                  headingBottomInPage > pageContentPx - minDistanceFromBoundary
                ) {
                  return true; // Heading too close to boundaries - move entire table to next page
                }
              }
            }

            // Check if table overlaps with this page (for other checks)
            if (!tableOverlapsPage) return false;

            // CRITICAL: Check if the header (heading + thead) would be split across pages
            // The header includes the table heading (e.g., "Item Required" or "Training log") and the table thead
            const headerTopPx = meta.headerTopPx;
            const headerHeightPx = meta.headerHeightPx || 0;
            const headerBottomPx = headerTopPx + headerHeightPx;

            // If header would be split (header extends beyond page end), move entire table to next page
            if (
              headerTopPx >= pageStart &&
              headerTopPx < pageEnd &&
              headerBottomPx > pageEnd
            ) {
              return true;
            }

            // Also check if header is too close to footer (would be split near footer)
            const headerTopInPage = headerTopPx - pageStart;
            const headerBottomInPage = headerBottomPx - pageStart;

            if (headerTopInPage >= 0 && headerTopInPage < pageContentPx) {
              if (headerBottomInPage > safePageEnd) {
                return true;
              }
            }

            // Check if table fits entirely on this page
            const tableTopInPage = meta.topPx - pageStart;
            const tableBottomInPage = meta.bottomPx - pageStart;

            // CRITICAL: For Item Required tables, add extra buffer to prevent last row from splitting
            // When table is near the footer, ensure we have enough space for complete rows
            if (meta.isItemRequiredTable || meta.isTrainingLogTable) {
              // Add buffer of approximately 60-100px to account for last row + safety margin
              // This prevents the last row from being split across pages
              const minBufferFromFooter = Math.round(100 * domToCanvasScaleY);
              const adjustedSafePageEnd = safePageEnd - minBufferFromFooter;

              // If table bottom is within the buffer zone near footer, move entire table/step
              if (
                tableBottomInPage > adjustedSafePageEnd &&
                tableBottomInPage <= safePageEnd + minBufferFromFooter
              ) {
                return true; // Table too close to footer - move to prevent row splitting
              }
            }

            // If table bottom extends beyond safe page end, move it to next page
            if (tableBottomInPage > safePageEnd) {
              return true;
            }

            // Also check if table would be split across pages
            // If table starts on this page but ends beyond page end, move it
            if (meta.bottomPx > pageEnd) {
              return true;
            }

            return false;
          });

          if (!itemRequiredTableToMove) return false;

          // DEBUG: Log when we find a table that needs to move
          // CRITICAL: If Item Required table is inside a step, we need to move the entire step
          // Find the parent step if this table has a stepId
          let parentStepMeta = null;
          if (
            itemRequiredTableToMove.isItemRequiredTable &&
            itemRequiredTableToMove.stepId !== undefined &&
            itemRequiredTableToMove.stepId !== null
          ) {
            parentStepMeta = stepMetas.find(
              (stepMeta) => stepMeta.stepId === itemRequiredTableToMove.stepId,
            );

            // DEBUG: Log parent step info
            if (parentStepMeta) {
            } else {
            }
          }

          // Adjust page break to move the entire step (if table is in a step) or just the table to next page
          // Use the step position first (if table is in a step), then heading position, then header position, then table position
          let positionToUse = null;

          // First priority: If table is inside a step, move the entire step.
          // IMPORTANT: Use the step header position (not container top) so we reliably move the
          // visible "Step" start + short description + Item Required table together.
          if (parentStepMeta) {
            const stepHeaderTopPx = parentStepMeta.headerTopPx;
            const stepHeaderTopInPage = stepHeaderTopPx - pageStart;

            // If step starts before page, move entire page content to next page (position 0)
            if (stepHeaderTopPx < pageStart) {
              positionToUse = 0;
            }
            // If step starts on this page, move the entire step to next page
            else if (
              stepHeaderTopInPage >= 0 &&
              stepHeaderTopInPage < pageContentPx
            ) {
              positionToUse = applyBreakPadding(
                Math.max(0, stepHeaderTopInPage),
              );
            }
          }

          // Second priority: Use header position to prevent header (thead) from splitting
          if (positionToUse === null) {
            const headerTopPx = itemRequiredTableToMove.headerTopPx;
            const headerTopInPage = headerTopPx - pageStart;

            // If header starts before page, move entire page content to next page
            if (headerTopPx < pageStart) {
              positionToUse = 0;
            } else if (headerTopInPage > 0 && headerTopInPage < pageContentPx) {
              positionToUse = applyBreakPadding(Math.max(0, headerTopInPage));
            }
          }

          // Fourth priority: Use table position
          if (positionToUse === null) {
            const tableTopInPage = itemRequiredTableToMove.topPx - pageStart;
            if (tableTopInPage > 0 && tableTopInPage < pageContentPx) {
              positionToUse = tableTopInPage;
            }
          }

          // Allow positionToUse = 0 to move entire page content to next page
          if (
            positionToUse !== null &&
            positionToUse >= 0 &&
            positionToUse < pageContentPx
          ) {
            pageContentPx = Math.floor(positionToUse);
            return true;
          }

          return false;
        };

        while (adjustForItemRequiredTables()) {}

        // Keep Section heading + short description together (avoid splitting "Section X" across pages).
        // DISABLED: Section nodes are allowed to split across pages (no move-to-next-page for sections).
        const adjustForSectionHeadingPairs = () => {
          if (disableSectionHeadingPush) return false;
          if (!sectionHeadingPairsPx || sectionHeadingPairsPx.length === 0)
            return false;

          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          // Padding only — large values forced Section blocks to the next page with empty space left.
          const breakSafetyPaddingPx = Math.max(
            28,
            Math.round(36 * domToCanvasScaleY),
          );
          const applyBreakPadding = (posInPagePx) => {
            if (posInPagePx === null || posInPagePx === undefined)
              return posInPagePx;
            const minDistanceFromTopForPadding = 50;
            if (posInPagePx <= minDistanceFromTopForPadding) return posInPagePx;
            return Math.max(0, posInPagePx - breakSafetyPaddingPx);
          };

          const pairToMove = sectionHeadingPairsPx.find((pair) => {
            // Keep the very first section (right after BOM) on page 1 when the BOM→Section gap is small.
            // Otherwise the safety-band logic can still push it to page 2 even though it visually fits.
            if (
              pageStart === 0 &&
              firstSectionTopAfterBomPx != null &&
              bomToFirstSectionGapPx != null &&
              bomToFirstSectionGapPx < minBomToSectionTrimGapPx &&
              Math.abs(pair.headingTopPx - firstSectionTopAfterBomPx) <= 3
            ) {
              return false;
            }
            // Only consider pairs that START on this page.
            if (pair.headingTopPx < pageStart) return false;
            if (pair.headingTopPx >= pageEnd) return false;

            const pairHeightPx = pair.descBottomPx - pair.headingTopPx;
            // If it's too tall to reasonably fit, allow splitting to avoid infinite loops.
            if (pairHeightPx >= availableHeightPx * 0.95) return false;

            // If the heading itself would be cut by the page end (or within safety buffer), move it.
            const headingBottomWithExtra =
              pair.headingBottomPx + Math.round(8 * domToCanvasScaleY);
            if (headingBottomWithExtra > pageEnd - breakSafetyPaddingPx)
              return true;

            // If the description would not fully fit on this slice, move both.
            if (pair.descBottomPx > pageEnd) return true;

            // If the break falls between heading and description, move both.
            if (pageEnd > pair.headingBottomPx && pageEnd < pair.descBottomPx)
              return true;

            return false;
          });

          if (!pairToMove) return false;

          const headingTopInPage = pairToMove.headingTopPx - pageStart;
          // If it's already at the top (or would create an empty page), don't adjust here.
          if (headingTopInPage <= 0) return false;

          const positionToUse = applyBreakPadding(
            Math.max(0, headingTopInPage),
          );
          if (
            positionToUse !== null &&
            positionToUse > 0 &&
            positionToUse < pageContentPx
          ) {
            pageContentPx = Math.floor(positionToUse);
            return true;
          }

          return false;
        };

        if (!isZipExport) {
          while (adjustForSectionHeadingPairs()) {}
        }

        // Protect ALL section headers (heading only) - runs even when no description pairs exist
        // Fixes "Section 2", "Section 3" etc. being cut when they lack short description in pairs
        const adjustForSectionHeadingsOnly = () => {
          if (disableSectionHeadingPush) return false;
          if (!sectionHeadingOnlyPx || sectionHeadingOnlyPx.length === 0)
            return false;
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const sectionBandTailSafetyPx = Math.max(
            8,
            Math.round(14 * domToCanvasScaleY),
          );
          const bufferPx = Math.max(30, Math.round(35 * domToCanvasScaleY));
          const headingExtraPx = Math.round(20 * domToCanvasScaleY);
          const sectionToMove = sectionHeadingOnlyPx.find((sh) => {
            // Keep the very first section (right after BOM) on page 1 when the BOM→Section gap is small.
            if (
              pageStart === 0 &&
              firstSectionTopAfterBomPx != null &&
              bomToFirstSectionGapPx != null &&
              bomToFirstSectionGapPx < minBomToSectionTrimGapPx &&
              Math.abs(sh.headingTopPx - firstSectionTopAfterBomPx) <= 3
            ) {
              return false;
            }
            const bandTop = sh.headingTopPx - sectionHeadingBandGuardPx;
            const bandBottom =
              sh.headingBottomPx + headingExtraPx + sectionHeadingBandGuardPx;
            const contentMaxY = pageEnd - sectionBandTailSafetyPx;
            if (bandBottom <= pageStart || bandTop >= pageEnd) return false;
            const bandFitsOnPage =
              bandTop >= pageStart && bandBottom <= contentMaxY;
            if (bandFitsOnPage) return false;
            const sliceCutsBand = pageEnd > bandTop && pageEnd < bandBottom;
            const bandStartsOnPage = bandTop >= pageStart && bandTop < pageEnd;
            const bandDoesNotFit =
              bandStartsOnPage && bandBottom > contentMaxY;
            return sliceCutsBand || bandDoesNotFit;
          });
          if (!sectionToMove) return false;
          const bandTopMove =
            sectionToMove.headingTopPx - sectionHeadingBandGuardPx;
          const proposed = Math.max(
            0,
            Math.floor(bandTopMove - currentOffsetPx - bufferPx),
          );
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = Math.floor(proposed);
            return true;
          }
          return false;
        };
        if (!isZipExport) {
          while (adjustForSectionHeadingsOnly()) {}
        }

        // CRITICAL: Adjust for entire canvas blocks (including Short Description) FIRST
        // Prefer keeping Short Description on current page (if it fits) while moving only the canvas
        // content to the next page. If that isn't possible, move the whole canvas block.
        // This must run before adjustForCanvasImages to take priority
        const adjustForCanvasBlocks = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const debugCanvasBlocksOnPage = blockMetasPx.filter((m) => {
            if (m.label !== "canvas") return false;
            if (!m.includeInPagination) return false;
            return m.bottomPx > pageStart && m.topPx < pageEnd;
          });
          if (debugCanvasBlocksOnPage.length > 0) {
            const evaluations = debugCanvasBlocksOnPage.map((meta) => {
              const canvasBlockTopPx = Number.isFinite(meta.canvasShortDescTopPx)
                ? meta.canvasShortDescTopPx
                : meta.topPx;
              let canvasBlockBottomPx = Number.isFinite(meta.canvasShortDescBottomPx)
                ? Math.max(meta.bottomPx, meta.canvasShortDescBottomPx)
                : meta.bottomPx;
              let matchedImage = null;
              if (Number.isFinite(meta.canvasContentTopPx)) {
                matchedImage = canvasImageMetasPx.find(
                  (imgMeta) =>
                    Math.abs(imgMeta.topPx - meta.canvasContentTopPx) <= 6 &&
                    imgMeta.bottomPx > imgMeta.topPx,
                );
                if (matchedImage) {
                  canvasBlockBottomPx = Math.max(
                    Number.isFinite(meta.canvasShortDescBottomPx)
                      ? meta.canvasShortDescBottomPx
                      : meta.topPx,
                    matchedImage.bottomPx,
                  );
                }
              }
              const effectiveHeightPx = Math.max(
                0,
                canvasBlockBottomPx - canvasBlockTopPx,
              );
              const startsOnPage =
                canvasBlockTopPx >= pageStart && canvasBlockTopPx < pageEnd;
              const extendsPastPage = canvasBlockBottomPx > pageEnd;
              const tooTallForMove = effectiveHeightPx >= availableHeightPx * 0.8;
              const remainingSpace = pageEnd - canvasBlockTopPx;
              const topOffsetInPage = canvasBlockTopPx - pageStart;
              const availableSpaceOnPage = pageContentPx - topOffsetInPage;
              const fitsCurrentPage =
                effectiveHeightPx <= availableSpaceOnPage &&
                effectiveHeightPx <= remainingSpace;
              return {
                stepId: meta.stepId,
                substepId: meta.substepId,
                topPx: meta.topPx,
                bottomPx: meta.bottomPx,
                canvasBlockTopPx,
                canvasBlockBottomPx,
                canvasContentTopPx: meta.canvasContentTopPx,
                matchedImageTopPx: matchedImage?.topPx ?? null,
                matchedImageBottomPx: matchedImage?.bottomPx ?? null,
                effectiveHeightPx,
                startsOnPage,
                extendsPastPage,
                tooTallForMove,
                fitsCurrentPage,
                availableSpaceOnPage,
                remainingSpace,
              };
            });
          }

          // Find canvas blocks from blockMetasPx (includes entire block with Short Description)
          const splitCanvasBlock = blockMetasPx.find((meta) => {
            // Only process canvas blocks
            if (meta.label !== "canvas") return false;
            if (!meta.includeInPagination) return false;
            const canvasBlockTopPx = Number.isFinite(meta.canvasShortDescTopPx)
              ? meta.canvasShortDescTopPx
              : meta.topPx;
            let canvasBlockBottomPx = Number.isFinite(meta.canvasShortDescBottomPx)
              ? Math.max(meta.bottomPx, meta.canvasShortDescBottomPx)
              : meta.bottomPx;
            // Prefer the rendered canvas image bottom when possible. Some canvas blocks
            // report an inflated wrapper height (large trailing blank area), which can
            // incorrectly classify the block as "too tall" and skip move-to-next-page logic.
            if (Number.isFinite(meta.canvasContentTopPx)) {
              const matchedCanvasImage = canvasImageMetasPx.find(
                (imgMeta) =>
                  Math.abs(imgMeta.topPx - meta.canvasContentTopPx) <= 6 &&
                  imgMeta.bottomPx > imgMeta.topPx,
              );
              if (matchedCanvasImage) {
                canvasBlockBottomPx = Math.max(
                  Number.isFinite(meta.canvasShortDescBottomPx)
                    ? meta.canvasShortDescBottomPx
                    : meta.topPx,
                  matchedCanvasImage.bottomPx,
                );
              }
            }

            // Canvas block must start on this page
            if (canvasBlockTopPx < pageStart) return false;
            if (canvasBlockTopPx >= pageEnd) return false;

            // Canvas block must extend beyond page end (would be split)
            if (canvasBlockBottomPx <= pageEnd) return false;

            // Only adjust for canvas blocks that can fit entirely on a single page
            // If canvas block is too tall (>= 80% of available height), it will span pages anyway
            const effectiveHeightPx = Math.max(
              0,
              canvasBlockBottomPx - canvasBlockTopPx,
            );
            if (effectiveHeightPx >= availableHeightPx * 0.8) return false;

            // Check if canvas block can fit entirely on current page with remaining space
            const remainingSpace = pageEnd - canvasBlockTopPx;
            const topOffsetInPage = canvasBlockTopPx - pageStart;
            const availableSpaceOnPage = pageContentPx - topOffsetInPage;

            // Canvas block can fit if its height is less than or equal to the available space
            if (
              effectiveHeightPx <= availableSpaceOnPage &&
              effectiveHeightPx <= remainingSpace
            ) {
              // Canvas block can fit on current page, don't split it
              return false;
            }

            return true;
          });

          if (!splitCanvasBlock) return false;
          const canvasBlockTopPx = Number.isFinite(splitCanvasBlock.canvasShortDescTopPx)
            ? splitCanvasBlock.canvasShortDescTopPx
            : splitCanvasBlock.topPx;

          // Keep canvas short description with its canvas content.
          // Do not split exactly at canvas content top — that moves the description
          // to previous page and makes it appear "missing" where the canvas renders.

          // Calculate where the next page would start after moving the canvas block
          const proposedPageEnd = canvasBlockTopPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 20 &&
              substepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the canvas block,
          // we need to end the current page earlier so the canvas block (including Short Description)
          // does not get hidden behind the Step/Substep title header
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const canvasStart = canvasBlockTopPx;

            // Add spacing after the header to ensure the canvas block doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // The canvas block will be hidden/overlapped if it starts at or before the
            // header bottom + spacing
            const wouldOverlap = canvasStart <= headerBottom + headerSpacing;

            // Also treat the case where the canvas is very close to the header
            const isVeryClose =
              canvasStart >= headerTop - 20 &&
              canvasStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts
              // This ensures that on the next page:
              // - The header is at the top of the page
              // - The header renders first
              // - The canvas block (including Short Description) appears after the header instead of behind it
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the canvas)
              if (
                adjustedPageEnd < canvasStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Move the entire canvas block (including Short Description) to the next page
          const proposed = canvasBlockTopPx - currentOffsetPx;
          if (proposed > 0) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        // ZIP/non-module path: do not pre-move entire canvas blocks to next page.
        // That move can place canvas short descriptions under synthetic Step continuation
        // bands, making the description look "missing". Let normal page slicing keep
        // the description visible in-flow.
        if (!isZipExport) {
          while (adjustForCanvasBlocks()) {}
        }

        const adjustForCanvasImages = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const debugCanvasImagesOnPage = canvasImageMetasPx.filter((m) => {
            if (!m.includeInPagination) return false;
            return m.bottomPx > pageStart && m.topPx < pageEnd;
          });
          if (debugCanvasImagesOnPage.length > 0) {
            const evaluations = debugCanvasImagesOnPage.map((meta) => {
              const startsOnPage = meta.topPx >= pageStart && meta.topPx < pageEnd;
              const extendsPastPage = meta.bottomPx > pageEnd;
              const tooTallForMove = meta.heightPx >= availableHeightPx * 0.8;
              const remainingSpace = pageEnd - meta.topPx;
              const topOffsetInPage = meta.topPx - pageStart;
              const availableSpaceOnPage = pageContentPx - topOffsetInPage;
              const fitsCurrentPage =
                meta.heightPx <= availableSpaceOnPage &&
                meta.heightPx <= remainingSpace;
              const pairedCanvasBlock = blockMetasPx.find((b) => {
                if (b.label !== "canvas") return false;
                if (
                  !Number.isFinite(b.canvasShortDescTopPx) ||
                  !Number.isFinite(b.canvasShortDescBottomPx) ||
                  !Number.isFinite(b.canvasContentTopPx)
                ) {
                  return false;
                }
                const sameContentTop = Math.abs(b.canvasContentTopPx - meta.topPx) <= 4;
                const hasDescription = b.canvasShortDescBottomPx > b.canvasShortDescTopPx;
                return sameContentTop && hasDescription;
              });
              return {
                topPx: meta.topPx,
                bottomPx: meta.bottomPx,
                heightPx: meta.heightPx,
                startsOnPage,
                extendsPastPage,
                tooTallForMove,
                fitsCurrentPage,
                availableSpaceOnPage,
                remainingSpace,
                pairedCanvasBlock: !!pairedCanvasBlock,
                pairedStepId: pairedCanvasBlock?.stepId ?? null,
                pairedSubstepId: pairedCanvasBlock?.substepId ?? null,
              };
            });
          }

          const splitCanvasImage = canvasImageMetasPx.find((meta) => {
            // Canvas must start on this page
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            // Canvas must extend beyond page end (would be split)
            if (meta.bottomPx <= pageEnd) return false;
            // Only adjust for canvases that can fit entirely on a single page
            // If canvas is too tall (>= 80% of available height), it will span pages anyway
            if (meta.heightPx >= availableHeightPx * 0.8) return false;
            // Check if canvas can fit entirely on current page with remaining space
            // Account for the actual available space on the page (pageContentPx)
            const remainingSpace = pageEnd - meta.topPx;
            const topOffsetInPage = meta.topPx - pageStart;
            const availableSpaceOnPage = pageContentPx - topOffsetInPage;
            // Canvas can fit if its height is less than or equal to the available space
            if (
              meta.heightPx <= availableSpaceOnPage &&
              meta.heightPx <= remainingSpace
            ) {
              // Canvas can fit on current page, don't split it - let it render on current page
              return false;
            }
            // If this canvas image belongs to a canvas block with an explicit short description,
            // keep them together by letting block-level pagination handle it.
            const hasPairedCanvasShortDesc = blockMetasPx.some((b) => {
              if (b.label !== "canvas") return false;
              if (
                !Number.isFinite(b.canvasShortDescTopPx) ||
                !Number.isFinite(b.canvasShortDescBottomPx) ||
                !Number.isFinite(b.canvasContentTopPx)
              ) {
                return false;
              }
              const sameContentTop = Math.abs(b.canvasContentTopPx - meta.topPx) <= 4;
              const hasDescription = b.canvasShortDescBottomPx > b.canvasShortDescTopPx;
              return sameContentTop && hasDescription;
            });
            if (hasPairedCanvasShortDesc) return false;
            return true;
          });

          if (!splitCanvasImage) return false;

          // Calculate where the next page would start after moving the canvas image
          const proposedPageEnd = splitCanvasImage.topPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start.
          // The header position is absolute, so if it's at the same position as the canvas
          // start, they will overlap on the next page.
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            // Check if step header starts at or very close to where the canvas would start
            // Allow a tolerance to catch headers that are very close (increased range for better detection)
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start.
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 20 &&
              substepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the canvas image,
          // we need to end the current page earlier so the canvas image does not get hidden
          // behind the Step/Substep title header.
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const canvasStart = splitCanvasImage.topPx;

            // Add spacing after the header to ensure the canvas image doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // The canvas image will be hidden/overlapped if it starts at or before the
            // header bottom + spacing.
            const wouldOverlap = canvasStart <= headerBottom + headerSpacing;

            // Also treat the case where the canvas is very close to the header (increased range)
            const isVeryClose =
              canvasStart >= headerTop - 20 &&
              canvasStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts.
              // This ensures that on the next page:
              // - The header is at the top of the page.
              // - The header renders first.
              // - The canvas image appears after the header instead of behind it.
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the canvas)
              if (
                adjustedPageEnd < canvasStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          const proposed = splitCanvasImage.topPx - currentOffsetPx;
          if (proposed > 0) {
            pageContentPx = proposed;
            return true;
          }
          return false;
        };

        if (!isZipExport) {
          while (adjustForCanvasImages()) {}
        }

        const adjustForAttachmentImageSlices = () => {
          if (!attachmentImageMetasPx.length) return false;

          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const splitImage = attachmentImageMetasPx.find((meta) => {
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            // Only adjust for images that can fit entirely on a single page.
            if (meta.heightPx > availableHeightPx) return false;
            return true;
          });

          if (!splitImage) return false;

          const proposed = splitImage.topPx - currentOffsetPx;
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        if (!isZipExport) {
          while (adjustForAttachmentImageSlices()) {}
        }

        // Ensure attachment nodes at the top of a page have 100px margin from top
        const adjustForAttachmentsAtPageTop = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const nextPageStart = pageEnd;
          const requiredTopMarginPx = 100; // 100px margin required from top of page
          const maxAllowedEmptySpacePx = 10; // Maximum allowed empty space at bottom of previous page

          // Find attachment blocks that would start at the top of the next page
          // This includes attachments that start exactly at nextPageStart or very close to it
          const attachmentAtNextPageTop = blockMetasPx.find((meta) => {
            if (meta.label !== "attachment") return false;
            // Check if attachment would start at or very close to the next page start
            // This means it would be at the top of the next page (within 20px tolerance)
            const startsAtNextPageTop =
              meta.topPx >= nextPageStart - 20 &&
              meta.topPx <= nextPageStart + 20;
            return startsAtNextPageTop;
          });

          if (!attachmentAtNextPageTop) return false;

          // Calculate where the page should end to ensure attachment starts with 100px margin
          const attachmentStart = attachmentAtNextPageTop.topPx;
          const requiredPageEnd = attachmentStart - requiredTopMarginPx;

          // Also check for step headers that would be split
          const stepHeaderBeforeAttachment = stepMetas.find((meta) => {
            const headerStartsBeforeAttachment =
              meta.headerTopPx < attachmentStart;
            const headerBottom = meta.headerTopPx + meta.headerHeightPx;
            const headerWouldBeSplit =
              meta.headerTopPx < requiredPageEnd &&
              headerBottom > requiredPageEnd;
            return headerStartsBeforeAttachment && headerWouldBeSplit;
          });

          // If there's a step header that would be split, keep it on the previous page
          if (stepHeaderBeforeAttachment) {
            const keepOnPageEnd = stepHeaderBeforeAttachment.headerTopPx;

            if (
              keepOnPageEnd > currentOffsetPx &&
              keepOnPageEnd < attachmentStart
            ) {
              const spaceAfterHeader = attachmentStart - keepOnPageEnd;
              if (spaceAfterHeader >= requiredTopMarginPx) {
                const adjustedProposed = keepOnPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Find content that ends before the attachment and can fill space on current page
          // This minimizes empty space above the footer on the previous page
          const contentBeforeAttachment = blockMetasPx
            .filter((meta) => {
              // Skip the attachment we're adjusting for
              if (meta === attachmentAtNextPageTop) return false;
              // Only consider content that ends before the attachment and is on the current page
              return (
                meta.bottomPx < attachmentStart && meta.bottomPx > pageStart
              );
            })
            .sort((a, b) => b.bottomPx - a.bottomPx); // Sort by bottom position, highest first

          // Try to find content that can fill the space between pageEnd and requiredPageEnd
          if (contentBeforeAttachment.length > 0) {
            // Find content that ends closest to requiredPageEnd (the break point for 100px margin)
            // We want content that ends as close as possible to where we need to break
            const contentToKeep = contentBeforeAttachment.find((meta) => {
              // Content should end before or very close to where we need to break
              // We allow content that ends up to 20px after the required break point
              return meta.bottomPx <= requiredPageEnd + 20;
            });

            if (contentToKeep) {
              // Use the content's bottom position, but ensure we still get the 100px margin
              // If content ends before requiredPageEnd, we use requiredPageEnd
              // If content ends after requiredPageEnd (within tolerance), we use content's end
              const preferredPageEnd = Math.max(
                contentToKeep.bottomPx,
                requiredPageEnd,
              );

              // Calculate how much empty space would be left
              // This is the gap between content end and where we need to break
              const emptySpaceBeforeBreak = Math.max(
                0,
                requiredPageEnd - contentToKeep.bottomPx,
              );

              if (
                preferredPageEnd > currentOffsetPx &&
                preferredPageEnd < attachmentStart
              ) {
                const adjustedProposed = preferredPageEnd - currentOffsetPx;

                // Only apply if empty space is reasonable (try to keep it under 50px when possible)
                // But we'll still apply if content helps fill most of the gap
                if (
                  emptySpaceBeforeBreak <= maxAllowedEmptySpacePx &&
                  adjustedProposed > 0 &&
                  adjustedProposed < pageContentPx
                ) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Always apply the 100px margin requirement for attachments at page top
          // Even if this creates some empty space, it's necessary for proper formatting
          if (
            requiredPageEnd > currentOffsetPx &&
            requiredPageEnd < attachmentStart
          ) {
            const adjustedProposed = requiredPageEnd - currentOffsetPx;
            if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
              pageContentPx = adjustedProposed;
              return true;
            }
          }

          return false;
        };

        while (adjustForAttachmentsAtPageTop()) {}

        // Ensure canvas nodes at the top of a page have margin from top
        const adjustForCanvasAtPageTop = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const nextPageStart = pageEnd;
          const requiredTopMarginPx = 40; // Reduced margin from top of page to prevent merging with content below
          const maxAllowedEmptySpacePx = 10; // Maximum allowed empty space at bottom of previous page

          // Find canvas blocks that would start at the top of the next page
          // This includes canvas blocks that start exactly at nextPageStart or very close to it
          const canvasAtNextPageTop = blockMetasPx.find((meta) => {
            if (meta.label !== "canvas") return false;
            // Check if canvas would start at or very close to the next page start
            // This means it would be at the top of the next page (within 20px tolerance)
            const startsAtNextPageTop =
              meta.topPx >= nextPageStart - 20 &&
              meta.topPx <= nextPageStart + 20;
            return startsAtNextPageTop;
          });

          if (!canvasAtNextPageTop) return false;

          // Calculate where the page should end to ensure canvas starts with 100px margin
          const canvasStart = canvasAtNextPageTop.topPx;
          const requiredPageEnd = canvasStart - requiredTopMarginPx;

          // CRITICAL FIX: Check if there's an Item Required table that ends before the Canvas
          // If so, ensure the page break includes the full table (including the last row)
          // This prevents the last row from being hidden behind white overlays
          const itemRequiredTableBeforeCanvas = tableMetas.find((meta) => {
            if (!isItemRequiredTableMeta(meta)) return false;
            // Check if table ends before the Canvas (table bottom is less than Canvas start)
            const tableEndsBeforeCanvas = meta.bottomPx < canvasStart;
            // Check if table is on the current page (at least partially visible)
            const tableOnCurrentPage =
              meta.topPx < pageEnd && meta.bottomPx > pageStart;
            return tableEndsBeforeCanvas && tableOnCurrentPage;
          });

          // If there's an Item Required table ending before Canvas, ensure we include the full table
          if (itemRequiredTableBeforeCanvas) {
            const tableBottom = itemRequiredTableBeforeCanvas.bottomPx;
            // Ensure the page includes the full table with a larger buffer (20px) to prevent any cutoff
            // This buffer ensures the last row's border is fully visible and not hidden behind white lines
            const minPageEndForTable = tableBottom + 20;

            // Calculate the required page end for Canvas margin
            const requiredPageEndForCanvas = canvasStart - requiredTopMarginPx;

            // CRITICAL FIX: Check if canvas can fit on current page after the table
            // Only force page break if canvas cannot fit
            const canvasHeightPx = canvasAtNextPageTop.heightPx || 0;
            // Calculate space available on current page after the table
            const spaceAvailableOnCurrentPage = pageEnd - tableBottom;
            // Calculate space needed for canvas (height + margin from top)
            const spaceNeededForCanvas = canvasHeightPx + requiredTopMarginPx;
            // Check if canvas can fit on current page
            // Canvas can fit if: available space >= needed space AND canvas would still be within page bounds
            const canvasCanFitOnCurrentPage =
              spaceAvailableOnCurrentPage >= spaceNeededForCanvas;

            // If canvas can fit on current page, don't force a page break
            // Let the normal flow handle it - the canvas will render on the same page
            if (canvasCanFitOnCurrentPage) {
              // Canvas can fit, so we don't need to force a page break at the table
              // Just ensure the table is fully included (but don't break the page unnecessarily)
              // Skip the adjustment and let the normal pagination flow handle it
            } else {
              // Canvas cannot fit on current page after the table
              // We need to decide: break at table or at canvas margin point
              if (minPageEndForTable <= requiredPageEndForCanvas) {
                // Table fits with Canvas margin, but canvas can't fit - use table bottom as page end
                const adjustedProposed = minPageEndForTable - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              } else {
                // Table extends beyond the Canvas margin point
                // Prioritize including the full table - Canvas will move to next page
                const adjustedProposed = minPageEndForTable - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Also check for step headers that would be split
          const stepHeaderBeforeCanvas = stepMetas.find((meta) => {
            const headerStartsBeforeCanvas = meta.headerTopPx < canvasStart;
            const headerBottom = meta.headerTopPx + meta.headerHeightPx;
            const headerWouldBeSplit =
              meta.headerTopPx < requiredPageEnd &&
              headerBottom > requiredPageEnd;
            return headerStartsBeforeCanvas && headerWouldBeSplit;
          });

          if (stepHeaderBeforeCanvas) {
            const keepOnPageEnd = stepHeaderBeforeCanvas.headerTopPx;

            if (
              keepOnPageEnd > currentOffsetPx &&
              keepOnPageEnd < canvasStart
            ) {
              const spaceAfterHeader = canvasStart - keepOnPageEnd;
              if (spaceAfterHeader >= requiredTopMarginPx) {
                const adjustedProposed = keepOnPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Find content that ends before the canvas and can fill space on current page
          // This minimizes empty space above the footer on the previous page
          const contentBeforeCanvas = blockMetasPx
            .filter((meta) => {
              // Skip the canvas we're adjusting for
              if (meta === canvasAtNextPageTop) return false;
              // Only consider content that ends before the canvas and is on the current page
              return meta.bottomPx < canvasStart && meta.bottomPx > pageStart;
            })
            .sort((a, b) => b.bottomPx - a.bottomPx); // Sort by bottom position, highest first

          // Try to find content that can fill the space between pageEnd and requiredPageEnd
          if (contentBeforeCanvas.length > 0) {
            // Find content that ends closest to requiredPageEnd (the break point for 100px margin)
            // We want content that ends as close as possible to where we need to break
            const contentToKeep = contentBeforeCanvas.find((meta) => {
              // Content should end before or very close to where we need to break
              // We allow content that ends up to 20px after the required break point
              return meta.bottomPx <= requiredPageEnd + 20;
            });

            if (contentToKeep) {
              // Use the content's bottom position, but ensure we still get the 100px margin
              // If content ends before requiredPageEnd, we use requiredPageEnd
              // If content ends after requiredPageEnd (within tolerance), we use content's end
              const preferredPageEnd = Math.max(
                contentToKeep.bottomPx,
                requiredPageEnd,
              );

              // Calculate how much empty space would be left
              // This is the gap between content end and where we need to break
              const emptySpaceBeforeBreak = Math.max(
                0,
                requiredPageEnd - contentToKeep.bottomPx,
              );

              if (
                preferredPageEnd > currentOffsetPx &&
                preferredPageEnd < canvasStart
              ) {
                const adjustedProposed = preferredPageEnd - currentOffsetPx;

                // Only apply if empty space is reasonable (try to keep it under 50px when possible)
                // But we'll still apply if content helps fill most of the gap
                if (
                  emptySpaceBeforeBreak <= maxAllowedEmptySpacePx &&
                  adjustedProposed > 0 &&
                  adjustedProposed < pageContentPx
                ) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Always apply the 100px margin requirement for canvas at page top
          // Even if this creates some empty space, it's necessary for proper formatting
          if (
            requiredPageEnd > currentOffsetPx &&
            requiredPageEnd < canvasStart
          ) {
            const adjustedProposed = requiredPageEnd - currentOffsetPx;
            if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
              pageContentPx = adjustedProposed;
              return true;
            }
          }

          return false;
        };

        while (adjustForCanvasAtPageTop()) {}

        // Ensure rich-text nodes at the top of a page have 100px margin from top
        const adjustForRichTextAtPageTop = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const nextPageStart = pageEnd;

          const requiredTopMarginPx = 100; // Required gap above rich-text
          const maxAllowedEmptySpacePx = 10; // Allowed empty space on previous page

          // 1️⃣ Detect rich-text starting at top of next page
          const richTextMeta = blockMetasPx.find((meta) => {
            if (meta.label !== "rich-text") return false;

            return (
              meta.topPx >= nextPageStart - 20 &&
              meta.topPx <= nextPageStart + 20
            );
          });

          if (!richTextMeta) return false;

          const richTextStart = richTextMeta.topPx;
          const requiredPageEnd = richTextStart - requiredTopMarginPx;

          // 2️⃣ HARD SAFETY: enforce top margin no matter what
          if (requiredPageEnd > currentOffsetPx) {
            const proposed = requiredPageEnd - currentOffsetPx;

            if (proposed > 0 && proposed < pageContentPx) {
              pageContentPx = proposed;
              return true;
            }
          }

          // 3️⃣ Prevent step header splitting
          const stepHeader = stepMetas.find((meta) => {
            const headerBottom = meta.headerTopPx + meta.headerHeightPx;

            return (
              meta.headerTopPx < richTextStart &&
              meta.headerTopPx < requiredPageEnd &&
              headerBottom > requiredPageEnd
            );
          });

          if (stepHeader) {
            const proposed = stepHeader.headerTopPx - currentOffsetPx;

            if (proposed > 0 && proposed < pageContentPx) {
              pageContentPx = proposed;
              return true;
            }
          }

          // 5️⃣ Fill page intelligently without breaking margin rule
          const candidates = blockMetasPx
            .filter((meta) => {
              if (meta === richTextMeta) return false;
              return meta.bottomPx > pageStart && meta.bottomPx < richTextStart;
            })
            .sort((a, b) => b.bottomPx - a.bottomPx);

          for (const meta of candidates) {
            if (meta.bottomPx > requiredPageEnd) continue;

            const emptySpace = requiredPageEnd - meta.bottomPx;
            if (emptySpace > maxAllowedEmptySpacePx) continue;

            const proposed = meta.bottomPx - currentOffsetPx;

            if (proposed > 0 && proposed < pageContentPx) {
              pageContentPx = proposed;
              return true;
            }
          }

          return false;
        };

        // Re-run until stable
        while (adjustForRichTextAtPageTop()) {}

        // Ensure video nodes at the top of a page have 100px margin from top
        const adjustForVideoAtPageTop = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const nextPageStart = pageEnd;
          const requiredTopMarginPx = 100; // 100px margin required from top of page
          const maxAllowedEmptySpacePx = 10; // Maximum allowed empty space at bottom of previous page

          // Find video blocks that would start at the top of the next page
          const videoAtNextPageTop = blockMetasPx.find((meta) => {
            if (meta.label !== "video") return false;
            // Check if video would start at or very close to the next page start
            const startsAtNextPageTop =
              meta.topPx >= nextPageStart - 20 &&
              meta.topPx <= nextPageStart + 20;
            return startsAtNextPageTop;
          });

          if (!videoAtNextPageTop) return false;

          // Calculate where the page should end to ensure video starts with 100px margin
          const videoStart = videoAtNextPageTop.topPx;
          const requiredPageEnd = videoStart - requiredTopMarginPx;

          // Also check for step headers that would be split
          const stepHeaderBeforeVideo = stepMetas.find((meta) => {
            const headerStartsBeforeVideo = meta.headerTopPx < videoStart;
            const headerBottom = meta.headerTopPx + meta.headerHeightPx;
            const headerWouldBeSplit =
              meta.headerTopPx < requiredPageEnd &&
              headerBottom > requiredPageEnd;
            return headerStartsBeforeVideo && headerWouldBeSplit;
          });

          if (stepHeaderBeforeVideo) {
            const keepOnPageEnd = stepHeaderBeforeVideo.headerTopPx;

            if (keepOnPageEnd > currentOffsetPx && keepOnPageEnd < videoStart) {
              const spaceAfterHeader = videoStart - keepOnPageEnd;
              if (spaceAfterHeader >= requiredTopMarginPx) {
                const adjustedProposed = keepOnPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Find content that ends before the video and can fill space on current page
          const contentBeforeVideo = blockMetasPx
            .filter((meta) => {
              if (meta === videoAtNextPageTop) return false;
              return meta.bottomPx < videoStart && meta.bottomPx > pageStart;
            })
            .sort((a, b) => b.bottomPx - a.bottomPx);

          // Try to find content that can fill the space between pageEnd and requiredPageEnd
          if (contentBeforeVideo.length > 0) {
            const contentToKeep = contentBeforeVideo.find((meta) => {
              return meta.bottomPx <= requiredPageEnd + 20;
            });

            if (contentToKeep) {
              const preferredPageEnd = Math.max(
                contentToKeep.bottomPx,
                requiredPageEnd,
              );

              const emptySpaceBeforeBreak = Math.max(
                0,
                requiredPageEnd - contentToKeep.bottomPx,
              );

              if (
                preferredPageEnd > currentOffsetPx &&
                preferredPageEnd < videoStart
              ) {
                const adjustedProposed = preferredPageEnd - currentOffsetPx;

                if (
                  emptySpaceBeforeBreak <= maxAllowedEmptySpacePx &&
                  adjustedProposed > 0 &&
                  adjustedProposed < pageContentPx
                ) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          // Always apply the 100px margin requirement for video at page top
          if (
            requiredPageEnd > currentOffsetPx &&
            requiredPageEnd < videoStart
          ) {
            const adjustedProposed = requiredPageEnd - currentOffsetPx;
            if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
              pageContentPx = adjustedProposed;
              return true;
            }
          }

          return false;
        };

        while (adjustForVideoAtPageTop()) {}

        const adjustForAttachmentImages = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const splitAttachmentBlock = blockMetasPx.find((meta) => {
            if (meta.label !== "attachment") return false;
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            // Only skip moving the attachment if it is taller than the full usable
            // page height. If it can fit on a single page, we want to keep it from
            // being split across two pages.
            if (meta.heightPx > availableHeightPx) return false;
            return true;
          });

          if (!splitAttachmentBlock) return false;

          // Prefer splitting between short description and attachment content
          // so short description stays on current page (if it fits) and the attachment moves to next page.
          if (
            Number.isFinite(splitAttachmentBlock.attachmentShortDescBottomPx) &&
            Number.isFinite(splitAttachmentBlock.attachmentContentTopPx)
          ) {
            const shortDescBottomPx =
              splitAttachmentBlock.attachmentShortDescBottomPx;
            const contentTopPx = splitAttachmentBlock.attachmentContentTopPx;

            const shortDescFitsOnPage = shortDescBottomPx <= pageEnd;
            const contentStartsOnThisPage =
              contentTopPx >= pageStart && contentTopPx < pageEnd;

            if (shortDescFitsOnPage && contentStartsOnThisPage) {
              const proposed = contentTopPx - currentOffsetPx;
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }

          // Calculate where the next page would start after moving the attachment
          const proposedPageEnd = splitAttachmentBlock.topPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start
          // The header position is absolute, so if it's at the same position as the attachment
          // start, they will overlap on the next page
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            // Check if step header starts at or very close to where the attachment would start
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            // Check if substep header starts at or very close to where the attachment would start
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 20 &&
              substepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the attachment,
          // we need to end the current page earlier so the attachment (including description)
          // doesn't get hidden behind the header
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const attachmentStart = splitAttachmentBlock.topPx;

            // Add spacing after the header to ensure the attachment description doesn't overlap
            const headerSpacing = 20; // 20px spacing after header to account for description

            // Check if the attachment (including description at the top) would overlap with the header
            // The attachment description will be hidden if the attachment block starts
            // at or before the header bottom + spacing
            const wouldOverlap =
              attachmentStart <= headerBottom + headerSpacing;

            // Also check if attachment is very close to header (increased range for better detection)
            const isVeryClose =
              attachmentStart >= headerTop - 20 &&
              attachmentStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts
              // This ensures that on the next page:
              // - The header will be at the top (relative position 0 or very close to 0)
              // - The header will render first
              // - The attachment will appear after the header (even if close, header is visible)
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the attachment)
              if (
                adjustedPageEnd < attachmentStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          const proposed = splitAttachmentBlock.topPx - currentOffsetPx;
          if (proposed > 0) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        if (!isZipExport) {
          while (adjustForAttachmentImages()) {}
        }

        const adjustForVideoImages = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const splitVideoBlock = blockMetasPx.find((meta) => {
            if (meta.label !== "video") return false;
            if (!meta.hasImages || meta.imageCount === 0) return false;
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            if (meta.heightPx >= availableHeightPx * 0.8) return false;
            return true;
          });

          if (!splitVideoBlock) return false;

          // Prefer splitting between short description and video placeholder content
          // so short description stays on current page (if it fits) and the video moves to next page.
          if (
            Number.isFinite(splitVideoBlock.videoShortDescBottomPx) &&
            Number.isFinite(splitVideoBlock.videoContentTopPx)
          ) {
            const shortDescBottomPx = splitVideoBlock.videoShortDescBottomPx;
            const contentTopPx = splitVideoBlock.videoContentTopPx;

            const shortDescFitsOnPage = shortDescBottomPx <= pageEnd;
            const contentStartsOnThisPage =
              contentTopPx >= pageStart && contentTopPx < pageEnd;

            if (shortDescFitsOnPage && contentStartsOnThisPage) {
              const proposed = contentTopPx - currentOffsetPx;
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }

          // Calculate where the next page would start after moving the video block
          const proposedPageEnd = splitVideoBlock.topPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start.
          // The header position is absolute, so if it's at the same position as the video
          // start, they will overlap on the next page.
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            // Check if step header starts at or very close to where the video would start
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start.
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 20 &&
              substepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the video block,
          // we need to end the current page earlier so the video content does not get hidden
          // behind the Step/Substep title header.
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const videoStart = splitVideoBlock.topPx;

            // Add spacing after the header to ensure the video content doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // The video content will be hidden/overlapped if it starts at or before the
            // header bottom + spacing.
            const wouldOverlap = videoStart <= headerBottom + headerSpacing;

            // Also treat the case where the video is very close to the header (increased range)
            const isVeryClose =
              videoStart >= headerTop - 20 &&
              videoStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts.
              // This ensures that on the next page:
              // - The header is at the top of the page.
              // - The header renders first.
              // - The video content appears after the header instead of behind it.
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the video)
              if (
                adjustedPageEnd < videoStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          const proposed = splitVideoBlock.topPx - currentOffsetPx;
          if (proposed > 0) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        while (adjustForVideoImages()) {}

        const adjustForChildNodesNearHeaders = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          // Handle canvas blocks, form blocks and other child node types that might start at the top of a new page
          // Note: canvas images are handled by adjustForCanvasImages, video blocks with images by adjustForVideoImages
          const childNodeTypes = new Set(["canvas", "form"]);

          const splitChildBlock = blockMetasPx.find((meta) => {
            if (!childNodeTypes.has(meta.label)) return false;
            if (meta.topPx < pageStart) return false;
            if (meta.topPx >= pageEnd) return false;
            if (meta.bottomPx <= pageEnd) return false;
            // If the block is taller than the usable page height, allow it to split
            if (meta.heightPx >= availableHeightPx) return false;
            return true;
          });

          if (!splitChildBlock) return false;

          // Calculate where the next page would start after moving the child block
          const proposedPageEnd = splitChildBlock.topPx;
          const nextPageStart = proposedPageEnd;

          // Check if there's a step header that starts at or near the next page start.
          const stepHeaderOnNextPage = stepMetas.find((stepMeta) => {
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 10 &&
              stepMeta.headerTopPx <= nextPageStart + 50;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header that starts at or near the next page start.
          const substepHeaderOnNextPage = substepMetas.find((substepMeta) => {
            const headerStartsAtNextPage =
              substepMeta.headerTopPx >= nextPageStart - 10 &&
              substepMeta.headerTopPx <= nextPageStart + 50;
            return headerStartsAtNextPage;
          });

          // If there's a header on the next page that would overlap with the child block,
          // we need to end the current page earlier so the child content does not get hidden
          // behind the Step/Substep title header.
          if (stepHeaderOnNextPage || substepHeaderOnNextPage) {
            const headerMeta = stepHeaderOnNextPage || substepHeaderOnNextPage;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const childStart = splitChildBlock.topPx;

            // Add spacing after the header to ensure the child content doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // The child content will be hidden/overlapped if it starts at or before the
            // header bottom + spacing.
            const wouldOverlap = childStart <= headerBottom + headerSpacing;

            // Also treat the case where the child is very close to the header (increased range)
            const isVeryClose =
              childStart >= headerTop - 20 &&
              childStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts.
              // This ensures that on the next page:
              // - The header is at the top of the page.
              // - The header renders first.
              // - The child content appears after the header instead of behind it.
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the child)
              if (
                adjustedPageEnd < childStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          const proposed = splitChildBlock.topPx - currentOffsetPx;
          if (proposed > 0) {
            pageContentPx = proposed;
            return true;
          }

          return false;
        };

        while (adjustForChildNodesNearHeaders()) {}

        // Check for any child node that would start at the top of the next page where a header exists
        // This handles cases where content is already positioned at the next page start
        const adjustForChildNodesAtPageTop = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const nextPageStart = pageEnd;

          // Check all child node types
          const childNodeTypes = new Set([
            "canvas",
            "rich-text",
            "attachment",
            "video",
            "form",
          ]);

          // Find any child node that starts at or very close to the next page start
          const childNodeAtNextPageTop = blockMetasPx.find((meta) => {
            if (!childNodeTypes.has(meta.label)) return false;
            // Check if the child node starts at or very close to the next page start
            // Increased tolerance range for better detection
            const startsAtNextPageTop =
              meta.topPx >= nextPageStart - 20 &&
              meta.topPx <= nextPageStart + 100;
            return startsAtNextPageTop;
          });

          if (!childNodeAtNextPageTop) return false;

          // Check if there's a step header at the same position
          const stepHeaderAtSamePosition = stepMetas.find((stepMeta) => {
            // Increased tolerance range for better detection
            const headerStartsAtNextPage =
              stepMeta.headerTopPx >= nextPageStart - 20 &&
              stepMeta.headerTopPx <= nextPageStart + 100;
            return headerStartsAtNextPage;
          });

          // Check if there's a substep header at the same position
          const substepHeaderAtSamePosition = substepMetas.find(
            (substepMeta) => {
              // Increased tolerance range for better detection
              const headerStartsAtNextPage =
                substepMeta.headerTopPx >= nextPageStart - 20 &&
                substepMeta.headerTopPx <= nextPageStart + 100;
              return headerStartsAtNextPage;
            },
          );

          // If there's a header at the same position as the child node, adjust the page break
          if (stepHeaderAtSamePosition || substepHeaderAtSamePosition) {
            const headerMeta =
              stepHeaderAtSamePosition || substepHeaderAtSamePosition;
            const headerTop = headerMeta.headerTopPx;
            const headerHeight = headerMeta.headerHeightPx || 0;
            const headerBottom = headerTop + headerHeight;

            const childStart = childNodeAtNextPageTop.topPx;

            // Add spacing after the header to ensure the child content doesn't overlap
            const headerSpacing = 20; // 20px spacing after header for better visibility

            // Check if the child node would overlap with the header
            const wouldOverlap = childStart <= headerBottom + headerSpacing;
            const isVeryClose =
              childStart >= headerTop - 20 &&
              childStart <= headerBottom + headerSpacing;

            if (wouldOverlap || isVeryClose) {
              // End the current page before the header starts
              // This ensures the header renders first on the next page
              const adjustedPageEnd = Math.max(currentOffsetPx, headerTop - 10);

              // Only adjust if this makes sense (not ending too early, and before the child)
              if (
                adjustedPageEnd < childStart &&
                adjustedPageEnd > currentOffsetPx
              ) {
                const adjustedProposed = adjustedPageEnd - currentOffsetPx;
                if (adjustedProposed > 0 && adjustedProposed < pageContentPx) {
                  pageContentPx = adjustedProposed;
                  return true;
                }
              }
            }
          }

          return false;
        };

        while (adjustForChildNodesAtPageTop()) {}

        const adjustForTablesNearFooter = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const estimatedRowHeightPx = 35;
          const footerHeightPx = Math.floor(footerHeight / pixelToMm);
          const footerMarginPx = Math.max(0, Math.floor(footerHeightPx * 0.25));
          const baseMinSpaceFromFooterPx = footerMarginPx;
          const extraFooterMarginPx = Math.floor(8 / pixelToMm);

          const trackedTableIds = [
            revisionTableId,
            trainingLogTableId,
            bomTableId,
          ].filter((id) => id !== null);

          const getMinSpaceFromFooter = (tableId) => {
            if (trackedTableIds.includes(tableId)) {
              return baseMinSpaceFromFooterPx + extraFooterMarginPx;
            }
            return baseMinSpaceFromFooterPx;
          };

          const headerTooCloseTable = tableMetas.find((meta) => {
            if (!trackedTableIds.includes(meta.tableId)) return false;

            const tableStartsOnPage =
              meta.topPx >= pageStart && meta.topPx < pageEnd;
            if (!tableStartsOnPage) return false;

            const headerTopInPage = meta.headerTopPx - pageStart;
            if (headerTopInPage < 0 || headerTopInPage >= pageContentPx)
              return false;

            const minSpaceForThisTable = getMinSpaceFromFooter(meta.tableId);
            const remainingSpaceBelowHeader =
              pageContentPx - headerTopInPage - minSpaceForThisTable;

            const minRowsBelowHeaderPx = estimatedRowHeightPx * 3;
            return remainingSpaceBelowHeader < minRowsBelowHeaderPx;
          });

          if (headerTooCloseTable) {
            const headerTopInPage = headerTooCloseTable.headerTopPx - pageStart;
            const proposedPageContent = Math.max(0, headerTopInPage - 5);
            if (
              proposedPageContent > 0 &&
              proposedPageContent < pageContentPx
            ) {
              pageContentPx = Math.floor(proposedPageContent);
              return true;
            }
          }

          const tableTooClose = tableMetas.find((meta) => {
            const tableStartsOnPage =
              meta.topPx >= pageStart && meta.topPx < pageEnd;
            const tableContinuesOnPage =
              meta.topPx < pageStart && meta.bottomPx > pageStart;

            if (!tableStartsOnPage && !tableContinuesOnPage) return false;
            if (meta.bottomPx <= pageStart) return false;

            const tableBottomInPage = meta.bottomPx - pageStart;
            const minSpaceForThisTable = getMinSpaceFromFooter(meta.tableId);
            const safePageEnd = pageContentPx - minSpaceForThisTable;

            if (tableBottomInPage > safePageEnd) {
              return true;
            }

            return false;
          });

          if (!tableTooClose) return false;

          const tableBottomInPage = tableTooClose.bottomPx - pageStart;
          const tableTopInPage = Math.max(0, tableTooClose.topPx - pageStart);
          const minSpaceForThisTable = getMinSpaceFromFooter(
            tableTooClose.tableId,
          );

          const proposedPageContent = Math.max(
            tableTopInPage + (tableTooClose.headerCanvas?.height || 0),
            tableBottomInPage - minSpaceForThisTable,
          );

          const headerHeightPx = tableTooClose.headerCanvas?.height || 0;
          const minContentToShow = headerHeightPx + estimatedRowHeightPx * 2;

          if (
            proposedPageContent >= minContentToShow &&
            proposedPageContent < pageContentPx
          ) {
            pageContentPx = Math.floor(proposedPageContent);
            return true;
          }

          return false;
        };

        const adjustForStepHeaders = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const footerHeightPx = Math.floor(footerHeight / pixelToMm);
          const footerMarginPx = Math.max(0, Math.floor(footerHeightPx * 0.25));
          const minSpaceFromFooterPx = footerMarginPx;
          const safePageEndPx =
            pageStart + Math.max(0, pageContentPx - minSpaceFromFooterPx);
          // Safety buffer to prevent Section/Step/Substep headers from being cut by page break (rounding/clipping)
          const headerBreakSafetyPx = Math.max(
            100,
            Math.round(120 * domToCanvasScaleY),
          );

          // Check step headers
          const stepHeaderToMove = stepMetas.find((stepMeta) => {
            const isFirstStepOnFirstPage =
              pageStart === 0 &&
              keepFirstSectionOnPage1 &&
              firstStepHeaderTopPx != null &&
              Math.abs(stepMeta.headerTopPx - firstStepHeaderTopPx) <= 3;
            if (isFirstStepOnFirstPage) {
              // Avoid over-aggressive safety-buffer push for Step 1 on page 1.
              // If the full header band is still inside this page, do not move it.
              const firstStepHeaderBottomPx =
                stepMeta.headerTopPx + stepMeta.headerHeightPx;
              const firstStepHeaderTailSafetyPx = Math.max(
                8,
                Math.round(12 * domToCanvasScaleY),
              );
              if (firstStepHeaderBottomPx <= pageEnd - firstStepHeaderTailSafetyPx) {
                return false;
              }
            }
            // Check if step header overlaps this page (starts on page OR would be cut by page break)
            const headerOverlapsPage =
              stepMeta.headerTopPx < pageEnd &&
              stepMeta.headerTopPx + stepMeta.headerHeightPx > pageStart;
            if (!headerOverlapsPage) return false;

            // Add 15px to header bottom to account for rounding/measurement variance
            const headerBottomPx =
              stepMeta.headerTopPx +
              stepMeta.headerHeightPx +
              Math.round(15 * domToCanvasScaleY);
            // Move if: header would be split, or header bottom is within safety buffer of page end
            const headerWouldBeSplit =
              headerBottomPx > pageEnd - headerBreakSafetyPx;

            // If header would be split, we need to move it to next page
            if (headerWouldBeSplit) {
              return true;
            }

            // Also check if header is too close to footer
            const headerTopInPage = stepMeta.headerTopPx - pageStart;
            const headerBottomInPage = headerBottomPx - pageStart;
            const safePageEnd = pageContentPx - minSpaceFromFooterPx;

            if (headerBottomInPage > safePageEnd) {
              return true;
            }

            // Check if the step's NATIVE content — substeps physically inside this
            // step's container, and the item required table linked (or positionally
            // inside) this step — extends beyond the current page end.
            //
            // This catches the case where later adjustment passes (section-heading
            // pairs, canvas, attachments, etc.) shrink pageContentPx AFTER
            // adjustForItemRequiredTables already ran, leaving the step's core
            // content stranded on the next page while the header sits alone at the
            // bottom of the current page.
            //
            // NOTE: We intentionally do NOT look at generic child export blocks
            // (rich-text, attachment, canvas …) here — those are independent of the
            // step. We only look at substeps (part of the step structure) and the
            // item required table (intrinsic to the step).
            try {
              const nativeContentCandidates = [];

              // Substeps whose topPx falls inside this step's container range
              substepMetas.forEach((s) => {
                if (s.topPx > stepMeta.topPx && s.topPx <= stepMeta.bottomPx) {
                  nativeContentCandidates.push(s.bottomPx);
                }
              });

              // Item Required tables — matched by stepId (preferred) or by position
              tableMetas.forEach((t) => {
                if (t.isItemRequiredTable) {
                  const linkedToThisStep = t.stepId === stepMeta.stepId;
                  const positionedInStep =
                    t.topPx > stepMeta.topPx && t.topPx <= stepMeta.bottomPx;
                  if (linkedToThisStep || positionedInStep) {
                    nativeContentCandidates.push(t.bottomPx);
                  }
                }
              });
              if (
                stepMeta.stepShortDescBottomPx != null &&
                Number.isFinite(stepMeta.stepShortDescBottomPx)
              ) {
                nativeContentCandidates.push(stepMeta.stepShortDescBottomPx);
              }

              if (nativeContentCandidates.length > 0) {
                const nativeContentBottom = Math.max(
                  ...nativeContentCandidates,
                );
                if (nativeContentBottom > pageEnd) {
                  // Native content spills onto the next page.
                  // Only move the step if the full block (header → native bottom)
                  // can realistically fit on a single page; if it is taller than one
                  // page, splitting is unavoidable.
                  const nativeContentHeight =
                    nativeContentBottom - stepMeta.headerTopPx;
                  if (nativeContentHeight < availableHeightPx * 0.95) {
                    return true;
                  }
                }
              }
            } catch (e) {
              // best-effort — do not crash pagination if meta data is incomplete
            }

            return false;
          });

          // Check substep headers
          const substepHeaderToMove = substepMetas.find((substepMeta) => {
            // Check if substep header overlaps this page (starts on page OR would be cut by page break)
            const headerOverlapsPage =
              substepMeta.headerTopPx < pageEnd &&
              substepMeta.headerTopPx + substepMeta.headerHeightPx > pageStart;
            if (!headerOverlapsPage) return false;

            const substepChildLabels = new Set([
              "attachment",
              "rich-text",
              "video",
              "form",
              "canvas",
            ]);
            const childBottoms = blockMetasPx
              .filter(
                (m) =>
                  m.substepId === substepMeta.substepId &&
                  substepChildLabels.has(m.label),
              )
              .map((m) => m.bottomPx);
            const effectiveBottomPx =
              childBottoms.length > 0
                ? Math.max(substepMeta.bottomPx, ...childBottoms)
                : substepMeta.bottomPx;

            // No child export blocks: substep may only have header + short description — keep both on one page.
            if (childBottoms.length === 0) {
              const headerRowBottomPx =
                substepMeta.headerTopPx +
                substepMeta.headerHeightPx +
                Math.round(15 * domToCanvasScaleY);
              const descBottomForFit =
                substepMeta.substepShortDescBottomPx != null
                  ? substepMeta.substepShortDescBottomPx
                  : substepMeta.bottomPx;
              if (descBottomForFit > headerRowBottomPx + 1) {
                const safePageEnd = pageContentPx - minSpaceFromFooterPx;
                const descBottomInPage = descBottomForFit - pageStart;
                if (
                  substepMeta.headerTopPx >= pageStart &&
                  substepMeta.headerTopPx < pageEnd &&
                  descBottomInPage > safePageEnd
                ) {
                  return true;
                }
              }
            }

            if (effectiveBottomPx <= pageEnd) return false;

            // Add 15px to header bottom to account for rounding/measurement variance
            const headerBottomPx =
              substepMeta.headerTopPx +
              substepMeta.headerHeightPx +
              Math.round(15 * domToCanvasScaleY);
            // Move if: header would be split, or header bottom is within safety buffer of page end
            const headerWouldBeSplit =
              headerBottomPx > pageEnd - headerBreakSafetyPx;

            // If header would be split, we need to move it to next page
            if (headerWouldBeSplit) {
              return true;
            }

            // Also check if header is too close to footer
            const headerTopInPage = substepMeta.headerTopPx - pageStart;
            const headerBottomInPage = headerBottomPx - pageStart;
            const safePageEnd = pageContentPx - minSpaceFromFooterPx;

            if (headerBottomInPage > safePageEnd) {
              return true;
            }

            // Check if substep has child nodes - if so, ensure header + description + first child fit together
            const substepChildLabelsForFit = new Set([
              "attachment",
              "rich-text",
              "video",
              "form",
              "canvas",
            ]);

            const substepChildElements = blockMetasPx.filter((meta) => {
              if (!substepChildLabelsForFit.has(meta.label)) return false;
              return meta.substepId === substepMeta.substepId;
            });

            if (substepChildElements.length > 0) {
              // Find the first child node (lowest topPx)
              const firstChild = substepChildElements.reduce(
                (first, current) => {
                  return current.topPx < first.topPx ? current : first;
                },
              );

              // Get the description area - it's between the header bottom and the first child
              // The description is typically the content between hr and first child node
              const headerBottom =
                substepMeta.headerTopPx + substepMeta.headerHeightPx;
              const firstChildTop = firstChild.topPx;

              // Calculate the total height needed: header + description area + first child
              // Description area is from header bottom to first child top
              const descriptionAreaHeight = Math.max(
                0,
                firstChildTop - headerBottom,
              );
              const firstChildHeight =
                firstChild.heightPx || firstChild.bottomPx - firstChild.topPx;
              const totalRequiredHeight =
                substepMeta.headerHeightPx +
                descriptionAreaHeight +
                firstChildHeight;

              // Check if all of this fits on the current page
              const headerTopInPageForCheck =
                substepMeta.headerTopPx - pageStart;
              const requiredBottomInPage =
                headerTopInPageForCheck + totalRequiredHeight;

              // If the required content doesn't fit on this page, move to next page
              if (requiredBottomInPage > safePageEnd) {
                return true;
              }

              // Also check if first child would be split across pages
              const firstChildBottom = firstChild.bottomPx;
              if (firstChildBottom > pageEnd) {
                // Check if we can fit header + description + at least part of first child
                // If not, move the whole substep to next page
                const minFirstChildHeight = Math.min(firstChildHeight, 50); // At least 50px of first child
                const minRequiredHeight =
                  substepMeta.headerHeightPx +
                  descriptionAreaHeight +
                  minFirstChildHeight;
                if (headerTopInPageForCheck + minRequiredHeight > safePageEnd) {
                  return true;
                }
              }
            }

            return false;
          });

          // Prioritize step headers over substep headers if both need to move
          const headerToMove = stepHeaderToMove || substepHeaderToMove;
          if (!headerToMove) return false;

          // Adjust page break to move header to next page (with buffer to ensure clean break, no cutting)
          const proposed = headerToMove.headerTopPx - currentOffsetPx;
          const bufferPx = Math.max(25, Math.round(30 * domToCanvasScaleY));
          const proposedWithBuffer = Math.max(0, proposed - bufferPx);
          if (proposedWithBuffer > 0 && proposedWithBuffer < pageContentPx) {
            pageContentPx = Math.floor(proposedWithBuffer);
            return true;
          }

          return false;
        };

        if (!isZipExport) {
          while (adjustForStepHeaders()) {}
        }

        // Move entire substep to next page if it would be cut (and can fit on one page).
        // This prevents a substep from being split across pages mid-content.
        const adjustForSubstepBlocks = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const substepToMove = substepMetas.find((substepMeta) => {
            const headerStartsOnPage =
              substepMeta.headerTopPx >= pageStart &&
              substepMeta.headerTopPx < pageEnd;
            if (!headerStartsOnPage) return false;

            // Keep Substep Header and Short description together: if the page would cut between
            // the header and the end of the short description, move the whole substep to the next page.
            try {
              const headerBottomPx =
                substepMeta.headerTopPx + (substepMeta.headerHeightPx || 0);

              const childLabelsForDesc = new Set([
                "attachment",
                "rich-text",
                "video",
                "form",
                "canvas",
              ]);
              const substepChildrenForDesc = blockMetasPx
                .filter((m) => {
                  if (!childLabelsForDesc.has(m.label)) return false;
                  return m.substepId === substepMeta.substepId;
                })
                .sort((a, b) => a.topPx - b.topPx);

              // Header + short description block ends at: explicit shortDesc bottom when available,
              // otherwise first child top or substep bottom.
              const firstChildTopPx =
                substepChildrenForDesc.length > 0
                  ? substepChildrenForDesc[0].topPx
                  : substepMeta.bottomPx;
              const headerAndShortDescBottomPx =
                substepMeta.substepShortDescBottomPx != null
                  ? substepMeta.substepShortDescBottomPx
                  : firstChildTopPx;

              // If the page end falls inside the header+shortDesc region, move the whole substep.
              // This ensures Substep Header and Short description are never split across pages.
              const headerShortDescRegionExists =
                headerAndShortDescBottomPx > headerBottomPx + 1;
              if (headerShortDescRegionExists) {
                const actualPageEnd = pageStart + pageContentPx;
                const pageCutsHeaderOrShortDesc =
                  actualPageEnd > substepMeta.headerTopPx &&
                  actualPageEnd < headerAndShortDescBottomPx;
                if (pageCutsHeaderOrShortDesc) return true;
                // Only move if header+shortdesc extends past the actual page slice — not merely
                // past a conservative footer margin while the block still fits on the page.
                if (headerAndShortDescBottomPx > actualPageEnd) {
                  return true;
                }
              }
              // Fallback: if page cuts the description region (header bottom to first child)
              // when we don't have explicit shortDesc bounds.
              const descRegionExists = firstChildTopPx > headerBottomPx + 1;
              if (
                descRegionExists &&
                substepMeta.substepShortDescBottomPx == null
              ) {
                const actualPageEnd = pageStart + pageContentPx;
                const pageCutsDescription =
                  actualPageEnd > headerBottomPx &&
                  actualPageEnd < firstChildTopPx;
                if (pageCutsDescription) return true;
              }
            } catch (e) {
              // best-effort
            }

            // Compute the effective bottom of the substep including its child nodes.
            // `substepMeta.bottomPx` can under-report when child export blocks extend beyond
            // the container's bounding box.
            const childLabels = new Set([
              "attachment",
              "rich-text",
              "video",
              "form",
              "canvas",
            ]);
            const substepChildren = blockMetasPx.filter((m) => {
              if (!childLabels.has(m.label)) return false;
              return m.substepId === substepMeta.substepId;
            });
            const childrenBottomPx =
              substepChildren.length > 0
                ? Math.max(...substepChildren.map((m) => m.bottomPx))
                : substepMeta.bottomPx;
            const effectiveBottomPx = Math.max(
              substepMeta.bottomPx,
              childrenBottomPx,
            );

            // If the whole substep fits on this page, no need to move.
            if (effectiveBottomPx <= pageEnd) return false;

            const substepHeightPx = Math.max(
              0,
              effectiveBottomPx - substepMeta.headerTopPx,
            );
            // If the substep is too tall to fit on one page, allow it to split.
            if (substepHeightPx >= availableHeightPx * 0.95) return false;

            return true;
          });

          if (!substepToMove) return false;

          // IMPORTANT: Use headerTopPx (not topPx). The substep container can start earlier
          // due to tree-path/wrapper content; moving by topPx may result in a non-positive
          // proposal and no adjustment.
          const proposed = substepToMove.headerTopPx - currentOffsetPx;
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = Math.floor(proposed);
            return true;
          }

          return false;
        };

        if (!isZipExport) {
          while (adjustForSubstepBlocks()) {}
        }

        // Re-run step header adjustments after substep positions are finalised.
        // adjustForSubstepBlocks may have moved a substep to the next page, leaving
        // the parent step header stranded on the current page. Running this pass now
        // catches that via the native-content spill-over check added above.
        if (!isZipExport) {
          while (adjustForStepHeaders()) {}
        }

        // Re-run attachment-related adjustments after all other pagination tweaks.
        // This ensures that later changes (like table/footer or header adjustments)
        // do not accidentally cause attachment blocks or their images to be split
        // across pages when they could fit on a single page.
        if (!isZipExport) {
          while (adjustForAttachmentImageSlices()) {}
        }

        if (!isZipExport) {
          while (adjustForAttachmentImages()) {}
        }

        // When Attachment is immediately followed by Rich Text, Video, Canvas, or Attachment, ensure min 55px gap to prevent merging
        const adjustForAttachmentRichTextGap = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const minGapPx = 55;
          const blockTypesAfterAttachment = [
            "rich-text",
            "video",
            "canvas",
            "attachment",
          ];
          const blocksAfterAttachment = blockMetasPx.filter((m) =>
            blockTypesAfterAttachment.includes(m.label),
          );
          for (const block of blocksAfterAttachment) {
            const attBefore = blockMetasPx
              .filter(
                (m) => m.label === "attachment" && m.bottomPx <= block.topPx,
              )
              .sort((a, b) => b.bottomPx - a.bottomPx)[0];
            if (!attBefore) continue;
            const gap = block.topPx - attBefore.bottomPx;
            if (gap >= minGapPx) continue;
            const bothOnPage =
              attBefore.topPx < pageEnd && block.bottomPx > pageStart;
            if (!bothOnPage) continue;
            const proposed = Math.max(
              0,
              block.topPx - currentOffsetPx - minGapPx,
            );
            if (proposed > 0 && proposed < pageContentPx) {
              pageContentPx = Math.floor(proposed);
              return true;
            }
          }
          return false;
        };
        if (!isZipExport) {
          while (adjustForAttachmentRichTextGap()) {}
        }

        const maxSafeContentPx =
          availableHeightPx - headerInsertPx - tableBottomMarginPx;
        if (pageContentPx > maxSafeContentPx) {
          pageContentPx = Math.max(0, maxSafeContentPx);
        }

        // FINAL SAFETY PASS: by this point many rules may have adjusted `pageContentPx`
        // (step headers, attachments, canvas/rich text). Re-apply the Section keep-together
        // rule at the end so it cannot be overridden by later pagination tweaks.
        // If this changes the break, re-run the most sensitive adjustments once more.
        if (!isZipExport) {
          for (
            let _sectionKeepIters = 0;
            _sectionKeepIters < 3;
            _sectionKeepIters += 1
          ) {
            let changed = false;
            while (adjustForSectionHeadingPairs()) {
              changed = true;
            }
            while (adjustForSectionHeadingsOnly()) {
              changed = true;
            }
            if (!changed) break;

            while (adjustForStepHeaders()) {
              // Re-stabilize step/substep headers after moving content to next page.
            }
            while (adjustForAttachmentImageSlices()) {
              // Re-stabilize attachment image slicing after break changes.
            }
            while (adjustForAttachmentImages()) {
              // Re-stabilize attachment blocks after break changes.
            }
            while (adjustForAttachmentRichTextGap()) {
              // Re-stabilize attachment+rich-text gap after break changes.
            }

            const maxSafeContentPx2 =
              availableHeightPx - headerInsertPx - tableBottomMarginPx;
            if (pageContentPx > maxSafeContentPx2) {
              pageContentPx = Math.max(0, maxSafeContentPx2);
            }
          }
        }

        // Hard rule: page slice end must not fall inside the substep header band (title line).
        // If it would, end this page just before the band so the entire band prints on the next slice.
        const adjustForSubstepHeaderBandSlice = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const guardPx = Math.max(12, Math.round(20 * domToCanvasScaleY));
          for (const sm of substepMetas) {
            if (!sm.headerCanvas || sm.headerHeightPx <= 0) continue;
            const bandTop = sm.headerTopPx - guardPx;
            const bandBottom = sm.headerTopPx + sm.headerHeightPx + guardPx;
            const sliceWouldCutThroughBand =
              pageStart < bandTop && pageEnd > bandTop && pageEnd < bandBottom;
            if (sliceWouldCutThroughBand) {
              const proposed = Math.floor(bandTop - pageStart);
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }
          return false;
        };
        if (!isZipExport) {
          while (adjustForSubstepHeaderBandSlice()) {}
        }

        const maxSafeContentPxAfterSubstepBand =
          availableHeightPx - headerInsertPx - tableBottomMarginPx;
        if (pageContentPx > maxSafeContentPxAfterSubstepBand) {
          pageContentPx = Math.max(0, maxSafeContentPxAfterSubstepBand);
        }

        // Hard rule: slice must not cut through Training Log title + black thead (same idea as Section band).
        // If the slice would land inside the band, prefer extending the page to include the full band (or
        // the whole table) when it fits — shrinking to bandTop alone leaves a tiny canvas strip that scales
        // to a nearly blank PDF page with the Training log wrongly pushed to the next page.
        const adjustForTrainingLogHeaderBandSlice = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const guardPx = trainingLogHeaderBandGuardPx;
          const maxPagePx = Math.min(
            totalHeightPx - pageStart,
            availableHeightPx - headerInsertPx - tableBottomMarginPx,
          );
          const tableTailPx = Math.round(8 * domToCanvasScaleY);
          for (const tm of tableMetas) {
            if (
              !tm.isTrainingLogTable ||
              !tm.headerHeightPx ||
              tm.headerHeightPx <= 0
            )
              continue;
            const bandTop = tm.headerTopPx - guardPx;
            const bandBottom = tm.headerTopPx + tm.headerHeightPx + guardPx;
            const sliceEndInsideBand =
              pageEnd > bandTop && pageEnd < bandBottom;
            if (!sliceEndInsideBand) continue;
            if (pageStart < bandTop) {
              const fullTableEndPx = tm.bottomPx + tableTailPx;
              const extendToBandBottomPx = Math.floor(bandBottom - pageStart);
              const extendToFullTablePx = Math.floor(
                fullTableEndPx - pageStart,
              );
              const extendPx = Math.max(
                extendToBandBottomPx,
                extendToFullTablePx,
              );
              const shrinkBeforeBandPx = Math.floor(bandTop - pageStart);
              if (extendPx > 0 && extendPx <= maxPagePx) {
                pageContentPx = extendPx;
                return true;
              }
              if (
                shrinkBeforeBandPx > 0 &&
                shrinkBeforeBandPx < pageContentPx
              ) {
                pageContentPx = shrinkBeforeBandPx;
                return true;
              }
            } else if (pageStart >= bandTop && pageStart < bandBottom) {
              const proposed = Math.floor(bandBottom - pageStart);
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }
          return false;
        };
        while (adjustForTrainingLogHeaderBandSlice()) {}

        const maxSafeContentPxAfterTrainingLogBand =
          availableHeightPx - headerInsertPx - tableBottomMarginPx;
        if (pageContentPx > maxSafeContentPxAfterTrainingLogBand) {
          pageContentPx = Math.max(0, maxSafeContentPxAfterTrainingLogBand);
        }

        // BOM title + black thead — same extend-or-shrink rule as Training log.
        const adjustForBomHeaderBandSlice = () => {
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const guardPx = bomHeaderBandGuardPx;
          const maxPagePx = Math.min(
            totalHeightPx - pageStart,
            availableHeightPx - headerInsertPx - tableBottomMarginPx,
          );
          const tableTailPx = Math.round(8 * domToCanvasScaleY);
          for (const tm of tableMetas) {
            if (!tm.isBomTable || !tm.headerHeightPx || tm.headerHeightPx <= 0)
              continue;
            const bandTop = tm.headerTopPx - guardPx;
            const bandBottom = tm.headerTopPx + tm.headerHeightPx + guardPx;
            const sliceEndInsideBand =
              pageEnd > bandTop && pageEnd < bandBottom;
            if (!sliceEndInsideBand) continue;
            if (pageStart < bandTop) {
              const fullTableEndPx = tm.bottomPx + tableTailPx;
              const extendToBandBottomPx = Math.floor(bandBottom - pageStart);
              const extendToFullTablePx = Math.floor(
                fullTableEndPx - pageStart,
              );
              const extendPx = Math.max(
                extendToBandBottomPx,
                extendToFullTablePx,
              );
              const shrinkBeforeBandPx = Math.floor(bandTop - pageStart);
              if (extendPx > 0 && extendPx <= maxPagePx) {
                pageContentPx = extendPx;
                return true;
              }
              if (
                shrinkBeforeBandPx > 0 &&
                shrinkBeforeBandPx < pageContentPx
              ) {
                pageContentPx = shrinkBeforeBandPx;
                return true;
              }
            } else if (pageStart >= bandTop && pageStart < bandBottom) {
              const proposed = Math.floor(bandBottom - pageStart);
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }
          return false;
        };
        while (adjustForBomHeaderBandSlice()) {}

        const maxSafeContentPxAfterBomBand =
          availableHeightPx - headerInsertPx - tableBottomMarginPx;
        if (pageContentPx > maxSafeContentPxAfterBomBand) {
          pageContentPx = Math.max(0, maxSafeContentPxAfterBomBand);
        }

        // Same hard slice rule for "Section N" bands (title row + hr) — avoid bisecting the header in canvas.
        const adjustForSectionHeaderBandSlice = () => {
          if (disableSectionHeadingPush) return false;
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          if (!sectionHeadingOnlyPx || sectionHeadingOnlyPx.length === 0)
            return false;
          for (const sh of sectionHeadingOnlyPx) {
            if (
              pageStart === 0 &&
              keepFirstSectionOnPage1 &&
              Math.abs(sh.headingTopPx - firstSectionTopAfterBomPx) <= 3
            ) {
              continue;
            }
            const bandTop = sh.headingTopPx - sectionHeadingBandGuardPx;
            const bandBottom = sh.headingBottomPx + sectionHeadingBandGuardPx;
            const sliceEndInsideBand =
              pageEnd > bandTop && pageEnd < bandBottom;
            if (!sliceEndInsideBand) continue;
            if (pageStart < bandTop) {
              const proposed = Math.floor(bandTop - pageStart);
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            } else if (pageStart >= bandTop && pageStart < bandBottom) {
              const proposed = Math.floor(bandBottom - pageStart);
              if (proposed > 0 && proposed < pageContentPx) {
                pageContentPx = proposed;
                return true;
              }
            }
          }
          return false;
        };

        const maxSafeContentPxAfterSectionBand =
          availableHeightPx - headerInsertPx - tableBottomMarginPx;
        if (!isZipExport) {
          for (
            let _sectionBandStable = 0;
            _sectionBandStable < 8;
            _sectionBandStable++
          ) {
            let changed = false;
            while (adjustForSectionHeaderBandSlice()) {
              changed = true;
            }
            if (pageContentPx > maxSafeContentPxAfterSectionBand) {
              pageContentPx = Math.max(0, maxSafeContentPxAfterSectionBand);
            }
            while (adjustForSectionHeadingsOnly()) {
              changed = true;
            }
            if (pageContentPx > maxSafeContentPxAfterSectionBand) {
              pageContentPx = Math.max(0, maxSafeContentPxAfterSectionBand);
            }
            if (!changed) break;
          }
        }

        // Must run *after* adjustForSectionHeadingsOnly / section-band passes: those shrink
        // pageContentPx to push Section 1 to the next page but leave a tall blank below the BOM
        // unless we snap the slice to just past the BOM bottom again.
        if (firstSectionTopAfterBomPx != null && bomEndCanvasPx != null) {
          const pageEndCandidate = currentOffsetPx + pageContentPx;
          const bomBot = bomEndCanvasPx;
          const secTop = firstSectionTopAfterBomPx;
          const hasMeaningfulGap =
            bomToFirstSectionGapPx != null &&
            bomToFirstSectionGapPx >= minBomToSectionTrimGapPx;
          if (
            hasMeaningfulGap &&
            bomBot > currentOffsetPx &&
            bomBot < pageEndCandidate &&
            secTop >= pageEndCandidate - 0.5
          ) {
            const snap = Math.floor(
              bomBot - currentOffsetPx + padAfterBomTailPx,
            );
            if (snap > 0 && snap < pageContentPx) {
              pageContentPx = snap;
            }
          }
        }

        // ZIP-only guard: do not cut a step short description across page boundary.
        if (isZipExport) {
          const stepDescCutMeta = stepMetas.find((stepMeta) => {
            if (
              stepMeta.stepShortDescTopPx == null ||
              stepMeta.stepShortDescBottomPx == null
            ) {
              return false;
            }
            const pageStart = currentOffsetPx;
            const pageEnd = currentOffsetPx + pageContentPx;
            const headerOnPage =
              stepMeta.headerTopPx >= pageStart && stepMeta.headerTopPx < pageEnd;
            if (!headerOnPage) return false;
            const descStartsOnPage =
              stepMeta.stepShortDescTopPx >= pageStart &&
              stepMeta.stepShortDescTopPx < pageEnd;
            const descWouldBeCut = stepMeta.stepShortDescBottomPx > pageEnd;
            if (!descStartsOnPage || !descWouldBeCut) return false;
            const headerPlusDescHeight =
              stepMeta.stepShortDescBottomPx - stepMeta.headerTopPx;
            if (headerPlusDescHeight >= availableHeightPx * 0.95) return false;
            return true;
          });
          if (stepDescCutMeta) {
            let proposed = Math.floor(
              stepDescCutMeta.headerTopPx - currentOffsetPx,
            );
            // If header already sits at page top, break before description start.
            if (proposed <= 0) {
              proposed = Math.floor(
                stepDescCutMeta.stepShortDescTopPx - currentOffsetPx - 4,
              );
            }
            if (proposed > 0 && proposed < pageContentPx) {
              pageContentPx = proposed;
            }
          }
        }

        // Keep canvas short descriptions intact on a single page slice.
        // If a page would end inside a canvas short-description band, break before it
        // so the description starts cleanly on the next page with its canvas content.
        const canvasShortDescCutMeta = blockMetasPx.find((meta) => {
          if (meta.label !== "canvas") return false;
          if (!meta.includeInPagination) return false;
          if (
            !Number.isFinite(meta.canvasShortDescTopPx) ||
            !Number.isFinite(meta.canvasShortDescBottomPx)
          ) {
            return false;
          }
          const shortTop = meta.canvasShortDescTopPx;
          const shortBottom = meta.canvasShortDescBottomPx;
          if (shortBottom <= shortTop) return false;
          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;
          const startsOnPage = shortTop >= pageStart && shortTop < pageEnd;
          const isCut = shortBottom > pageEnd;
          return startsOnPage && isCut;
        });
        if (canvasShortDescCutMeta) {
          const proposed = Math.floor(
            canvasShortDescCutMeta.canvasShortDescTopPx - currentOffsetPx,
          );
          if (proposed > 0 && proposed < pageContentPx) {
            pageContentPx = proposed;
          }
        }

        const pageEndPx = pageStartPx + pageContentPx;
        const canvasBlocksIntersectingPage = blockMetasPx
          .filter(
            (meta) =>
              meta.label === "canvas" &&
              meta.includeInPagination &&
              meta.bottomPx > pageStartPx &&
              meta.topPx < pageEndPx,
          )
          .map((meta) => ({
            stepId: meta.stepId,
            substepId: meta.substepId,
            topPx: meta.topPx,
            bottomPx: meta.bottomPx,
            shortDescTopPx: meta.canvasShortDescTopPx,
            shortDescBottomPx: meta.canvasShortDescBottomPx,
            contentTopPx: meta.canvasContentTopPx,
            cutByPageEnd:
              Number.isFinite(meta.canvasContentTopPx) &&
              meta.canvasContentTopPx < pageEndPx &&
              meta.bottomPx > pageEndPx,
          }));
        const canvasImagesIntersectingPage = canvasImageMetasPx
          .filter(
            (meta) =>
              meta.includeInPagination &&
              meta.bottomPx > pageStartPx &&
              meta.topPx < pageEndPx,
          )
          .map((meta) => ({
            topPx: meta.topPx,
            bottomPx: meta.bottomPx,
            heightPx: meta.heightPx,
            cutByPageEnd: meta.topPx < pageEndPx && meta.bottomPx > pageEndPx,
          }));
        if (
          canvasBlocksIntersectingPage.length > 0 ||
          canvasImagesIntersectingPage.length > 0
        ) {
        }
        const tablesOnPageSet = new Set();
        tableMetas.forEach((meta) => {
          if (meta.bottomPx > pageStartPx && meta.topPx < pageEndPx) {
            tablesOnPageSet.add(meta.tableId);
          }
        });
        const tablesOnPage = Array.from(tablesOnPageSet);

        const pageIndexForTracking = pageImages.length;
        const allTrackedTableIds = [
          revisionTableId,
          trainingLogTableId,
          bomTableId,
        ].filter((id) => id !== null);

        allTrackedTableIds.forEach((tableId) => {
          if (tablesOnPage.includes(tableId)) {
            if (!multiPageTables.has(tableId)) {
              multiPageTables.set(tableId, []);
            }
            multiPageTables.get(tableId).push(pageIndexForTracking);
          }
        });

        const childBlockLabels = new Set([
          "attachment",
          "rich-text",
          "video",
          "form",
          "canvas",
        ]);

        const childElementsOnPage = blockMetasPx.filter((meta) => {
          if (!childBlockLabels.has(meta.label)) return false;
          if (meta.stepId === null || meta.stepId === undefined) return false;
          // Step continuation counters should consider only direct step children.
          if (meta.substepId !== null && meta.substepId !== undefined)
            return false;
          return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
        });

        const stepsOnThisPage = new Set();
        childElementsOnPage.forEach((meta) => {
          if (meta.stepId !== null && meta.stepId !== undefined) {
            stepsOnThisPage.add(meta.stepId);
          }
        });

        stepMetas.forEach((stepMeta) => {
          const headerBottomPx = stepMeta.headerTopPx + stepMeta.headerHeightPx;
          const headerOverlapsPage =
            headerBottomPx > pageStartPx && stepMeta.headerTopPx < pageEndPx;

          // Check if step has child elements anywhere (not just on this page)
          // Include Item Required tables as child elements
          const hasChildElements =
            blockMetasPx.some((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              if (meta.substepId !== null && meta.substepId !== undefined)
                return false;
              return meta.stepId === stepMeta.stepId;
            }) ||
            tableMetas.some((tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              if (tableMeta.stepId === stepMeta.stepId) return true;
              if (
                tableMeta.stepContainer &&
                stepMeta.element &&
                tableMeta.stepContainer === stepMeta.element
              )
                return true;
              return false;
            }) ||
            (stepMeta.stepShortDescBottomPx != null &&
              stepMeta.stepShortDescTopPx != null &&
              Number.isFinite(stepMeta.stepShortDescBottomPx) &&
              Number.isFinite(stepMeta.stepShortDescTopPx) &&
              stepMeta.stepShortDescBottomPx > stepMeta.stepShortDescTopPx);

          // Check if step has child elements that extend to this page
          // Include Item Required tables as child elements
          const hasChildElementsOnThisPage =
            blockMetasPx.some((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              if (meta.substepId !== null && meta.substepId !== undefined)
                return false;
              if (meta.stepId !== stepMeta.stepId) return false;
              // Child element overlaps this page
              return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
            }) ||
            tableMetas.some((tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              // Check if table belongs to this step
              const belongsToStep =
                tableMeta.stepId === stepMeta.stepId ||
                (tableMeta.stepContainer &&
                  stepMeta.element &&
                  tableMeta.stepContainer === stepMeta.element);
              if (!belongsToStep) return false;
              // Table overlaps this page
              return (
                tableMeta.bottomPx > pageStartPx && tableMeta.topPx < pageEndPx
              );
            }) ||
            (stepMeta.stepShortDescBottomPx != null &&
              stepMeta.stepShortDescTopPx != null &&
              stepMeta.stepShortDescBottomPx > pageStartPx &&
              stepMeta.stepShortDescTopPx < pageEndPx);

          // Track step on this page if:
          // 1. Header overlaps page AND step has content (handles header-only pages)
          // 2. OR child elements overlap this page (handles content-only pages)
          if (
            (headerOverlapsPage && hasChildElements) ||
            hasChildElementsOnThisPage
          ) {
            stepsOnThisPage.add(stepMeta.stepId);
          }
        });

        stepsOnThisPage.forEach((stepId) => {
          if (!multiPageSteps.has(stepId)) {
            multiPageSteps.set(stepId, []);
          }
          const pageIndices = multiPageSteps.get(stepId);
          if (!pageIndices.includes(pageIndexForTracking)) {
            pageIndices.push(pageIndexForTracking);
          }
        });

        // Track steps that have Item Required tables spanning pages
        // Item Required tables are children of Steps, so we need to track their parent steps
        tableMetas.forEach((tableMeta) => {
          if (tableMeta.isItemRequiredTable) {
            // Find the parent step - use stepId if available, otherwise find by stepContainer
            let parentStepId = tableMeta.stepId;
            if (!parentStepId && tableMeta.stepContainer) {
              const parentStep = stepMetas.find((stepMeta) => {
                return stepMeta.element === tableMeta.stepContainer;
              });
              if (parentStep) {
                parentStepId = parentStep.stepId;
              }
            }

            if (parentStepId !== null && parentStepId !== undefined) {
              // Check if this Item Required table is on this page or continues from previous page
              const tableOnPage =
                tableMeta.bottomPx > pageStartPx && tableMeta.topPx < pageEndPx;
              const tableContinues =
                tableMeta.topPx < pageStartPx &&
                tableMeta.bottomPx > pageStartPx;

              if (tableOnPage || tableContinues) {
                // Add the step to multiPageSteps if not already tracked
                if (!multiPageSteps.has(parentStepId)) {
                  multiPageSteps.set(parentStepId, []);
                }
                const stepPageIndices = multiPageSteps.get(parentStepId);
                if (!stepPageIndices.includes(pageIndexForTracking)) {
                  stepPageIndices.push(pageIndexForTracking);
                }
              }
            }
          }
        });

        if (stepsOnThisPage.size > 0) {
          stepsOnThisPage.forEach((stepId) => {
            const pageIndices = multiPageSteps.get(stepId) || [];
            const stepMeta = stepMetas.find((s) => s.stepId === stepId);
            const stepChildElements = blockMetasPx.filter((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              // Exclude child nodes that belong to substeps - they are not direct children of the step
              if (meta.substepId !== null && meta.substepId !== undefined)
                return false;
              return meta.stepId === stepId;
            });
            const childRanges = stepChildElements.map((m) => ({
              top: m.topPx,
              bottom: m.bottomPx,
              label: m.label,
            }));
          });
        }

        const childElementsOnPageForSubsteps = blockMetasPx.filter((meta) => {
          if (!childBlockLabels.has(meta.label)) return false;
          if (meta.substepId === null || meta.substepId === undefined)
            return false;
          return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
        });

        const substepsOnThisPage = new Set();
        childElementsOnPageForSubsteps.forEach((meta) => {
          if (meta.substepId !== null && meta.substepId !== undefined) {
            substepsOnThisPage.add(meta.substepId);
          }
        });

        substepMetas.forEach((substepMeta) => {
          const headerBottomPx =
            substepMeta.headerTopPx + substepMeta.headerHeightPx;
          const headerOverlapsPage =
            headerBottomPx > pageStartPx && substepMeta.headerTopPx < pageEndPx;

          if (headerOverlapsPage) {
            const hasChildElements = blockMetasPx.some((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              return meta.substepId === substepMeta.substepId;
            });

            if (hasChildElements) {
              substepsOnThisPage.add(substepMeta.substepId);
            }
          }
        });

        substepsOnThisPage.forEach((substepId) => {
          if (!multiPageSubsteps.has(substepId)) {
            multiPageSubsteps.set(substepId, []);
          }
          const pageIndices = multiPageSubsteps.get(substepId);
          if (!pageIndices.includes(pageIndexForTracking)) {
            pageIndices.push(pageIndexForTracking);
          }
        });

        // Helper function to check if a table is an Item Required table (redefined in this scope)
        const isItemRequiredTableOnPage = (meta) => {
          if (!meta || !meta.element) return false;
          const table = meta.element;

          // Check if table has "Where Mentioned" in ANY th
          const thead = table.querySelector("thead");
          if (thead) {
            const allThs = thead.querySelectorAll("th");
            for (const th of allThs) {
              const thText = (th.textContent || "").trim();
              if (thText.includes("Where Mentioned")) {
                return true;
              }
            }
          }

          // Also check tbody for "Where Mentioned"
          const tbody = table.querySelector("tbody");
          if (tbody) {
            const firstRow = tbody.querySelector("tr");
            if (firstRow) {
              const firstCell = firstRow.querySelector("td, th");
              if (
                firstCell &&
                (firstCell.textContent || "").includes("Where Mentioned")
              ) {
                return true;
              }
            }
          }

          // Check table text content
          const tableText = (table.textContent || "").toLowerCase();
          if (
            tableText.includes("where mentioned") &&
            !table.closest("[data-export-block='revision']") &&
            !table.closest("[data-export-block='signature-history']")
          ) {
            return true;
          }

          // Check if table follows a heading with "item required" or "parts required"
          let prevSibling = table.previousElementSibling;
          let checkDepth = 0;
          while (prevSibling && checkDepth < 10) {
            if (prevSibling.tagName && /^H[1-6]$/i.test(prevSibling.tagName)) {
              const headingText = (prevSibling.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText.includes("item required") ||
                headingText.includes("items required") ||
                headingText.includes("parts required")
              ) {
                return true;
              }
            }
            if (prevSibling.querySelector) {
              const headingInParent = prevSibling.querySelector(
                "h1, h2, h3, h4, h5, h6",
              );
              if (headingInParent) {
                const headingText = (headingInParent.textContent || "")
                  .trim()
                  .toLowerCase();
                if (
                  headingText.includes("item required") ||
                  headingText.includes("items required") ||
                  headingText.includes("parts required")
                ) {
                  return true;
                }
              }
            }
            prevSibling = prevSibling.previousElementSibling;
            checkDepth++;
          }

          // Check in parent containers
          if (table.parentElement) {
            const parent = table.parentElement;
            const headingInParent = parent.querySelector(
              "h1, h2, h3, h4, h5, h6",
            );
            if (headingInParent) {
              const headingText = (headingInParent.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText.includes("item required") ||
                headingText.includes("items required") ||
                headingText.includes("parts required")
              ) {
                const allChildren = Array.from(parent.children);
                const headingIndex = allChildren.indexOf(headingInParent);
                const tableIndex = allChildren.indexOf(table);
                if (
                  headingIndex >= 0 &&
                  tableIndex >= 0 &&
                  headingIndex < tableIndex
                ) {
                  return true;
                }
              }
            }
          }

          // Check in step/rich-text containers
          const stepContainer = table.closest(
            "[data-export-block='step'], [data-export-block='substep'], [data-export-block='rich-text'], [data-export-block='section']",
          );
          if (stepContainer) {
            const headingInContainer = stepContainer.querySelector(
              "h1, h2, h3, h4, h5, h6",
            );
            if (headingInContainer) {
              const headingText = (headingInContainer.textContent || "")
                .trim()
                .toLowerCase();
              if (
                headingText.includes("item required") ||
                headingText.includes("items required") ||
                headingText.includes("parts required")
              ) {
                const allElements = Array.from(
                  stepContainer.querySelectorAll("*"),
                );
                const headingPos = allElements.indexOf(headingInContainer);
                const tablePos = allElements.indexOf(table);
                if (headingPos >= 0 && tablePos >= 0 && headingPos < tablePos) {
                  return true;
                }
              }
            }
          }

          // Also check for Items Required table structure (Type, Item Code, Description, Qty Per)
          // but only if it's NOT a BOM table
          if (thead) {
            const theadText = (thead.textContent || "").toLowerCase();
            if (
              theadText.includes("type") &&
              theadText.includes("item code") &&
              theadText.includes("description") &&
              (theadText.includes("qty per") || theadText.includes("qty"))
            ) {
              // Check if this is NOT a BOM table
              const isBomTable =
                table.closest("[data-export-block='bom']") !== null;
              if (!isBomTable) {
                // Check if there's a BOM heading nearby
                let hasBomHeading = false;
                let checkSibling = table.previousElementSibling;
                let checkDepth = 0;
                while (checkSibling && checkDepth < 5) {
                  if (
                    checkSibling.tagName &&
                    /^H[1-6]$/i.test(checkSibling.tagName)
                  ) {
                    const headingText = (checkSibling.textContent || "")
                      .trim()
                      .toLowerCase();
                    if (headingText.endsWith(" bom")) {
                      hasBomHeading = true;
                      break;
                    }
                  }
                  if (checkSibling.querySelector) {
                    const headingInParent = checkSibling.querySelector(
                      "h1, h2, h3, h4, h5, h6",
                    );
                    if (headingInParent) {
                      const headingText = (headingInParent.textContent || "")
                        .trim()
                        .toLowerCase();
                      if (headingText.endsWith(" bom")) {
                        hasBomHeading = true;
                        break;
                      }
                    }
                  }
                  checkSibling = checkSibling.previousElementSibling;
                  checkDepth++;
                }
                // If no BOM heading found, it's likely an Items Required table
                if (!hasBomHeading) {
                  return true;
                }
              }
            }
          }

          return false;
        };

        const allTablesOnPage = tableMetas.filter((meta) => {
          // Item Required tables are now included
          return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
        });

        const stepsWithContentOnPageSet = new Set();

        stepsOnThisPage.forEach((stepId) => {
          stepsWithContentOnPageSet.add(stepId);
        });

        stepMetas.forEach((stepMeta) => {
          // Check if step has child elements (include Item Required tables)
          const hasChildElements =
            blockMetasPx.some((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              if (meta.substepId !== null && meta.substepId !== undefined)
                return false;
              return meta.stepId === stepMeta.stepId;
            }) ||
            tableMetas.some((tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              if (tableMeta.stepId === stepMeta.stepId) return true;
              if (
                tableMeta.stepContainer &&
                stepMeta.element &&
                tableMeta.stepContainer === stepMeta.element
              )
                return true;
              return false;
            }) ||
            (stepMeta.stepShortDescBottomPx != null &&
              stepMeta.stepShortDescTopPx != null &&
              Number.isFinite(stepMeta.stepShortDescBottomPx) &&
              Number.isFinite(stepMeta.stepShortDescTopPx) &&
              stepMeta.stepShortDescBottomPx > stepMeta.stepShortDescTopPx);

          if (!hasChildElements) return;
          const headerBottomPx = stepMeta.headerTopPx + stepMeta.headerHeightPx;
          const headerOverlapsPage =
            headerBottomPx > pageStartPx && stepMeta.headerTopPx < pageEndPx;

          // Check if step has child elements on this page (include Item Required tables)
          const hasChildElementsOnPage =
            blockMetasPx.some((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              if (meta.substepId !== null && meta.substepId !== undefined)
                return false;
              if (meta.stepId !== stepMeta.stepId) return false;
              return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
            }) ||
            tableMetas.some((tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              // Check if table belongs to this step
              const belongsToStep =
                tableMeta.stepId === stepMeta.stepId ||
                (tableMeta.stepContainer &&
                  stepMeta.element &&
                  tableMeta.stepContainer === stepMeta.element);
              if (!belongsToStep) return false;
              // Table overlaps this page
              return (
                tableMeta.bottomPx > pageStartPx && tableMeta.topPx < pageEndPx
              );
            }) ||
            (stepMeta.stepShortDescBottomPx != null &&
              stepMeta.stepShortDescTopPx != null &&
              stepMeta.stepShortDescBottomPx > pageStartPx &&
              stepMeta.stepShortDescTopPx < pageEndPx);

          if (headerOverlapsPage || hasChildElementsOnPage) {
            stepsWithContentOnPageSet.add(stepMeta.stepId);
          }
        });

        const stepsWithContentOnPage = Array.from(stepsWithContentOnPageSet)
          .map((stepId) => {
            return stepMetas.find((meta) => meta.stepId === stepId);
          })
          .filter(Boolean);

        // Include step headers that are on this page OR steps that have Item Required tables continuing on this page
        const stepsWithHeaderOnPage = stepMetas.filter((meta) => {
          // Check if step header is physically on this page
          if (meta.headerTopPx >= pageStartPx && meta.headerTopPx < pageEndPx) {
            return true;
          }

          // Check if step has an Item Required table that continues on this page
          // Item Required tables are children of Steps, so we need to find them by stepId or step element
          // First check continuingTables (tables that are actually continuing)
          const hasItemRequiredInContinuing = continuingTables.some(
            (tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              // Primary match: by stepId
              if (tableMeta.stepId === meta.stepId) return true;
              // Fallback: check if table is within step's DOM element
              if (
                tableMeta.stepContainer &&
                meta.element &&
                tableMeta.stepContainer === meta.element
              )
                return true;
              return false;
            },
          );

          // Also check tableMetas for tables that continue or are on this page
          const hasItemRequiredTableContinuing = tableMetas.some(
            (tableMeta) => {
              if (!tableMeta.isItemRequiredTable) return false;
              // Primary match: by stepId
              if (tableMeta.stepId !== meta.stepId) {
                // Fallback: check if table is within step's DOM element
                if (
                  !tableMeta.stepContainer ||
                  !meta.element ||
                  tableMeta.stepContainer !== meta.element
                ) {
                  return false;
                }
              }
              // Table continues from previous page or is on this page
              const tableContinues =
                tableMeta.topPx < pageStartPx &&
                tableMeta.bottomPx > pageStartPx;
              const tableOnPage =
                tableMeta.bottomPx > pageStartPx && tableMeta.topPx < pageEndPx;
              return tableContinues || tableOnPage;
            },
          );

          return hasItemRequiredInContinuing || hasItemRequiredTableContinuing;
        });

        const substepsWithContentOnPageSet = new Set();

        substepsOnThisPage.forEach((substepId) => {
          substepsWithContentOnPageSet.add(substepId);
        });

        substepMetas.forEach((substepMeta) => {
          const hasChildElements = blockMetasPx.some((meta) => {
            if (!childBlockLabels.has(meta.label)) return false;
            return meta.substepId === substepMeta.substepId;
          });

          if (!hasChildElements) return;
          const headerBottomPx =
            substepMeta.headerTopPx + substepMeta.headerHeightPx;
          const headerOverlapsPage =
            headerBottomPx > pageStartPx && substepMeta.headerTopPx < pageEndPx;

          const hasChildElementsOnPage = blockMetasPx.some((meta) => {
            if (!childBlockLabels.has(meta.label)) return false;
            if (meta.substepId !== substepMeta.substepId) return false;
            return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
          });

          if (headerOverlapsPage || hasChildElementsOnPage) {
            substepsWithContentOnPageSet.add(substepMeta.substepId);
          }
        });

        const substepsWithContentOnPage = Array.from(
          substepsWithContentOnPageSet,
        )
          .map((substepId) => {
            return substepMetas.find((meta) => meta.substepId === substepId);
          })
          .filter(Boolean);

        const substepsWithHeaderOnPage = substepMetas.filter((meta) => {
          const headerBottomPx = meta.headerTopPx + meta.headerHeightPx;
          return headerBottomPx > pageStartPx && meta.headerTopPx < pageEndPx;
        });

        // Guard: avoid generating extremely tiny/zero slices which explode page count and create blanks.
        // Use a dynamic minimum based on the usable page height.
        const remainingPx = totalHeightPx - currentOffsetPx;
        const MIN_PAGE_CONTENT_PX = Math.max(
          120,
          Math.floor(availableHeightPx * 0.2),
        );
        if (remainingPx > 0 && pageContentPx <= 0) {
          pageContentPx = Math.min(remainingPx, MIN_PAGE_CONTENT_PX);
        } else if (
          remainingPx > MIN_PAGE_CONTENT_PX &&
          pageContentPx > 0 &&
          pageContentPx < MIN_PAGE_CONTENT_PX
        ) {
          const bomOnlyShortPage =
            firstSectionTopAfterBomPx != null &&
            bomEndCanvasPx != null &&
            bomToFirstSectionGapPx != null &&
            bomToFirstSectionGapPx >= minBomToSectionTrimGapPx &&
            bomEndCanvasPx > currentOffsetPx &&
            firstSectionTopAfterBomPx >=
              currentOffsetPx + pageContentPx - 0.5;
          if (!bomOnlyShortPage) {
            pageContentPx = Math.min(remainingPx, MIN_PAGE_CONTENT_PX);
          }
        }

        // HARD OVERRIDE (page 1): if Section 1 is kept on page 1 and Step 1 can fit
        // in page-1 usable space, do not allow any prior rule to end page 1 before
        // Step 1 (prefer full step block, fallback to header band).
        if (
          currentOffsetPx === 0 &&
          !isZipExport &&
          keepFirstSectionOnPage1 &&
          firstStepHeaderTopPx != null
        ) {
          const firstStepHeaderTailPx = Math.max(
            14,
            Math.round(22 * domToCanvasScaleY),
          );
          const requiredFirstStepBandPx = Math.floor(
            firstStepHeaderTopPx - currentOffsetPx + firstStepHeaderTailPx,
          );
          const maxPageContentPx = Math.max(
            0,
            availableHeightPx - headerInsertPx - tableBottomMarginPx,
          );
          const firstStepMeta = Array.isArray(stepMetas)
            ? stepMetas.reduce((minMeta, s) => {
                const t = Number(s?.topPx);
                if (!Number.isFinite(t)) return minMeta;
                if (!minMeta) return s;
                const mt = Number(minMeta?.topPx);
                if (!Number.isFinite(mt)) return s;
                return t < mt ? s : minMeta;
              }, null)
            : null;
          const firstStepBlockTailPx = Math.max(
            12,
            Math.round(18 * domToCanvasScaleY),
          );
          const firstStepId = firstStepMeta?.stepId;
          const firstStepNativeBottomPx =
            firstStepId != null
              ? (() => {
                  const bottoms = [];
                  // Child blocks that belong to Step 1 (attachments/canvas/video/rich-text/form/substeps)
                  blockMetasPx.forEach((m) => {
                    if (m?.stepId === firstStepId && Number.isFinite(m?.bottomPx)) {
                      bottoms.push(Number(m.bottomPx));
                    }
                  });
                  // Item Required tables linked to Step 1
                  tableMetas.forEach((t) => {
                    if (!t?.isItemRequiredTable) return;
                    const linkedById = t.stepId === firstStepId;
                    const linkedByContainer =
                      t.stepContainer &&
                      firstStepMeta?.element &&
                      t.stepContainer === firstStepMeta.element;
                    if (
                      (linkedById || linkedByContainer) &&
                      Number.isFinite(t?.bottomPx)
                    ) {
                      bottoms.push(Number(t.bottomPx));
                    }
                  });
                  // Fallback to step block bottom when native children were not detected.
                  if (
                    bottoms.length === 0 &&
                    Number.isFinite(Number(firstStepMeta?.bottomPx))
                  ) {
                    bottoms.push(Number(firstStepMeta.bottomPx));
                  }
                  if (bottoms.length === 0) return null;
                  return Math.max(...bottoms);
                })()
              : null;
          const requiredFirstStepBlockPx =
            Number.isFinite(Number(firstStepNativeBottomPx))
              ? Math.floor(
                  Number(firstStepNativeBottomPx) -
                    currentOffsetPx +
                    firstStepBlockTailPx,
                )
              : null;
          const targetRequiredPx =
            requiredFirstStepBlockPx != null &&
            requiredFirstStepBlockPx > 0 &&
            requiredFirstStepBlockPx <= maxPageContentPx
              ? requiredFirstStepBlockPx
              : requiredFirstStepBandPx;
          // Single-step exports: consume full usable page-1 content area so child nodes
          // (canvas/rich-text/attachments/videos/substeps/item-required) stay with Step 1
          // whenever physically possible.
          if (stepMetas.length === 1) {
            const fullPage1Px = Math.min(
              maxPageContentPx,
              Math.max(0, totalHeightPx - currentOffsetPx),
            );
            if (fullPage1Px > pageContentPx) {
              pageContentPx = fullPage1Px;
            }
          }
          // Only force when the chosen Step 1 target can actually fit on page 1.
          if (
            targetRequiredPx > 0 &&
            targetRequiredPx <= maxPageContentPx &&
            pageContentPx < targetRequiredPx
          ) {
            pageContentPx = targetRequiredPx;
          }
        }

        // Cap header insertion so it never consumes most of the page (prevents "white pages").
        if (headerInsertPx > Math.floor(availableHeightPx * 0.4)) {
          headerInsertPx = Math.floor(availableHeightPx * 0.4);
        }

        const pageTotalPx = headerInsertPx + pageContentPx;

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.max(1, pageTotalPx);
        const pageCtx = pageCanvas.getContext("2d");
        pageCtx.fillStyle = "#ffffff";
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        // Draw canvas content - Item Required headings are already hidden in DOM with display:none
        if (pageContentPx > 0) {
          pageCtx.drawImage(
            canvas,
            0,
            currentOffsetPx,
            canvas.width,
            pageContentPx,
            0,
            headerInsertPx,
            canvas.width,
            pageContentPx,
          );
        }

        // CRITICAL FIX: Check for Attachment blocks at the top of the page
        // If found, mask them and redraw with 100px margin to prevent overlap with step headers
        const attachmentsAtTop = blockMetasPx.filter((meta) => {
          if (meta.label !== "attachment") return false;

          const attachmentTopPx = meta.topPx;
          const attachmentTopInPage = attachmentTopPx - pageStartPx;

          // Check if attachment is at or near the top of this page (within first 150px)
          // Apply margin to ANY attachment at the top to ensure consistent spacing
          return attachmentTopInPage >= 0 && attachmentTopInPage < 150;
        });

        // For each attachment at the top, mask it from original position and redraw with margin
        attachmentsAtTop.forEach((attachmentMeta) => {
          const attachmentTopPx = attachmentMeta.topPx;
          const attachmentHeightPx = attachmentMeta.heightPx || 0;
          const attachmentWidthPx = attachmentMeta.widthPx || canvas.width;
          const attachmentLeftPx = attachmentMeta.leftPx || 0;

          const attachmentTopInPage = Math.max(
            0,
            attachmentTopPx - pageStartPx,
          );
          const attachmentTopInPageCanvas =
            headerInsertPx + attachmentTopInPage;

          // Mask the original attachment from the main canvas
          const whiteBgPadding = 10;
          const whiteBgLeft = Math.max(0, attachmentLeftPx - whiteBgPadding);
          const maskTop = Math.max(
            0,
            attachmentTopInPageCanvas - whiteBgPadding,
          );

          // Calculate how much of the attachment is visible on this page
          const attachmentBottomPx = attachmentTopPx + attachmentHeightPx;
          const attachmentEndInPage = Math.min(
            attachmentBottomPx,
            pageStartPx + pageContentPx,
          );
          const visibleAttachmentHeight = Math.max(
            0,
            attachmentEndInPage - attachmentTopPx,
          );
          const maskHeight = visibleAttachmentHeight + whiteBgPadding * 2;

          if (
            maskHeight > 0 &&
            attachmentTopInPage < 150 &&
            visibleAttachmentHeight > 0
          ) {
            // Use composite operations to completely clear the area
            pageCtx.save();
            pageCtx.globalCompositeOperation = "destination-out";
            pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
            pageCtx.fillRect(
              whiteBgLeft,
              maskTop,
              attachmentWidthPx + whiteBgPadding * 2,
              maskHeight,
            );
            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(
              whiteBgLeft,
              maskTop,
              attachmentWidthPx + whiteBgPadding * 2,
              maskHeight,
            );
            pageCtx.restore();

            // Now redraw the attachment with 100px margin from top
            const newDrawPosition = headerInsertPx + 100; // 100px margin from top

            // Draw white background at new position
            const attachmentBgPadding = 5;
            const attachmentBgLeft = Math.max(
              0,
              attachmentLeftPx - attachmentBgPadding,
            );
            const attachmentBgWidth =
              attachmentWidthPx + attachmentBgPadding * 2;
            const attachmentBgHeight =
              visibleAttachmentHeight + attachmentBgPadding;
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(
              attachmentBgLeft,
              newDrawPosition - attachmentBgPadding,
              attachmentBgWidth,
              attachmentBgHeight,
            );

            // Redraw the attachment at new position (with 100px margin)
            const availableSpace = Math.max(
              0,
              headerInsertPx + pageContentPx - newDrawPosition,
            );
            const drawHeight = Math.min(
              visibleAttachmentHeight,
              availableSpace,
            );

            if (drawHeight > 0) {
              pageCtx.drawImage(
                canvas,
                attachmentLeftPx,
                attachmentTopPx,
                attachmentWidthPx,
                drawHeight,
                attachmentLeftPx,
                newDrawPosition,
                attachmentWidthPx,
                drawHeight,
              );
            }
          }
        });

        // CRITICAL FIX: Apply top-band clearance for Video blocks too.
        // Step/Substep continuation headers are redrawn at the page top; without
        // this pass, video blocks can start under that band and look overlapped.
        const videosAtTop = blockMetasPx.filter((meta) => {
          if (meta.label !== "video") return false;
          const videoTopPx = meta.topPx;
          const videoBottomPx = meta.bottomPx;
          const videoTopInPage = videoTopPx - pageStartPx;
          const startsNearTop = videoTopInPage >= 0 && videoTopInPage < 180;
          const continuesFromPrevious =
            videoTopPx < pageStartPx && videoBottomPx > pageStartPx;
          return startsNearTop || continuesFromPrevious;
        });

        videosAtTop.forEach((videoMeta) => {
          const videoTopPx = videoMeta.topPx;
          const videoBottomPx = videoMeta.bottomPx;
          const videoWidthPx = videoMeta.widthPx || canvas.width;
          const videoHeightPx = videoMeta.heightPx || 0;
          const videoLeftPx = videoMeta.leftPx || 0;

          const videoContinues =
            videoTopPx < pageStartPx && videoBottomPx > pageStartPx;
          const visibleVideoStartPx = videoContinues ? pageStartPx : videoTopPx;
          const visibleVideoBottomPx = Math.min(
            videoBottomPx,
            pageStartPx + pageContentPx,
          );
          const visibleVideoHeight = Math.max(
            0,
            visibleVideoBottomPx - visibleVideoStartPx,
          );
          if (visibleVideoHeight <= 0) return;

          const relatedStepMeta =
            videoMeta.stepId !== null && videoMeta.stepId !== undefined
              ? stepMetas.find((s) => s.stepId === videoMeta.stepId)
              : null;
          const relatedSubstepMeta =
            videoMeta.substepId !== null && videoMeta.substepId !== undefined
              ? substepMetas.find((s) => s.substepId === videoMeta.substepId)
              : null;

          const stepHeaderInSlice = relatedStepMeta
            ? relatedStepMeta.headerTopPx >= pageStartPx &&
              relatedStepMeta.headerTopPx < pageEndPx
            : false;
          const substepHeaderInSlice = relatedSubstepMeta
            ? relatedSubstepMeta.headerTopPx >= pageStartPx &&
              relatedSubstepMeta.headerTopPx < pageEndPx
            : false;

          const needsTopHeadingClearance =
            (relatedSubstepMeta && !substepHeaderInSlice) ||
            (relatedStepMeta && !stepHeaderInSlice);
          if (!needsTopHeadingClearance) return;

          const topHeadingReservePx =
            headerInsertPx + Math.round(72 * domToCanvasScaleY);
          const extraStepBandPx =
            relatedStepMeta && !stepHeaderInSlice
              ? Math.round(14 * domToCanvasScaleY)
              : 0;
          const newDrawTop = topHeadingReservePx + extraStepBandPx;

          const srcTopPx = videoContinues ? pageStartPx : videoTopPx;
          const srcHeightPx = visibleVideoHeight;
          const maxDrawableHeight = Math.max(
            0,
            headerInsertPx + pageContentPx - newDrawTop,
          );
          const drawHeightPx = Math.min(srcHeightPx, maxDrawableHeight);
          if (drawHeightPx <= 0) return;

          const videoTopInPageCanvas =
            headerInsertPx + Math.max(0, videoTopPx - pageStartPx);
          const clearTop = videoContinues
            ? Math.max(0, headerInsertPx - 2)
            : Math.max(0, videoTopInPageCanvas - 2);
          const clearHeight = visibleVideoHeight + 4;

          pageCtx.save();
          pageCtx.globalCompositeOperation = "destination-out";
          pageCtx.fillStyle = "#ffffff";
          pageCtx.fillRect(0, clearTop, pageCanvas.width, clearHeight);
          pageCtx.globalCompositeOperation = "source-over";
          pageCtx.fillStyle = "#ffffff";
          pageCtx.fillRect(0, clearTop, pageCanvas.width, clearHeight);
          pageCtx.restore();

          pageCtx.drawImage(
            canvas,
            videoLeftPx,
            srcTopPx,
            videoWidthPx,
            drawHeightPx,
            videoLeftPx,
            newDrawTop,
            videoWidthPx,
            drawHeightPx,
          );
        });

        // CRITICAL FIX: Check for Canvas blocks at the top of the page
        // If found, mask them and redraw with margin-top to prevent Short Description from being hidden behind step headers
        // Store canvas blocks that need redrawing - we'll redraw them AFTER step headers are drawn
        const canvasBlocksToRedraw = [];
        const canvasBlocksAtTop = blockMetasPx.filter((meta) => {
          if (meta.label !== "canvas") return false;

          const canvasTopPx = meta.topPx;
          const canvasBottomPx = meta.bottomPx;
          const canvasTopInPage = canvasTopPx - pageStartPx;

          // Check if canvas block is at or near the top of this page (within first 150px)
          // OR if canvas block continues from previous page (starts before pageStartPx but extends into this page)
          // This includes the Short Description which is part of the canvas block
          const canvasStartsAtTop =
            canvasTopInPage >= 0 && canvasTopInPage < 150;
          const canvasContinuesFromPrevious =
            canvasTopPx < pageStartPx && canvasBottomPx > pageStartPx;

          return canvasStartsAtTop || canvasContinuesFromPrevious;
        });

        // For each canvas block at the top, check if there's a step header that would overlap
        // and prepare to redraw with margin-top to prevent Short Description from being hidden
        canvasBlocksAtTop.forEach((canvasMeta) => {
          const canvasTopPx = canvasMeta.topPx;
          const canvasBottomPx = canvasMeta.bottomPx;
          const canvasBlockTopPx = Number.isFinite(canvasMeta.canvasShortDescTopPx)
            ? Math.min(canvasTopPx, canvasMeta.canvasShortDescTopPx)
            : canvasTopPx;
          const canvasBlockBottomPx = Number.isFinite(
            canvasMeta.canvasShortDescBottomPx,
          )
            ? Math.max(canvasBottomPx, canvasMeta.canvasShortDescBottomPx)
            : canvasBottomPx;
          const canvasHeightPx = Math.max(0, canvasBlockBottomPx - canvasBlockTopPx);
          const canvasTopInPage = Math.max(0, canvasTopPx - pageStartPx);
          const canvasTopInPageCanvas = headerInsertPx + canvasTopInPage;

          // Check if canvas continues from previous page
          const canvasContinues =
            canvasBlockTopPx < pageStartPx && canvasBlockBottomPx > pageStartPx;

          // Check if there's a step header that would be drawn at the top for this canvas's step
          const stepMeta =
            canvasMeta.stepId !== null && canvasMeta.stepId !== undefined
              ? stepMetas.find((s) => s.stepId === canvasMeta.stepId)
              : null;

          let needsMarginForStepHeader = false;
          let marginTopForStepHeader = 0;

          if (stepMeta) {
            // Check if step header will be drawn at the top (same logic as drawing code)
            const stepHeaderTopInPage = Math.max(
              0,
              stepMeta.headerTopPx - pageStartPx,
            );
            const stepHeaderOnPage =
              stepMeta.headerTopPx >= pageStartPx &&
              stepMeta.headerTopPx < pageStartPx + pageContentPx;

            let canvasTopInPageForCheck = null;
            if (canvasTopPx >= pageStartPx) {
              canvasTopInPageForCheck = canvasTopPx - pageStartPx;
            } else {
              canvasTopInPageForCheck = 0;
            }

            const stepHeaderBelowCanvas =
              stepHeaderOnPage &&
              canvasTopInPageForCheck !== null &&
              stepHeaderTopInPage > canvasTopInPageForCheck;
            const willBeDrawnAtTop =
              canvasContinues || stepHeaderBelowCanvas || !stepHeaderOnPage;

            // If step header will be drawn at top and (canvas is near top OR canvas continues from previous page), add margin
            if (
              willBeDrawnAtTop &&
              (canvasTopInPage < 150 || canvasContinues)
            ) {
              needsMarginForStepHeader = true;
              const stepHeaderHeight = stepMeta.headerHeightPx || 0;
              const stepHeaderSpacing = Math.round(10 * domToCanvasScaleY);
              // Extra room below Step continuation title + HR (drawn in pageCtx) so the
              // canvas short description is not covered; mirrors video top-band clearance.
              const extraMarginForShortDescription = Math.round(
                14 * domToCanvasScaleY,
              );
              const measuredBand =
                stepHeaderHeight +
                stepHeaderSpacing +
                extraMarginForShortDescription;
              const continuationMinClearPx = Math.round(
                72 * domToCanvasScaleY,
              );
              marginTopForStepHeader = Math.max(
                measuredBand,
                continuationMinClearPx,
              );
            }
          }

          // Also check if canvas is too close to page header (within first 100px)
          const isCanvasTooCloseToPageHeader = canvasTopInPage < 100;
          let needsMarginForPageHeader = false;
          let marginTopForPageHeader = 0;

          if (isCanvasTooCloseToPageHeader) {
            needsMarginForPageHeader = true;
            marginTopForPageHeader = 60; // Reduced from 100px to prevent merging with content below
          }

          // If canvas needs margin (either for page header or step header), prepare to redraw it
          if (needsMarginForPageHeader || needsMarginForStepHeader) {
            // Use the larger margin to ensure canvas block (including Short Description) is fully visible
            const finalMarginTop = Math.max(
              marginTopForPageHeader,
              marginTopForStepHeader,
            );

            // Calculate how much of the canvas block is visible on this page
            // If canvas continues from previous page, calculate from pageStartPx
            const canvasBottomInPage = Math.min(
              canvasBlockBottomPx,
              pageStartPx + pageContentPx,
            );
            const visibleCanvasStartPx = canvasContinues
              ? pageStartPx
              : canvasBlockTopPx;
            const visibleCanvasHeight = Math.max(
              0,
              canvasBottomInPage - visibleCanvasStartPx,
            );

            // CRITICAL FIX: Check if there's a section or rich-text block that comes after this canvas block
            // If so, ensure the white background doesn't extend beyond the canvas bottom to avoid covering content below
            let actualCanvasBottomForMask = canvasBlockBottomPx;
            const blocksAfterCanvas = blockMetasPx.filter((meta) => {
              // Check for both sections and rich-text blocks that come after canvas
              if (meta.label !== "section" && meta.label !== "rich-text")
                return false;
              // Check if block starts after this canvas block (on the same page or next page)
              // Use a buffer to catch blocks that are close to the canvas bottom
              return (
                meta.topPx >= canvasBlockBottomPx &&
                meta.topPx <= canvasBlockBottomPx + 200
              ); // 200px buffer to catch nearby blocks
            });

            if (blocksAfterCanvas.length > 0) {
              // Find the closest block (section or rich-text) that comes after the canvas
              const closestBlock = blocksAfterCanvas.reduce(
                (closest, current) => {
                  return current.topPx < closest.topPx ? current : closest;
                },
              );

              // Ensure white background mask doesn't extend beyond canvas bottom or into the block below
              // Use a small buffer (5px) to ensure we don't accidentally cover content
              // Only limit if the block is on the same page as the canvas
              if (closestBlock.topPx <= pageStartPx + pageContentPx) {
                actualCanvasBottomForMask = Math.min(
                  canvasBlockBottomPx,
                  closestBlock.topPx - 5,
                );
              }
            }

            // Calculate the actual visible height for masking (don't extend into sections)
            // For continuing canvas blocks, calculate from pageStartPx
            const actualCanvasBottomInPage = Math.min(
              actualCanvasBottomForMask,
              pageStartPx + pageContentPx,
            );
            const actualVisibleCanvasStartPx = canvasContinues
              ? pageStartPx
              : canvasBlockTopPx;
            const actualVisibleCanvasHeight = Math.max(
              0,
              actualCanvasBottomInPage - actualVisibleCanvasStartPx,
            );

            // Mask the original canvas block (including Short Description) from the main canvas
            // CRITICAL: Only mask the actual canvas block area, not sections below
            // For continuing canvas blocks, mask starts at headerInsertPx (top of page)
            const whiteBgPadding = 1;
            const maskTop = canvasContinues
              ? headerInsertPx - whiteBgPadding
              : Math.max(0, canvasTopInPageCanvas - whiteBgPadding);
            // Use actualVisibleCanvasHeight instead of visibleCanvasHeight to prevent covering sections
            const maskHeight = actualVisibleCanvasHeight + whiteBgPadding * 2;

            if (maskHeight > 0 && actualVisibleCanvasHeight > 0) {
              // Use composite operations to completely clear the area
              pageCtx.save();
              pageCtx.globalCompositeOperation = "destination-out";
              pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
              pageCtx.fillRect(0, maskTop, canvas.width, maskHeight);
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(0, maskTop, canvas.width, maskHeight);
              pageCtx.restore();

              // Find the canvas block element to extract Short Description information
              const canvasBlockElement = Array.from(
                clone.querySelectorAll("[data-export-block='canvas']"),
              ).find((el) => {
                const rect = el.getBoundingClientRect();
                const elTopDom = rect.top - cloneRect.top;
                const elTopPx = Math.round(elTopDom * domToCanvasScaleY);
                const shortDescTopPx = Number.isFinite(
                  canvasMeta.canvasShortDescTopPx,
                )
                  ? canvasMeta.canvasShortDescTopPx
                  : null;
                return (
                  Math.abs(elTopPx - canvasTopPx) < 5 ||
                  (shortDescTopPx !== null &&
                    Math.abs(elTopPx - shortDescTopPx) < 5)
                ); // Match by block top or short-description top
              });

              // Extract Short Description text and position
              let shortDescriptionText = "";
              let shortDescriptionTopDom = null;
              let shortDescriptionLeftDom = null;

              if (canvasBlockElement) {
                // Prefer explicit rich-text description blocks in canvas nodes.
                // These were previously skipped by generic-div heuristics.
                const richTextDescDiv = Array.from(
                  canvasBlockElement.querySelectorAll(".rich-text-content-display"),
                ).find((div) => {
                  const r = div.getBoundingClientRect();
                  if (!r || r.height <= 0) return false;
                  const text = (div.innerText || div.textContent || "").trim();
                  if (!text) return false;
                  if (Number.isFinite(canvasMeta.canvasContentTopPx)) {
                    const topPx = Math.round((r.top - cloneRect.top) * domToCanvasScaleY);
                    return topPx <= canvasMeta.canvasContentTopPx + 12;
                  }
                  return true;
                });

                if (richTextDescDiv) {
                  const descRect = richTextDescDiv.getBoundingClientRect();
                  shortDescriptionText = (
                    richTextDescDiv.innerText ||
                    richTextDescDiv.textContent ||
                    ""
                  ).trim();
                  shortDescriptionTopDom = descRect.top - cloneRect.top;
                  shortDescriptionLeftDom = descRect.left - cloneRect.left;
                }

                const allDivs = Array.from(
                  canvasBlockElement.querySelectorAll("div"),
                );
                allDivs.forEach((div) => {
                  const style = window.getComputedStyle(div);
                  const text = div.textContent?.trim() || "";

                  // Check if this div is the heading container
                  const isHeadingContainer =
                    div.querySelector("h2") ||
                    div.querySelector('input[type="checkbox"]');

                  // Short description div detection
                  const hasExpectedStyles =
                    (style.whiteSpace === "pre-wrap" ||
                      style.whiteSpace === "pre") &&
                    style.marginLeft &&
                    style.marginLeft.includes("5px");

                  const isBeforeImages = (() => {
                    if (div.parentElement === canvasBlockElement) {
                      let sibling = div.nextElementSibling;
                      while (sibling) {
                        if (
                          sibling.tagName === "IMG" ||
                          sibling.querySelector("img")
                        ) {
                          return true;
                        }
                        sibling = sibling.nextElementSibling;
                      }
                    }
                    return false;
                  })();

                  const isDescriptionDiv =
                    text &&
                    text.length > 0 &&
                    !isHeadingContainer &&
                    !div.classList.contains("editor-shell") &&
                    !div.closest(".editor-shell") &&
                    (hasExpectedStyles ||
                      (isBeforeImages &&
                        div.parentElement === canvasBlockElement));

                  if (isDescriptionDiv && !shortDescriptionText) {
                    shortDescriptionText = text;
                    const descRect = div.getBoundingClientRect();
                    shortDescriptionTopDom = descRect.top - cloneRect.top;
                    shortDescriptionLeftDom = descRect.left - cloneRect.left;
                  }
                });
              }

              // Store canvas block info for redrawing AFTER step headers are drawn
              // For continuing canvas blocks, use pageStartPx as the source position
              const sourceTopPx = canvasContinues
                ? pageStartPx
                : canvasBlockTopPx;
              canvasBlocksToRedraw.push({
                canvasTopPx: sourceTopPx,
                visibleCanvasHeight,
                finalMarginTop,
                canvasTopInPageCanvas,
                shortDescriptionText,
                shortDescriptionTopDom,
                shortDescriptionLeftDom,
                canvasContinues,
                originalCanvasTopPx: canvasBlockTopPx,
                canvasContentTopPx: Number.isFinite(canvasMeta.canvasContentTopPx)
                  ? canvasMeta.canvasContentTopPx
                  : null,
              });
            }
          }
        });

        // Item Required tables are now rendered with autotable, so skip all canvas-based heading drawing
        // Track which Item Required tables have been handled (empty set since we're not drawing them)
        const handledItemRequiredTables = new Set();

        // CRITICAL: Mask step headers that will be drawn at the top BEFORE masking table headers
        // This prevents duplicate step headers from appearing in the main canvas
        // We need to identify which step headers will be drawn at the top and mask them from their natural position
        stepsWithHeaderOnPage.forEach((stepMeta) => {
          if (!stepMeta.headerCanvas) return;

          // Check if this step has an Item Required table continuing
          const itemRequiredTableOnPage =
            continuingTables.find((tableMeta) => {
              return (
                tableMeta.isItemRequiredTable &&
                tableMeta.stepId === stepMeta.stepId
              );
            }) ||
            tableMetas.find((tableMeta) => {
              if (
                !tableMeta.isItemRequiredTable ||
                tableMeta.stepId !== stepMeta.stepId
              ) {
                return false;
              }
              const tableContinues =
                tableMeta.topPx < pageStartPx &&
                tableMeta.bottomPx > pageStartPx;
              const tableOnPage =
                tableMeta.topPx >= pageStartPx &&
                tableMeta.topPx < pageStartPx + pageContentPx;
              return tableContinues || tableOnPage;
            });

          if (!itemRequiredTableOnPage) return;

          // Determine if step header will be drawn at the top (same logic as drawing code)
          const stepHeaderTopInPage = Math.max(
            0,
            stepMeta.headerTopPx - pageStartPx,
          );
          const stepHeaderOnPage =
            stepMeta.headerTopPx >= pageStartPx &&
            stepMeta.headerTopPx < pageStartPx + pageContentPx;
          const tableContinues =
            itemRequiredTableOnPage.topPx < pageStartPx &&
            itemRequiredTableOnPage.bottomPx > pageStartPx;

          let tableTopInPage = null;
          if (itemRequiredTableOnPage.topPx >= pageStartPx) {
            tableTopInPage = itemRequiredTableOnPage.topPx - pageStartPx;
          } else {
            tableTopInPage = 0; // Table continues from previous page
          }

          const stepHeaderBelowTable =
            stepHeaderOnPage &&
            tableTopInPage !== null &&
            stepHeaderTopInPage > tableTopInPage;

          // Step header will be drawn at top if: table continues OR step header is below table OR step header not on page
          const willBeDrawnAtTop =
            tableContinues || stepHeaderBelowTable || !stepHeaderOnPage;
          // Also mask when Item Required table extends beyond this page - step will be re-rendered with (1/2) indicator
          const tableExtendsBeyondPage =
            itemRequiredTableOnPage.bottomPx > pageStartPx + pageContentPx;
          const shouldMaskForRerender =
            stepHeaderOnPage && tableExtendsBeyondPage && !willBeDrawnAtTop;

          if (willBeDrawnAtTop || shouldMaskForRerender) {
            // Step header will be drawn at top, so mask it from main canvas to prevent duplicate
            // CRITICAL: We must mask the step header from wherever it appears in the main canvas
            // The main canvas is drawn from currentOffsetPx to currentOffsetPx + pageContentPx
            // and placed at headerInsertPx in the page canvas

            const whiteBgPadding = 20; // Extra padding to fully erase and prevent ghosting
            const stepHeaderWidth = stepMeta.headerCanvas.width;
            const stepHeaderLeft = stepMeta.headerLeftPx || 0;
            const hrExtraPx =
              stepMeta.hrTopPx != null && stepMeta.hrHeightPx
                ? stepMeta.hrHeightPx + 5
                : 0;

            // Calculate where step header appears in the main canvas slice (include HR line)
            const stepHeaderStartInCanvas =
              stepMeta.headerTopPx - currentOffsetPx;
            const stepHeaderEndInCanvas =
              stepMeta.headerTopPx +
              stepMeta.headerHeightPx +
              hrExtraPx -
              currentOffsetPx;

            // Check if step header overlaps the main canvas slice at all
            const stepHeaderOverlapsCanvas =
              stepHeaderEndInCanvas > 0 &&
              stepHeaderStartInCanvas < pageContentPx;

            if (stepHeaderOverlapsCanvas) {
              // Calculate the exact mask area in page canvas coordinates
              // The main canvas is at headerInsertPx, so step header appears at headerInsertPx + offset
              const maskStartInCanvas = Math.max(0, stepHeaderStartInCanvas);
              const maskEndInCanvas = Math.min(
                pageContentPx,
                stepHeaderEndInCanvas,
              );

              const maskTop =
                headerInsertPx + maskStartInCanvas - whiteBgPadding;
              const maskBottom =
                headerInsertPx + maskEndInCanvas + whiteBgPadding;
              const maskHeight = maskBottom - maskTop;

              if (maskHeight > 0) {
                pageCtx.save();
                // CRITICAL: Mask the FULL WIDTH of the page, not just the step header width
                // The step header might extend beyond its canvas width, or there might be
                // other content (like breadcrumbs) that need to be masked too
                // Use full page width to ensure complete coverage
                const maskLeft = 0; // Start from left edge
                const maskWidth = pageCanvas.width; // Full page width

                // First, erase the step header area completely
                pageCtx.globalCompositeOperation = "destination-out";
                pageCtx.fillStyle = "#ffffff";
                pageCtx.fillRect(maskLeft, maskTop, maskWidth, maskHeight);

                // Then fill with white to ensure complete coverage
                pageCtx.globalCompositeOperation = "source-over";
                pageCtx.fillStyle = "#ffffff";
                pageCtx.fillRect(maskLeft, maskTop, maskWidth, maskHeight);

                // Draw multiple overlapping rectangles to ensure complete coverage (anti-aliasing issues)
                for (let i = 0; i < 20; i++) {
                  pageCtx.fillRect(
                    maskLeft,
                    maskTop - i,
                    maskWidth,
                    maskHeight + i * 2,
                  );
                }
                pageCtx.restore();
              }
            }
          }
        });

        // Mask original headers from main canvas for continuing tables BEFORE any other content is drawn
        // This prevents duplicate headers when tables span multiple pages
        const allTablesToMask = [...continuingTables];

        // Also check for Training Log tables where heading would be split
        tableMetas.forEach((meta) => {
          if (!meta.isTrainingLogTable || !meta.headingElement) return;
          if (meta.headingTopDom === null || meta.headingHeightDom === null)
            return;

          const headingTopPx = Math.round(
            meta.headingTopDom * domToCanvasScaleY,
          );
          const headingHeightPx = Math.round(
            meta.headingHeightDom * domToCanvasScaleY,
          );
          const headingBottomPx = headingTopPx + headingHeightPx;

          // Check if heading would be split in the current page slice
          const headingStartsInSlice =
            headingTopPx >= currentOffsetPx &&
            headingTopPx < currentOffsetPx + pageContentPx;
          const headingEndsInSlice =
            headingBottomPx > currentOffsetPx &&
            headingBottomPx <= currentOffsetPx + pageContentPx;
          const headingOverlapsSlice =
            headingTopPx < currentOffsetPx + pageContentPx &&
            headingBottomPx > currentOffsetPx;

          // If heading would be split (starts before slice but extends into it, or starts in slice but extends beyond)
          const headingWouldBeSplit =
            (headingTopPx < currentOffsetPx &&
              headingBottomPx > currentOffsetPx) ||
            (headingStartsInSlice &&
              headingBottomPx > currentOffsetPx + pageContentPx);

          if (
            headingWouldBeSplit &&
            !allTablesToMask.find((m) => m.tableId === meta.tableId)
          ) {
            allTablesToMask.push(meta);
          }
        });

        allTablesToMask.forEach((meta) => {
          if (!meta.headerCanvas) return;

          const tableName = tableIdToName.get(meta.tableId);
          const isTrainingLog =
            tableName && tableName.toLowerCase().includes("training");
          const isBOM = tableName && tableName.toLowerCase().includes("bom");
          const isRevision =
            tableName && tableName.toLowerCase().includes("revision");
          const isItemRequired = meta.isItemRequiredTable || false;

          if (!isTrainingLog && !isBOM && !isRevision && !isItemRequired)
            return;

          // Calculate where the original header appears in the main canvas
          const originalHeaderTopInCanvas = meta.headerTopPx;
          const headingHeightPx = Math.round(
            meta.headingHeightDom * domToCanvasScaleY,
          );
          const spacingPx = Math.round(
            (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
          );
          const theadHeightPx = Math.round(
            meta.theadHeightDom * domToCanvasScaleY,
          );
          const originalHeaderHeight =
            headingHeightPx + spacingPx + theadHeightPx;
          const originalHeaderBottomInCanvas =
            originalHeaderTopInCanvas + originalHeaderHeight;

          // Check if the original header is within the page slice we just drew
          const headerStartsInSlice =
            originalHeaderTopInCanvas >= currentOffsetPx &&
            originalHeaderTopInCanvas < currentOffsetPx + pageContentPx;
          const headerEndsInSlice =
            originalHeaderBottomInCanvas > currentOffsetPx &&
            originalHeaderBottomInCanvas <= currentOffsetPx + pageContentPx;
          const headerOverlapsSlice =
            originalHeaderTopInCanvas < currentOffsetPx + pageContentPx &&
            originalHeaderBottomInCanvas > currentOffsetPx;

          if (headerOverlapsSlice) {
            // Calculate the position in the page canvas coordinate system
            const headerTopInSlice = Math.max(
              0,
              originalHeaderTopInCanvas - currentOffsetPx,
            );
            const headerBottomInSlice = Math.min(
              pageContentPx,
              originalHeaderBottomInCanvas - currentOffsetPx,
            );
            const headerTopInPageCanvas = headerInsertPx + headerTopInSlice;
            const headerBottomInPageCanvas =
              headerInsertPx + headerBottomInSlice;

            // Avoid covering content above the header; only clear from the header down
            const whiteBgPaddingX = 10;
            const whiteBgPaddingTop = 0;
            const whiteBgPaddingBottom = 10;
            const tableWidthPx =
              meta.widthPx || meta.headerCanvas.width || canvas.width;
            const whiteBgLeft = Math.max(
              0,
              (meta.leftPx || 0) - whiteBgPaddingX,
            );
            const overlayTop = Math.max(
              0,
              headerTopInPageCanvas - whiteBgPaddingTop,
            );
            const overlayBottom =
              headerBottomInPageCanvas + whiteBgPaddingBottom;
            const overlayHeight = overlayBottom - overlayTop;

            if (overlayHeight > 0) {
              pageCtx.save();
              // Use destination-out to clear the area first
              pageCtx.globalCompositeOperation = "destination-out";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPaddingX * 2,
                overlayHeight,
              );
              // Then fill with white
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPaddingX * 2,
                overlayHeight,
              );
              // Draw multiple overlapping rectangles to ensure complete coverage
              for (let i = 0; i < 5; i++) {
                pageCtx.fillRect(
                  whiteBgLeft,
                  overlayTop - i,
                  tableWidthPx + whiteBgPaddingX * 2,
                  overlayHeight + i * 2,
                );
              }
              pageCtx.restore();
            }
          }
        });

        // Additionally, mask Training Log headings that would be split, even if table isn't continuing
        tableMetas.forEach((meta) => {
          if (!meta.isTrainingLogTable || !meta.headingElement) return;
          if (meta.headingTopDom === null || meta.headingHeightDom === null)
            return;
          if (allTablesToMask.find((m) => m.tableId === meta.tableId)) return; // Already handled above

          const headingTopPx = Math.round(
            meta.headingTopDom * domToCanvasScaleY,
          );
          const headingHeightPx = Math.round(
            meta.headingHeightDom * domToCanvasScaleY,
          );
          const headingBottomPx = headingTopPx + headingHeightPx;

          // Check if heading would be split in the current page slice
          const headingStartsInSlice =
            headingTopPx >= currentOffsetPx &&
            headingTopPx < currentOffsetPx + pageContentPx;
          const headingOverlapsSlice =
            headingTopPx < currentOffsetPx + pageContentPx &&
            headingBottomPx > currentOffsetPx;

          // If heading would be split (starts before slice but extends into it, or starts in slice but extends beyond)
          const headingWouldBeSplit =
            (headingTopPx < currentOffsetPx &&
              headingBottomPx > currentOffsetPx) ||
            (headingStartsInSlice &&
              headingBottomPx > currentOffsetPx + pageContentPx);

          if (headingWouldBeSplit) {
            // Mask the heading from main canvas to prevent it from being split
            const headingTopInSlice = Math.max(
              0,
              headingTopPx - currentOffsetPx,
            );
            const headingBottomInSlice = Math.min(
              pageContentPx,
              headingBottomPx - currentOffsetPx,
            );
            const headingTopInPageCanvas = headerInsertPx + headingTopInSlice;
            const headingBottomInPageCanvas =
              headerInsertPx + headingBottomInSlice;

            const whiteBgPadding = 5;
            const tableWidthPx =
              meta.widthPx || meta.headerCanvas?.width || canvas.width;
            const whiteBgLeft = Math.max(
              0,
              (meta.leftPx || 0) - whiteBgPadding,
            );
            const overlayTop = Math.max(
              0,
              headingTopInPageCanvas - whiteBgPadding,
            );
            const overlayBottom = headingBottomInPageCanvas + whiteBgPadding;
            const overlayHeight = overlayBottom - overlayTop;

            if (overlayHeight > 0) {
              pageCtx.save();
              pageCtx.globalCompositeOperation = "destination-out";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPadding * 2,
                overlayHeight,
              );
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPadding * 2,
                overlayHeight,
              );
              // Draw multiple overlapping rectangles to ensure complete coverage
              for (let i = 0; i < 5; i++) {
                pageCtx.fillRect(
                  whiteBgLeft,
                  overlayTop - i,
                  tableWidthPx + whiteBgPadding * 2,
                  overlayHeight + i * 2,
                );
              }
              pageCtx.restore();
            }
          }
        });

        const trainingLogTableOnPage = allTablesOnPage.find((table) => {
          const tableName = tableIdToName.get(table.tableId);
          return tableName && tableName.toLowerCase().includes("training");
        });

        const bomTableOnPage = allTablesOnPage.find((table) => {
          const tableName = tableIdToName.get(table.tableId);
          return tableName && tableName.toLowerCase().includes("bom");
        });

        if (
          trainingLogTableOnPage &&
          bomTableOnPage &&
          trainingLogTableOnPage.topPx > bomTableOnPage.topPx &&
          trainingLogTableOnPage.headingElement &&
          trainingLogTableOnPage.headingTopDom !== null
        ) {
          const headingHeightPx = Math.round(
            trainingLogTableOnPage.headingHeightDom * domToCanvasScaleY,
          );
          const spacingPx = Math.round(2 * domToCanvasScaleY); // 2px spacing for Training Log
          const theadHeightPx = Math.round(
            trainingLogTableOnPage.theadHeightDom * domToCanvasScaleY,
          );

          const originalHeadingTopPx = Math.round(
            trainingLogTableOnPage.headingTopDom * domToCanvasScaleY,
          );
          const headingTopInPage = Math.max(
            0,
            originalHeadingTopPx - pageStartPx,
          );
          const headingTopInPageCanvas = headerInsertPx + headingTopInPage;

          const tableHeaderTopInPage = Math.max(
            0,
            trainingLogTableOnPage.headerTopPx - pageStartPx,
          );
          const originalHeaderTopInPageCanvas =
            headerInsertPx + tableHeaderTopInPage;

          const originalHeaderHeight =
            headingHeightPx + spacingPx + theadHeightPx;
          const originalHeaderBottom =
            originalHeaderTopInPageCanvas + originalHeaderHeight;

          const whiteBgPaddingX = 10;
          const whiteBgPaddingTop = 0;
          const whiteBgPaddingBottom = 10;
          const tableWidthPx =
            trainingLogTableOnPage.widthPx ||
            trainingLogTableOnPage.headerCanvas?.width ||
            canvas.width;
          const whiteBgLeft = Math.max(
            0,
            (trainingLogTableOnPage.leftPx || 0) - whiteBgPaddingX,
          );
          const whiteBgTop = Math.max(
            0,
            headingTopInPageCanvas - whiteBgPaddingTop,
          );
          const whiteBgHeight = Math.max(
            originalHeaderBottom - whiteBgTop + whiteBgPaddingBottom,
            originalHeaderHeight + whiteBgPaddingBottom,
          );

          // Use composite operations to properly clear the area
          pageCtx.save();
          pageCtx.globalCompositeOperation = "destination-out";
          pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
          pageCtx.fillRect(
            whiteBgLeft,
            whiteBgTop,
            tableWidthPx + whiteBgPaddingX * 2,
            whiteBgHeight,
          );
          pageCtx.globalCompositeOperation = "source-over";
          pageCtx.fillStyle = "#ffffff";
          pageCtx.fillRect(
            whiteBgLeft,
            whiteBgTop,
            tableWidthPx + whiteBgPaddingX * 2,
            whiteBgHeight,
          );
          pageCtx.restore();
        }

        let headerOffsetPx = 15;
        const headerSlotPositions = new Map();
        const headerSlotCanvases = new Map();
        const currentPageIndex = pageImages.length;

        // Track which steps had headers drawn at the top (for adding margin to Item Required tables below them)
        const stepsWithHeadersDrawnAtTop = new Set();

        // Item Required tables and Steps are siblings in a group/block, not parent-child
        // Therefore, we should NOT draw step headers above Item Required tables

        // CRITICAL: Redraw canvas blocks AFTER all step header operations (including masking) are complete
        // This ensures canvas blocks (including Short Description) appear on top of all white backgrounds
        canvasBlocksToRedraw.forEach((canvasBlock) => {
          const newDrawPosition = headerInsertPx + canvasBlock.finalMarginTop;
          const availableSpace = Math.max(
            0,
            headerInsertPx + pageContentPx - newDrawPosition,
          );
          const drawHeight = Math.min(
            canvasBlock.visibleCanvasHeight,
            availableSpace,
          );

          if (drawHeight > 0) {
            // Redraw the canvas block at new position
            // Item Required headings are already hidden in DOM, so no need to exclude them
            pageCtx.drawImage(
              canvas,
              0,
              canvasBlock.canvasTopPx,
              canvas.width,
              drawHeight,
              0,
              newDrawPosition,
              canvas.width,
              drawHeight,
            );

            // CRITICAL: Draw Short Description text separately to ensure it's visible on top
            // This acts like a z-index - drawing it last ensures it appears above everything
            if (
              canvasBlock.shortDescriptionText &&
              canvasBlock.shortDescriptionTopDom !== null &&
              canvasBlock.shortDescriptionLeftDom !== null
            ) {
              const shortDescTopPx = Math.round(
                canvasBlock.shortDescriptionTopDom * domToCanvasScaleY,
              );
              const shortDescLeftPx = Math.round(
                canvasBlock.shortDescriptionLeftDom * domToCanvasScaleX,
              );

              // Calculate the offset from canvas block top to Short Description
              // For continuing canvas blocks, use originalCanvasTopPx to calculate offset correctly
              const canvasTopForOffset = canvasBlock.canvasContinues
                ? canvasBlock.originalCanvasTopPx
                : canvasBlock.canvasTopPx;
              const shortDescOffsetFromBlockTop =
                shortDescTopPx - canvasTopForOffset;

              // For continuing canvas blocks, Short Description might be on previous page
              // Only draw if Short Description is on this page
              const shortDescOnThisPage =
                shortDescTopPx >= pageStartPx &&
                shortDescTopPx < pageStartPx + pageContentPx;

              // Calculate new position for Short Description (relative to new canvas block position)
              // For continuing canvas blocks, adjust offset based on where we're drawing from
              let adjustedOffset = canvasBlock.canvasContinues
                ? shortDescTopPx - pageStartPx
                : shortDescOffsetFromBlockTop;
              let newShortDescTop = newDrawPosition + adjustedOffset;

              // Only draw if Short Description is within the visible area
              // For continuing canvas blocks, also check if Short Description is on this page
              let shouldDrawShortDesc = canvasBlock.canvasContinues
                ? shortDescOnThisPage &&
                  newShortDescTop >= headerInsertPx &&
                  newShortDescTop < headerInsertPx + pageContentPx
                : newShortDescTop >= headerInsertPx &&
                  newShortDescTop < headerInsertPx + pageContentPx;

              // Continuation pages can start directly at canvas content, leaving short description
              // on the previous slice. In that case, force-render the short description at the top
              // of the moved canvas block so it remains visible below Step/Substep continuation.
              const startsAtOrAfterCanvasContent =
                canvasBlock.canvasContentTopPx !== null &&
                Number.isFinite(canvasBlock.canvasContentTopPx) &&
                pageStartPx >= canvasBlock.canvasContentTopPx - 2;
              const needsForcedContinuationShortDesc =
                canvasBlock.canvasContinues &&
                !shortDescOnThisPage &&
                startsAtOrAfterCanvasContent;
              if (needsForcedContinuationShortDesc) {
                adjustedOffset = Math.round(20 * domToCanvasScaleY);
                newShortDescTop = newDrawPosition + adjustedOffset;
                shouldDrawShortDesc =
                  newShortDescTop >= headerInsertPx &&
                  newShortDescTop < headerInsertPx + pageContentPx;
              }

              if (shouldDrawShortDesc) {
                pageCtx.save();
                pageCtx.fillStyle = "#000000"; // Black text
                pageCtx.font = `${Math.round(14 * domToCanvasScaleY)}px Arial`; // Match typical font size
                pageCtx.textAlign = "left";
                pageCtx.textBaseline = "top";

                // Split text into lines if it contains newlines
                const lines = canvasBlock.shortDescriptionText.split("\n");
                let currentY = newShortDescTop;
                const lineHeight = Math.round(20 * domToCanvasScaleY); // Line height

                lines.forEach((line) => {
                  if (line.trim()) {
                    // Draw each line of the Short Description
                    pageCtx.fillText(line.trim(), shortDescLeftPx, currentY);
                    currentY += lineHeight;
                  } else {
                    // Empty line - add spacing
                    currentY += lineHeight * 0.5;
                  }
                });

                pageCtx.restore();
              }
            }
          }
        });

        const getTableIndicatorInfo = (tableId) => {
          let pageIndices = multiPageTables.get(tableId) || [];
          const tableName = tableIdToName.get(tableId);
          if (!tableName) return null;
          const tableNameLower = tableName.toLowerCase();

          const tableMeta = tableMetas.find((meta) => meta.tableId === tableId);
          if (!tableMeta) return null;

          const pageEndPx = pageStartPx + pageContentPx;
          const tableOnThisPage =
            tableMeta.topPx < pageEndPx && tableMeta.bottomPx > pageStartPx;

          if (!tableOnThisPage) return null;

          const isTableContinuing = continuingTables.some(
            (t) => t.tableId === tableId,
          );

          const tableExtendsBeyondPage = tableMeta.bottomPx > pageEndPx;
          const tableStartsBeforePage = tableMeta.topPx < pageStartPx;

          const tableHeight = tableMeta.bottomPx - tableMeta.topPx;

          const actuallySpansMultiplePages =
            isTableContinuing ||
            (tableMeta.topPx >= pageStartPx && tableExtendsBeyondPage) ||
            tableHeight > availableHeightPx;

          if (tableNameLower.includes("training")) {
            return null;
          }

          // For the Revision Table and BOM, page indicators are rendered
          // using dedicated header overlays (revisionTablePageIndicators /
          // BOM overlay), so we avoid baking any "(x/n)" text into the table
          // header canvas here to prevent duplicate titles.
          if (
            tableNameLower.includes("revision") ||
            tableNameLower.includes("bom")
          ) {
            return null;
          }

          if (!pageIndices.includes(currentPageIndex)) {
            pageIndices = [...pageIndices, currentPageIndex];
            pageIndices.sort((a, b) => a - b);
            multiPageTables.set(tableId, pageIndices);
          }

          // For the Revision Table, we now rely exclusively on the final
          // revisionTablePageIndicators map (computed after pagination) to
          // render accurate "Revision Table (n/total)" labels in the PDF
          // header. To avoid conflicting or incorrect labels baked into the
          // table header canvas itself, skip generating indicator info here.
          if (tableNameLower.includes("revision")) {
            return null;
          }

          const sortedPageIndices = [...pageIndices].sort((a, b) => a - b);
          let totalPages = sortedPageIndices.length;
          let pageSeq = 1;

          if (totalPages > 1) {
            if (actuallySpansMultiplePages) {
              const pageIndexInArray =
                sortedPageIndices.indexOf(currentPageIndex);

              if (pageIndexInArray >= 0) {
                pageSeq = pageIndexInArray + 1;
              } else {
                if (isTableContinuing) {
                  const pagesBefore = sortedPageIndices.filter(
                    (idx) => idx < currentPageIndex,
                  ).length;
                  pageSeq = pagesBefore + 1;
                } else {
                  pageSeq = 1;
                }
              }
            } else {
              const needsIndicator =
                tableName && tableName.toLowerCase().includes("revision");
              if (needsIndicator) {
                return {
                  tableName,
                  pageSeq: 1,
                  totalPages: 1,
                  indicatorText: tableName,
                  showPageIndicator: true,
                };
              }
              return null;
            }
          } else {
            if (tableExtendsBeyondPage || isTableContinuing) {
              const remainingHeight = tableExtendsBeyondPage
                ? tableMeta.bottomPx - pageEndPx
                : tableMeta.bottomPx - tableMeta.topPx;
              const estimatedAdditionalPages = Math.ceil(
                remainingHeight / pageContentPx,
              );
              totalPages = Math.max(2, 1 + estimatedAdditionalPages);

              if (isTableContinuing) {
                const firstPageIndex =
                  sortedPageIndices.length > 0 ? sortedPageIndices[0] : 0;
                if (currentPageIndex > firstPageIndex) {
                  pageSeq = currentPageIndex - firstPageIndex + 1;
                } else {
                  pageSeq = 1;
                }
              } else {
                pageSeq = 1;
              }
            } else {
              const needsIndicator =
                tableName && tableName.toLowerCase().includes("revision");
              if (needsIndicator) {
                return {
                  tableName,
                  pageSeq: 1,
                  totalPages: 1,
                  indicatorText: tableName,
                  showPageIndicator: true,
                };
              }
              return null;
            }
          }

          if (isTableContinuing) {
            if (totalPages <= 1) {
              const tableHeight = tableMeta.bottomPx - tableMeta.topPx;
              totalPages = Math.max(
                2,
                Math.ceil(tableHeight / availableHeightPx),
              );
            }

            if (sortedPageIndices.length > 0) {
              const pageIndexInArray =
                sortedPageIndices.indexOf(currentPageIndex);
              if (pageIndexInArray >= 0) {
                pageSeq = pageIndexInArray + 1;
              } else {
                const firstPageIndex = sortedPageIndices[0];
                pageSeq = currentPageIndex - firstPageIndex + 1;
                if (pageSeq < 1) pageSeq = 1;
              }
            } else {
              pageSeq = currentPageIndex + 1;
            }

            if (pageSeq > totalPages) {
              pageSeq = totalPages;
            }

            return {
              tableName,
              pageSeq,
              totalPages: totalPages,
              indicatorText: `${tableName} (${pageSeq}/${totalPages})`,
              showPageIndicator: true,
            };
          }

          if (totalPages <= 1) {
            return null;
          }

          return {
            tableName,
            pageSeq,
            totalPages: totalPages,
            indicatorText: `${tableName} (${pageSeq}/${totalPages})`,
            showPageIndicator: true,
          };
        };

        allTablesOnPage.forEach((tableMetaOnPage) => {
          const tableId = tableMetaOnPage.tableId;

          if (!allTrackedTableIds.includes(tableId)) return;

          if (!tableMetaOnPage.headerCanvas) return;

          // Skip revision table, training log table, BOM table, and Item Required tables - they're now rendered with autotable instead of canvas
          if (
            tableId === revisionTableId ||
            tableId === trainingLogTableId ||
            tableId === bomTableId
          )
            return;

          // Skip Item Required tables that are being rendered with autotable
          const isItemRequiredTable = tableMetaOnPage.isItemRequiredTable;
          const itemRequiredTableData = itemRequiredTablesData.find(
            (t) => t.tableId === tableId,
          );
          if (isItemRequiredTable && itemRequiredTableData) return;

          const tableIsContinuing = continuingTables.some(
            (meta) => meta.tableId === tableId,
          );
          if (tableIsContinuing) return;

          // Skip Item Required tables that have been handled by the top-of-page logic
          // Also skip if being rendered with autotable
          const isItemRequiredAtTop =
            tableMetaOnPage.isItemRequiredTable &&
            handledItemRequiredTables.has(tableId);
          const itemRequiredTableDataForSkip = itemRequiredTablesData.find(
            (t) => t.tableId === tableId,
          );
          if (isItemRequiredAtTop && !itemRequiredTableDataForSkip) {
            // Still draw the table body, but skip the header since it's already been drawn with margin-top
            const headingHeightPx = Math.round(
              tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
            );
            const spacingPx = Math.round(10 * domToCanvasScaleY);
            const theadHeightPx = Math.round(
              tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
            );
            const titlePadding = 10; // Match the padding used when redrawing the heading
            const adjustedHeaderHeight =
              headingHeightPx + titlePadding + spacingPx + theadHeightPx;

            // Calculate where table body should start (after the adjusted heading position)
            const stepMeta = stepMetas.find(
              (s) => s.stepId === tableMetaOnPage.stepId,
            );
            const stepHeaderHeight = stepMeta
              ? stepMeta.headerHeightPx || 0
              : 0;
            const stepHeaderSpacing = Math.round(35 * domToCanvasScaleY);
            const adjustedHeaderTop =
              headerInsertPx + stepHeaderHeight + stepHeaderSpacing;
            const tableBodyStartInCanvas =
              tableMetaOnPage.headerTopPx +
              (headingHeightPx + spacingPx + theadHeightPx);
            const tableBodyEndInCanvas = tableMetaOnPage.bottomPx;

            const tableBodyStartInPage =
              headerInsertPx +
              Math.max(0, tableBodyStartInCanvas - pageStartPx);
            const tableBodyEndInPage =
              headerInsertPx +
              Math.max(
                0,
                Math.min(tableBodyEndInCanvas, pageStartPx + pageContentPx) -
                  pageStartPx,
              );

            if (tableBodyStartInCanvas < tableBodyEndInCanvas) {
              const tableBodySourceStart = Math.max(
                tableBodyStartInCanvas,
                pageStartPx,
              );
              const pageEndInCanvas = pageStartPx + pageContentPx;
              const tableWidthPx =
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width;
              let tableBodySourceEnd = Math.min(
                tableBodyEndInCanvas,
                pageEndInCanvas,
              );
              const tableBodySourceHeight = Math.max(
                0,
                tableBodySourceEnd - tableBodySourceStart,
              );

              const headerBodySpacingPx = 5;
              const tableBodyDestStart =
                adjustedHeaderTop + adjustedHeaderHeight + headerBodySpacingPx;

              const tableName = tableIdToName.get(tableId);
              const isBOMTable =
                tableName && tableName.toLowerCase().includes("bom");
              const isRevisionTable =
                tableName && tableName.toLowerCase().includes("revision");
              const isTrainingLogTable =
                tableName && tableName.toLowerCase().includes("training");
              const needsExtraFooterMargin =
                isRevisionTable || isTrainingLogTable || isBOMTable;
              const extraFooterMarginPx = needsExtraFooterMargin
                ? Math.floor(8 / pixelToMm)
                : 0;
              const footerMarginForRowsPx =
                Math.floor(
                  (contentBottomPadding + footerHeight + 10) / pixelToMm,
                ) + extraFooterMarginPx;
              const safePageBottom = pageCanvas.height - footerMarginForRowsPx;
              const pageContentEndInPageCanvas = headerInsertPx + pageContentPx;

              const availableSpace = Math.min(
                safePageBottom - tableBodyDestStart,
                pageContentEndInPageCanvas - tableBodyDestStart,
                tableBodyEndInPage - tableBodyDestStart,
              );
              const tableBodyDrawHeight = Math.min(
                tableBodySourceHeight,
                Math.max(0, availableSpace),
              );

              if (
                tableBodyDrawHeight > 0 &&
                tableBodySourceHeight > 0 &&
                tableBodySourceStart < tableBodySourceEnd &&
                tableBodyDestStart < safePageBottom
              ) {
                pageCtx.drawImage(
                  canvas,
                  tableMetaOnPage.leftPx,
                  tableBodySourceStart,
                  tableWidthPx,
                  tableBodyDrawHeight,
                  tableMetaOnPage.leftPx,
                  tableBodyDestStart,
                  tableWidthPx,
                  tableBodyDrawHeight,
                );
              }
            }
            return; // Skip header drawing for this table
          }
          const indicatorInfo = getTableIndicatorInfo(tableId);

          const tableName = tableIdToName.get(tableId);
          const isTrainingLog =
            tableName && tableName.toLowerCase().includes("training");
          const isBOM = tableName && tableName.toLowerCase().includes("bom");
          const isTrainingLogOrBOM = isTrainingLog || isBOM;

          const hasHeading =
            tableMetaOnPage.headingElement &&
            tableMetaOnPage.headingTopDom !== null;
          const tableSpansMultiplePages =
            indicatorInfo && indicatorInfo.totalPages > 1;

          // Training Log tables: headerCanvas is already correct, skip special title handling
          // unless there's a BOM table before it (which requires masking the original header)
          if (isTrainingLog && hasHeading) {
            const bomTableOnPage = allTablesOnPage.find((otherTable) => {
              const otherTableName = tableIdToName.get(otherTable.tableId);
              const otherIsBOM =
                otherTableName && otherTableName.toLowerCase().includes("bom");
              return otherIsBOM && otherTable.tableId !== tableId;
            });

            // Only do special handling if BOM table exists before Training Log on same page
            if (
              bomTableOnPage &&
              bomTableOnPage.topPx < tableMetaOnPage.topPx
            ) {
              const headingHeightPx = Math.round(
                tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
              );
              const spacingPx = Math.round(2 * domToCanvasScaleY); // 2px spacing for Training Log
              const theadHeightPx = Math.round(
                tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
              );

              const originalHeadingTopPx = Math.round(
                tableMetaOnPage.headingTopDom * domToCanvasScaleY,
              );
              const headingTopInPage = Math.max(
                0,
                originalHeadingTopPx - pageStartPx,
              );
              const headingTopInPageCanvas = headerInsertPx + headingTopInPage;

              const tableHeaderTopInPage = Math.max(
                0,
                tableMetaOnPage.headerTopPx - pageStartPx,
              );
              const originalHeaderTopInPageCanvas =
                headerInsertPx + tableHeaderTopInPage;

              const originalHeaderHeight =
                headingHeightPx + spacingPx + theadHeightPx;
              const originalHeaderBottom =
                originalHeaderTopInPageCanvas + originalHeaderHeight;

              const whiteBgPadding = 5;
              const tableWidthPx =
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width;
              const whiteBgLeft = Math.max(
                0,
                tableMetaOnPage.leftPx - whiteBgPadding,
              );
              const whiteBgTop = Math.max(
                0,
                headingTopInPageCanvas - whiteBgPadding,
              );
              const whiteBgHeight =
                originalHeaderBottom - whiteBgTop + whiteBgPadding * 2;

              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                whiteBgTop,
                tableWidthPx + whiteBgPadding * 2,
                whiteBgHeight,
              );

              return;
            }

            // Training Log headerCanvas is already correct, no need for special title handling
            // Return early to avoid duplicate rendering
            return;
          }

          // Skip BOM tables - they're now rendered with autotable instead of canvas
          if (isBOM && tableId === bomTableId) {
            return;
          }

          // BOM tables need special title/indicator handling below
          if (isBOM && hasHeading) {
            const headingHeightPx = Math.round(
              tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
            );
            const spacingPx = Math.round(10 * domToCanvasScaleY); // 10px for BOM
            const theadHeightPx = Math.round(
              tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
            );

            const tableStartsOnThisPage =
              tableMetaOnPage.topPx >= pageStartPx &&
              tableMetaOnPage.topPx < pageStartPx + pageContentPx;
            const isFirstPage = tableStartsOnThisPage;
            if (isFirstPage) {
              const originalHeadingText = (
                tableMetaOnPage.headingElement.textContent || ""
              ).trim();
              let titleText = originalHeadingText
                .replace(/\s*\(\d+\/\d+\)\s*$/, "")
                .trim();

              if (!titleText.toUpperCase().endsWith("BOM")) {
                titleText = (
                  tableMetaOnPage.headingElement.textContent || ""
                ).trim();
                titleText = titleText.replace(/\s*\(\d+\/\d+\)\s*$/, "").trim();
              }

              const modifiedCanvas = document.createElement("canvas");
              modifiedCanvas.width = tableMetaOnPage.headerCanvas.width;
              const titlePadding = 5;
              modifiedCanvas.height =
                headingHeightPx + titlePadding + spacingPx + theadHeightPx;
              const modifiedCtx = modifiedCanvas.getContext("2d");

              modifiedCtx.fillStyle = "#ffffff";
              modifiedCtx.fillRect(
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );

              modifiedCtx.fillStyle = "#000000";
              modifiedCtx.font = "bold 30px Arial";
              modifiedCtx.textAlign = "left";
              modifiedCtx.textBaseline = "top";
              const titleY = titlePadding / 2;
              modifiedCtx.fillText(titleText, 0, titleY);

              const theadStartInOriginalCanvas = headingHeightPx + spacingPx;
              const theadDrawY = headingHeightPx + titlePadding + spacingPx;
              modifiedCtx.drawImage(
                tableMetaOnPage.headerCanvas,
                0,
                theadStartInOriginalCanvas,
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                theadHeightPx,
                0,
                theadDrawY,
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                theadHeightPx,
              );

              tableMetaOnPage.headerCanvas = modifiedCanvas;
            } else {
              const theadHeightPx = Math.round(
                tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
              );
              const headingHeightPx = Math.round(
                tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
              );
              const spacingPx = Math.round(10 * domToCanvasScaleY); // 10px for BOM
              const theadStartInOriginalCanvas = headingHeightPx + spacingPx;

              const modifiedCanvas = document.createElement("canvas");
              modifiedCanvas.width = tableMetaOnPage.headerCanvas.width;
              modifiedCanvas.height = theadHeightPx;
              const modifiedCtx = modifiedCanvas.getContext("2d");

              modifiedCtx.fillStyle = "#ffffff";
              modifiedCtx.fillRect(
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );

              modifiedCtx.drawImage(
                tableMetaOnPage.headerCanvas,
                0,
                theadStartInOriginalCanvas,
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                theadHeightPx,
                0,
                0,
                tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                theadHeightPx,
              );

              tableMetaOnPage.headerCanvas = modifiedCanvas;
            }

            const whiteBgPadding = 5;
            const tableWidthPx =
              tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width;
            const whiteBgLeft = Math.max(
              0,
              tableMetaOnPage.leftPx - whiteBgPadding,
            );

            const originalHeadingTopPx = Math.round(
              tableMetaOnPage.headingTopDom * domToCanvasScaleY,
            );
            const headingTopInPage = Math.max(
              0,
              originalHeadingTopPx - pageStartPx,
            );
            const headingTopInPageCanvas = headerInsertPx + headingTopInPage;

            const tableHeaderTopInPage = Math.max(
              0,
              tableMetaOnPage.headerTopPx - pageStartPx,
            );
            const originalHeaderTopInPageCanvas =
              headerInsertPx + tableHeaderTopInPage;

            const originalHeaderHeightForBg =
              headingHeightPx + spacingPx + theadHeightPx;
            const originalHeaderBottomForBg =
              originalHeaderTopInPageCanvas + originalHeaderHeightForBg;

            if (isFirstPage) {
              // Calculate positions first
              const headerDrawY = headingTopInPageCanvas;
              const modifiedHeaderBottomForBg =
                headingTopInPageCanvas + tableMetaOnPage.headerCanvas.height;
              const originalHeaderHeightForBody =
                headingHeightPx + spacingPx + theadHeightPx;
              const originalHeaderBottomForBody =
                originalHeaderTopInPageCanvas + originalHeaderHeightForBody;

              // Removed BOM heading masking - no longer masking the original header area

              // Now draw the new header canvas
              pageCtx.drawImage(
                tableMetaOnPage.headerCanvas,
                tableMetaOnPage.leftPx,
                headerDrawY,
                tableMetaOnPage.headerCanvas.width,
                tableMetaOnPage.headerCanvas.height,
              );

              headerSlotCanvases.set(
                tableMetaOnPage.tableId,
                tableMetaOnPage.headerCanvas,
              );
              headerSlotPositions.set(tableMetaOnPage.tableId, headerDrawY);

              const tableBodyStartInCanvas =
                tableMetaOnPage.headerTopPx + originalHeaderHeightForBody;
              const tableBodyEndInCanvas = tableMetaOnPage.bottomPx;

              const tableBodyStartInPage =
                headerInsertPx +
                Math.max(0, tableBodyStartInCanvas - pageStartPx);
              const tableBodyEndInPage =
                headerInsertPx +
                Math.max(
                  0,
                  Math.min(tableBodyEndInCanvas, pageStartPx + pageContentPx) -
                    pageStartPx,
                );

              const modifiedHeaderBottomForBody =
                headerDrawY + tableMetaOnPage.headerCanvas.height;

              if (tableBodyStartInCanvas < tableBodyEndInCanvas) {
                const tableBodySourceStart = Math.max(
                  tableBodyStartInCanvas,
                  pageStartPx,
                );
                const pageEndInCanvas = pageStartPx + pageContentPx;

                const tableName = tableIdToName.get(tableMetaOnPage.tableId);
                const isBOMTable =
                  tableName && tableName.toLowerCase().includes("bom");

                // For BOM tables, add small buffer only when near the actual table end to capture last rows
                // Don't extend beyond the actual table end to avoid covering content below
                let tableBodySourceEnd = Math.min(
                  tableBodyEndInCanvas,
                  pageEndInCanvas,
                );
                if (isBOMTable && tableBodyEndInCanvas <= pageEndInCanvas) {
                  // Only add buffer if table ends on this page and we're close to the end
                  const distanceFromEnd =
                    tableBodyEndInCanvas - tableBodySourceStart;
                  const bomBufferPx = Math.floor(20 / pixelToMm); // Reduced buffer
                  tableBodySourceEnd = Math.min(
                    tableBodyEndInCanvas + bomBufferPx,
                    pageEndInCanvas,
                  );
                }

                const tableBodySourceHeight = Math.max(
                  0,
                  tableBodySourceEnd - tableBodySourceStart,
                );

                const headerBodySpacingPx = 5;
                const tableBodyDestStart =
                  modifiedHeaderBottomForBody + headerBodySpacingPx;

                const isRevisionTable =
                  tableName && tableName.toLowerCase().includes("revision");
                const isTrainingLogTable =
                  tableName && tableName.toLowerCase().includes("training");
                const needsExtraFooterMargin =
                  isRevisionTable || isTrainingLogTable || isBOMTable;
                const extraFooterMarginPx = needsExtraFooterMargin
                  ? Math.floor(8 / pixelToMm)
                  : 0;
                const footerMarginForRowsPx =
                  Math.floor(
                    (contentBottomPadding + footerHeight + 10) / pixelToMm,
                  ) + extraFooterMarginPx;
                const safePageBottom =
                  pageCanvas.height - footerMarginForRowsPx;
                const pageContentEndInPageCanvas =
                  headerInsertPx + pageContentPx;

                const availableSpace = Math.min(
                  safePageBottom - tableBodyDestStart,
                  pageContentEndInPageCanvas - tableBodyDestStart,
                  tableBodyEndInPage - tableBodyDestStart,
                );
                const tableBodyDrawHeight = Math.min(
                  tableBodySourceHeight,
                  Math.max(0, availableSpace),
                );

                if (
                  tableBodyDrawHeight > 0 &&
                  tableBodySourceHeight > 0 &&
                  tableBodySourceStart < tableBodySourceEnd &&
                  tableBodyDestStart < safePageBottom
                ) {
                  pageCtx.drawImage(
                    canvas,
                    tableMetaOnPage.leftPx,
                    tableBodySourceStart,
                    tableWidthPx,
                    tableBodyDrawHeight,
                    tableMetaOnPage.leftPx,
                    tableBodyDestStart,
                    tableWidthPx,
                    tableBodyDrawHeight,
                  );
                }
              }
            } else {
              const slotTopPx = headerOffsetPx;

              // For continuing tables, mask the original header from main canvas before drawing new header
              // The main canvas content (drawn earlier) includes the original table header
              // We need to mask it to prevent duplicate headers
              const originalHeaderTopInPage = Math.max(
                0,
                tableMetaOnPage.headerTopPx - pageStartPx,
              );

              // If the original header position is within the page content, mask it
              if (
                originalHeaderTopInPage >= 0 &&
                originalHeaderTopInPage < pageContentPx
              ) {
                const originalHeaderTopInPageCanvas =
                  headerInsertPx + originalHeaderTopInPage;
                const originalHeaderHeight =
                  headingHeightPx + spacingPx + theadHeightPx;
                const originalHeaderBottomInPageCanvas =
                  originalHeaderTopInPageCanvas + originalHeaderHeight;

                // Mask the original header area from main canvas
                const overlayTop = Math.max(
                  0,
                  originalHeaderTopInPageCanvas - whiteBgPadding,
                );
                const overlayBottom =
                  originalHeaderBottomInPageCanvas + whiteBgPadding;
                const overlayHeight = overlayBottom - overlayTop;

                pageCtx.save();
                // Use destination-out to clear the area first
                pageCtx.globalCompositeOperation = "destination-out";
                pageCtx.fillStyle = "#ffffff";
                pageCtx.fillRect(
                  whiteBgLeft,
                  overlayTop,
                  tableWidthPx + whiteBgPadding * 2,
                  overlayHeight,
                );
                // Then fill with white
                pageCtx.globalCompositeOperation = "source-over";
                pageCtx.fillRect(
                  whiteBgLeft,
                  overlayTop,
                  tableWidthPx + whiteBgPadding * 2,
                  overlayHeight,
                );
                // Draw multiple overlapping rectangles to ensure complete coverage
                for (let i = 0; i < 3; i++) {
                  pageCtx.fillRect(
                    whiteBgLeft,
                    overlayTop - i,
                    tableWidthPx + whiteBgPadding * 2,
                    overlayHeight + i * 2,
                  );
                }
                pageCtx.restore();
              }

              // Draw white background at the slot position first
              const headerBottomPx =
                slotTopPx + tableMetaOnPage.headerCanvas.height;
              const maskTop = Math.max(0, slotTopPx - whiteBgPadding);
              const maskBottom = headerBottomPx + whiteBgPadding;
              const maskHeight = maskBottom - maskTop;

              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                maskTop,
                tableWidthPx + whiteBgPadding * 2,
                maskHeight,
              );

              // Now draw the new header at the slot position
              pageCtx.drawImage(
                tableMetaOnPage.headerCanvas,
                tableMetaOnPage.leftPx,
                slotTopPx,
                tableMetaOnPage.headerCanvas.width,
                tableMetaOnPage.headerCanvas.height,
              );

              headerSlotCanvases.set(
                tableMetaOnPage.tableId,
                tableMetaOnPage.headerCanvas,
              );
              headerSlotPositions.set(tableMetaOnPage.tableId, slotTopPx);
              headerOffsetPx += tableMetaOnPage.headerCanvas.height;

              const originalHeaderHeightForBody =
                headingHeightPx + spacingPx + theadHeightPx;
              const tableBodyStartInCanvas =
                tableMetaOnPage.headerTopPx + originalHeaderHeightForBody;
              const tableBodyEndInCanvas = tableMetaOnPage.bottomPx;

              const tableBodyStartInPage =
                headerInsertPx +
                Math.max(0, tableBodyStartInCanvas - pageStartPx);
              const tableBodyEndInPage =
                headerInsertPx +
                Math.max(
                  0,
                  Math.min(tableBodyEndInCanvas, pageStartPx + pageContentPx) -
                    pageStartPx,
                );

              if (tableBodyStartInCanvas < tableBodyEndInCanvas) {
                const tableBodySourceStart = Math.max(
                  tableBodyStartInCanvas,
                  pageStartPx,
                );
                const pageEndInCanvas = pageStartPx + pageContentPx;

                const tableNameForMarginElse = tableIdToName.get(
                  tableMetaOnPage.tableId,
                );
                const isBOMTableForMarginElse =
                  tableNameForMarginElse &&
                  tableNameForMarginElse.toLowerCase().includes("bom");

                // For BOM tables, add small buffer only when near the actual table end to capture last rows
                // Don't extend beyond the actual table end to avoid covering content below
                let tableBodySourceEnd = Math.min(
                  tableBodyEndInCanvas,
                  pageEndInCanvas,
                );
                if (
                  isBOMTableForMarginElse &&
                  tableBodyEndInCanvas <= pageEndInCanvas
                ) {
                  // Only add buffer if table ends on this page and we're close to the end
                  const bomBufferPx = Math.floor(20 / pixelToMm); // Reduced buffer
                  tableBodySourceEnd = Math.min(
                    tableBodyEndInCanvas + bomBufferPx,
                    pageEndInCanvas,
                  );
                }

                const tableBodySourceHeight = Math.max(
                  0,
                  tableBodySourceEnd - tableBodySourceStart,
                );

                const headerBodySpacingPx = 5;
                const tableBodyDestStart = headerBottomPx + headerBodySpacingPx;

                const isRevisionTableForMarginElse =
                  tableNameForMarginElse &&
                  tableNameForMarginElse.toLowerCase().includes("revision");
                const isTrainingLogTableForMarginElse =
                  tableNameForMarginElse &&
                  tableNameForMarginElse.toLowerCase().includes("training");
                const needsExtraFooterMarginElse =
                  isRevisionTableForMarginElse ||
                  isTrainingLogTableForMarginElse ||
                  isBOMTableForMarginElse;
                const extraFooterMarginPxElse = needsExtraFooterMarginElse
                  ? Math.floor(8 / pixelToMm)
                  : 0;
                const footerMarginForRowsPx =
                  Math.floor(
                    (contentBottomPadding + footerHeight + 10) / pixelToMm,
                  ) + extraFooterMarginPxElse;
                const safePageBottom =
                  pageCanvas.height - footerMarginForRowsPx;
                const pageContentEndInPageCanvas =
                  headerInsertPx + pageContentPx;
                const availableSpace = Math.min(
                  safePageBottom - tableBodyDestStart,
                  pageContentEndInPageCanvas - tableBodyDestStart,
                  tableBodyEndInPage - tableBodyDestStart,
                );
                const tableBodyDrawHeight = Math.min(
                  tableBodySourceHeight,
                  Math.max(0, availableSpace),
                );

                if (
                  tableBodyDrawHeight > 0 &&
                  tableBodySourceHeight > 0 &&
                  tableBodySourceStart < tableBodySourceEnd &&
                  tableBodyDestStart < safePageBottom
                ) {
                  pageCtx.drawImage(
                    canvas,
                    tableMetaOnPage.leftPx,
                    tableBodySourceStart,
                    tableWidthPx,
                    tableBodyDrawHeight,
                    tableMetaOnPage.leftPx,
                    tableBodyDestStart,
                    tableWidthPx,
                    tableBodyDrawHeight,
                  );
                }
              }
            }

            return;
          }

          if (isBOM && hasHeading) {
            // BOM table title and page indicators are now handled in the PDF
            // header overlay using bomTablePageIndicators. Leave the original
            // headerCanvas and table body drawing unchanged here.
            return;
          }

          const tableHeaderTopInPage = Math.max(
            0,
            tableMetaOnPage.headerTopPx - pageStartPx,
          );
          const originalHeaderTopInPageCanvas =
            headerInsertPx + tableHeaderTopInPage;

          const whiteBgPadding = 5;
          const tableWidthPx =
            tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width;
          const whiteBgLeft = Math.max(
            0,
            tableMetaOnPage.leftPx - whiteBgPadding,
          );
          const whiteBgTop = Math.max(
            0,
            originalHeaderTopInPageCanvas - whiteBgPadding,
          );

          if (
            !tableMetaOnPage.headerCanvas ||
            tableMetaOnPage.headerCanvas.width === 0 ||
            tableMetaOnPage.headerCanvas.height === 0
          ) {
            return;
          }

          // Skip Item Required tables - they're now rendered with autotable which handles the heading
          const isItemRequiredTableOnPage = tableMetaOnPage.isItemRequiredTable;
          const itemRequiredTableDataOnPage = itemRequiredTablesData.find(
            (t) => t.tableId === tableMetaOnPage.tableId,
          );
          if (isItemRequiredTableOnPage && itemRequiredTableDataOnPage) {
            return;
          }

          const needsStandardIndicator = false;
          if (
            hasHeading &&
            indicatorInfo &&
            indicatorInfo.totalPages > 1 &&
            !needsStandardIndicator
          ) {
            // Skip Item Required tables - they're now rendered with autotable which handles the heading
            if (isItemRequiredTableOnPage && itemRequiredTableDataOnPage) {
              // Don't draw heading for Item Required tables
            } else {
              const headingTopPx = Math.round(
                tableMetaOnPage.headingTopDom * domToCanvasScaleY,
              );
              const headingHeightPx = Math.round(
                tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
              );

              const originalHeadingText = (
                tableMetaOnPage.headingElement.textContent || ""
              ).trim();
              const baseHeadingText = originalHeadingText
                .replace(/\s*\(\d+\/\d+\)\s*$/, "")
                .trim();
              const newHeadingText = `${baseHeadingText} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;

              const spacingPx = Math.round(
                (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
              ); // 2px for Training Log, 10px for others
              const theadTopPx = Math.round(
                tableMetaOnPage.theadTopDom * domToCanvasScaleY,
              );
              const theadHeightPx = Math.round(
                tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
              );
              const totalHeight = headingHeightPx + spacingPx + theadHeightPx;

              const modifiedCanvas = document.createElement("canvas");
              modifiedCanvas.width = tableMetaOnPage.headerCanvas.width;
              modifiedCanvas.height = totalHeight;
              const modifiedCtx = modifiedCanvas.getContext("2d");

              modifiedCtx.fillStyle = "#ffffff";
              modifiedCtx.fillRect(
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );

              modifiedCtx.fillStyle = "#000000";
              modifiedCtx.font = "bold 30px Arial";
              modifiedCtx.textAlign = "left";
              modifiedCtx.textBaseline = "top";
              modifiedCtx.fillText(newHeadingText, 0, 0);

              const theadStartInOriginalCanvas = headingHeightPx + spacingPx;
              modifiedCtx.drawImage(
                tableMetaOnPage.headerCanvas,
                0,
                theadStartInOriginalCanvas,
                tableMetaOnPage.widthPx,
                theadHeightPx,
                0,
                headingHeightPx + spacingPx,
                tableMetaOnPage.widthPx,
                theadHeightPx,
              );

              tableMetaOnPage.headerCanvas = modifiedCanvas;
            }
          } else if (indicatorInfo && indicatorInfo.showPageIndicator) {
            const indicatorMarginTopPx = 5;
            const indicatorTextHeightPx = 60;
            const indicatorHeightPx =
              indicatorMarginTopPx + indicatorTextHeightPx;

            const originalTheadHeightPx = Math.round(
              tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
            );

            let theadStartInHeaderCanvas = 0;
            let needsTheadExtraction = false;
            let titleText = null;
            let titleHeightPx = 0;
            const spacingBetweenTitleAndIndicatorPx = 15;
            const spacingBetweenIndicatorAndTheadPx = 10;
            if (
              tableMetaOnPage.headingElement &&
              tableMetaOnPage.headingHeightDom !== null
            ) {
              const originalHeadingText = (
                tableMetaOnPage.headingElement.textContent || ""
              ).trim();
              titleText = originalHeadingText
                .replace(/\s*\(\d+\/\d+\)\s*$/, "")
                .trim();
              const headingHeightPx = Math.round(
                tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
              );
              const spacingPx = Math.round(
                (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
              ); // 2px for Training Log, 10px for others
              theadStartInHeaderCanvas = headingHeightPx + spacingPx;
              needsTheadExtraction = true;
              titleHeightPx = 20;
            } else {
              theadStartInHeaderCanvas = 0;
              needsTheadExtraction = false;
            }

            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = tableMetaOnPage.headerCanvas.width;

            if (needsTheadExtraction) {
              const titleSectionHeight =
                titleText && indicatorInfo && indicatorInfo.pageSeq === 1
                  ? titleHeightPx + spacingBetweenTitleAndIndicatorPx
                  : 0;
              modifiedCanvas.height =
                titleSectionHeight +
                indicatorHeightPx +
                spacingBetweenIndicatorAndTheadPx +
                originalTheadHeightPx;
            } else {
              modifiedCanvas.height =
                indicatorHeightPx +
                spacingBetweenIndicatorAndTheadPx +
                tableMetaOnPage.headerCanvas.height;
            }

            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            let currentY = 0;

            modifiedCtx.fillStyle = "#0d1b2a";
            modifiedCtx.font = "bold 30px Arial";
            modifiedCtx.textAlign = "left";
            modifiedCtx.textBaseline = "middle";
            modifiedCtx.fillText(
              indicatorInfo.indicatorText,
              0,
              currentY + indicatorMarginTopPx + indicatorTextHeightPx / 2,
            );
            currentY += indicatorHeightPx + spacingBetweenIndicatorAndTheadPx;

            if (needsTheadExtraction) {
              const theadWidthPx = tableMetaOnPage.headerCanvas.width;
              if (
                theadWidthPx > 0 &&
                originalTheadHeightPx > 0 &&
                theadStartInHeaderCanvas >= 0 &&
                theadStartInHeaderCanvas + originalTheadHeightPx <=
                  tableMetaOnPage.headerCanvas.height
              ) {
                modifiedCtx.drawImage(
                  tableMetaOnPage.headerCanvas,
                  0,
                  theadStartInHeaderCanvas,
                  theadWidthPx,
                  originalTheadHeightPx,
                  0,
                  currentY,
                  theadWidthPx,
                  originalTheadHeightPx,
                );
              }
            } else {
              modifiedCtx.drawImage(tableMetaOnPage.headerCanvas, 0, currentY);
            }

            tableMetaOnPage.headerCanvas = modifiedCanvas;
          }

          if (
            (hasHeading && indicatorInfo && indicatorInfo.totalPages > 1) ||
            (indicatorInfo && indicatorInfo.showPageIndicator)
          ) {
            // Skip Item Required tables - they're now rendered with autotable which handles the heading
            if (isItemRequiredTableOnPage && itemRequiredTableDataOnPage) {
              return;
            }
            if (isTrainingLogOrBOM) {
              return;
            }

            const currentHeaderCanvas = tableMetaOnPage.headerCanvas;
            const originalTheadHeightPx = Math.round(
              tableMetaOnPage.theadHeightDom * domToCanvasScaleY,
            );
            const originalHeaderHeightPx = tableMetaOnPage.headingElement
              ? Math.round(
                  tableMetaOnPage.headingHeightDom * domToCanvasScaleY,
                ) +
                Math.round((isTrainingLog ? 2 : 10) * domToCanvasScaleY) +
                originalTheadHeightPx
              : originalTheadHeightPx;

            const originalHeaderBottomInPageCanvas =
              originalHeaderTopInPageCanvas + originalHeaderHeightPx;
            const slotTopPx =
              originalHeaderBottomInPageCanvas - currentHeaderCanvas.height;

            const minOffset =
              headerInsertPx === 0 && tableHeaderTopInPage < 5 ? 10 : 0;
            const adjustedSlotTopPx = Math.max(
              minOffset,
              Math.min(
                slotTopPx,
                pageCanvas.height - currentHeaderCanvas.height,
              ),
            );

            const whiteBgWidth = tableWidthPx + whiteBgPadding * 2;
            let whiteBgTopForIndicator;
            if (hasHeading && tableMetaOnPage.headingElement) {
              const originalHeadingTopInPageCanvas =
                headerInsertPx +
                Math.max(
                  0,
                  Math.round(
                    tableMetaOnPage.headingTopDom * domToCanvasScaleY,
                  ) - pageStartPx,
                );
              whiteBgTopForIndicator = Math.max(
                0,
                Math.min(adjustedSlotTopPx, originalHeadingTopInPageCanvas) -
                  whiteBgPadding,
              );
            } else {
              whiteBgTopForIndicator = Math.max(
                0,
                Math.min(adjustedSlotTopPx, originalHeaderTopInPageCanvas) -
                  whiteBgPadding,
              );
            }
            // Use the actual header canvas bottom instead of original header bottom
            // This prevents white background from covering table body rows on subsequent pages
            // Don't add bottom padding to avoid covering the first table body row
            const whiteBgBottom =
              adjustedSlotTopPx + currentHeaderCanvas.height;
            const whiteBgHeight = Math.max(
              0,
              whiteBgBottom - whiteBgTopForIndicator,
            );

            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(
              whiteBgLeft,
              whiteBgTopForIndicator,
              whiteBgWidth,
              whiteBgHeight,
            );

            pageCtx.drawImage(
              currentHeaderCanvas,
              tableMetaOnPage.leftPx,
              adjustedSlotTopPx,
              currentHeaderCanvas.width,
              currentHeaderCanvas.height,
            );

            const tableBodyStartInCanvas =
              tableMetaOnPage.headerTopPx + originalHeaderHeightPx;
            const tableBodyEndInCanvas = tableMetaOnPage.bottomPx;
            const headerBodySpacingPx = 5;
            const modifiedHeaderBottomInPageCanvas =
              adjustedSlotTopPx + currentHeaderCanvas.height;
            const tableBodyStartInPage =
              modifiedHeaderBottomInPageCanvas + headerBodySpacingPx;
            const tableBottomInPage = Math.min(
              tableMetaOnPage.bottomPx - pageStartPx + headerInsertPx,
              pageCanvas.height,
            );

            const tableSpacingPx = 20;
            let effectiveTableBottom = tableBottomInPage;

            const tablesAfterThis = allTablesOnPage
              .filter((otherTable) => {
                return (
                  otherTable.tableId !== tableMetaOnPage.tableId &&
                  otherTable.topPx > tableMetaOnPage.topPx &&
                  otherTable.topPx < pageStartPx + pageContentPx
                );
              })
              .sort((a, b) => a.topPx - b.topPx);
            if (tablesAfterThis.length > 0) {
              const nextTable = tablesAfterThis[0];
              const nextTableTopInPage =
                headerInsertPx + Math.max(0, nextTable.topPx - pageStartPx);
              effectiveTableBottom = Math.min(
                effectiveTableBottom,
                nextTableTopInPage - tableSpacingPx,
              );
            }

            const tableBodySourceStart = Math.max(
              tableBodyStartInCanvas,
              pageStartPx,
            );
            const tableBodySourceEnd = Math.min(
              tableBodyEndInCanvas,
              pageStartPx + pageContentPx,
            );
            const tableBodySourceHeight =
              tableBodySourceEnd - tableBodySourceStart;

            const tableNameForMarginOther = tableIdToName.get(
              tableMetaOnPage.tableId,
            );
            const isRevisionTableForMarginOther =
              tableNameForMarginOther &&
              tableNameForMarginOther.toLowerCase().includes("revision");
            const isTrainingLogTableForMarginOther =
              tableNameForMarginOther &&
              tableNameForMarginOther.toLowerCase().includes("training");
            const isBOMTableForMarginOther =
              tableNameForMarginOther &&
              tableNameForMarginOther.toLowerCase().includes("bom");
            const needsExtraFooterMarginOther =
              isRevisionTableForMarginOther ||
              isTrainingLogTableForMarginOther ||
              isBOMTableForMarginOther;
            const extraFooterMarginPxOther = needsExtraFooterMarginOther
              ? Math.floor(8 / pixelToMm)
              : 0;
            const footerMarginForRowsPx =
              Math.floor(
                (contentBottomPadding + footerHeight + 10) / pixelToMm,
              ) + extraFooterMarginPxOther;
            const safePageBottom = pageCanvas.height - footerMarginForRowsPx;
            const tableBodyDestStart = tableBodyStartInPage;
            const tableBodyDestEnd = Math.min(
              effectiveTableBottom,
              headerInsertPx + pageContentPx,
              safePageBottom,
            );
            const tableBodyDestHeight = Math.max(
              0,
              tableBodyDestEnd - tableBodyDestStart,
            );

            if (
              tableBodySourceHeight > 0 &&
              tableBodyDestHeight > 0 &&
              tableBodyStartInCanvas < tableBodyEndInCanvas
            ) {
              const drawHeight = Math.min(
                tableBodySourceHeight,
                tableBodyDestHeight,
              );

              const sourceOffset =
                tableBodySourceStart - tableBodyStartInCanvas;

              if (drawHeight > 0 && sourceOffset >= 0) {
                pageCtx.drawImage(
                  canvas,
                  tableMetaOnPage.leftPx,
                  tableBodyStartInCanvas + sourceOffset,
                  tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                  drawHeight,
                  tableMetaOnPage.leftPx,
                  tableBodyDestStart,
                  tableMetaOnPage.widthPx || tableMetaOnPage.headerCanvas.width,
                  drawHeight,
                );
              }
            }

            headerSlotCanvases.set(
              tableMetaOnPage.tableId,
              currentHeaderCanvas,
            );
            headerSlotPositions.set(tableMetaOnPage.tableId, adjustedSlotTopPx);
          } else {
            // Skip Item Required tables - they're now rendered with autotable which handles the heading
            if (isItemRequiredTableOnPage && itemRequiredTableDataOnPage) {
              return;
            }
            const slotTopPx = originalHeaderTopInPageCanvas;

            const adjustedSlotTopPx = Math.max(
              0,
              Math.min(
                slotTopPx,
                pageCanvas.height - tableMetaOnPage.headerCanvas.height,
              ),
            );

            const whiteBgWidth =
              tableMetaOnPage.headerCanvas.width + whiteBgPadding * 2;
            const whiteBgHeight =
              tableMetaOnPage.headerCanvas.height + whiteBgPadding * 2;

            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(
              whiteBgLeft,
              whiteBgTop,
              whiteBgWidth,
              whiteBgHeight,
            );

            pageCtx.drawImage(
              tableMetaOnPage.headerCanvas,
              tableMetaOnPage.leftPx,
              adjustedSlotTopPx,
              tableMetaOnPage.headerCanvas.width,
              tableMetaOnPage.headerCanvas.height,
            );

            headerSlotCanvases.set(
              tableMetaOnPage.tableId,
              tableMetaOnPage.headerCanvas,
            );
            headerSlotPositions.set(tableMetaOnPage.tableId, adjustedSlotTopPx);
          }
        });

        continuingTables.forEach((meta) => {
          if (!meta.headerCanvas) return;
          const slotTopPx = headerOffsetPx;
          const indicatorInfo = getTableIndicatorInfo(meta.tableId);

          const tableName = tableIdToName.get(meta.tableId);
          const isTrainingLog =
            tableName && tableName.toLowerCase().includes("training");
          const isBOM = tableName && tableName.toLowerCase().includes("bom");
          const isTrainingLogOrBOM = isTrainingLog || isBOM;
          const isItemRequired = meta.isItemRequiredTable || false;

          // Mask the original header from main canvas before drawing continuing table header
          // The main canvas content (drawn earlier) includes the original table header
          // We need to mask it to prevent duplicate headers
          const originalHeaderTopInPage = Math.max(
            0,
            meta.headerTopPx - pageStartPx,
          );

          // If the original header position is within the page content, mask it
          if (
            originalHeaderTopInPage >= 0 &&
            originalHeaderTopInPage < pageContentPx
          ) {
            const headingHeightPx = Math.round(
              meta.headingHeightDom * domToCanvasScaleY,
            );
            const spacingPx = Math.round(
              (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
            ); // 2px for Training Log, 10px for others
            const theadHeightPx = Math.round(
              meta.theadHeightDom * domToCanvasScaleY,
            );
            const originalHeaderHeight =
              headingHeightPx + spacingPx + theadHeightPx;

            const originalHeaderTopInPageCanvas =
              headerInsertPx + originalHeaderTopInPage;
            const originalHeaderBottomInPageCanvas =
              originalHeaderTopInPageCanvas + originalHeaderHeight;

            const whiteBgPadding = 5;
            const tableWidthPx = meta.widthPx || meta.headerCanvas.width;
            const whiteBgLeft = Math.max(0, meta.leftPx - whiteBgPadding);

            // Mask the original header area from main canvas
            const overlayTop = Math.max(
              0,
              originalHeaderTopInPageCanvas - whiteBgPadding,
            );
            const overlayBottom =
              originalHeaderBottomInPageCanvas + whiteBgPadding;
            const overlayHeight = overlayBottom - overlayTop;

            if (overlayHeight > 0) {
              pageCtx.save();
              // Use destination-out to clear the area first
              pageCtx.globalCompositeOperation = "destination-out";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPadding * 2,
                overlayHeight,
              );
              // Then fill with white
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillRect(
                whiteBgLeft,
                overlayTop,
                tableWidthPx + whiteBgPadding * 2,
                overlayHeight,
              );
              // Draw multiple overlapping rectangles to ensure complete coverage
              for (let i = 0; i < 3; i++) {
                pageCtx.fillRect(
                  whiteBgLeft,
                  overlayTop - i,
                  tableWidthPx + whiteBgPadding * 2,
                  overlayHeight + i * 2,
                );
              }
              pageCtx.restore();
            }
          }

          if (meta.tableId === bomTableId) {
          }

          if (
            isTrainingLog &&
            meta.headingElement &&
            meta.headingTopDom !== null
          ) {
            const theadHeightPx = Math.round(
              meta.theadHeightDom * domToCanvasScaleY,
            );
            const headingHeightPx = Math.round(
              meta.headingHeightDom * domToCanvasScaleY,
            );
            const spacingPx = Math.round(2 * domToCanvasScaleY); // 2px spacing for Training Log
            const theadStartInOriginalCanvas = headingHeightPx + spacingPx;

            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = meta.headerCanvas.width;
            modifiedCanvas.height = theadHeightPx;
            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            modifiedCtx.drawImage(
              meta.headerCanvas,
              0,
              theadStartInOriginalCanvas,
              meta.widthPx || meta.headerCanvas.width,
              theadHeightPx,
              0,
              0,
              meta.widthPx || meta.headerCanvas.width,
              theadHeightPx,
            );

            pageCtx.drawImage(
              modifiedCanvas,
              meta.leftPx,
              headerOffsetPx,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            headerSlotCanvases.set(meta.tableId, modifiedCanvas);
            headerSlotPositions.set(meta.tableId, slotTopPx);
            headerOffsetPx += modifiedCanvas.height;

            const whiteBgPadding = 5;
            const tableWidthPx = meta.widthPx || meta.headerCanvas.width;
            const whiteBgLeft = Math.max(0, meta.leftPx - whiteBgPadding);

            const originalHeadingTopPx = Math.round(
              meta.headingTopDom * domToCanvasScaleY,
            );
            const headingTopInPage = Math.max(
              0,
              originalHeadingTopPx - pageStartPx,
            );
            const headingTopInPageCanvas = headerInsertPx + headingTopInPage;

            const originalHeaderHeightForBg =
              headingHeightPx + spacingPx + theadHeightPx;
            const originalHeaderBottomForBg =
              headingTopInPageCanvas + originalHeaderHeightForBg;

            const maskTop = Math.max(
              0,
              headingTopInPageCanvas - whiteBgPadding,
            );
            const maskBottom = originalHeaderBottomForBg + whiteBgPadding;
            const maskHeight = maskBottom - maskTop;

            if (maskHeight > 0) {
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(
                whiteBgLeft,
                maskTop,
                tableWidthPx + whiteBgPadding * 2,
                maskHeight,
              );
            }
          }

          // Skip BOM heading drawing if it's being rendered with autotable (skip all BOM tables including main one)
          if (isBOM && meta.headingElement && meta.headingTopDom !== null) {
            // Skip if this is the main BOM table being rendered with autotable
            if (meta.tableId === bomTableId) return;
            const bomIndicatorInfo = getTableIndicatorInfo(meta.tableId);
            const bomSpansMultiplePages =
              bomIndicatorInfo && bomIndicatorInfo.totalPages > 1;

            const theadHeightPx = Math.round(
              meta.theadHeightDom * domToCanvasScaleY,
            );
            const headingHeightPx = Math.round(
              meta.headingHeightDom * domToCanvasScaleY,
            );
            const spacingPx = Math.round(10 * domToCanvasScaleY);
            const theadStartInOriginalCanvas = headingHeightPx + spacingPx;

            const originalHeadingText = (
              meta.headingElement.textContent || ""
            ).trim();
            let titleText = originalHeadingText
              .replace(/\s*\(\d+\/\d+\)\s*$/, "")
              .trim();

            if (!titleText.toUpperCase().endsWith("BOM")) {
              titleText = (meta.headingElement.textContent || "").trim();
              titleText = titleText.replace(/\s*\(\d+\/\d+\)\s*$/, "").trim();
            }

            if (bomSpansMultiplePages && bomIndicatorInfo) {
              titleText =
                titleText +
                " (" +
                bomIndicatorInfo.pageSeq +
                "/" +
                bomIndicatorInfo.totalPages +
                ")";
            }

            const titlePadding = 5;
            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = meta.headerCanvas.width;
            modifiedCanvas.height =
              headingHeightPx + titlePadding + spacingPx + theadHeightPx;
            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            modifiedCtx.fillStyle = "#000000";
            modifiedCtx.font = "bold 30px Arial";
            modifiedCtx.textAlign = "left";
            modifiedCtx.textBaseline = "top";
            const titleY = titlePadding / 2;
            modifiedCtx.fillText(titleText, 0, titleY);

            const theadDrawY = headingHeightPx + titlePadding + spacingPx;
            modifiedCtx.drawImage(
              meta.headerCanvas,
              0,
              theadStartInOriginalCanvas,
              meta.widthPx || meta.headerCanvas.width,
              theadHeightPx,
              0,
              theadDrawY,
              meta.widthPx || meta.headerCanvas.width,
              theadHeightPx,
            );

            pageCtx.drawImage(
              modifiedCanvas,
              meta.leftPx,
              headerOffsetPx,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            headerSlotCanvases.set(meta.tableId, modifiedCanvas);
            headerSlotPositions.set(meta.tableId, slotTopPx);
            headerOffsetPx += modifiedCanvas.height;
          }

          // Skip Item Required tables - they're now rendered with autotable which handles the heading
          if (isItemRequired) {
            // Item Required tables are rendered with autotable, so skip all canvas-based heading drawing
            return;
          }

          const hasHeadingContinuing =
            meta.headingElement && meta.headingTopDom !== null;
          const tableSpansMultiplePagesContinuing =
            indicatorInfo && indicatorInfo.totalPages > 1;
          const needsStandardIndicatorContinuing = false;
          if (
            hasHeadingContinuing &&
            tableSpansMultiplePagesContinuing &&
            indicatorInfo &&
            !needsStandardIndicatorContinuing
          ) {
            const headingTopPx = Math.round(
              meta.headingTopDom * domToCanvasScaleY,
            );
            const headingHeightPx = Math.round(
              meta.headingHeightDom * domToCanvasScaleY,
            );

            const originalHeadingText = (
              meta.headingElement.textContent || ""
            ).trim();
            const baseHeadingText = originalHeadingText
              .replace(/\s*\(\d+\/\d+\)\s*$/, "")
              .trim();
            const newHeadingText = `${baseHeadingText} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;

            const spacingPx = Math.round(
              (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
            ); // 2px for Training Log, 10px for others
            const theadTopPx = Math.round(meta.theadTopDom * domToCanvasScaleY);
            const theadHeightPx = Math.round(
              meta.theadHeightDom * domToCanvasScaleY,
            );
            const totalHeight = headingHeightPx + spacingPx + theadHeightPx;

            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = meta.headerCanvas.width;
            modifiedCanvas.height = totalHeight;
            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            modifiedCtx.fillStyle = "#000000";
            modifiedCtx.font = "bold 30px Arial";
            modifiedCtx.textAlign = "left";
            modifiedCtx.textBaseline = "top";
            modifiedCtx.fillText(newHeadingText, 0, 0);

            const theadStartInOriginalCanvas = headingHeightPx + spacingPx;
            modifiedCtx.drawImage(
              meta.headerCanvas,
              0,
              theadStartInOriginalCanvas,
              meta.widthPx,
              theadHeightPx,
              0,
              headingHeightPx + spacingPx,
              meta.widthPx,
              theadHeightPx,
            );

            pageCtx.drawImage(
              modifiedCanvas,
              meta.leftPx,
              headerOffsetPx,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            headerSlotCanvases.set(meta.tableId, modifiedCanvas);
            headerOffsetPx += modifiedCanvas.height;
          } else if (indicatorInfo && indicatorInfo.showPageIndicator) {
            const indicatorMarginTopPx = 5;
            const indicatorTextHeightPx = 60;
            const indicatorHeightPx =
              indicatorMarginTopPx + indicatorTextHeightPx;

            const originalTheadHeightPx = Math.round(
              meta.theadHeightDom * domToCanvasScaleY,
            );

            let theadStartInHeaderCanvas = 0;
            let titleText = null;
            let titleHeightPx = 0;
            const spacingBetweenTitleAndIndicatorPx = 15;
            const spacingBetweenIndicatorAndTheadPx = 10;
            if (meta.headingElement && meta.headingHeightDom !== null) {
              const originalHeadingText = (
                meta.headingElement.textContent || ""
              ).trim();
              titleText = originalHeadingText
                .replace(/\s*\(\d+\/\d+\)\s*$/, "")
                .trim();
              const headingHeightPx = Math.round(
                meta.headingHeightDom * domToCanvasScaleY,
              );
              const spacingPx = Math.round(
                (isTrainingLog ? 2 : 10) * domToCanvasScaleY,
              ); // 2px for Training Log, 10px for others

              const expectedHeightWithoutIndicator =
                headingHeightPx + spacingPx + originalTheadHeightPx;
              const headerCanvasHasIndicator =
                meta.headerCanvas.height > expectedHeightWithoutIndicator + 20;

              if (headerCanvasHasIndicator) {
                theadStartInHeaderCanvas =
                  indicatorHeightPx + headingHeightPx + spacingPx;
              } else {
                theadStartInHeaderCanvas = headingHeightPx + spacingPx;
              }
              titleHeightPx = 20;
            } else {
              const headerCanvasHasIndicator =
                meta.headerCanvas.height > originalTheadHeightPx + 20;
              theadStartInHeaderCanvas = headerCanvasHasIndicator
                ? indicatorHeightPx
                : 0;
            }

            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = meta.widthPx || meta.headerCanvas.width;

            const needsTheadExtraction =
              meta.headingElement && meta.headingHeightDom !== null;

            if (needsTheadExtraction) {
              const titleSectionHeight =
                titleText && indicatorInfo && indicatorInfo.pageSeq === 1
                  ? titleHeightPx + spacingBetweenTitleAndIndicatorPx
                  : 0;
              modifiedCanvas.height =
                titleSectionHeight +
                indicatorHeightPx +
                spacingBetweenIndicatorAndTheadPx +
                originalTheadHeightPx;
            } else {
              modifiedCanvas.height =
                indicatorHeightPx +
                spacingBetweenIndicatorAndTheadPx +
                meta.headerCanvas.height;
            }

            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            let currentY = 0;

            if (titleText && indicatorInfo && indicatorInfo.pageSeq === 1) {
              modifiedCtx.fillStyle = "#000000";
              modifiedCtx.font = "bold 30px Arial";
              modifiedCtx.textAlign = "left";
              modifiedCtx.textBaseline = "top";
              modifiedCtx.fillText(titleText, 0, currentY);
              currentY += titleHeightPx + spacingBetweenTitleAndIndicatorPx;
            }

            modifiedCtx.fillStyle = "#0d1b2a";
            modifiedCtx.font = "bold 30px Arial";
            modifiedCtx.textAlign = "left";
            modifiedCtx.textBaseline = "middle";
            modifiedCtx.fillText(
              indicatorInfo.indicatorText,
              0,
              currentY + indicatorMarginTopPx + indicatorTextHeightPx / 2,
            );
            currentY += indicatorHeightPx + spacingBetweenIndicatorAndTheadPx;

            if (needsTheadExtraction) {
              const theadWidthPx = meta.widthPx || meta.headerCanvas.width;
              if (
                theadWidthPx > 0 &&
                originalTheadHeightPx > 0 &&
                theadStartInHeaderCanvas >= 0 &&
                theadStartInHeaderCanvas + originalTheadHeightPx <=
                  meta.headerCanvas.height
              ) {
                modifiedCtx.drawImage(
                  meta.headerCanvas,
                  0,
                  theadStartInHeaderCanvas,
                  theadWidthPx,
                  originalTheadHeightPx,
                  0,
                  currentY,
                  theadWidthPx,
                  originalTheadHeightPx,
                );
              }
            } else {
              modifiedCtx.drawImage(meta.headerCanvas, 0, currentY);
            }

            pageCtx.drawImage(
              modifiedCanvas,
              meta.leftPx,
              headerOffsetPx,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            headerSlotCanvases.set(meta.tableId, modifiedCanvas);
            headerOffsetPx += modifiedCanvas.height;
          } else {
            pageCtx.drawImage(
              meta.headerCanvas,
              meta.leftPx,
              headerOffsetPx,
              meta.headerCanvas.width,
              meta.headerCanvas.height,
            );

            headerSlotCanvases.set(meta.tableId, meta.headerCanvas);
            headerOffsetPx += meta.headerCanvas.height;
          }

          headerSlotPositions.set(meta.tableId, slotTopPx);
        });

        continuingTables.forEach((meta) => {
          if (!meta.headerCanvas) return;

          const headerTopPx = headerSlotPositions.get(meta.tableId) || 0;
          const headerCanvas =
            headerSlotCanvases.get(meta.tableId) || meta.headerCanvas;
          const headerBottomPx = headerTopPx + headerCanvas.height;

          const tableName = tableIdToName.get(meta.tableId);
          const isTrainingLog =
            tableName && tableName.toLowerCase().includes("training");
          const isBOM = tableName && tableName.toLowerCase().includes("bom");
          const isTrainingLogOrBOM = isTrainingLog || isBOM;
          const isItemRequired = meta.isItemRequiredTable || false;

          const headerHeightForBodyCalc = meta.headingElement
            ? Math.round(meta.headingHeightDom * domToCanvasScaleY) +
              Math.round((isTrainingLog ? 2 : 10) * domToCanvasScaleY) +
              Math.round(meta.theadHeightDom * domToCanvasScaleY)
            : meta.headerCanvas.height;
          const tableBodyStartInCanvas =
            meta.headerTopPx + headerHeightForBodyCalc;
          const pageEndInCanvas = pageStartPx + pageContentPx;
          const tableBodyEndInCanvas = Math.min(meta.bottomPx, pageEndInCanvas);

          const tableBodyStartInPage =
            headerInsertPx + Math.max(0, tableBodyStartInCanvas - pageStartPx);
          const tableBodyEndInPage =
            headerInsertPx + Math.max(0, tableBodyEndInCanvas - pageStartPx);

          const tableWidthPx = meta.widthPx || meta.headerCanvas.width;
          const whiteBgPadding = 5;

          const shouldMask =
            isTrainingLogOrBOM ||
            (tableBodyStartInPage < headerBottomPx &&
              tableBodyEndInPage > headerTopPx);
          const shouldDrawBody =
            meta.bottomPx > pageStartPx &&
            tableBodyStartInCanvas < meta.bottomPx;

          if (shouldMask) {
            if (isTrainingLogOrBOM) {
              if (
                isTrainingLog &&
                meta.headingElement &&
                meta.headingTopDom !== null
              ) {
                const headingHeightPx = Math.round(
                  meta.headingHeightDom * domToCanvasScaleY,
                );
                const spacingPx = Math.round(2 * domToCanvasScaleY); // 2px spacing for Training Log
                const theadHeightPx = Math.round(
                  meta.theadHeightDom * domToCanvasScaleY,
                );
                const originalHeaderHeightForBg =
                  headingHeightPx + spacingPx + theadHeightPx;

                const originalHeadingTopPx = Math.round(
                  meta.headingTopDom * domToCanvasScaleY,
                );
                const headingTopInPage = Math.max(
                  0,
                  originalHeadingTopPx - pageStartPx,
                );
                const headingTopInPageCanvas =
                  headerInsertPx + headingTopInPage;
                const originalHeaderBottomForBg =
                  headingTopInPageCanvas + originalHeaderHeightForBg;

                const headingMaskTop = Math.max(
                  0,
                  headingTopInPageCanvas - whiteBgPadding,
                );
                const headingMaskBottom =
                  originalHeaderBottomForBg + whiteBgPadding;
                const headingMaskHeight = headingMaskBottom - headingMaskTop;

                if (headingMaskHeight > 0) {
                  pageCtx.save();
                  pageCtx.globalCompositeOperation = "destination-out";
                  pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
                  pageCtx.fillRect(
                    Math.max(0, meta.leftPx - whiteBgPadding),
                    headingMaskTop,
                    tableWidthPx + whiteBgPadding * 2,
                    headingMaskHeight,
                  );
                  pageCtx.globalCompositeOperation = "source-over";
                  pageCtx.fillStyle = "#ffffff";
                  pageCtx.fillRect(
                    Math.max(0, meta.leftPx - whiteBgPadding),
                    headingMaskTop,
                    tableWidthPx + whiteBgPadding * 2,
                    headingMaskHeight,
                  );
                  pageCtx.restore();
                }

                const originalTableBodyStartInPage =
                  headerInsertPx +
                  Math.max(0, tableBodyStartInCanvas - pageStartPx);
                const originalTableBodyEndInPage =
                  headerInsertPx +
                  Math.max(
                    0,
                    Math.min(meta.bottomPx, pageStartPx + pageContentPx) -
                      pageStartPx,
                  );

                if (
                  originalTableBodyStartInPage < headerBottomPx &&
                  originalTableBodyEndInPage > headerTopPx
                ) {
                  const maskStart = Math.max(
                    originalTableBodyStartInPage,
                    headerTopPx,
                  );
                  const maskEnd = Math.min(
                    originalTableBodyEndInPage,
                    headerBottomPx,
                  );
                  const maskHeight = Math.max(0, maskEnd - maskStart);

                  if (maskHeight > 0) {
                    pageCtx.save();
                    pageCtx.globalCompositeOperation = "destination-out";
                    pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
                    pageCtx.fillRect(
                      Math.max(0, meta.leftPx - whiteBgPadding),
                      Math.max(0, maskStart - whiteBgPadding),
                      tableWidthPx + whiteBgPadding * 2,
                      maskHeight + whiteBgPadding * 2,
                    );
                    pageCtx.globalCompositeOperation = "source-over";
                    pageCtx.fillStyle = "#ffffff";
                    pageCtx.fillRect(
                      Math.max(0, meta.leftPx - whiteBgPadding),
                      Math.max(0, maskStart - whiteBgPadding),
                      tableWidthPx + whiteBgPadding * 2,
                      maskHeight + whiteBgPadding * 2,
                    );
                    pageCtx.restore();
                  }
                }
              } else {
                const maskStart = Math.max(0, tableBodyStartInPage);
                const maskEnd = Math.min(pageCanvas.height, tableBodyEndInPage);
                const maskHeight = Math.max(0, maskEnd - maskStart);

                if (maskHeight > 0) {
                  pageCtx.save();
                  pageCtx.globalCompositeOperation = "destination-out";
                  pageCtx.fillStyle = "rgba(255, 255, 255, 1)";
                  pageCtx.fillRect(
                    Math.max(0, meta.leftPx - whiteBgPadding),
                    Math.max(0, maskStart - whiteBgPadding),
                    tableWidthPx + whiteBgPadding * 2,
                    maskHeight + whiteBgPadding * 2,
                  );
                  pageCtx.globalCompositeOperation = "source-over";
                  pageCtx.fillStyle = "#ffffff";
                  pageCtx.fillRect(
                    Math.max(0, meta.leftPx - whiteBgPadding),
                    Math.max(0, maskStart - whiteBgPadding),
                    tableWidthPx + whiteBgPadding * 2,
                    maskHeight + whiteBgPadding * 2,
                  );
                  pageCtx.restore();
                }
              }
            } else {
              const overlapStart = Math.max(headerTopPx, tableBodyStartInPage);
              const overlapEnd = Math.min(headerBottomPx, tableBodyEndInPage);
              const overlapHeight = Math.max(0, overlapEnd - overlapStart);

              if (overlapHeight > 0) {
                pageCtx.fillStyle = "#ffffff";
                pageCtx.fillRect(
                  Math.max(0, meta.leftPx - whiteBgPadding),
                  Math.max(0, overlapStart - whiteBgPadding),
                  tableWidthPx + whiteBgPadding * 2,
                  overlapHeight + whiteBgPadding * 2,
                );
              }
            }
          }

          // Draw table body for all continuing tables
          if (shouldDrawBody) {
            const tableName = tableIdToName.get(meta.tableId);
            const isBOMTable =
              tableName && tableName.toLowerCase().includes("bom");

            const tableBodySourceStart = Math.max(
              tableBodyStartInCanvas,
              pageStartPx,
            );
            let tableBodySourceEnd = Math.min(meta.bottomPx, pageEndInCanvas);

            // For BOM tables, add small buffer only when near the actual table end to capture last rows
            // Don't extend beyond the actual table end to avoid covering content below
            if (isBOMTable && meta.bottomPx <= pageEndInCanvas) {
              // Only add buffer if table ends on this page
              const bomBufferPx = Math.floor(20 / pixelToMm); // Reduced buffer
              tableBodySourceEnd = Math.min(
                meta.bottomPx + bomBufferPx,
                pageEndInCanvas,
              );
            }

            const tableBodySourceHeight = Math.max(
              0,
              tableBodySourceEnd - tableBodySourceStart,
            );

            if (
              tableBodySourceHeight > 0 &&
              tableBodySourceStart < tableBodySourceEnd
            ) {
              const headerBodySpacingPx = 5;
              const tableBodyDestStart = headerBottomPx + headerBodySpacingPx;

              const tableBottomInPage =
                headerInsertPx + Math.max(0, meta.bottomPx - pageStartPx);
              const isRevisionTable =
                tableName && tableName.toLowerCase().includes("revision");
              const isTrainingLogTable =
                tableName && tableName.toLowerCase().includes("training");
              const needsExtraFooterMargin =
                isRevisionTable || isTrainingLogTable || isBOMTable;
              const extraFooterMarginPx = needsExtraFooterMargin
                ? Math.floor(8 / pixelToMm)
                : 0;
              const footerMarginForRowsPx =
                Math.floor(
                  (contentBottomPadding + footerHeight + 10) / pixelToMm,
                ) + extraFooterMarginPx;
              const safePageBottom = pageCanvas.height - footerMarginForRowsPx;
              const pageContentEndInPageCanvas = headerInsertPx + pageContentPx;

              const availableSpace = Math.min(
                safePageBottom - tableBodyDestStart,
                pageContentEndInPageCanvas - tableBodyDestStart,
                tableBottomInPage - tableBodyDestStart,
              );
              const tableBodyDrawHeight = Math.min(
                tableBodySourceHeight,
                Math.max(0, availableSpace),
              );

              if (
                tableBodyDrawHeight > 0 &&
                tableBodyDestStart < safePageBottom &&
                tableBodyDestStart + tableBodyDrawHeight <=
                  pageContentEndInPageCanvas
              ) {
                pageCtx.drawImage(
                  canvas,
                  meta.leftPx,
                  tableBodySourceStart,
                  tableWidthPx,
                  tableBodyDrawHeight,
                  meta.leftPx,
                  tableBodyDestStart,
                  tableWidthPx,
                  tableBodyDrawHeight,
                );
              }
            }
          }
        });

        const continuingHeaderSlots = continuingTables
          .map((meta) => {
            const canvasToUse =
              headerSlotCanvases.get(meta.tableId) || meta.headerCanvas;
            return {
              leftPx: meta.leftPx,
              topPx: headerSlotPositions.get(meta.tableId) || 0,
              widthPx: canvasToUse?.width || 0,
              heightPx: canvasToUse?.height || 0,
              dataUrl: canvasToUse ? canvasToUse.toDataURL("image/png") : null,
              source: meta,
            };
          })
          .filter((slot) => slot.dataUrl);

        const startingTableHeaderSlots = allTablesOnPage
          .map((tableMetaOnPage) => {
            const tableId = tableMetaOnPage.tableId;

            if (!allTrackedTableIds.includes(tableId)) return null;

            const tableIsContinuing = continuingTables.some(
              (meta) => meta.tableId === tableId,
            );
            if (tableIsContinuing) return null;
            const tableName = tableIdToName.get(tableId);
            const isTrainingLog =
              tableName && tableName.toLowerCase().includes("training");
            if (isTrainingLog) {
              const bomTableOnPage = allTablesOnPage.find((otherTable) => {
                const otherTableName = tableIdToName.get(otherTable.tableId);
                const otherIsBOM =
                  otherTableName &&
                  otherTableName.toLowerCase().includes("bom");
                return otherIsBOM && otherTable.tableId !== tableId;
              });

              if (
                bomTableOnPage &&
                bomTableOnPage.topPx < tableMetaOnPage.topPx
              ) {
                return null;
              }
            }

            const canvasToUse = headerSlotCanvases.get(tableId);
            if (!canvasToUse) return null;

            const topPx = headerSlotPositions.get(tableId);
            if (topPx === undefined) return null;

            return {
              leftPx: tableMetaOnPage.leftPx,
              topPx: topPx,
              widthPx: canvasToUse.width || 0,
              heightPx: canvasToUse.height || 0,
              dataUrl: canvasToUse.toDataURL("image/png"),
              source: tableMetaOnPage,
            };
          })
          .filter((slot) => slot !== null);

        const headerSlots = [
          ...startingTableHeaderSlots,
          ...continuingHeaderSlots,
        ];

        const getStepIndicatorInfo = (stepId) => {
          const pageIndices = multiPageSteps.get(stepId) || [];
          // Always return indicator info, even for single-page steps (1/1)
          if (pageIndices.length === 0) return null;
          if (!pageIndices.includes(pageIndexForTracking)) return null;
          const sortedPageIndices = [...pageIndices].sort((a, b) => a - b);
          const pageSeq = sortedPageIndices.indexOf(pageIndexForTracking) + 1;
          return {
            pageSeq,
            totalPages: sortedPageIndices.length,
          };
        };

        stepsWithContentOnPage.forEach((stepMeta) => {
          // Blocks that belong to this step for vertical span / page-count math.
          // Step numbering should be based on direct step child nodes only.
          const stepChildElements = blockMetasPx.filter((meta) => {
            if (!childBlockLabels.has(meta.label)) return false;
            if (meta.substepId !== null && meta.substepId !== undefined)
              return false;
            return meta.stepId === stepMeta.stepId;
          });

          // Get Item Required tables for this step (to include them as child nodes)
          const stepItemRequiredTables = tableMetas.filter((tableMeta) => {
            if (!tableMeta.isItemRequiredTable) return false;
            // Check if table belongs to this step by stepId
            if (tableMeta.stepId === stepMeta.stepId) return true;
            // Fallback: check if table is within step's DOM element
            if (
              tableMeta.stepContainer &&
              stepMeta.element &&
              tableMeta.stepContainer === stepMeta.element
            )
              return true;
            return false;
          });

          if (stepMeta.headerTopPx !== undefined && stepMeta.headerTopPx >= 0) {
            const stepHeaderTop = stepMeta.headerTopPx;
            const stepHeaderBottom =
              stepMeta.headerTopPx + (stepMeta.headerHeightPx || 0);

            // Find the bottom of the last child node or Item Required table, or use step header bottom if no children
            let stepBottom = stepHeaderBottom;
            const allChildBottomPx = [];
            if (stepChildElements.length > 0) {
              allChildBottomPx.push(
                ...stepChildElements.map((meta) => meta.bottomPx),
              );
            }
            if (stepItemRequiredTables.length > 0) {
              allChildBottomPx.push(
                ...stepItemRequiredTables.map(
                  (tableMeta) => tableMeta.bottomPx,
                ),
              );
            }
            if (
              stepMeta.stepShortDescBottomPx != null &&
              Number.isFinite(stepMeta.stepShortDescBottomPx)
            ) {
              allChildBottomPx.push(stepMeta.stepShortDescBottomPx);
            }
            if (allChildBottomPx.length > 0) {
              stepBottom = Math.max(stepHeaderBottom, ...allChildBottomPx);
            }

            // Find which pages this step spans by checking actual page boundaries
            // Check all pages created so far plus current page
            const allPagesToCheck = [
              ...pageImages,
              {
                pageStartPx: pageStartPx,
                pageContentPx: pageContentPx,
              },
            ];

            const stepPageIndices = [];
            allPagesToCheck.forEach((pageImage, pageIdx) => {
              const pageStart = pageImage.pageStartPx;
              const pageEnd = pageStart + pageImage.pageContentPx;

              // Step appears on this page if step header or content overlaps with this page
              const stepOverlapsPage =
                stepHeaderTop < pageEnd && stepBottom > pageStart;

              if (stepOverlapsPage && !stepPageIndices.includes(pageIdx)) {
                stepPageIndices.push(pageIdx);
              }
            });

            // Update multiPageSteps with accurate page indices
            if (stepPageIndices.length > 0) {
              const existingIndices = multiPageSteps.get(stepMeta.stepId) || [];
              const combinedIndices = [
                ...new Set([...existingIndices, ...stepPageIndices]),
              ].sort((a, b) => a - b);
              multiPageSteps.set(stepMeta.stepId, combinedIndices);
            }

            // Calculate total pages based on step's full pixel range
            // Find the first and last page the step will span based on pixel positions
            let firstPageIdx = -1;
            let lastPageIdx = -1;

            allPagesToCheck.forEach((pageImage, pageIdx) => {
              const pageStart = pageImage.pageStartPx;
              const pageEnd = pageStart + pageImage.pageContentPx;

              if (stepHeaderTop < pageEnd && stepBottom > pageStart) {
                if (firstPageIdx === -1) {
                  firstPageIdx = pageIdx;
                }
                lastPageIdx = pageIdx;
              }
            });

            // If step extends beyond current pages, estimate additional pages needed
            if (
              lastPageIdx >= 0 &&
              stepBottom >
                allPagesToCheck[lastPageIdx].pageStartPx +
                  allPagesToCheck[lastPageIdx].pageContentPx
            ) {
              // Step extends beyond the last created page, estimate how many more pages
              const remainingContent =
                stepBottom -
                (allPagesToCheck[lastPageIdx].pageStartPx +
                  allPagesToCheck[lastPageIdx].pageContentPx);
              const estimatedAdditionalPages = Math.ceil(
                remainingContent / availableHeightPx,
              );
              lastPageIdx = lastPageIdx + estimatedAdditionalPages;
            }

            // Calculate total pages based on the range
            const calculatedTotalPages =
              firstPageIdx >= 0 && lastPageIdx >= 0
                ? lastPageIdx - firstPageIdx + 1
                : 1;

            // Store the calculated total in multiPageSteps for later use
            // We'll use the actual tracked indices count, but ensure it's at least the calculated range
            const pageIndices = multiPageSteps.get(stepMeta.stepId) || [];
            if (pageIndices.length < calculatedTotalPages) {
              // Ensure we have enough page indices tracked
              for (let i = firstPageIdx; i <= lastPageIdx; i++) {
                if (i >= 0 && !pageIndices.includes(i)) {
                  pageIndices.push(i);
                }
              }
              pageIndices.sort((a, b) => a - b);
              multiPageSteps.set(stepMeta.stepId, pageIndices);
            }
          }

          const pageIndices = multiPageSteps.get(stepMeta.stepId) || [];
          const sortedPageIndices = [...pageIndices].sort((a, b) => a - b);

          // Calculate total pages based on the step's full pixel range
          let calculatedTotalPages = 1;
          if (stepMeta.headerTopPx !== undefined && stepMeta.headerTopPx >= 0) {
            const stepHeaderTop = stepMeta.headerTopPx;
            let stepBottom =
              stepMeta.headerTopPx + (stepMeta.headerHeightPx || 0);
            const allChildBottomPx = [];
            if (stepChildElements.length > 0) {
              allChildBottomPx.push(
                ...stepChildElements.map((meta) => meta.bottomPx),
              );
            }
            if (stepItemRequiredTables.length > 0) {
              allChildBottomPx.push(
                ...stepItemRequiredTables.map(
                  (tableMeta) => tableMeta.bottomPx,
                ),
              );
            }
            if (
              stepMeta.stepShortDescBottomPx != null &&
              Number.isFinite(stepMeta.stepShortDescBottomPx)
            ) {
              allChildBottomPx.push(stepMeta.stepShortDescBottomPx);
            }
            if (allChildBottomPx.length > 0) {
              stepBottom = Math.max(stepBottom, ...allChildBottomPx);
            }

            // Calculate total pages based on step's full pixel range
            // Find which page the step starts on
            let firstPageIdx = -1;
            pageImages.forEach((pageImage, pageIdx) => {
              const pageStart = pageImage.pageStartPx;
              const pageEnd = pageStart + pageImage.pageContentPx;
              if (stepHeaderTop >= pageStart && stepHeaderTop < pageEnd) {
                firstPageIdx = pageIdx;
              }
            });

            // If not found in existing pages, check current page
            if (firstPageIdx === -1) {
              if (stepHeaderTop >= pageStartPx && stepHeaderTop < pageEndPx) {
                firstPageIdx = pageImages.length;
              }
            }

            // Calculate which page the step ends on based on stepBottom pixel position
            // Use the total height and page boundaries to estimate
            let lastPageIdx = firstPageIdx;
            if (firstPageIdx >= 0) {
              // Find the last page that contains any part of the step
              const allPagesToCheck = [
                ...pageImages,
                {
                  pageStartPx: pageStartPx,
                  pageContentPx: pageContentPx,
                },
              ];

              // Find the last page that contains any part of the step
              allPagesToCheck.forEach((pageImage, pageIdx) => {
                const pageStart = pageImage.pageStartPx;
                const pageEnd = pageStart + pageImage.pageContentPx;
                // Step appears on this page if it overlaps
                if (stepBottom > pageStart && stepHeaderTop < pageEnd) {
                  if (pageIdx > lastPageIdx) {
                    lastPageIdx = pageIdx;
                  }
                }
              });

              // If step extends beyond all created pages, estimate additional pages needed
              if (lastPageIdx >= 0) {
                const lastPage = allPagesToCheck[lastPageIdx];
                const lastPageEnd =
                  lastPage.pageStartPx + lastPage.pageContentPx;
                if (stepBottom > lastPageEnd) {
                  const remainingContent = stepBottom - lastPageEnd;
                  const estimatedAdditionalPages = Math.max(
                    0,
                    Math.ceil(remainingContent / availableHeightPx),
                  );
                  lastPageIdx = lastPageIdx + estimatedAdditionalPages;
                }
              }
            }

            // Calculate total pages from the range
            if (firstPageIdx >= 0 && lastPageIdx >= firstPageIdx) {
              calculatedTotalPages = lastPageIdx - firstPageIdx + 1;
            } else if (sortedPageIndices.length > 0) {
              // Fallback: use tracked indices, but ensure we use the full range
              const minIdx = Math.min(...sortedPageIndices);
              const maxIdx = Math.max(...sortedPageIndices);
              calculatedTotalPages = maxIdx - minIdx + 1;
              // Ensure it's at least the number of unique pages
              if (calculatedTotalPages < sortedPageIndices.length) {
                calculatedTotalPages = sortedPageIndices.length;
              }
            }
          } else if (sortedPageIndices.length > 0) {
            calculatedTotalPages = sortedPageIndices.length;
          }

          // Use only the real step h2 position for title drawing. `stepsWithHeaderOnPage`
          // also includes steps whose Item Required table continues on this page (header on a
          // prior page); treating that as "header visible" made continuation pages hit
          // `wasDrawnAtTop` and return early, so "Step N (2/t)" never drew above spilled content.
          const stepHeaderPhysicallyOnPage =
            stepMeta.headerTopPx >= pageStartPx &&
            stepMeta.headerTopPx < pageEndPx;

          const hasSubstepsOnThisPage = substepsWithContentOnPage.some(
            (substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              return substepStepNumber === stepMeta.stepNumber;
            },
          );

          const hasSubstepHeadersOnThisPage = substepsWithHeaderOnPage.some(
            (substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              return substepStepNumber === stepMeta.stepNumber;
            },
          );

          // Check if any substep header is at the top of the page
          // If substep header is at the top (within first 50px of page), hide step header
          const hasSubstepHeaderAtTop = substepsWithHeaderOnPage.some(
            (substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              if (substepStepNumber !== stepMeta.stepNumber) return false;

              // Check if substep header is at or near the top of current page
              const substepHeaderTopOnPage =
                substepMeta.headerTopPx - pageStartPx;
              return substepHeaderTopOnPage >= 0 && substepHeaderTopOnPage < 50;
            },
          );

          // Check if there are substep child nodes on this page that belong to this step
          // For substep's child nodes, we only need substep header, not step header
          const hasSubstepChildNodesOnPage = substepsWithContentOnPage.some(
            (substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              return substepStepNumber === stepMeta.stepNumber;
            },
          );

          // Check if there's an Item Required table on this page for this step
          // Item Required tables and Steps are siblings in a group/block, not parent-child
          // Therefore, we should NOT show Step header when Item Required tables are present
          const hasItemRequiredTableOnThisPage = stepItemRequiredTables.some(
            (tableMeta) => {
              // Check if table is on this page (continues from previous page OR starts on this page)
              const tableContinues =
                tableMeta.topPx < pageStartPx &&
                tableMeta.bottomPx > pageStartPx;
              const tableOnPage =
                tableMeta.topPx >= pageStartPx &&
                tableMeta.topPx < pageStartPx + pageContentPx;
              return tableContinues || tableOnPage;
            },
          );

          // Skip drawing the in-flow step header when a substep header sits at the top,
          // when substep children share this page with the step band, or when an Item
          // Required table occupies the band — those layouts use substep/table chrome
          // instead of duplicating the step title. Only apply this when the step header
          // is actually on this page; on continuation pages (header not visible) we must
          // still run the logic below so "Step N (x/y)" is drawn above spilled content
          // (ZIP export and other paths rely on this for multi-page steps/substeps).
          // When the step spans multiple pages, we must still run this pass so page 1
          // gets "Step N (1/t)" (otherwise the raster shows plain "Step N" while later
          // pages show "(2/t)").
          if (stepHeaderPhysicallyOnPage) {
            const mustRedrawMultiPageStepTitle = calculatedTotalPages > 1;
            if (
              !mustRedrawMultiPageStepTitle &&
              (hasSubstepHeaderAtTop ||
                hasSubstepChildNodesOnPage ||
                hasItemRequiredTableOnThisPage)
            ) {
              return;
            }
          }

          let indicatorInfo = null;
          let pageSeq = null;

          // Always calculate pageSeq and totalPages, even for single-page steps
          // Use the sorted page indices to determine the current page sequence
          if (sortedPageIndices.length > 0) {
            if (sortedPageIndices.includes(pageIndexForTracking)) {
              // Current page is in the step's page range
              pageSeq = sortedPageIndices.indexOf(pageIndexForTracking) + 1;
            } else {
              // Current page might not be tracked yet, but we can infer from position
              // Check if step content overlaps with current page
              const stepHeaderTop = stepMeta.headerTopPx;
              const stepHeaderBottom =
                stepMeta.headerTopPx + (stepMeta.headerHeightPx || 0);
              let stepBottom = stepHeaderBottom;
              const allChildBottomPx5 = [];
              if (stepChildElements.length > 0) {
                allChildBottomPx5.push(
                  ...stepChildElements.map((meta) => meta.bottomPx),
                );
              }
              if (stepItemRequiredTables.length > 0) {
                allChildBottomPx5.push(
                  ...stepItemRequiredTables.map(
                    (tableMeta) => tableMeta.bottomPx,
                  ),
                );
              }
              if (
                stepMeta.stepShortDescBottomPx != null &&
                Number.isFinite(stepMeta.stepShortDescBottomPx)
              ) {
                allChildBottomPx5.push(stepMeta.stepShortDescBottomPx);
              }
              if (allChildBottomPx5.length > 0) {
                stepBottom = Math.max(stepHeaderBottom, ...allChildBottomPx5);
              }

              const stepOverlapsCurrentPage =
                stepHeaderTop < pageEndPx && stepBottom > pageStartPx;

              if (stepOverlapsCurrentPage) {
                // Step overlaps current page, find its position in the sequence
                const pagesBefore = sortedPageIndices.filter(
                  (idx) => idx < pageIndexForTracking,
                ).length;
                pageSeq = pagesBefore + 1;
                // If this page should be in the list but isn't yet, it will be added
                // For now, use the count of pages before + 1
                if (pageSeq > calculatedTotalPages) {
                  pageSeq = calculatedTotalPages;
                }
              } else {
                // Step doesn't overlap current page, can't determine sequence
                pageSeq = null;
              }
            }
          } else {
            // No tracked pages yet, check if step overlaps current page
            if (
              stepMeta.headerTopPx !== undefined &&
              stepMeta.headerTopPx >= 0
            ) {
              const stepHeaderTop = stepMeta.headerTopPx;
              const stepHeaderBottom =
                stepMeta.headerTopPx + (stepMeta.headerHeightPx || 0);
              let stepBottom = stepHeaderBottom;
              const allChildBottomPx4 = [];
              if (stepChildElements.length > 0) {
                allChildBottomPx4.push(
                  ...stepChildElements.map((meta) => meta.bottomPx),
                );
              }
              if (stepItemRequiredTables.length > 0) {
                allChildBottomPx4.push(
                  ...stepItemRequiredTables.map(
                    (tableMeta) => tableMeta.bottomPx,
                  ),
                );
              }
              if (
                stepMeta.stepShortDescBottomPx != null &&
                Number.isFinite(stepMeta.stepShortDescBottomPx)
              ) {
                allChildBottomPx4.push(stepMeta.stepShortDescBottomPx);
              }
              if (allChildBottomPx4.length > 0) {
                stepBottom = Math.max(stepHeaderBottom, ...allChildBottomPx4);
              }

              const stepOverlapsCurrentPage =
                stepHeaderTop < pageEndPx && stepBottom > pageStartPx;

              if (stepOverlapsCurrentPage) {
                // This is likely the first page of the step
                pageSeq = 1;
              } else {
                pageSeq = null;
              }
            }
          }

          // Final fallback: if we still don't have pageSeq, default to 1
          if (pageSeq === null) {
            pageSeq = 1;
          }

          // Prefer the larger of range-based estimate vs tracked page indices so early
          // pages do not show "(1/3)" while later pages show "(2/4)" after pagination
          // refines (estimates used availableHeightPx; actual slices differ).
          const totalPagesForIndicator = Math.max(
            calculatedTotalPages,
            sortedPageIndices.length,
          );
          // Always create indicator info if we have valid values
          // This includes single-page steps (1/1)
          if (
            pageSeq >= 1 &&
            pageSeq <= totalPagesForIndicator &&
            totalPagesForIndicator >= 1
          ) {
            indicatorInfo = {
              pageSeq,
              totalPages: totalPagesForIndicator,
            };
          }
          let indicatorY;
          let indicatorX;
          let stepContinuationBandPx = 0;

          if (stepHeaderPhysicallyOnPage && stepMeta.headerCanvas) {
            // Check if this step has an Item Required table continuing on this page
            const itemRequiredTableOnPage =
              continuingTables.find((tableMeta) => {
                return (
                  tableMeta.isItemRequiredTable &&
                  tableMeta.stepId === stepMeta.stepId
                );
              }) ||
              tableMetas.find((tableMeta) => {
                if (
                  !tableMeta.isItemRequiredTable ||
                  tableMeta.stepId !== stepMeta.stepId
                ) {
                  return false;
                }
                // Table continues from previous page OR table is on this page
                const tableContinues =
                  tableMeta.topPx < pageStartPx &&
                  tableMeta.bottomPx > pageStartPx;
                const tableOnPage =
                  tableMeta.topPx >= pageStartPx &&
                  tableMeta.topPx < pageStartPx + pageContentPx;
                return tableContinues || tableOnPage;
              });

            const hasItemRequiredTableContinuing =
              itemRequiredTableOnPage !== undefined;

            // Check if step header is below the Item Required table on this page
            const stepHeaderTopInPage = Math.max(
              0,
              stepMeta.headerTopPx - pageStartPx,
            );
            const stepHeaderOnPage =
              stepMeta.headerTopPx >= pageStartPx &&
              stepMeta.headerTopPx < pageStartPx + pageContentPx;

            let tableTopInPage = null;
            if (itemRequiredTableOnPage) {
              if (itemRequiredTableOnPage.topPx >= pageStartPx) {
                tableTopInPage = itemRequiredTableOnPage.topPx - pageStartPx;
              } else {
                tableTopInPage = 0; // Table continues from previous page
              }
            }

            const stepHeaderBelowTable =
              stepHeaderOnPage &&
              tableTopInPage !== null &&
              stepHeaderTopInPage > tableTopInPage;

            // Check if table continues from previous page
            const tableContinues =
              itemRequiredTableOnPage &&
              itemRequiredTableOnPage.topPx < pageStartPx &&
              itemRequiredTableOnPage.bottomPx > pageStartPx;

            // If step header was drawn at top (before table headers), skip drawing it here
            // This must match EXACTLY the conditions used when drawing at the top (line 9047-9051)
            // The drawing logic uses: shouldDrawAtTop = tableContinues || stepHeaderBelowTable || !stepHeaderOnPage
            const wasDrawnAtTop =
              hasItemRequiredTableContinuing &&
              (tableContinues || stepHeaderBelowTable || !stepHeaderOnPage);
            if (wasDrawnAtTop) {
              return; // Skip drawing - already drawn at top before table headers
            }

            const actualStepHeaderTop = stepHeaderTopInPage;

            indicatorY =
              headerInsertPx +
              actualStepHeaderTop +
              stepMeta.headerHeightPx / 2;
            indicatorX = stepMeta.headerLeftPx;

            const hasSubsteps = substepMetas.some((substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              return substepStepNumber === stepMeta.stepNumber;
            });

            const hasSubstepHeadersVisible = substepsWithHeaderOnPage.some(
              (substepMeta) => {
                const substepStepNumber = parseInt(
                  substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                  10,
                );
                return substepStepNumber === stepMeta.stepNumber;
              },
            );

            let firstSubstepTopPx = null;
            if (hasSubsteps) {
              const stepSubsteps = substepMetas.filter((substepMeta) => {
                const substepStepNumber = parseInt(
                  substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                  10,
                );
                return substepStepNumber === stepMeta.stepNumber;
              });
              if (stepSubsteps.length > 0) {
                const firstSubstep = stepSubsteps.reduce((first, current) => {
                  return current.topPx < first.topPx ? current : first;
                });
                firstSubstepTopPx = firstSubstep.topPx;
              }
            }

            // Calculate overlay to mask the header area from main canvas.
            // This prevents duplicate/overlapping headers (header drawn from base canvas + our indicator re-draw).
            // The original header is at stepHeaderTopInPage in the page coordinate system
            // If step header should be at top (Item Required table continues), use 0
            const headerTopOnPage = headerInsertPx + actualStepHeaderTop;

            // Cover only the header area (header text + HR if present), not the description below
            // The headerCanvas might include description, so we use the measured headerHeightPx instead
            // Calculate the exact bottom position of the HR line to ensure complete coverage
            let hrBottomPx = 0;
            if (stepMeta.hrTopPx !== null && stepMeta.hrTopPx !== undefined) {
              hrBottomPx =
                stepMeta.hrTopPx +
                (stepMeta.hrHeightPx ||
                  Math.max(1, Math.round(1 * domToCanvasScaleY)));
            }
            // Use only the header height (text + HR), not the full canvas height which might include description
            // Ensure we cover at least up to the HR line bottom plus a buffer
            const headerOnlyHeight = Math.max(
              stepMeta.headerHeightPx,
              hrBottomPx > 0 ? hrBottomPx : stepMeta.headerHeightPx,
            );
            // Use the smaller of headerCanvas height or calculated height to avoid covering description
            // Add extra buffer to ensure HR line is fully covered and prevent double lines
            const hrBuffer = hrBottomPx > 0 ? 15 : 5; // Larger buffer when HR exists to prevent double lines
            // Calculate overlay height to cover header and HR line completely
            // This prevents the original HR line from showing through and creating a double line effect
            const overlayBaseHeight = stepMeta.headerCanvas?.height
              ? Math.min(
                  stepMeta.headerCanvas.height,
                  headerOnlyHeight + hrBuffer,
                ) // Cover header + HR with buffer
              : headerOnlyHeight + hrBuffer;

            // Use minimal buffer above to avoid covering content above the header
            // Only use a small buffer to ensure complete coverage of the original header text
            // But sufficient buffer below to cover HR line and prevent double lines
            const overlayTopBuffer = 5; // Small buffer above, just enough to cover header text edges
            const overlayBottomBuffer =
              stepMeta.hrTopPx !== null && stepMeta.hrTopPx !== undefined
                ? 22
                : 8; // Extra bottom buffer avoids faint duplicate "Step" glyphs
            // Only apply top buffer if we're not at the very top of the page content
            const isAtPageTop = stepHeaderTopInPage <= 10;
            const effectiveTopBuffer = isAtPageTop ? 0 : overlayTopBuffer;
            const overlayTop = Math.max(
              0,
              headerTopOnPage - effectiveTopBuffer,
            );
            let overlayHeight =
              overlayBaseHeight + effectiveTopBuffer + overlayBottomBuffer;
            const minStepHeaderOverlayHeight =
              overlayBaseHeight + effectiveTopBuffer + overlayBottomBuffer;

            if (hasSubsteps && firstSubstepTopPx !== null) {
              const firstSubstepTopOnPage =
                headerInsertPx + Math.max(0, firstSubstepTopPx - pageStartPx);
              const maxOverlayBottom = firstSubstepTopOnPage - 10;
              const maxHeightBeforeSubstep = maxOverlayBottom - overlayTop;
              // Never shrink the mask shorter than the full Step title + HR band — doing
              // so was erasing only part of the header on page 1 and the redrawn grey line
              // failed to appear while page-2 continuation looked fine.
              if (maxHeightBeforeSubstep >= minStepHeaderOverlayHeight) {
                overlayHeight = Math.min(overlayHeight, maxHeightBeforeSubstep);
              }
            }

            overlayHeight = Math.max(overlayHeight, minStepHeaderOverlayHeight);

            // Draw white overlay to mask the original header from main canvas
            // This MUST be drawn before we draw our modified header to completely hide the original
            // Use multiple strategies to ensure complete coverage
            if (overlayHeight > 0) {
              pageCtx.save();

              // Strategy 1: Use destination-out to clear the area first
              pageCtx.globalCompositeOperation = "destination-out";
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(0, overlayTop, pageCanvas.width, overlayHeight);

              // Strategy 2: Use source-over to fill with white
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillStyle = "#ffffff";

              // Draw multiple overlapping rectangles with slight variations to ensure complete coverage
              // This handles any sub-pixel rendering or anti-aliasing issues
              for (let i = 0; i < 10; i++) {
                // Slight vertical offset variations to catch any edge cases
                const offset = i * 0.5;
                pageCtx.fillRect(
                  0,
                  overlayTop - offset,
                  pageCanvas.width,
                  overlayHeight + offset * 2,
                );
              }

              // Strategy 3: Draw multiple final full-coverage rectangles to ensure no gaps
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.fillStyle = "#ffffff";
              for (let i = 0; i < 3; i++) {
                pageCtx.fillRect(
                  0,
                  overlayTop - i,
                  pageCanvas.width,
                  overlayHeight + i * 2,
                );
              }

              pageCtx.restore();
            }

            // Always create a modified canvas to avoid drawing the original headerCanvas
            // This ensures we never show the original "Step X" text without indicators
            const modifiedCanvas = document.createElement("canvas");
            modifiedCanvas.width = stepMeta.headerCanvas.width;
            modifiedCanvas.height = stepMeta.headerCanvas.height;
            const modifiedCtx = modifiedCanvas.getContext("2d");

            modifiedCtx.fillStyle = "#ffffff";
            modifiedCtx.fillRect(
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );

            const scaledFontSize = Math.round(20 * domToCanvasScaleY);
            modifiedCtx.font = `bold ${scaledFontSize}px Arial`;
            modifiedCtx.textAlign = "left";
            modifiedCtx.textBaseline = "top";

            // Check if step has Item Required tables (consider them as child nodes)
            // This is needed because Item Required tables are not in childBlockLabels,
            // so they're not counted as child elements, but we should treat them as child nodes
            const hasItemRequiredTables = stepItemRequiredTables.length > 0;

            // Check if step has Item Required tables that span multiple pages
            // This is needed to show indicators when Item Required tables span pages
            const hasItemRequiredTablesSpanningPages = (() => {
              if (stepItemRequiredTables.length === 0) return false;

              // Check if any Item Required table spans multiple pages
              // A table spans multiple pages if its top and bottom pixels span across page boundaries
              const pageEndPx = pageStartPx + pageContentPx;
              return stepItemRequiredTables.some((tableMeta) => {
                // Check all pages created so far plus current page to see if table spans multiple pages
                const allPagesToCheck = [
                  ...pageImages,
                  {
                    pageStartPx: pageStartPx,
                    pageContentPx: pageContentPx,
                  },
                ];

                const pagesTableSpans = allPagesToCheck.filter(
                  (pageImage, pageIdx) => {
                    const pageStart = pageImage.pageStartPx;
                    const pageEnd = pageStart + pageImage.pageContentPx;
                    return (
                      tableMeta.topPx < pageEnd &&
                      tableMeta.bottomPx > pageStart
                    );
                  },
                );

                return pagesTableSpans.length > 1;
              });
            })();

            // Always show step indicator
            const shouldShowStepIndicator = true;

            // Show indicator only for steps that span multiple pages
            let stepIndicatorText;
            if (
              indicatorInfo &&
              indicatorInfo.totalPages > 1 &&
              shouldShowStepIndicator
            ) {
              stepIndicatorText = `Step ${stepMeta.stepNumber} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;
            } else {
              stepIndicatorText = `Step ${stepMeta.stepNumber}`;
            }

            const baseTitle = `Step ${stepMeta.stepNumber}`;
            modifiedCtx.fillStyle = "#333333";
            modifiedCtx.fillText(baseTitle, stepMeta.headerLeftPx, 0);

            // Add indicator part if we have indicator info and it spans multiple pages
            if (
              indicatorInfo &&
              indicatorInfo.totalPages > 1 &&
              shouldShowStepIndicator
            ) {
              const baseTitleWidth = modifiedCtx.measureText(baseTitle).width;
              const indicatorPart = ` (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;
              modifiedCtx.fillStyle = "#6C757D";
              modifiedCtx.fillText(
                indicatorPart,
                stepMeta.headerLeftPx + baseTitleWidth,
                0,
              );
            }

            if (
              stepMeta.sectionIndex !== null &&
              stepMeta.sectionIndex !== undefined
            ) {
              const treePathText = `Section ${stepMeta.sectionIndex + 1} > Step ${stepMeta.globalStepNumber || stepMeta.stepNumber}`;
              const scaledTreePathFontSize = Math.round(14 * domToCanvasScaleY);
              modifiedCtx.fillStyle = "#888";
              modifiedCtx.font = `${scaledTreePathFontSize}px Arial`;
              modifiedCtx.textAlign = "right";
              modifiedCtx.textBaseline = "top";
              modifiedCtx.fillText(treePathText, modifiedCanvas.width - 20, 0);
            }

            let hrY;
            if (stepMeta.hrTopPx !== null && stepMeta.hrTopPx !== undefined) {
              hrY = stepMeta.hrTopPx;
            } else {
              const estimatedTextHeight = Math.round(24 * domToCanvasScaleY);
              hrY = estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
            }
            if (!(Number.isFinite(hrY) && hrY >= 0)) {
              const estimatedTextHeight = Math.round(24 * domToCanvasScaleY);
              hrY = estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
            }
            {
              const estimatedFallback =
                Math.round(24 * domToCanvasScaleY) +
                Math.round(10 * domToCanvasScaleY);
              if (!(hrY >= 0 && hrY < modifiedCanvas.height)) {
                hrY = estimatedFallback;
              }
              const hrMaxY = Math.max(1, modifiedCanvas.height - 1);
              hrY = Math.min(Math.max(hrY, 1), hrMaxY);
            }

            if (modifiedCanvas.height >= 2) {
              modifiedCtx.strokeStyle = "#cccccc";
              modifiedCtx.lineWidth = Math.max(
                1,
                Math.round(1 * domToCanvasScaleY),
              );
              modifiedCtx.beginPath();
              modifiedCtx.moveTo(0, hrY);
              modifiedCtx.lineTo(modifiedCanvas.width, hrY);
              modifiedCtx.stroke();
            }

            // Always draw the modified canvas (never the original headerCanvas)
            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.drawImage(
              modifiedCanvas,
              0,
              0,
              modifiedCanvas.width,
              modifiedCanvas.height,
              0,
              headerInsertPx + stepHeaderTopInPage,
              modifiedCanvas.width,
              modifiedCanvas.height,
            );
            pageCtx.globalCompositeOperation = "source-over";
          } else {
            const childBlockLabels = new Set([
              "attachment",
              "rich-text",
              "video",
              "form",
              "canvas",
            ]);
            const childElementsOnThisPage = blockMetasPx.filter((meta) => {
              if (!childBlockLabels.has(meta.label)) return false;
              if (meta.stepId !== stepMeta.stepId) return false;
              return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
            });

            const isMultiPageStepContinuation =
              !stepHeaderPhysicallyOnPage &&
              indicatorInfo &&
              indicatorInfo.totalPages > 1;

            if (isMultiPageStepContinuation) {
              // Pin continuation titles just below the inserted page header, not above
              // the first child (avoids aligning with substep/canvas and wrong Y).
              const continuationTopPx = Math.round(20 * domToCanvasScaleY);
              const continuationMidPx = Math.round(12 * domToCanvasScaleY);
              const defaultIndicatorY =
                headerInsertPx + continuationTopPx + continuationMidPx;
              // Keep step continuation title above the first real step content
              // (short description / direct child blocks), so it never overlaps.
              const firstDirectStepChildTopInPage = blockMetasPx
                .filter((meta) => {
                  if (!childBlockLabels.has(meta.label)) return false;
                  if (meta.stepId !== stepMeta.stepId) return false;
                  if (meta.substepId !== null && meta.substepId !== undefined)
                    return false;
                  return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
                })
                .map((meta) => {
                  const shortDescTopPxCandidates = [
                    meta.canvasShortDescTopPx,
                    meta.videoShortDescTopPx,
                    meta.attachmentShortDescTopPx,
                    meta.richTextShortDescTopPx,
                  ].filter((v) => Number.isFinite(v));
                  const effectiveTopPx =
                    shortDescTopPxCandidates.length > 0
                      ? Math.min(meta.topPx, ...shortDescTopPxCandidates)
                      : meta.topPx;
                  return Math.max(0, effectiveTopPx - pageStartPx);
                })
                .reduce(
                  (minTop, cur) => (cur < minTop ? cur : minTop),
                  Number.POSITIVE_INFINITY,
                );
              const firstStepDescTopInPage =
                stepMeta.stepShortDescTopPx != null &&
                stepMeta.stepShortDescTopPx >= pageStartPx &&
                stepMeta.stepShortDescTopPx < pageEndPx
                  ? Math.max(0, stepMeta.stepShortDescTopPx - pageStartPx)
                  : Number.POSITIVE_INFINITY;
              const firstStepContentTopInPage = Math.min(
                firstDirectStepChildTopInPage,
                firstStepDescTopInPage,
              );
              if (Number.isFinite(firstStepContentTopInPage)) {
                const maxIndicatorY =
                  headerInsertPx +
                  firstStepContentTopInPage -
                  Math.round(24 * domToCanvasScaleY);
                indicatorY = Math.min(defaultIndicatorY, maxIndicatorY);
              } else {
                indicatorY = defaultIndicatorY;
              }
              // Keep a small minimum top inset so glyph ascenders are never clipped.
              indicatorY = Math.max(
                indicatorY,
                headerInsertPx + Math.round(36 * domToCanvasScaleY),
              );
              indicatorX = stepMeta.headerLeftPx || 20;
            } else if (childElementsOnThisPage.length > 0) {
              const topmostChild = childElementsOnThisPage.reduce(
                (top, current) => {
                  const currentTopOnPage = Math.max(
                    0,
                    current.topPx - pageStartPx,
                  );
                  const topTopOnPage = Math.max(0, top.topPx - pageStartPx);
                  return currentTopOnPage < topTopOnPage ? current : top;
                },
              );

              const childTopOnPage = Math.max(
                0,
                topmostChild.topPx - pageStartPx,
              );

              indicatorY = headerInsertPx + Math.max(15, childTopOnPage - 45);
              indicatorX = stepMeta.headerLeftPx || 20;
            } else {
              indicatorY = headerInsertPx + 25;
              indicatorX = stepMeta.headerLeftPx || 20;
            }
          }

          if (!stepHeaderPhysicallyOnPage) {
            // ZIP keeps suppressing whenever any substep header is present.
            // Normal export suppresses only when a substep starts in the top band.
            let shouldSuppressStepContinuation = isZipExport
              ? hasSubstepHeadersOnThisPage
              : hasSubstepHeaderAtTop;
            // Normal PDF: Item Required table on a continuation page still needs the
            // Step N (x/y) band above the table/substep (ZIP often shows the in-flow h2).
            if (!isZipExport && hasItemRequiredTableOnThisPage) {
              shouldSuppressStepContinuation = false;
            }
            if (shouldSuppressStepContinuation) {
              return;
            }
            let indicatorText;

            const hasSubsteps = substepMetas.some((substepMeta) => {
              const substepStepNumber = parseInt(
                substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                10,
              );
              return substepStepNumber === stepMeta.stepNumber;
            });

            const hasSubstepHeadersVisible = substepsWithHeaderOnPage.some(
              (substepMeta) => {
                const substepStepNumber = parseInt(
                  substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                  10,
                );
                return substepStepNumber === stepMeta.stepNumber;
              },
            );

            // Check if step has Item Required tables (consider them as child nodes)
            // This is needed because Item Required tables are not in childBlockLabels,
            // so they're not counted as child elements, but we should treat them as child nodes
            const hasItemRequiredTables = stepItemRequiredTables.length > 0;

            // Check if step has Item Required tables that span multiple pages
            // This is needed to show indicators when Item Required tables span pages
            const hasItemRequiredTablesSpanningPages = (() => {
              if (stepItemRequiredTables.length === 0) return false;

              // Check if any Item Required table spans multiple pages
              // A table spans multiple pages if its top and bottom pixels span across page boundaries
              const pageEndPx = pageStartPx + pageContentPx;
              return stepItemRequiredTables.some((tableMeta) => {
                // Check all pages created so far plus current page to see if table spans multiple pages
                const allPagesToCheck = [
                  ...pageImages,
                  {
                    pageStartPx: pageStartPx,
                    pageContentPx: pageContentPx,
                  },
                ];

                const pagesTableSpans = allPagesToCheck.filter(
                  (pageImage, pageIdx) => {
                    const pageStart = pageImage.pageStartPx;
                    const pageEnd = pageStart + pageImage.pageContentPx;
                    return (
                      tableMeta.topPx < pageEnd &&
                      tableMeta.bottomPx > pageStart
                    );
                  },
                );

                return pagesTableSpans.length > 1;
              });
            })();

            // Always show step indicator
            const shouldShowStepIndicator = true;

            // Show indicator only for steps that span multiple pages
            if (
              indicatorInfo &&
              indicatorInfo.totalPages > 1 &&
              shouldShowStepIndicator
            ) {
              indicatorText = `Step ${stepMeta.stepNumber} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;
            } else {
              indicatorText = `Step ${stepMeta.stepNumber}`;
            }

            pageCtx.save();
            pageCtx.fillStyle = "#333333";
            const scaledFontSize = Math.round(20 * domToCanvasScaleY);
            pageCtx.font = `bold ${scaledFontSize}px Arial`;
            pageCtx.textAlign = "left";
            pageCtx.textBaseline = "middle";

            const textMetrics = pageCtx.measureText(indicatorText);
            const textWidth = textMetrics.width;
            const textHeight = 24;
            const backgroundPaddingY = 12;
            // Keep continuation band compact so first child descriptions/captions
            // (especially canvas node descriptions) are not masked on split pages.
            const spacingBelowTitle = 4;
            let backgroundHeight =
              textHeight + backgroundPaddingY * 2 + spacingBelowTitle;
            const backgroundTopBase =
              indicatorY - textHeight / 2 - backgroundPaddingY;

            // Never let the step continuation background cover direct step child content
            // (e.g. canvas/rich-text) that starts immediately below the heading band.
            const firstDirectStepChildTopOnPage = blockMetasPx
              .filter((meta) => {
                if (!childBlockLabels.has(meta.label)) return false;
                if (meta.stepId !== stepMeta.stepId) return false;
                if (meta.substepId !== null && meta.substepId !== undefined)
                  return false;
                return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
              })
              .map((meta) => {
                const shortDescTopPxCandidates = [
                  meta.canvasShortDescTopPx,
                  meta.videoShortDescTopPx,
                  meta.attachmentShortDescTopPx,
                  meta.richTextShortDescTopPx,
                ].filter((v) => Number.isFinite(v));
                const effectiveTopPx =
                  shortDescTopPxCandidates.length > 0
                    ? Math.min(meta.topPx, ...shortDescTopPxCandidates)
                    : meta.topPx;
                return Math.max(0, effectiveTopPx - pageStartPx);
              })
              .reduce(
                (minTop, cur) => (cur < minTop ? cur : minTop),
                Number.POSITIVE_INFINITY,
              );
            const firstStepShortDescTopOnPage =
              stepMeta.stepShortDescTopPx != null &&
              Number.isFinite(stepMeta.stepShortDescTopPx) &&
              stepMeta.stepShortDescTopPx >= pageStartPx &&
              stepMeta.stepShortDescTopPx < pageEndPx
                ? Math.max(0, stepMeta.stepShortDescTopPx - pageStartPx)
                : Number.POSITIVE_INFINITY;
            const firstStepContentTopOnPage = Math.min(
              firstDirectStepChildTopOnPage,
              firstStepShortDescTopOnPage,
            );
            if (Number.isFinite(firstStepContentTopOnPage)) {
              const maxBackgroundBottom =
                headerInsertPx + firstStepContentTopOnPage - 10;
              backgroundHeight = Math.min(
                backgroundHeight,
                Math.max(0, maxBackgroundBottom - backgroundTopBase),
              );
            }
            // Hard cap to avoid oversized white overlays covering first visible content.
            const maxContinuationBandHeight = Math.round(58 * domToCanvasScaleY);
            backgroundHeight = Math.min(backgroundHeight, maxContinuationBandHeight);

            if (!stepHeaderPhysicallyOnPage && hasSubsteps) {
              const stepSubsteps = substepMetas.filter((substepMeta) => {
                const substepStepNumber = parseInt(
                  substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                  10,
                );
                return substepStepNumber === stepMeta.stepNumber;
              });
              if (stepSubsteps.length > 0) {
                const substepsOnThisPage = stepSubsteps.filter(
                  (substepMeta) => {
                    const substepTopOnPage = Math.max(
                      0,
                      substepMeta.topPx - pageStartPx,
                    );
                    return (
                      substepTopOnPage >= 0 && substepTopOnPage < pageContentPx
                    );
                  },
                );
                if (substepsOnThisPage.length > 0) {
                  const firstSubstepOnPage = substepsOnThisPage.reduce(
                    (first, current) => {
                      const firstTop = Math.max(0, first.topPx - pageStartPx);
                      const currentTop = Math.max(
                        0,
                        current.topPx - pageStartPx,
                      );
                      return currentTop < firstTop ? current : first;
                    },
                  );
                  const firstSubstepTopOnPage =
                    headerInsertPx +
                    Math.max(0, firstSubstepOnPage.topPx - pageStartPx);
                  const maxBackgroundBottom = firstSubstepTopOnPage - 10;
                  backgroundHeight = Math.min(
                    backgroundHeight,
                    maxBackgroundBottom - backgroundTopBase,
                  );
                }
              }
            }

            // Do not paint a full-width white background behind continuation step heading.
            // It can cover the first canvas/rich-text short-description line on split pages.

            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.font = `bold ${scaledFontSize}px Arial`;
            let baseTitle = indicatorText;
            let indicatorPart = "";
            const indicatorMatch = indicatorText.match(
              /^(.+?)\s*(\(\d+\/\d+\))$/,
            );
            if (indicatorMatch) {
              baseTitle = indicatorMatch[1];
              indicatorPart = indicatorMatch[2];
            }

            pageCtx.fillStyle = "#333333";
            pageCtx.fillText(baseTitle, indicatorX, indicatorY);

            if (indicatorPart) {
              const baseTitleWidth = pageCtx.measureText(baseTitle).width;
              const spaceWidth = pageCtx.measureText(" ").width;
              pageCtx.fillStyle = "#6C757D";
              pageCtx.fillText(
                " " + indicatorPart,
                indicatorX + baseTitleWidth,
                indicatorY,
              );
            }

            if (
              stepMeta.sectionIndex !== null &&
              stepMeta.sectionIndex !== undefined
            ) {
              const treePathText = `Section ${stepMeta.sectionIndex + 1} > Step ${stepMeta.globalStepNumber || stepMeta.stepNumber}`;
              const scaledTreePathFontSize = Math.round(14 * domToCanvasScaleY);
              pageCtx.fillStyle = "#888";
              pageCtx.font = `${scaledTreePathFontSize}px Arial`;
              pageCtx.textAlign = "right";
              pageCtx.textBaseline = "middle";
              pageCtx.fillText(treePathText, pageCanvas.width - 20, indicatorY);
            }

            const scaledTextHeight = Math.round(24 * domToCanvasScaleY);
            const hrY =
              indicatorY +
              scaledTextHeight / 2 +
              Math.round(8 * domToCanvasScaleY);
            pageCtx.strokeStyle = "#cccccc";
            pageCtx.lineWidth = Math.max(1, Math.round(1 * domToCanvasScaleY));
            pageCtx.beginPath();
            pageCtx.moveTo(0, hrY);
            pageCtx.lineTo(pageCanvas.width, hrY);
            pageCtx.stroke();

            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.restore();
          }
        });

        // Reserve top band for drawn "Step N (x/y)" only when that continuation
        // title actually runs. When substep headers are on this page, step
        // continuation is suppressed (see early return above) — skipping this
        // band keeps substep titles flush to the top instead of a blank gap.
        const computeSubstepStepContinuationBandPx = (parentStepMeta) => {
          if (!parentStepMeta) return 0;
          const parentPageIndices =
            multiPageSteps.get(parentStepMeta.stepId) || [];
          if (parentPageIndices.length <= 1) return 0;
          const parentHeaderPhysical =
            parentStepMeta.headerTopPx >= pageStartPx &&
            parentStepMeta.headerTopPx < pageEndPx;
          if (parentHeaderPhysical) return 0;
          const stepContinuationSuppressedBySubstep =
            substepsWithHeaderOnPage.some((m) => {
              if (m.stepNumber !== parentStepMeta.stepNumber) return false;
              if (
                parentStepMeta.sectionIndex != null &&
                m.sectionIndex !== parentStepMeta.sectionIndex
              ) {
                return false;
              }
              return true;
            });
          if (stepContinuationSuppressedBySubstep) return 0;
          return Math.round(84 * domToCanvasScaleY);
        };

        substepsWithContentOnPage.forEach((substepMeta) => {
          const substepChildElements = blockMetasPx.filter((meta) => {
            if (!childBlockLabels.has(meta.label)) return false;
            return meta.substepId === substepMeta.substepId;
          });

          // Calculate total pages based on substep's full pixel range
          let calculatedTotalPages = 1;

          if (
            substepMeta.headerTopPx !== undefined &&
            substepMeta.headerTopPx >= 0
          ) {
            const substepHeaderTop = substepMeta.headerTopPx;
            const substepHeaderBottom =
              substepMeta.headerTopPx + (substepMeta.headerHeightPx || 0);

            // Find the bottom of the last child node, or use substep header bottom if no children.
            // Include short-description bounds so multi-page substeps that only have long
            // short text (no canvas/attachment blocks) still get correct (1/t) on page 1.
            let substepBottom = substepHeaderBottom;
            if (substepChildElements.length > 0) {
              substepBottom = Math.max(
                substepHeaderBottom,
                ...substepChildElements.map((meta) => meta.bottomPx),
              );
            }
            if (
              substepMeta.substepShortDescBottomPx != null &&
              Number.isFinite(substepMeta.substepShortDescBottomPx)
            ) {
              substepBottom = Math.max(
                substepBottom,
                substepMeta.substepShortDescBottomPx,
              );
            }

            // Find which pages this substep spans by checking actual page boundaries
            // Check all pages created so far plus current page
            const allPagesToCheck = [
              ...pageImages,
              {
                pageStartPx: pageStartPx,
                pageContentPx: pageContentPx,
              },
            ];

            const substepPageIndices = [];
            allPagesToCheck.forEach((pageImage, pageIdx) => {
              const pageStart = pageImage.pageStartPx;
              const pageEnd = pageStart + pageImage.pageContentPx;

              // Substep appears on this page if substep header or content overlaps with this page
              const substepOverlapsPage =
                substepHeaderTop < pageEnd && substepBottom > pageStart;

              if (
                substepOverlapsPage &&
                !substepPageIndices.includes(pageIdx)
              ) {
                substepPageIndices.push(pageIdx);
              }
            });

            // Update multiPageSubsteps with accurate page indices
            if (substepPageIndices.length > 0) {
              const existingIndices =
                multiPageSubsteps.get(substepMeta.substepId) || [];
              const combinedIndices = [
                ...new Set([...existingIndices, ...substepPageIndices]),
              ].sort((a, b) => a - b);
              multiPageSubsteps.set(substepMeta.substepId, combinedIndices);
            }

            // Calculate total pages based on substep's full pixel range
            // Find which page the substep starts on
            let firstPageIdx = -1;
            pageImages.forEach((pageImage, pageIdx) => {
              const pageStart = pageImage.pageStartPx;
              const pageEnd = pageStart + pageImage.pageContentPx;
              if (substepHeaderTop >= pageStart && substepHeaderTop < pageEnd) {
                firstPageIdx = pageIdx;
              }
            });

            // If not found in existing pages, check current page
            if (firstPageIdx === -1) {
              if (
                substepHeaderTop >= pageStartPx &&
                substepHeaderTop < pageEndPx
              ) {
                firstPageIdx = pageImages.length;
              }
            }

            // Calculate which page the substep ends on based on substepBottom pixel position
            let lastPageIdx = firstPageIdx;
            if (firstPageIdx >= 0) {
              // Find the last page that contains any part of the substep
              allPagesToCheck.forEach((pageImage, pageIdx) => {
                const pageStart = pageImage.pageStartPx;
                const pageEnd = pageStart + pageImage.pageContentPx;
                // Substep appears on this page if it overlaps
                if (substepBottom > pageStart && substepHeaderTop < pageEnd) {
                  if (pageIdx > lastPageIdx) {
                    lastPageIdx = pageIdx;
                  }
                }
              });

              // If substep extends beyond all created pages, estimate additional pages needed
              if (lastPageIdx >= 0) {
                const lastPage = allPagesToCheck[lastPageIdx];
                const lastPageEnd =
                  lastPage.pageStartPx + lastPage.pageContentPx;
                if (substepBottom > lastPageEnd) {
                  const remainingContent = substepBottom - lastPageEnd;
                  const estimatedAdditionalPages = Math.max(
                    0,
                    Math.ceil(remainingContent / availableHeightPx),
                  );
                  lastPageIdx = lastPageIdx + estimatedAdditionalPages;
                }
              }
            }

            // Calculate total pages from the range
            if (firstPageIdx >= 0 && lastPageIdx >= firstPageIdx) {
              calculatedTotalPages = lastPageIdx - firstPageIdx + 1;
            }
          }

          const pageIndices =
            multiPageSubsteps.get(substepMeta.substepId) || [];
          const sortedPageIndices = [...pageIndices].sort((a, b) => a - b);

          // If calculated total is still 1, use tracked indices as fallback
          if (calculatedTotalPages === 1 && sortedPageIndices.length > 0) {
            // Use the range from first to last page index
            const minIdx = Math.min(...sortedPageIndices);
            const maxIdx = Math.max(...sortedPageIndices);
            calculatedTotalPages = maxIdx - minIdx + 1;
            // Ensure it's at least the number of unique pages
            if (calculatedTotalPages < sortedPageIndices.length) {
              calculatedTotalPages = sortedPageIndices.length;
            }
          }

          let indicatorInfo = null;

          const headerVisible = substepsWithHeaderOnPage.some(
            (m) => m.substepId === substepMeta.substepId,
          );

          // Only show indicators for substeps that span multiple pages
          if (calculatedTotalPages > 1) {
            let pageSeq = null;

            // Use the sorted page indices to determine the current page sequence
            if (sortedPageIndices.length > 0) {
              if (sortedPageIndices.includes(pageIndexForTracking)) {
                // Current page is in the substep's page range
                pageSeq = sortedPageIndices.indexOf(pageIndexForTracking) + 1;
              } else {
                // Current page might not be tracked yet, but we can infer from position
                // Check if substep content overlaps with current page
                if (
                  substepMeta.headerTopPx !== undefined &&
                  substepMeta.headerTopPx >= 0
                ) {
                  const substepHeaderTop = substepMeta.headerTopPx;
                  const substepHeaderBottom =
                    substepMeta.headerTopPx + (substepMeta.headerHeightPx || 0);
                  let substepBottom = substepHeaderBottom;
                  if (substepChildElements.length > 0) {
                    substepBottom = Math.max(
                      substepHeaderBottom,
                      ...substepChildElements.map((meta) => meta.bottomPx),
                    );
                  }

                  const substepOverlapsCurrentPage =
                    substepHeaderTop < pageEndPx && substepBottom > pageStartPx;

                  if (substepOverlapsCurrentPage) {
                    // Substep overlaps current page, find its position in the sequence
                    const pagesBefore = sortedPageIndices.filter(
                      (idx) => idx < pageIndexForTracking,
                    ).length;
                    pageSeq = pagesBefore + 1;
                    // If this page should be in the list but isn't yet, it will be added
                    // For now, use the count of pages before + 1
                    if (pageSeq > calculatedTotalPages) {
                      pageSeq = calculatedTotalPages;
                    }
                  } else {
                    // Substep doesn't overlap current page, can't determine sequence
                    pageSeq = null;
                  }
                }
              }
            } else {
              // No tracked pages yet, check if substep overlaps current page
              if (
                substepMeta.headerTopPx !== undefined &&
                substepMeta.headerTopPx >= 0
              ) {
                const substepHeaderTop = substepMeta.headerTopPx;
                const substepHeaderBottom =
                  substepMeta.headerTopPx + (substepMeta.headerHeightPx || 0);
                let substepBottom = substepHeaderBottom;
                if (substepChildElements.length > 0) {
                  substepBottom = Math.max(
                    substepHeaderBottom,
                    ...substepChildElements.map((meta) => meta.bottomPx),
                  );
                }

                const substepOverlapsCurrentPage =
                  substepHeaderTop < pageEndPx && substepBottom > pageStartPx;

                if (substepOverlapsCurrentPage) {
                  // This is likely the first page of the substep
                  pageSeq = 1;
                } else {
                  pageSeq = null;
                }
              }
            }

            // Create indicatorInfo if substep spans multiple pages
            // Use calculatedTotalPages as the primary check since it's based on pixel range
            if (
              calculatedTotalPages > 1 &&
              pageSeq !== null &&
              pageSeq >= 1 &&
              pageSeq <= calculatedTotalPages
            ) {
              indicatorInfo = {
                pageSeq,
                totalPages: calculatedTotalPages,
              };
            } else {
              indicatorInfo = null;
            }
          } else {
            indicatorInfo = null;
          }

          let indicatorY;
          let indicatorX;
          let stepContinuationBandPx = 0;

          if (headerVisible && substepMeta.headerCanvas) {
            const naturalSubstepHeaderTopInPage = Math.max(
              0,
              substepMeta.headerTopPx - pageStartPx,
            );
            // When the parent step shows a continuation band (no step h2 on this page),
            // shift the substep header down so it does not overlap "Step N (x/y)".
            const parentStepMetaForBand = stepMetas.find((s) => {
              if (s.stepNumber !== substepMeta.stepNumber) return false;
              if (
                substepMeta.sectionIndex != null &&
                s.sectionIndex !== substepMeta.sectionIndex
              )
                return false;
              return true;
            });
            stepContinuationBandPx = computeSubstepStepContinuationBandPx(
              parentStepMetaForBand,
            );
            // True when the physical substep h2 is not part of this vertical slice (page
            // starts inside child content). ZIP must use the same pin + mask or titles
            // merge with rich text on (2/2) pages.
            const substepHeaderInSlice =
              substepMeta.headerTopPx >= pageStartPx &&
              substepMeta.headerTopPx < pageEndPx;
            const isMultiPageSubstep =
              indicatorInfo && indicatorInfo.totalPages > 1;
            const usePinnedSubstepHeader =
              isMultiPageSubstep &&
              (!substepHeaderInSlice || (indicatorInfo.pageSeq ?? 1) > 1);
            // When the step continuation band is 0, leave extra Y so the redrawn title
            // clears body text (slice often starts inside rich text at the content top).
            // When the band already reserves space for "Step N (x/y)", keep a small pad only.
            const pinnedTopInPage =
              stepContinuationBandPx +
              (stepContinuationBandPx > 0
                ? Math.round(8 * domToCanvasScaleY)
                : Math.round(36 * domToCanvasScaleY));
            const substepHeaderTopInPage = substepHeaderInSlice
              ? naturalSubstepHeaderTopInPage
              : usePinnedSubstepHeader
                ? pinnedTopInPage
                : naturalSubstepHeaderTopInPage + stepContinuationBandPx;
            indicatorY =
              headerInsertPx +
              substepHeaderTopInPage +
              substepMeta.headerHeightPx / 2;
            indicatorX = substepMeta.headerLeftPx;

            // Calculate overlay to mask the original substep header from main canvas
            // This prevents duplicate/overlapping headers (header drawn from base canvas + our indicator re-draw)
            const headerTopOnPage = headerInsertPx + substepHeaderTopInPage;

            // If we moved the substep, erase the raster at the original position
            const hasMovedSubstepHeader =
              substepHeaderTopInPage !== naturalSubstepHeaderTopInPage;
            if (hasMovedSubstepHeader) {
              const eraseTop =
                headerInsertPx + naturalSubstepHeaderTopInPage - 2;
              const eraseH = Math.max(
                (substepMeta.headerHeightPx || 0) +
                  Math.round(14 * domToCanvasScaleY),
                (substepMeta.headerCanvas?.height || 0) + 10,
              );
              pageCtx.save();
              pageCtx.fillStyle = "#ffffff";
              pageCtx.fillRect(0, eraseTop, pageCanvas.width, eraseH);
              pageCtx.restore();
            }

            // Calculate the exact bottom position of the HR line to ensure complete coverage
            let hrBottomPx = 0;
            if (
              substepMeta.hrTopPx !== null &&
              substepMeta.hrTopPx !== undefined
            ) {
              hrBottomPx =
                substepMeta.hrTopPx +
                (substepMeta.hrHeightPx ||
                  Math.max(1, Math.round(1 * domToCanvasScaleY)));
            }
            // Use only the header height (text + HR), not the full canvas height which might include description
            // Ensure we cover at least up to the HR line bottom plus a buffer
            const headerOnlyHeight = Math.max(
              substepMeta.headerHeightPx,
              hrBottomPx > 0 ? hrBottomPx : substepMeta.headerHeightPx,
            );
            // Add extra buffer to ensure HR line is fully covered and prevent double lines
            const hrBuffer = hrBottomPx > 0 ? 15 : 5; // Larger buffer when HR exists to prevent double lines
            const overlayBaseHeight = Math.max(
              substepMeta.headerCanvas?.height || 0,
              headerOnlyHeight + hrBuffer + Math.round(18 * domToCanvasScaleY),
            );

            // Use minimal buffer above to avoid covering content above the header (especially tables)
            // But sufficient buffer below to cover HR line and prevent double lines
            const overlayTopBuffer = 0; // No top buffer to prevent covering table rows above
            const overlayBottomBuffer =
              substepMeta.hrTopPx !== null && substepMeta.hrTopPx !== undefined
                ? 15
                : 5; // Larger buffer below to fully cover HR line
            // Only apply top buffer if we're not at the very top of the page content
            const isAtPageTop = substepHeaderTopInPage <= 10;
            const effectiveTopBuffer = isAtPageTop ? 0 : overlayTopBuffer;
            let overlayTop = Math.max(
              0,
              headerTopOnPage - effectiveTopBuffer,
            );
            let overlayHeight =
              overlayBaseHeight + effectiveTopBuffer + overlayBottomBuffer;
            // Only use a tall erase band on continuation slices where header is not physically in this page.
            // On regular in-slice headers, a large band can wipe the substep short description.
            if (!substepHeaderInSlice) {
              overlayHeight = Math.max(
                overlayHeight,
                Math.round(96 * domToCanvasScaleY),
              );
            }
            // When the DOM header is not in this slice, body text starts at the content
            // top (y≈headerInsertPx). Mask only from headerTopOnPage leaves those lines
            // visible under the redrawn title — clear from the content top through the band.
            // Continuation pages (2/2, …) can still have a sliver of the DOM header in the
            // slice; pinning the title without a full-bleed mask leaves body text visible under it.
            const substepPageSeq = indicatorInfo?.pageSeq ?? 1;
            if (
              isMultiPageSubstep &&
              (!substepHeaderInSlice || substepPageSeq > 1)
            ) {
              const headerBandBottomPx =
                headerTopOnPage + overlayBaseHeight + overlayBottomBuffer;
              overlayTop = headerInsertPx;
              overlayHeight = Math.max(
                overlayHeight,
                headerBandBottomPx - overlayTop,
              );
            }

            // Draw white overlay to mask the original header from main canvas
            // This MUST be drawn before we draw our modified header to completely hide the original
            pageCtx.save();
            pageCtx.fillStyle = "#ffffff";
            // Use destination-out to clear the area first
            pageCtx.globalCompositeOperation = "destination-out";
            pageCtx.fillRect(0, overlayTop, pageCanvas.width, overlayHeight);
            // Then fill with white
            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.fillRect(0, overlayTop, pageCanvas.width, overlayHeight);
            // Draw multiple overlapping rectangles to ensure complete coverage
            for (let i = 0; i < 3; i++) {
              pageCtx.fillRect(
                0,
                overlayTop - i,
                pageCanvas.width,
                overlayHeight + i * 2,
              );
            }
            pageCtx.restore();

            // Show indicator whenever the substep spans multiple pages (child blocks and/or
            // short description can drive page breaks).
            if (indicatorInfo && indicatorInfo.totalPages > 1) {
              const indicatorText = `Substep ${substepMeta.substepLabel} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;

              const modifiedCanvas = document.createElement("canvas");
              modifiedCanvas.width = substepMeta.headerCanvas.width;
              modifiedCanvas.height = substepMeta.headerCanvas.height;
              const modifiedCtx = modifiedCanvas.getContext("2d");

              modifiedCtx.fillStyle = "#ffffff";
              modifiedCtx.fillRect(
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );

              const scaledFontSize = Math.round(20 * domToCanvasScaleY);
              modifiedCtx.font = `bold ${scaledFontSize}px Arial`;
              modifiedCtx.textAlign = "left";
              modifiedCtx.textBaseline = "top";

              const baseTitle = `Substep ${substepMeta.substepLabel}`;
              const indicatorPart = ` (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;

              modifiedCtx.fillStyle = "#333333";
              modifiedCtx.fillText(baseTitle, substepMeta.headerLeftPx, 0);

              const baseTitleWidth = modifiedCtx.measureText(baseTitle).width;

              modifiedCtx.fillStyle = "#6C757D";
              modifiedCtx.fillText(
                indicatorPart,
                substepMeta.headerLeftPx + baseTitleWidth,
                0,
              );

              if (
                substepMeta.sectionIndex !== null &&
                substepMeta.sectionIndex !== undefined
              ) {
                const stepNum =
                  substepMeta.stepNumber ||
                  parseInt(
                    substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                    10,
                  );
                const treePathText = `Section ${substepMeta.sectionIndex + 1} > Step ${stepNum} > Substep ${substepMeta.substepLabel}`;
                const scaledTreePathFontSize = Math.round(
                  14 * domToCanvasScaleY,
                );
                modifiedCtx.fillStyle = "#888";
                modifiedCtx.font = `${scaledTreePathFontSize}px Arial`;
                modifiedCtx.textAlign = "right";
                modifiedCtx.textBaseline = "top";
                modifiedCtx.fillText(
                  treePathText,
                  modifiedCanvas.width - 20,
                  0,
                );
              }

              // Do not draw an additional HR in continuation indicator canvas.
              // The source header/content already provides a divider line and
              // drawing it again causes a visible double-HR under "Substep X (1/2)".

              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.drawImage(
                modifiedCanvas,
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
                0,
                headerInsertPx + substepHeaderTopInPage,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );
              pageCtx.globalCompositeOperation = "source-over";
            } else {
              // Always create a modified canvas to avoid drawing the original headerCanvas
              // This ensures we never show indicators for single-page substeps
              const modifiedCanvas = document.createElement("canvas");
              modifiedCanvas.width = substepMeta.headerCanvas.width;
              modifiedCanvas.height = substepMeta.headerCanvas.height;
              const modifiedCtx = modifiedCanvas.getContext("2d");

              modifiedCtx.fillStyle = "#ffffff";
              modifiedCtx.fillRect(
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );

              const scaledFontSize = Math.round(20 * domToCanvasScaleY);
              modifiedCtx.font = `bold ${scaledFontSize}px Arial`;
              modifiedCtx.textAlign = "left";
              modifiedCtx.textBaseline = "top";

              // Draw substep title without indicators (since it's single page)
              const baseTitle = `Substep ${substepMeta.substepLabel}`;
              modifiedCtx.fillStyle = "#333333";
              modifiedCtx.fillText(baseTitle, substepMeta.headerLeftPx, 0);

              if (
                substepMeta.sectionIndex !== null &&
                substepMeta.sectionIndex !== undefined
              ) {
                const stepNum =
                  substepMeta.stepNumber ||
                  parseInt(
                    substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                    10,
                  );
                const treePathText = `Section ${substepMeta.sectionIndex + 1} > Step ${stepNum} > Substep ${substepMeta.substepLabel}`;
                const scaledTreePathFontSize = Math.round(
                  14 * domToCanvasScaleY,
                );
                modifiedCtx.fillStyle = "#888";
                modifiedCtx.font = `${scaledTreePathFontSize}px Arial`;
                modifiedCtx.textAlign = "right";
                modifiedCtx.textBaseline = "top";
                modifiedCtx.fillText(
                  treePathText,
                  modifiedCanvas.width - 20,
                  0,
                );
              }

              let hrY;
              if (
                substepMeta.hrTopPx !== null &&
                substepMeta.hrTopPx !== undefined
              ) {
                hrY = substepMeta.hrTopPx;
              } else {
                const estimatedTextHeight = Math.round(24 * domToCanvasScaleY);
                hrY = estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
              }
              {
                const estimatedFallback =
                  Math.round(24 * domToCanvasScaleY) +
                  Math.round(10 * domToCanvasScaleY);
                if (!(Number.isFinite(hrY) && hrY >= 0)) hrY = estimatedFallback;
                if (!(hrY >= 0 && hrY < modifiedCanvas.height)) {
                  hrY = estimatedFallback;
                }
                const hrMaxY = Math.max(1, modifiedCanvas.height - 1);
                hrY = Math.min(Math.max(hrY, 1), hrMaxY);
              }

              if (modifiedCanvas.height >= 2) {
                modifiedCtx.strokeStyle = "#cccccc";
                modifiedCtx.lineWidth = Math.max(
                  1,
                  Math.round(1 * domToCanvasScaleY),
                );
                modifiedCtx.beginPath();
                modifiedCtx.moveTo(0, hrY);
                modifiedCtx.lineTo(modifiedCanvas.width, hrY);
                modifiedCtx.stroke();
              }

              // Draw the modified canvas (never the original headerCanvas)
              pageCtx.globalCompositeOperation = "source-over";
              pageCtx.drawImage(
                modifiedCanvas,
                0,
                0,
                modifiedCanvas.width,
                modifiedCanvas.height,
                0,
                headerInsertPx + substepHeaderTopInPage,
                modifiedCanvas.width,
                modifiedCanvas.height,
              );
              pageCtx.globalCompositeOperation = "source-over";
            }
          } else {
            const parentStepMetaForBand = stepMetas.find((s) => {
              if (s.stepNumber !== substepMeta.stepNumber) return false;
              if (
                substepMeta.sectionIndex != null &&
                s.sectionIndex !== substepMeta.sectionIndex
              ) {
                return false;
              }
              return true;
            });
            stepContinuationBandPx = computeSubstepStepContinuationBandPx(
              parentStepMetaForBand,
            );
            const childElementsOnThisPage = isZipExport
              ? blockMetasPx.filter((meta) => {
                  if (!childBlockLabels.has(meta.label)) return false;
                  if (meta.substepId !== substepMeta.substepId) return false;
                  return meta.bottomPx > pageStartPx && meta.topPx < pageEndPx;
                })
              : [];

            if (stepContinuationBandPx > 0) {
              indicatorY =
                headerInsertPx +
                stepContinuationBandPx +
                Math.round(34 * domToCanvasScaleY);
              indicatorX = substepMeta.headerLeftPx || 20;
            } else if (!isZipExport) {
              // Normal export: pin continuation substep title near the page top.
              // This avoids drifting into rich-text content on 2/2 pages.
              indicatorY = headerInsertPx + Math.round(30 * domToCanvasScaleY);
              indicatorX = substepMeta.headerLeftPx || 20;
            } else if (childElementsOnThisPage.length > 0) {
              // Keep continuation substep heading near top band; do not anchor it
              // to child content top, otherwise heading can drift too low.
              indicatorY = headerInsertPx + Math.round(26 * domToCanvasScaleY);
              indicatorX = substepMeta.headerLeftPx || 20;
            } else {
              indicatorY = headerInsertPx + 25;
              indicatorX = substepMeta.headerLeftPx || 20;
            }
          }

          if (!headerVisible) {
            const isSubstepContinuationPage =
              indicatorInfo &&
              indicatorInfo.totalPages > 1 &&
              (indicatorInfo.pageSeq ?? 1) > 1;
            let indicatorText;
            if (indicatorInfo && indicatorInfo.totalPages > 1) {
              indicatorText = `Substep ${substepMeta.substepLabel} (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;
            } else {
              indicatorText = `Substep ${substepMeta.substepLabel}`;
            }

            pageCtx.save();
            pageCtx.fillStyle = "#333333";
            const scaledFontSize = Math.round(20 * domToCanvasScaleY);
            pageCtx.font = `bold ${scaledFontSize}px Arial`;
            pageCtx.textAlign = "left";
            pageCtx.textBaseline = "middle";

            const textMetrics = pageCtx.measureText(indicatorText);
            const textWidth = textMetrics.width;
            const textHeight = 24;
            const substepBackgroundPaddingY = 6;
            let backgroundHeight = textHeight + substepBackgroundPaddingY * 2;
            let backgroundTop =
              indicatorY - textHeight / 2 - substepBackgroundPaddingY;
            // Keep a larger white band on continuation pages so child content
            // doesn't bleed around/behind the substep heading + HR line.
            const inStepContinuationBand = stepContinuationBandPx > 0;
            if (inStepContinuationBand) {
              backgroundHeight += Math.round(28 * domToCanvasScaleY);
            }
            if (isSubstepContinuationPage) {
              // Always pin substep continuation heading to the top band.
              // Without this, child short-description text can appear above the heading.
              indicatorY = headerInsertPx + Math.round(30 * domToCanvasScaleY);
              backgroundTop = Math.min(
                backgroundTop,
                headerInsertPx + Math.round(4 * domToCanvasScaleY),
              );
              backgroundHeight = Math.max(
                backgroundHeight,
                Math.round(72 * domToCanvasScaleY),
              );
            }
            if (!isZipExport && !inStepContinuationBand) {
              const forcedTop = headerInsertPx + Math.round(6 * domToCanvasScaleY);
              backgroundTop = Math.min(backgroundTop, forcedTop);
              backgroundHeight = Math.max(
                backgroundHeight,
                Math.round(64 * domToCanvasScaleY),
              );
            }
            const scaledTextHeightForBg = Math.round(24 * domToCanvasScaleY);
            const hrYForBg =
              indicatorY +
              scaledTextHeightForBg / 2 +
              Math.round(8 * domToCanvasScaleY);
            const minBgHeight = Math.max(
              0,
              hrYForBg + Math.round(10 * domToCanvasScaleY) - backgroundTop,
            );
            backgroundHeight = Math.max(backgroundHeight, minBgHeight);

            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(
              0,
              backgroundTop,
              pageCanvas.width,
              backgroundHeight,
            );

            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.font = `bold ${scaledFontSize}px Arial`;
            let baseTitle = indicatorText;
            let indicatorPart = "";
            const indicatorMatch = indicatorText.match(
              /^(.+?)\s*(\(\d+\/\d+\))$/,
            );
            if (indicatorMatch) {
              baseTitle = indicatorMatch[1];
              indicatorPart = indicatorMatch[2];
            }

            pageCtx.fillStyle = "#333333";
            pageCtx.fillText(baseTitle, indicatorX, indicatorY);

            if (indicatorPart) {
              const baseTitleWidth = pageCtx.measureText(baseTitle).width;
              pageCtx.fillStyle = "#6C757D";
              pageCtx.fillText(
                " " + indicatorPart,
                indicatorX + baseTitleWidth,
                indicatorY,
              );
            }

            if (
              substepMeta.sectionIndex !== null &&
              substepMeta.sectionIndex !== undefined
            ) {
              const stepNum =
                substepMeta.stepNumber ||
                parseInt(
                  substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                  10,
                );
              const treePathText = `Section ${substepMeta.sectionIndex + 1} > Step ${stepNum} > Substep ${substepMeta.substepLabel}`;
              const scaledTreePathFontSize = Math.round(14 * domToCanvasScaleY);
              pageCtx.fillStyle = "#888";
              pageCtx.font = `${scaledTreePathFontSize}px Arial`;
              pageCtx.textAlign = "right";
              pageCtx.textBaseline = "middle";
              pageCtx.fillText(treePathText, pageCanvas.width - 20, indicatorY);
            }

            const scaledTextHeight = Math.round(24 * domToCanvasScaleY);
            const hrY =
              indicatorY +
              scaledTextHeight / 2 +
              Math.round(8 * domToCanvasScaleY);
            pageCtx.strokeStyle = "#cccccc";
            pageCtx.lineWidth = Math.max(1, Math.round(1 * domToCanvasScaleY));
            pageCtx.beginPath();
            pageCtx.moveTo(0, hrY);
            pageCtx.lineTo(pageCanvas.width, hrY);
            pageCtx.stroke();

            pageCtx.globalCompositeOperation = "source-over";
            pageCtx.restore();
          }
        });

        // Final safety pass for section titles: paint "Section X" text last so it remains
        // visible even if earlier mask/overlay logic has wiped the heading glyphs.
        const sectionTitlesOnPage = (sectionHeadingTextMetasPx || []).filter((meta) => {
          const top = Number(meta?.topPx);
          const h = Number(meta?.heightPx || 0);
          if (!Number.isFinite(top) || !Number.isFinite(h) || h <= 0) return false;
          const overlapsPage = top + h > pageStartPx && top < pageEndPx;
          if (!overlapsPage) return false;
          const topInPage = top - pageStartPx;
          const nearBottomRiskBand = topInPage >= pageContentPx * 0.62;
          const hasContinuationPressure =
            stepsWithContentOnPage.some((stepMeta) => {
              const info = getStepIndicatorInfo(stepMeta.stepId);
              return (
                info &&
                info.totalPages > 1 &&
                Number.isFinite(stepMeta.headerTopPx) &&
                stepMeta.headerTopPx < pageStartPx
              );
            }) || continuingTables.length > 0;
          // Only force-draw in risk scenarios to avoid duplicate headings in normal pages.
          return nearBottomRiskBand || hasContinuationPressure;
        });
        if (sectionTitlesOnPage.length > 0) {
        }
        sectionTitlesOnPage.forEach((meta) => {
          const y =
            headerInsertPx +
            Math.max(
              Math.round(14 * domToCanvasScaleY),
              meta.topPx - pageStartPx + Math.round(meta.heightPx * 0.55),
            );
          const x = Number.isFinite(meta.leftPx) ? meta.leftPx : 20;
          pageCtx.save();
          pageCtx.globalCompositeOperation = "source-over";
          pageCtx.fillStyle = "#333333";
          pageCtx.font = `bold ${Math.round(20 * domToCanvasScaleY)}px Arial`;
          pageCtx.textAlign = "left";
          pageCtx.textBaseline = "middle";
          pageCtx.fillText(meta.text, x, y);
          pageCtx.restore();
        });

        pageImages.push({
          dataUrl: pageCanvas.toDataURL("image/png"),
          heightMm: pageCanvas.height * pixelToMm,
          headerSlots,
          tablesOnPage,
          pageStartPx,
          pageContentPx,
          headerInsertPx,
          pageHeightPx: pageCanvas.height,
        });

        // Safety: always advance at least 1px to avoid rare infinite loops
        // when pageContentPx ends up as 0 due to rounding/measurement edge cases.
        currentOffsetPx += Math.max(1, pageContentPx);
      }

      if (pageImages.length === 0) {
        pageImages.push({
          dataUrl: canvas.toDataURL("image/png"),
          heightMm: imgHeight,
          headerSlots: [],
          tablesOnPage: [],
          pageStartPx: 0,
          pageContentPx: totalHeightPx,
          headerInsertPx: 0,
          pageHeightPx: canvas.height,
        });
      }

      // Recalculate step page ranges based on actual page boundaries
      // This ensures accurate step page indicators by checking which pages each step actually spans
      const recalculatedMultiPageSteps = new Map();
      stepMetas.forEach((stepMeta) => {
        const stepChildElements = blockMetasPx.filter((meta) => {
          if (!childBlockLabels.has(meta.label)) return false;
          if (meta.substepId !== null && meta.substepId !== undefined)
            return false;
          return meta.stepId === stepMeta.stepId;
        });

        // Get Item Required tables for this step (to include them as child nodes)
        const stepItemRequiredTables = tableMetas.filter((tableMeta) => {
          if (!tableMeta.isItemRequiredTable) return false;
          // Check if table belongs to this step by stepId
          if (tableMeta.stepId === stepMeta.stepId) return true;
          // Fallback: check if table is within step's DOM element
          if (
            tableMeta.stepContainer &&
            stepMeta.element &&
            tableMeta.stepContainer === stepMeta.element
          )
            return true;
          return false;
        });

        if (stepMeta.headerTopPx === undefined || stepMeta.headerTopPx < 0) {
          return;
        }

        const stepHeaderTop = stepMeta.headerTopPx;
        const stepHeaderBottom =
          stepMeta.headerTopPx + (stepMeta.headerHeightPx || 0);

        // Find the bottom of the last child node or Item Required table, or use step header bottom if no children
        let stepBottom = stepHeaderBottom;
        const allChildBottomPxFinal = [];
        if (stepChildElements.length > 0) {
          allChildBottomPxFinal.push(
            ...stepChildElements.map((meta) => meta.bottomPx),
          );
        }
        if (stepItemRequiredTables.length > 0) {
          allChildBottomPxFinal.push(
            ...stepItemRequiredTables.map((tableMeta) => tableMeta.bottomPx),
          );
        }
        if (allChildBottomPxFinal.length > 0) {
          stepBottom = Math.max(stepHeaderBottom, ...allChildBottomPxFinal);
        }
        if (
          stepMeta.stepShortDescBottomPx != null &&
          Number.isFinite(stepMeta.stepShortDescBottomPx)
        ) {
          stepBottom = Math.max(stepBottom, stepMeta.stepShortDescBottomPx);
        }

        // Find which pages this step spans by checking page boundaries
        const stepPageIndices = [];
        pageImages.forEach((pageImage, pageIdx) => {
          const pageStart = pageImage.pageStartPx;
          const pageEnd = pageStart + pageImage.pageContentPx;

          // Step appears on this page if:
          // - Step header or content overlaps with this page
          const stepOverlapsPage =
            stepHeaderTop < pageEnd && stepBottom > pageStart;

          if (stepOverlapsPage) {
            stepPageIndices.push(pageIdx);
          }
        });

        if (stepPageIndices.length > 0) {
          recalculatedMultiPageSteps.set(stepMeta.stepId, stepPageIndices);
        }
      });

      // Update multiPageSteps with recalculated values
      recalculatedMultiPageSteps.forEach((pageIndices, stepId) => {
        multiPageSteps.set(stepId, pageIndices);
      });

      // Recalculate substep page ranges based on actual page boundaries
      // This ensures accurate substep page indicators by checking which pages each substep actually spans
      const recalculatedMultiPageSubsteps = new Map();
      substepMetas.forEach((substepMeta) => {
        const substepChildElements = blockMetasPx.filter((meta) => {
          if (!childBlockLabels.has(meta.label)) return false;
          return meta.substepId === substepMeta.substepId;
        });

        if (
          substepMeta.headerTopPx === undefined ||
          substepMeta.headerTopPx < 0
        ) {
          return;
        }

        const substepHeaderTop = substepMeta.headerTopPx;
        const substepHeaderBottom =
          substepMeta.headerTopPx + (substepMeta.headerHeightPx || 0);

        // Find the bottom of the last child node, or use substep header bottom if no children
        let substepBottom = substepHeaderBottom;
        if (substepChildElements.length > 0) {
          substepBottom = Math.max(
            substepHeaderBottom,
            ...substepChildElements.map((meta) => meta.bottomPx),
          );
        }
        if (
          substepMeta.substepShortDescBottomPx != null &&
          Number.isFinite(substepMeta.substepShortDescBottomPx)
        ) {
          substepBottom = Math.max(
            substepBottom,
            substepMeta.substepShortDescBottomPx,
          );
        }

        // Find which pages this substep spans by checking page boundaries
        const substepPageIndices = [];
        pageImages.forEach((pageImage, pageIdx) => {
          const pageStart = pageImage.pageStartPx;
          const pageEnd = pageStart + pageImage.pageContentPx;

          // Substep appears on this page if:
          // - Substep header or content overlaps with this page
          const substepOverlapsPage =
            substepHeaderTop < pageEnd && substepBottom > pageStart;

          if (substepOverlapsPage) {
            substepPageIndices.push(pageIdx);
          }
        });

        if (substepPageIndices.length > 0) {
          recalculatedMultiPageSubsteps.set(
            substepMeta.substepId,
            substepPageIndices,
          );
        }
      });

      // Update multiPageSubsteps with recalculated values
      recalculatedMultiPageSubsteps.forEach((pageIndices, substepId) => {
        multiPageSubsteps.set(substepId, pageIndices);
      });

      // Re-render step headers with updated page indicators after final page boundaries exist.
      // Fixes mismatches like "Step 1 (1/3)" on an early page vs "Step 1 (2/4)" on the next pass
      // when estimates used availableHeightPx before all slices were known.
      const disableStepSubstepHeaderRerender = false;
      if (!disableStepSubstepHeaderRerender) {
      }

      const reRenderPromises = disableStepSubstepHeaderRerender
        ? []
        : pageImages.map(
        (pageImage, pageIndexForRerender) => {
          return new Promise((resolve, reject) => {
            const pageCanvas = document.createElement("canvas");
            const pageCtx = pageCanvas.getContext("2d");

            // Create an image from the existing dataUrl to draw it on the canvas
            const img = new Image();
            img.onload = () => {
              try {
                pageCanvas.width = img.width;
                pageCanvas.height = img.height;
                pageCtx.drawImage(img, 0, 0);

                const pageStartPx = pageImage.pageStartPx;
                const pageEndPx = pageStartPx + pageImage.pageContentPx;

                // Find steps that have content on this page
                const stepsOnThisPageRerender = stepMetas.filter((stepMeta) => {
                  if (!stepMeta.headerCanvas) return false;
                  const headerOnPage =
                    stepMeta.headerTopPx >= pageStartPx &&
                    stepMeta.headerTopPx < pageEndPx;
                  const stepChildElements = blockMetasPx.filter((meta) => {
                    const childBlockLabels = new Set([
                      "attachment",
                      "rich-text",
                      "video",
                      "form",
                      "canvas",
                    ]);
                    if (!childBlockLabels.has(meta.label)) return false;
                    if (meta.substepId !== null && meta.substepId !== undefined)
                      return false;
                    return meta.stepId === stepMeta.stepId;
                  });
                  const stepItemRequiredTables = tableMetas.filter(
                    (tableMeta) => {
                      if (!tableMeta.isItemRequiredTable) return false;
                      if (tableMeta.stepId === stepMeta.stepId) return true;
                      if (
                        tableMeta.stepContainer &&
                        stepMeta.element &&
                        tableMeta.stepContainer === stepMeta.element
                      )
                        return true;
                      return false;
                    },
                  );
                  const hasChildrenOnPage =
                    stepChildElements.some(
                      (meta) =>
                        meta.bottomPx > pageStartPx && meta.topPx < pageEndPx,
                    ) ||
                    stepItemRequiredTables.some(
                      (tableMeta) =>
                        tableMeta.bottomPx > pageStartPx &&
                        tableMeta.topPx < pageEndPx,
                    ) ||
                    (stepMeta.stepShortDescBottomPx != null &&
                      stepMeta.stepShortDescTopPx != null &&
                      stepMeta.stepShortDescBottomPx > pageStartPx &&
                      stepMeta.stepShortDescTopPx < pageEndPx);
                  return headerOnPage || hasChildrenOnPage;
                });

                // Re-render each step header on this page with updated indicators
                stepsOnThisPageRerender.forEach((stepMeta) => {
                  const pageIndices = multiPageSteps.get(stepMeta.stepId) || [];
                  if (pageIndices.length <= 1) return; // Skip single-page steps

                  const sortedPageIndices = [...pageIndices].sort(
                    (a, b) => a - b,
                  );
                  const pageSeq =
                    sortedPageIndices.indexOf(pageIndexForRerender) + 1;
                  if (pageSeq <= 0) return; // Step not on this page

                  const totalPages = sortedPageIndices.length;
                  const indicatorInfo = { pageSeq, totalPages };

                  // Calculate where the step header is on this page
                  const stepHeaderTopInPage = Math.max(
                    0,
                    stepMeta.headerTopPx - pageStartPx,
                  );
                  const headerInsertPx = pageImage.headerInsertPx || 0;
                  const stepHeaderTopOnCanvas =
                    headerInsertPx + stepHeaderTopInPage;

                  const stepHeaderBandOnPage =
                    stepMeta.headerTopPx >= pageStartPx &&
                    stepMeta.headerTopPx < pageEndPx;
                  const hasSubstepHeadersOnThisPage = substepMetas.some(
                    (substepMeta) =>
                      substepMeta.stepId === stepMeta.stepId &&
                      substepMeta.headerTopPx >= pageStartPx &&
                      substepMeta.headerTopPx < pageEndPx,
                  );
                  const hasSubstepHeaderAtTop = substepMetas.some(
                    (substepMeta) => {
                      if (substepMeta.stepId !== stepMeta.stepId) return false;
                      const substepHeaderTopOnPage =
                        substepMeta.headerTopPx - pageStartPx;
                      return (
                        substepHeaderTopOnPage >= 0 &&
                        substepHeaderTopOnPage < 50
                      );
                    },
                  );
                  const hasItemRequiredTableOnThisPage = tableMetas.some(
                    (tableMeta) => {
                      if (!tableMeta.isItemRequiredTable) return false;
                      const belongsToStep =
                        tableMeta.stepId === stepMeta.stepId ||
                        (tableMeta.stepContainer &&
                          stepMeta.element &&
                          tableMeta.stepContainer === stepMeta.element);
                      if (!belongsToStep) return false;
                      const tableContinues =
                        tableMeta.topPx < pageStartPx &&
                        tableMeta.bottomPx > pageStartPx;
                      const tableOnPage =
                        tableMeta.topPx >= pageStartPx &&
                        tableMeta.topPx < pageEndPx;
                      return tableContinues || tableOnPage;
                    },
                  );
                  let shouldSuppressStepContinuation = isZipExport
                    ? hasSubstepHeadersOnThisPage
                    : hasSubstepHeaderAtTop;
                  if (!isZipExport && hasItemRequiredTableOnThisPage) {
                    shouldSuppressStepContinuation = false;
                  }

                  // Match ZIP behavior: on continuation pages where a substep header
                  // is present, suppress the step continuation heading entirely.
                  if (!stepHeaderBandOnPage && shouldSuppressStepContinuation) {
                    return;
                  }

                  // Continuation page: step h2 is not in this slice — do not paste headerCanvas
                  // at y≈0 (wrong art and wrong totals). Paint title + breadcrumb + HR only.
                  if (!stepHeaderBandOnPage) {
                    const contTop =
                      headerInsertPx + Math.round(6 * domToCanvasScaleY);
                    // Keep continuation clear band tight; a tall wipe here can erase
                    // substep continuation headers that are drawn just below Step (x/y).
                    const contH = Math.round(64 * domToCanvasScaleY);
                    pageCtx.save();
                    pageCtx.fillStyle = "#ffffff";
                    pageCtx.fillRect(0, contTop, pageCanvas.width, contH);
                    pageCtx.restore();

                    const scaledFontSize = Math.round(20 * domToCanvasScaleY);
                    const leftX = stepMeta.headerLeftPx || 20;
                    const textY =
                      contTop + Math.round(22 * domToCanvasScaleY);
                    const baseTitle = `Step ${stepMeta.stepNumber}`;
                    pageCtx.save();
                    pageCtx.fillStyle = "#333333";
                    pageCtx.font = `bold ${scaledFontSize}px Arial`;
                    pageCtx.textAlign = "left";
                    pageCtx.textBaseline = "middle";
                    pageCtx.fillText(baseTitle, leftX, textY);
                    const baseW = pageCtx.measureText(baseTitle).width;
                    pageCtx.fillStyle = "#6C757D";
                    pageCtx.fillText(
                      ` (${pageSeq}/${totalPages})`,
                      leftX + baseW,
                      textY,
                    );
                    if (
                      stepMeta.sectionIndex !== null &&
                      stepMeta.sectionIndex !== undefined
                    ) {
                      const treePathText = `Section ${stepMeta.sectionIndex + 1} > Step ${stepMeta.globalStepNumber || stepMeta.stepNumber}`;
                      const scaledTreePathFontSize = Math.round(
                        14 * domToCanvasScaleY,
                      );
                      pageCtx.fillStyle = "#888";
                      pageCtx.font = `${scaledTreePathFontSize}px Arial`;
                      pageCtx.textAlign = "right";
                      pageCtx.textBaseline = "middle";
                      pageCtx.fillText(
                        treePathText,
                        pageCanvas.width - 20,
                        textY,
                      );
                    }
                    const hrY =
                      textY +
                      Math.round(14 * domToCanvasScaleY) +
                      Math.round(8 * domToCanvasScaleY);
                    pageCtx.strokeStyle = "#cccccc";
                    pageCtx.lineWidth = Math.max(
                      1,
                      Math.round(1 * domToCanvasScaleY),
                    );
                    pageCtx.beginPath();
                    pageCtx.moveTo(0, hrY);
                    pageCtx.lineTo(pageCanvas.width, hrY);
                    pageCtx.stroke();
                    pageCtx.restore();
                    return;
                  }

                  // Clear the old header area using robust multi-strategy approach
                  // This prevents duplicate/ghosting when Item Required table splits across pages
                  const headerOnlyHeight =
                    stepMeta.headerHeightPx ||
                    stepMeta.headerCanvas?.height ||
                    100;
                  const hrHeight =
                    stepMeta.hrTopPx != null && stepMeta.hrHeightPx
                      ? stepMeta.hrHeightPx
                      : 0;
                  const headerHeightToCover = headerOnlyHeight + hrHeight + 10; // Include HR + buffer
                  // Do not erase too much above the step header, otherwise nearby section headings
                  // can get wiped when step headers are re-rendered.
                  const topMaskBufferPx = 2;
                  const bottomMaskBufferPx = 15;
                  const overlayTop = Math.max(
                    0,
                    stepHeaderTopOnCanvas - topMaskBufferPx,
                  );
                  const overlayHeight =
                    headerHeightToCover + topMaskBufferPx + bottomMaskBufferPx;

                  if (overlayHeight > 0) {
                    pageCtx.save();

                    // Strategy 1: Use destination-out to clear the area first
                    pageCtx.globalCompositeOperation = "destination-out";
                    pageCtx.fillStyle = "#ffffff";
                    pageCtx.fillRect(
                      0,
                      overlayTop,
                      pageCanvas.width,
                      overlayHeight,
                    );

                    // Strategy 2: Use source-over to fill with white - multiple overlapping passes
                    pageCtx.globalCompositeOperation = "source-over";
                    pageCtx.fillStyle = "#ffffff";
                    for (let i = 0; i < 20; i++) {
                      const offset = i * 1;
                      pageCtx.fillRect(
                        0,
                        overlayTop - offset,
                        pageCanvas.width,
                        overlayHeight + offset * 2,
                      );
                    }

                    // Strategy 3: Final full-coverage passes to eliminate any ghosting
                    for (let i = 0; i < 5; i++) {
                      pageCtx.fillRect(
                        0,
                        overlayTop - i * 2,
                        pageCanvas.width,
                        overlayHeight + i * 4,
                      );
                    }

                    pageCtx.restore();
                  }

                  // Re-draw the step header with the updated indicator
                  const modifiedCanvas = document.createElement("canvas");
                  modifiedCanvas.width = stepMeta.headerCanvas.width;
                  modifiedCanvas.height = stepMeta.headerCanvas.height;
                  const modifiedCtx = modifiedCanvas.getContext("2d");

                  modifiedCtx.fillStyle = "#ffffff";
                  modifiedCtx.fillRect(
                    0,
                    0,
                    modifiedCanvas.width,
                    modifiedCanvas.height,
                  );

                  const scaledFontSize = Math.round(20 * domToCanvasScaleY);
                  modifiedCtx.font = `bold ${scaledFontSize}px Arial`;
                  modifiedCtx.textAlign = "left";
                  modifiedCtx.textBaseline = "top";

                  const baseTitle = `Step ${stepMeta.stepNumber}`;
                  modifiedCtx.fillStyle = "#333333";
                  modifiedCtx.fillText(baseTitle, stepMeta.headerLeftPx, 0);

                  // Add indicator part
                  const baseTitleWidth =
                    modifiedCtx.measureText(baseTitle).width;
                  const indicatorPart = ` (${indicatorInfo.pageSeq}/${indicatorInfo.totalPages})`;
                  modifiedCtx.fillStyle = "#6C757D";
                  modifiedCtx.fillText(
                    indicatorPart,
                    stepMeta.headerLeftPx + baseTitleWidth,
                    0,
                  );

                  // Add section path if present
                  if (
                    stepMeta.sectionIndex !== null &&
                    stepMeta.sectionIndex !== undefined
                  ) {
                    const treePathText = `Section ${stepMeta.sectionIndex + 1} > Step ${stepMeta.globalStepNumber || stepMeta.stepNumber}`;
                    const scaledTreePathFontSize = Math.round(
                      14 * domToCanvasScaleY,
                    );
                    modifiedCtx.fillStyle = "#888";
                    modifiedCtx.font = `${scaledTreePathFontSize}px Arial`;
                    modifiedCtx.textAlign = "right";
                    modifiedCtx.textBaseline = "top";
                    modifiedCtx.fillText(
                      treePathText,
                      modifiedCanvas.width - 20,
                      0,
                    );
                  }

                  // Draw HR line if present
                  let hrY;
                  if (
                    stepMeta.hrTopPx !== null &&
                    stepMeta.hrTopPx !== undefined
                  ) {
                    hrY = stepMeta.hrTopPx;
                  } else {
                    const estimatedTextHeight = Math.round(
                      24 * domToCanvasScaleY,
                    );
                    hrY =
                      estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
                  }
                  if (!(Number.isFinite(hrY) && hrY >= 0)) {
                    const estimatedTextHeight = Math.round(
                      24 * domToCanvasScaleY,
                    );
                    hrY =
                      estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
                  }
                  {
                    const estimatedFallback =
                      Math.round(24 * domToCanvasScaleY) +
                      Math.round(10 * domToCanvasScaleY);
                    if (!(hrY >= 0 && hrY < modifiedCanvas.height)) {
                      hrY = estimatedFallback;
                    }
                    const hrMaxY = Math.max(1, modifiedCanvas.height - 1);
                    hrY = Math.min(Math.max(hrY, 1), hrMaxY);
                  }

                  if (modifiedCanvas.height >= 2) {
                    modifiedCtx.strokeStyle = "#cccccc";
                    modifiedCtx.lineWidth = Math.max(
                      1,
                      Math.round(1 * domToCanvasScaleY),
                    );
                    modifiedCtx.beginPath();
                    modifiedCtx.moveTo(0, hrY);
                    modifiedCtx.lineTo(modifiedCanvas.width, hrY);
                    modifiedCtx.stroke();
                  }

                  // Draw the modified canvas onto the page canvas
                  pageCtx.drawImage(
                    modifiedCanvas,
                    0,
                    0,
                    modifiedCanvas.width,
                    modifiedCanvas.height,
                    0,
                    stepHeaderTopOnCanvas,
                    modifiedCanvas.width,
                    modifiedCanvas.height,
                  );
                });

                const disableSubstepHeaderRerender = true;
                if (!isZipExport && !disableSubstepHeaderRerender) {
                  const substepsOnThisPageRerender = substepMetas.filter(
                    (substepMeta) =>
                      !!substepMeta.headerCanvas &&
                      substepMeta.headerTopPx >= pageStartPx &&
                      substepMeta.headerTopPx < pageEndPx,
                  );

                  substepsOnThisPageRerender.forEach((substepMeta) => {
                    const pageIndices =
                      multiPageSubsteps.get(substepMeta.substepId) || [];
                    if (pageIndices.length <= 1) return;
                    const sortedPageIndices = [...pageIndices].sort(
                      (a, b) => a - b,
                    );
                    const pageSeq =
                      sortedPageIndices.indexOf(pageIndexForRerender) + 1;
                    if (pageSeq <= 0) return;
                    // Keep page-1 substep headers from being drawn twice.
                    // The main render pass already paints them with indicators.
                    if (pageSeq === 1) return;

                    const substepHeaderTopInPage = Math.max(
                      0,
                      substepMeta.headerTopPx - pageStartPx,
                    );
                    const substepHeaderTopOnCanvas =
                      (pageImage.headerInsertPx || 0) + substepHeaderTopInPage;

                    const clearHeight = Math.max(
                      substepMeta.headerHeightPx || 0,
                      Math.min(substepMeta.headerCanvas.height || 0, 120),
                    );
                    pageCtx.save();
                    pageCtx.fillStyle = "#ffffff";
                    pageCtx.fillRect(
                      0,
                      Math.max(0, substepHeaderTopOnCanvas - 2),
                      pageCanvas.width,
                      clearHeight + Math.round(16 * domToCanvasScaleY),
                    );
                    pageCtx.restore();

                    const modifiedCanvas = document.createElement("canvas");
                    modifiedCanvas.width = substepMeta.headerCanvas.width;
                    modifiedCanvas.height = substepMeta.headerCanvas.height;
                    const modifiedCtx = modifiedCanvas.getContext("2d");
                    modifiedCtx.fillStyle = "#ffffff";
                    modifiedCtx.fillRect(
                      0,
                      0,
                      modifiedCanvas.width,
                      modifiedCanvas.height,
                    );

                    const scaledFontSize = Math.round(20 * domToCanvasScaleY);
                    modifiedCtx.font = `bold ${scaledFontSize}px Arial`;
                    modifiedCtx.textAlign = "left";
                    modifiedCtx.textBaseline = "top";

                    const baseTitle = `Substep ${substepMeta.substepLabel}`;
                    const indicatorPart = ` (${pageSeq}/${sortedPageIndices.length})`;
                    modifiedCtx.fillStyle = "#333333";
                    modifiedCtx.fillText(baseTitle, substepMeta.headerLeftPx, 0);
                    const baseTitleWidth = modifiedCtx.measureText(baseTitle).width;
                    modifiedCtx.fillStyle = "#6C757D";
                    modifiedCtx.fillText(
                      indicatorPart,
                      substepMeta.headerLeftPx + baseTitleWidth,
                      0,
                    );

                    if (
                      substepMeta.sectionIndex !== null &&
                      substepMeta.sectionIndex !== undefined
                    ) {
                      const stepNum =
                        substepMeta.stepNumber ||
                        parseInt(
                          substepMeta.substepLabel.match(/^\d+/)?.[0] || "0",
                          10,
                        );
                      const treePathText = `Section ${substepMeta.sectionIndex + 1} > Step ${stepNum} > Substep ${substepMeta.substepLabel}`;
                      const scaledTreePathFontSize = Math.round(
                        14 * domToCanvasScaleY,
                      );
                      modifiedCtx.fillStyle = "#888";
                      modifiedCtx.font = `${scaledTreePathFontSize}px Arial`;
                      modifiedCtx.textAlign = "right";
                      modifiedCtx.textBaseline = "top";
                      modifiedCtx.fillText(
                        treePathText,
                        modifiedCanvas.width - 20,
                        0,
                      );
                    }

                    let hrY;
                    if (
                      substepMeta.hrTopPx !== null &&
                      substepMeta.hrTopPx !== undefined
                    ) {
                      hrY = substepMeta.hrTopPx;
                    } else {
                      const estimatedTextHeight = Math.round(
                        24 * domToCanvasScaleY,
                      );
                      hrY =
                        estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
                    }
                    if (!(Number.isFinite(hrY) && hrY >= 0)) {
                      const estimatedTextHeight = Math.round(
                        24 * domToCanvasScaleY,
                      );
                      hrY =
                        estimatedTextHeight + Math.round(10 * domToCanvasScaleY);
                    }
                    {
                      const estimatedFallback =
                        Math.round(24 * domToCanvasScaleY) +
                        Math.round(10 * domToCanvasScaleY);
                      if (!(hrY >= 0 && hrY < modifiedCanvas.height)) {
                        hrY = estimatedFallback;
                      }
                      const hrMaxY = Math.max(1, modifiedCanvas.height - 1);
                      hrY = Math.min(Math.max(hrY, 1), hrMaxY);
                    }

                    if (modifiedCanvas.height >= 2) {
                      modifiedCtx.strokeStyle = "#cccccc";
                      modifiedCtx.lineWidth = Math.max(
                        1,
                        Math.round(1 * domToCanvasScaleY),
                      );
                      modifiedCtx.beginPath();
                      modifiedCtx.moveTo(0, hrY);
                      modifiedCtx.lineTo(modifiedCanvas.width, hrY);
                      modifiedCtx.stroke();
                    }

                    pageCtx.drawImage(
                      modifiedCanvas,
                      0,
                      0,
                      modifiedCanvas.width,
                      modifiedCanvas.height,
                      0,
                      substepHeaderTopOnCanvas,
                      modifiedCanvas.width,
                      modifiedCanvas.height,
                    );
                  });
                }

                // Update the pageImage dataUrl with the re-rendered canvas
                pageImage.dataUrl = pageCanvas.toDataURL("image/png");
                resolve();
              } catch (error) {
                console.error(
                  `[Re-render] Error re-rendering page ${pageIndexForRerender}:`,
                  error,
                );
                resolve(); // Resolve anyway to not block the PDF generation
              }
            };
            img.onerror = () => {
              console.error(
                `[Re-render] Failed to load image for page ${pageIndexForRerender}`,
              );
              resolve(); // Resolve anyway to not block the PDF generation
            };
            img.src = pageImage.dataUrl;
          });
        },
      );

      // Wait for all re-rendering to complete before continuing
      await Promise.all(reRenderPromises);
      if (!disableStepSubstepHeaderRerender) {
      }

      // Trim trailing blank pages produced by conservative pagination guards.
      // Keep this strict to avoid dropping real content pages.
      while (pageImages.length > 1) {
        const last = pageImages[pageImages.length - 1];
        const pageStartPx = Number(last?.pageStartPx || 0);
        const pageEndPx = pageStartPx + Number(last?.pageContentPx || 0);
        const hasTable = Array.isArray(last?.tablesOnPage) && last.tablesOnPage.length > 0;
        const hasHeaderSlots =
          Array.isArray(last?.headerSlots) &&
          last.headerSlots.some((s) => Number(s?.heightPx || 0) > 0);
        const hasTrackedBlock = (blockMetasPx || []).some((m) => {
          if (m?.includeInPagination === false) return false;
          const top = Number(m?.topPx);
          const bottom = Number(m?.bottomPx);
          if (!Number.isFinite(top) || !Number.isFinite(bottom)) return false;
          return bottom > pageStartPx && top < pageEndPx;
        });
        const hasTrackedTable = (tableMetas || []).some((t) => {
          const top = Number(t?.topPx);
          const bottom = Number(t?.bottomPx);
          if (!Number.isFinite(top) || !Number.isFinite(bottom)) return false;
          return bottom > pageStartPx && top < pageEndPx;
        });
        const pageContentPx = Number(last?.pageContentPx || 0);
        const isTinyTail = pageContentPx <= 8;
        if (hasTable || hasHeaderSlots || hasTrackedBlock || hasTrackedTable || !isTinyTail) {
          break;
        }
        pageImages.pop();
      }

      // Module preview PDF: html2canvas often leaves a tall wrapper; section/step/substep
      // rects span the full clone so the generic trim keeps a blank tail page (headerSlots +
      // structural overlap). Drop tail pages with no real tables or leaf blocks.
      if (isModulePreview && !isZipExport) {
        const structuralPaginationLabels = new Set(["section", "step", "substep"]);
        while (pageImages.length > 1) {
          const last = pageImages[pageImages.length - 1];
          const pageStartPx = Number(last?.pageStartPx || 0);
          const pageEndPx = pageStartPx + Number(last?.pageContentPx || 0);
          const hasTable =
            Array.isArray(last?.tablesOnPage) && last.tablesOnPage.length > 0;
          const hasLeafTrackedBlock = (blockMetasPx || []).some((m) => {
            if (m?.includeInPagination === false) return false;
            if (structuralPaginationLabels.has(m.label)) return false;
            const top = Number(m?.topPx);
            const bottom = Number(m?.bottomPx);
            if (!Number.isFinite(top) || !Number.isFinite(bottom)) return false;
            return bottom > pageStartPx && top < pageEndPx;
          });
          const hasTrackedTable = (tableMetas || []).some((t) => {
            const top = Number(t?.topPx);
            const bottom = Number(t?.bottomPx);
            if (!Number.isFinite(top) || !Number.isFinite(bottom)) return false;
            return bottom > pageStartPx && top < pageEndPx;
          });
          if (!hasTable && !hasLeafTrackedBlock && !hasTrackedTable) {
            pageImages.pop();
            continue;
          }
          break;
        }
      }

      const revisionTablePageIndicators = new Map();
      if (revisionTableId != null) {
        const revisionPages = [];
        pageImages.forEach((page, idx) => {
          if (page.tablesOnPage?.includes(revisionTableId)) {
            revisionPages.push(idx);
          }
        });
        // Always populate the map, even for single-page Revision Tables
        // so the heading "Revision Table" is shown
        revisionPages.forEach((pageIdx, seqIdx) => {
          revisionTablePageIndicators.set(pageIdx, {
            index: seqIdx + 1,
            total: revisionPages.length,
          });
        });
      }

      const bomTablePageIndicators = new Map();
      if (bomTableId != null) {
        const bomPages = [];
        pageImages.forEach((page, idx) => {
          if (page.tablesOnPage?.includes(bomTableId)) {
            bomPages.push(idx);
          }
        });
        if (bomPages.length > 1) {
          bomPages.forEach((pageIdx, seqIdx) => {
            bomTablePageIndicators.set(pageIdx, {
              index: seqIdx + 1,
              total: bomPages.length,
            });
          });
        }
      }

      pageImages.forEach((pageImage, index) => {
        if (index > 0) {
          pdf.addPage();
        }

        // CRITICAL: Draw white rectangles for header and footer BEFORE adding page image
        // This prevents the white rectangles from covering content (like rich text) at the bottom
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, headerHeight + contentTopPadding, "F");
        pdf.rect(
          0,
          pdfHeight - footerHeight - contentBottomPadding,
          pdfWidth,
          footerHeight + contentBottomPadding,
          "F",
        );

        const maxImageHeightMm =
          pdfHeight -
          headerHeight -
          contentTopPadding -
          footerHeight -
          contentBottomPadding;

        const imageHeightMm = Math.min(
          pageImage.heightMm,
          maxImageHeightMm - 2,
        );

        pdf.addImage(
          pageImage.dataUrl,
          "PNG",
          horizontalMargin,
          headerHeight + contentTopPadding,
          imgWidth,
          imageHeightMm,
        );

        (pageImage.headerSlots || []).forEach((slot) => {
          if (!slot?.dataUrl || slot.widthPx <= 0 || slot.heightPx <= 0) return;
          const slotWidthMm = slot.widthPx * pixelToMm;
          const slotHeightMm = slot.heightPx * pixelToMm;
          const slotLeftMm = horizontalMargin + slot.leftPx * pixelToMm;
          const slotTopMm =
            headerHeight + contentTopPadding + slot.topPx * pixelToMm;

          pdf.setFillColor(255, 255, 255);
          pdf.rect(slotLeftMm, slotTopMm, slotWidthMm, slotHeightMm, "F");
          pdf.addImage(
            slot.dataUrl,
            "PNG",
            slotLeftMm,
            slotTopMm,
            slotWidthMm,
            slotHeightMm,
          );
        });

        // Add clickable link annotations for links that appear on this page
        const pageStartPx = pageImage.pageStartPx || 0;
        const pageContentPx = pageImage.pageContentPx || 0;
        const headerInsertPx = pageImage.headerInsertPx || 0;
        const pageEndPx = pageStartPx + pageContentPx;
        if (typeof pdf.link === "function") {
          let linksAddedOnPage = 0;
          (linkMetasPx || []).forEach((link) => {
            if (link.topPx >= pageEndPx || link.bottomPx <= pageStartPx) return;
            const visibleTop = Math.max(link.topPx, pageStartPx);
            const visibleBottom = Math.min(link.bottomPx, pageEndPx);
            const visibleHeight = Math.max(1, visibleBottom - visibleTop);
            const topOnPage = visibleTop - pageStartPx + headerInsertPx;
            const leftMm = horizontalMargin + link.leftPx * pixelToMm;
            const topMm =
              headerHeight + contentTopPadding + topOnPage * pixelToMm;
            const widthMm = link.widthPx * pixelToMm;
            const heightMm = visibleHeight * pixelToMm;
            try {
              pdf.link(leftMm, topMm, widthMm, heightMm, { url: link.href });
              linksAddedOnPage++;
            } catch (e) {
              console.warn(
                "[PDF Export - Links] Failed to add link on page",
                index + 1,
                ":",
                e,
              );
            }
          });
          if (linksAddedOnPage > 0) {
          }
        } else {
          console.warn(
            "[PDF Export - Links] pdf.link is not a function - link annotations skipped",
          );
        }
      });

      let revisionAutotableFinalMm = null;
      let revisionAutotableEndPdfPage = null;
      let trainingAutotableFinalMm = null;
      let trainingAutotableEndPdfPage = null;

      // Render revision table with autotable to prevent row cutting
      if (revisionTableData && revisionTableMeta) {
        // Find the first page where the revision table appears
        const revisionTablePages = [];
        pageImages.forEach((pageImage, pageIdx) => {
          if (pageImage.tablesOnPage?.includes(revisionTableId)) {
            revisionTablePages.push(pageIdx);
          }
        });

        if (revisionTablePages.length > 0) {
          const firstRevisionPageIdx = revisionTablePages[0];
          const firstRevisionPage = pageImages[firstRevisionPageIdx];

          // Calculate where the table should start on the first page
          let tableStartY = headerHeight + contentTopPadding;
          if (firstRevisionPage && revisionTableMeta) {
            const pageStartPx = firstRevisionPage.pageStartPx || 0;
            const headerInsertPx = firstRevisionPage.headerInsertPx || 0;

            // Calculate table top position relative to page start
            const tableTopInPagePx = Math.max(
              0,
              revisionTableMeta.topPx - pageStartPx,
            );
            const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;

            // Convert to mm
            tableStartY =
              headerHeight +
              contentTopPadding +
              tableTopInPageCanvasPx * pixelToMm;
            tableStartY -= PDF_AUTOTABLE_REVISION_TOP_LIFT_MM;
          }
          tableStartY += PDF_AUTOTABLE_REVISION_HEADING_EXTRA_TOP_MM;

          // Mask the revision table area on all pages where it appears
          revisionTablePages.forEach((pageIdx) => {
            pdf.setPage(pageIdx + 1);

            const pageImage = pageImages[pageIdx];
            const pageStartPx = pageImage.pageStartPx || 0;
            const headerInsertPx = pageImage.headerInsertPx || 0;

            // Calculate table bounds on this page
            const tableTopInPagePx = Math.max(
              0,
              revisionTableMeta.topPx - pageStartPx,
            );
            const tableBottomInPagePx = Math.min(
              pageImage.pageContentPx || 0,
              revisionTableMeta.bottomPx - pageStartPx,
            );

            if (tableBottomInPagePx > tableTopInPagePx) {
              const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;
              const tableHeightInPagePx =
                tableBottomInPagePx - tableTopInPagePx;

              // Convert to mm
              // Extend mask area to cover any borders from previous canvas rendering
              const extraMaskTopMm = 2; // Extra space above to cover borders
              const extraMaskBottomMm = 2; // Extra space below to cover borders
              const extraMaskSidesMm = 2; // Extra space on left and right to cover vertical black borders
              const maskTopMm =
                headerHeight +
                contentTopPadding +
                tableTopInPageCanvasPx * pixelToMm -
                extraMaskTopMm;
              const maskHeightMm =
                tableHeightInPagePx * pixelToMm +
                extraMaskTopMm +
                extraMaskBottomMm;
              const maskLeftMm =
                horizontalMargin +
                (revisionTableMeta.leftPx || 0) * pixelToMm -
                extraMaskSidesMm;
              const maskWidthMm =
                (revisionTableMeta.widthPx || imgWidth) * pixelToMm +
                extraMaskSidesMm * 2;

              // Mask the table area with white rectangle (including extra space to cover borders)
              pdf.setFillColor(255, 255, 255);
              pdf.rect(maskLeftMm, maskTopMm, maskWidthMm, maskHeightMm, "F");
            }
          });

          // Render the table with autotable starting from the first page
          pdf.setPage(firstRevisionPageIdx + 1);

          // Draw "Revision Table" heading above the table
          const revisionTableName =
            tableIdToName.get(revisionTableId) || "Revision Table";
          const revisionTablePagesCount = revisionTablePages.length;
          const revisionHeadingText =
            revisionTablePagesCount > 1
              ? `${revisionTableName} (1/${revisionTablePagesCount})`
              : revisionTableName;

          // Position heading above the table (slightly tighter than other tables)
          const headingY = tableStartY - effectiveRevisionHeadingToTableMm;
          const headingX =
            horizontalMargin + (revisionTableMeta.leftPx || 0) * pixelToMm;

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(13, 27, 42);
          pdf.text(revisionHeadingText, headingX, headingY);

          // Calculate table width
          const tableWidthMm = Math.min(
            (revisionTableMeta.widthPx || canvas.width) * pixelToMm,
            pdfWidth - horizontalMargin * 2,
          );

          // Calculate left margin
          const tableLeftMm =
            horizontalMargin + (revisionTableMeta.leftPx || 0) * pixelToMm;

          // Calculate top margin to avoid overlapping with page header on new pages
          const topMarginForHeader = headerHeight + contentTopPadding;

          // Calculate startY for continuation pages (accounts for heading space)
          // Heading is at topMarginForHeader (no extra offset)
          // We need: heading Y + heading height + spacing to ensure table doesn't overlap heading
          const headingYPosition = topMarginForHeader;
          const headingHeight = 4; // Height of 7pt font text including line spacing
          const spacingAfterHeading = 15; // Margin top for table - spacing between heading and table
          const startYForContinuationPages =
            headingYPosition + headingHeight + spacingAfterHeading;

          // Store the initial page number before rendering
          const initialPageNumber = pdf.getNumberOfPages();
          let tablePageCounter = 0; // Track which page of the table we're on (1-based)

          autoTable(pdf, {
            startY: tableStartY,
            head: [revisionTableData.headers],
            body: revisionTableData.rows,
            margin: {
              left: tableLeftMm,
              right: pdfWidth - tableLeftMm - tableWidthMm,
              top: topMarginForHeader, // This ensures new pages start below header (will be adjusted in didDrawPage)
            },
            theme: "plain", // Disable default striped row styling
            styles: {
              fontSize: 6, // Reduced font size for smaller text
              cellPadding: 2, // Reduced padding to match smaller font
              overflow: "linebreak",
              valign: "top",
              lineWidth: 0.1,
              lineColor: [200, 200, 200], // Light gray border color
            },
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left",
              fontSize: 6, // Match body font size
              lineColor: [0, 0, 0], // Black border to match black background (invisible)
              lineWidth: 0, // No border for header cells
            },
            bodyStyles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background for all rows
              lineColor: [200, 200, 200], // Light gray border for body cells
              lineWidth: 0.1,
            },
            pageBreak: "auto",
            rowPageBreak: "avoid", // Prevent row cutting - this is the key fix
            tableWidth: tableWidthMm,
            showHead: "everyPage", // Repeat headers on each page
            didDrawPage: (data) => {
              // didDrawPage is called for each page the table spans
              // Increment counter for each page (starts at 0, becomes 1 for first page, 2 for second, etc.)
              tablePageCounter++;

              // Draw heading on continuation pages (page 2, 3, etc. of the table)
              // First page heading is already drawn before autotable (lines 14661-14675)
              if (tablePageCounter > 1) {
                const headingText =
                  revisionTablePagesCount > 1
                    ? `${revisionTableName} (${tablePageCounter}/${revisionTablePagesCount})`
                    : revisionTableName;

                // Draw heading first (no top margin - position at top margin)
                // Apply -5px margin-top for continuation pages
                const headingY = topMarginForHeader - 10 * pixelToMm; // Position at header margin with -5px offset
                const headingX =
                  horizontalMargin +
                  (revisionTableMeta.leftPx || 0) * pixelToMm;

                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(8.5);
                pdf.setTextColor(13, 27, 42);
                pdf.text(headingText, headingX, headingY);

                // Adjust margin.top for continuation pages to position table below the heading
                // Autotable uses margin.top for new pages, so this ensures proper spacing
                data.settings.margin.top = startYForContinuationPages;
              }
            },
          });

          const revLastAt = pdf.lastAutoTable;
          if (
            revLastAt &&
            typeof revLastAt.finalY === "number" &&
            !Number.isNaN(revLastAt.finalY)
          ) {
            revisionAutotableFinalMm = revLastAt.finalY;
            revisionAutotableEndPdfPage = pdf.getCurrentPageInfo().pageNumber;
          }

          // After autotable renders, verify new pages don't overlap header
          // If autotable created new pages, they should respect margin.top
          const finalPageNumber = pdf.getNumberOfPages();
          if (finalPageNumber > initialPageNumber) {
            // Autotable created new pages - verify they start below header
            // The margin.top setting should have ensured proper spacing
          }
        }
      }

      // Render training log table with autotable to prevent row cutting
      if (trainingLogTableData && trainingLogTableMeta) {
        // Find the first page where the training log table appears
        const trainingLogTablePages = [];
        pageImages.forEach((pageImage, pageIdx) => {
          if (pageImage.tablesOnPage?.includes(trainingLogTableId)) {
            trainingLogTablePages.push(pageIdx);
          }
        });

        if (trainingLogTablePages.length > 0) {
          const firstTrainingLogPageIdx = trainingLogTablePages[0];
          const firstTrainingLogPage = pageImages[firstTrainingLogPageIdx];

          // Calculate where the table should start on the first page
          let tableStartY = headerHeight + contentTopPadding;
          if (firstTrainingLogPage && trainingLogTableMeta) {
            const pageStartPx = firstTrainingLogPage.pageStartPx || 0;
            const headerInsertPx = firstTrainingLogPage.headerInsertPx || 0;

            // Calculate table top position relative to page start
            const tableTopInPagePx = Math.max(
              0,
              trainingLogTableMeta.topPx - pageStartPx,
            );
            const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;

            // Convert to mm
            tableStartY =
              headerHeight +
              contentTopPadding +
              tableTopInPageCanvasPx * pixelToMm;
          }

          const trainingLogTargetPdfPage = firstTrainingLogPageIdx + 1;
          if (
            revisionAutotableFinalMm != null &&
            revisionAutotableEndPdfPage != null &&
            revisionAutotableEndPdfPage === trainingLogTargetPdfPage
          ) {
            tableStartY =
              revisionAutotableFinalMm + PDF_AUTOTABLE_GAP_AFTER_REVISION_MM;
          }

          // Mask canvas "Training log" h3 + table so autotable is the only title (same idea as revision table).
          const bandTopPxForTrainingLogMask =
            trainingLogTableMeta.headingElement &&
            trainingLogTableMeta.headerTopPx != null
              ? trainingLogTableMeta.headerTopPx
              : trainingLogTableMeta.topPx;

          trainingLogTablePages.forEach((pageIdx) => {
            pdf.setPage(pageIdx + 1);

            const pageImage = pageImages[pageIdx];
            const pageStartPx = pageImage.pageStartPx || 0;
            const headerInsertPx = pageImage.headerInsertPx || 0;
            const pageEndPx = pageStartPx + (pageImage.pageContentPx || 0);

            const visibleTopPx = Math.max(
              pageStartPx,
              bandTopPxForTrainingLogMask,
            );
            const bomGapCanvasPx = Math.max(
              1,
              Math.round(6 * domToCanvasScaleY),
            );
            let trainingMaskBottomPx = trainingLogTableMeta.bottomPx;
            if (
              bomTableMeta &&
              typeof bomTableMeta.topPx === "number" &&
              bomTableMeta.topPx > trainingLogTableMeta.topPx
            ) {
              trainingMaskBottomPx = Math.min(
                trainingMaskBottomPx,
                bomTableMeta.topPx - bomGapCanvasPx,
              );
            }
            const visibleBottomPx = Math.min(
              pageEndPx,
              trainingMaskBottomPx,
            );

            if (visibleBottomPx > visibleTopPx) {
              const tableTopInPagePx = visibleTopPx - pageStartPx;
              const tableHeightInPagePx = visibleBottomPx - visibleTopPx;

              const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;
              const extraMaskTopMm = 4;
              const extraMaskBottomMm = 6;
              const extraMaskSidesMm = 2;
              const maskTopMm =
                headerHeight +
                contentTopPadding +
                tableTopInPageCanvasPx * pixelToMm -
                extraMaskTopMm;
              const maskHeightMm =
                tableHeightInPagePx * pixelToMm +
                extraMaskTopMm +
                extraMaskBottomMm;
              const maskLeftMm =
                horizontalMargin +
                (trainingLogTableMeta.leftPx || 0) * pixelToMm -
                extraMaskSidesMm;
              const maskWidthMm =
                (trainingLogTableMeta.widthPx || imgWidth) * pixelToMm +
                extraMaskSidesMm * 2;

              pdf.setFillColor(255, 255, 255);
              pdf.rect(maskLeftMm, maskTopMm, maskWidthMm, maskHeightMm, "F");
            }
          });

          // Render the table with autotable starting from the first page
          pdf.setPage(firstTrainingLogPageIdx + 1);

          // Draw "Training Log" heading above the table
          const trainingLogTableName =
            tableIdToName.get(trainingLogTableId) || "Training Log";
          const trainingLogTablePagesCount = trainingLogTablePages.length;
          const trainingLogHeadingText =
            trainingLogTablePagesCount > 1
              ? `${trainingLogTableName} (1/${trainingLogTablePagesCount})`
              : trainingLogTableName;

          // Position heading above the table
          const headingY = tableStartY - effectiveHeadingToTableMm;
          const headingX =
            horizontalMargin + (trainingLogTableMeta.leftPx || 0) * pixelToMm;

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(13, 27, 42);
          pdf.text(trainingLogHeadingText, headingX, headingY);

          // Calculate table width
          const tableWidthMm = Math.min(
            (trainingLogTableMeta.widthPx || canvas.width) * pixelToMm,
            pdfWidth - horizontalMargin * 2,
          );

          // Calculate left margin
          const tableLeftMm =
            horizontalMargin + (trainingLogTableMeta.leftPx || 0) * pixelToMm;

          // Calculate top margin to avoid overlapping with page header on new pages
          const topMarginForHeader = headerHeight + contentTopPadding;

          const headingYPosition = topMarginForHeader;
          const headingHeight = 4;
          const spacingAfterHeading = 15;
          const startYForContinuationPages =
            headingYPosition + headingHeight + spacingAfterHeading;

          // Store the initial page number before rendering
          const initialPageNumber = pdf.getNumberOfPages();
          let tablePageCounter = 0;

          const trainingLogPdfTrainerSigCol = 2;
          const trainingLogPdfTraineeSigCol = 4;

          autoTable(pdf, {
            startY: tableStartY,
            head: [trainingLogTableData.headers],
            body: trainingLogTableData.rows,
            margin: {
              left: tableLeftMm,
              right: pdfWidth - tableLeftMm - tableWidthMm,
              top: topMarginForHeader, // This ensures new pages start below header
            },
            theme: "plain", // Disable default striped row styling
            styles: {
              fontSize: 6, // Reduced font size for smaller text
              cellPadding: 2, // Reduced padding to match smaller font
              overflow: "linebreak",
              valign: "top",
              lineWidth: 0.1,
              lineColor: [200, 200, 200], // Light gray border color
              minCellHeight: 8,
            },
            columnStyles: {
              [trainingLogPdfTrainerSigCol]: {
                minCellHeight: 8,
                cellPadding: 1,
              },
              [trainingLogPdfTraineeSigCol]: {
                minCellHeight: 8,
                cellPadding: 1,
              },
            },
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left",
              fontSize: 6, // Match body font size
              lineColor: [0, 0, 0], // Black border to match black background (invisible)
              lineWidth: 0, // No border for header cells
            },
            bodyStyles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background for all rows
              lineColor: [200, 200, 200], // Light gray border for body cells
              lineWidth: 0.1,
            },
            pageBreak: "auto",
            rowPageBreak: "avoid", // Prevent row cutting - this is the key fix
            tableWidth: tableWidthMm,
            showHead: "everyPage", // Repeat headers on each page
            didDrawCell: (data) => {
              try {
                if (data.section !== "body") return;
                const col = data.column.index;
                if (
                  col !== trainingLogPdfTrainerSigCol &&
                  col !== trainingLogPdfTraineeSigCol
                ) {
                  return;
                }
                const rowIdx =
                  typeof data.row?.index === "number" ? data.row.index : -1;
                if (
                  rowIdx < 0 ||
                  !Array.isArray(trainingLogsData) ||
                  !trainingLogsData[rowIdx]
                ) {
                  return;
                }
                const log = trainingLogsData[rowIdx];
                const sig =
                  col === trainingLogPdfTrainerSigCol
                    ? log.trainer_signature
                    : log.trainee_signature;
                const sc = getTrainingLogSignatureCell(sig);
                if (sc.type !== "img" || !sc.src) return;
                const pad = 0.5;
                const cw = data.cell.width - pad * 2;
                const ch = data.cell.height - pad * 2;
                // Keep signatures compact (mm); cap size and center in cell
                const maxSigW = Math.min(13, cw);
                const maxSigH = Math.min(4, ch);
                let iw = maxSigW;
                let ih = maxSigH;
                const srcLower = String(sc.src).toLowerCase();
                const fmt = srcLower.includes("jpeg") || srcLower.includes("jpg")
                  ? "JPEG"
                  : srcLower.includes("gif")
                    ? "GIF"
                    : srcLower.includes("webp")
                      ? "WEBP"
                      : "PNG";
                const imgX = data.cell.x + pad + (cw - iw) / 2;
                const imgY = data.cell.y + pad + (ch - ih) / 2;
                try {
                  pdf.addImage(sc.src, fmt, imgX, imgY, iw, ih);
                } catch (_) {
                  try {
                    pdf.addImage(sc.src, "PNG", imgX, imgY, iw, ih);
                  } catch (__) {}
                }
              } catch (_) {}
            },
            didDrawPage: (data) => {
              tablePageCounter++;
              if (tablePageCounter > 1) {
                const headingText =
                  trainingLogTablePagesCount > 1
                    ? trainingLogTableName +
                      " (" +
                      tablePageCounter +
                      "/" +
                      trainingLogTablePagesCount +
                      ")"
                    : trainingLogTableName;

                const headingY = topMarginForHeader - 10 * pixelToMm;
                const headingX =
                  horizontalMargin +
                  (trainingLogTableMeta.leftPx || 0) * pixelToMm;

                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(8.5);
                pdf.setTextColor(13, 27, 42);
                pdf.text(headingText, headingX, headingY);

                data.settings.margin.top = startYForContinuationPages;
              }
            },
          });

          const trainLastAt = pdf.lastAutoTable;
          if (
            trainLastAt &&
            typeof trainLastAt.finalY === "number" &&
            !Number.isNaN(trainLastAt.finalY)
          ) {
            trainingAutotableFinalMm = trainLastAt.finalY;
            trainingAutotableEndPdfPage = pdf.getCurrentPageInfo().pageNumber;
          }

          // After autotable renders, verify new pages don't overlap header
          // If autotable created new pages, they should respect margin.top
          const finalPageNumber = pdf.getNumberOfPages();
          if (finalPageNumber > initialPageNumber) {
            // Autotable created new pages - verify they start below header
            // The margin.top setting should have ensured proper spacing
          }
        }
      }

      // Render BOM table with autotable to prevent row cutting
      if (bomTableAutotableData && bomTableMeta) {
        // Find the first page where the BOM table should be rendered.
        // NOTE: For an "empty" BOM, the table can collapse (tbody has no rows),
        // so it may not get included in `pageImage.tablesOnPage`. We fall back to
        // page overlap logic using `bomTableMeta` to still render the header row.
        const bomTablePages = [];
        const bomTopPx = bomTableMeta?.topPx;
        const bomBottomPx = bomTableMeta?.bottomPx;

        const EPS_BOM_PX = 1;

        pageImages.forEach((pageImage, pageIdx) => {
          const pageStartPx = pageImage.pageStartPx || 0;
          const pageContentPx = pageImage.pageContentPx || 0;
          const pageEndPx = pageStartPx + pageContentPx;

          const overlapsMeta =
            typeof bomTopPx === "number" &&
            typeof bomBottomPx === "number" &&
            bomBottomPx >= pageStartPx - EPS_BOM_PX &&
            bomTopPx <= pageEndPx + EPS_BOM_PX;

          // Empty/collapsed tables can land exactly on a slice boundary, which fails
          // the strict `bottomPx > pageStartPx` check.
          const collapsedWithinPage =
            typeof bomTopPx === "number" &&
            typeof bomBottomPx === "number" &&
            Math.abs(bomBottomPx - bomTopPx) < 1 &&
            bomTopPx >= pageStartPx - EPS_BOM_PX &&
            bomTopPx <= pageEndPx + EPS_BOM_PX;

          if (overlapsMeta || collapsedWithinPage) {
            bomTablePages.push(pageIdx);
            return;
          }

          // If we can't compute overlap reliably, fall back to the existing tracking.
          if (
            bomTableId != null &&
            pageImage.tablesOnPage?.includes(bomTableId) === true
          ) {
            bomTablePages.push(pageIdx);
          }
        });

        // Safety net: if overlap detection fails (can happen with very thin/empty
        // tables on slice boundaries), still render on the closest page so the
        // BOM header isn't missing in the PDF.
        if (bomTablePages.length === 0 && typeof bomTopPx === "number") {
          let bestIdx = 0;
          let bestDist = Number.POSITIVE_INFINITY;
          pageImages.forEach((pageImage, idx) => {
            const pageStartPx = pageImage.pageStartPx || 0;
            const pageContentPx = pageImage.pageContentPx || 0;
            const pageEndPx = pageStartPx + pageContentPx;

            const dist =
              bomTopPx < pageStartPx
                ? pageStartPx - bomTopPx
                : bomTopPx > pageEndPx
                  ? bomTopPx - pageEndPx
                  : 0;

            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          });
          bomTablePages.push(bestIdx);
        }

        if (bomTablePages.length > 0) {
          const firstBomPageIdx = bomTablePages[0];
          const firstBomPage = pageImages[firstBomPageIdx];

          // Calculate where the table should start on the first page
          let tableStartY = headerHeight + contentTopPadding;
          if (firstBomPage && bomTableMeta) {
            const pageStartPx = firstBomPage.pageStartPx || 0;
            const headerInsertPx = firstBomPage.headerInsertPx || 0;

            // Calculate table top position relative to page start
            const tableTopInPagePx = Math.max(
              0,
              bomTableMeta.topPx - pageStartPx,
            );
            const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;

            // Convert to mm
            tableStartY =
              headerHeight +
              contentTopPadding +
              tableTopInPageCanvasPx * pixelToMm;
          }

          const bomTargetPdfPage = firstBomPageIdx + 1;
          if (
            trainingAutotableFinalMm != null &&
            trainingAutotableEndPdfPage != null &&
            trainingAutotableEndPdfPage === bomTargetPdfPage
          ) {
            tableStartY =
              trainingAutotableFinalMm + PDF_AUTOTABLE_GAP_AFTER_TRAINING_MM;
          }

          // Mask the BOM table area on all pages where it appears
          bomTablePages.forEach((pageIdx) => {
            pdf.setPage(pageIdx + 1);

            const pageImage = pageImages[pageIdx];
            const pageStartPx = pageImage.pageStartPx || 0;
            const headerInsertPx = pageImage.headerInsertPx || 0;

            // Calculate heading top position if heading exists
            let headingTopPx = null;
            let headingHeightPx = 0;
            if (
              bomTableMeta.headingElement &&
              bomTableMeta.headingTopDom !== null &&
              bomTableMeta.headingTopDom !== undefined
            ) {
              headingTopPx = Math.round(
                bomTableMeta.headingTopDom * domToCanvasScaleY,
              );
              if (
                bomTableMeta.headingHeightDom !== null &&
                bomTableMeta.headingHeightDom !== undefined
              ) {
                headingHeightPx = Math.round(
                  bomTableMeta.headingHeightDom * domToCanvasScaleY,
                );
              }
            }

            // Removed BOM heading masking - no longer masking the heading area

            // Mask 2: Table area (header row + body)
            const tableHeaderTopPx =
              bomTableMeta.headerTopPx ?? bomTableMeta.topPx;
            const tableTopInPagePx = Math.max(
              0,
              tableHeaderTopPx - pageStartPx,
            );
            const tableBottomInPagePx = Math.min(
              pageImage.pageContentPx || 0,
              bomTableMeta.bottomPx - pageStartPx,
            );

            if (tableBottomInPagePx > tableTopInPagePx) {
              const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;
              const tableHeightInPagePx =
                tableBottomInPagePx - tableTopInPagePx;

              // Convert to mm
              // Extend mask area to cover any borders from previous canvas rendering
              // On first page: reduce mask extension above table if Section header is close to prevent it from being hidden behind the BOM table
              let extraMaskTopMm = 3; // Small margin above
              if (
                pageIdx === firstBomPageIdx &&
                sectionHeadingPairsPx &&
                sectionHeadingPairsPx.length > 0
              ) {
                const pageEndPx = pageStartPx + (pageImage.pageContentPx || 0);
                const sectionHeaderAboveTable = sectionHeadingPairsPx.find(
                  (pair) => {
                    const headerAboveTable =
                      pair.headingBottomPx < bomTableMeta.topPx;
                    const headerOnPage =
                      pair.headingTopPx >= pageStartPx &&
                      pair.headingBottomPx < pageEndPx;
                    const gapPx = bomTableMeta.topPx - pair.headingBottomPx;
                    const gapMm = gapPx * pixelToMm;
                    return headerAboveTable && headerOnPage && gapMm < 10; // Section header within 10mm of table top
                  },
                );
                if (sectionHeaderAboveTable) {
                  extraMaskTopMm = 0; // Do not extend mask above table to avoid covering Section header
                }
              }
              const extraMaskBottomMm = 2; // Extra space below to cover borders
              const extraMaskSidesMm = 2; // Extra space on left and right to cover vertical black borders
              let maskTopMm =
                headerHeight +
                contentTopPadding +
                tableTopInPageCanvasPx * pixelToMm -
                extraMaskTopMm;
              let maskHeightMm =
                tableHeightInPagePx * pixelToMm +
                extraMaskTopMm +
                extraMaskBottomMm;
              // Canvas BOM top can map slightly above the vector Training Log end on the same page.
              // A white rect starting too high paints over the Training Log autotable's last row border.
              const maskBottomMm = maskTopMm + maskHeightMm;
              if (
                trainingAutotableFinalMm != null &&
                trainingAutotableEndPdfPage === pageIdx + 1
              ) {
                const minTopAfterTrainingMm = trainingAutotableFinalMm + 0.35;
                if (maskTopMm < minTopAfterTrainingMm) {
                  maskTopMm = minTopAfterTrainingMm;
                }
              }
              maskHeightMm = Math.max(0, maskBottomMm - maskTopMm);
              const maskLeftMm =
                horizontalMargin +
                (bomTableMeta.leftPx || 0) * pixelToMm -
                extraMaskSidesMm;
              const maskWidthMm =
                (bomTableMeta.widthPx || imgWidth) * pixelToMm +
                extraMaskSidesMm * 2;

              // Mask the table area with white rectangle
              pdf.setFillColor(255, 255, 255);
              pdf.rect(maskLeftMm, maskTopMm, maskWidthMm, maskHeightMm, "F");
            }
          });

          // Render the table with autotable starting from the first page
          pdf.setPage(firstBomPageIdx + 1);

          // Draw BOM heading above the table
          const bomTableName = tableIdToName.get(bomTableId) || "BOM";
          const bomTablePagesCount = bomTablePages.length;
          // BOM heading should not show page counters in PDF exports.
          // (e.g. no "BOM (1/2)" even if exporter splits the table into pages)
          const bomHeadingText = bomTableName;

          // Position heading above the table
          const headingY = tableStartY - effectiveHeadingToTableMm;
          const headingX =
            horizontalMargin + (bomTableMeta.leftPx || 0) * pixelToMm;

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(13, 27, 42);
          pdf.text(bomHeadingText, headingX, headingY);

          // Calculate table width
          const tableWidthMm = Math.min(
            (bomTableMeta.widthPx || canvas.width) * pixelToMm,
            pdfWidth - horizontalMargin * 2,
          );

          // Calculate left margin
          const tableLeftMm =
            horizontalMargin + (bomTableMeta.leftPx || 0) * pixelToMm;

          // Calculate top margin to avoid overlapping with page header on new pages
          const topMarginForHeader = headerHeight + contentTopPadding;

          // Calculate startY for continuation pages (accounts for heading space)
          // Heading is at topMarginForHeader (no extra offset)
          // We need: heading Y + heading height + spacing to ensure table doesn't overlap heading
          const headingYPosition = topMarginForHeader;
          const headingHeight = 4; // Height of 7pt font text including line spacing
          const spacingAfterHeading = 15; // Margin top for table - spacing between heading and table
          const startYForContinuationPages =
            headingYPosition + headingHeight + spacingAfterHeading;

          // Store the initial page number before rendering
          const initialPageNumber = pdf.getNumberOfPages();
          let tablePageCounter = 0; // Track which page of the table we're on (1-based)

          autoTable(pdf, {
            startY: tableStartY,
            head: [bomTableAutotableData.headers],
            body: bomTableAutotableData.rows,
            margin: {
              left: tableLeftMm,
              right: pdfWidth - tableLeftMm - tableWidthMm,
              top: topMarginForHeader, // This ensures new pages start below header (will be adjusted in didDrawPage)
            },
            theme: "plain", // Disable default striped row styling
            styles: {
              fontSize: 6, // Reduced font size for smaller text
              cellPadding: 2, // Reduced padding to match smaller font
              overflow: "linebreak",
              valign: "top",
              lineWidth: 0.1,
              lineColor: [200, 200, 200], // Light gray border color
            },
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left",
              fontSize: 6, // Match body font size
              lineColor: [0, 0, 0], // Black border to match black background (invisible)
              lineWidth: 0, // No border for header cells
            },
            bodyStyles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background for all rows
              lineColor: [200, 200, 200], // Light gray border for body cells
              lineWidth: 0.1,
            },
            pageBreak: "auto",
            rowPageBreak: "avoid", // Prevent row cutting - this is the key fix
            tableWidth: tableWidthMm,
            showHead: "everyPage", // Repeat headers on each page
            didDrawPage: (data) => {
              // didDrawPage is called for each page the table spans
              // Increment counter for each page (starts at 0, becomes 1 for first page, 2 for second, etc.)
              tablePageCounter++;

              // Draw heading on continuation pages (page 2, 3, etc. of the table)
              // First page heading is already drawn before autotable
              if (tablePageCounter > 1) {
                const headingText = bomTableName;

                // Draw heading first (no top margin - position at top margin)
                // Apply -10px margin-top for continuation pages
                const headingY = topMarginForHeader - 10 * pixelToMm; // Position at header margin with -10px offset
                const headingX =
                  horizontalMargin + (bomTableMeta.leftPx || 0) * pixelToMm;

                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(8.5);
                pdf.setTextColor(13, 27, 42);
                pdf.text(headingText, headingX, headingY);

                // Adjust margin.top for continuation pages to position table below the heading
                // Autotable uses margin.top for new pages, so this ensures proper spacing
                data.settings.margin.top = startYForContinuationPages;
              }
            },
          });

          // After autotable renders, verify new pages don't overlap header
          // If autotable created new pages, they should respect margin.top
          const finalPageNumber = pdf.getNumberOfPages();
          if (finalPageNumber > initialPageNumber) {
            // Autotable created new pages - verify they start below header
            // The margin.top setting should have ensured proper spacing
          }
        }
      }

      // Render Item Required tables with autotable to prevent row cutting
      itemRequiredTablesData.forEach((itemRequiredTableData) => {
        const itemRequiredTableMeta = itemRequiredTableData.meta;
        const itemRequiredTableId = itemRequiredTableData.tableId;

        // Find all pages where this Item Required table appears
        const itemRequiredTablePages = [];
        pageImages.forEach((pageImage, pageIdx) => {
          if (pageImage.tablesOnPage?.includes(itemRequiredTableId)) {
            itemRequiredTablePages.push(pageIdx);
          }
        });

        if (itemRequiredTablePages.length > 0) {
          const firstItemRequiredPageIdx = itemRequiredTablePages[0];
          const firstItemRequiredPage = pageImages[firstItemRequiredPageIdx];

          // Calculate where the table should start on the first page
          let tableStartY = headerHeight + contentTopPadding;
          if (firstItemRequiredPage && itemRequiredTableMeta) {
            const pageStartPx = firstItemRequiredPage.pageStartPx || 0;
            const headerInsertPx = firstItemRequiredPage.headerInsertPx || 0;

            // Calculate table top position relative to page start (use original table position)
            const tableTopInPagePx = Math.max(
              0,
              itemRequiredTableMeta.topPx - pageStartPx,
            );
            const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;

            // Convert to mm - use original table position
            tableStartY =
              headerHeight +
              contentTopPadding +
              tableTopInPageCanvasPx * pixelToMm;
          }

          // Mask the Item Required table area and heading on all pages where it appears
          itemRequiredTablePages.forEach((pageIdx) => {
            pdf.setPage(pageIdx + 1);

            const pageImage = pageImages[pageIdx];
            const pageStartPx = pageImage.pageStartPx || 0;
            const headerInsertPx = pageImage.headerInsertPx || 0;

            // Calculate table bounds on this page
            const tableTopInPagePx = Math.max(
              0,
              itemRequiredTableMeta.topPx - pageStartPx,
            );
            const tableBottomInPagePx = Math.min(
              pageImage.pageContentPx || 0,
              itemRequiredTableMeta.bottomPx - pageStartPx,
            );

            // Note: Heading masking is done at pageCanvas level, so we only need to mask the table area here
            // Mask table area
            if (tableBottomInPagePx > tableTopInPagePx) {
              const tableTopInPageCanvasPx = headerInsertPx + tableTopInPagePx;
              const tableHeightInPagePx =
                tableBottomInPagePx - tableTopInPagePx;

              // Convert to mm
              const extraMaskTopMm = 4;
              // Keep ZIP spacing modest here; large mask bottoms can clip next content.
              const extraMaskBottomMm = isZipExport ? 4 : 2;
              const extraMaskSidesMm = 2;
              const maskTopMm =
                headerHeight +
                contentTopPadding +
                tableTopInPageCanvasPx * pixelToMm -
                extraMaskTopMm;
              const maskHeightMm =
                tableHeightInPagePx * pixelToMm +
                extraMaskTopMm +
                extraMaskBottomMm;
              const maskLeftMm =
                horizontalMargin +
                (itemRequiredTableMeta.leftPx || 0) * pixelToMm -
                extraMaskSidesMm;
              const maskWidthMm =
                (itemRequiredTableMeta.widthPx || imgWidth) * pixelToMm +
                extraMaskSidesMm * 2;

              // Mask the table area with white rectangle
              pdf.setFillColor(255, 255, 255);
              pdf.rect(maskLeftMm, maskTopMm, maskWidthMm, maskHeightMm, "F");
            }
          });

          // Render the table with autotable starting from the first page
          pdf.setPage(firstItemRequiredPageIdx + 1);

          // Draw "Item Required" heading above the table (split like BOM: "Item Required (1/2)", "(2/2)", etc.)
          const itemRequiredTableName = itemRequiredTableData.tableName;
          // Use max of canvas pages and estimated pages from row count - autotable may split differently than canvas
          const rowCount = itemRequiredTableData.isEmptyState
            ? 1
            : itemRequiredTableData.rows?.length || 1;
          const estimatedRowsPerPage = 45; // ~45 rows per page with fontSize 6, cellPadding 2
          const estimatedPagesFromRows = Math.max(
            1,
            Math.ceil(rowCount / estimatedRowsPerPage),
          );
          const itemRequiredTablePagesCount = Math.max(
            itemRequiredTablePages.length,
            estimatedPagesFromRows,
          );
          const itemRequiredHeadingText =
            itemRequiredTablePagesCount > 1
              ? `${itemRequiredTableName} (1/${itemRequiredTablePagesCount})`
              : itemRequiredTableName;

          // Position heading above the table
          const headingY = tableStartY - effectiveHeadingToTableMm;
          const headingX =
            horizontalMargin + (itemRequiredTableMeta.leftPx || 0) * pixelToMm;

          // Calculate heading dimensions for masking
          const headingHeightMm = 8 * 0.352778; // Convert 8pt font to mm (approximately 2.82mm)
          const maskAboveHeadingMm = 2 * pixelToMm; // 2px to mm conversion
          const headingWidthMm =
            (itemRequiredTableMeta.widthPx || canvas.width) * pixelToMm;

          // Mask area above the heading to prevent other headings from showing through
          pdf.setFillColor(255, 255, 255);
          pdf.rect(
            headingX,
            headingY - headingHeightMm - maskAboveHeadingMm,
            headingWidthMm,
            headingHeightMm + maskAboveHeadingMm,
            "F",
          );

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8);
          pdf.setTextColor(13, 27, 42);
          pdf.text(itemRequiredHeadingText, headingX, headingY);

          // Calculate table width
          const tableWidthMm = Math.min(
            (itemRequiredTableMeta.widthPx || canvas.width) * pixelToMm,
            pdfWidth - horizontalMargin * 2,
          );

          // Calculate left margin
          const tableLeftMm =
            horizontalMargin + (itemRequiredTableMeta.leftPx || 0) * pixelToMm;

          // Calculate top margin to avoid overlapping with page header on new pages
          const topMarginForHeader = headerHeight + contentTopPadding;

          // Calculate startY for continuation pages (accounts for heading space)
          const headingYPosition = topMarginForHeader;
          const headingHeight = 4; // Height of 7pt font text including line spacing
          const spacingAfterHeading = 15; // Margin top for table - spacing between heading and table
          const startYForContinuationPages =
            headingYPosition + headingHeight + spacingAfterHeading;

          // Store the initial page number before rendering
          const initialPageNumber = pdf.getNumberOfPages();
          let tablePageCounter = 0; // Track which page of the table we're on (1-based)

          // For empty state: use single cell spanning all columns, centered, no column dividers
          const tableBody = itemRequiredTableData.isEmptyState
            ? [
                [
                  {
                    content: "No parts to display.",
                    colSpan: itemRequiredTableData.headers.length,
                    styles: { halign: "center", cellPadding: 2 },
                  },
                ],
              ]
            : itemRequiredTableData.rows;

          autoTable(pdf, {
            startY: tableStartY,
            head: [itemRequiredTableData.headers],
            body: tableBody,
            margin: {
              left: tableLeftMm,
              right: pdfWidth - tableLeftMm - tableWidthMm,
              top: topMarginForHeader, // This ensures new pages start below header (will be adjusted in didDrawPage)
            },
            theme: "plain", // Disable default striped row styling
            styles: {
              fontSize: 6, // Reduced font size for smaller text
              cellPadding: 2, // Reduced padding to match smaller font
              overflow: "linebreak",
              valign: "top",
              lineWidth: 0.1,
              lineColor: [200, 200, 200], // Light gray border color
            },
            headStyles: {
              fillColor: [0, 0, 0],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              halign: "left",
              fontSize: 6, // Match body font size
              lineColor: [0, 0, 0], // Black border to match black background (invisible)
              lineWidth: 0, // No border for header cells
            },
            bodyStyles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background for all rows
              lineColor: [200, 200, 200], // Light gray border for body cells
              lineWidth: 0.1,
            },
            pageBreak: "auto",
            rowPageBreak: "avoid", // Prevent row cutting - this is the key fix
            tableWidth: tableWidthMm,
            showHead: "everyPage", // Repeat headers on each page
            didDrawPage: (data) => {
              // didDrawPage is called for each page the table spans
              // Increment counter for each page (starts at 0, becomes 1 for first page, 2 for second, etc.)
              tablePageCounter++;

              // Draw heading on continuation pages (page 2, 3, etc. of the table)
              // First page heading is already drawn before autotable
              if (tablePageCounter > 1) {
                const headingText =
                  itemRequiredTablePagesCount > 1
                    ? `${itemRequiredTableName} (${tablePageCounter}/${itemRequiredTablePagesCount})`
                    : itemRequiredTableName;

                // Draw heading first (no top margin - position at top margin)
                // Apply -10px margin-top for continuation pages
                const headingY = topMarginForHeader - 10 * pixelToMm; // Position at header margin with -10px offset
                const headingX =
                  horizontalMargin +
                  (itemRequiredTableMeta.leftPx || 0) * pixelToMm;

                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(7);
                pdf.setTextColor(13, 27, 42);
                pdf.text(headingText, headingX, headingY);

                // Adjust margin.top for continuation pages to position table below the heading
                // Autotable uses margin.top for new pages, so this ensures proper spacing
                data.settings.margin.top = startYForContinuationPages;
              }
            },
          });

          // After autotable renders, correct headings with actual page count (tablePageCounter)
          const actualPagesUsed = tablePageCounter;
          if (actualPagesUsed > 1) {
            const correctFirstHeading = `${itemRequiredTableName} (1/${actualPagesUsed})`;
            pdf.setPage(firstItemRequiredPageIdx + 1);
            pdf.setFillColor(255, 255, 255);
            pdf.rect(
              headingX,
              headingY - headingHeightMm - maskAboveHeadingMm,
              headingWidthMm,
              headingHeightMm + maskAboveHeadingMm,
              "F",
            );
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(8);
            pdf.setTextColor(13, 27, 42);
            pdf.text(correctFirstHeading, headingX, headingY);
            // Redraw continuation page headings with correct total (pages are consecutive from first)
            for (let p = 2; p <= actualPagesUsed; p++) {
              const continuationPageNum = firstItemRequiredPageIdx + p;
              if (continuationPageNum <= pdf.getNumberOfPages()) {
                pdf.setPage(continuationPageNum);
                const correctContHeading = `${itemRequiredTableName} (${p}/${actualPagesUsed})`;
                const contHeadingY = topMarginForHeader - 10 * pixelToMm;
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(7);
                pdf.setTextColor(13, 27, 42);
                pdf.text(correctContHeading, headingX, contHeadingY);
              }
            }
          }
        }
      });

      const typeStr = String(effectiveType || "").trim();
      const partNumberStr = String(effectivePartNumber || "").trim();
      let descriptionText = "";
      if (effectiveDescription) {
        const stripped =
          stripHtmlPreserveFormatting(effectiveDescription).trim();
        descriptionText = stripped || String(effectiveDescription).trim();
      }
      const titlePrefix =
        typeStr && partNumberStr
          ? `${partNumberStr}`
          : typeStr || partNumberStr;
      const headerTitle = descriptionText
        ? `${titlePrefix}, ${descriptionText}`
        : titlePrefix;
      const cleanedTitlePrefix = (titlePrefix || "")
        .replace(/,\s*$/, "")
        .trim();
      const cleanedDescriptionForBlue = descriptionText
        ? descriptionText.replace(/^,\s*/, "").trim()
        : "";
      const blueHeaderTitle = cleanedDescriptionForBlue
        ? `${cleanedTitlePrefix} ${cleanedDescriptionForBlue}`
        : cleanedTitlePrefix;
      const headerSubtitle = effectiveMyRevision
        ? `Revision ${effectiveMyRevision}`
        : "";
      const docInfoLeft = headerSubtitle
        ? blueHeaderTitle
          ? `${blueHeaderTitle} | ${headerSubtitle}`
          : headerSubtitle
        : blueHeaderTitle;
      const footerText =
        appSetup?.footer?.trim() ||
        (companyName
          ? `Proprietary information of RUSHABH Instruments, LLC (RI). Do not duplicate or reproduce in any form without written permission of RI.`
          : "Proprietary information. Do not duplicate or reproduce without written permission.");
      const totalPages = pdf.getNumberOfPages();

      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        pdf.setPage(pageNumber);

        const pageIndexZeroBased = pageNumber - 1;

        // Skip drawing Revision Table heading here - it's now drawn by autotable rendering above
        // The old heading drawing code is skipped since we're using autotable which handles the heading
        // if (
        //   revisionTableId != null &&
        //   revisionTablePageIndicators.has(pageIndexZeroBased)
        // ) {
        //   // Skip - heading is now drawn by autotable
        // }

        // Skip BOM table title and page indicators - they're now drawn by autotable
        // (similar to how Revision Table heading is handled)
        // if (bomTableId != null) {
        //   // Skip - heading is now drawn by autotable
        // }

        const logoY = 12;
        const logoHeight = 14;
        const logoWidth = 32;
        if (companyLogoUrl) {
          try {
            let imageFormat = "PNG";
            let logoToUse = companyLogoUrl;

            if (companyLogoUrl.startsWith("data:image/")) {
              const formatMatch = companyLogoUrl.match(/data:image\/([^;]+)/);
              if (formatMatch) {
                const format = formatMatch[1].toUpperCase();
                if (["PNG", "JPEG", "JPG", "WEBP"].includes(format)) {
                  imageFormat = format === "JPG" ? "JPEG" : format;
                } else if (format === "SVG" || format === "SVG+XML") {
                  const pngDataUrl = await convertSvgToPng(companyLogoUrl);
                  if (pngDataUrl) {
                    logoToUse = pngDataUrl;
                    imageFormat = "PNG";
                  } else {
                    logoToUse = null;
                  }
                }
              }
            }

            if (logoToUse) {
              pdf.addImage(
                logoToUse,
                imageFormat,
                horizontalMargin,
                logoY,
                logoWidth,
                logoHeight,
              );
            }
          } catch (err) {}
        } else {
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.setTextColor(17, 17, 17);
        if (companyName) {
          pdf.text(companyName, pdfWidth - horizontalMargin, logoY + 9, {
            align: "right",
          });
        }

        if (companyAddress || companyPhone) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(17, 17, 17);

          const textY = logoY + 14;
          let currentX = pdfWidth - horizontalMargin;

          if (companyPhone) {
            const phoneIconPngDataUrl = await getPhoneIconPngDataUrl();
            const iconSize = 2.3;
            const iconSpacing = 2;
            const fontSize = 8;
            const iconYOffset = 0.4;
            const phoneTextWidth = pdf.getTextWidth(companyPhone);
            const iconX = currentX - phoneTextWidth - iconSize - iconSpacing;

            if (phoneIconPngDataUrl) {
              try {
                pdf.addImage(
                  phoneIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }

            pdf.text(companyPhone, currentX, textY, { align: "right" });
            currentX = iconX - 3;
          }

          if (companyAddress && companyPhone) {
            const separator = "      ";
            const separatorWidth = pdf.getTextWidth(separator);
            pdf.text(separator, currentX, textY, { align: "right" });
            currentX = currentX - separatorWidth;
          }

          if (companyAddress) {
            const distanceIconPngDataUrl = await getDistanceIconPngDataUrl();
            const iconSize = 2.3;
            const iconSpacing = 2;
            const fontSize = 8;
            const iconYOffset = 0.4;
            const addressTextWidth = pdf.getTextWidth(companyAddress);
            const iconX = currentX - addressTextWidth - iconSize - iconSpacing;

            if (distanceIconPngDataUrl) {
              try {
                pdf.addImage(
                  distanceIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }

            pdf.text(companyAddress, currentX, textY, { align: "right" });
          }

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(16);
          pdf.setTextColor(17, 17, 17);
        }

        const dividerY = headerHeight - 14;
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          dividerY,
          pdfWidth - horizontalMargin,
          dividerY,
        );

        const infoY = headerHeight - 8;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(26, 95, 180);
        const infoColumnWidth = (pdfWidth - horizontalMargin * 2) / 2;
        if (docInfoLeft) {
          pdf.text(docInfoLeft, horizontalMargin, infoY, {
            maxWidth: infoColumnWidth - 4,
          });
        }
        const rightColumnSegments = [];
        if (releaseInfo) {
          rightColumnSegments.push(splitReleaseInfoLines(releaseInfo));
        }
        if (exportInfo) {
          rightColumnSegments.push(splitExportInfoLines(exportInfo));
        }
        if (rightColumnSegments.length) {
          const separatorHeight = 3.5;
          const separatorGap = 3;
          let currentX = pdfWidth - horizontalMargin;

          for (let idx = rightColumnSegments.length - 1; idx >= 0; idx -= 1) {
            const segmentLines = rightColumnSegments[idx].length
              ? rightColumnSegments[idx]
              : [""];
            const lineHeight = 4;
            const maxLineWidth = Math.max(
              ...segmentLines.map((line) => pdf.getTextWidth(line)),
            );
            const startY = infoY - ((segmentLines.length - 1) * lineHeight) / 2;
            segmentLines.forEach((line, lineIdx) => {
              const lineY = startY + lineIdx * lineHeight;
              pdf.text(line, currentX, lineY, { align: "right" });
            });
            currentX -= maxLineWidth;

            if (idx > 0) {
              currentX -= separatorGap;
              pdf.setDrawColor(209, 213, 219);
              pdf.setLineWidth(0.4);
              pdf.line(
                currentX,
                infoY - separatorHeight,
                currentX,
                infoY + separatorHeight,
              );
              currentX -= separatorGap;
            }
          }
        }
        const footerY = pdfHeight - 4;

        const footerBorderY = footerY - 4;
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          footerBorderY,
          pdfWidth - horizontalMargin,
          footerBorderY,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(102, 102, 102);

        const pageNumberText = `Page ${pageNumber}/${totalPages}`;
        const pageNumberWidth = pdf.getTextWidth(pageNumberText);
        const separatorGap = 3;
        const separatorHeight = 3;
        const separatorX =
          pdfWidth - horizontalMargin - pageNumberWidth - separatorGap;

        const maxFooterWidth = separatorX - horizontalMargin - separatorGap;
        const lineHeight = 3.5;
        if (footerText) {
          const footerLines = pdf.splitTextToSize(footerText, maxFooterWidth);

          const textStartY = footerBorderY + 2.6;
          footerLines.forEach((line, index) => {
            const lineY = textStartY + index * lineHeight;
            pdf.text(line, horizontalMargin, lineY);
          });
        }

        const separatorTopMargin = 1;
        pdf.setDrawColor(209, 213, 219);
        pdf.setLineWidth(0.3);
        pdf.line(
          separatorX,
          footerBorderY + separatorTopMargin,
          separatorX,
          footerBorderY + separatorTopMargin + separatorHeight * 2,
        );
        const pageNumberY = footerText
          ? footerBorderY + separatorTopMargin + 1.5
          : footerY;
        pdf.text(pageNumberText, pdfWidth - horizontalMargin, pageNumberY, {
          align: "right",
        });
      }

      const fileName = isModulePreview
        ? `${effectiveType || "Module"}_${effectivePartNumber || ""}.pdf`.replace(
            /\s+/g,
            "_",
          )
        : `${effectiveType || "Document"}_${effectivePartNumber || ""}_Rev_${
            effectiveMyRevision || ""
          }.pdf`.replace(/\s+/g, "_");
      if (options?.returnBlob) {
        return pdf.output("blob");
      }
      pdf.save(fileName);
    } catch (e) {
      console.error("[PDF Export] Error:", e);
      try {
        container.style.transform = pdfExportInitialTransform;
      } catch (_) {}
    }
  };

  const handleExportPdfRef = useRef(handleExportPDF);
  handleExportPdfRef.current = handleExportPDF;

  useEffect(() => {
    if (!renderPdfEngineOnly) return;
    registerDocumentPdfExporter(async (opts) => {
      const exportRoot = zipExportRootRef.current;
      if (!exportRoot) {
        throw new Error("Export root not ready");
      }
      const prev = contentRef?.current;
      if (contentRef) contentRef.current = exportRoot;
      try {
        return await handleExportPdfRef.current({
          ...opts,
          rootElement: exportRoot,
        });
      } finally {
        if (contentRef) contentRef.current = prev;
      }
    });
    return () => registerDocumentPdfExporter(null);
  }, [renderPdfEngineOnly, contentRef]);

  useEffect(() => {
    if (pdfEngineZipPayload != null) {
      setZipExportView(pdfEngineZipPayload);
    }
  }, [pdfEngineZipPayload]);

  const handleExportMHTML = async () => {
    if (!contentRef || !contentRef.current) {
      return;
    }

    const container = contentRef.current;
    const mhtmlExportInitialTransform = container.style.transform;

    try {
      await waitForContentToRender();
      container.style.transform = "scale(1)";

      const titleText = isModulePreview
        ? `${type || "Module"} ${partNumber || ""}`.trim()
        : `${type || "Document"} ${partNumber || ""} Rev ${
            myRevision || ""
          }`.trim();

      const clone = container.cloneNode(true);

      try {
        const liveCanvases = Array.from(container.querySelectorAll("canvas"));
        const clonedCanvases = Array.from(clone.querySelectorAll("canvas"));
        for (let i = 0; i < clonedCanvases.length; i++) {
          const live = liveCanvases[i];
          const cloned = clonedCanvases[i];
          if (!live || !cloned) continue;
          let dataURL = "";
          try {
            dataURL = live.toDataURL("image/png");
          } catch (_) {
            const snap = await html2canvas(live, {
              useCORS: true,
              crossOrigin: "anonymous",
              allowTaint: false,
              imageTimeout: 0,
              backgroundColor: "#fff",
              scale: 2,
              logging: false,
            });
            dataURL = snap.toDataURL("image/png");
          }
          const img = document.createElement("img");
          img.src = dataURL;
          const rect = live.getBoundingClientRect();
          img.width = Math.max(1, Math.round(rect.width));
          img.height = Math.max(1, Math.round(rect.height));
          img.style.width = rect.width + "px";
          img.style.height = rect.height + "px";
          cloned.replaceWith(img);
        }
      } catch (_) {}

      // Same caption layout as PDF export: below the image, not beside/overlaid (Lexical + inlined CSS alone keep editor layout).
      const rootStyle = clone.style;
      const savedRootLayout = {
        position: rootStyle.position,
        left: rootStyle.left,
        top: rootStyle.top,
        width: rootStyle.width,
      };
      try {
        document.body.appendChild(clone);
        rootStyle.position = "fixed";
        rootStyle.left = "-10000px";
        rootStyle.top = "0";
        rootStyle.width = "1000px";
        clone.querySelectorAll(".inline-editor-image").forEach((el) => {
          el.style.setProperty("display", "inline-block", "important");
          el.style.setProperty("vertical-align", "top", "important");
          el.style.setProperty("float", "none", "important");
          el.style.setProperty("clear", "both", "important");
          el.style.setProperty("max-width", "100%", "important");
          el.style.setProperty("width", "max-content", "important");
          if (
            el.classList.contains("position-left") ||
            el.classList.contains("position-right")
          ) {
            el.style.setProperty("max-width", "50%", "important");
          }
        });
        clone.querySelectorAll(".editor-image").forEach((el) => {
          el.style.setProperty("display", "flex", "important");
          el.style.setProperty("flex-direction", "column", "important");
          el.style.setProperty("align-items", "stretch", "important");
          el.style.setProperty("gap", "0", "important");
          el.style.setProperty("row-gap", "0", "important");
          el.style.setProperty("float", "none", "important");
          el.style.setProperty("clear", "both", "important");
          el.style.setProperty("max-width", "100%", "important");
        });
        clone
          .querySelectorAll(".image-caption-container")
          .forEach(applyImageCaptionLayoutForExport);
        // Resizers / edit UI are absolutely positioned but can still affect flex order in some viewers.
        clone
          .querySelectorAll(
            ".inline-editor-image .image-resizer, .editor-image .image-resizer, .inline-editor-image .image-edit-button, .editor-image .image-edit-button",
          )
          .forEach((el) => {
            el.remove();
          });

        if (isCaptionExportDebugEnabled()) {
          const wrappers = Array.from(
            clone.querySelectorAll(".inline-editor-image, .editor-image"),
          );
          const captions = Array.from(
            clone.querySelectorAll(".image-caption-container"),
          );
          const sample = captions.slice(0, 5).map((captionEl, index) => {
            const wrapper = captionEl.closest(
              ".inline-editor-image, .editor-image",
            );
            const cs = window.getComputedStyle(captionEl);
            return {
              index,
              captionTag: captionEl.tagName,
              wrapperClasses: wrapper?.className || "(none)",
              captionChildren: captionEl.children.length,
              hasStaticCaption: !!captionEl.querySelector("[data-pdf-caption]"),
              background: cs.backgroundColor,
              marginTop: cs.marginTop,
              position: cs.position,
            };
          });
          // eslint-disable-next-line no-console
        }

        // Ensure wrappers around image nodes don't keep inline/float behavior in exported HTML.
        clone
          .querySelectorAll(".inline-editor-image, .editor-image")
          .forEach((wrapper) => {
            const imageNodes = wrapper.querySelectorAll("img");
            imageNodes.forEach((img) => {
              img.style.setProperty("display", "block", "important");
              img.style.setProperty("max-width", "100%", "important");
              img.style.setProperty("height", "auto", "important");
              img.style.setProperty("float", "none", "important");
              img.style.setProperty("margin", "0", "important");
              img.style.setProperty("vertical-align", "bottom", "important");
            });
            // Lexical wraps the image in an inline span; block layout matches InlineImageComponent.
            wrapper.querySelectorAll("span[draggable]").forEach((el) => {
              el.style.setProperty("display", "block", "important");
              if (wrapper.classList.contains("inline-editor-image")) {
                el.style.setProperty("width", "auto", "important");
                el.style.setProperty("max-width", "100%", "important");
                el.style.setProperty("line-height", "0", "important");
                el.style.setProperty("font-size", "0", "important");
              } else {
                el.style.setProperty("width", "100%", "important");
                el.style.setProperty("max-width", "100%", "important");
              }
            });
          });
        // Lock caption width to the image; strip whitespace nodes (gap under inline-block).
        clone.querySelectorAll(".inline-editor-image").forEach((wrapper) => {
          removeWhitespaceOnlyTextNodesFromElement(wrapper);
          const img = wrapper.querySelector("img");
          if (!img) return;
          const rect = img.getBoundingClientRect();
          const widthPx = Math.round(rect.width || img.width || 0);
          if (widthPx > 0) {
            wrapper.style.setProperty("width", `${widthPx}px`, "important");
            wrapper.style.setProperty("min-width", `${widthPx}px`, "important");
            wrapper.style.setProperty("max-width", `${widthPx}px`, "important");
            const caption = wrapper.querySelector(".image-caption-container");
            if (caption) {
              caption.style.setProperty("width", `${widthPx}px`, "important");
              caption.style.setProperty("min-width", `${widthPx}px`, "important");
              caption.style.setProperty("max-width", `${widthPx}px`, "important");
              caption.style.setProperty("margin-top", "0", "important");
              caption.style.setProperty("padding-top", "0", "important");
            }
          }
          wrapper.style.setProperty("font-size", "0", "important");
          const capEl = wrapper.querySelector(".image-caption-container");
          if (capEl) {
            capEl.style.setProperty("font-size", "14px", "important");
          }
        });
      } catch (_) {
      } finally {
        if (clone.parentNode) {
          document.body.removeChild(clone);
        }
        rootStyle.position = savedRootLayout.position;
        rootStyle.left = savedRootLayout.left;
        rootStyle.top = savedRootLayout.top;
        rootStyle.width = savedRootLayout.width;
      }

      let inlinedCSS =
        "body{margin:16px;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif;}";
      try {
        Array.from(document.styleSheets).forEach((sheet) => {
          try {
            const href = sheet.href || window.location.href;
            const rules = sheet.cssRules || [];
            Array.from(rules).forEach((rule) => {
              const text = rule.cssText || "";
              inlinedCSS += absolutizeCssUrlsForMhtml(text, href);
            });
          } catch (_) {}
        });
      } catch (_) {}

      const externalStylesheetTags = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]'),
      )
        .map((link) => {
          const raw = link.getAttribute("href");
          if (!raw || raw.startsWith("blob:")) return "";
          try {
            const abs = new URL(raw, window.location.href).href;
            const safe = abs.replace(/"/g, "&quot;");
            return `<link rel="stylesheet" href="${safe}"/>`;
          } catch {
            return "";
          }
        })
        .filter(Boolean)
        .join("\n");

      // Defensive export-only CSS for Lexical image captions:
      // keep caption below image even when editor positioning/floats are present.
      inlinedCSS += `
        .editor-image,
        .editor-shell .editor-image {
          display: flex !important;
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 0 !important;
          row-gap: 0 !important;
          float: none !important;
          clear: both !important;
          max-width: 100% !important;
          position: relative !important;
        }
        .inline-editor-image,
        .editor-shell .inline-editor-image {
          display: inline-block !important;
          vertical-align: top !important;
          float: none !important;
          clear: both !important;
          max-width: 100% !important;
          width: max-content !important;
          position: relative !important;
          font-size: 0 !important;
        }
        .inline-editor-image.position-left,
        .inline-editor-image.position-right,
        .editor-shell .inline-editor-image.position-left,
        .editor-shell .inline-editor-image.position-right {
          float: none !important;
          margin: 1em 0 !important;
          max-width: 50% !important;
        }
        .inline-editor-image.position-full,
        .editor-shell .inline-editor-image.position-full {
          float: none !important;
          margin: 1em 0 !important;
          max-width: 100% !important;
        }
        .editor-image .image-caption-container,
        .editor-shell .editor-image .image-caption-container,
        .short-description-editor-wrapper .editor-image .image-caption-container {
          display: block !important;
          position: static !important;
          order: 2 !important;
          width: 100% !important;
          min-width: 100% !important;
          clear: both !important;
          margin-top: 0 !important;
          box-sizing: border-box !important;
          background: #f4f4f4 !important;
          background-color: #f4f4f4 !important;
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .inline-editor-image .image-caption-container,
        .editor-shell .inline-editor-image .image-caption-container,
        .short-description-editor-wrapper .inline-editor-image .image-caption-container {
          display: block !important;
          position: static !important;
          order: 2 !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          clear: both !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          box-sizing: border-box !important;
          background: #f4f4f4 !important;
          background-color: #f4f4f4 !important;
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .short-description-editor-wrapper .inline-editor-image span[draggable],
        .inline-editor-image span[draggable] {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          line-height: 0 !important;
          font-size: 0 !important;
        }
        .image-caption-container [data-pdf-caption="true"] {
          display: block !important;
          background: transparent !important;
        }
        /* Make short description toolbars non-sticky in exported files */
        .short-description-editor-wrapper .toolbars {
          position: static !important;
          top: auto !important;
          z-index: auto !important;
        }
        /* Module preview: toolbar is disabled but must stay readable (Editor/styles.css uses opacity 0.2 on :disabled icons) */
        .short-description-editor-wrapper .toolbars {
          opacity: 1 !important;
          visibility: visible !important;
          filter: none !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .short-description-editor-wrapper .toolbars button.toolbar-item i.format,
        .short-description-editor-wrapper .toolbars .toolbar-item .icon {
          opacity: 1 !important;
          visibility: visible !important;
        }
        .short-description-editor-wrapper .toolbars button.toolbar-item:disabled i.format {
          opacity: 0.85 !important;
        }
        .short-description-editor-wrapper .toolbars select.toolbar-item,
        .short-description-editor-wrapper .toolbars .toolbar-item .text {
          opacity: 1 !important;
          color: #333 !important;
        }
        /* Highest specificity + block caption div: survives linked stylesheets / Word HTML round-trip */
        #export-root .editor-image,
        #export-root .editor-shell .editor-image {
          display: flex !important;
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 0 !important;
          row-gap: 0 !important;
          float: none !important;
          clear: both !important;
          max-width: 100% !important;
          position: relative !important;
        }
        #export-root .inline-editor-image,
        #export-root .editor-shell .inline-editor-image {
          display: inline-block !important;
          vertical-align: top !important;
          float: none !important;
          clear: both !important;
          max-width: 100% !important;
          width: max-content !important;
          position: relative !important;
          font-size: 0 !important;
        }
        #export-root .inline-editor-image.position-left,
        #export-root .inline-editor-image.position-right,
        #export-root .editor-shell .inline-editor-image.position-left,
        #export-root .editor-shell .inline-editor-image.position-right {
          float: none !important;
          margin: 1em 0 !important;
          max-width: 50% !important;
        }
        #export-root .inline-editor-image.position-full,
        #export-root .editor-shell .inline-editor-image.position-full {
          float: none !important;
          margin: 1em 0 !important;
          max-width: 100% !important;
        }
        #export-root .editor-image .image-caption-container,
        #export-root .editor-shell .editor-image .image-caption-container,
        #export-root .short-description-editor-wrapper .editor-image .image-caption-container {
          display: block !important;
          position: static !important;
          order: 2 !important;
          width: 100% !important;
          min-width: 100% !important;
          clear: both !important;
          margin-top: 0 !important;
          box-sizing: border-box !important;
          background: #f4f4f4 !important;
          background-color: #f4f4f4 !important;
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #export-root .inline-editor-image .image-caption-container,
        #export-root .editor-shell .inline-editor-image .image-caption-container,
        #export-root .short-description-editor-wrapper .inline-editor-image .image-caption-container {
          display: block !important;
          position: static !important;
          order: 2 !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          clear: both !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          box-sizing: border-box !important;
          background: #f4f4f4 !important;
          background-color: #f4f4f4 !important;
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #export-root .inline-editor-image span[draggable],
        #export-root .short-description-editor-wrapper .inline-editor-image span[draggable] {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          line-height: 0 !important;
          font-size: 0 !important;
        }
      `;

      const html = `<!DOCTYPE html>\n<html>\n<head>\n<meta charset="UTF-8"/>\n<meta http-equiv="X-UA-Compatible" content="IE=edge"/>\n<meta name="viewport" content="width=device-width, initial-scale=1"/>\n<title>${titleText}</title>\n${externalStylesheetTags}\n<style>${inlinedCSS}</style>\n</head>\n<body style="margin:16px">\n<div id="export-root" style="position:relative; transform-origin: top left;">${clone.outerHTML}</div>\n<script>try{document.querySelectorAll('[contenteditable]').forEach(e=>e.removeAttribute('contenteditable'));}catch(e){}</script>\n</body>\n</html>`;

      container.style.transform = mhtmlExportInitialTransform;

      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileName = isModulePreview
        ? `${type || "Module"}_${partNumber || ""}.html`.replace(/\s+/g, "_")
        : `${type || "Document"}_${partNumber || ""}_Rev_${
            myRevision || ""
          }.html`.replace(/\s+/g, "_");
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      try {
        container.style.transform = mhtmlExportInitialTransform;
      } catch (_) {}
    }
  };

  const handleExportSignaturesPDF = async () => {
    let tempContainer = null;
    try {
      await waitForContentToRender();
      const targetStatuses = [
        "submitted",
        "submitted for approval",
        "approved",
        "approve",
        "released",
        "release",
      ];

      const dataToUse =
        filteredRevisionData.length > 0 ? filteredRevisionData : revisionData;

      const nonRejectedRevisions = dataToUse.filter((revision) => {
        if (
          !revision?.status_changes ||
          !Array.isArray(revision.status_changes) ||
          revision.status_changes.length === 0
        ) {
          return false;
        }

        const latestStatusChange = [...revision.status_changes].sort((a, b) => {
          const dateA = new Date(a?.audit_trail?.date || a?.created_at || 0);
          const dateB = new Date(b?.audit_trail?.date || b?.created_at || 0);
          return dateB - dateA;
        })[0];

        const currentStatus = (
          latestStatusChange?.status_changed_to || ""
        ).toLowerCase();
        return !currentStatus.includes("reject");
      });

      const allStatusChanges = [];

      nonRejectedRevisions.forEach((revision) => {
        if (
          !revision?.status_changes ||
          !Array.isArray(revision.status_changes)
        )
          return;

        const relevantStatusChanges = [...revision.status_changes]
          .filter((statusChange) => {
            const status = (
              statusChange?.status_changed_to || ""
            ).toLowerCase();
            if (status === "not submitted") {
              return false;
            }
            const isTargetStatus = targetStatuses.some((targetStatus) =>
              status.includes(targetStatus),
            );
            return isTargetStatus;
          })
          .sort((a, b) => {
            const dateA = new Date(a?.audit_trail?.date || a?.created_at || 0);
            const dateB = new Date(b?.audit_trail?.date || b?.created_at || 0);
            return dateB - dateA;
          });

        relevantStatusChanges.forEach((statusChange) => {
          allStatusChanges.push({
            ...statusChange,
            revisionRev: revision.rev,
            revisionId: revision.procedure_revision_id,
          });
        });
      });

      allStatusChanges.sort((a, b) => {
        const dateA = new Date(a?.audit_trail?.date || a?.created_at || 0);
        const dateB = new Date(b?.audit_trail?.date || b?.created_at || 0);
        return dateB - dateA;
      });

      const rows = [];

      const pushRow = (entry) => {
        if (!entry?.audit_trail) {
          return;
        }

        const user = entry.audit_trail.user;
        const fullName = user
          ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
          : "-";

        const dateSource = entry.audit_trail?.date || entry?.created_at || "";
        let dateObj = null;
        if (dateSource) {
          const dateStr = dateSource.trim();
          const hasTimezone =
            dateStr.endsWith("Z") ||
            /[+-]\d{2}:?\d{2}$/.test(dateStr) ||
            /[+-]\d{4}$/.test(dateStr);

          const normalizedDateStr = hasTimezone ? dateStr : dateStr + "Z";
          dateObj = new Date(normalizedDateStr);

          if (isNaN(dateObj.getTime())) {
            dateObj = new Date(dateStr);
          }
        }

        const date =
          dateObj && !isNaN(dateObj.getTime())
            ? dateObj.toLocaleDateString("en-CA", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                timeZone: "America/New_York",
              })
            : "-";

        const time =
          dateObj && !isNaN(dateObj.getTime())
            ? dateObj.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
                timeZone: "America/New_York",
              })
            : "-";

        const sigUrl = normalizeSignatureDataUrl(
          entry.audit_trail?.signature || "",
        );

        const statusRaw = entry.status_changed_to || "";
        const statusLower = statusRaw.toLowerCase();
        const statusFromAction = deriveDisplayStatusFromAuditAction(
          entry.audit_trail?.action,
        );

        const isSubmittedForApproval =
          statusLower.includes("submitted for approval") ||
          (statusLower.includes("submitted") &&
            statusLower.includes("approval"));

        let displayStatus = statusRaw;
        if (statusFromAction) {
          displayStatus = statusFromAction;
        } else if (isSubmittedForApproval) {
          displayStatus = "Submitted For Approval";
        } else if (statusLower.includes("submitted")) {
          displayStatus = "Submitted";
        } else if (
          statusLower.includes("approved") ||
          statusLower.includes("approve")
        ) {
          displayStatus = "Approved";
        } else if (
          statusLower.includes("released") ||
          statusLower.includes("release")
        ) {
          displayStatus = "Released";
        }

        const allowedStatuses = [
          "Submitted",
          "Approved",
          "Released",
          "Submitted For Approval",
        ];

        const revisionIdMatches =
          String(entry.revisionId) === String(revisionId);
        const statusAllowed = allowedStatuses.includes(displayStatus);

        if (!revisionIdMatches || !statusAllowed) {
          return;
        }

        if (displayStatus === "Approved" && statusFromAction !== "Approved") {
          const matchedRevision = dataToUse.find(
            (rev) =>
              String(rev?.procedure_revision_id) === String(revisionId ?? ""),
          );

          if (matchedRevision && Array.isArray(matchedRevision.required_by)) {
            const approveRequiredBy = matchedRevision.required_by.filter(
              (rb) => {
                const purpose = (rb?.purpose || "").toLowerCase().trim();
                return purpose === "approve";
              },
            );

            if (approveRequiredBy.length > 0) {
              const hasOpenApprove = approveRequiredBy.some((rb) => {
                const isOpen =
                  rb.is_open === true ||
                  rb.is_open === "true" ||
                  rb.is_open === 1 ||
                  String(rb.is_open).toLowerCase() === "true";
                return isOpen;
              });

              if (hasOpenApprove) {
                displayStatus = "Submitted";
              }
            } else {
              displayStatus = "Submitted";
            }
          } else {
            displayStatus = "Submitted";
          }
        }

        if (displayStatus === "Submitted" && !isSubmittedForApproval) {
          const revision = dataToUse.find(
            (rev) =>
              String(rev?.procedure_revision_id) === String(entry.revisionId),
          );
          if (revision && Array.isArray(revision.required_by)) {
            const submitRequiredBy = revision.required_by.find(
              (rb) => rb?.purpose?.toLowerCase() === "submit",
            );

            if (submitRequiredBy && submitRequiredBy.is_open === true) {
              return;
            }
          }
        }

        if (displayStatus === "Submitted For Approval") {
          displayStatus = "Submitted";
        }

        rows.push({
          fullName,
          date,
          time,
          sigUrl,
          status: displayStatus,
          revisionRev: entry.revisionRev,
        });
      };

      allStatusChanges.forEach(pushRow);

      rows.sort((a, b) => {
        const statusOrder = { Submitted: 0, Approved: 1, Released: 2 };
        const statusDiff =
          (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        if (statusDiff !== 0) return statusDiff;
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return (a.time || "").localeCompare(b.time || "");
      });

      if (rows.length === 0) {
        window.alert("No signature data found for the specified statuses.");
        return;
      }

      const resolvedAppSetupItems = await resolveAppSetupItems(true);
      const appSetup =
        resolvedAppSetupItems.length > 0 ? resolvedAppSetupItems[0] : null;

      const companyName = appSetup?.company_name || "";
      const companyLogo = getAppSetupLogoPath(appSetup);
      const companyAddress = appSetup?.company_address || "";
      const companyPhone = formatPhoneNumber(appSetup?.company_phone || "");

      const companyLogoUrl = await getCompanyLogoDataUrl(companyLogo);
      const [phoneIconPngDataUrl, distanceIconPngDataUrl] = await Promise.all([
        getPhoneIconPngDataUrl().catch(() => null),
        getDistanceIconPngDataUrl().catch(() => null),
      ]);

      const typeStr = String(type || "").trim();
      const partNumberStr = String(partNumber || "").trim();
      let descriptionText = "";
      if (description) {
        const stripped = stripHtmlPreserveFormatting(description).trim();
        descriptionText = stripped || String(description).trim();
      }
      const titlePrefix =
        typeStr && partNumberStr
          ? `${typeStr} ${partNumberStr}`
          : typeStr || partNumberStr;
      const headerTitle = descriptionText
        ? `${titlePrefix}, ${descriptionText}`
        : titlePrefix;
      const cleanedPartNumberStr = (partNumberStr || "")
        .replace(/,\s*$/, "")
        .trim();
      const blueHeaderTitlePrefix = cleanedPartNumberStr || typeStr;
      const cleanedDescriptionText = descriptionText
        ? descriptionText.replace(/^,\s*/, "").trim()
        : "";
      const blueHeaderTitle = cleanedDescriptionText
        ? `${blueHeaderTitlePrefix} ${cleanedDescriptionText}`
        : blueHeaderTitlePrefix;
      const headerSub = myRevision
        ? `Revision ${myRevision} Submission, Approval, and Release History`
        : `Revision- Submission, Approval, and Release History`;
      const docRevisionLabel = myRevision ? `Revision ${myRevision}` : "";
      const docInfoLeft =
        docRevisionLabel && blueHeaderTitle
          ? `${blueHeaderTitle} | ${docRevisionLabel}`
          : docRevisionLabel || blueHeaderTitle;

      let releaseInfo = "";

      const releaseStatusChange = allStatusChanges.find(
        (statusChange) =>
          statusChange?.status_changed_to &&
          statusChange.status_changed_to.toLowerCase().includes("released"),
      );

      if (releaseStatusChange?.audit_trail?.date) {
        const dateSource = releaseStatusChange.audit_trail.date;
        const releaseUser = releaseStatusChange.audit_trail.user;
        const releaseUserName = releaseUser
          ? `${releaseUser.first_name || ""} ${
              releaseUser.last_name || ""
            }`.trim()
          : "Unknown User";

        let releaseDate = null;
        if (dateSource) {
          const dateStr = String(dateSource).trim();
          const hasTimezone =
            dateStr.endsWith("Z") ||
            /[+-]\d{2}:?\d{2}$/.test(dateStr) ||
            /[+-]\d{4}$/.test(dateStr);

          const normalizedDateStr = hasTimezone ? dateStr : dateStr + "Z";
          releaseDate = new Date(normalizedDateStr);

          if (isNaN(releaseDate.getTime())) {
            releaseDate = new Date(dateStr);
          }
        }

        if (!releaseDate || isNaN(releaseDate.getTime())) {
          releaseInfo = "";
        } else {
          const estFormatter = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/New_York",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          const parts = estFormatter.formatToParts(releaseDate);
          const year = parts.find((p) => p.type === "year")?.value || "";
          const month = parts.find((p) => p.type === "month")?.value || "";
          const day = parts.find((p) => p.type === "day")?.value || "";
          const hour = parts.find((p) => p.type === "hour")?.value || "";
          const minute = parts.find((p) => p.type === "minute")?.value || "";
          const dayPeriod =
            parts.find((p) => p.type === "dayPeriod")?.value || "";

          const releaseDateFormatted = `${year}-${month.padStart(
            2,
            "0",
          )}-${day.padStart(2, "0")}`;
          const releaseTimeFormatted = `${hour.padStart(2, "0")}:${minute.padStart(
            2,
            "0",
          )} ${dayPeriod.toUpperCase()}`;

          const timezoneParts = releaseDate
            .toLocaleDateString("en-US", {
              timeZoneName: "short",
              timeZone: "America/New_York",
            })
            .split(", ");
          const timezone = timezoneParts[1] || "EST";

          releaseInfo = `Released ${releaseDateFormatted} at ${releaseTimeFormatted} ${timezone} by ${releaseUserName}`;
        }
      }

      const currentDate = new Date();
      const exportDateFormatted = currentDate.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "America/New_York",
      });
      const exportTimeFormatted = currentDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/New_York",
      });
      const exportTimezone = currentDate
        .toLocaleDateString("en-US", {
          timeZoneName: "short",
          timeZone: "America/New_York",
        })
        .split(", ")[1];

      const exportInfo = `Exported on ${exportDateFormatted} at ${exportTimeFormatted} ${exportTimezone} by ${localStorage.getItem(
        "user_name",
      )}`;

      tempContainer = document.createElement("div");
      tempContainer.style.position = "fixed";
      tempContainer.style.left = "-10000px";
      tempContainer.style.top = "0";
      tempContainer.style.background = "#fff";
      tempContainer.style.padding = "16px";
      tempContainer.style.width = "1000px";
      document.body.appendChild(tempContainer);

      const baseTableWrapper = document.createElement("div");
      baseTableWrapper.style.cssText =
        "min-height: 100vh; display: flex; flex-direction: column; font-family: Arial, Helvetica, sans-serif; color: #111; position: relative;";

      const revisionLabel = myRevision ? `Revision ${myRevision}` : "Revision-";
      const signatureTableHeadingText = `${revisionLabel} Submission, Approval, and Release History`;

      // Prepare footer text
      const footerCopy =
        (appSetup && appSetup.footer && appSetup.footer.trim()) ||
        (companyName
          ? `Proprietary information of RUSHABH Instruments, LLC (RI). Do not duplicate or reproduce in any form without written permission of RI.`
          : "Proprietary information. Do not duplicate or reproduce without written permission.");

      // Clean up temp container - we don't need it for autotable
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
        tempContainer = null;
      }

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const horizontalMargin = 12;
      const headerHeight = 42;
      const footerHeight = 5;
      const contentTopPadding = 6;
      const contentBottomPadding = 12;
      const topContentY = headerHeight + contentTopPadding;
      const bottomContentY = pdfHeight - footerHeight - contentBottomPadding;
      const drawableHeight = Math.max(0, bottomContentY - topContentY);
      const logoY = 12;
      const logoHeight = 14;
      const logoWidth = 32;
      const dividerY = headerHeight - 14;
      const infoY = headerHeight - 8;
      const footerY = pdfHeight - footerHeight;
      const iconSize = 2.3;
      const iconSpacing = 2;
      const iconYOffset = 0.4;

      // Prepare signature table data for autotable
      const signatureTableHeaders = [
        "User",
        "Date",
        "Time",
        "Signature",
        "Status",
      ];
      const signatureTableRows = rows.map((row) => [
        row.fullName,
        row.date,
        row.time,
        row.sigUrl || "", // Store signature URL - will be handled in didParseCell
        row.status,
      ]);

      // Prepare logo URL (convert SVG to PNG if needed)
      let logoToUse = companyLogoUrl;
      if (companyLogoUrl) {
        try {
          const isSvg =
            companyLogoUrl.includes("svg") ||
            companyLogoUrl.includes("svg+xml");
          if (isSvg) {
            const pngDataUrl = await convertSvgToPng(companyLogoUrl);
            if (pngDataUrl) {
              logoToUse = pngDataUrl;
            } else {
              logoToUse = null;
            }
          }
        } catch (err) {
          logoToUse = companyLogoUrl; // Fallback to original
        }
      }

      // Helper function to draw header/footer on each page
      const drawPageHeaderFooter = (currentPageNum, totalPagesNum) => {
        // White background for header and footer areas
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, headerHeight + contentTopPadding, "F");
        pdf.rect(
          0,
          pdfHeight - footerHeight - contentBottomPadding,
          pdfWidth,
          footerHeight + contentBottomPadding,
          "F",
        );

        // Draw logo
        if (logoToUse) {
          try {
            pdf.addImage(
              logoToUse,
              "PNG",
              horizontalMargin,
              logoY,
              logoWidth,
              logoHeight,
            );
          } catch (err) {}
        }

        // Draw company name
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.setTextColor(17, 17, 17);
        if (companyName) {
          pdf.text(companyName, pdfWidth - horizontalMargin, logoY + 9, {
            align: "right",
          });
        }

        // Draw company address and phone
        if (companyAddress || companyPhone) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(17, 17, 17);

          const textY = logoY + 14;
          let currentX = pdfWidth - horizontalMargin;

          if (companyPhone) {
            const phoneTextWidth = pdf.getTextWidth(companyPhone);
            const iconX = currentX - phoneTextWidth - iconSize - iconSpacing;
            if (phoneIconPngDataUrl) {
              try {
                pdf.addImage(
                  phoneIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }
            pdf.text(companyPhone, currentX, textY, { align: "right" });
            currentX = iconX - 3;
          }

          if (companyAddress && companyPhone) {
            const separatorWidth = 4;
            currentX -= separatorWidth;
          }

          if (companyAddress) {
            const addressTextWidth = pdf.getTextWidth(companyAddress);
            const iconX = currentX - addressTextWidth - iconSize - iconSpacing;
            if (distanceIconPngDataUrl) {
              try {
                pdf.addImage(
                  distanceIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }
            pdf.text(companyAddress, currentX, textY, { align: "right" });
          }

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(16);
          pdf.setTextColor(17, 17, 17);
        }

        // Draw divider line
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          dividerY,
          pdfWidth - horizontalMargin,
          dividerY,
        );

        // Draw document info
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(26, 95, 180);
        const infoColumnWidth = (pdfWidth - horizontalMargin * 2) / 2;
        if (docInfoLeft) {
          pdf.text(docInfoLeft, horizontalMargin, infoY, {
            maxWidth: infoColumnWidth - 4,
          });
        }
        const rightColumnSegments = [];
        if (releaseInfo) {
          rightColumnSegments.push(splitReleaseInfoLines(releaseInfo));
        }
        if (exportInfo) {
          rightColumnSegments.push(splitExportInfoLines(exportInfo));
        }
        if (rightColumnSegments.length) {
          const separatorHeight = 3.5;
          const separatorGap = 3;
          let currentX = pdfWidth - horizontalMargin;

          for (let idx = rightColumnSegments.length - 1; idx >= 0; idx -= 1) {
            const segmentLines = rightColumnSegments[idx].length
              ? rightColumnSegments[idx]
              : [""];
            const lineHeight = 4;
            const maxLineWidth = Math.max(
              ...segmentLines.map((line) => pdf.getTextWidth(line)),
            );
            const startY = infoY - ((segmentLines.length - 1) * lineHeight) / 2;
            segmentLines.forEach((line, lineIdx) => {
              const lineY = startY + lineIdx * lineHeight;
              pdf.text(line, currentX, lineY, { align: "right" });
            });
            currentX -= maxLineWidth;

            if (idx > 0) {
              currentX -= separatorGap;
              pdf.setDrawColor(209, 213, 219);
              pdf.setLineWidth(0.4);
              pdf.line(
                currentX,
                infoY - separatorHeight,
                currentX,
                infoY + separatorHeight,
              );
              currentX -= separatorGap;
            }
          }
        }

        // Draw footer
        const footerBorderY = footerY - 4;
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          footerBorderY,
          pdfWidth - horizontalMargin,
          footerBorderY,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(102, 102, 102);
        if (footerCopy) {
          pdf.text(footerCopy, horizontalMargin, footerY);
        }
        const pageNumberText = `Page ${currentPageNum}/${totalPagesNum}`;
        const pageNumberWidth = pdf.getTextWidth(pageNumberText);
        const separatorGap = 3;
        const separatorHeight = 3;
        const separatorX =
          pdfWidth - horizontalMargin - pageNumberWidth - separatorGap;
        pdf.setDrawColor(209, 213, 219);
        pdf.setLineWidth(0.3);
        pdf.line(
          separatorX,
          footerY - separatorHeight,
          separatorX,
          footerY + separatorHeight,
        );
        pdf.text(pageNumberText, pdfWidth - horizontalMargin, footerY, {
          align: "right",
        });
      };

      // Draw header/footer on first page
      drawPageHeaderFooter(1, 1);

      // Calculate table start position (after header and heading)
      const headingY = topContentY + 2;
      const tableStartY = headingY + 3.5; // Tighter gap under title

      // Draw heading (slightly smaller than body table text)
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(0, 0, 0);
      pdf.text(signatureTableHeadingText, horizontalMargin, headingY);

      // Calculate table width
      const tableWidthMm = pdfWidth - horizontalMargin * 2;

      // Render signature table with autotable
      const initialPageCount = pdf.getNumberOfPages();
      autoTable(pdf, {
        startY: tableStartY,
        head: [signatureTableHeaders],
        body: signatureTableRows,
        margin: {
          left: horizontalMargin,
          right: horizontalMargin,
          top: headerHeight + contentTopPadding,
        },
        theme: "plain", // Disable default striped row styling
        styles: {
          fontSize: 8,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          minCellHeight: 9,
        },
        headStyles: {
          fillColor: [0, 0, 0],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
          fontSize: 9,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        columnStyles: {
          3: { halign: "center", cellWidth: 38, minCellHeight: 9 },
        },
        bodyStyles: {
          halign: "left",
          fillColor: [255, 255, 255], // White background for all rows
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        pageBreak: "auto",
        rowPageBreak: "avoid",
        showHead: "everyPage",
        didParseCell: (data) => {
          // For signature column (index 3), set minimum cell height and clear text content
          if (data.column.index === 3 && data.section === "body") {
            const sigUrl = data.row.raw && data.row.raw[3];
            if (sigUrl && sigUrl.trim()) {
              data.cell.minHeight = 9;
              // Clear text content - we'll draw the image instead
              data.cell.text = [""];
            }
          }
        },
        didDrawCell: (data) => {
          // Draw signature images in column 3 (index 3)
          if (
            data.column.index === 3 &&
            data.section === "body" &&
            data.row.raw &&
            data.row.raw[3]
          ) {
            const sigUrl = data.row.raw[3];
            if (sigUrl && sigUrl.trim()) {
              try {
                const cellX = data.cell.x;
                const cellY = data.cell.y;
                const cellWidth = data.cell.width;
                const cellHeight = data.cell.height;
                const pad = 0.5;
                const cw = cellWidth - pad * 2;
                const ch = cellHeight - pad * 2;
                const maxSigW = Math.min(22, cw);
                const maxSigH = Math.min(6.5, ch);
                const imgWidth = maxSigW;
                const imgHeight = maxSigH;
                const imgX = cellX + pad + (cw - imgWidth) / 2;
                const imgY = cellY + pad + (ch - imgHeight) / 2;
                const srcLower = String(sigUrl).toLowerCase();
                const fmt =
                  srcLower.includes("jpeg") || srcLower.includes("jpg")
                    ? "JPEG"
                    : srcLower.includes("gif")
                      ? "GIF"
                      : srcLower.includes("webp")
                        ? "WEBP"
                        : "PNG";
                try {
                  pdf.addImage(sigUrl, fmt, imgX, imgY, imgWidth, imgHeight);
                } catch (_) {
                  try {
                    pdf.addImage(sigUrl, "PNG", imgX, imgY, imgWidth, imgHeight);
                  } catch (__) {}
                }
              } catch (err) {
                console.error("Error drawing signature image:", err);
              }
            }
          }
        },
        didDrawPage: (data) => {
          // This will be called for each page that autotable draws on
          const currentPage = data.pageNumber;
          const finalPageCount = pdf.getNumberOfPages();

          // Draw header/footer on every page
          drawPageHeaderFooter(currentPage, finalPageCount);

          // Draw heading on ALL pages (including first page, since didDrawPage runs after header/footer)
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(0, 0, 0);
          pdf.text(signatureTableHeadingText, horizontalMargin, headingY);
        },
        willDrawPage: (data) => {
          // Adjust startY for continuation pages to leave space for heading
          if (data.pageNumber > 1) {
            // On continuation pages, we need to start the table after the heading
            const continuationTableStartY = headingY + 3.5;
            // Update the startY for this page
            data.settings.startY = continuationTableStartY;
          }
        },
      });

      const fileName = `${type || "Document"}_${partNumber || ""}_${
        filteredRevisionData.length > 0 ? "Filtered" : "All"
      }_Revisions_Signatures.pdf`;
      pdf.save(fileName.replace(/\s+/g, "_"));
    } catch (e) {
    } finally {
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      if (exportFormat === "csv_bom") {
        handleExportBOMCSV();
      } else if (exportFormat === "csv_revisions") {
        handleExportRevisionsCSV();
      } else if (exportFormat === "csv_training") {
        handleExportTrainingCSV();
      } else if (exportFormat === "csv_module_procedures") {
        handleExportModuleProceduresCSV();
      } else if (exportFormat === "pdf") {
        await handleExportPDF();
      } else if (exportFormat === "mhtml") {
        await handleExportMHTML();
      } else if (exportFormat === "signatures_workflow") {
        await handleExportSignaturesPDF();
      } else if (exportFormat === "zip") {
        await handleExportZIP();
      }
      onClose();
    } finally {
      setIsExporting(false);
    }
  };

  const generatePDFFromNodes = async (
    nodesData,
    partNumber,
    revision,
    typeName,
    description = "",
    options = {},
  ) => {
    try {
      const resolvedAppSetupItems = await resolveAppSetupItems(true);
      const appSetup =
        resolvedAppSetupItems.length > 0 ? resolvedAppSetupItems[0] : null;

      const companyName = appSetup?.company_name || "";
      const companyLogo = getAppSetupLogoPath(appSetup);
      const companyAddress = (appSetup?.company_address || "").trim();
      const rawPhone = (appSetup?.company_phone || "").trim();
      const companyPhone = formatPhoneNumber(rawPhone);
      const companyLogoUrl = await getCompanyLogoDataUrl(companyLogo);

      const tempContainer = document.createElement("div");
      tempContainer.style.position = "fixed";
      tempContainer.style.left = "-10000px";
      tempContainer.style.top = "0";
      tempContainer.style.background = "#fff";
      tempContainer.style.padding = "20px";
      tempContainer.style.width = "1000px";
      tempContainer.style.fontFamily = "Arial, Helvetica, sans-serif";
      tempContainer.style.color = "#111";
      document.body.appendChild(tempContainer);

      // Ensure Lexical / rich text toolbars are not visible in generated PDFs
      hideRichTextUndoToolbarsForExport(tempContainer);
      if (isModulePreview) {
        enforceSingleLineModulePreviewToolbarForExport(tempContainer);
        normalizeModulePreviewInputAlignmentForExport(tempContainer);
      }

      // context = { sectionIdx, stepNum, substepIdx }
      // sectionIdx: 0-based section index (shown as sectionIdx+1)
      // stepNum: global step number offset for sections; actual step number for steps/substeps
      // substepIdx: 0-based substep index (converted to letter A, B, C…)
      const renderNode = (
        node,
        depth = 0,
        nodeType = "",
        context = { sectionIdx: 0, stepNum: 0, substepIdx: 0 },
      ) => {
        const nodeDiv = document.createElement("div");
        nodeDiv.style.marginLeft = `${depth * 20}px`;
        nodeDiv.style.marginTop = "20px";
        nodeDiv.style.clear = "both";
        // No left/right padding so the short description spans the full available width

        if (node.detail) {
          const detectedNodeType = node.detail.node_type || nodeType || "";
          const sectionName = node.detail.section_name || "";

          // Process short_description the same way Preview components (SectionNode, StepNode, SubstepNode) do
          const rawShort = node.detail.short_description || "";
          let shortDescHtml = "";
          if (rawShort) {
            if (
              typeof rawShort === "string" &&
              rawShort.includes('class="editor-shell"')
            ) {
              shortDescHtml = rawShort.replace(
                /^<div class="editor-shell">([\s\S]*)<\/div>\s*$/s,
                "$1",
              );
            } else if (rawShort.includes("<") && rawShort.includes(">")) {
              shortDescHtml = rawShort;
            } else if (rawShort.trim() !== "") {
              shortDescHtml = `<p>${rawShort.replace(/"/g, "&quot;")}</p>`;
            }
          }

          if (detectedNodeType === "section" || nodeType === "section") {
            // ── Section: h2 heading → double HR → section_name → short description ──
            const headingRow = document.createElement("div");
            headingRow.style.display = "flex";
            headingRow.style.alignItems = "center";
            headingRow.style.gap = "8px";

            const h2 = document.createElement("h2");
            h2.style.fontSize = "20px";
            h2.style.margin = "0";
            h2.style.fontWeight = "bold";
            h2.textContent = `Section ${context.sectionIdx + 1}`;
            headingRow.appendChild(h2);
            nodeDiv.appendChild(headingRow);

            const hr1 = document.createElement("hr");
            hr1.style.margin = "4px 0";
            const hr2 = document.createElement("hr");
            hr2.style.margin = "4px 0";
            nodeDiv.appendChild(hr1);
            nodeDiv.appendChild(hr2);

            if (sectionName) {
              const nameDiv = document.createElement("div");
              nameDiv.textContent = sectionName;
              nameDiv.style.width = "100%";
              nameDiv.style.boxSizing = "border-box";
              nameDiv.style.marginTop = "4px";
              nodeDiv.appendChild(nameDiv);
            }
          } else if (detectedNodeType === "step" || nodeType === "step") {
            // ── Step: h2 heading (left) + breadcrumb (right) → single HR ──
            const headingRow = document.createElement("div");
            headingRow.style.display = "flex";
            headingRow.style.justifyContent = "space-between";
            headingRow.style.alignItems = "center";

            const innerLeft = document.createElement("div");
            innerLeft.style.display = "flex";
            innerLeft.style.alignItems = "center";
            innerLeft.style.gap = "8px";

            const h2 = document.createElement("h2");
            h2.style.fontSize = "20px";
            h2.style.margin = "0";
            h2.textContent = `Step ${context.stepNum}`;
            innerLeft.appendChild(h2);
            headingRow.appendChild(innerLeft);

            const breadcrumb = document.createElement("div");
            breadcrumb.style.color = "#888";
            breadcrumb.style.fontSize = "14px";
            breadcrumb.style.fontWeight = "normal";
            breadcrumb.textContent = `Section ${context.sectionIdx + 1} > Step ${context.stepNum}`;
            headingRow.appendChild(breadcrumb);

            nodeDiv.appendChild(headingRow);

            const hr = document.createElement("hr");
            hr.style.margin = "4px 0";
            nodeDiv.appendChild(hr);
          } else if (detectedNodeType === "substep" || nodeType === "substep") {
            // ── Substep: h2 heading (left) + breadcrumb (right) → single HR ──
            const substepLetter = String.fromCharCode(65 + context.substepIdx);
            const substepLabel = `Substep ${context.stepNum}${substepLetter}`;

            const headingRow = document.createElement("div");
            headingRow.style.display = "flex";
            headingRow.style.justifyContent = "space-between";
            headingRow.style.alignItems = "center";

            const innerLeft = document.createElement("div");
            innerLeft.style.display = "flex";
            innerLeft.style.alignItems = "center";
            innerLeft.style.gap = "8px";

            const h2 = document.createElement("h2");
            h2.style.fontSize = "20px";
            h2.style.margin = "0";
            h2.textContent = substepLabel;
            innerLeft.appendChild(h2);
            headingRow.appendChild(innerLeft);

            const breadcrumb = document.createElement("div");
            breadcrumb.style.color = "#888";
            breadcrumb.style.fontSize = "14px";
            breadcrumb.style.fontWeight = "normal";
            breadcrumb.textContent = `Section ${context.sectionIdx + 1} > Step ${context.stepNum} > Substep ${context.stepNum}${substepLetter}`;
            headingRow.appendChild(breadcrumb);

            nodeDiv.appendChild(headingRow);

            const hr = document.createElement("hr");
            hr.style.margin = "4px 0";
            nodeDiv.appendChild(hr);
          } else {
            // ── Other child node types ──
            if (shortDescHtml) {
              const p = document.createElement("p");
              p.style.margin = "0";
              p.innerHTML = shortDescHtml;
              nodeDiv.appendChild(p);
            }
          }

          // Short description rendered as a full-width block below the HR (matches Preview)
          const isMainNode =
            detectedNodeType === "section" ||
            detectedNodeType === "step" ||
            detectedNodeType === "substep" ||
            nodeType === "section" ||
            nodeType === "step" ||
            nodeType === "substep";
          if (shortDescHtml && isMainNode) {
            const shortDescDiv = document.createElement("div");
            shortDescDiv.style.width = "100%";
            shortDescDiv.style.boxSizing = "border-box";
            shortDescDiv.style.marginTop = "5px";
            shortDescDiv.style.wordBreak = "break-word";
            shortDescDiv.style.overflowWrap = "break-word";
            shortDescDiv.innerHTML = shortDescHtml;
            nodeDiv.appendChild(shortDescDiv);
          }

          if (node.detail.rich_text_content) {
            const richTextDiv = document.createElement("div");
            richTextDiv.innerHTML = node.detail.rich_text_content;
            richTextDiv.style.marginTop = "5px";
            richTextDiv.style.width = "100%";
            richTextDiv.style.boxSizing = "border-box";
            nodeDiv.appendChild(richTextDiv);
          }
        }

        tempContainer.appendChild(nodeDiv);

        if (node.steps && Array.isArray(node.steps)) {
          node.steps.forEach((step, stepIndex) => {
            const stepGlobalNum = context.stepNum + stepIndex + 1;
            renderNode(step, depth + 1, "step", {
              sectionIdx: context.sectionIdx,
              stepNum: stepGlobalNum,
              substepIdx: 0,
            });
            if (step.substeps && Array.isArray(step.substeps)) {
              step.substeps.forEach((substep, substepIndex) => {
                renderNode(substep, depth + 2, "substep", {
                  sectionIdx: context.sectionIdx,
                  stepNum: stepGlobalNum,
                  substepIdx: substepIndex,
                });
                if (substep.children && Array.isArray(substep.children)) {
                  substep.children.forEach((child) =>
                    renderNode(child, depth + 3, "", {
                      sectionIdx: context.sectionIdx,
                      stepNum: stepGlobalNum,
                      substepIdx: substepIndex,
                    }),
                  );
                }
              });
            }
            if (step.children && Array.isArray(step.children)) {
              step.children.forEach((child) =>
                renderNode(child, depth + 2, "", {
                  sectionIdx: context.sectionIdx,
                  stepNum: stepGlobalNum,
                  substepIdx: 0,
                }),
              );
            }
          });
        }

        if (node.children && Array.isArray(node.children) && !node.steps) {
          node.children.forEach((child) =>
            renderNode(child, depth + 1, "", context),
          );
        }
      };

      // Calculate global step offset per section (same logic as RenderRevisionNodes.getGlobalStepStart)
      let globalStepOffset = 0;
      if (nodesData && Array.isArray(nodesData)) {
        nodesData.forEach((section, sectionIdx) => {
          renderNode(section, 0, "section", {
            sectionIdx,
            stepNum: globalStepOffset,
            substepIdx: 0,
          });
          globalStepOffset += section.steps?.length || 0;
        });
      }

      const images = Array.from(tempContainer.querySelectorAll("img"));
      await Promise.all(
        images.map((img) =>
          img.decode?.().catch(
            () =>
              new Promise((resolve) => {
                if (img.complete) return resolve();
                img.onload = () => resolve();
                img.onerror = () => resolve();
              }),
          ),
        ),
      );

      if (options?.returnContainer) {
        return tempContainer;
      }

      const containerRect = tempContainer.getBoundingClientRect();
      const imageMetasDom = images
        .map((img) => {
          const rect = img.getBoundingClientRect();
          if (rect.height < 10) return null;
          return {
            topDom: rect.top - containerRect.top,
            bottomDom: rect.bottom - containerRect.top,
            heightDom: rect.height,
          };
        })
        .filter((meta) => meta !== null);

      const canvas = await html2canvas(tempContainer, {
        useCORS: true,
        crossOrigin: "anonymous",
        allowTaint: false,
        imageTimeout: 0,
        backgroundColor: "#fff",
        scale: 2,
        logging: false,
      });

      if (tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const horizontalMargin = 12;
      const headerHeight = 42;
      const footerHeight = 3;
      const contentTopPadding = 6;
      const contentBottomPadding = 6;
      const availableHeight =
        pdfHeight -
        headerHeight -
        footerHeight -
        contentTopPadding -
        contentBottomPadding;
      const imgWidth = pdfWidth - horizontalMargin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pixelToMm = imgWidth / canvas.width;
      const availableHeightPx = Math.floor(availableHeight / pixelToMm);

      const domToCanvasScaleY =
        containerRect && containerRect.height > 0
          ? canvas.height / containerRect.height
          : 1;
      const imageMetasPx = imageMetasDom
        .map((meta) => ({
          topPx: Math.round(meta.topDom * domToCanvasScaleY),
          bottomPx: Math.round(meta.bottomDom * domToCanvasScaleY),
          heightPx: Math.max(
            1,
            Math.round((meta.heightDom || 0) * domToCanvasScaleY),
          ),
        }))
        .filter((meta) => meta.heightPx > 0);

      const pageImages = [];
      let currentOffsetPx = 0;
      const totalHeightPx = canvas.height;

      while (currentOffsetPx < totalHeightPx - 1) {
        let pageContentPx = Math.min(
          availableHeightPx,
          totalHeightPx - currentOffsetPx,
        );
        let forcedBreakBeforeImage = false;

        const adjustForImageBlocks = () => {
          if (!imageMetasPx.length) return false;

          const pageStart = currentOffsetPx;
          const pageEnd = currentOffsetPx + pageContentPx;

          const splitImage = imageMetasPx.find((meta) => {
            // Detect any image that would be cut by this page end.
            const crossesPageEnd = meta.topPx < pageEnd && meta.bottomPx > pageEnd;
            if (!crossesPageEnd) return false;
            // We can only move this page break if image starts on this page.
            if (meta.topPx <= pageStart) return false;
            // Only adjust for images that can fit entirely on a single page.
            // If the image is taller than the usable page height, it will
            // necessarily span multiple pages.
            if (meta.heightPx > availableHeightPx) return false;
            return true;
          });

          if (!splitImage) return false;

          // Keep a tiny safety gap so rounding doesn't leave a 1-2px image strip
          // on the previous page.
          const proposed = Math.max(0, splitImage.topPx - currentOffsetPx - 2);
          if (proposed > 0) {
            pageContentPx = proposed;
            forcedBreakBeforeImage = true;
            return true;
          }

          return false;
        };

        while (adjustForImageBlocks()) {
          // Keep adjusting until no splittable image remains on this page.
        }

        // Guard: avoid generating extremely tiny/zero slices which explode page count.
        const remainingPx = totalHeightPx - currentOffsetPx;
        const MIN_PAGE_CONTENT_PX = Math.max(
          120,
          Math.floor(availableHeightPx * 0.2),
        );
        if (remainingPx > 0 && pageContentPx <= 0) {
          pageContentPx = Math.min(remainingPx, MIN_PAGE_CONTENT_PX);
        } else if (
          !forcedBreakBeforeImage &&
          remainingPx > MIN_PAGE_CONTENT_PX &&
          pageContentPx > 0 &&
          pageContentPx < MIN_PAGE_CONTENT_PX
        ) {
          pageContentPx = Math.min(remainingPx, MIN_PAGE_CONTENT_PX);
        }

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.max(1, pageContentPx);
        const pageCtx = pageCanvas.getContext("2d");
        pageCtx.fillStyle = "#ffffff";
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
          canvas,
          0,
          currentOffsetPx,
          canvas.width,
          pageContentPx,
          0,
          0,
          canvas.width,
          pageContentPx,
        );

        pageImages.push({
          dataUrl: pageCanvas.toDataURL("image/png"),
          heightMm: pageCanvas.height * pixelToMm,
        });

        currentOffsetPx += Math.max(1, pageContentPx);
      }

      if (pageImages.length === 0) {
        pageImages.push({
          dataUrl: canvas.toDataURL("image/png"),
          heightMm: imgHeight,
        });
      }

      const typeStr = String(typeName || "").trim();
      const partNumberStr = String(partNumber || "").trim();
      let descriptionText = "";
      if (description) {
        const stripped = stripHtmlPreserveFormatting(description).trim();
        descriptionText = stripped || String(description).trim();
      }
      const titlePrefix =
        typeStr && partNumberStr
          ? `${partNumberStr}`
          : typeStr || partNumberStr;
      const headerTitle = descriptionText
        ? `${titlePrefix}, ${descriptionText}`
        : titlePrefix;
      const cleanedTitlePrefix = (titlePrefix || "")
        .replace(/,\s*$/, "")
        .trim();
      const cleanedDescriptionForBlue = descriptionText
        ? descriptionText.replace(/^,\s*/, "").trim()
        : "";
      const blueHeaderTitle = cleanedDescriptionForBlue
        ? `${cleanedTitlePrefix} ${cleanedDescriptionForBlue}`
        : cleanedTitlePrefix;
      const headerSubtitle = revision ? `Revision ${revision}` : "";
      const docInfoLeft = headerSubtitle
        ? blueHeaderTitle
          ? `${blueHeaderTitle} | ${headerSubtitle}`
          : headerSubtitle
        : blueHeaderTitle;
      const footerText =
        appSetup?.footer?.trim() ||
        (companyName
          ? `Proprietary information of RUSHABH Instruments, LLC (RI). Do not duplicate or reproduce in any form without written permission of RI.`
          : "Proprietary information. Do not duplicate or reproduce without written permission.");
      const totalPages = pageImages.length;

      const currentDate = new Date();
      const exportDateFormatted = currentDate.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: "America/New_York",
      });
      const exportTimeFormatted = currentDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/New_York",
      });
      const exportTimezone = currentDate
        .toLocaleDateString("en-US", {
          timeZoneName: "short",
          timeZone: "America/New_York",
        })
        .split(", ")[1];
      const exportInfo = `Exported on ${exportDateFormatted} at ${exportTimeFormatted} ${exportTimezone} by ${
        localStorage.getItem("user_name") || "Unknown User"
      }`;

      const [phoneIconPngDataUrl, distanceIconPngDataUrl] = await Promise.all([
        getPhoneIconPngDataUrl().catch(() => null),
        getDistanceIconPngDataUrl().catch(() => null),
      ]);

      for (let index = 0; index < pageImages.length; index++) {
        const pageImage = pageImages[index];
        if (index > 0) {
          pdf.addPage();
        }

        // CRITICAL: Draw white rectangles for header and footer BEFORE adding page image
        // This prevents the white rectangles from covering content (like rich text) at the bottom
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, headerHeight + contentTopPadding, "F");
        pdf.rect(
          0,
          pdfHeight - footerHeight - contentBottomPadding,
          pdfWidth,
          footerHeight + contentBottomPadding,
          "F",
        );

        const maxImageHeightMm =
          pdfHeight -
          headerHeight -
          contentTopPadding -
          footerHeight -
          contentBottomPadding;

        const imageHeightMm = Math.min(
          pageImage.heightMm,
          maxImageHeightMm - 2,
        );

        pdf.addImage(
          pageImage.dataUrl,
          "PNG",
          horizontalMargin,
          headerHeight + contentTopPadding,
          imgWidth,
          imageHeightMm,
        );

        const logoY = 12;
        const logoHeight = 14;
        const logoWidth = 32;
        if (companyLogoUrl) {
          try {
            let imageFormat = "PNG";
            let logoToUse = companyLogoUrl;

            if (companyLogoUrl.startsWith("data:image/")) {
              const formatMatch = companyLogoUrl.match(/data:image\/([^;]+)/);
              if (formatMatch) {
                const format = formatMatch[1].toUpperCase();
                if (["PNG", "JPEG", "JPG", "WEBP"].includes(format)) {
                  imageFormat = format === "JPG" ? "JPEG" : format;
                } else if (format === "SVG" || format === "SVG+XML") {
                  const pngDataUrl = await convertSvgToPng(companyLogoUrl);
                  if (pngDataUrl) {
                    logoToUse = pngDataUrl;
                    imageFormat = "PNG";
                  } else {
                    logoToUse = null;
                  }
                }
              }
            }

            if (logoToUse) {
              pdf.addImage(
                logoToUse,
                imageFormat,
                horizontalMargin,
                logoY,
                logoWidth,
                logoHeight,
              );
            }
          } catch (err) {}
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.setTextColor(17, 17, 17);
        if (companyName) {
          pdf.text(companyName, pdfWidth - horizontalMargin, logoY + 9, {
            align: "right",
          });
        }

        if (companyAddress || companyPhone) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(17, 17, 17);

          const textY = logoY + 14;
          let currentX = pdfWidth - horizontalMargin;

          if (companyPhone) {
            const iconSize = 2.3;
            const iconSpacing = 2;
            const iconYOffset = 0.4;
            const phoneTextWidth = pdf.getTextWidth(companyPhone);
            const iconX = currentX - phoneTextWidth - iconSize - iconSpacing;

            if (phoneIconPngDataUrl) {
              try {
                pdf.addImage(
                  phoneIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }

            pdf.text(companyPhone, currentX, textY, { align: "right" });
            currentX = iconX - 3;
          }

          if (companyAddress && companyPhone) {
            const separator = "      ";
            const separatorWidth = pdf.getTextWidth(separator);
            pdf.text(separator, currentX, textY, { align: "right" });
            currentX = currentX - separatorWidth;
          }

          if (companyAddress) {
            const iconSize = 2.3;
            const iconSpacing = 2;
            const iconYOffset = 0.4;
            const addressTextWidth = pdf.getTextWidth(companyAddress);
            const iconX = currentX - addressTextWidth - iconSize - iconSpacing;

            if (distanceIconPngDataUrl) {
              try {
                pdf.addImage(
                  distanceIconPngDataUrl,
                  "PNG",
                  iconX,
                  textY - iconSize / 2 - iconYOffset - 0.8,
                  iconSize,
                  iconSize,
                );
              } catch (err) {}
            }

            pdf.text(companyAddress, currentX, textY, { align: "right" });
          }

          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(16);
          pdf.setTextColor(17, 17, 17);
        }

        const dividerY = headerHeight - 14;
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          dividerY,
          pdfWidth - horizontalMargin,
          dividerY,
        );

        const infoY = headerHeight - 8;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(26, 95, 180);
        const infoColumnWidth = (pdfWidth - horizontalMargin * 2) / 2;
        if (docInfoLeft) {
          pdf.text(docInfoLeft, horizontalMargin, infoY, {
            maxWidth: infoColumnWidth - 4,
          });
        }

        if (exportInfo) {
          const exportLines = splitExportInfoLines(exportInfo);
          if (exportLines.length) {
            const lineHeight = 4;
            const startY = infoY - ((exportLines.length - 1) * lineHeight) / 2;
            exportLines.forEach((line, lineIdx) => {
              const lineY = startY + lineIdx * lineHeight;
              pdf.text(line, pdfWidth - horizontalMargin, lineY, {
                align: "right",
              });
            });
          }
        }

        const footerY = pdfHeight - 4;

        const footerBorderY = footerY - 3;
        pdf.setDrawColor(204, 204, 204);
        pdf.setLineWidth(0.5);
        pdf.line(
          horizontalMargin,
          footerBorderY,
          pdfWidth - horizontalMargin,
          footerBorderY,
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(102, 102, 102);
        if (footerText) {
          pdf.text(footerText, horizontalMargin, footerY);
        }
        const pageNumberText = `Page ${index + 1}/${totalPages}`;
        const pageNumberWidth = pdf.getTextWidth(pageNumberText);
        const separatorGap = 3;
        const separatorHeight = 3;
        const separatorX =
          pdfWidth - horizontalMargin - pageNumberWidth - separatorGap;
        pdf.setDrawColor(209, 213, 219);
        pdf.setLineWidth(0.3);
        pdf.line(
          separatorX,
          footerY - separatorHeight,
          separatorX,
          footerY + separatorHeight,
        );
        pdf.text(pageNumberText, pdfWidth - horizontalMargin, footerY, {
          align: "right",
        });
      }

      return pdf;
    } catch (error) {
      throw error;
    }
  };

  const handleExportZIP = async () => {
    const sanitizePathSegment = (value, fallback = "Unknown") => {
      const raw = String(value ?? "").trim();
      const safe = raw
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "_")
        .replace(/\s+/g, " ")
        .trim();
      return safe || fallback;
    };

    const sanitizeFileName = (value, fallback = "file") =>
      sanitizePathSegment(value, fallback).replace(/\s+/g, "_");
    const normalizeFileToken = (value, fallback = "Unknown") => {
      const token = sanitizePathSegment(value, fallback)
        .replace(/\s+/g, "_")
        // Collapse mixed legacy separators like "_-_" / "-_-" / "__" / "--"
        .replace(/[-_]{2,}/g, "_")
        .replace(/_-/g, "_")
        .replace(/-_/g, "_")
        .replace(/^[-_.]+|[-_.]+$/g, "");
      return token || fallback;
    };
    const normalizeRevisionToken = (value, fallback = "UnknownRev") => {
      const token = sanitizePathSegment(value, fallback)
        .replace(/\s+/g, "_")
        .replace(/[-_]{2,}/g, "_")
        .replace(/_-/g, "_")
        .replace(/-_/g, "_")
        // Keep trailing hyphens in revision labels (e.g. "A-").
        .replace(/^[_.]+|[_.]+$/g, "");
      return token || fallback;
    };
    const escapeRegExp = (value) =>
      String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getLatestStatusForRevision = (revision) => {
      const statusChanges = Array.isArray(revision?.status_changes)
        ? revision.status_changes
        : [];
      if (statusChanges.length === 0) {
        return "";
      }

      const latestMarked = statusChanges.find((sc) => sc?.is_latest === true);
      if (latestMarked?.status_changed_to) {
        return String(latestMarked.status_changed_to).trim();
      }

      const sorted = [...statusChanges].sort((a, b) => {
        const dateA = new Date(a?.audit_trail?.date || a?.created_at || 0);
        const dateB = new Date(b?.audit_trail?.date || b?.created_at || 0);
        return dateB - dateA;
      });
      return String(sorted[0]?.status_changed_to || "").trim();
    };

    const collectAttachmentUrls = (nodes) => {
      const urls = [];
      const seen = new Set();

      const addFromChildren = (children) => {
        if (!Array.isArray(children)) return;
        children.forEach((child) => {
          if (child?.type !== "attachment") return;
          const attachments = child?.detail?.attachments;
          if (!Array.isArray(attachments)) return;
          attachments.forEach((att) => {
            const url = att?.attachment;
            if (typeof url === "string" && url.trim()) {
              const normalized = url.trim();
              if (!seen.has(normalized)) {
                seen.add(normalized);
                urls.push(normalized);
              }
            }
          });
        });
      };

      if (!Array.isArray(nodes)) return urls;
      nodes.forEach((section) => {
        const steps = Array.isArray(section?.steps) ? section.steps : [];
        steps.forEach((step) => {
          addFromChildren(step?.children);
          const substeps = Array.isArray(step?.substeps) ? step.substeps : [];
          substeps.forEach((substep) => addFromChildren(substep?.children));
        });
      });

      return urls;
    };

    const resolveAttachmentFileName = (url, fallbackIndex) => {
      try {
        const parsed = new URL(url);
        const name = decodeURIComponent(parsed.pathname.split("/").pop() || "");
        if (name) return name;
      } catch (e) {}
      const fallbackExtMatch = String(url || "").match(
        /\.[a-zA-Z0-9]{1,8}(?:$|\?)/,
      );
      const fallbackExt = fallbackExtMatch
        ? fallbackExtMatch[0].replace("?", "")
        : ".bin";
      return `attachment_${fallbackIndex}${fallbackExt}`;
    };

    const fetchAttachmentBlob = async (url) => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download attachment (${response.status})`);
      }
      return response.blob();
    };

    try {
      if (!moduleProcedures || moduleProcedures.length === 0) {
        alert("No procedures available to export");
        return;
      }

      const zip = new JSZip();
      const now = new Date();
      const exportDate = now.toLocaleDateString("en-CA");
      const modulePartNumber = sanitizePathSegment(
        moduleData?.itemCode || partNumber || "Module",
      );
      const exportRootName = `${modulePartNumber} - Exported ${exportDate}`;
      const rootFolder = zip.folder(exportRootName);

      let exportedPdfCount = 0;
      let exportedAttachmentCount = 0;
      const errors = [];

      for (const procedure of moduleProcedures) {
        try {
          const partId = procedure.partId || procedure.part_id;
          if (!partId) {
            errors.push(
              `Procedure ${procedure.partNumber || "unknown"} has no partId`,
            );
            continue;
          }

          const revisionsResult = await dispatch(
            getProcedureRevisionsForPart({ partId }),
          ).unwrap();

          const revisions = revisionsResult?.results || [];
          if (revisions.length === 0) {
            errors.push(
              `No revisions found for ${procedure.partNumber || partId}`,
            );
            continue;
          }

          const exportableRevisions = revisions.filter((rev) => {
            const latestStatus = getLatestStatusForRevision(rev).toLowerCase();
            return latestStatus === "released" || latestStatus === "archived";
          });

          if (exportableRevisions.length === 0) {
            errors.push(
              `No released or archived revisions found for ${
                procedure.partNumber || partId
              }`,
            );
            continue;
          }

          for (const exportableRevision of exportableRevisions) {
            const revisionLabel = sanitizePathSegment(
              exportableRevision?.rev || "Unknown Rev",
            );
            const latestStatusLabel = sanitizePathSegment(
              getLatestStatusForRevision(exportableRevision) || "Unknown",
            );

            // Match Document.js / BOMTable: use revision `type_name` first (same as currentRevision.type_name).
            // Module procedure rows often still have `procedure.type` as a legacy label (e.g. "Product …"),
            // which wrongly produced headings like "Product TM102 BOM" in zipped PDFs.
            const typeName = sanitizePathSegment(
              exportableRevision.type_name ||
                procedure.type_name ||
                procedure.type ||
                "Unknown Type",
            );
            const itemCode = sanitizePathSegment(
              procedure.partNumber ||
                procedure.part_number ||
                exportableRevision.part_number ||
                "Unknown Item",
            );
            const partDescription =
              procedure.description ||
              procedure.part_description ||
              exportableRevision.part_description ||
              "";

            const typeFolder = rootFolder?.folder(typeName);
            const itemFolder = typeFolder?.folder(itemCode);
            const revisionFolder = itemFolder?.folder(`Rev ${revisionLabel}`);
            if (!revisionFolder) {
              errors.push(
                `Failed to create ZIP folder for ${itemCode} Rev ${revisionLabel}`,
              );
              continue;
            }

            const nodesResult = await dispatch(
              getRevAllNodes({
                partId,
                revisionId: exportableRevision.procedure_revision_id,
              }),
            ).unwrap();

            const nodesData = nodesResult || [];
            if (!Array.isArray(nodesData) || nodesData.length === 0) {
              errors.push(
                `No nodes found for ${itemCode} Rev ${revisionLabel}`,
              );
              continue;
            }

            setZipExportView({
              partId,
              revisionId: exportableRevision.procedure_revision_id,
              myRevision: exportableRevision.rev || "",
              typeName,
              partNumber: itemCode,
              description: partDescription,
              revisionData: revisions,
              nodesData,
            });

            await new Promise((resolve) =>
              requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );

            const exportRoot = zipExportRootRef.current;
            if (!exportRoot) {
              errors.push(
                `Export root not ready for ${itemCode} Rev ${revisionLabel}`,
              );
              continue;
            }

            const prevContentRef = contentRef?.current;
            if (contentRef) contentRef.current = exportRoot;

            try {
              await waitForContentToRender();

              let zipBomRows = [];
              try {
                const bomPayload = await dispatch(
                  getBom({
                    partId,
                    revisionId: exportableRevision.procedure_revision_id,
                  }),
                ).unwrap();
                zipBomRows = Array.isArray(bomPayload?.results)
                  ? bomPayload.results
                  : [];
              } catch (bomErr) {
                console.warn(
                  `ZIP export: could not load BOM for ${itemCode} Rev ${revisionLabel}`,
                  bomErr,
                );
              }

              let zipTrainingLogRows = [];
              try {
                const tlPayload = await dispatch(
                  getTrainingLogsForProcedureRevision({
                    procedureRevisionId: exportableRevision.procedure_revision_id,
                    limit: 500,
                    ...(partId ? { part_ref: String(partId).trim() } : {}),
                  }),
                ).unwrap();
                zipTrainingLogRows = Array.isArray(tlPayload?.results)
                  ? tlPayload.results
                  : Array.isArray(tlPayload)
                    ? tlPayload
                    : [];
                // Raw rows only — `computeTrainingLogExportRowsForPdf` runs the same
                // `buildTrainingLogDisplayRows` path as Document/TrainingLogTable.
              } catch (tlErr) {
                console.warn(
                  `ZIP export: could not load training logs for ${itemCode} Rev ${revisionLabel}`,
                  tlErr,
                );
              }

              const pdfBlob = await handleExportPDF({
                rootElement: exportRoot,
                returnBlob: true,
                meta: {
                  type: typeName,
                  partNumber: itemCode,
                  description: partDescription,
                  myRevision: exportableRevision.rev || "",
                  revisionId: exportableRevision.procedure_revision_id,
                },
                revisionDataOverride: revisions,
                filteredRevisionDataOverride: [],
                bomTableOverride: zipBomRows,
                trainingLogsOverride: zipTrainingLogRows,
                allNodesDataForTrainingLog: nodesData,
              });

              const pdfFileName = `${normalizeFileToken(itemCode, "Item")}_Rev_${normalizeRevisionToken(revisionLabel, "UnknownRev")}_${normalizeFileToken(latestStatusLabel, "Unknown")}.pdf`;
              revisionFolder.file(pdfFileName, pdfBlob);
              exportedPdfCount += 1;
            } finally {
              if (contentRef) contentRef.current = prevContentRef;
            }

            // Export raw attachment files under the same revision folder.
            const attachmentUrls = collectAttachmentUrls(nodesData);
            for (let i = 0; i < attachmentUrls.length; i++) {
              const attachmentUrl = attachmentUrls[i];
              try {
                const attachmentBlob = await fetchAttachmentBlob(attachmentUrl);
                const sourceName = resolveAttachmentFileName(
                  attachmentUrl,
                  i + 1,
                );
                const sourceBaseName = String(sourceName || "")
                  .replace(/\.[^/.]+$/, "")
                  .trim();
                const sourceExtensionMatch = String(sourceName || "").match(
                  /\.([a-zA-Z0-9]{1,10})$/,
                );
                const sourceExtension = sourceExtensionMatch
                  ? `.${sourceExtensionMatch[1].toLowerCase()}`
                  : "";
                const normalizedSourceToken = normalizeFileToken(
                  sourceBaseName || `attachment_${i + 1}`,
                  `attachment_${i + 1}`,
                );
                const itemToken = normalizeFileToken(itemCode, "Item");
                const revToken = normalizeRevisionToken(
                  revisionLabel,
                  "UnknownRev",
                );
                // Avoid re-prefixing when stored attachment name already contains the old
                // "<item>_-_Rev_<rev>-_-_Attachment_" legacy pattern.
                const legacyPrefixPattern = new RegExp(
                  `^${escapeRegExp(itemToken)}[-_]*Rev[-_]*${escapeRegExp(revToken)}[-_]*Attachment[-_]*`,
                  "i",
                );
                const sourceTokenWithoutLegacyPrefix = normalizedSourceToken.replace(
                  legacyPrefixPattern,
                  "",
                );
                const finalAttachmentToken = normalizeFileToken(
                  sourceTokenWithoutLegacyPrefix || `attachment_${i + 1}`,
                  `attachment_${i + 1}`,
                );
                const attachmentFileName = `${itemToken}_Rev_${revToken}_Attachment_${finalAttachmentToken}${sourceExtension}`;
                revisionFolder.file(attachmentFileName, attachmentBlob);
                exportedAttachmentCount += 1;
              } catch (attachmentError) {
                errors.push(
                  `Failed attachment download for ${itemCode} Rev ${revisionLabel}: ${attachmentError.message}`,
                );
              }
            }
          }
        } catch (error) {
          errors.push(
            `Error processing ${procedure.partNumber || procedure.part_id}: ${
              error.message
            }`,
          );
        }
      }

      if (exportedPdfCount === 0) {
        alert(
          `No PDFs were generated. ${
            errors.length > 0
              ? `Errors: ${errors.join("; ")}`
              : "No released or archived revisions found."
          }`,
        );
        return;
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
      });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sanitizeFileName(
        `${exportRootName}.zip`,
        "Module_Export.zip",
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      if (errors.length > 0) {
        console.warn(
          `ZIP export completed with ${errors.length} issue(s).`,
          errors,
        );
      }

    } catch (error) {
      alert(
        `Error exporting ZIP file: ${error.message}. Please check the console for details.`,
      );
    }
  };

  const zipExportHiddenSubtree = (
    <div
      ref={zipExportRootRef}
      style={{
        position: "fixed",
        left: "-10000px",
        top: 0,
        width: "1000px",
        background: "#ffffff",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#111",
        padding: 0,
        margin: 0,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
        {zipExportView ? (
          <>
            <RevisionTable
              revisionData={zipExportView.revisionData || []}
              revisionId={zipExportView.revisionId}
              showCreateButton={false}
              isReadOnly={true}
              hideLinkStyle={true}
              partId={zipExportView.partId}
              partNumber={zipExportView.partNumber}
              type={zipExportView.typeName}
              description={zipExportView.description}
              myRevision={zipExportView.myRevision}
              users={[]}
              approvedUsers={[]}
              releasedUsers={[]}
              rule={null}
              onRevisionCreated={null}
              onCloseAdditionalInfo={null}
            />
            <RenderRevisionNodes
              partRef={zipExportView.partId}
              procedureRevRef={zipExportView.revisionId}
              partNumber={zipExportView.partNumber}
              revision={zipExportView.myRevision}
              disabled={false}
              showCheckbox={false}
              preloadedData={zipExportView.nodesData || []}
            />
          </>
        ) : null}
    </div>
  );

  if (renderPdfEngineOnly) {
    return zipExportHiddenSubtree;
  }

  if (!isOpen) return null;

  return (
    <>
      {zipExportHiddenSubtree}
      <Popup
        title={<span style={{ fontSize: 18 }}>Select the Export Format</span>}
        closeModal={onClose}
        width={350}
        headerStyle={{
          boxShadow: "none",
          padding: "10px 38px 6px 16px",
          textAlign: "left",
        }}
      >
        <div
          style={{
            marginLeft: "20px",
            padding: "10px",
            borderTop: "1px solid #E0E0E0",
          }}
        >
          <div
            style={{
              paddingTop: 4,
              paddingBottom: 12,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 6,
              textAlign: "left",
            }}
          >
            {isModulePreview ? (
              <>
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="mhtml"
                    checked={exportFormat === "mhtml"}
                    onChange={() => setExportFormat("mhtml")}
                  />
                  <span>mHTML</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="pdf"
                    checked={exportFormat === "pdf"}
                    onChange={() => setExportFormat("pdf")}
                  />
                  <span>PDF</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="csv_module_procedures"
                    checked={exportFormat === "csv_module_procedures"}
                    onChange={() => setExportFormat("csv_module_procedures")}
                  />
                  <span>CSV (Table of Procedures)</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="zip"
                    checked={exportFormat === "zip"}
                    onChange={() => setExportFormat("zip")}
                  />
                  <span>
                    Zipped Folder All Revisions of Procedures in Table of
                    Procedures
                  </span>
                </label>
              </>
            ) : (
              <>
                {}
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="mhtml"
                    checked={exportFormat === "mhtml"}
                    onChange={() => setExportFormat("mhtml")}
                  />
                  <span>mHTML</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="pdf"
                    checked={exportFormat === "pdf"}
                    onChange={() => setExportFormat("pdf")}
                  />
                  <span>PDF</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="csv_bom"
                    checked={exportFormat === "csv_bom"}
                    onChange={() => setExportFormat("csv_bom")}
                  />
                  <span>CSV (BOM)</span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    alignItems: "start",
                    columnGap: 8,
                    marginBottom: 4,
                    width: "100%",
                  }}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="csv_revisions"
                    checked={exportFormat === "csv_revisions"}
                    onChange={() => setExportFormat("csv_revisions")}
                  />
                  <span>CSV (Revisions Table)</span>
                </label>

                {status?.toLowerCase() !== "not submitted" && (
                  <label
                    style={{
                      display: "grid",
                      gridTemplateColumns: "16px 1fr",
                      alignItems: "start",
                      columnGap: 8,
                      marginBottom: 4,
                      width: "100%",
                    }}
                  >
                    <input
                      type="radio"
                      name="exportFormat"
                      value="signatures_workflow"
                      checked={exportFormat === "signatures_workflow"}
                      onChange={() => setExportFormat("signatures_workflow")}
                    />
                    <span>Signatures (Submit, Approve, Release)</span>
                  </label>
                )}
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              borderTop: "1px solid #E0E0E0",
              paddingTop: 10,
              marginTop: 6,
            }}
          >
            <button
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                background: "#6c757d",
                color: "#e9ecef",
                borderRadius: 4,
                cursor: "pointer",
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              style={{
                padding: "6px 12px",
                border: "1px solid #0D6EFD",
                background: isExporting ? "#6c91ff" : "#0D6EFD",
                color: "#fff",
                borderRadius: 4,
                cursor: isExporting ? "default" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
              disabled={isExporting}
              onClick={handleExport}
            >
              {isExporting ? (
                <>
                  <span
                    className="spinner"
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,0.6)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <span>Exporting...</span>
                </>
              ) : (
                <span>Export</span>
              )}
            </button>
          </div>
        </div>

        <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </Popup>
    </>
  );
};

export default ExportFormatPopup;
